import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { Line2 } from 'three/addons/lines/Line2.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';
import { LineGeometry } from 'three/addons/lines/LineGeometry.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { VRButton } from 'three/addons/webxr/VRButton.js';
import { ARButton } from 'three/addons/webxr/ARButton.js';
import { FXAAShader } from 'three/addons/shaders/FXAAShader.js';
import { SMAAPass } from 'three/addons/postprocessing/SMAAPass.js';
import gsap from 'gsap';
import { createGalaxy, GALAXY_VIEW } from './galaxy.js';
import { GALAXY_LOOK, GALAXY_PALETTE, MW_STRUCTURE } from './galaxyParticles.js';
import { createBlackHole } from './blackhole.js';
import { afterNextPaint } from './afterpaint.js';
import { loadDataMapTexture } from './bitmaptexture.js';
import { enableBandUploads, uploadInBands, loadImage, dropImageBytes, decodedCopy, imageFileUrl, bandUploadLog } from './bandupload.js';
import { createStarCatalog, createFaintStarCatalog, STAR_REFERENCE_MAG, STAR_PSF_AREA } from './stars.js';
import { SKY_MAP_GLSL, skyMapUniforms, useSkyMap, SCENE_TO_EQUATORIAL, equatorialToScene } from './sky/frame.js';
import { galileanOffsets, jupiterPole } from './astro/galilean.js';
import { SHOTS, shotPose, planFlight, flightProgress, flightPoint } from './xr/shots.js';
import { onPhoneOrTablet } from './xr/device.js';
import { createPlanetPoints } from './sky/planetPoints.js';
import { createAsteroids } from './sky/asteroids.js';
import { applyShapeModel } from './bodies/shapeModel.js';
import { PHOBOS_SHAPE } from './data/phobosShape.js';
import { DEIMOS_SHAPE } from './data/deimosShape.js';
import { createSkyCube } from './sky/skyCube.js';
import { createCanvasRecorder, isRecordingSupported } from './cinematicRecorder.js';
import { createWarpEffect } from './warpEffect.js';
import { createHyperspace, kelvinToRgb, HYPERSPACE_TIMING } from './hyperspace.js';
import { createScreensaverTour, detailLimitDistance, readScreensaverOptions } from './screensaver/tour.js';
import { createIssScreensaverShow } from './screensaver/issshow.js';
import { FILM_GRAIN_GLSL } from './shaders/filmgrain.js';
import { CONTRAST_GLSL } from './shaders/contrast.js';
import { SUN_GLARE_GLSL } from './shaders/sunglare.js';
import { createDeepSkyObjects } from './dso.js';
import { createConstellations } from './constellations.js';
import { createWormholeTunnel } from './wormholeTunnel.js';
import { startTips } from './ui/tips.js';
import { initIssTracking } from './ui/isstrack.js';
import { initClimateMode } from './climate/index.js';
import { initSunSwitch } from './ui/sunswitch.js';
import { createCupolaView } from './ui/cupola.js';
import { startGpuWarmup, compileShownNow, compileForSceneAsync, pmremFromSceneAsync, pmremStandIn, SLOW_COMPILE_LIMIT_MS } from './gpuwarm.js';
import { mergeByMaterial, modelInView } from './issmodel.js';
import { SOFTWARE_RENDERING } from './softwaregpu.js';
import { initTimeMachine } from './ui/timemachine.js';
import { initGoSearch } from './ui/gosearch.js';
import { decodeView, encodeView, isViewHash, cameraToSpherical, sphericalToCamera, OVERLAYS as VIEW_OVERLAYS } from './ui/viewlink.js';
import { IS_APP, IS_QUEST_APP } from './appmode.js';
// The craft the App Store apps offer: the saucer and the sphere, both ours.
// No fan-made or licensed ships in anything sold, and their models are not
// bundled (apple/build-web.mjs).
const APP_CRAFT = new Set(['saucer', 'sphere']);
import { getPlanetPosition, getOrbitState, dateToJD, solveKepler, elementsPosition, SUPPORTED_PLANETS } from './orbits/ephemeris.js';
import { probeHelio, probePlace, FLYBY_WINDOW } from './probes/trajectory.js';
import { EARTH_OBLIQUITY, EARTH_SIDEREAL_TURNS_PER_DAY, earthRotationAngle } from './orbits/earthframe.js';
import { moonGeocentricJ2000, sunGeocentricJ2000, ECLIPSE_GLSL, R_EARTH_KM, R_MOON_KM, R_SUN_KM } from './eclipse/geometry.js';
import { futureRequested, futureState, futureMoonKm, installFutureHook } from './whatiffuture.js';
import { createRedGiant, GIANT_FROM } from './redgiant.js';
import { eclipsePhotoBoost } from './eclipse/totality.js';
import { tiltRequested, tiltState, heldSpinAngle, installTiltHook } from './whatiftilt.js';
import { earthRingsRequested, earthRingUniforms, createEarthRingMesh, ringTauTable, setEarthRingTau, updateEarthRingUniforms } from './earthrings.js';
import { createEclipseMode, eclipseFromHash } from './eclipse/mode.js';

// Cinematic post-processing shader
const CinematicShader = {
  uniforms: {
    tDiffuse: { value: null },
    // Defaults tuned via the cinematic tuner: exposure 0.82,
    // contrast 1.07, saturation 1.03, vignette 0.32, grain 0.13.
    vignetteIntensity: { value: 0.32 },
    vignetteSmoothness: { value: 0.5 },
    exposure: { value: 0.82 },
    // A famous photo's film floor (src/photos/mode.js): light under it, after
    // the exposure, goes to black, as the night side of a sunlit Earth does
    // on film. 0 everywhere else.
    photoBlackPoint: { value: 0.0 },
    contrast: { value: 1.07 },
    saturation: { value: 1.03 },
    adaptiveGlare: { value: 0.0 },
    // Where the glare centres: the centre of light of the part of the disc
    // that shows (measureSunLight). The disc's own centre, its limb in the
    // view's degrees (as fov x uv) and how far it is overexposed (1 while it
    // is a point to the lens, 0 once it shows its surface).
    lensFlareSun: { value: new THREE.Vector2(0.5, 0.5) },
    lensFlareDiscUv: { value: new THREE.Vector2(0.5, 0.5) },
    lensFlareDisc: { value: new THREE.Vector2(0, 1) },
    lensFlareAmount: { value: 0.0 },
    lensFlareAspect: { value: 1.0 },
    // Apparent angular radius of the sun in normalised-device units —
    // bigger when close, tiny at Pluto distance. Drives the flare core
    // size so the visible bloom shrinks proportionally with how big
    // the sun actually looks on screen (NASA-photo behaviour).
    lensFlareSunSize: { value: 0.05 },
    // Occlusion fraction (0 = clear, 1 = fully eclipsed). When the
    // sun's disc is partially blocked by a planet limb, the core
    // shrinks toward a sharp point — the "diamond ring" / Bailey's
    // beads effect — instead of just fading.
    lensFlareOcclusion: { value: 0.0 },
    // The Sun as a camera photographs it (src/shaders/sunglare.js, set by
    // updateLensFlare from SUN_GLARE): its presence on screen (0..1).
    lensFlareSpace: { value: 0.0 },
    // The core's peak, from the Sun's flux at the camera: the core always
    // clips white, and a brighter Sun widens the white and the glow.
    lensFlareGain: { value: 1.0 },
    // Degrees of view per unit of vertical UV (the camera's vertical fov),
    // and the view's height in CSS pixels.
    lensFlareDegPerUv: { value: 45.0 },
    lensFlareViewPx: { value: 900.0 },
    // How much the whole photograph is enlarged (1 or more), so its white
    // covers a disc drawn wider than its core (the artistic disc, the Sun
    // from Mercury at true scale) with the proportions it has round a point.
    lensFlareScale: { value: 1.0 },
    // The same for the veil, which grows only by its square root.
    lensFlareFarScale: { value: 1.0 },
    lensFlareGlowScale: { value: 1.0 },
    // The glow round the core and the wide veil (peaks, 0 for none).
    lensFlareGlow: { value: new THREE.Vector2(0, 0) },
    // Round a disc that shows its surface, the glow at the limb as a share
    // of the limb's own light as this pass draws it.
    lensFlareLimbGlow: { value: 0.0 },
    // Where the glow's tail is cut off, in widths of the glow.
    lensFlareGlowEnd: { value: 3.0 },
    // The diffraction rays' peak (0 for none) and the glare degrees over
    // which the longest fades to 1/e.
    lensFlareSpikes: { value: 0.0 },
    lensFlareSpikeLength: { value: 1.6 },
    lensFlareRayCount: { value: 20 },
    lensFlareRayTurn: { value: 0.37 },
    lensFlareStrands: { value: 0.0 },
    lensFlareStrandShape: { value: new THREE.Vector4(300, 0.75, 1.15, 0.8) },
    lensFlareStrandMix: { value: new THREE.Vector4(0.8, 0.11, 0.7, 0) },
    lensFlareStrandShift: { value: new THREE.Vector2(0.8, 1.5) },
    // The horizontal streak and the ghosts (0 for none).
    lensFlareStreak: { value: 0.0 },
    lensFlareGhosts: { value: 0.0 },
    // Widths in glare degrees: core, glow, veil, veil's end.
    lensFlareShape: { value: new THREE.Vector4(0.06, 0.25, 1.2, 6.0) },
    // A ray's line width, the distance along it where its fall begins, its
    // root's width and share of the light (glare degrees), and the distance
    // over which the root fades.
    lensFlareRayShape: { value: new THREE.Vector4(0.02, 0.05, 0.09, 0.25) },
    lensFlareRootFade: { value: 0.25 },
    // The lens's warmth on the glow and rays, and the colour the Sun's light
    // keeps on its way to the camera: white in space, reddened through the
    // Earth's air near the limb.
    lensFlareWarm: { value: new THREE.Color(1, 1, 1) },
    lensFlareRayTint: { value: new THREE.Color(1, 0.82, 0.6) },
    lensFlareTint: { value: new THREE.Color(1, 1, 1) },
    // What the spacecraft in front of the Sun leave of it (updateSunOccluders):
    // white where the Sun shows, black where a part covers it, drawn round
    // the Sun on screen. Its smallest mip is the share of the disc that shows.
    lensFlareOccMap: { value: null },
    lensFlareOccOn: { value: 0.0 },
    lensFlareOccScale: { value: new THREE.Vector2(1, 1) },
    // The body cutting the Sun (measureSunLight): its direction in view
    // space and the cosine of its angular radius. The core stays off its
    // silhouette (a white core centred on a half-set Sun lay over the
    // planet). The inverse projection turns a pixel into its ray.
    lensFlareBody: { value: new THREE.Vector4(0, 0, -1, 2) },
    lensFlareBodyMask: { value: 0.0 },
    lensFlareInvProj: { value: new THREE.Matrix4() },
    // The Earth's air bending the Sun's light over its limb (0 for none,
    // else AIR_GRAZING_BEND): the Earth's centre in view space and the sine
    // of its angular radius, the projection, and where the light that shows
    // left the Sun, round which the core is drawn.
    lensFlareRefract: { value: 0.0 },
    lensFlareAir: { value: new THREE.Vector4(0, 0, -1, 0) },
    lensFlareProj: { value: new THREE.Matrix4() },
    lensFlareSunSrc: { value: new THREE.Vector2(0.5, 0.5) },
    // Film grain. Subtle screen-space noise modulating brightness so
    // the image reads as photographed by a real camera sensor rather
    // than a clean digital frame. Time uniform drives the noise
    // pattern's evolution per frame.
    grainTime: { value: 0.0 },
    // Default grainStrength bumped from 0.07 to 0.13 so the noise
    // grain reads as a real photographic sensor texture rather than
    // a subliminal hint. 0.13 is a perceptible +/-6.5% per-pixel
    // brightness modulation, close to ISO 800 noise levels in real
    // photography. Toggleable via the FILM GRAIN button; the toggle
    // sets this back to 0 when off.
    grainStrength: { value: 0.13 },
    // Chromatic aberration — channels sampled at a sub-pixel radial
    // offset that grows toward the frame corners, the faint colour
    // fringing every real lens has. Kept tiny (sub-pixel near centre,
    // ~1px at the corners) so it reads as photographic, not a filter.
    chromaticAberration: { value: 0.0016 },
    // Volumetric god rays / sunshafts. Screen-space radial accumulation
    // of brightness from each pixel toward the sun's projected
    // position. Strength is driven by lensFlareAmount so the rays
    // ramp up and down with the same visibility/occlusion logic as
    // the lens flare core, which gives the right "sun is partially
    // visible" reading.
    godRayStrength: { value: 0.55 }
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float vignetteIntensity;
    uniform float vignetteSmoothness;
    uniform float exposure;
    uniform float photoBlackPoint;
    uniform float contrast;
    uniform float saturation;
    uniform float adaptiveGlare;
    uniform vec2 lensFlareSun;
    uniform float lensFlareAmount;
    uniform float lensFlareAspect;
    uniform float lensFlareSunSize;
    uniform float lensFlareOcclusion;
    uniform sampler2D lensFlareOccMap;
    uniform float lensFlareOccOn;
    uniform vec2 lensFlareOccScale;
    uniform float grainTime;
    uniform float grainStrength;
    uniform float chromaticAberration;
    uniform float godRayStrength;
    varying vec2 vUv;

    ${FILM_GRAIN_GLSL}
    ${CONTRAST_GLSL}

    // The scene's light through this pass's exposure, and then its
    // contrast and saturation. The glare reads the Sun's limb through
    // both, so the glow round a disc that shows its surface starts from
    // the limb as it is drawn.
    vec3 cinematicExposed(vec3 c) {
      c = clamp(c, vec3(0.0), vec3(6.0)) * exposure;
      c = max(c - photoBlackPoint, vec3(0.0)) / (1.0 - photoBlackPoint);
      float glare = clamp(adaptiveGlare, 0.0, 1.0);
      c = max(c - glare * 0.035, vec3(0.0));
      return pow(c, vec3(1.0 + glare * 0.16));
    }
    vec3 cinematicGraded(vec3 c) {
      c = photographicContrast(c, contrast);
      float grey = dot(c, vec3(0.2126, 0.7152, 0.0722));
      return mix(vec3(grey), c, saturation);
    }

    ${SUN_GLARE_GLSL}

    void main() {
      // The Sun's light past the planets and moons (lensFlareOcclusion)
      // and past any spacecraft in front of it (lensFlareOccMap: its 1 x 1
      // mip is the share of the disc they leave, a texel the light at that
      // pixel when the pixel lies on the disc).
      float occAvg = 1.0;
      float pixVis = 1.0;
      if (lensFlareOccOn > 0.5) {
        occAvg = textureLod(lensFlareOccMap, vec2(0.5), 5.0).r;
        vec2 occUv = (vUv - lensFlareDiscUv) * lensFlareOccScale + 0.5;
        if (all(greaterThan(occUv, vec2(0.0))) && all(lessThan(occUv, vec2(1.0)))) {
          pixVis = textureLod(lensFlareOccMap, occUv, 0.0).r;
        }
      }
      float flareOcc = 1.0 - (1.0 - lensFlareOcclusion) * occAvg;
      // Chromatic aberration. Offset R and B channels in opposite
      // directions along the radial vector from the frame centre,
      // scaled by radius² so the centre stays sharp and the fringing
      // only shows toward the edges/corners. caStrength is sub-pixel.
      vec2 caDir = vUv - 0.5;
      float caAmt = dot(caDir, caDir) * chromaticAberration;
      vec4 color;
      color.r = texture2D(tDiffuse, vUv - caDir * caAmt).r;
      color.g = texture2D(tDiffuse, vUv).g;
      color.b = texture2D(tDiffuse, vUv + caDir * caAmt).b;
      color.a = 1.0;
      if (any(notEqual(color.rgb, color.rgb))) {
        color.rgb = vec3(0.0);
      }
      // Exposure
      color.rgb = cinematicExposed(color.rgb);

      float flareAmount = clamp(lensFlareAmount, 0.0, 1.0);

      // Volumetric god rays. Classic radial-accumulation: walk from the
      // current fragment toward the sun's UV position, sample the
      // framebuffer along the path, and sum the contributions of any
      // HDR-bright pixels we cross. Distant-from-sun fragments
      // naturally pick up less brightness because most of their path
      // walks through dark space — no explicit falloff term needed.
      // An explicit falloff weights samples by where they sit on the
      // path, which produces directional asymmetry depending on which
      // side of the sun the fragment lives on; the path-length-only
      // formulation gives radially-symmetric rays.
      //
      // Two jitters keep the ray bundle volumetric rather than striped:
      // one along the ray to break the discrete-sample banding, and
      // one perpendicular to spread each sample sideways so adjacent
      // pixels see slightly different paths. Cost: 14 extra texture
      // samples per pixel. Only while a limb is cutting the disc: on a
      // clear sun the walk just smears the disc into uneven rays.
      float sunShaft = smoothstep(0.35, 0.7, flareOcc)
        * (1.0 - smoothstep(0.88, 0.98, flareOcc));
      if (flareAmount > 0.001 && godRayStrength > 0.001 && sunShaft > 0.001) {
        const int GOD_RAY_SAMPLES = 14;
        vec2 toSun = lensFlareSun - vUv;
        vec2 perp = normalize(vec2(-toSun.y, toSun.x) + vec2(1e-5));
        perp.x /= max(lensFlareAspect, 0.0001);
        vec3 rayAcc = vec3(0.0);
        float jitter = fract(sin(dot(vUv, vec2(12.9898, 78.233))) * 43758.5453);
        for (int i = 0; i < GOD_RAY_SAMPLES; i++) {
          float t = (float(i) + jitter) / float(GOD_RAY_SAMPLES);
          float jitterPerp = fract(sin(dot(vUv * 1.7 + float(i) * 0.13, vec2(45.31, 91.7))) * 9173.31) - 0.5;
          vec2 samplePos = vUv + toSun * t + perp * jitterPerp * 0.010;
          if (samplePos.x < 0.0 || samplePos.x > 1.0 ||
              samplePos.y < 0.0 || samplePos.y > 1.0) continue;
          vec3 sampleColor = texture2D(tDiffuse, samplePos).rgb;
          float brightness = max(max(sampleColor.r, sampleColor.g), sampleColor.b);
          // Strict gate: only the sun core (HDR ≳ 1.0) contributes.
          // Keeps bloomed planet limbs from fanning out their own ray
          // patterns and stops the rays from looking one-sided when
          // bloom asymmetrically halos the sun.
          float gate = smoothstep(1.0, 2.4, brightness);
          rayAcc += sampleColor * gate;
        }
        rayAcc /= float(GOD_RAY_SAMPLES);
        vec3 godRayColor = vec3(1.0, 0.92, 0.78);
        // Damp strength when the sun fills a large portion of the
        // screen (camera close approach). At that point each pixel's
        // path-to-sun walks through huge sun-bright regions and the
        // rays would smear an overpowering streak across the whole
        // frame. lensFlareSunSize ≈ 0.04 at typical mid-distance,
        // ≥0.18 when the sun's disc is dominant.
        float sunCloseDamp = smoothstep(0.18, 0.04, lensFlareSunSize);
        color.rgb += rayAcc * godRayColor * godRayStrength * flareAmount * 0.45 * sunCloseDamp * sunShaft;
      }

      // Contrast and saturation
      color.rgb = cinematicGraded(color.rgb);

      // Vignette
      vec2 uv = vUv * (1.0 - vUv.yx);
      float vigBase = max(uv.x * uv.y * 15.0, 0.0001);
      float vigPow = max(vignetteIntensity * vignetteSmoothness, 0.0001);
      float vig = pow(vigBase, vigPow);
      float vigMix = smoothstep(0.001, 0.3, vignetteIntensity);
      color.rgb *= mix(1.0, vig, vigMix);

      // Subtle blue in shadows, warm in highlights
      float lum = dot(color.rgb, vec3(0.2126, 0.7152, 0.0722));
      // Grade illuminated shadows without adding a blue pedestal to space.
      color.b += (1.0 - lum) * smoothstep(0.0, 0.08, max(lum, 0.0)) * 0.02;
      color.r += lum * 0.01;

      // The Sun as a camera photographs it, in both scales
      // (src/shaders/sunglare.js), after the grade (whose blue in the
      // shadows ringed its warm glow). Its uniforms already carry what the
      // planets, moons and air leave of the disc (measureSunLight); this is
      // what the spacecraft in front leave, compressed as that is. Its
      // vignette is round, as a lens's is: the scene's follows the frame's
      // edges, and over a glow that fills the frame that drew the glow as a
      // rounded rectangle, a square Sun. This one is the scene's along the
      // diagonals (the same corners) and a function of the distance from
      // the middle of the frame alone.
      vec2 fromMid = (vUv - 0.5) * vec2(lensFlareAspect, 1.0);
      float rho2 = dot(fromMid, fromMid) / (0.25 * (lensFlareAspect * lensFlareAspect + 1.0));
      float vigRound = pow(max(0.9375 * (1.0 - rho2) * (1.0 - rho2), 0.0001), vigPow);
      color.rgb += sunGlare(vUv, pow(occAvg, 0.6), pixVis) * mix(1.0, vigRound, vigMix);

      // Film grain — cheap screen-space white noise modulating
      // brightness slightly. Adds a sensor-photographed feel that
      // makes the image less "perfectly clean digital", more
      // "captured by a real camera". Time-driven hash so the
      // grain pattern evolves per frame instead of locking.
      float grainHash = sensorNoise(gl_FragCoord.xy, floor(grainTime * 24.0));
      float grain = (grainHash - 0.5) * grainStrength;
      color.rgb *= 1.0 + grain;

      if (any(notEqual(color.rgb, color.rgb))) {
        color.rgb = vec3(0.0);
      }
      color.rgb = max(color.rgb, vec3(0.0));

      gl_FragColor = color;
    }
  `
};

const BloomStabilizerShader = {
  uniforms: {
    tDiffuse: { value: null },
    texelSize: { value: new THREE.Vector2(1 / 1920, 1 / 1080) },
    maxValue: { value: 4.0 },
    fireflyThreshold: { value: 2.75 },
    neighborRatio: { value: 3.0 }
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform vec2 texelSize;
    uniform float maxValue;
    uniform float fireflyThreshold;
    uniform float neighborRatio;
    varying vec2 vUv;

    float luma(vec3 color) {
      return dot(color, vec3(0.2126, 0.7152, 0.0722));
    }

    vec3 cleanColor(vec3 color) {
      if (any(notEqual(color, color))) {
        color = vec3(0.0);
      }
      return clamp(color, vec3(0.0), vec3(maxValue));
    }

    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      vec3 center = cleanColor(color.rgb);
      vec3 neighborhood = (
        cleanColor(texture2D(tDiffuse, vUv + vec2(texelSize.x, 0.0)).rgb) +
        cleanColor(texture2D(tDiffuse, vUv - vec2(texelSize.x, 0.0)).rgb) +
        cleanColor(texture2D(tDiffuse, vUv + vec2(0.0, texelSize.y)).rgb) +
        cleanColor(texture2D(tDiffuse, vUv - vec2(0.0, texelSize.y)).rgb)
      ) * 0.25;

      float centerLuma = luma(center);
      float neighborLuma = max(luma(neighborhood), 0.001);
      float isolatedSpike = smoothstep(fireflyThreshold, fireflyThreshold * 1.4, centerLuma) *
        smoothstep(neighborLuma * neighborRatio, neighborLuma * neighborRatio * 1.25, centerLuma);
      color.rgb = mix(center, min(center, neighborhood * neighborRatio), isolatedSpike);
      gl_FragColor = color;
    }
  `
};

import { celestialData, textureUrls, planetOrder } from './data/celestialData.js';
import { NEXT_SEAMLESS } from './data/nextTextures.js';
import { detectWebpSupport, applyTextureFormat, applyMobileTextures, mobileTextureUrl } from './textureFormat.js';
import { COMPACT_LAYOUT_QUERY, isMobileLikeDevice, RUNNING_ON_MOBILE, shrinkModelTextures } from './mobiledevice.js';
import { startReloadWatch, noteContextLoss, markOwnReload } from './reloadwatch.js';
import { listSystems, getSystem, getCurrentSystemId, setCurrentSystem, restoreCurrentSystem, registerSystem } from './systems/index.js';
import { buildSystem } from './systems/builder.js';
import { createNibiru } from './bodies/nibiru.js';
import { createZodiacalLight, updateZodiacalLight } from './zodiacalLight.js';
import { enhanceAllSelects } from './ui/dropdown.js';
import { computeAtmosphericEntry, createAtmosphericEntryState, FLIGHT_STATES } from './flight/atmosphericEntry.js';
import { satellites, nearEarthObjects, comets, spaceProbes, dwarfPlanets, asteroidBeltBodies, interstellarObjects, ASTEROID_ELEMENTS_EPOCH_JD } from './data/satellites.js';
import { jwstGeocentric, visitorHelio, jwstArtisticRange } from './orbits/visitors.js';
// Bennu, Ryugu, Eros, Vesta, Pallas, Hygiea and Apophis in their real shapes.
import { initSmallBodies, registerSmallBody, smallBodyModel, orientSmallBody, updateSmallBodies, smallBodyStates, loadSmallBodyNow, smallBodyRecord, smallBodyPlaceholderGeometry, smallBodyPlaceholderMaterial } from './smallbodies/index.js';
// Every active satellite (Starlink, OneWeb, GPS...) from CelesTrak through SGP4.
import { createRealSatellites } from './satellites/index.js';
import { issTeme, satrecFrom, ISS_NORAD } from './iss/orbit.js';
import { createDebrisField } from './debris.js';
import { DOCUMENTARY_CHAPTERS } from './data/documentary.js';
import { OKLAB_MIX_GLSL } from './shaders/oklab.js';
import { createCometVolume, updateCometVolume } from './comets/cometVolume.js';
import {
  earthAtmosphereVertexShader,
  earthAtmosphereFragmentShader
} from './shaders/atmosphere.js';
import {
  sunVertexShader,
  sunFragmentShader,
  sunProminenceVertexShader,
  sunProminenceFragmentShader,
  sunChromosphereFragmentShader
} from './shaders/sun.js';
import { earthVertexShader, earthFragmentShader } from './shaders/earth.js';
import { earthImageryUniforms } from './rocketry/earthimagery.js';
import { moonImageryUniforms, MOON_IMAGERY_GLSL, getMoonImageryStream } from './rocketry/moonimagery.js';
import { MOON_RADIUS } from './data/moonImagerySource.js';
import { cloudImageryUniforms, updateViewerCloudImagery } from './rocketry/cloudimagery.js';
import { nightImageryUniforms, updateViewerNightImagery } from './rocketry/nightimagery.js';
import { updateEarthWeather, prepareEarthWeather } from './earthweather.js';
import { createEarthCloseView, earthSurfaceUV, earthSurfaceDirection, EARTH_METRES, EARTH_ELEVATION_METRES, EARTH_INSPECTION_CLEARANCE, AIR_GRAZING_BEND, VIEWER_SKY_HANDOVER } from './earthclose.js';
import { sunFarFieldUniforms, updateSunFarField } from './sunfarfield.js';
import { smoothWheelZoom } from './ui/smoothwheel.js';
import { oceanWaveUniforms, updateOceanWaves } from './oceanwaves.js';
import { createViewerSceneTarget } from './scenetarget.js';
import {
  earthAuroraVertexShader,
  earthAuroraFragmentShader,
  northernLightsVertexShader,
  northernLightsFragmentShader
} from './shaders/aurora.js';
import { createWormholeLens } from './wormholeLens.js';
import { applyGasGiantBands, applyRingShadowProjection, applyGasGiantLimb, applyGasGiantLightning } from './shaders/gasGiant.js';
import {
  proceduralGasGiantVertexShader,
  proceduralGasGiantFragmentShader
} from './shaders/proceduralGasGiant.js';
import { venusVertexShader, venusFragmentShader } from './shaders/venus.js';
import { titanHazeVertexShader, titanHazeFragmentShader } from './shaders/titanHaze.js';

// App mode parsing. URL params let one web build serve multiple products:
//   ?mode=screensaver  -> Mac/Windows screensaver, Steam Wallpaper Engine.
//                         No UI, auto-tour, audio muted, input ignored.
//   ?mode=immersive    -> Vision Pro (Nova) wrapper. Minimal UI, optimized
//                         for hand interaction, no fly mode by default.
//   (no param)         -> Regular interactive site at 3dsolarsystem.online.
// Each wrapper just points its WebView at the right URL and gets the
// matching experience without us shipping separate web builds per product.
const APP_MODE = (() => {
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const m = (params.get('mode') || '').toLowerCase();
  return m === 'screensaver' || m === 'immersive' ? m : 'default';
})();
const IS_SCREENSAVER = APP_MODE === 'screensaver';
const IS_IMMERSIVE = APP_MODE === 'immersive';
// The screensaver's ISS shows (?show=iss, ?show=cupola,
// src/screensaver/issshow.js) are the only screensaver pages that build ISS
// tracking and the Cupola: both are viewer UI with their own cards and input,
// which the other shows have no use for.
const SCREENSAVER_ISS_SHOW = IS_SCREENSAVER && ['iss', 'cupola'].includes(readScreensaverOptions(window.location.search).show);
// ?stream=cupola: the round-the-clock broadcast of the ISS Cupola window
// (src/stream/cupolastream.js). It runs in the ordinary viewer, with its own
// caption and curtain.
const STREAM_MODE = (() => {
  try { return new URLSearchParams(window.location.search).get('stream') === 'cupola' ? 'cupola' : null; } catch { return null; }
})();
// GAME MODE: set by game.html (inline `window.__GAME_MODE__ = true` before
// any module loads). The game shell (src/game/index.js) imports this module
// and takes over the startup flow: no hero focus, chrome suppressed via the
// body.game-mode class, and a tiny window.__gameBridge handle is exposed at
// the end of init() so the hangar can board ships. The website bundle never
// sets the flag, so all IS_GAME branches are dead code there.
const IS_GAME = typeof window !== 'undefined' && window.__GAME_MODE__ === true;
if (IS_GAME && typeof document !== 'undefined') {
  if (document.body) {
    document.body.classList.add('game-mode');
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      document.body.classList.add('game-mode');
    });
  }
}
if (typeof document !== 'undefined' && document.body) {
  document.body.classList.add(`mode-${APP_MODE}`);
} else if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', () => {
    document.body.classList.add(`mode-${APP_MODE}`);
  });
}
import {
  simpleRingVertexShader,
  proceduralRingFragmentShader,
  saturnRingVertexShader,
  saturnRingFragmentShader,
  saturnDustRingVertexShader,
  saturnDustRingFragmentShader
} from './shaders/rings.js';
import { matryoshka } from './ui/matryoshka.js';
import { getBody } from './data/bodies/index.js';
import {
  phaseAngleDeg,
  illuminationPercent,
  apparentDiameterDeg
} from './science.js';
import { track, modeEnter, modeExit, viewerOpened, explore, countStep } from './analytics.js';
// Today's price of each product, the one checkout charges.
import { priceOf } from '../api/_lib/prices.js';
import { activeOffer } from '../api/_lib/offers.js';
import { auroraLiveUniforms, updateLiveAurora, sunLiveUniforms, updateLiveSun, startSpaceWeather, installDebug as installSpaceWeatherDebug } from './spaceweather/viewer.js';
import { initSpaceWeatherUI } from './spaceweather/ui.js';
import { createSunDiscBloomPass, updateSunDiscBloom } from './sunbloom.js';
import { applyViewerTranslations, initViewerLanguageLinks, vnum, vt } from './i18n/viewer.js';

if (typeof document !== 'undefined' && document.documentElement) {
  document.documentElement.dataset.mainBoot = 'module';
}

// The control panel in the viewer's language (src/i18n/viewer.js, ?lang=),
// before anything below writes a label. English leaves the markup as it is.
applyViewerTranslations();
initViewerLanguageLinks();

let spaceAmbientModulePromise = null;
let spaceAmbientModule = null;
let pendingUfoHumIntensity = null;
let pendingUfoHumAction = null;
let pendingAlienScanAudioState = null;

// Which music plays (spaceAmbient.js): the generative bed by default, or
// Quiet Wonder, the recorded track (id 'orbit'), picked under the Ambient
// button (#music-pick).
const MUSIC_KEY = 'ss.music';
let musicPiece = 'generative';
try { if (localStorage.getItem(MUSIC_KEY) === 'orbit') musicPiece = 'orbit'; } catch { /* storage blocked */ }

function loadSpaceAmbientModule() {
  if (spaceAmbientModule) return Promise.resolve(spaceAmbientModule);
  if (!spaceAmbientModulePromise) {
    spaceAmbientModulePromise = import('./audio/spaceAmbient.js').then((mod) => {
      spaceAmbientModule = mod;
      mod.setMusicPiece(musicPiece);
      if (pendingUfoHumIntensity !== null) {
        mod.setUfoHumIntensity(pendingUfoHumIntensity);
      }
      if (pendingUfoHumAction === 'start') {
        mod.startUfoHum();
      } else if (pendingUfoHumAction === 'stop') {
        mod.stopUfoHum();
      }
      if (pendingAlienScanAudioState && mod.setAlienScanAudioState) {
        mod.setAlienScanAudioState(pendingAlienScanAudioState);
      }
      pendingUfoHumAction = null;
      return mod;
    }).catch((err) => {
      spaceAmbientModulePromise = null;
      throw err;
    });
  }
  return spaceAmbientModulePromise;
}

async function initUserGesture() {
  return (await loadSpaceAmbientModule()).initUserGesture();
}

// Chromium and Firefox let audio start at any moment once the page has been
// used; Safari, and every browser on iOS, only inside a press.
const AUDIO_STARTS_AFTER_USE = typeof navigator !== 'undefined'
  && /Chrome\/|Firefox\//.test(navigator.userAgent) && !/iPhone|iPad|iPod/.test(navigator.userAgent);

// A tap that starts the music: the audio context is resumed inside the
// gesture when the music module is already loaded (Safari starts audio only
// there), and the rest waits until the tap's frame is drawn. Making the
// context and the reverbs takes 0.2 to 0.4 s, and done first the tap waited
// for all of it before anything showed (INP).
async function audioGesture() {
  if (spaceAmbientModule) spaceAmbientModule.initUserGesture().catch(() => {});
  await afterNextPaint();
  await initUserGesture();
}

async function toggleAmbient() {
  return (await loadSpaceAmbientModule()).toggleAmbient();
}

async function startAmbient() {
  return (await loadSpaceAmbientModule()).startAmbient();
}

async function stopAmbient() {
  return (await loadSpaceAmbientModule()).stopAmbient();
}

function startUfoHum() {
  if (spaceAmbientModule) {
    spaceAmbientModule.startUfoHum();
    return;
  }
  pendingUfoHumAction = 'start';
  loadSpaceAmbientModule().catch((err) => console.warn('[audio] failed to load ambient module', err));
}

function stopUfoHum() {
  if (spaceAmbientModule) {
    spaceAmbientModule.stopUfoHum();
    return;
  }
  pendingUfoHumAction = 'stop';
  loadSpaceAmbientModule().catch((err) => console.warn('[audio] failed to load ambient module', err));
}

function setUfoHumIntensity(intensity) {
  pendingUfoHumIntensity = intensity;
  if (spaceAmbientModule) {
    spaceAmbientModule.setUfoHumIntensity(intensity);
    return;
  }
  loadSpaceAmbientModule().catch((err) => console.warn('[audio] failed to load ambient module', err));
}

function setAlienScanAudioState(state) {
  pendingAlienScanAudioState = state;
  if (spaceAmbientModule && spaceAmbientModule.setAlienScanAudioState) {
    spaceAmbientModule.setAlienScanAudioState(state);
  }
}

function stopAlienScanAudio() {
  setAlienScanAudioState({ active: false, strength: 0, targetKey: '' });
}

// Graphics-quality presets. Changing the preset saves to localStorage
// and reloads the page — geometry and post-processing can't be rebuilt
// cleanly at runtime, but config is tiny and reloads are fast.
//
// Deliberately light: no selective bloom, no backdrop blurs, no MSAA
// above 4x (we learned the hard way those melt mid-range GPUs). Low
// turns bloom off entirely so the perf floor matches a non-webgl-post
// render.
const GRAPHICS_PRESETS = {
  low: {
    label: 'Low',
    pixelRatio: 0.75,
    msaa: 0,
    bloom: false,
    smaa: false,
    fxaa: false,
    cinematic: false,
    anisotropy: 4,
    planetSegments: 48,
    moonSegments: 20,
    dwarfSegments: 28,
    sunSegments: 64,
    earthWidth: 128,
    earthHeight: 64,
    atmosphereSegments: 48,
    lensFlare: false,
    lensFlareIntensity: 0,
    autoExposure: false,
    autoExposureStrength: 0,
    showFps: false
  },
  medium: {
    label: 'Medium',
    pixelRatio: 1,
    msaa: 0,
    bloom: true,
    smaa: true,
    fxaa: false,
    cinematic: true,
    anisotropy: 8,
    planetSegments: 80,
    moonSegments: 32,
    dwarfSegments: 40,
    sunSegments: 96,
    earthWidth: 192,
    earthHeight: 96,
    atmosphereSegments: 72,
    lensFlare: true,
    lensFlareIntensity: 0.3,
    autoExposure: true,
    autoExposureStrength: 0.22,
    showFps: false
  },
  high: {
    label: 'High',
    // DPR 2 quadruples pixel cost vs 1. 1.5 is visually indistinguishable
    // on most retina screens but cuts fragment work ~44%.
    pixelRatio: Math.min(window.devicePixelRatio || 1, 1.5),
    // 4× MSAA looks identical to 8× to the eye on this content (no thin
    // black wires) while halving MSAA bandwidth on the HDR target.
    msaa: 4,
    bloom: true,
    smaa: true,
    // FXAA is redundant when SMAA + MSAA are both enabled. Pure waste —
    // it reads/writes the entire framebuffer for no visible benefit.
    fxaa: false,
    cinematic: true,
    anisotropy: 16,
    planetSegments: 128,
    moonSegments: 48,
    dwarfSegments: 56,
    sunSegments: 128,
    earthWidth: 256,
    earthHeight: 128,
    atmosphereSegments: 96,
    lensFlare: true,
    lensFlareIntensity: 0.42,
    autoExposure: true,
    autoExposureStrength: 0.3,
    showFps: false
  },
  ultra: {
    // Targets 60 FPS on enthusiast desktops (3060 Ti class and up). The
    // big-spender knobs (renderScale, MSAA on HDR, FXAA-after-SMAA) all
    // got dropped in favour of geometry / shader headroom that the user
    // can actually see. SMAA + 4× MSAA on a 1.5× DPR canvas already
    // cleans every edge in this scene.
    label: 'Ultra',
    pixelRatio: Math.min(window.devicePixelRatio || 1, 1.5),
    renderScale: 1,
    msaa: 4,
    bloom: true,
    smaa: true,
    fxaa: false,
    cinematic: true,
    anisotropy: 16,
    planetSegments: 160,
    moonSegments: 56,
    dwarfSegments: 64,
    sunSegments: 144,
    earthWidth: 320,
    earthHeight: 160,
    atmosphereSegments: 112,
    starfield: 'hi',
    lensFlare: true,
    lensFlareIntensity: 0.54,
    autoExposure: true,
    autoExposureStrength: 0.36,
    showFps: false
  },
  max: {
    // MAX preset — everything cranked. Targets workstation-class GPUs
    // (4070+ class, M-series Pro/Max). Will tank on integrated
    // graphics; users have to opt in manually from the Advanced
    // modal. Above-ultra settings are doubled where it matters:
    // full-DPR canvas, 8× MSAA, doubled geometry resolution,
    // doubled atmosphere segments, FXAA-after-SMAA for the last
    // subpixel sliver, max lens flare, and max anisotropy.
    label: 'Max',
    pixelRatio: window.devicePixelRatio || 1,
    renderScale: 1,
    msaa: 8,
    bloom: true,
    smaa: true,
    fxaa: true,
    cinematic: true,
    anisotropy: 16,
    planetSegments: 256,
    moonSegments: 96,
    dwarfSegments: 128,
    sunSegments: 256,
    earthWidth: 512,
    earthHeight: 256,
    atmosphereSegments: 192,
    starfield: 'hi',
    lensFlare: true,
    lensFlareIntensity: 0.7,
    autoExposure: true,
    autoExposureStrength: 0.4,
    showFps: false
  }
};

function isTabletLikeDevice() {
  const ua = navigator.userAgent || '';
  const touchDevice = navigator.maxTouchPoints && navigator.maxTouchPoints > 1;
  return /iPad|Tablet/i.test(ua)
    || (touchDevice && Math.min(window.innerWidth || 0, window.innerHeight || 0) >= 820);
}

// Auto-detect a reasonable default for new visitors. Mobile is now a
// first-class visual target: modern phones/tablets get the cinematic
// path by default, while Save-Data, reduced-motion, and very old
// devices remain protected from crash loops.
function detectDefaultGraphicsPreset() {
  const isLocalhost = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
  const isFileUrl = window.location.protocol === 'file:';
  if (isLocalhost || isFileUrl) return 'ultra';

  const cores = navigator.hardwareConcurrency || 4;
  const memGB = navigator.deviceMemory || 4;
  const isMobile = isMobileLikeDevice();
  const isTablet = isTabletLikeDevice();
  const lowDpr = (window.devicePixelRatio || 1) <= 1.25;
  const saveData = !!(navigator.connection && navigator.connection.saveData);
  const reducedMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const weakCpu = cores <= 4;
  const weakMemory = memGB <= 4;
  const veryWeak = cores <= 2 || memGB <= 2;
  const viewportPixels = Math.max(1, (window.innerWidth || 1) * (window.innerHeight || 1));

  if (saveData || reducedMotion || veryWeak) return 'low';
  if (isMobile) {
    // Mobile GPUs (especially iOS Safari) have hard memory ceilings on
    // WebGL render targets. Ultra would allocate ~25MP HDR + 8x MSAA
    // and reliably kill the context, so cap auto-detection at High for
    // capable tablets only and Medium for everything else. Users can
    // still opt into Ultra manually from the Advanced graphics modal.
    const flagshipTablet = isTablet
      && cores >= 8
      && memGB >= 6
      && viewportPixels >= 900000
      && !saveData
      && !reducedMotion;
    if (flagshipTablet) return 'high';
    return 'medium';
  }
  if (weakCpu && weakMemory) return 'low';
  if (cores >= 8 && memGB >= 8 && !lowDpr && !saveData) return 'high';
  return 'medium';
}

const MOBILE_SESSION_PRESET_KEY = 'graphicsSessionPreset';

function loadGraphicsPreset() {
  const isLocalhost = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
  const isFileUrl = window.location.protocol === 'file:';
  // ?preset= in screensaver mode picks the quality outright (the live
  // wallpaper, which draws all day, asks for less than the GPU could do).
  const forcedPreset = IS_SCREENSAVER && new URLSearchParams(window.location.search).get('preset');
  if (forcedPreset && GRAPHICS_PRESETS[forcedPreset]) return forcedPreset;
  // A phone that lost its graphics twice in a row runs Low for the rest of
  // this tab's session only (the webglcontextlost handler in setupRenderer).
  if (isMobileLikeDevice()) {
    try {
      const sessionPreset = sessionStorage.getItem(MOBILE_SESSION_PRESET_KEY);
      if (sessionPreset && GRAPHICS_PRESETS[sessionPreset]) return sessionPreset;
    } catch (e) { /* storage blocked */ }
  }

  try {
    const autoVersion = 'mobile-v6';
    const saved = localStorage.getItem('graphicsPreset');
    const manual = localStorage.getItem('graphicsPresetManual') === '1';
    // Localhost defaults to 'max' for dev work, but respect an
    // explicit manual override so the quality dropdown actually
    // does something locally — the previous unconditional
    // `return 'max'` was eating every saved preset on reload.
    if ((isLocalhost || isFileUrl) && !manual) return 'max';
    const safetyFallback = localStorage.getItem('graphicsSafetyFallback') === '1';
    const savedAutoVersion = localStorage.getItem('graphicsAutoPresetVersion');
    // Re-detect when the auto-version bumps. v5 also forcibly demotes
    // any non-manual user who got auto-promoted to Ultra by v4 — that
    // preset OOMs every modern phone we tested.
    const onMobile = isMobileLikeDevice();
    // A phone the context-loss handler used to put on Low for good after a
    // single loss gets its detected preset back, once: those losses were
    // memory (docs/mobile-memory.md), and a phone now falls back one tab at
    // a time (MOBILE_SESSION_PRESET_KEY).
    if (onMobile && safetyFallback && !manual && localStorage.getItem('graphicsSafetyFallbackReset') !== '1') {
      localStorage.removeItem('graphicsSafetyFallback');
      localStorage.setItem('graphicsSafetyFallbackReset', '1');
      const detected = detectDefaultGraphicsPreset();
      localStorage.setItem('graphicsPreset', detected);
      localStorage.setItem('graphicsAutoPresetVersion', autoVersion);
      return detected;
    }
    const stuckOnUltra = onMobile && saved === 'ultra' && !manual;
    // A preset chosen by hand is kept: a new detection only replaces one it
    // picked itself.
    if ((saved === 'low' || saved === 'medium' || saved === 'high' || stuckOnUltra) && !manual && !safetyFallback && savedAutoVersion !== autoVersion) {
      const detected = detectDefaultGraphicsPreset();
      localStorage.setItem('graphicsPreset', detected);
      localStorage.setItem('graphicsAutoPresetVersion', autoVersion);
      return detected;
    }
    if (saved === 'custom') return 'custom';
    if (saved && GRAPHICS_PRESETS[saved]) return saved;
  } catch (e) { /* private mode etc. */ }
  const detected = detectDefaultGraphicsPreset();
  try {
    localStorage.setItem('graphicsPreset', detected);
    localStorage.setItem('graphicsAutoPresetVersion', 'mobile-v6');
  } catch (e) {}
  return detected;
}

function setGraphicsPreset(name) {
  if (name !== 'custom' && !GRAPHICS_PRESETS[name]) return;
  try {
    localStorage.setItem('graphicsPreset', name);
    localStorage.setItem('graphicsPresetManual', '1');
    localStorage.removeItem('graphicsSafetyFallback');
    // A choice made here outranks a phone's Low for the session.
    sessionStorage.removeItem(MOBILE_SESSION_PRESET_KEY);
  } catch (e) {}
  window.location.reload();
}

// Custom preset — persisted knob-by-knob config from the Advanced
// graphics modal. Falls back to Medium if somehow corrupted.
function loadCustomGraphicsConfig() {
  try {
    const raw = localStorage.getItem('graphicsCustom');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch (e) {}
  return null;
}

function normalizeGraphicsConfig(cfg, fallback = GRAPHICS_PRESETS.medium) {
  return {
    ...fallback,
    ...cfg,
    lensFlare: cfg.lensFlare ?? fallback.lensFlare,
    lensFlareIntensity: cfg.lensFlareIntensity ?? fallback.lensFlareIntensity,
    autoExposure: cfg.autoExposure ?? fallback.autoExposure,
    autoExposureStrength: cfg.autoExposureStrength ?? fallback.autoExposureStrength
  };
}

let activeGraphicsPreset = loadGraphicsPreset();
let graphicsConfig;
if (activeGraphicsPreset === 'custom') {
  const customCfg = loadCustomGraphicsConfig();
  if (customCfg) {
    graphicsConfig = normalizeGraphicsConfig(customCfg);
  } else {
    activeGraphicsPreset = 'medium';
    graphicsConfig = GRAPHICS_PRESETS.medium;
  }
} else {
  graphicsConfig = GRAPHICS_PRESETS[activeGraphicsPreset];
}

// Mobile safety clamp. iOS Safari's WebGL ceiling is roughly 256MB of
// GPU memory; the 8K starfield panorama alone decodes to ~170MB before
// any other texture loads, which triggers the "A problem repeatedly
// occurred" tab kill before our context-loss handler can fire. Cap the
// expensive knobs aggressively here so the renderer still initialises
// on every modern phone. Users can opt into more via Advanced settings
// (with the warning that they might crash).
if (isMobileLikeDevice()) {
  graphicsConfig = {
    ...graphicsConfig,
    pixelRatio: 1,
    renderScale: 1,
    msaa: 0,
    anisotropy: Math.min(graphicsConfig.anisotropy || 4, 4),
    starfield: 'lite',
    // HDR HalfFloat composer targets double GPU memory vs 8-bit and
    // some iOS GPU drivers refuse to allocate large half-float buffers
    // at all. Drop bloom on phones — the cinematic pass still runs.
    bloom: false
  };
}

// ?pr= in screensaver mode caps the pixel ratio and drops supersampling: the
// live wallpaper passes 1, so a Retina desktop draws a quarter of its pixels.
const SCREENSAVER_PR_CAP = IS_SCREENSAVER ? Number(new URLSearchParams(window.location.search).get('pr')) || 0 : 0;
if (SCREENSAVER_PR_CAP > 0) {
  graphicsConfig = {
    ...graphicsConfig,
    pixelRatio: Math.min(graphicsConfig.pixelRatio || 1, SCREENSAVER_PR_CAP),
    renderScale: Math.min(graphicsConfig.renderScale || 1, 1),
  };
  // A live wallpaper on a high-density screen (1.4x and up) needs no
  // anti-aliasing: its pixels are already finer than an edge's stair-step,
  // and High's 4x multisampling on a 6K desktop was about a gigabyte of
  // render target and its bandwidth every frame.
  const wallpaperPage = new URLSearchParams(window.location.search).get('wallpaper') === '1';
  if (wallpaperPage && Math.min(window.devicePixelRatio || 1, graphicsConfig.pixelRatio || 1) >= 1.4) {
    graphicsConfig = { ...graphicsConfig, msaa: 0, smaa: false, fxaa: false };
  }
}

function graphicsPresetLabel(name) {
  return vt(`controls.quality.${name === 'custom' || GRAPHICS_PRESETS[name] ? name : 'medium'}.name`);
}

function qualityOptionButtons() {
  return qualityMenu ? Array.from(qualityMenu.querySelectorAll('.quality-option')) : [];
}

function focusQualityOption(preset) {
  if (!qualityMenu) return;
  const options = qualityOptionButtons();
  if (!options.length) return;
  const target = options.find((btn) => btn.dataset.preset === preset) || options.find((btn) => btn.classList.contains('active')) || options[0];
  target.focus();
}

function moveQualityFocus(direction) {
  if (!qualityMenu) return;
  const options = qualityOptionButtons();
  if (!options.length) return;
  const currentIndex = Math.max(0, options.findIndex((btn) => btn === document.activeElement));
  const nextIndex = (currentIndex + direction + options.length) % options.length;
  options[nextIndex].focus();
}

function syncQualityPicker() {
  if (qualityTriggerLabel) {
    qualityTriggerLabel.textContent = graphicsPresetLabel(activeGraphicsPreset);
  }
  if (qualityPicker) {
    qualityPicker.dataset.preset = activeGraphicsPreset;
  }
  if (qualityMenu) {
    qualityMenu.querySelectorAll('.quality-option').forEach((btn) => {
      const isActive = btn.dataset.preset === activeGraphicsPreset;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-selected', String(isActive));
    });
  }
}

function openQualityPicker() {
  if (!qualityPicker || !qualityTrigger) return;
  qualityPicker.classList.add('open');
  qualityTrigger.setAttribute('aria-expanded', 'true');
  requestAnimationFrame(() => focusQualityOption(activeGraphicsPreset));
}

function closeQualityPicker(returnFocus = false) {
  if (!qualityPicker || !qualityTrigger) return;
  qualityPicker.classList.remove('open');
  qualityTrigger.setAttribute('aria-expanded', 'false');
  if (returnFocus) qualityTrigger.focus();
}

function toggleQualityPicker() {
  if (!qualityPicker) return;
  if (qualityPicker.classList.contains('open')) closeQualityPicker();
  else openQualityPicker();
}

function chooseQualityPreset(preset) {
  if (!preset || preset === activeGraphicsPreset) {
    closeQualityPicker(true);
    return;
  }
  closeQualityPicker(true);
  setGraphicsPreset(preset);
}

// Map a live graphicsConfig back to the Advanced-modal form controls.
// Inverts the lossy compression the save path does (e.g. msaa=4 → 'msaa4',
// smaa+fxaa combos → single select value) so re-opening the modal shows
// what's actually active. Default cases fall back to what High preset
// uses so the form never shows blanks.
function populateAdvancedGraphicsForm() {
  const cfg = graphicsConfig;
  const pr = cfg.pixelRatio;
  let prSel = 'native';
  if (pr === 0.5) prSel = '0.5';
  else if (pr === 0.75) prSel = '0.75';
  else if (pr === 1) prSel = '1';

  let aaSel = 'off';
  if (cfg.msaa === 8 && cfg.smaa) aaSel = 'msaa8+smaa';
  else if (cfg.msaa === 8) aaSel = 'msaa8';
  else if (cfg.msaa === 4) aaSel = 'msaa4';
  else if (cfg.smaa && cfg.fxaa) aaSel = 'smaa+fxaa';
  else if (cfg.smaa) aaSel = 'smaa';
  else if (cfg.fxaa) aaSel = 'fxaa';

  const setVal = (id, val) => {
    const el = document.getElementById(id);
    if (el) el.value = val;
  };
  setVal('gfx-pixel-ratio', prSel);
  setVal('gfx-render-scale', String(cfg.renderScale || 1));
  setVal('gfx-aa', aaSel);
  setVal('gfx-anisotropy', String(cfg.anisotropy || 4));
  setVal('gfx-bloom', cfg.bloom ? 'on' : 'off');
  setVal('gfx-cinematic', cfg.cinematic ? 'on' : 'off');
  setVal('gfx-lens-flare', cfg.lensFlare
    ? (cfg.lensFlareIntensity >= 0.5 ? 'cinematic' : 'subtle')
    : 'off');
  setVal('gfx-auto-exposure', cfg.autoExposure
    ? (cfg.autoExposureStrength >= 0.32 ? 'camera' : 'subtle')
    : 'off');
  setVal('gfx-planet-segments', String(cfg.planetSegments || 80));
  setVal('gfx-earth-detail', String(cfg.earthWidth || 192));
  setVal('gfx-atmosphere-segments', String(cfg.atmosphereSegments || 72));
  setVal('gfx-moon-segments', String(cfg.moonSegments || 32));
  setVal('gfx-starfield', cfg.starfield || (graphicsConfig === GRAPHICS_PRESETS.low ? 'lite' : 'hi'));
  setVal('gfx-show-fps', cfg.showFps ? 'on' : 'off');
}

function openAdvancedGraphics() {
  const modal = document.getElementById('graphics-modal');
  if (!modal) return;
  populateAdvancedGraphicsForm();
  // Live toggle for "Show FPS" — flipping this in the modal applies
  // immediately, no reload needed. Other settings still need a reload
  // because they wire into one-time init paths (composer, geometry).
  const fpsSelect = document.getElementById('gfx-show-fps');
  if (fpsSelect && !fpsSelect.dataset.liveBound) {
    fpsSelect.dataset.liveBound = '1';
    fpsSelect.addEventListener('change', () => {
      const on = fpsSelect.value === 'on';
      graphicsConfig.showFps = on;
      if (fpsLabel) fpsLabel.classList.toggle('visible', on);
    });
  }
  modal.classList.add('visible');
  modal.setAttribute('aria-hidden', 'false');
}

function closeAdvancedGraphics() {
  const modal = document.getElementById('graphics-modal');
  if (!modal) return;
  modal.classList.remove('visible');
  modal.setAttribute('aria-hidden', 'true');
}

// Read the form, assemble a full graphicsConfig object matching the
// preset shape, persist it, and reload. Reload is necessary because
// most settings feed into one-time init paths (geometry, composer
// chain, renderer params).
function saveAdvancedGraphics() {
  const getVal = (id) => {
    const el = document.getElementById(id);
    return el ? el.value : null;
  };
  const prRaw = getVal('gfx-pixel-ratio');
  const pixelRatio = prRaw === 'native'
    ? Math.min(window.devicePixelRatio || 1, 2)
    : parseFloat(prRaw);

  const aa = getVal('gfx-aa');
  const msaa = aa === 'msaa4' ? 4
    : (aa === 'msaa8' || aa === 'msaa8+smaa') ? 8
    : 0;
  const smaa = aa === 'smaa' || aa === 'smaa+fxaa' || aa === 'msaa8+smaa';
  const fxaa = aa === 'fxaa' || aa === 'smaa+fxaa';

  const planetSegments = parseInt(getVal('gfx-planet-segments'), 10);
  const earthWidth = parseInt(getVal('gfx-earth-detail'), 10);
  const lensFlareMode = getVal('gfx-lens-flare') || 'subtle';
  const autoExposureMode = getVal('gfx-auto-exposure') || 'subtle';
  const custom = {
    label: 'Custom',
    pixelRatio,
    renderScale: parseFloat(getVal('gfx-render-scale')) || 1,
    msaa,
    smaa,
    fxaa,
    bloom: getVal('gfx-bloom') === 'on',
    cinematic: getVal('gfx-cinematic') === 'on',
    anisotropy: parseInt(getVal('gfx-anisotropy'), 10) || 4,
    planetSegments: planetSegments || 80,
    moonSegments: parseInt(getVal('gfx-moon-segments'), 10) || 32,
    dwarfSegments: Math.max(28, Math.floor((planetSegments || 80) * 0.7)),
    sunSegments: Math.max(96, planetSegments || 96),
    earthWidth: earthWidth || 192,
    earthHeight: Math.floor((earthWidth || 192) / 2),
    atmosphereSegments: parseInt(getVal('gfx-atmosphere-segments'), 10) || 72,
    starfield: getVal('gfx-starfield') || 'hi',
    lensFlare: lensFlareMode !== 'off',
    lensFlareIntensity: lensFlareMode === 'cinematic' ? 0.54 : (lensFlareMode === 'subtle' ? 0.32 : 0),
    autoExposure: autoExposureMode !== 'off',
    autoExposureStrength: autoExposureMode === 'camera' ? 0.36 : (autoExposureMode === 'subtle' ? 0.22 : 0),
    showFps: getVal('gfx-show-fps') === 'on'
  };

  try {
    localStorage.setItem('graphicsCustom', JSON.stringify(custom));
    localStorage.setItem('graphicsPreset', 'custom');
    localStorage.setItem('graphicsPresetManual', '1');
    localStorage.removeItem('graphicsSafetyFallback');
  } catch (e) {}
  window.location.reload();
}

function resetAdvancedGraphics() {
  try {
    localStorage.removeItem('graphicsCustom');
    // Revert to the auto-detected preset rather than leaving the user
    // stranded on 'custom' with no custom config.
    localStorage.removeItem('graphicsPreset');
    localStorage.removeItem('graphicsPresetManual');
    localStorage.removeItem('graphicsSafetyFallback');
    localStorage.removeItem('graphicsAutoPresetVersion');
  } catch (e) {}
  window.location.reload();
}

// Global state
let audioPlaying = false;
let audioAutoStarted = false;
let scene, camera, renderer, controls, composer, cinematicPass, composerRenderPass;
// The ISS's Cupola view (src/ui/cupola.js), made once the viewer is up.
let cupolaView = null;
// Climate mode: NASA and NOAA climate data on the Earth (src/climate/).
let climateMode = null;
let sunSwitch = null;
let sun, planets = {}, moons = {}, orbitLines = [], moonOrbitLines = {};
// Spacetime mode, once loaded (loadSpacetimeMode).
let spacetimeMode = null;
let spacetimeLoad = null;
// Scattered drifting meteoroids / asteroids / comet (CC BY 4.0 models,
// credited on /credits). Loaded async, website viewer + screensaver only.
let debrisField = null;
// The debris shell starts at 5000 scene units from the Sun (src/debris.js),
// past Neptune at the artistic scale. Its seven models are 10 MB to fetch and
// about 45 MB of GPU memory, so a phone loads them when the camera settles
// out that way or flies, and lets them go after half a minute back in the
// inner system (docs/mobile-memory.md).
const MOBILE_DEBRIS_WAKE_DISTANCE = 2500;
let mobileDebrisInsideSince = 0;
function updateMobileDebris() {
  if (IS_GAME || !scene || !camera) return;
  const d = camera.position.length();
  if (!debrisField) {
    // Settled out there, not passing through on the way to the galaxy.
    if (flyMode || (!realisticScaleMode && !isAnimating && d > MOBILE_DEBRIS_WAKE_DISTANCE && d < MOBILE_DEBRIS_WAKE_DISTANCE * 16)) {
      debrisField = createDebrisField(scene, { maxTextureSize: 512, renderer, camera });
    }
    return;
  }
  if (flyMode || (!realisticScaleMode && d > MOBILE_DEBRIS_WAKE_DISTANCE / 2)) { mobileDebrisInsideSince = 0; return; }
  const now = performance.now();
  if (!mobileDebrisInsideSince) mobileDebrisInsideSince = now;
  else if (now - mobileDebrisInsideSince > 30000) {
    debrisField.dispose();
    debrisField = null;
    mobileDebrisInsideSince = 0;
  }
}

// Procedural-surface registry. Ice giants (Uranus, Neptune) are
// rendered using the proceduralGasGiant shader instead of the
// textured surface material to eliminate the equirectangular-seam
// problem at the source — no texture, no UV wrap, no seam possible.
// Entries are { mesh, uniforms } per planet key; animate() pushes
// uTime + uSunDir into each per frame.
const proceduralPlanetSurfaces = {};
// Scratch vectors reused per frame in the procedural-surface sun-
// direction update so the hot path doesn't allocate.
const _procSunPosScratch = new THREE.Vector3();
const _procBodyPosScratch = new THREE.Vector3();
const _procSunDirScratch = new THREE.Vector3();
const _venusBodyWorld = new THREE.Vector3();
const _venusSunWorld = new THREE.Vector3();

// Per-ice-giant palette and band tuning for the procedural surface.
// Pulled out of the per-planet branch in createPlanets so it's easy
// to dial in from outside (the planet tuner can poke these too).
const PROCEDURAL_PLANET_CONFIGS = {
  uranus: {
    // Uranus is the most featureless gas/ice giant in real imagery —
    // Voyager 2 and Hubble show a near-uniform pale cyan disc with
    // only subtle banding. Low band count + low turbulence + a
    // palette that barely differentiates belt from zone keeps the
    // shader output near-uniform, like the real planet, while the
    // seamless 3D math removes the wrap artifact.
    paletteDeep:  0x8fb8c8,
    paletteBelt:  0xa5c3d4,
    paletteZone:  0xe0f0f7,
    paletteStorm: 0xa5c3d4,
    bandCount:    4.0,
    turbulence:   0.45,
    stormSize:    0.001,
    limbDarken:   0.85,
    rimGlow:      0.50,
    cloudDetailStrength: 0.12,  // barely any: stronger aliases into specks
    cloudDetailFreq:     5.0,
    bandChaos:           0.0   // Uranus is near-uniform; no chaos
  },
  neptune: {
    // Neptune has visible cloud bands and the Great Dark Spot.
    // Deeper azure palette than Uranus, moderate band count for the
    // zonal striping, dark-spot storm placed at -22° lat / +50° lon
    // matching the historical Voyager 2 sighting position.
    // Deep, even azure with a few soft bands: Voyager's Neptune. The old
    // 14-band, near-white-zone palette read as candy stripes, and the
    // strong detail noise aliased into dark speckles at any distance.
    paletteDeep:  0x1f3b73,
    paletteBelt:  0x2f5596,
    paletteZone:  0x4f7bbd,
    paletteStorm: 0x142852,
    bandCount:    6.0,
    turbulence:   0.6,
    stormLatDeg:  -22,
    stormLonDeg:  50,
    stormSize:    0.32,
    limbDarken:   0.85,
    rimGlow:      0.55,
    cloudDetailStrength: 0.35,
    cloudDetailFreq:     6.0,
    bandChaos:           0.25  // some cloud structure around the dark spot
  }
};

// Multi-star-system runtime state. `remoteSystems` holds handles for
// every non-Sol system the catalog knows about (currently just Alpha
// Centauri; later, procedural systems register here too). Sol is
// rendered by the legacy pipeline and isn't kept here.
const remoteSystems = {};
// Which system the camera is currently 'in'. Updated by jumpToSystem.
// Persists in localStorage via the registry so reloads return the
// player to the system they left from.
let activeSystemId = 'sol';
// Cached reference position for whichever non-Sol system the camera
// is currently inside, used for the floating-origin-light approach
// where the warp jump teleports the camera near the system's primary
// star but the system Group stays parked at its catalog scenePosition.
let activeSystemTarget = null;
// Latch so a jump in-flight can't be re-triggered by spamming the
// star map.
let starMapJumpInFlight = false;
// Secret 12th-planet handle. Built procedurally with no textures,
// hidden from every UI list, autopilot menu, and planet picker.
// Discoverable via the URL hash `#nibiru` or `window.__nibiru.goto()`.
let nibiruHandle = null;
let satelliteObjects = [], neoObjects = [], cometObjects = [];
// The real-satellite layer (src/satellites/index.js), made after the ISS.
let realSatellites = null;
let cometOrbitLines = [];
let satelliteOrbitLines = [];
let beltBodyObjects = [];
let vanAllenBeltsOn = false;       // science mode's belts (setVanAllenBeltsVisible)
// The magnetosphere layer (src/magnetosphere/), once loaded.
let magnetosphereMode = null;
let magnetosphereLoading = null;
let earthAuroras = null;
// The space weather card and links (src/spaceweather/ui.js), once built.
let spaceWeatherUI = null;
let wormholeObject = null;
// Invisible Object3D parked at the galactic centre. Autopilot uses it
// as the "Milky Way" destination so a cruise + orbit naturally pulls
// the camera well outside the disc (25M-unit radius) for a vista shot.
let milkyWayVista = null;
// The wormhole's gravitational lens (src/wormholeLens.js).
let wormholeLens = null;
// The 8K star panorama, also read by the UFO's hull reflection.
let wormholeStarMapTexture = null;
let wormholeStarMapFallbackTexture = null;
let ufoStarReflectionMaterial = null;
let monolith; // 2001 Space Odyssey easter egg
let monolithDiscovered = false; // First-click cinematic gate

// === PHOTO MODE ===
// Cinematic-camera + post-processing controls overlay. Lets the user
// dial in FOV, roll, exposure, contrast, saturation, vignette, hide
// UI for a clean shot, and capture the canvas to a PNG. Restores the
// pre-entry state on exit so toggling in/out is non-destructive.
let photoModeActive = false;
// A famous photo's exposure against the viewer's while it is held (2 **
// photo.exposure stops, src/photos/mode.js): long exposures are what show
// the backlit rings and the dot of the Earth. Read by applyCinematicUniforms.
let famousPhotoExposure = 1;
// And its film floor (photo.blackPoint), the cinematic pass's photoBlackPoint.
let famousPhotoBlackPoint = 0;
// And its colour (photo.saturation), against the viewer's saturation.
let famousPhotoSaturation = 1;
let photoModePrevState = null;
let photoModeCustomRoll = 0;

function enterPhotoMode() {
  if (photoModeActive) return;
  photoModeActive = true;
  // Snapshot state we'll restore on exit. Camera FOV + up vector get
  // mutated for roll, post-process uniforms for exposure/contrast.
  photoModePrevState = {
    fov: camera ? camera.fov : 45,
    cameraUp: camera ? camera.up.clone() : null,
    exposure: cinematicPass && cinematicPass.uniforms.exposure
      ? cinematicPass.uniforms.exposure.value
      : 1,
    contrast: cinematicPass && cinematicPass.uniforms.contrast
      ? cinematicPass.uniforms.contrast.value
      : 1,
    saturation: cinematicPass && cinematicPass.uniforms.saturation
      ? cinematicPass.uniforms.saturation.value
      : 1,
    vignetteIntensity: cinematicPass && cinematicPass.uniforms.vignetteIntensity
      ? cinematicPass.uniforms.vignetteIntensity.value
      : 0.3,
    timeSpeed,
    orbitsVisible,
    starFieldVisible: true
  };
  photoModeCustomRoll = 0;
  if (photoModePanel) {
    photoModePanel.classList.add('is-visible');
    photoModePanel.setAttribute('aria-hidden', 'false');
  }
  if (photoModeToggle) photoModeToggle.classList.add('playing');
  if (photoModeLabel) photoModeLabel.textContent = vt('controls.photo.exit');
  // Twin button in the fly-controls panel — kept in sync so it shows
  // EXIT PHOTO whichever mode the user is in when they enter.
  const flyPhotoBtn = document.getElementById('fly-photo-mode-btn');
  if (flyPhotoBtn) {
    flyPhotoBtn.textContent = vt('controls.photo.exit');
    flyPhotoBtn.classList.add('playing');
  }
  // Push current values into the slider DOM so the panel reflects state.
  syncPhotoModePanelValues();
}

function exitPhotoMode() {
  if (!photoModeActive) return;
  photoModeActive = false;
  // Restore everything we mutated.
  if (photoModePrevState && camera) {
    camera.fov = photoModePrevState.fov;
    camera.updateProjectionMatrix();
    if (photoModePrevState.cameraUp) camera.up.copy(photoModePrevState.cameraUp);
  }
  if (photoModePrevState && cinematicPass && cinematicPass.uniforms) {
    if (cinematicPass.uniforms.exposure) cinematicPass.uniforms.exposure.value = photoModePrevState.exposure;
    if (cinematicPass.uniforms.contrast) cinematicPass.uniforms.contrast.value = photoModePrevState.contrast;
    if (cinematicPass.uniforms.saturation) cinematicPass.uniforms.saturation.value = photoModePrevState.saturation;
    if (cinematicPass.uniforms.vignetteIntensity) cinematicPass.uniforms.vignetteIntensity.value = photoModePrevState.vignetteIntensity;
  }
  if (photoModePrevState) {
    timeSpeed = photoModePrevState.timeSpeed;
  }
  document.body.classList.remove('photo-mode-clean');
  if (photoModePanel) {
    photoModePanel.classList.remove('is-visible');
    photoModePanel.setAttribute('aria-hidden', 'true');
  }
  if (photoModeToggle) photoModeToggle.classList.remove('playing');
  if (photoModeLabel) photoModeLabel.textContent = vt('controls.photo.label');
  const flyPhotoBtn = document.getElementById('fly-photo-mode-btn');
  if (flyPhotoBtn) {
    flyPhotoBtn.textContent = vt('controls.photo.label');
    flyPhotoBtn.classList.remove('playing');
  }
  photoModePrevState = null;
}

function syncPhotoModePanelValues() {
  const ids = {
    'pm-fov':       camera ? camera.fov : 45,
    'pm-roll':      photoModeCustomRoll,
    'pm-exposure':  cinematicPass && cinematicPass.uniforms.exposure ? cinematicPass.uniforms.exposure.value : 1,
    'pm-contrast':  cinematicPass && cinematicPass.uniforms.contrast ? cinematicPass.uniforms.contrast.value : 1,
    'pm-saturation':cinematicPass && cinematicPass.uniforms.saturation ? cinematicPass.uniforms.saturation.value : 1,
    'pm-vignette':  cinematicPass && cinematicPass.uniforms.vignetteIntensity ? cinematicPass.uniforms.vignetteIntensity.value : 0.3
  };
  Object.entries(ids).forEach(([id, value]) => {
    const el = document.getElementById(id);
    if (el) el.value = value;
    const valEl = document.getElementById(`${id}-val`);
    if (valEl) valEl.textContent = formatPhotoModeValue(id, value);
  });
}

function formatPhotoModeValue(id, value) {
  if (id === 'pm-fov')  return `${Math.round(value)}°`;
  if (id === 'pm-roll') return `${Math.round(value)}°`;
  return Number(value).toFixed(2);
}

function applyPhotoModeRoll(degrees) {
  if (!camera || !controls) return;
  photoModeCustomRoll = degrees;
  // Roll = rotate camera.up around the forward axis (camera→target).
  // OrbitControls re-evaluates camera.up each update so we set it
  // directly to the rolled vector. Start from world-up so the roll
  // is absolute, not cumulative across slider changes.
  const forward = new THREE.Vector3().subVectors(controls.target, camera.position).normalize();
  const worldUp = new THREE.Vector3(0, 1, 0);
  const rolled = worldUp.clone().applyAxisAngle(forward, THREE.MathUtils.degToRad(degrees));
  camera.up.copy(rolled);
}

function capturePhoto() {
  if (!renderer) return;
  // Render once into the on-screen canvas (we're already rendering
  // every frame, but force a fresh draw so the screenshot doesn't
  // include any half-state from the click event itself).
  if (composer) composer.render();
  else renderer.render(scene, camera);
  const canvas = renderer.domElement;
  // Some browsers gate toBlob behind preserveDrawingBuffer; try
  // toDataURL as a robust fallback.
  try {
    canvas.toBlob((blob) => {
      if (!blob) {
        downloadDataURL(canvas.toDataURL('image/png'));
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `solar-system-${Date.now()}.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, 'image/png');
  } catch {
    downloadDataURL(canvas.toDataURL('image/png'));
  }
}

function downloadDataURL(dataUrl, name = `solar-system-${Date.now()}.png`) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = name;
  a.click();
}

// Save the current view as a wallpaper-resolution PNG. Unlike
// capturePhoto() — which grabs the on-screen canvas at its current
// size — this temporarily boosts the renderer's pixel ratio so the
// output matches the device's native screen resolution along the long
// edge, keeping the composition the user sees but rendering it crisp
// enough to use as a desktop/phone background. The view aspect is left
// untouched (camera projection unchanged), only the buffer scales up.
//
// Mobile gets a hard cap on the upscale and total pixel count: this
// repo has a history of OOM crashes when allocating large GPU buffers
// on phones, and a full-resolution offscreen render is exactly that
// kind of allocation.
function saveWallpaper() {
  if (!renderer) return;
  const canvas = renderer.domElement;
  const prevPR = renderer.getPixelRatio();
  const w = window.innerWidth;
  const h = window.innerHeight;
  const dpr = window.devicePixelRatio || 1;
  const rs = Math.max(1, graphicsConfig.renderScale || 1);

  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    || (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);

  // Aim the long edge of the output at the screen's native long edge.
  const screenLong = Math.max(window.screen.width || w, window.screen.height || h) * dpr;
  const viewLong = Math.max(w, h);
  let targetPR = screenLong / viewLong;
  // Never render below what's already on screen; cap the upscale to
  // protect GPU memory (tighter on mobile).
  const maxPR = isMobile ? Math.min(prevPR * 1.5, 2) : 4;
  targetPR = Math.max(prevPR, Math.min(targetPR, maxPR));
  // Final guard: a hard ceiling on total drawing-buffer pixels.
  const maxPixels = isMobile ? 6e6 : 40e6; // ~6MP mobile, ~40MP desktop
  while (w * h * targetPR * targetPR * rs * rs > maxPixels && targetPR > prevPR) {
    targetPR -= 0.25;
  }
  if (targetPR < prevPR) targetPR = prevPR;

  const restore = () => {
    // onWindowResize() reads renderer.getPixelRatio(), so reset the
    // pixel ratio first, then let it rebuild every size-dependent piece
    // (composer targets, FXAA texel uniforms, orbit line resolutions).
    // The composer caches its own pixel ratio, so reset that too before
    // onWindowResize's composer.setSize call re-multiplies by it.
    renderer.setPixelRatio(prevPR);
    if (composer) composer.setPixelRatio(prevPR);
    onWindowResize();
  };

  const filename = `solar-system-wallpaper-${Date.now()}.png`;

  try {
    renderer.setPixelRatio(targetPR);
    renderer.setSize(w, h); // CSS size unchanged; buffer scales with PR
    if (composer) {
      // composer.setSize() re-multiplies by the composer's cached pixel
      // ratio internally, so update that ratio and hand it CSS dims —
      // passing physical pixels here double-applied the ratio and
      // allocated ratio² targets (the Chrome black-screen class fixed
      // at the setupPostProcessing init site).
      // cw/ch = TRUE physical pixels, for the texel-size uniforms only.
      const cw = w * targetPR * rs;
      const ch = h * targetPR * rs;
      composer.setPixelRatio(targetPR);
      composer.setSize(w * rs, h * rs);
      composer.passes.forEach((pass) => {
        if (pass.material && pass.material.uniforms && pass.material.uniforms.texelSize) {
          pass.material.uniforms.texelSize.value.set(1 / cw, 1 / ch);
        }
        if (pass.material && pass.material.uniforms && pass.material.uniforms.resolution
            && pass.material.fragmentShader && pass.material.fragmentShader.indexOf('FXAA') !== -1) {
          pass.material.uniforms.resolution.value.set(1 / cw, 1 / ch);
        }
      });
      composer.render();
    } else {
      renderer.render(scene, camera);
    }

    // toBlob snapshots the canvas synchronously, so it's safe to restore
    // the on-screen size in the callback once the snapshot is taken.
    try {
      canvas.toBlob((blob) => {
        if (!blob) {
          downloadDataURL(canvas.toDataURL('image/png'), filename);
          restore();
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        restore();
      }, 'image/png');
    } catch {
      downloadDataURL(canvas.toDataURL('image/png'), filename);
      restore();
    }
  } catch (err) {
    console.error('Wallpaper export failed', err);
    restore();
  }
}

function setupPhotoModeControls() {
  const bind = (id, handler, format) => {
    const el = document.getElementById(id);
    const valEl = document.getElementById(`${id}-val`);
    if (!el) return;
    el.addEventListener('input', () => {
      const v = parseFloat(el.value);
      handler(v);
      if (valEl) valEl.textContent = format ? format(v) : formatPhotoModeValue(id, v);
    });
  };

  bind('pm-fov', (v) => {
    if (!camera) return;
    camera.fov = v;
    camera.updateProjectionMatrix();
  });

  bind('pm-roll', applyPhotoModeRoll);

  bind('pm-exposure', (v) => {
    if (cinematicPass && cinematicPass.uniforms.exposure) {
      cinematicPass.uniforms.exposure.value = v;
      adaptiveExposure = v;
    }
  });
  bind('pm-contrast', (v) => {
    if (cinematicPass && cinematicPass.uniforms.contrast) {
      cinematicPass.uniforms.contrast.value = v;
      adaptiveContrast = v;
    }
  });
  bind('pm-saturation', (v) => {
    if (cinematicPass && cinematicPass.uniforms.saturation) {
      cinematicPass.uniforms.saturation.value = v;
      adaptiveSaturation = v;
    }
  });
  bind('pm-vignette', (v) => {
    if (cinematicPass && cinematicPass.uniforms.vignetteIntensity) {
      cinematicPass.uniforms.vignetteIntensity.value = v;
      adaptiveVignette = v;
    }
  });

  const cleanToggle = document.getElementById('pm-clean-toggle');
  if (cleanToggle) {
    cleanToggle.addEventListener('click', () => {
      const isClean = document.body.classList.toggle('photo-mode-clean');
      cleanToggle.classList.toggle('is-active', isClean);
    });
  }

  const orbitsTogglePM = document.getElementById('pm-orbits-toggle');
  if (orbitsTogglePM) {
    orbitsTogglePM.addEventListener('click', () => {
      orbitsVisible = !orbitsVisible;
      updateOrbitsVisibility();
      orbitsTogglePM.classList.toggle('is-active', orbitsVisible);
      if (orbitsLabel) orbitsLabel.textContent = vt(orbitsVisible ? 'controls.orbits.on' : 'controls.orbits.off');
      if (orbitsToggle) orbitsToggle.classList.toggle('playing', orbitsVisible);
    });
  }

  const starsTogglePM = document.getElementById('pm-stars-toggle');
  if (starsTogglePM) {
    starsTogglePM.addEventListener('click', () => {
      const skybox = scene && scene.getObjectByName('skybox');
      if (skybox) {
        skybox.visible = !skybox.visible;
        starsTogglePM.classList.toggle('is-active', skybox.visible);
      }
    });
  }

  const pauseTogglePM = document.getElementById('pm-pause-toggle');
  if (pauseTogglePM) {
    pauseTogglePM.addEventListener('click', () => {
      const isPaused = pauseTogglePM.classList.toggle('is-active');
      timeSpeed = isPaused ? 0 : (photoModePrevState ? photoModePrevState.timeSpeed : 0.5);
    });
  }

  const resetBtn = document.getElementById('pm-reset');
  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      // Reset all sliders to their default values without exiting.
      if (camera) {
        camera.fov = 45;
        camera.updateProjectionMatrix();
        camera.up.set(0, 1, 0);
      }
      photoModeCustomRoll = 0;
      if (cinematicPass && cinematicPass.uniforms) {
        if (cinematicPass.uniforms.exposure) cinematicPass.uniforms.exposure.value = 1;
        if (cinematicPass.uniforms.contrast) cinematicPass.uniforms.contrast.value = 1.05;
        if (cinematicPass.uniforms.saturation) cinematicPass.uniforms.saturation.value = 1.05;
        if (cinematicPass.uniforms.vignetteIntensity) cinematicPass.uniforms.vignetteIntensity.value = 0.3;
      }
      syncPhotoModePanelValues();
    });
  }

  const captureBtn = document.getElementById('pm-screenshot');
  if (captureBtn) {
    captureBtn.addEventListener('click', capturePhoto);
  }
}

// TMA-1 cinematic discovery overlay. Fades the screen to black,
// presents a Kubrick-style centred caption, then fades back. Only
// fires the first time the user clicks the monolith.
function showMonolithDiscovery() {
  const overlay = document.getElementById('tma-discovery-overlay');
  if (!overlay) return;
  overlay.classList.remove('is-fading-out');
  // Force a reflow so the transition replays even if the class was
  // toggled rapidly (prevents the second-click case from no-oping).
  void overlay.offsetWidth;
  overlay.classList.add('is-fading-in');
  setTimeout(() => {
    overlay.classList.remove('is-fading-in');
    overlay.classList.add('is-fading-out');
    setTimeout(() => {
      overlay.classList.remove('is-fading-out');
    }, 2200);
  }, 5200);
}
let raycaster, mouse;
let mouseScreenX = 0, mouseScreenY = 0; // Screen coordinates for hover label
let sceneClickStart = null;
let selectedObject = null;
let hoveredObject = null;
let hoverLabel = null; // DOM element for hover label
let isAnimating = false;
let activeCameraTween = null;
// exitFlyMode's 2 s return-to-overview tweens (camera.position +
// controls.target). Stored so enterFlyMode / focusOnObject can kill
// them mid-flight instead of letting them fight the new camera motion;
// the generation counter guards the delayed onComplete from clobbering
// a selection made during the tween window.
let exitFlyTweens = [];
let exitFlyTweenGen = 0;
function killExitFlyTweens() {
  exitFlyTweenGen++;
  exitFlyTweens.forEach((t) => { try { t.kill(); } catch (_) {} });
  exitFlyTweens = [];
}
// Overwritten at start by initTimePresets, which opens on Real: the sky as
// it is right now, one simulated second per real second.
let timeSpeed = 0.01;
let scaleMultiplier = 3;
let clock;
let loadedTextures = {};
let textureLoader;
let gltfLoader;
let gltfLoaderPromise;
let textureLoadPromises = {};
let textureUrlLoadPromises = {};
let starfieldMaterial = null;
let starCatalog = null;
let faintStars = null;
let planetPoints = null; // src/sky/planetPoints.js: far planets as points of light, in both scales
let deepSkyObjects = null;
let constellations = null;
let galaxy = null;
let blackHole = null;
let warpEffect = null;
// Interstellar jump transition (streak field + tunnel), see hyperspace.js.
let hyperspace = null;
let wormholeTunnel = null;
let wormholeTunnelInFlight = false;
let gargantuaReturnMouth = null;
let lastWormholeTriggerMs = 0;
let activeWormholeFlight = null;
// Accumulated galaxy time, in seconds, scaled by the timeSpeed slider.
// Drives the GPU-side rotation in the galaxy's vertex shader, so the
// Milky Way responds to time-speed boosts even though the solar system
// uses a separate clock for orbital motion.
let galaxyClockTime = 0;
// The 3D galaxy's share of the view (galaxy.tick's opacity), 1 from outside
// the Milky Way, where the Sun's glare goes (updateLensFlare).
let galaxyViewShare = 0;
// Wrap period for galaxyClockTime so the shader-side omega·t angles
// never outgrow float32 precision at high time speed. 8250 s is the
// exact common period of the two coherent rotating structures in
// galaxy.js: the spiral arm pattern (omega = 2π/330 rad/s → 25 full
// turns) and the bar (39 turns), derived from its calibration of
// omega(R_sun) = 2π/300 at 8 kpc with vFlat = 220 km/s. Off-arm stars
// jump phase along their own circular orbits at the wrap, which is
// statistically invisible; the BH disc noise re-seeds once per period.
const GALAXY_CLOCK_PERIOD = 8250;
let prefetchTimer = null;
let prefetchTargetKey = null;
let prefetchSerial = 0;
let deepSpaceBodiesLoaded = false;
let deepSpaceBodiesVisible = false;
let baseDistances = {}; // Store original distances for scaling
let fpsLabel = null;
let fpsFrames = 0;
let fpsAccum = 0;
let lensFlareOpacity = 0;
let lensFlareSpaceAmount = 0;
let adaptiveExposure = 1;
let adaptiveGlare = 0;
let adaptiveContrast = 1.05;
let adaptiveSaturation = 1.05;
let adaptiveVignette = 0.3;

const lensFlareSunPos = new THREE.Vector3();
const lensFlareProjected = new THREE.Vector3();
const lensFlareToSun = new THREE.Vector3();
const lensFlareCameraDir = new THREE.Vector3();
const lensFlareBodyPos = new THREE.Vector3();
const exposureSunPos = new THREE.Vector3();
const exposureProjected = new THREE.Vector3();
const exposureToSun = new THREE.Vector3();
const exposureCameraDir = new THREE.Vector3();

// Per-moon world position scratch for the umbra (planet-shadow) test —
// moons live inside their parent's tilted moonFrame, so the local
// position is no longer a world-space offset from the planet centre.
const _moonUmbraWorld = new THREE.Vector3();

// Reused inverse matrix for gas-giant local-space conversions
// (ring-shadow + lightning both need it). One per-frame allocation
// avoided per gas giant per frame.
const _gasGiantInverseMatrix = new THREE.Matrix4();

const MAX_PLANET_ORBIT_STEP = 0.16;
const MAX_MOON_ORBIT_STEP = 0.2;
const MAX_SMALL_BODY_ORBIT_STEP = 0.18;
const MAX_SPIN_STEP = 0.35;
const MAX_SHADER_PHASE_STEP = 0.28;
const FOCUS_FOLLOW_DAMPING = 28;
const MOON_TRANSIT_SHADOW_SCALE = 1.08;
const EARTH_MOON_TRANSIT_SHADOW_RADIUS_RATIO = 0.045;
const FULL_TURN = Math.PI * 2;
const SCENE_UNITS_PER_AU = 150;
const UFO_SCAN_MIN_RANGE = 24;
const UFO_SCAN_RADIUS_MULTIPLIER = 9.5;
const ALIEN_GLYPHS = ['⟁', '⟊', '⟒', '⟐', '⌬', '⌁', '⧖', '⋔', '∴', '⩚', '◌', '⊙', '⟡', '⟜'];

const alienScanBodyPos = new THREE.Vector3();
const alienScanProjected = new THREE.Vector3();
const alienScanEdgeProjected = new THREE.Vector3();
const alienScanCameraDir = new THREE.Vector3();
const alienScanCameraRight = new THREE.Vector3();
const alienScanCameraUp = new THREE.Vector3();
const alienScanToBody = new THREE.Vector3();
let alienScanStrength = 0;
let alienScanSightLock = 0;
let alienScanTicker = 0;
let alienScanGlyphPhase = 0;
let alienScanLastTarget = '';
let alienScanCurrentTarget = null;

// Scratch vectors for the focus-follow block in animate(). Allocating
// these per frame (which is what happened when this code used .clone()
// and new Vector3()) costs ~5 GC-hot allocations per render at 60 FPS,
// which adds up to noticeable jank during long sessions.
const _focusObjPos = new THREE.Vector3();
const _focusTargetDelta = new THREE.Vector3();
const _focusFromBody = new THREE.Vector3();
const _focusSafeDir = new THREE.Vector3();
// Previous world position of the focused object — used by the
// rigid-follow path so we can compute the body's per-frame motion
// delta and rigid-shift the camera + target by exactly that,
// preserving the user's panning offset instead of snapping the
// target back to the body's centre every frame (which was the
// wobble source at ISS close zoom).
const _focusPrevObjPos = new THREE.Vector3();
let _focusPrevObj = null;
// The body a focus flight has just landed on. The flight aims at where the
// body was last placed, which a craft or probe has left by the time the
// follow starts (Webb at true scale moves its own size in a frame at 10 fps),
// so the first follow frame puts the target back on it, the camera with it.
let _focusLandedOn = null;
// ISS-specific co-orbital follow scratch. Tracks the ISS-from-Earth
// direction so we can derive the per-frame orbital rotation around
// Earth and apply it to camera + target — keeping the user's chosen
// framing (e.g. "Earth below, ISS centred") stable as the station
// sweeps around. Without this the rigid-by-delta follow translates
// the camera tangentially each frame, so Earth slowly slides out of
// the composition.
const _issPrevWorldOffset = new THREE.Vector3();
const _issWorldOffset = new THREE.Vector3();
const _issEarthPos = new THREE.Vector3();
const _issOrbitalDeltaQuat = new THREE.Quaternion();
const _issCamRel = new THREE.Vector3();
const _issTargetRel = new THREE.Vector3();
// The camera's up eases to the station's local vertical while the ISS is
// followed (the horizon stays level round the lap), and goes back to world
// up as soon as anything else is focused.
let _issUpActive = false;
const _scaleSwitchBodyPos = new THREE.Vector3();
const _scaleSwitchFallback = new THREE.Vector3();
// An instant scale switch keeps the camera at its framing of a body, but the
// bodies only move to the new scale's positions in the next frame's planet
// pass; the camera is placed there, right after that pass (applyScaleAnchor).
let _scaleAnchor = null;
const _scaleAnchorPos = new THREE.Vector3();
function setScaleAnchor(body, offset) {
  _scaleAnchor = { body, offset: offset.clone() };
}
function applyScaleAnchor() {
  if (!_scaleAnchor) return;
  const { body, offset } = _scaleAnchor;
  _scaleAnchor = null;
  body.getWorldPosition(_scaleAnchorPos);
  camera.position.copy(_scaleAnchorPos).add(offset);
  controls.target.copy(_scaleAnchorPos);
  camera.lookAt(controls.target);
}
// The ISS fly-in (focusOnObject): scratch, and how far above the station the
// great-circle leg ends, in multiples of the final framing offset.
const ISS_FLY_FAR = 60;
const _issFlyEarth = new THREE.Vector3();
const _issFlyRel0 = new THREE.Vector3();
const _issFlyFar = new THREE.Vector3();
const _issFlyRel = new THREE.Vector3();
const _issFlyDir = new THREE.Vector3();
const _issFlyMacro = new THREE.Vector3();
const _issFlyDolly = new THREE.Vector3();
const _issFlyUp0 = new THREE.Vector3();
const _issFlyRadial = new THREE.Vector3();
// Turns one direction toward another along a great circle. A turn that
// starts past a right angle goes about an axis fixed when it starts: found
// afresh every frame (setFromUnitVectors) the axis swings wildly while the
// two are nearly opposite, the commonest case for a camera going round to
// the far side of a planet, and the turn jumps. at() then follows an end
// that moves: the angle round the fixed axis to it (kept continuous through
// a half turn), then the small rest of the way out of that plane, both
// scaled by k, so k = 1 lands exactly on it. A smaller turn takes the
// shortest way each frame, which is steady there (a fixed axis is not: with
// the two nearly together it is arbitrary, and an end drifting across it
// flips the turn).
const _gcIdentity = new THREE.Quaternion();
const _gcIn = new THREE.Vector3();
const _gcTo = new THREE.Vector3();
const _gcCross = new THREE.Vector3();
const _gcQuat = new THREE.Quaternion();
const _gcRest = new THREE.Quaternion();
const _gcRestK = new THREE.Quaternion();
class GreatCircleTurn {
  constructor() {
    this.from = new THREE.Vector3(0, 0, 1);
    this.axis = new THREE.Vector3(0, 1, 0);
    this.angle = 0;
    this.fixed = false;
  }
  start(from, to, upHint) {
    this.from.copy(from).normalize();
    _gcTo.copy(to).normalize();
    this.fixed = this.from.dot(_gcTo) < 0;
    this.axis.crossVectors(this.from, _gcTo);
    if (this.axis.lengthSq() < 1e-8 && upHint) this.axis.copy(upHint).cross(this.from);
    if (this.axis.lengthSq() < 1e-8) this.axis.set(0, 1, 0).cross(this.from);
    if (this.axis.lengthSq() < 1e-8) this.axis.set(1, 0, 0).cross(this.from);
    this.axis.normalize();
    this.angle = 0;
    this.at(_gcIn, _gcTo, 0);
    return this;
  }
  // The whole turn so far, in radians (for sizing a lift over a long one).
  get size() { return Math.abs(this.angle); }
  at(out, to, k) {
    _gcTo.copy(to).normalize();
    if (!this.fixed) {
      this.angle = this.from.angleTo(_gcTo);
      _gcRest.setFromUnitVectors(this.from, _gcTo);
      _gcRestK.slerpQuaternions(_gcIdentity, _gcRest, k);
      return out.copy(this.from).applyQuaternion(_gcRestK);
    }
    _gcIn.copy(_gcTo).addScaledVector(this.axis, -_gcTo.dot(this.axis));
    if (_gcIn.lengthSq() > 1e-12) {
      _gcIn.normalize();
      let angle = Math.atan2(this.axis.dot(_gcCross.crossVectors(this.from, _gcIn)), this.from.dot(_gcIn));
      while (angle - this.angle > Math.PI) angle -= Math.PI * 2;
      while (angle - this.angle < -Math.PI) angle += Math.PI * 2;
      this.angle = angle;
    } else {
      _gcIn.copy(this.from).applyAxisAngle(this.axis, this.angle);
    }
    _gcQuat.setFromAxisAngle(this.axis, this.angle * k);
    _gcRest.setFromUnitVectors(_gcIn, _gcTo);
    _gcRestK.slerpQuaternions(_gcIdentity, _gcRest, k);
    return out.copy(this.from).applyQuaternion(_gcQuat).applyQuaternion(_gcRestK);
  }
}
// The ISS fly-in's two turns: the camera round the Earth, and the view to
// the station.
const _issFlyArc = new GreatCircleTurn();
const _issLookTurn = new GreatCircleTurn();
const _issLookTo = new THREE.Vector3();
// How high the great-circle leg climbs when the station is far round the
// Earth: its top is at least 1 + ISS_FLY_LIFT x the angle (radians) Earth
// radii from the centre, so a trip to the far side goes up and over instead
// of skimming the ground at thousands of kilometres a second.
const ISS_FLY_LIFT = 0.6;
function isIssObject(object) {
  return Boolean(object && object.userData && object.userData.isSatellite && object.userData.name === 'ISS');
}
function releaseIssCameraUp() {
  if (!_issUpActive) return;
  camera.up.set(0, 1, 0);
  _issUpActive = false;
}
// Satellite inspection uses a floating render origin. The canonical
// camera/controls stay in normal solar-system coordinates, while the
// composer render pass receives this proxy camera with the selected
// satellite at local origin.
const _originShiftVec = new THREE.Vector3();
const _satelliteLocalRenderCamera = new THREE.PerspectiveCamera();

// World-space uniforms that must be patched in lockstep with the
// pre-render origin shift, otherwise lighting (sunPosition) and
// shadow ray-traces (planetCenter, parentPlanetCenter,
// moonShadowPositions) point at the wrong world location and the
// shifted scene renders dark / wrong.
//
// Reused scratch arrays so we don't allocate per frame.
const _shiftableVec3Uniforms = [];     // entries: { uniform: { value: Vector3 } }
const _shiftableVec3ArrayUniforms = []; // entries: { uniform: { value: Vector3[] } }
const SHIFTABLE_VEC3_NAMES = ['sunPosition', 'planetCenter', 'planetPosition', 'parentPlanetCenter', 'uEclipseEarthCenter', 'uEclipseMoonCenter'];
const SHIFTABLE_VEC3_ARRAY_NAMES = ['moonShadowPositions'];

function collectShiftableUniforms() {
  _shiftableVec3Uniforms.length = 0;
  _shiftableVec3ArrayUniforms.length = 0;
  if (!scene) return;
  scene.traverse((obj) => {
    if (!obj.material) return;
    const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
    for (let mi = 0; mi < materials.length; mi++) {
      const m = materials[mi];
      // Transit shadows patched in through onBeforeCompile keep their
      // uniforms on the material's userData.
      const ms = m && m.userData && m.userData.moonShadowUniforms;
      if (ms && ms.sun) {
        if (!_shiftableVec3Uniforms.includes(ms.sun)) _shiftableVec3Uniforms.push(ms.sun);
        if (!_shiftableVec3ArrayUniforms.includes(ms.positions)) _shiftableVec3ArrayUniforms.push(ms.positions);
      }
      if (!m || !m.uniforms) continue;
      for (let ni = 0; ni < SHIFTABLE_VEC3_NAMES.length; ni++) {
        const name = SHIFTABLE_VEC3_NAMES[ni];
        const u = m.uniforms[name];
        // Several materials share one uniform (the Earth's surface and its
        // shell); it must move once.
        if (u && u.value && u.value.isVector3 && !_shiftableVec3Uniforms.includes(u)) _shiftableVec3Uniforms.push(u);
      }
      for (let ni = 0; ni < SHIFTABLE_VEC3_ARRAY_NAMES.length; ni++) {
        const name = SHIFTABLE_VEC3_ARRAY_NAMES[ni];
        const u = m.uniforms[name];
        if (u && Array.isArray(u.value) && u.value.length > 0 && u.value[0].isVector3 && !_shiftableVec3ArrayUniforms.includes(u)) {
          _shiftableVec3ArrayUniforms.push(u);
        }
      }
    }
  });
}

function applyOriginShift(shiftAmount) {
  for (let i = 0; i < _shiftableVec3Uniforms.length; i++) {
    _shiftableVec3Uniforms[i].value.sub(shiftAmount);
  }
  for (let i = 0; i < _shiftableVec3ArrayUniforms.length; i++) {
    const arr = _shiftableVec3ArrayUniforms[i].value;
    for (let j = 0; j < arr.length; j++) arr[j].sub(shiftAmount);
  }
}

function unapplyOriginShift(shiftAmount) {
  for (let i = 0; i < _shiftableVec3Uniforms.length; i++) {
    _shiftableVec3Uniforms[i].value.add(shiftAmount);
  }
  for (let i = 0; i < _shiftableVec3ArrayUniforms.length; i++) {
    const arr = _shiftableVec3ArrayUniforms[i].value;
    for (let j = 0; j < arr.length; j++) arr[j].add(shiftAmount);
  }
}

function isSatelliteInspectionActive() {
  return !!(
    selectedObject &&
    selectedObject.userData &&
    selectedObject.userData.isSatellite &&
    !flyMode &&
    !isAnimating
  );
}

function syncSatelliteLocalRenderCamera(shiftAmount) {
  _satelliteLocalRenderCamera.copy(camera, false);
  _satelliteLocalRenderCamera.position.copy(camera.position).sub(shiftAmount);
  _satelliteLocalRenderCamera.quaternion.copy(camera.quaternion);
  _satelliteLocalRenderCamera.scale.copy(camera.scale);
  _satelliteLocalRenderCamera.layers.mask = camera.layers.mask;
  _satelliteLocalRenderCamera.updateProjectionMatrix();
  _satelliteLocalRenderCamera.updateMatrixWorld(true);
}

const _earthFollowSpin = new THREE.Quaternion();
const _earthFollowDelta = new THREE.Quaternion();
const _earthFollowNow = new THREE.Quaternion();
let _earthFollowMesh = null;

// Close to the Sun. The follow code above skips it, so its only guard was
// the zoom limit, which left the photosphere exactly one near-plane distance
// from the camera (and the limit is measured to the orbit target, so a pan
// brought the camera closer still): the surface was clipped open and the Sun
// vanished. Near the Sun the near plane now shrinks with the height above
// the photosphere, keeping it a fifth of that height, and the camera is held
// above the zoom floor whatever the target. Far off the near plane is the
// focus code's own.
//
// The overview is on the Sun too: nothing is selected, the nav reads "Sun"
// and the orbit target is its centre. The back button, a scale switch and
// leaving fly mode all land there with zoom limits that were never the
// Sun's (20, inside the true-scale Sun of 23.25; 0.05; the last planet's),
// and the photosphere draws front faces only, so the wheel carried the
// camera inside and the Sun was gone. The guard covers it the same way.
//
// The floor is three hundredths of a radius above the photosphere (about
// 21,000 km) in both scales. That keeps the camera outside the chromosphere
// shell (1.02 R, switched off from inside), so the spicule fringe stays on
// the limb, and below the tops of every prominence (0.05 to 0.17 R), while
// a 1,200 km granule still spans some 60 pixels of a 900-pixel-tall view.
// True scale used to allow half a percent, where a granule was a third of
// the screen tall and the chromosphere had switched off.
//
// The hold checks the path each controls update moves the camera along, not
// only where it ends. With the orbit target panned off the Sun the zoom limit
// is measured to a point in empty space, and a fast wheel or a pinch (which
// OrbitControls applies in its event handler, between frames) carried the
// camera past the Sun's centre in one step: pushed out from there it landed
// on the far side, facing the target and away from the Sun. A zoom in toward
// a target behind the Sun now stops where its path met the floor, as does a
// drag that would cross the Sun from above it; a drag or a zoom that only
// dips under the floor slides along it, a drag or a zoom out from the floor
// that would cross the Sun slides round the limb, and no zoom carries the
// camera past the Sun in one step (stopAtSunFloor).
//
// The Sun is solid whatever is selected. A planet's zoom (Mercury at the
// smallest spread sits under three solar radii out), the tween that leaves
// fly mode and any other tween can all carry the camera through it, so the
// path check and the lift apply with a planet focused too, and the camera
// tweens go round it (lerpRoundSun), clear of the poles OrbitControls will
// not cross. Only the zoom limit stays the Sun's own. A tween places the
// camera itself, so the controls do not clamp its frames, which the path
// check took for zooms (leaseCameraToTween).
//
// The near plane is clamped on every frame the Sun is close, tweens and
// other selections included: the focus fly-in, the back button and a scale
// switch each set a near plane for where the camera ends (50 units at the
// widest artistic spread), which clipped the whole Sun on the frames that
// start at the floor. The guard only ever lowers whatever near plane the
// view set, and hands that one back once the Sun is far enough off.
const SUN_MIN_HEIGHT = 0.03;
function getSunZoomFloor(radius) {
  return radius * (1 + SUN_MIN_HEIGHT);
}
// The overview's own near plane: updateScale, the scale switch, the back
// button and the start of a focus fly-in all set it.
function getOverviewCameraNear() {
  return realisticScaleMode ? 0.001 : Math.max(0.1, scaleMultiplier * 0.1);
}
const _sunCloseCentre = new THREE.Vector3();
const _sunCloseDir = new THREE.Vector3();
const _sunCloseRel = new THREE.Vector3();
const _sunClosePath = new THREE.Vector3();
const _sunCloseFrom = new THREE.Vector3();
const _sunCloseTarget = new THREE.Vector3();
const _sunCloseTurned = new THREE.Vector3();
const _sunCloseZoomed = new THREE.Vector3();
const _sunCloseEntry = new THREE.Vector3();
const _sunCloseAxis = new THREE.Vector3();
// Which way to lift a camera that sits exactly on the Sun's centre.
const _sunCloseFallbackDir = new THREE.Vector3(1, 0.25, 1);
// The near plane the guard last wrote, and the view's own one it lowered.
// A near plane that is not the guard's was set by someone else (a focus, the
// back button, a scale switch, the slider) and becomes the one to hand back.
let _sunCloseNear = NaN;
let _sunCloseOwnerNear = NaN;
// The zoom limit the guard raised to the floor, and the one it replaced, put
// back when the orbit target leaves the Sun. Another writer takes it over.
let _sunCloseMinDistance = NaN;
let _sunCloseSavedMinDistance = null;
// The Sun the camera may not enter: the focused Sun, or Sol's Sun whatever
// else is selected. Fly mode, To Scale and rocketry place the camera
// themselves.
function getSolidSun() {
  if (!camera || !controls || flyMode || scaleModeActive || rocketryActive) return null;
  if (selectedObject?.userData?.isSun) return selectedObject;
  return sun && activeSystemId === 'sol' && !IS_SCREENSAVER ? sun : null;
}
// The Sun when the controls orbit it: the Sun focused, or the overview
// (nothing selected, orbiting the Sun).
function getSunPivot() {
  const body = getSolidSun();
  return body && (!selectedObject || selectedObject === body) ? body : null;
}
// The Sun while the zoom limit is held on it: its own views with no tween
// in flight.
function getHeldSun() {
  return isAnimating ? null : getSunPivot();
}
// The camera tweens (lerpRoundSun's callers) place the camera themselves
// every frame, and the frame loop's controls.update clamped each of those
// frames to the zoom limits of the view the tween left, measured to an orbit
// target the tween is moving. Leaving fly mode beside the Sun with a planet
// selected, that pulled the camera out to 160 radii, and with the Sun's path
// check taking each clamp for a zoom it went in and out by a hundred radii a
// frame; a focus in the middle of that flight jumped 130 radii in four frames.
// So a tween takes a lease on the camera when it starts and renews it every
// frame, and while the lease lasts the frame loop's own updates leave the
// distance alone. It lasts two frame-loop updates from the last renewal,
// whichever of gsap and the frame loop runs first in a frame, so a tween that
// lands or is killed hands the camera back at once and no limit is left open.
// Updates from input (a wheel on the way back from fly mode, whose controls
// stay on) keep their limits.
let cameraTweenLease = 0;
function leaseCameraToTween() {
  cameraTweenLease = 2;
}
// Wraps controls.update, which OrbitControls' wheel, pinch and key handlers
// call as well as animate, so the hold sees every step the controls take.
function holdControlsOffSun() {
  const baseUpdate = controls.update;
  const updateControls = (deltaTime) => {
    // window.event is the event being handled when input calls this, and
    // undefined in the frame loop.
    if (!(cameraTweenLease > 0) || window.event) return baseUpdate(deltaTime);
    cameraTweenLease--;
    const minDistance = controls.minDistance;
    const maxDistance = controls.maxDistance;
    controls.minDistance = 0;
    controls.maxDistance = Infinity;
    try {
      return baseUpdate(deltaTime);
    } finally {
      controls.minDistance = minDistance;
      controls.maxDistance = maxDistance;
    }
  };
  controls.update = function update(deltaTime) {
    const body = getSolidSun();
    if (!body) return updateControls(deltaTime);
    _sunCloseFrom.copy(camera.position);
    _sunCloseTarget.copy(controls.target);
    const changed = updateControls(deltaTime);
    stopAtSunFloor(body);
    return changed;
  };
}
// Puts `point` back on the floor straight out from the Sun's centre
// (_sunCloseCentre).
function liftOntoSunFloor(point, floor, fallbackDir) {
  _sunCloseDir.copy(point).sub(_sunCloseCentre);
  if (_sunCloseDir.lengthSq() < floor * floor * 1e-12) _sunCloseDir.copy(fallbackDir);
  return point.copy(_sunCloseCentre).addScaledVector(_sunCloseDir.normalize(), floor);
}
const SUN_STEP_CLEAR = 0;
const SUN_STEP_CROSSES = 1;
const SUN_STEP_UNDER = 2;
const SUN_STEP_GRAZES = 3;
// How a straight step meets the floor sphere round _sunCloseCentre: clear of
// it, crossing the Sun (ending in the hemisphere opposite the start, or
// dipping into the Sun and out again, from outside the floor), ending under
// the floor on the near side, or grazing it (in under the floor and out
// again on the near side, above the Sun). A step that comes in from outside
// the floor leaves the point where it met the floor in _sunCloseEntry.
function classifySunStep(from, to, radius, floor) {
  _sunClosePath.copy(to).sub(from);
  _sunCloseRel.copy(from).sub(_sunCloseCentre);
  const m2 = _sunCloseRel.lengthSq();
  const f2 = floor * floor;
  const a = _sunClosePath.lengthSq();
  if (a === 0) return m2 < f2 * (1 - 1e-9) ? SUN_STEP_UNDER : SUN_STEP_CLEAR;
  const b = _sunCloseRel.dot(_sunClosePath);
  const tc = Math.min(1, Math.max(0, -b / a));
  const close2 = m2 + tc * (2 * b + tc * a);
  if (close2 >= f2 * (1 - 1e-9)) return SUN_STEP_CLEAR;
  const endInside = to.distanceToSquared(_sunCloseCentre) < f2 * (1 - 1e-9);
  const outside = m2 >= f2 * (1 - 1e-6);
  const t = outside ? Math.max(0, (-b - Math.sqrt(Math.max(0, b * b - a * (m2 - f2)))) / a) : 0;
  _sunCloseEntry.copy(from).addScaledVector(_sunClosePath, t);
  if (outside && (m2 + b <= 0 || (!endInside && close2 < radius * radius))) return SUN_STEP_CROSSES;
  return endInside ? SUN_STEP_UNDER : SUN_STEP_GRAZES;
}
// The widest angle round the Sun a slide over the floor covers in one
// controls update, about 25 degrees. The straight line between two points
// on the floor dips to floor * cos(angle / 2), which this keeps outside the
// photosphere (a tenth under the angle where it would touch it), and from a
// start higher up the line only stays further out, so not even the chord
// between two updates cuts the Sun (1.0057 R at the closest).
const SUN_FLOOR_SLIDE_MAX = 2 * Math.acos(1 / (1 + SUN_MIN_HEIGHT)) * 0.9;
// Puts `out` (which may be `toward`) on the floor, over the direction of
// `toward` from the centre, or as far toward it from over `from` as
// SUN_FLOOR_SLIDE_MAX allows. For a short step that is the straight lift of
// `toward`. A step aimed at the far side through the centre, or at the
// centre itself, goes round the way `path` leans, and one that leans no way
// at all goes round by sunWayRound.
//
// `minAngle` is the least it goes round. A wheel out passes the step's own
// length over the floor: the lift alone keeps only the part of the step
// across the floor, which for a camera backing straight into the Sun is
// nothing, so with the Sun dead behind the camera the wheel parked it on the
// floor for good, and with a slight lean crept it round by a sliver a notch.
function slideOverSunFloor(out, from, toward, path, floor, minAngle = 0) {
  _sunCloseRel.copy(from).sub(_sunCloseCentre).normalize();
  _sunCloseDir.copy(toward).sub(_sunCloseCentre);
  _sunCloseAxis.crossVectors(_sunCloseRel, _sunCloseDir);
  let angle = Math.atan2(_sunCloseAxis.length(), _sunCloseRel.dot(_sunCloseDir));
  if (_sunCloseAxis.lengthSq() <= _sunCloseDir.lengthSq() * 1e-12) {
    angle = _sunCloseRel.dot(_sunCloseDir) > 0 ? 0 : SUN_FLOOR_SLIDE_MAX;
    _sunCloseAxis.crossVectors(_sunCloseRel, path);
    if (_sunCloseAxis.lengthSq() <= path.lengthSq() * 1e-12) sunWayRound(_sunCloseAxis, _sunCloseRel);
  }
  angle = Math.min(Math.max(angle, minAngle), SUN_FLOOR_SLIDE_MAX);
  if (angle > 0) _sunCloseRel.applyAxisAngle(_sunCloseAxis.normalize(), angle);
  return out.copy(_sunCloseCentre).addScaledVector(_sunCloseRel, floor);
}
// The axis to go round the Sun by for a move aimed straight through its
// centre, which leans no way of its own (`rel` is a unit vector from the
// centre): sideways round the camera's up, the way the planets go round.
// The camera's up is the orbit axis, the ecliptic's north unless a view
// tipped it, so every frame of a tween picks the same way round; taken from
// the camera's own orientation it would turn with the view mid-tween. Over
// the top, toward the up itself, ran into the controls' polar limit (27
// degrees short of the pole), which held the back button's camera still for
// two frames and then let it jump 0.97 R. Within about 25 degrees of the
// pole, where sideways has no meaning, the world's x axis stands in.
const _sunCloseLean = new THREE.Vector3();
function sunWayRound(out, rel) {
  _sunCloseLean.copy(camera.up).normalize();
  if (Math.abs(_sunCloseLean.dot(rel)) > 0.9) _sunCloseLean.set(1, 0, 0);
  _sunCloseLean.crossVectors(_sunCloseLean, rel);
  return out.crossVectors(rel, _sunCloseLean);
}
const _sunSweepPath = new THREE.Vector3();
const _sunSweepRel = new THREE.Vector3();
const _sunSweepAxis = new THREE.Vector3();
const _sunSweepDir = new THREE.Vector3();
const _sunSweepTurn = new THREE.Vector3();
// Shortens a zoom step from `from` to `to` (moving `to`) so that it carries
// the camera at most a set angle round the Sun's centre, and says whether it
// did. Seen from the camera that angle is how far the Sun moves across the
// view, as a zoom does not turn it. The limit is SUN_FLOOR_SLIDE_MAX for a
// line that passes at the floor, as a slide over it covers, and grows with
// the distance the line passes at, so it lets go about seven radii out and a
// zoom past a far Sun is untouched. A line through the photosphere is left
// alone: the floor stops or slides it first. OrbitControls drops what a step
// was not given, and the next wheel notch carries on from there.
function limitSunZoomSweep(from, to, radius, floor) {
  _sunSweepPath.copy(to).sub(from);
  const a = _sunSweepPath.lengthSq();
  if (a === 0) return false;
  _sunSweepRel.copy(from).sub(_sunCloseCentre);
  _sunSweepAxis.crossVectors(_sunSweepRel, _sunSweepPath);
  const passes = Math.sqrt(_sunSweepAxis.lengthSq() / a);
  if (passes < radius) return false;
  const limit = SUN_FLOOR_SLIDE_MAX * passes / floor;
  if (limit >= Math.PI) return false;
  _sunSweepDir.copy(to).sub(_sunCloseCentre);
  const swept = Math.atan2(_sunSweepTurn.crossVectors(_sunSweepRel, _sunSweepDir).length(), _sunSweepRel.dot(_sunSweepDir));
  if (swept <= limit) return false;
  // Where the step's direction from the centre has turned by the limit: the
  // line meets the ray from the centre at that angle.
  _sunSweepAxis.normalize();
  _sunSweepDir.copy(_sunSweepRel).normalize().applyAxisAngle(_sunSweepAxis, limit);
  _sunSweepAxis.cross(_sunSweepDir);
  const t = -_sunSweepRel.dot(_sunSweepAxis) / _sunSweepPath.dot(_sunSweepAxis);
  if (!(t > 0 && t < 1)) return false;
  to.copy(from).addScaledVector(_sunSweepPath, t);
  return true;
}
// Whether the straight line from `from` to `to` keeps clear of the
// photosphere (not the floor).
function sunLineClearsPhotosphere(from, to, radius) {
  _sunSweepPath.copy(to).sub(from);
  _sunSweepRel.copy(from).sub(_sunCloseCentre);
  const a = _sunSweepPath.lengthSq();
  const t = a > 0 ? Math.min(1, Math.max(0, -_sunSweepRel.dot(_sunSweepPath) / a)) : 0;
  return _sunSweepRel.addScaledVector(_sunSweepPath, t).lengthSq() >= radius * radius;
}
// One controls update, split into its turn and pan (applied at the old
// distance from the orbit target) and its zoom (along the view axis the
// turn left). They meet the Sun differently.
//
// A turn or a pan that ends under the floor slides along it, so drags keep
// moving over the surface (stopped at the floor they froze the camera
// there), and one that only grazed it between two frames stands. One that
// would cross the Sun stops where it met the floor, unless it starts on the
// floor: there that point is where it started, and a fast flick froze the
// camera for the frames its steps were long enough to cross. It slides round
// the limb instead. Every slide covers at most SUN_FLOOR_SLIDE_MAX a step,
// so it keeps moving and never goes through: lifted straight out, a fling's
// step that ended under the floor 68 degrees round put the camera there in
// one update, past a chord 0.86 R from the centre.
//
// A zoom first has its sweep limited (limitSunZoomSweep): along a line that
// passes close to the Sun one update carries the camera at most a little way
// round the centre, so the Sun never jumps across the view. A hard wheel from
// beside the limb, with the target thirty radii off, carried the camera from
// 1.4 R to 21 R past the Sun in one update and read as a teleport.
//
// A zoom in whose line to the target goes through the photosphere stops at
// the floor: the target is behind the Sun, and sliding walked the camera
// round the limb and out past it. One whose line clears the photosphere and
// only dips under the floor slides along the floor like a zoom out, round to
// where the line clears the floor and on to the target. Stopped, the wheel
// went dead there with the target in plain view beside the limb. A step that
// only grazes the floor between two frames stands, and with the sweep limited
// none passes the Sun in one step.
//
// A zoom out moves away from the target, so it can only meet the Sun when
// the Sun is behind the camera. Under the floor it slides along it, round to
// where its line from the target clears the Sun, and covers its own length
// over the floor each update, so the wheel always backs the camera out. One
// that would cross the Sun stops where it met the floor, unless it starts on
// the floor: there that point is where it started, and with the Sun straight
// behind the camera no wheel, pinch or notch moved it until a drag did. It
// slides round the limb instead, the way its line leans or, aimed straight
// through the centre, sideways round the orbit axis (sunWayRound).
//
// Splitting also keeps a pinch whose turn or pan moved the view axis off
// the Sun from zooming past it on the unchanged one (a tenth of a radius
// clear of the floor, out the far side).
//
// An update that starts under the floor (a view link or a deep link put the
// camera there, or a tween's frame did) has no path to check, so only where
// it ends is lifted, straight out from the centre like the per-frame lift.
// Split, the turn's lift was scaled again by the zoom limit's own push out
// (from half a radius to the floor is twice the distance), and a link half a
// radius in landed 2.1 radii out instead of on the floor.
function stopAtSunFloor(body) {
  const radius = getBodyBaseRadius(body);
  body.getWorldPosition(_sunCloseCentre);
  const floor = getSunZoomFloor(radius);
  const f2 = floor * floor;
  const from2 = _sunCloseFrom.distanceToSquared(_sunCloseCentre);
  if (from2 < f2 * (1 - 1e-9)) {
    if (camera.position.distanceToSquared(_sunCloseCentre) < f2 * (1 - 1e-9)) {
      liftOntoSunFloor(camera.position, floor, _sunCloseFallbackDir);
      camera.lookAt(controls.target);
    }
    return;
  }
  const d0 = _sunCloseFrom.distanceTo(_sunCloseTarget);
  const d1 = camera.position.distanceTo(controls.target);
  const zoomed = d0 > 0 && d1 > 0 && Math.abs(d1 - d0) > d0 * 1e-9;
  _sunCloseTurned.copy(camera.position);
  if (zoomed) _sunCloseTurned.sub(controls.target).multiplyScalar(d0 / d1).add(controls.target);
  let moved = false;
  const turn = classifySunStep(_sunCloseFrom, _sunCloseTurned, radius, floor);
  if (turn === SUN_STEP_CROSSES && from2 > f2 * (1 + 1e-6)) {
    _sunCloseTurned.copy(_sunCloseEntry);
    moved = true;
  } else if (turn === SUN_STEP_CROSSES || turn === SUN_STEP_UNDER) {
    slideOverSunFloor(_sunCloseTurned, _sunCloseFrom, _sunCloseTurned, _sunClosePath, floor);
    moved = true;
  }
  if (zoomed) {
    _sunCloseZoomed.copy(_sunCloseTurned).sub(controls.target).multiplyScalar(d1 / d0).add(controls.target);
    if (limitSunZoomSweep(_sunCloseTurned, _sunCloseZoomed, radius, floor)) moved = true;
    const zoom = classifySunStep(_sunCloseTurned, _sunCloseZoomed, radius, floor);
    const zoomOut = d1 > d0;
    const onFloor = _sunCloseTurned.distanceToSquared(_sunCloseCentre) <= f2 * (1 + 1e-6);
    if (zoom === SUN_STEP_CLEAR || zoom === SUN_STEP_GRAZES) {
      if (moved) camera.position.copy(_sunCloseZoomed);
    } else if ((zoom === SUN_STEP_UNDER || onFloor)
      && (zoomOut || sunLineClearsPhotosphere(_sunCloseTurned, controls.target, radius))) {
      const minAngle = zoomOut ? _sunClosePath.length() / floor : 0;
      camera.position.copy(slideOverSunFloor(_sunCloseZoomed, _sunCloseTurned, _sunCloseZoomed, _sunClosePath, floor, minAngle));
      moved = true;
    } else {
      camera.position.copy(_sunCloseEntry);
      moved = true;
    }
  } else if (moved) {
    camera.position.copy(_sunCloseTurned);
  }
  if (moved) camera.lookAt(controls.target);
}
// The share of the gap to the floor the zoom limit closes each frame while it
// eases out a camera that was nearer the target than the floor: nine tenths
// of the way in ten frames, settled in about half a second at 60 fps.
const SUN_LIMIT_EASE = 0.2;
function holdSunZoomLimit(radius, floor) {
  if (_sunCloseSavedMinDistance !== null && controls.minDistance !== _sunCloseMinDistance) {
    _sunCloseSavedMinDistance = null;
  }
  // With the orbit target on the Sun the floor is OrbitControls' own zoom
  // limit, so the wheel stops there by itself, whichever path last set the
  // limit.
  if (controls.target.distanceTo(_sunCloseCentre) < radius) {
    if (controls.minDistance < floor) {
      if (_sunCloseSavedMinDistance === null) _sunCloseSavedMinDistance = controls.minDistance;
      // A camera already nearer the target than that (the target panned
      // back over the Sun from beside it, the true-scale overview's own
      // limit being 0.86 R) is eased out to it over a few frames. Raised at
      // once, the limit moved it the whole difference in one frame, 0.13 R
      // from 0.9 R.
      const dist = camera.position.distanceTo(controls.target);
      let limit = floor;
      if (dist < floor) {
        const start = Math.max(controls.minDistance, dist);
        limit = start + (floor - start) * SUN_LIMIT_EASE;
        if (floor - limit < floor * 1e-4) limit = floor;
      }
      controls.minDistance = _sunCloseMinDistance = limit;
    }
  } else if (_sunCloseSavedMinDistance !== null) {
    // Panned off into empty space the floor is no limit of the target's:
    // left raised, the wheel stopped a Sun's radius short of nothing.
    controls.minDistance = _sunCloseSavedMinDistance;
    _sunCloseSavedMinDistance = null;
  }
}
// Two fingers on a phone. OrbitControls takes each finger's move as an event
// of its own, pans (DOLLY_PAN) or turns (DOLLY_ROTATE) by how far that move
// shifted the fingers' midpoint, and updates the camera at once. In a still
// pinch the two fingers' moves shift the midpoint one way and back, which
// should cancel, but the first move's zoom had already moved the camera, so
// the second's pan was measured at another distance and left the
// difference: every pinch left the orbit target about a quarter of a radius
// off the Sun (a radius and a half after one fast pinch, at true scale or in
// from the artistic overview), until the Sun left the view and the zoom
// limit let go. The turn swung the view axis a third of a
// radius across the Sun between the two halves, and a zoom on the swung axis
// passed beside the Sun instead of stopping at its floor. So the update a
// finger's move asks for waits until every finger down has moved (or for
// the frame's own update), and the halves of a move are measured at one
// distance and cancel (steadyTwoFingerUpdates).
//
// Close to the Sun the pinch also switches the pan off until the fingers
// lift (OrbitControls reads enablePan on every move) and is a pure zoom:
// fingers never move quite symmetrically, and there the view is a few radii
// across and the zoom limit holds only while the target is on the disc. One
// finger still turns the view, and the touch hint only ever offered drag,
// pinch and tap.
//
// Close means the camera within SUN_PINCH_ZOOM_ONLY_RADII of the Sun's
// centre when the second finger lands. Further out two fingers pinch and pan
// as everywhere else (the ISS close-up uses it to reframe the station):
// switched off whenever the controls orbited the Sun, the pan was gone from
// the whole overview, the solar system's own view. Twenty radii is inside
// both scales' overviews (46 R artistic, 30 R true scale), with room for the
// slight zoom a pan often comes with, and holds every view a pinch toward the
// Sun settles in (its focus frames it from 6 R), where pinches follow one
// another.
//
// The choice is made once a gesture and kept until the fingers lift. It
// cannot come back on mid-gesture: OrbitControls only takes the pan's
// starting point when a gesture begins with it on, and would pan from a
// stale one.
const SUN_PINCH_ZOOM_ONLY_RADII = 20;
const _sunPinchTouches = new Set();
const _sunPinchCentre = new THREE.Vector3();
// The fingers that have moved since the controls last updated.
const _pinchMovedSinceUpdate = new Set();
let _sunPinchSavedPan = null;
function isCameraCloseToSunPivot() {
  const body = getSunPivot();
  if (!body) return false;
  body.getWorldPosition(_sunPinchCentre);
  return camera.position.distanceTo(_sunPinchCentre) < getBodyBaseRadius(body) * SUN_PINCH_ZOOM_ONLY_RADII;
}
function zoomOnlySunPinch() {
  const track = (event) => {
    if (event.pointerType !== 'touch') return;
    if (event.type === 'pointerdown') {
      // A new gesture starts with its primary finger, so a lift the page
      // never heard about cannot leave a finger counted.
      if (event.isPrimary) _sunPinchTouches.clear();
      _sunPinchTouches.add(event.pointerId);
    } else {
      _sunPinchTouches.delete(event.pointerId);
    }
    _pinchMovedSinceUpdate.clear();
    const pinching = _sunPinchTouches.size >= 2;
    if (event.type === 'pointerdown' && pinching && _sunPinchSavedPan === null && isCameraCloseToSunPivot()) {
      _sunPinchSavedPan = controls.enablePan;
      controls.enablePan = false;
    } else if (!pinching && _sunPinchSavedPan !== null) {
      if (controls.enablePan === false) controls.enablePan = _sunPinchSavedPan;
      _sunPinchSavedPan = null;
    }
  };
  // Capture, so the pan is off before OrbitControls sees the second finger.
  for (const type of ['pointerdown', 'pointerup', 'pointercancel']) {
    controls.domElement.addEventListener(type, track, true);
  }
}
// Wraps controls.update, outermost, so the update OrbitControls makes at the
// end of a finger's move waits while another finger down has yet to move.
// The frame loop's own update always runs, so nothing waits past a frame,
// and a gesture's pan, turn and zoom still land each frame as one step that
// the Sun hold checks like any other.
function steadyTwoFingerUpdates() {
  const baseUpdate = controls.update;
  controls.update = function update(deltaTime) {
    // The event being dispatched when OrbitControls calls this from its
    // pointermove listener; undefined when the frame loop calls it.
    const event = window.event;
    if (event?.type === 'pointermove' && event.pointerType === 'touch' && _sunPinchTouches.size >= 2) {
      _pinchMovedSinceUpdate.add(event.pointerId);
      if (_pinchMovedSinceUpdate.size < _sunPinchTouches.size) return false;
    }
    _pinchMovedSinceUpdate.clear();
    return baseUpdate(deltaTime);
  };
}
// The camera tweens (a focus fly-in, the back button, an event shot, the way
// back from fly mode) move the camera from where it was to where it lands at
// a share `s` of the way. Straight, that line can cut through the Sun: from
// the floor on the far side of a planet it passes within a fifth of a radius
// of the centre. Lifted onto the floor frame by frame, a point that passes
// close to the centre swings round the limb in a handful of frames (steps of
// 0.7 R and view turns of 20 degrees a frame against a median step of
// 0.07 R), and one that passes through it flips to the far side in a single
// frame, 2.06 R and the view turned right round.
//
// So when the line dips under the floor the camera takes the way a string
// pulled taut round the floor would: straight to where that string first
// touches the floor, round the floor, and straight on from where it leaves.
// The string lies in a plane through both ends, and every such plane cuts
// the floor in a circle the line passes inside, so the string never goes
// under the floor whichever plane it is. The share `s` is taken of that
// path's length, so the tween keeps its pace and easing, and every frame's
// step stays near what it was on the straight line. The path meets the
// straight one exactly when the line only touches the floor, so a moving end
// never makes it jump. A tween that ends under the floor is left straight for
// the per-frame lift.
//
// The plane is the one through the Sun's centre (the great circle, the
// shortest way round), unless that goes over a pole: OrbitControls holds the
// camera 27 degrees off the pole of its orbit target, and with the target on
// the Sun it held a back button's camera at that edge for a few frames, then
// flipped it across the cap in one, 1 R with the view turned 54 degrees. The
// plane is then tilted about the line between the ends, the least that keeps
// the whole route SUN_ROUTE_POLE_MARGIN clear of the cap or no higher than
// its ends, toward whichever side needs less. A line aimed straight through
// the centre has no plane of its own and goes round sideways (sunWayRound).
//
// From high on the far side no plane through both ends keeps clear of the
// cap (a back button from 45 degrees up went over the pole whatever the
// tilt), and a straight line close over the pole meets the same cap. Those
// take a spiral round the orbit axis instead (layOutSunSpiral), which never
// climbs above its ends.
const SUN_ROUTE_POLE_MARGIN = Math.PI / 60;
const SUN_ROUTE_TILT_STEP = Math.PI / 36;
const SUN_ROUTE_TILT_MAX = 15;
const _sunRouteCentre = new THREE.Vector3();
const _sunRouteFrom = new THREE.Vector3();
const _sunRouteTo = new THREE.Vector3();
const _sunRouteLine = new THREE.Vector3();
const _sunRouteBase = new THREE.Vector3();
const _sunRouteTip = new THREE.Vector3();
const _sunRouteNormal = new THREE.Vector3();
const _sunRouteDisc = new THREE.Vector3();
const _sunRouteU = new THREE.Vector3();
const _sunRouteSide = new THREE.Vector3();
const _sunRouteAxis = new THREE.Vector3();
const _sunRoutePoint = new THREE.Vector3();
const _sunRouteUp = new THREE.Vector3();
// The route last laid out: the way round (1 or -1), the circle's radius, the
// angles round it from the start where the string touches and leaves it, and
// the straight legs; or, for a spiral, its latitudes, turn and log distances
// at the two ends.
const _sunRoute = {
  way: 0, radius: 0, touch: 0, leave: 0, inLeg: 0, outLeg: 0, length: 0,
  latFrom: 0, latTo: 0, turn: 0, logFrom: 0, logTo: 0,
};
const SUN_ROUTE_STRAIGHT = 0;
const SUN_ROUTE_STRING = 1;
const SUN_ROUTE_SPIRAL = 2;
// Where a focus tween's straight line would pass through a planet or moon
// (the Earth to a Moon behind it in artistic scale, Mars to Phobos on its far
// side), the camera goes round that body instead, so it never goes inside:
// its direction from the body's centre turns along a great circle while its
// distance goes from the start's to a few times that (lifted halfway over a
// long turn, as the ISS fly-in is), then, for an end further off, it runs
// straight out to the end along the end's own direction from the body, which
// cannot cut it. The two legs share the tween in proportion to their lengths
// and overlap a little, so the speed carries through the change from going
// round to going out. The body is picked when the tween starts, from the
// bodies it leaves and goes to and their parents; it needs the line to pass
// the body (not just start beside it) within BODY_ROUTE_CLEAR of its radius,
// since skimming a surface at full speed while looking at the body whips the
// view round even when it misses, or, for the body left and the body gone to,
// closer than the camera is to it at that end (a dive past the planet to a
// framing on its far side swings the view through half a turn in a second).
// Going round the body being left, the run out can still pass close by the
// one being gone to (Mars to the Earth when the Earth is framed from its far
// side): then it goes round that one too.
const _arriveTurn = new GreatCircleTurn();
const _arriveCentre = new THREE.Vector3();
const _arriveRel0 = new THREE.Vector3();
let _arriveBody = null;
const BODY_ROUTE_CLEAR = 2;
// For the body at either end: route when the line comes closer to it than
// this share of the camera's distance from it there.
const BODY_ROUTE_DIP = 0.9;
// The body the last focus tween went round, for checks (window.__debug.focusRoute).
let _lastFocusRoute = null;
const _bodyRouteTurn = new GreatCircleTurn();
const _bodyLookTurn = new GreatCircleTurn();
const _bodyRouteCentre = new THREE.Vector3();
const _bodyRouteRel0 = new THREE.Vector3();
const _bodyRouteRel = new THREE.Vector3();
const _bodyRouteDir = new THREE.Vector3();
const _bodyRouteSeg = new THREE.Vector3();
function isRouteBody(o) {
  const u = o && o.userData;
  return Boolean(u && u.radius > 0 && !u.isSun && !u.isSatellite && !u.isGalaxy && !u.isBlackHole
    && !u.isWormhole && !u.isComet && !u.isSpaceProbe && typeof o.getWorldPosition === 'function');
}
function routeBodyCandidates(objects) {
  const out = [];
  for (const o of objects) {
    for (let b = o; b; b = b.parent) {
      if (isRouteBody(b) && !out.includes(b)) out.push(b);
    }
  }
  return out;
}
// The body the straight line from start to end passes deepest inside its
// clearance, or null. Sets up the turn round it when there is one.
function startBodyRoute(start, end, objects) {
  let best = null;
  let bestDepth = 0;
  _bodyRouteSeg.copy(end).sub(start);
  const len2 = _bodyRouteSeg.lengthSq();
  if (!(len2 > 0)) return null;
  const leave = objects[0];
  const arrive = objects[objects.length - 1];
  for (const body of routeBodyCandidates(objects)) {
    let clear = getBodyBaseRadius(body) * BODY_ROUTE_CLEAR;
    if (!(clear > 0)) continue;
    body.getWorldPosition(_bodyRouteCentre);
    if (body === leave) clear = Math.max(clear, BODY_ROUTE_DIP * start.distanceTo(_bodyRouteCentre));
    if (body === arrive) clear = Math.max(clear, BODY_ROUTE_DIP * end.distanceTo(_bodyRouteCentre));
    _bodyRouteRel.copy(start).sub(_bodyRouteCentre);
    const tc = -_bodyRouteRel.dot(_bodyRouteSeg) / len2;
    if (tc <= 0 || tc >= 1) continue;
    const d = _bodyRouteRel.addScaledVector(_bodyRouteSeg, tc).length();
    if (d < clear && (clear - d) / clear > bestDepth) {
      bestDepth = (clear - d) / clear;
      best = body;
    }
  }
  _arriveBody = null;
  if (!best) return null;
  best.getWorldPosition(_bodyRouteCentre);
  _bodyRouteRel0.copy(start).sub(_bodyRouteCentre);
  _bodyRouteRel.copy(end).sub(_bodyRouteCentre);
  _bodyRouteTurn.start(_bodyRouteRel0, _bodyRouteRel, camera.up);
  // The run out from the turn's end to the end, against the body gone to.
  if (isRouteBody(arrive) && arrive !== best) {
    const r1 = _bodyRouteRel.length();
    const radius = getBodyBaseRadius(best);
    const rW = Math.min(r1, Math.max(_bodyRouteRel0.length(), radius * BODY_ROUTE_CLEAR) * 4);
    if (r1 > rW) {
      const waypoint = _bodyRouteSeg.copy(_bodyRouteRel).multiplyScalar(rW / r1).add(_bodyRouteCentre);
      arrive.getWorldPosition(_arriveCentre);
      const clear = Math.max(getBodyBaseRadius(arrive) * BODY_ROUTE_CLEAR, BODY_ROUTE_DIP * end.distanceTo(_arriveCentre));
      _arriveRel0.copy(waypoint).sub(_arriveCentre);
      const run = FOCUS_SCRATCH_DIR_NOW.copy(end).sub(waypoint);
      const tc = -_arriveRel0.dot(run) / Math.max(run.lengthSq(), 1e-30);
      if (tc > 0 && tc < 1 && _bodyRouteSeg.copy(_arriveRel0).addScaledVector(run, tc).length() < clear) {
        _arriveBody = arrive;
        _arriveTurn.start(_arriveRel0, FOCUS_SCRATCH_DIR_NOW.copy(end).sub(_arriveCentre), camera.up);
      }
    }
  }
  return best;
}
function arcRoundBody(out, body, end, s) {
  leaseCameraToTween();
  body.getWorldPosition(_bodyRouteCentre);
  _bodyRouteRel.copy(end).sub(_bodyRouteCentre);
  const radius = getBodyBaseRadius(body);
  const r0 = Math.max(_bodyRouteRel0.length(), 1e-12);
  const r1 = Math.max(_bodyRouteRel.length(), 1e-12);
  // Where going round ends: the end itself when it is near, otherwise a few
  // times the start's distance out, on the end's side.
  const rW = Math.min(r1, Math.max(r0, radius * BODY_ROUTE_CLEAR) * 4);
  const angle = _bodyRouteTurn.size;
  const lift = Math.max(0, radius * (1 + ISS_FLY_LIFT * angle) - (r0 + rW) / 2);
  const roundLen = angle * (r0 + rW) / 2 + Math.abs(rW - r0) + lift;
  const outLen = r1 - rW;
  const join = outLen > 1e-9 * r1 ? roundLen / (roundLen + outLen) : 1;
  const half = Math.min(0.1, join / 2, (1 - join) / 2);
  // Going round. Never below either end (a camera parked close to the ground
  // leaves from there), and otherwise clear of the surface.
  const u1 = Math.min(1, s / (join + half));
  _bodyRouteTurn.at(_bodyRouteDir, _bodyRouteRel, u1);
  const floor = Math.min(radius * 1.05, r0, r1);
  const r = Math.max(r0 + (rW - r0) * u1 + lift * Math.sin(Math.PI * u1), floor);
  out.copy(_bodyRouteCentre).addScaledVector(_bodyRouteDir, r);
  if (join >= 1) return out;
  // Going out, along the end's direction from the body, blended in over the
  // overlap; or round the body gone to, from the turn's end to the end, when
  // the straight run would pass close by it.
  const u2 = Math.max(0, (s - (join - half)) / (1 - (join - half)));
  if (_arriveBody) {
    const waypoint = _bodyRouteSeg.copy(_bodyRouteRel).multiplyScalar(rW / r1).add(_bodyRouteCentre);
    _arriveBody.getWorldPosition(_arriveCentre);
    const ra = Math.max(waypoint.distanceTo(_arriveCentre), 1e-12);
    _bodyRouteRel.copy(end).sub(_arriveCentre);
    const rb = Math.max(_bodyRouteRel.length(), 1e-12);
    const radiusB = getBodyBaseRadius(_arriveBody);
    _arriveTurn.at(_bodyRouteDir, _bodyRouteRel, u2);
    const liftB = Math.max(0, radiusB * (1 + ISS_FLY_LIFT * _arriveTurn.size) - (ra + rb) / 2);
    const rr = Math.max(Math.exp(Math.log(ra) + (Math.log(rb) - Math.log(ra)) * u2) + liftB * Math.sin(Math.PI * u2), Math.min(radiusB * 1.05, rb));
    _bodyRouteRel.copy(_arriveCentre).addScaledVector(_bodyRouteDir, rr);
  } else {
    _bodyRouteRel.divideScalar(r1).multiplyScalar(rW + (r1 - rW) * u2).add(_bodyRouteCentre);
  }
  return out.lerp(_bodyRouteRel, THREE.MathUtils.smoothstep(s, join - half, join + half));
}
// Each tween's choice of route, made on its first frame and kept, keyed by
// the tween's own start vector. Remade every frame while the end moved (a
// planet along its orbit), the choice switched sides of a pole, or between
// the string and the spiral, in mid-flight: with the end moving 5 degrees a
// second, 6 percent of focus tweens from the floor jumped, by up to 350 times
// a frame's step. Kept, the string's plane holds its tilt from the one
// through the centre, which moves smoothly with the ends, and the string and
// the spiral keep going round the way they set off, which an end crossing to
// the far side of the centre would otherwise turn about.
const _sunRoutePlans = new WeakMap();
function lerpRoundSun(out, start, end, s) {
  // Every camera tween places the camera through here each frame.
  leaseCameraToTween();
  const body = getSolidSun();
  if (!body) return out.lerpVectors(start, end, s);
  body.getWorldPosition(_sunRouteCentre);
  const floor = getSunZoomFloor(getBodyBaseRadius(body));
  _sunRouteFrom.copy(start).sub(_sunRouteCentre);
  _sunRouteTo.copy(end).sub(_sunRouteCentre);
  const toLen = _sunRouteTo.length();
  if (toLen < floor) return out.lerpVectors(start, end, s);
  _sunRouteUp.copy(camera.up).normalize();
  let plan = _sunRoutePlans.get(start);
  if (!plan) {
    plan = chooseSunRoute(floor, toLen);
    _sunRoutePlans.set(start, plan);
  }
  const route = layOutSunRoute(plan, floor, toLen);
  if (route === SUN_ROUTE_STRING) return walkSunRoute(out, start, end, s);
  if (route === SUN_ROUTE_SPIRAL) return spiralSunRoute(out, s);
  return out.lerpVectors(start, end, s);
}
// Lays the planned route out for this frame's ends. Says which it is.
function layOutSunRoute(plan, floor, toLen) {
  if (plan.spiral) {
    if (!layOutSunSpiral(floor, plan.way)) return SUN_ROUTE_STRAIGHT;
    plan.way = _sunRoute.way;
    return SUN_ROUTE_SPIRAL;
  }
  if (!aimSunRoute(floor, toLen) || !tiltSunRoute(plan.tilt, floor, plan.way)) return SUN_ROUTE_STRAIGHT;
  plan.way = _sunRoute.way;
  return SUN_ROUTE_STRING;
}
// Sets up the line between the ends, the normal of the plane through it and
// the centre (made square to the line) and the way that normal tilts about
// the line. Says whether the line dips under the floor at all.
function aimSunRoute(floor, toLen) {
  _sunRouteLine.copy(_sunRouteTo).sub(_sunRouteFrom);
  const len2 = _sunRouteLine.lengthSq();
  const tc = len2 > 0 ? Math.min(1, Math.max(0, -_sunRouteFrom.dot(_sunRouteLine) / len2)) : 0;
  _sunRoutePoint.copy(_sunRouteFrom).addScaledVector(_sunRouteLine, tc);
  if (_sunRoutePoint.lengthSq() >= floor * floor) return false;
  _sunRouteLine.normalize();
  _sunRouteBase.copy(_sunRouteFrom).normalize();
  _sunRouteBase.crossVectors(_sunRouteBase, _sunRouteTo);
  if (_sunRouteBase.lengthSq() <= toLen * toLen * 1e-12) {
    _sunRouteAxis.copy(_sunRouteFrom).normalize();
    sunWayRound(_sunRouteBase, _sunRouteAxis);
  }
  _sunRouteBase.addScaledVector(_sunRouteLine, -_sunRouteBase.dot(_sunRouteLine)).normalize();
  _sunRouteTip.crossVectors(_sunRouteLine, _sunRouteBase);
  return true;
}
// Chooses the route for a tween's first frame (see above): the spiral, or the
// string in the plane at `tilt` (the straight line where the line clears the
// floor).
function chooseSunRoute(floor, toLen) {
  // The highest the route may climb toward either pole of the camera's orbit
  // axis (_sunRouteUp), as the sine of the latitude about the Sun's centre.
  const fromUp = _sunRouteUp.dot(_sunRouteFrom) / Math.max(_sunRouteFrom.length(), 1e-12);
  const toUp = _sunRouteUp.dot(_sunRouteTo) / toLen;
  const north = Math.max(Math.cos(controls.minPolarAngle + SUN_ROUTE_POLE_MARGIN), fromUp, toUp);
  const south = Math.max(Math.cos(Math.PI - controls.maxPolarAngle + SUN_ROUTE_POLE_MARGIN), -fromUp, -toUp);
  // `way` is the way round, set on the first frame that goes round.
  const spiral = { spiral: true, tilt: 0, way: 0 };
  const plane = (tilt) => ({ spiral: false, tilt, way: 0 });
  // A line that keeps clear of the floor (or only touches it) is the route,
  // unless it passes over a pole.
  if (!aimSunRoute(floor, toLen) || !tiltSunRoute(0, floor)) {
    return sunSegmentClimb(_sunRouteFrom, _sunRouteTo, north, south) > 0 && layOutSunSpiral(floor) ? spiral : plane(0);
  }
  if (sunRouteClimb(north, south) <= 0) return plane(0);
  // The least tilt either way, in steps, then narrowed down to the edge.
  let found = 0, foundClimb = Infinity;
  for (let k = 1; k <= SUN_ROUTE_TILT_MAX && !found; k++) {
    for (const sign of [1, -1]) {
      if (!tiltSunRoute(sign * k * SUN_ROUTE_TILT_STEP, floor)) continue;
      const climb = sunRouteClimb(north, south);
      if (climb <= 0 && climb < foundClimb) {
        found = sign * k;
        foundClimb = climb;
      }
    }
  }
  if (!found) return layOutSunSpiral(floor) ? spiral : plane(0);
  const sign = Math.sign(found);
  let low = (Math.abs(found) - 1) * SUN_ROUTE_TILT_STEP;
  let high = Math.abs(found) * SUN_ROUTE_TILT_STEP;
  for (let i = 0; i < 12; i++) {
    const mid = (low + high) / 2;
    if (tiltSunRoute(sign * mid, floor) && sunRouteClimb(north, south) <= 0) high = mid;
    else low = mid;
  }
  return plane(sign * high);
}
// Lays out the spiral round the camera's orbit axis through the Sun's centre
// from the start to the end: latitude and the turn round the axis change
// evenly, the turn the shorter way (forward, the way the planets go, when the
// ends are opposite) or, given `way`, that way, and the distance evenly in its
// log. Every point is
// between the ends' latitudes and no nearer than the nearer end, so it keeps
// off both the caps and the floor. Says whether the ends have a direction
// round the axis to start it from.
function layOutSunSpiral(floor, way = 0) {
  _sunRouteU.copy(_sunRouteFrom).addScaledVector(_sunRouteUp, -_sunRouteUp.dot(_sunRouteFrom));
  _sunRoutePoint.copy(_sunRouteTo).addScaledVector(_sunRouteUp, -_sunRouteUp.dot(_sunRouteTo));
  const fromLen = _sunRouteFrom.length();
  const toLen = _sunRouteTo.length();
  if (_sunRouteU.lengthSq() <= fromLen * fromLen * 1e-12 || _sunRoutePoint.lengthSq() <= toLen * toLen * 1e-12) return false;
  _sunRouteU.normalize();
  _sunRouteSide.crossVectors(_sunRouteUp, _sunRouteU);
  let turn = Math.atan2(_sunRoutePoint.dot(_sunRouteSide), _sunRoutePoint.dot(_sunRouteU));
  if (turn < -Math.PI + 1e-9) turn = Math.PI;
  if (way > 0 && turn < 0) turn += 2 * Math.PI;
  if (way < 0 && turn > 0) turn -= 2 * Math.PI;
  const route = _sunRoute;
  route.way = turn < 0 ? -1 : 1;
  route.latFrom = Math.asin(THREE.MathUtils.clamp(_sunRouteUp.dot(_sunRouteFrom) / fromLen, -1, 1));
  route.latTo = Math.asin(THREE.MathUtils.clamp(_sunRouteUp.dot(_sunRouteTo) / toLen, -1, 1));
  route.turn = turn;
  route.logFrom = Math.log(Math.max(fromLen, floor));
  route.logTo = Math.log(toLen);
  return true;
}
// The point a share `s` along the spiral.
function spiralSunRoute(out, s) {
  const route = _sunRoute;
  const lat = route.latFrom + (route.latTo - route.latFrom) * s;
  const turn = route.turn * s;
  const distance = Math.exp(route.logFrom + (route.logTo - route.logFrom) * s);
  const across = distance * Math.cos(lat);
  return out.copy(_sunRouteCentre)
    .addScaledVector(_sunRouteU, across * Math.cos(turn))
    .addScaledVector(_sunRouteSide, across * Math.sin(turn))
    .addScaledVector(_sunRouteUp, distance * Math.sin(lat));
}
// Lays the route out in the plane through the line tilted `tilt` from the
// one through the centre, round the circle the shorter way or, given `way`
// (1 or -1 about the plane's normal), that way. Says whether it goes round
// at all.
function tiltSunRoute(tilt, floor, way = 0) {
  _sunRouteNormal.copy(_sunRouteBase).multiplyScalar(Math.cos(tilt)).addScaledVector(_sunRouteTip, Math.sin(tilt));
  const offset = _sunRouteNormal.dot(_sunRouteFrom);
  const radius2 = floor * floor - offset * offset;
  if (!(radius2 > 0)) return false;
  const radius = Math.sqrt(radius2);
  // The ends seen from the circle's centre, in its plane. A start under the
  // floor counts as on the circle, so the route starts on it.
  _sunRouteDisc.copy(_sunRouteNormal).multiplyScalar(offset);
  _sunRouteU.copy(_sunRouteFrom).sub(_sunRouteDisc);
  _sunRoutePoint.copy(_sunRouteTo).sub(_sunRouteDisc);
  const fromLen = Math.max(_sunRouteU.length(), radius);
  const toLen = _sunRoutePoint.length();
  if (_sunRouteU.lengthSq() <= radius2 * 1e-12 || toLen < radius) return false;
  _sunRouteU.normalize();
  // The end's angle round the circle from the start, the shorter way (0 to
  // 180 degrees) or the given one.
  if (!way) {
    _sunRouteAxis.crossVectors(_sunRouteU, _sunRoutePoint);
    way = _sunRouteAxis.dot(_sunRouteNormal) < 0 ? -1 : 1;
  }
  _sunRouteAxis.copy(_sunRouteNormal).multiplyScalar(way);
  _sunRouteSide.crossVectors(_sunRouteAxis, _sunRouteU);
  let endAngle = Math.atan2(_sunRoutePoint.dot(_sunRouteSide), _sunRoutePoint.dot(_sunRouteU));
  if (endAngle < 0) endAngle += 2 * Math.PI;
  // Where the string touches the circle, as angles round from the start.
  const touch = Math.acos(radius / fromLen);
  const leave = endAngle - Math.acos(Math.min(1, radius / toLen));
  if (!(leave > touch)) return false;
  _sunRoute.way = way;
  _sunRoute.radius = radius;
  _sunRoute.touch = touch;
  _sunRoute.leave = leave;
  // A start on the circle has no in leg, and squared back from the root its
  // radius can come out a rounding under radius2.
  _sunRoute.inLeg = Math.sqrt(Math.max(0, fromLen * fromLen - radius2));
  _sunRoute.outLeg = Math.sqrt(Math.max(0, toLen * toLen - radius2));
  _sunRoute.length = _sunRoute.inLeg + radius * (leave - touch) + _sunRoute.outLeg;
  return true;
}
// The point on the route's circle `angle` round from the start.
function sunRoutePoint(out, angle) {
  return out.copy(_sunRouteCentre).add(_sunRouteDisc)
    .addScaledVector(_sunRouteU, _sunRoute.radius * Math.cos(angle))
    .addScaledVector(_sunRouteSide, _sunRoute.radius * Math.sin(angle));
}
// The point a share `s` of the laid-out route's length along it.
function walkSunRoute(out, start, end, s) {
  const route = _sunRoute;
  let u = s * route.length;
  if (u < route.inLeg) return out.lerpVectors(start, sunRoutePoint(_sunRoutePoint, route.touch), u / route.inLeg);
  u -= route.inLeg;
  const arc = route.radius * (route.leave - route.touch);
  if (u < arc) return sunRoutePoint(out, route.touch + u / route.radius);
  u -= arc;
  sunRoutePoint(_sunRoutePoint, route.leave);
  return out.lerpVectors(_sunRoutePoint, end, route.outLeg > 0 ? Math.min(1, u / route.outLeg) : 1);
}
// How far the laid-out route climbs past `north` toward the orbit axis's
// pole, or past `south` toward the other (sines of the latitude about the
// Sun's centre). Every point of the circle is a floor's height from the
// centre, so over the arc that sine is a cosine wave in the angle, and its
// peak is exact.
const _sunRouteSample = new THREE.Vector3();
function sunRouteClimb(north, south) {
  const route = _sunRoute;
  const floor = Math.sqrt(route.radius * route.radius + _sunRouteDisc.lengthSq());
  const level = _sunRouteDisc.dot(_sunRouteUp) / floor;
  const along = route.radius * _sunRouteU.dot(_sunRouteUp) / floor;
  const across = route.radius * _sunRouteSide.dot(_sunRouteUp) / floor;
  const wave = (angle) => level + along * Math.cos(angle) + across * Math.sin(angle);
  let high = Math.max(wave(route.touch), wave(route.leave));
  let low = Math.min(wave(route.touch), wave(route.leave));
  const peak = Math.atan2(across, along);
  for (const angle of [peak - 2 * Math.PI, peak, peak + Math.PI, peak + 2 * Math.PI, peak - Math.PI]) {
    if (angle > route.touch && angle < route.leave) {
      high = Math.max(high, wave(angle));
      low = Math.min(low, wave(angle));
    }
  }
  let climb = Math.max(high - north, -low - south);
  for (let leg = 0; leg < 2; leg++) {
    if (!((leg ? route.outLeg : route.inLeg) > 0)) continue;
    sunRoutePoint(_sunRouteAxis, leg ? route.leave : route.touch).sub(_sunRouteCentre);
    climb = Math.max(climb, leg
      ? sunSegmentClimb(_sunRouteAxis, _sunRouteTo, north, south)
      : sunSegmentClimb(_sunRouteFrom, _sunRouteAxis, north, south));
  }
  return climb;
}
// The same for the straight segment from `from` to `to` (from the Sun's
// centre). Along it the sine is (a + b t) / |A + t (B - A)|, which turns once
// at most, where (b d - a e) t = a d - b c. Sampled, the out leg missed its
// peak a tenth of a radius off the floor, where it climbs past the arc, and
// the cap held the camera there.
function sunSegmentClimb(from, to, north, south) {
  _sunRouteSample.copy(to).sub(from);
  const a = from.dot(_sunRouteUp);
  const b = _sunRouteSample.dot(_sunRouteUp);
  const c = from.lengthSq();
  const d = from.dot(_sunRouteSample);
  const e = _sunRouteSample.lengthSq();
  const turn = (a * d - b * c) / (b * d - a * e);
  let climb = -Infinity;
  for (const t of [0, 1, turn]) {
    if (!(t >= 0 && t <= 1)) continue;
    const sine = (a + b * t) / Math.sqrt(Math.max(c + t * (2 * d + t * e), 1e-24));
    climb = Math.max(climb, sine - north, -sine - south);
  }
  return climb;
}
function updateSunCloseCamera() {
  if (!camera || !controls || flyMode || scaleModeActive || rocketryActive) return;
  const body = getSolidSun();
  if (!body) {
    if (camera.near === _sunCloseNear) setCameraNearPlane(_sunCloseOwnerNear);
    _sunCloseNear = NaN;
    return;
  }
  const radius = getBodyBaseRadius(body);
  body.getWorldPosition(_sunCloseCentre);
  const floor = getSunZoomFloor(radius);
  // The zoom limit is only held on the Sun's own views, and waits for tweens
  // to land. Whatever moved the camera inside the floor, with any body
  // selected (a teleport, a deep link, a planet's follow), is lifted straight
  // out from the centre. The camera tweens go round the Sun on their own
  // (lerpRoundSun); this catches any other, and the tween never sees it, as
  // it places the camera from its own ends each step.
  if (body === getHeldSun()) holdSunZoomLimit(radius, floor);
  if (camera.position.distanceToSquared(_sunCloseCentre) < floor * floor * (1 - 1e-9)) {
    liftOntoSunFloor(camera.position, floor, _sunCloseFallbackDir);
    camera.lookAt(controls.target);
  }
  if (camera.near !== _sunCloseNear) {
    _sunCloseOwnerNear = camera.near;
    _sunCloseNear = NaN;
  }
  const height = camera.position.distanceTo(_sunCloseCentre) - radius;
  const near = Math.max(height * 0.2, radius * 0.0005);
  if (near < _sunCloseOwnerNear) {
    // Lowered at once, raised only past a 2% step, so a slow drift does not
    // rebuild the projection every frame.
    if (near < camera.near || near > camera.near * 1.02) {
      setCameraNearPlane(near);
      _sunCloseNear = near;
    }
  } else if (camera.near === _sunCloseNear) {
    setCameraNearPlane(_sunCloseOwnerNear);
    _sunCloseNear = NaN;
  }
}

function updateFocusedObjectFollow(delta) {
  if (!isIssObject(selectedObject) || flyMode || scaleModeActive) releaseIssCameraUp();
  // To-Scale owns the camera; following the hidden focused planet dragged
  // the lineup shot along that planet's orbit. So do the tweens that leave
  // fly mode for the overview, which keep the planet flown from selected
  // until they land: gsap sets the camera and the target each frame, and
  // the damped follow then pulled both a share of the way to Venus or
  // Jupiter, so the flight wandered off toward the planet and, when the
  // selection cleared on landing, jumped the rest of the way in one frame
  // (37 solar radii from Jupiter). The Earth's rigid follow only added its
  // own motion, which is why leaving from there looked smooth.
  if (!selectedObject || !controls || !camera || flyMode || scaleModeActive || isAnimating || exitFlyTweens.length > 0) {
    _focusPrevObj = null;
    _earthFollowMesh = null;
    return;
  }
  const data = selectedObject.userData;
  if (!data || !(
    data.isPlanet ||
    data.isMoon ||
    data.isSatellite ||
    data.isWormhole
  )) {
    _focusPrevObj = null;
    return;
  }

  selectedObject.getWorldPosition(_focusObjPos);

  // Rigid-by-delta follow for satellites AND remote-system bodies.
  // Remote planets orbit at huge scene radii (Gargantua's Miller sits
  // ~110k units out), so even a modest angular rate is a large per-
  // frame linear motion. The damped follow below lags that badly —
  // the planet drifts out of frame and reads as "tracking is broken."
  // The rigid path translates camera AND controls.target by the body's
  // exact per-frame world delta, so the framing locks regardless of
  // how fast the body sweeps. (Same invariant the ISS follow relies on:
  // the camera-to-target offset never changes between user drags.)
  const useRigidFollow = data.isSatellite || selectedObject === planets.earth || (activeSystemId && activeSystemId !== 'sol')
    // An eclipse plays at up to five minutes a second, when the Moon covers
    // its own width in a few seconds; the damped follow would lose it.
    || eclipseModeActive()
    // A probe is a few units across and covers AU a second at the fast
    // presets (and jumps with the clock).
    || data.isSpaceProbe;

  const continuedFollow = _focusPrevObj === selectedObject;
  if (useRigidFollow) {
    if (continuedFollow) {
      _focusTargetDelta.copy(_focusObjPos).sub(_focusPrevObjPos);
      controls.target.add(_focusTargetDelta);
      camera.position.add(_focusTargetDelta);
    } else if (_focusLandedOn === selectedObject && (data.isSatellite || data.isSpaceProbe)) {
      _focusTargetDelta.copy(_focusObjPos).sub(controls.target);
      controls.target.add(_focusTargetDelta);
      camera.position.add(_focusTargetDelta);
    }
    _focusLandedOn = null;
    _focusPrevObjPos.copy(_focusObjPos);
    _focusPrevObj = selectedObject;
  } else {
    const followAlpha = dampingFactor(delta, FOCUS_FOLLOW_DAMPING);
    _focusTargetDelta.copy(_focusObjPos).sub(controls.target).multiplyScalar(followAlpha);
    controls.target.add(_focusTargetDelta);
    camera.position.add(_focusTargetDelta);
    _focusPrevObj = null;
  }

  // Rotate the inspection offset with the station's orbit, after applying
  // its translation. This preserves an Earth-facing composition instead of
  // gradually turning toward space as the station rounds the planet.
  if (data.isSatellite && data.name === 'ISS' && planets.earth) {
    planets.earth.getWorldPosition(_issEarthPos);
    _issWorldOffset.copy(_focusObjPos).sub(_issEarthPos).normalize();
    if (continuedFollow) {
      _issOrbitalDeltaQuat.setFromUnitVectors(_issPrevWorldOffset, _issWorldOffset);
      _issCamRel.copy(camera.position).sub(_focusObjPos).applyQuaternion(_issOrbitalDeltaQuat);
      _issTargetRel.copy(controls.target).sub(_focusObjPos).applyQuaternion(_issOrbitalDeltaQuat);
      camera.position.copy(_focusObjPos).add(_issCamRel);
      controls.target.copy(_focusObjPos).add(_issTargetRel);
      camera.lookAt(controls.target);
    }
    _issPrevWorldOffset.copy(_issWorldOffset);
    // Level horizon: with world up the Earth hung overhead whenever the
    // station was south of the equator.
    camera.up.lerp(_issWorldOffset, 1 - Math.exp(-delta * 3)).normalize();
    camera.lookAt(controls.target);
    _issUpActive = true;
  }

  // At surface distances keep the inspected geography under the camera as
  // Earth spins. Artistic time otherwise sweeps hundreds of kilometres of
  // terrain past a close camera every second, constantly replacing tiles.
  const earthMesh = selectedObject === planets.earth ? getEarthMeshForTerrain() : null;
  const trackSurface = earthMesh && camera.position.distanceTo(_focusObjPos) < getBodyBaseRadius(selectedObject) * 1.2;
  if (trackSurface) {
    earthMesh.getWorldQuaternion(_earthFollowNow);
    if (_earthFollowMesh === earthMesh) {
      _earthFollowDelta.copy(_earthFollowSpin).invert().premultiply(_earthFollowNow);
      camera.position.sub(_focusObjPos).applyQuaternion(_earthFollowDelta).add(_focusObjPos);
      controls.target.sub(_focusObjPos).applyQuaternion(_earthFollowDelta).add(_focusObjPos);
      camera.lookAt(controls.target);
    }
    _earthFollowSpin.copy(_earthFollowNow);
    _earthFollowMesh = earthMesh;
  } else _earthFollowMesh = null;

  // Earth inspection follows the displaced surface in both display scales.
  // The old 1.35-radius safety bubble silently undid the close zoom limits.
  const earthInspection = selectedObject === planets.earth;
  if (earthInspection) {
    const radius = getBodyBaseRadius(selectedObject);
    controls.minDistance = radius * (1 + EARTH_INSPECTION_CLEARANCE / EARTH_METRES);
    const altitude = camera.position.distanceTo(_focusObjPos) / radius * EARTH_METRES - EARTH_METRES;
    controls.zoomSpeed = THREE.MathUtils.clamp(altitude / EARTH_METRES * 4, 0.0015, 1.2);
    controls.rotateSpeed = THREE.MathUtils.lerp(0.0004, 0.8, THREE.MathUtils.smoothstep(altitude, 2000, 2e6));
    setCameraNearPlane(radius * Math.max(1, Math.min(1000, altitude * 0.05)) / EARTH_METRES);
  } else if (data.shapeReach && !realisticScaleMode) {
    // A small body's near plane (1% of its reach at the zoom floor) left the
    // depth buffer no precision for planets once zoomed out from it: it
    // follows the gap to the body's furthest point, up to the usual near.
    const reach = getBodyBaseRadius(selectedObject) * data.shapeReach;
    const gap = camera.position.distanceTo(_focusObjPos) - reach;
    const near = THREE.MathUtils.clamp(gap * 0.3, reach * 0.01, getOverviewCameraNear());
    if (Math.abs(camera.near - near) > near * 0.02) setCameraNearPlane(near);
  }
  const bodyClearance = earthInspection
    ? getBodyVisualSurfaceRadius(selectedObject, _focusFromBody.copy(camera.position).sub(_focusObjPos).normalize())
        + getBodyBaseRadius(selectedObject) * EARTH_INSPECTION_CLEARANCE / EARTH_METRES
    : data.isSatellite
    ? Math.max(
        ISS_CLOSE_MIN_DISTANCE,
        (data.physicalRadius || data.radius * 0.0625) * 0.02,
        camera.near * 2
      )
    // The drawn radius: in realistic mode the authored one is up to 18x
    // the body (Mars), and this bubble held the camera 24 radii out.
    // A small body with a shape model (src/smallbodies/) keeps just
    // outside its own furthest point, so its surface can be seen close.
    : Math.max(
        getBodyBaseRadius(selectedObject) * (data.shapeReach ? data.shapeReach * 1.02 : 1.35),
        camera.near * 3
      );
  _focusFromBody.copy(camera.position).sub(_focusObjPos);
  const distToCenter = _focusFromBody.length();
  if (distToCenter < bodyClearance) {
    if (distToCenter > 1e-6) {
      _focusSafeDir.copy(_focusFromBody).normalize();
    } else {
      _focusSafeDir.set(1, 0.25, 1).normalize();
    }
    camera.position.copy(_focusObjPos).addScaledVector(_focusSafeDir, bodyClearance);
  }

}

function updateEarthCloseView() {
  if (renderer && planets.earth) {
    const earthMesh = getEarthMeshForTerrain();
    // A planet print pins the weather's clock (window.__printWeatherNow), so
    // the clouds hold still while its tiles are drawn one after another.
    const weatherNow = typeof window.__printWeatherNow === 'number' ? window.__printWeatherNow : performance.now();
    updateEarthWeather(renderer, earthMesh, weatherNow, !!earthCloseView || rocketryActive);
    const eu = earthMesh?.material?.uniforms;
    // Rocketry sets it from its own quality tier while it is up
    // (orbitclouds.js), so the globe and its orbital shell carve alike.
    if (eu?.uCloudTopQuality && !rocketryActive) eu.uCloudTopQuality.value = activeGraphicsPreset === 'low' ? 0 : 1;
    // A/B for the captures: window.__earthCloudTops = false draws the map alone.
    if (eu?.uCloudVolOn && window.__earthCloudTops === false) eu.uCloudVolOn.value = 0;
    // XR: the cloud map alone, as on Low (the carve and its volumes are the
    // globe's costliest part).
    if (eu && renderer.xr.isPresenting) {
      if (eu.uCloudTopQuality) eu.uCloudTopQuality.value = 0;
      if (eu.uCloudVolOn && !xrEarthCarve) eu.uCloudVolOn.value = 0;
    }
    // The sea's waves up close and the camera's height for the globe's haze
    // (oceanwaves.js); rocketry draws its own sea.
    updateOceanWaves(renderer, earthMesh, camera, performance.now(), !rocketryActive && !rocketryLoading && !scaleModeActive && activeSystemId === 'sol', activeGraphicsPreset !== 'low');
    // The cloud map's 1 km pages around the view (rocketry owns them while it is up).
    if (!rocketryActive && !rocketryLoading && !scaleModeActive && activeSystemId === 'sol') {
      updateViewerCloudImagery(renderer, earthMesh, camera, performance.now());
      // The city lights' 500 m pages, while the view is close and reaches the night side.
      updateViewerNightImagery(renderer, earthMesh, camera, performance.now(), eu?.sunPosition?.value);
    }
  }
  if (!earthCloseView && renderer && planets.earth && !rocketryActive && !rocketryLoading) {
    const radius = getBodyBaseRadius(planets.earth);
    if (camera.position.distanceTo(planets.earth.getWorldPosition(_focusObjPos)) < radius * 5) {
      earthCloseView = createEarthCloseView({
        renderer, camera,
        get composer() { return composer; },
        get composerRenderPass() { return composerRenderPass; },
        getEarthMesh: getEarthMeshForTerrain,
        getSunPosition: () => sun.getWorldPosition(new THREE.Vector3()),
        getLoadedTextures: () => loadedTextures,
        getSimulationJD: () => simulationJD,
        getTimeSpeed: () => timeSpeed,
        getQuality: () => activeGraphicsPreset === 'low' ? 'low' : activeGraphicsPreset === 'medium' ? 'medium' : 'high',
        suspended: () => rocketryActive || rocketryLoading || scaleModeActive || activeSystemId !== 'sol',
        isEarthFocused: () => flyMode || ((!isAnimating) && (selectedObject === planets.earth || (selectedObject?.userData?.isSatellite && !selectedObject.userData.atL2))),
        getCloudOccluder: () => flyMode ? playerShip : selectedObject?.userData?.isSatellite && !selectedObject.userData.atL2 ? selectedObject : null,
        // Climate mode's data lies over the ground: the cloud volumes (drawn
        // after the scene) stand aside while a layer shows (src/climate/).
        cloudsHeld: () => Boolean(climateMode && climateMode.coversGround),
        // The low-orbit sky dome draws over the scaled scene: the
        // Earth-orbiting models and the fly ship in front of the air keep it
        // off (earthclose.js).
        getSkyOccluders: () => earthSkyOccluders(),
        // A solar eclipse in the close sky (src/eclipse/totality.js): the
        // Sun's and the Moon's centres at true scale, and their radii in km.
        getEclipseBodies: () => {
          if (!realisticScaleMode || !sun || giantShown) return null;
          const moon = (moons.earth || []).find(isEarthsMoon);
          if (!moon || !moon.visible) return null;
          return {
            sun: sun.getWorldPosition(_eclipseBodySun), moon: moon.getWorldPosition(_eclipseBodyMoon),
            sunR: R_SUN_KM * (FUTURE_ON ? futureState.sunScale : 1), moonR: R_MOON_KM,
          };
        },
        // The far future's red giant, drawn by the dome from the ground.
        getGiant: () => (giantShown ? { sun: sun.getWorldPosition(_eclipseBodySun), sunR: R_SUN_KM * futureState.sunScale } : null),
        setSunDrawnBySky
      });
    }
  }
  // Rocketry owns the same atlas while it is active. Do not cancel its
  // requests every normal-viewer render; hand ownership over only on entry.
  if (!rocketryActive && !rocketryLoading) earthCloseView?.update(performance.now());
}

// While the close sky draws an eclipsed Sun (src/eclipse/totality.js), the
// scene's own Sun meshes stand aside on a layer the camera does not draw,
// which leaves the group, its light and the code that shows and hides its
// parts every frame (the prominences, the glow) as they are. The Moon's mesh
// goes aside too: the sky draws its silhouette, and the mesh, lit from
// almost exactly behind, caught the light along its faceted limb.
const _eclipseBodySun = new THREE.Vector3();
const _eclipseBodyMoon = new THREE.Vector3();
const SUN_ASIDE_LAYER = 31;
let sunDrawnBySky = false;
function setSunDrawnBySky(on) {
  sunDrawnBySky = !!on;
  keepSunAside();
}
// Every frame while the sky draws the Sun: parts of the Sun built after the
// eclipse began (the prominences are made as they come into view) go aside
// too.
function keepSunAside() {
  if (!sun) return;
  const moon = (moons.earth || []).find(isEarthsMoon);
  const aside = (on) => (o) => {
    if (o.isLight || o === sun) return;
    if (on) {
      if (o.userData.eclipseLayers == null) o.userData.eclipseLayers = o.layers.mask;
      o.layers.set(SUN_ASIDE_LAYER);
    } else if (o.userData.eclipseLayers != null) {
      o.layers.mask = o.userData.eclipseLayers;
      delete o.userData.eclipseLayers;
    }
  };
  sun.traverse(aside(sunDrawnBySky || giantShown));
  if (moon) moon.traverse(aside(sunDrawnBySky && !giantShown));
}

// The far future's Sun as a red giant (?whatif=future, src/redgiant.js):
// from futureState.sunScale GIANT_FROM up it takes the Sun's place at true
// scale, its light turns orange, and futureState.orbits (planet names)
// shows only those planets' orbit lines.
let redGiant = null;
let giantShown = false;
const _giantCenter = new THREE.Vector3();
const _giantBody = new THREE.Vector3();
let giantNear = false;
const _giantLight = new THREE.Color(1, 0.62, 0.38);
function updateFutureSun(now) {
  if (!FUTURE_ON || !sun) return;
  const on = realisticScaleMode && futureState.sunScale >= GIANT_FROM;
  if (on && !redGiant) {
    redGiant = createRedGiant();
    scene.add(redGiant.group);
  }
  if (on !== giantShown) {
    giantShown = on;
    keepSunAside();
    const light = sun.children.find((c) => c.isPointLight);
    if (light) {
      if (light.userData.baseColor == null) light.userData.baseColor = light.color.getHex();
      if (on) light.color.copy(_giantLight); else light.color.setHex(light.userData.baseColor);
    }
  } else if (giantShown) keepSunAside();
  if (redGiant) {
    redGiant.group.visible = on;
    if (on) redGiant.update(now / 1000, sun.getWorldPosition(_giantCenter), (getRealisticBodyRadius('sun') || 1) * futureState.sunScale);
  }
  if (Array.isArray(futureState.orbits)) {
    orbitLines.forEach((line, i) => { line.visible = futureState.orbits.includes(planetOrder[i]); });
  }
  const earthU = getEarthMeshForTerrain()?.material?.uniforms;
  if (earthU && earthU.uNightLights) earthU.uNightLights.value = futureState.lights ?? 1;
  globalThis.__earthLights = futureState.lights ?? 1;
  // Far out at true scale the overview's 0.001 near plane leaves the depth
  // buffer no precision at the giant (orbit lines showed through it): the
  // near plane follows the nearest surface while it is shown.
  if (giantShown) {
    let nearest = camera.position.distanceTo(_giantCenter) - (getRealisticBodyRadius('sun') || 1) * futureState.sunScale;
    for (const body of [...Object.values(planets), (moons.earth || []).find(isEarthsMoon)]) {
      if (!body) continue;
      nearest = Math.min(nearest, camera.position.distanceTo(body.getWorldPosition(_giantBody)) - getBodyBaseRadius(body));
    }
    const near = Math.max(getOverviewCameraNear(), nearest * 0.2);
    if (Math.abs(camera.near - near) > near * 0.02) setCameraNearPlane(near);
    giantNear = true;
  } else if (giantNear) {
    setCameraNearPlane(getOverviewCameraNear());
    giantNear = false;
  }
}
// The Sun's glare left for the share of its disc showing from the ground,
// for the eye's adaptation and the lens's glare (which also stands aside
// once the eclipsed disc is resolved, updateLensFlare).
function eclipseGlare() {
  const ecl = earthCloseView && earthCloseView.eclipse;
  return ecl && ecl.on ? ecl.visible : 1;
}

// The viewer's Moon up close. moon.jpg holds 11 texels per degree; from the
// closest zoom the middle of the disc shows about 20 pixels per degree (41 on
// a retina display), so it is magnified two to four times. The NASA CGI Moon
// Kit tiles rocketry streams (rocketry/moonimagery.js, 512 px per 11.25
// degrees at level 4, the finest the project ships) take over where the view
// needs them, through the same body-generic streamer the viewer's Earth uses
// (earthclose.js): the camera in the Moon mesh's own frame, in metres.
// Capped at level 4: past it the static site has no tiles and the streamer
// falls back to the grey Kaguya mosaic.
const MOON_VIEW_RANGE = 8; // Moon radii from the centre; moon.jpg suffices past it
const MOON_VIEW_LEVEL_CAP = 4;
let moonViewStream = null;
let moonViewStreaming = false;
const _moonViewPosition = new THREE.Vector3();
const _moonViewOrientation = new THREE.Quaternion();
const _moonViewCameraQuat = new THREE.Quaternion();
const _moonViewInverse = new THREE.Matrix4();
let moonViewMesh = null;
function updateMoonCloseView() {
  const stop = () => {
    if (moonViewStreaming) moonViewStream?.cancel();
    moonViewStreaming = false;
  };
  // window.__moonViewImageryOff = true draws moon.jpg alone (before/after).
  if (typeof window !== 'undefined' && window.__moonViewImageryOff) {
    stop();
    moonImageryUniforms.uMoonImageryOn.value = 0;
    return;
  }
  // Rocketry owns the Moon's atlas while it is up (moonsurface.js).
  if (!renderer || rocketryActive || rocketryLoading || scaleModeActive || activeSystemId !== 'sol'
    || renderer.xr.isPresenting || activeGraphicsPreset === 'low') { stop(); return; }
  // The Moon's mesh, found once (getObjectByName walks the Earth's subtree)
  // and looked up again only if it is rebuilt or detached.
  let mesh = moonViewMesh;
  if (!mesh || !mesh.parent) {
    const moonGroup = planets.earth?.getObjectByName('Moon');
    mesh = moonViewMesh = moonGroup?.children.find((child) => child.isMesh && child.geometry?.parameters?.radius) || null;
  }
  if (!mesh) { stop(); return; }
  const radius = mesh.geometry.parameters.radius;
  mesh.updateWorldMatrix(true, false);
  _moonViewPosition.setFromMatrixPosition(camera.matrixWorld).applyMatrix4(_moonViewInverse.copy(mesh.matrixWorld).invert());
  // A planet print sees the Moon from the Earth's distance and streams the
  // tiles from there (window.__printMoonRange, scripts/prints/render-planets.mjs).
  if (_moonViewPosition.length() > radius * ((typeof window !== 'undefined' && window.__printMoonRange) || MOON_VIEW_RANGE)) {
    stop();
    // A phone frees the atlas once the Moon is a small disc.
    if (RUNNING_ON_MOBILE && _moonViewPosition.length() > radius * MOON_VIEW_RANGE * 4) moonViewStream?.release();
    return;
  }
  _moonViewPosition.multiplyScalar(MOON_RADIUS / radius);
  mesh.getWorldQuaternion(_moonViewOrientation).invert().multiply(camera.getWorldQuaternion(_moonViewCameraQuat));
  if (!moonViewStream) {
    moonViewStream = getMoonImageryStream(renderer);
    // The stream's stats for checks from the console or a test.
    if (typeof window !== 'undefined') window.__moonImagery = moonViewStream;
  }
  moonViewStreaming = true;
  moonViewStream.update({ now: performance.now(), position: _moonViewPosition, orientation: _moonViewOrientation,
    lens: camera.projectionMatrix, budget: 48, maxLevel: MOON_VIEW_LEVEL_CAP });
}

const viewerSceneTarget = createViewerSceneTarget();

// The ISS model is drawn only while some of it is in view (src/issmodel.js):
// with culling off on its parts it was 80% of the viewer's draw calls in
// every view. window.__issGateOff = true draws it always, for before/after
// captures.
let issModelGroup = null;
const _issModelCenter = new THREE.Vector3();
const _issModelScale = new THREE.Vector3();
function updateIssModelVisibility() {
  if (!issModelGroup) {
    const iss = satelliteObjects.find((s) => s.mesh && s.mesh.userData && s.mesh.userData.name === 'ISS');
    if (!iss) return;
    issModelGroup = iss.mesh;
  }
  const model = issModelGroup.userData.issModel;
  if (!model || !camera) return;
  if (model.userData.compiling) {
    model.visible = false;
    return;
  }
  // A headset looks every way from the 2D camera; XR hides what it needs to.
  if (renderer.xr.isPresenting || window.__issGateOff) {
    model.visible = true;
    return;
  }
  camera.updateMatrixWorld();
  issModelGroup.getWorldPosition(_issModelCenter);
  const length = model.userData.drawnLength * issModelGroup.getWorldScale(_issModelScale).x;
  model.visible = modelInView({ center: _issModelCenter, length, camera });
}

function renderFrame() {
  updateEarthCloseView();
  if (climateMode) climateMode.frame();
  updateMoonCloseView();
  if (realSatellites) realSatellites.update();
  if (magnetosphereMode) magnetosphereMode.update();
  updateIssModelVisibility();
  updateCraftModelVisibility();
  // In XR mode, bypass the EffectComposer pipeline because the
  // post-processing passes write to a single 2D framebuffer which
  // doesn't match the XR runtime's per-eye layered targets. The base
  // renderer handles stereo rendering automatically when an XR
  // session is active.
  if (renderer.xr.isPresenting) {
    // The tier's layers stay hidden whatever the frame's own updates set
    // (the ISS outside the Cupola's windows excepted).
    // What you travelled to shows, whatever its layer (the ISS outside the
    // Cupola, Gargantua).
    _xrKeep.clear();
    for (let p = selectedObject; p; p = p.parent) _xrKeep.add(p);
    // The station's 361 meshes only while the headset holds tier 0.
    for (const [obj, was] of xrHidden) {
      const kept = (_xrKeep.has(obj) && !(xrTier > 0 && obj.userData && obj.userData.isSatellite))
        || (activeSystemId === 'gargantua' && obj.name === 'gargantua'); // its own system, nothing selected
      obj.visible = kept ? was : false;
    }
    // The head rides the 2D camera, near and far per pass (src/xr/experience.js);
    // black until it has loaded.
    if (xrExperience) xrExperience.render(performance.now());
    else renderer.clear();
    xrQualityTick(performance.now());
    return;
  }

  // The scene passes on their own multisampled target, the post chain on
  // single-sample ones (scenetarget.js); rocketry runs its own version.
  if (!rocketryActive && !rocketryLoading) viewerSceneTarget.sync(composer);

  const earthInspection = !rocketryActive && (flyMode || (selectedObject === planets.earth && !isAnimating))
    && camera.position.distanceTo(planets.earth.getWorldPosition(_focusObjPos)) < getBodyBaseRadius(planets.earth) * (flyMode ? 1.7 : 1.2);
  if ((!isSatelliteInspectionActive() && !earthInspection) || !composerRenderPass) {
    composer.render();
    return;
  }

  (earthInspection ? planets.earth : selectedObject).getWorldPosition(_originShiftVec);
  syncSatelliteLocalRenderCamera(_originShiftVec);
  collectShiftableUniforms();
  applyOriginShift(_originShiftVec);
  scene.position.sub(_originShiftVec);
  scene.updateMatrixWorld(true);

  const savedCamera = composerRenderPass.camera;
  composerRenderPass.camera = _satelliteLocalRenderCamera;
  try {
    composer.render();
  } finally {
    composerRenderPass.camera = savedCamera;
    scene.position.add(_originShiftVec);
    unapplyOriginShift(_originShiftVec);
    scene.updateMatrixWorld(true);
  }
}

const ufoPlanetReflectBodyPos = new THREE.Vector3();
const ufoPlanetReflectShipPos = new THREE.Vector3();
const ufoPlanetReflectDir = new THREE.Vector3(0, 0, -1);
const ufoPlanetReflectColor = new THREE.Color(0x9ed8ff);
let ufoPlanetReflectStrength = 0;

// Reused scratch vector for the per-frame Jupiter aurora drive, which runs
// every frame the body is in view (a fresh Vector3 here churned the GC).
const _auroraWorldPos = new THREE.Vector3();

function boundedAngularStep(rate, scaledDelta, maxStep) {
  const step = (rate || 0) * scaledDelta;
  return THREE.MathUtils.clamp(step, -maxStep, maxStep);
}

// A body's spin rate in celestialData is a display rate, 0.6 for a 24 h day
// (Jupiter's 1.45 is its 9.93 h), so its real period is 14.4 h / rate.
// Realistic scale turns the Sun, planets and dwarfs at their real speed from
// the sim clock, as Earth always does; the screensaver runs there at real
// time, where the display rate times timeSpeed = 1 spun Jupiter once every
// 4 seconds. Artistic mode keeps the display rates. The dwarfs' display
// rates are on no common scale, so they pass their period in hours.
function bodySpinStep(rate, scaledDelta, simStepJD, periodHours = 14.4 / (rate || 0)) {
  if (!realisticScaleMode) return boundedAngularStep(rate, scaledDelta, MAX_SPIN_STEP);
  const step = FULL_TURN / (periodHours * 3600) * simStepJD * 86400;
  return THREE.MathUtils.clamp(step, -MAX_SPIN_STEP, MAX_SPIN_STEP);
}

function dampingFactor(delta, damping) {
  return 1 - Math.exp(-damping * delta);
}

function wrapAngle(angle) {
  return THREE.MathUtils.euclideanModulo(angle, FULL_TURN);
}

// Earth's spin follows the simulation clock: the mesh angle is the Earth
// rotation angle for simulationJD (src/orbits/earthframe.js), so the Sun
// stands over the right meridian for the date and time. When the clock runs
// so fast that one frame's true turn passes MAX_SPIN_STEP, the drawn turn is
// held at MAX_SPIN_STEP (anything faster only strobes) and the shortfall
// builds up in earthSpinLag. Once the clock slows the lag is paid back
// forward at EARTH_SPIN_CATCHUP rad/s, so the globe never turns backward.
// earthSpinPose is an offset that window.__debug.poseEarthSpin sets for test
// scripts that frame a longitude against the Sun; it is 0 otherwise.
const EARTH_SPIN_CATCHUP = 1.5;
let earthSpinLag = 0;
let earthSpinPose = 0;
function earthSpinAngle(clockStepJD, delta) {
  const trueStep = clockStepJD * EARTH_SIDEREAL_TURNS_PER_DAY * FULL_TURN;
  if (Math.abs(trueStep) > MAX_SPIN_STEP) {
    earthSpinLag = wrapAngle(earthSpinLag + trueStep - Math.sign(trueStep) * MAX_SPIN_STEP);
  } else if (earthSpinLag > 0) {
    earthSpinLag = Math.max(0, earthSpinLag - EARTH_SPIN_CATCHUP * delta);
  }
  return wrapAngle(earthRotationAngle(simulationJD) - earthSpinLag + earthSpinPose);
}

// ?whatif=notilt (src/whatiftilt.js): the tilt group takes the state's
// obliquity and lean, the spin is held to a local solar time when a render
// asks, and the polar-ice lift's spin axis and the axis rod follow.
const _tiltSun = new THREE.Vector3();
const _tiltEarth = new THREE.Vector3();
const _tiltQuat = new THREE.Quaternion();
const _tiltInverse = new THREE.Quaternion();
let tiltAxisShown = false;
function applyTiltWhatIf(planet, tiltGroup) {
  // Tipped toward -Z (ecliptic longitude 90), then turned about the
  // ecliptic pole to lean toward longitude tiltState.lean.
  tiltGroup.rotation.set(
    -THREE.MathUtils.degToRad(tiltState.obliquity),
    THREE.MathUtils.degToRad(tiltState.lean - 90),
    0,
    'YXZ'
  );
  const mesh = tiltGroup.children[0];
  tiltGroup.getWorldQuaternion(_tiltQuat);
  if (tiltState.noon) {
    if (sun) sun.getWorldPosition(_tiltSun); else _tiltSun.set(0, 0, 0);
    planet.getWorldPosition(_tiltEarth);
    _tiltSun.sub(_tiltEarth).applyQuaternion(_tiltInverse.copy(_tiltQuat).invert());
    mesh.rotation.y = heldSpinAngle(_tiltSun, tiltState.noon);
  }
  const u = mesh.material && mesh.material.uniforms;
  if (u && u.uSpinAxis) u.uSpinAxis.value.set(0, 1, 0).applyQuaternion(_tiltQuat);
  if (tiltState.axis !== tiltAxisShown) {
    tiltAxisShown = tiltState.axis;
    setLessonEarthAxis(tiltAxisShown);
  }
}

// Mean anomaly → true anomaly via Kepler's equation (shared Newton
// solver from ephemeris.js). Lets the fallback orbits advance the mean
// anomaly at a constant rate and still obey Kepler's second law: the
// body sweeps equal areas in equal times, visibly speeding up at
// perihelion on eccentric orbits instead of gliding uniformly.
function trueAnomalyFromMean(M, e) {
  if (!e) return M;
  const E = solveKepler(M, e);
  return Math.atan2(Math.sqrt(1 - e * e) * Math.sin(E), Math.cos(E) - e);
}

function isLocalEnvironment() {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  // A debug build of the App Store app adds debug=1 (apple/, WebHost.swift);
  // the release build never does.
  if (window.location.protocol === 'oss:') return /[?&]debug=1/.test(window.location.search);
  return window.location.protocol === 'file:'
    || host === 'localhost'
    || host === '127.0.0.1'
    || host === '::1';
}

function applyLocalEnvironmentChrome() {
  if (typeof document === 'undefined' || !document.documentElement) return;
  document.documentElement.classList.toggle('local-environment', isLocalEnvironment());
}

function isFlyControlsEvent(event) {
  if (!event.target || !event.target.closest) return false;
  // The craft-admin tuner is dev-only but lives inside fly mode, so
  // include it here — otherwise its sliders would trigger
  // fly-mouse-look on mousedown and never receive drag input. The
  // photo-mode panel has the same problem: clicking its Capture
  // button was registering as a left-mouse-look mousedown on the
  // scene because the panel wasn't recognised as a fly-control UI
  // surface, which is why the camera would swing when the user
  // clicked Capture.
  return !!(
    event.target.closest('.fly-controls-panel') ||
    event.target.closest('.fast-travel-menu') ||
    event.target.closest('.autopilot-status') ||
    event.target.closest('.tuner-dock') ||
    event.target.closest('.photo-mode-panel') ||
    event.target.closest('#mobile-fly-controls') ||
    // Styled-dropdown trigger + its listbox. The listbox (.sd-menu) is
    // portaled to <body>, OUTSIDE every panel above, so without this an
    // option click in fly mode (craft / propulsion / range pickers) falls
    // through to the scene and fires mouse-look instead of selecting —
    // which read as "switching craft doesn't work in fly mode".
    event.target.closest('.sd-menu') ||
    event.target.closest('.sd-trigger')
  );
}

function isScenePickEvent(event) {
  if (!event.target || !event.target.closest) return true;
  return !event.target.closest('button, input, select, textarea, a, aside, #controls-panel, #planet-nav');
}

function sceneClickMoved(event) {
  if (!sceneClickStart) return false;
  const dx = event.clientX - sceneClickStart.x;
  const dy = event.clientY - sceneClickStart.y;
  return Math.hypot(dx, dy) > SCENE_CLICK_DRAG_TOLERANCE_PX;
}

function setLensFlareHidden(delta = 1 / 60) {
  sunEarthAirUniform.value.w = 0;
  sunTintUniform.value.setRGB(1, 1, 1);
  if (!cinematicPass || !cinematicPass.uniforms) return;
  const alpha = dampingFactor(delta, 20);
  lensFlareOpacity += (0 - lensFlareOpacity) * alpha;
  if (lensFlareOpacity < 0.002) lensFlareOpacity = 0;
  lensFlareSpaceAmount += (0 - lensFlareSpaceAmount) * alpha;
  if (lensFlareSpaceAmount < 0.002) lensFlareSpaceAmount = 0;
  if (cinematicPass.uniforms.lensFlareAmount) {
    cinematicPass.uniforms.lensFlareAmount.value = lensFlareOpacity;
  }
  if (cinematicPass.uniforms.lensFlareSpace) {
    cinematicPass.uniforms.lensFlareSpace.value = lensFlareSpaceAmount;
  }
}

// What of the Sun reaches the camera this frame (measureSunLight): the share
// of its light past the planets and moons in front of it and through the
// Earth's air near its limb, the colour that light keeps, and the direction
// of its centre of light, where the glare centres (a Sun half behind a limb
// flares from its visible half). The disc is sampled at 64 limb-darkened
// points, each hidden by a body whose disc covers it with an edge one sample
// wide, so a limb sweeping across moves the share smoothly, and each taken
// through the air down to its ray's tangent height (sunAirTransmission). The
// old test went by the line to the Sun's centre, so the glare was gone with
// half the disc still up. At true scale the air also bends each point's
// light up over the limb (sunAirLift), so the disc rises lifted and squashed
// and the glare centres on where its light shows.
const SUN_LIGHT_SAMPLES = (() => {
  const out = [];
  const turn = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < 64; i++) {
    const r = Math.sqrt((i + 0.5) / 64);
    // Linear limb darkening, u = 0.6 in the visible.
    out.push({ x: r * Math.cos(i * turn), y: r * Math.sin(i * turn), w: 1 - 0.6 * (1 - Math.sqrt(1 - r * r)) });
  }
  return out;
})();
const sunLight = {
  frame: -1,
  // The share of the Sun's light (as luminance) that reaches the camera, the
  // colour it keeps (brightest channel 1) and its centre's direction.
  share: 1,
  tint: new THREE.Color(1, 1, 1),
  centre: new THREE.Vector3(),
  // Where that light left the disc, before the air bent it (refract), which
  // the glare's core is drawn round.
  source: new THREE.Vector3(),
  refract: false,
  // The body most in front of the disc, for the core's mask.
  cutter: false,
  cutterPosition: new THREE.Vector3(),
  cutterRadius: 0,
};
const _sunLightBodies = [];
let _sunLightCount = 0;
let _sunLightNearest = Infinity;
const _sunLightE1 = new THREE.Vector3();
const _sunLightE2 = new THREE.Vector3();
const _sunLightRay = new THREE.Vector3();
const _sunLightCross = new THREE.Vector3();
const _sunLightEarth = new THREE.Vector3();
const _sunLightAir = new THREE.Color();
const _sunLightSum = new THREE.Color();
const _sunLightUp = new THREE.Vector3(0, 1, 0);
const _sunLightSide = new THREE.Vector3(1, 0, 0);
const _sunLightSeen = new THREE.Vector3();
const _sunLightSeenSum = new THREE.Vector3();
const _sunLightSourceSum = new THREE.Vector3();
const _sunLightLimbUp = new THREE.Vector3();

// The tangent height (km) at which light that left the Sun along a ray of
// geometric tangent height g arrives through the Earth's air: a = g +
// K e^(-a / 8 km), K the bend at the ground (AIR_GRAZING_BEND) times the
// distance to the limb in km. a - g grows as a falls, so Newton from
// max(g, 0), below the root, climbs to it. Light with g + K <= 0 is bent
// into the ground: a negative height, for the soft edge.
function sunAirLift(g, K) {
  if (g + K <= 0) return (g + K) / (1 + K / 8);
  let a = Math.max(g, 0);
  for (let i = 0; i < 12; i++) {
    const bend = K * Math.exp(-a / 8);
    a -= (a - g - bend) / (1 + bend / 8);
  }
  return a;
}

// A planet or moon of drawn radius against the disc: 0 when the whole disc
// is behind it, else 1, listing it when its disc reaches the Sun's. lift:
// how far its air can bend the disc up over its limb (the Earth's, whose
// entry then leaves the cut to the air in measureSunLight).
function sunLightBody(object, radius, directionToSun, distanceToSun, sunAngular, lift = 0) {
  object.getWorldPosition(lensFlareBodyPos).sub(camera.position);
  const t = lensFlareBodyPos.dot(directionToSun);
  if (!(radius > 0) || t <= 0 || t >= distanceToSun) return 1;
  const dist = lensFlareBodyPos.length();
  const beta = Math.asin(Math.min(1, radius / dist));
  const sep = Math.atan2(_sunLightCross.crossVectors(lensFlareBodyPos, directionToSun).length(), t);
  const margin = 0.12 * sunAngular;
  if (sep >= beta + sunAngular + margin) return 1;
  if (sep <= beta - sunAngular - margin - lift) return 0;
  let entry = _sunLightBodies[_sunLightCount];
  if (!entry) entry = _sunLightBodies[_sunLightCount] = { dir: new THREE.Vector3(), beta: 0, air: false };
  entry.dir.copy(lensFlareBodyPos).multiplyScalar(1 / dist);
  entry.beta = beta;
  entry.air = lift > 0;
  _sunLightCount++;
  if (sep - beta < _sunLightNearest) {
    _sunLightNearest = sep - beta;
    sunLight.cutter = true;
    object.getWorldPosition(sunLight.cutterPosition);
    sunLight.cutterRadius = radius;
  }
  return 1;
}

function measureSunLight(directionToSun, distanceToSun) {
  if (sunLight.frame === frameCount) return sunLight;
  sunLight.frame = frameCount;
  sunLight.share = 1;
  sunLight.tint.setRGB(1, 1, 1);
  sunLight.centre.copy(directionToSun);
  sunLight.source.copy(directionToSun);
  sunLight.refract = false;
  sunLight.cutter = false;
  sunEarthAirUniform.value.w = 0;
  if (!planets || !camera || distanceToSun <= 0) return sunLight;
  const sunAngular = Math.asin(Math.min(1, (realisticScaleMode
    ? (getRealisticBodyRadius('sun') || celestialData.sun.radius)
    : celestialData.sun.radius) / distanceToSun));

  // The Earth's air, while the camera is above it and a ray to the disc
  // passes within 120 km of the ground (inside the air the sky and rocketry
  // draw their own Sun). At true scale, within the close sky's reach
  // (VIEWER_SKY_HANDOVER, where the dome lifts its disc the same way), it
  // bends the light over the limb, so the disc shows until it is
  // AIR_GRAZING_BEND past it. From further out the bent light rings the
  // whole Earth, round a centre in the middle of it.
  let air = false;
  const earth = planets.earth;
  let earthRadius = 0;
  if (earth && activeSystemId === 'sol') {
    earthRadius = getBodyBaseRadius(earth);
    earth.getWorldPosition(_sunLightEarth);
    const centreX = _sunLightEarth.x, centreY = _sunLightEarth.y, centreZ = _sunLightEarth.z;
    _sunLightEarth.sub(camera.position);
    const t = _sunLightEarth.dot(directionToSun);
    const dist = _sunLightEarth.length();
    if (t > 0 && t < distanceToSun && dist > earthRadius * (1 + 100 / 6371)) {
      const b = Math.sqrt(Math.max(0, dist * dist - t * t));
      if ((b - sunAngular * t) / earthRadius - 1 <= 120 / 6371) {
        air = true;
        sunLight.refract = realisticScaleMode && dist < earthRadius * (1 + VIEWER_SKY_HANDOVER[1] / EARTH_METRES);
        sunEarthAirUniform.value.set(centreX, centreY, centreZ, earthRadius);
      }
    }
  }

  _sunLightCount = 0;
  _sunLightNearest = Infinity;
  const hide = () => {
    sunLight.share = 0;
    sunLight.refract = false;
    sunEarthAirUniform.value.w = 0;
    return sunLight;
  };
  // Every drawn body with a radius of its own: the planets, the dwarf
  // planets kept with them (Eris, Makemake, Haumea and Sedna have no
  // celestialData entry, and were skipped, so the glare showed through
  // them), the moons and the belt bodies (Ceres, Vesta, Pallas, Hygiea).
  const blocks = (body, lift = 0) => body && body.visible && body.userData && body.userData.radius > 0
    && !sunLightBody(body, getBodyBaseRadius(body), directionToSun, distanceToSun, sunAngular, lift);
  for (const planet of Object.values(planets)) {
    if (blocks(planet, planet === earth && sunLight.refract ? AIR_GRAZING_BEND : 0)) return hide();
  }
  if (moons) {
    for (const list of Object.values(moons)) {
      for (const moon of list) if (blocks(moon)) return hide();
    }
  }
  for (const body of beltBodyObjects) if (blocks(body)) return hide();
  if (!_sunLightCount && !air) return sunLight;

  _sunLightE1.crossVectors(directionToSun, Math.abs(directionToSun.y) < 0.9 ? _sunLightUp : _sunLightSide).normalize();
  _sunLightE2.crossVectors(directionToSun, _sunLightE1);
  const tanA = Math.tan(sunAngular);
  const margin = 0.12 * sunAngular;
  const earthD2 = _sunLightEarth.lengthSq();
  let weight = 0;
  let lit = 0;
  _sunLightSum.setRGB(0, 0, 0);
  _sunLightSeenSum.set(0, 0, 0);
  _sunLightSourceSum.set(0, 0, 0);
  for (const sample of SUN_LIGHT_SAMPLES) {
    weight += sample.w;
    _sunLightRay.copy(directionToSun)
      .addScaledVector(_sunLightE1, sample.x * tanA)
      .addScaledVector(_sunLightE2, sample.y * tanA)
      .normalize();
    let v = 1;
    for (let k = 0; k < _sunLightCount && v > 0; k++) {
      const body = _sunLightBodies[k];
      if (body.air) continue;
      const angle = Math.atan2(_sunLightCross.crossVectors(_sunLightRay, body.dir).length(), _sunLightRay.dot(body.dir));
      v *= THREE.MathUtils.smoothstep(angle - body.beta, -margin, margin);
    }
    if (v <= 0) continue;
    _sunLightAir.setRGB(1, 1, 1);
    _sunLightSeen.copy(_sunLightRay);
    if (air) {
      const t = _sunLightEarth.dot(_sunLightRay);
      if (t > 0) {
        let hKm = (Math.sqrt(Math.max(0, earthD2 - t * t)) / earthRadius - 1) * 6371;
        if (sunLight.refract) {
          // Bent up over the limb: where this light shows, which is cut by
          // the limb (over a body's soft edge) only once even that is
          // below it, and the air it crossed there.
          const tKm = t / earthRadius * 6371;
          const seenKm = sunAirLift(hKm, AIR_GRAZING_BEND * tKm);
          v *= THREE.MathUtils.smoothstep(seenKm, -margin * tKm, margin * tKm);
          if (v <= 0) continue;
          _sunLightLimbUp.copy(_sunLightRay).multiplyScalar(t).sub(_sunLightEarth).normalize();
          _sunLightSeen.addScaledVector(_sunLightLimbUp, (seenKm - hKm) / tKm).normalize();
          hKm = seenKm;
        }
        sunAirTransmission(hKm, _sunLightAir);
      }
    }
    const w = sample.w * v;
    _sunLightSum.r += w * _sunLightAir.r;
    _sunLightSum.g += w * _sunLightAir.g;
    _sunLightSum.b += w * _sunLightAir.b;
    const y = w * (0.2126 * _sunLightAir.r + 0.7152 * _sunLightAir.g + 0.0722 * _sunLightAir.b);
    lit += y;
    _sunLightSeenSum.addScaledVector(_sunLightSeen, y);
    _sunLightSourceSum.addScaledVector(_sunLightRay, y);
  }
  _sunLightSum.multiplyScalar(1 / weight);
  sunLight.share = lit / weight;
  const top = Math.max(_sunLightSum.r, _sunLightSum.g, _sunLightSum.b);
  if (top > 1e-9) sunLight.tint.setRGB(_sunLightSum.r / top, _sunLightSum.g / top, _sunLightSum.b / top);
  if (lit > 1e-12) {
    sunLight.centre.copy(_sunLightSeenSum).normalize();
    sunLight.source.copy(_sunLightSourceSum).normalize();
  }
  return sunLight;
}

// The share of the Sun's light reaching the camera, for the glare and the
// exposure.
function estimateSunLineOcclusion(directionToSun, distanceToSun) {
  return measureSunLight(directionToSun, distanceToSun).share * eclipseGlare();
}

// Spacecraft in front of the Sun (the ISS, the probes, the other models in
// satelliteObjects). The glare is drawn over the frame, so it showed through
// them. When one comes near the line to the Sun, its meshes are drawn black
// on white into a small target that is the camera's own view cut down to a
// square round the Sun on screen (setViewOffset, so a texel lies exactly
// under its pixel); the cinematic pass reads its smallest mip as the share of
// the Sun left (the glare and spikes from the light that gets through, so it
// shines between the truss and the arrays) and a texel per pixel over the
// disc. Nothing is drawn while nothing is near the line.
const SUN_OCC_LAYER = 30;
const SUN_OCC_SIZE = 32;
let sunOccTarget = null;
let sunOccCamera = null;
let sunOccMaterial = null;
const _sunOccPos = new THREE.Vector3();
const _sunOccScale = new THREE.Vector3();
const _sunOccBox = new THREE.Box3();
const _sunOccSphere = new THREE.Sphere();
const _sunOccClear = new THREE.Color();

function tagSunOccluder(object) {
  object.traverse((o) => {
    if (!o.isMesh || o.userData.sunOccTagged) return;
    o.userData.sunOccTagged = true;
    const m = Array.isArray(o.material) ? o.material[0] : o.material;
    // Hit targets and other invisible helpers stay out.
    if (!m || m.visible === false || m.colorWrite === false || (m.transparent && m.opacity < 0.05)) return;
    o.layers.enable(SUN_OCC_LAYER);
  });
}

// Made before the first frame, its shader compiled behind the loading screen
// (the boot gate waits for it): made when a spacecraft first crossed the Sun,
// that frame waited on the driver to compile it.
function prepareSunOccluders() {
  if (sunOccTarget) return;
  sunOccTarget = new THREE.WebGLRenderTarget(SUN_OCC_SIZE, SUN_OCC_SIZE, {
    minFilter: THREE.LinearMipmapLinearFilter,
    magFilter: THREE.LinearFilter,
    generateMipmaps: true,
    depthBuffer: true,
  });
  sunOccCamera = new THREE.PerspectiveCamera();
  sunOccMaterial = new THREE.MeshBasicMaterial({ color: 0x000000, side: THREE.DoubleSide, fog: false });
  // Its camera sees only the occluders' layer, so no lights: compiled the
  // same way, or the program is one the pass never draws with.
  sunOccCamera.layers.set(SUN_OCC_LAYER);
  if (renderer && scene) compileForSceneAsync(renderer, new THREE.Mesh(new THREE.BoxGeometry(), sunOccMaterial), sunOccCamera, scene);
}

function updateSunOccluders(directionToSun, distanceToSun, sunAngular, sunUv, sunRadiusPx) {
  const u = cinematicPass && cinematicPass.uniforms;
  if (!u || !u.lensFlareOccOn) return;
  u.lensFlareOccOn.value = 0;
  if (renderer.xr.isPresenting || window.__sunOccOff) return;
  let near = false;
  for (const { mesh } of satelliteObjects) {
    if (!mesh || !mesh.visible || !mesh.parent) continue;
    // A bounding radius per object, measured once in its own units.
    if (mesh.userData.sunOccRadius == null || mesh.userData.sunOccChildren !== mesh.children.length) {
      _sunOccBox.setFromObject(mesh);
      if (_sunOccBox.isEmpty()) continue;
      _sunOccBox.getBoundingSphere(_sunOccSphere);
      mesh.userData.sunOccRadius = _sunOccSphere.radius / Math.max(mesh.getWorldScale(_sunOccScale).x, 1e-12);
      mesh.userData.sunOccChildren = mesh.children.length;
    }
    const radius = mesh.userData.sunOccRadius * mesh.getWorldScale(_sunOccScale).x * 1.2;
    mesh.getWorldPosition(_sunOccPos).sub(camera.position);
    const t = _sunOccPos.dot(directionToSun);
    if (t <= -radius || t >= distanceToSun) continue;
    const closest = Math.sqrt(Math.max(0, _sunOccPos.lengthSq() - t * t));
    if (closest > radius + Math.max(t, 0) * sunAngular) continue;
    tagSunOccluder(mesh);
    near = true;
  }
  if (!near) return;

  prepareSunOccluders();
  // The camera's own view, cut down to a square round the Sun at least four
  // pixels across.
  const w = renderer.domElement.clientWidth || window.innerWidth;
  const h = renderer.domElement.clientHeight || window.innerHeight;
  const size = Math.max(4, sunRadiusPx * 2);
  sunOccCamera.copy(camera);
  sunOccCamera.layers.set(SUN_OCC_LAYER);
  sunOccCamera.setViewOffset(w, h, sunUv.x * w - size / 2, (1 - sunUv.y) * h - size / 2, size, size);
  sunOccCamera.updateProjectionMatrix();

  const target = renderer.getRenderTarget();
  const clearAlpha = renderer.getClearAlpha();
  renderer.getClearColor(_sunOccClear);
  const background = scene.background;
  const override = scene.overrideMaterial;
  scene.background = null;
  scene.overrideMaterial = sunOccMaterial;
  renderer.setRenderTarget(sunOccTarget);
  renderer.setClearColor(0xffffff, 1);
  renderer.clear(true, true, false);
  renderer.render(scene, sunOccCamera);
  renderer.setRenderTarget(target);
  renderer.setClearColor(_sunOccClear, clearAlpha);
  scene.background = background;
  scene.overrideMaterial = override;

  u.lensFlareOccMap.value = sunOccTarget.texture;
  u.lensFlareOccScale.value.set(w / size, h / size);
  u.lensFlareOccOn.value = 1;
}

// Compressed inverse-square sunlight falloff. Real insolation spans
// four orders of magnitude from Mercury to Neptune; the full 1/r²
// would render the outer system unusably dark, so we compress with
// a square root and clamp. Normalized at Earth = 1.0:
//   Mercury 1.6 · Venus 1.18 · Mars 0.81 · Jupiter 0.44
//   Saturn 0.32 · Uranus/Neptune/Pluto floor at 0.25
// Consumed by the uSunIntensity shader uniform (gas giants, Venus,
// rings) and by diffuse-color dimming on standard-material bodies.
const PLANET_AU = {
  mercury: 0.387, venus: 0.723, earth: 1.0, mars: 1.524, jupiter: 5.203,
  saturn: 9.537, uranus: 19.19, neptune: 30.07, pluto: 39.48
};
// Per-body visual-exposure overrides. The sqrt distance model below is
// a good default, but for some bodies it underexposes the disc relative
// to how real spacecraft imagery (which auto-exposes per target) reads.
// An entry here pins that body's direct-sun multiplier and bypasses the
// distance model for it ONLY — every other body keeps the falloff.
const SUN_IRRADIANCE_OVERRIDE = {
  // The sqrt(1/AU) model below floors the OUTER planets at 0.25–0.44,
  // which renders them dark and underexposed — Jupiter, Saturn, Uranus,
  // Neptune and Pluto all read too dim, while the inner planets
  // (Mercury/Venus/Earth/Mars, factor 0.81–1.6) look right. Real
  // spacecraft imagery auto-exposes per target, so pin the outer bodies
  // to a bright, well-exposed multiplier (gentle descending gradient,
  // all clearly lit). Inner planets keep the untouched distance model.
  jupiter: 0.86,
  saturn:  0.84,
  uranus:  0.82,
  neptune: 0.80,
  pluto:   0.92
};
function getSunIrradianceFactor(name) {
  const key = (name || '').toLowerCase();
  if (SUN_IRRADIANCE_OVERRIDE[key] != null) return SUN_IRRADIANCE_OVERRIDE[key];
  const au = PLANET_AU[key];
  if (!au) return 1.0;
  return Math.min(1.6, Math.max(0.25, Math.sqrt(1.0 / au)));
}

// Sun's angular radius (radians) as seen from a body — drives the
// physical penumbra width of ring/eclipse shadows. Uses the realistic
// solar radius when that mode is active; the artistic sun (radius 15)
// is oversized relative to orbital distances, so the result is capped
// to keep shadow edges crisp instead of mushy.
// Analytic ray-sphere raycast override for high-poly body meshes.
// three.js Mesh.raycast iterates every triangle once the bounding
// sphere is hit — Earth's 384-segment surface is ~290k triangles, so
// hovering the cursor over a planet was a multi-millisecond CPU scan
// per raycast. Spherical bodies don't need it: an exact ray-sphere
// intersection gives the same hit (well within the ±1% vertex
// displacement) at constant cost. Picking precision is unchanged
// because the bodies ARE spheres.
const _sphereRaycastSphere = new THREE.Sphere();
const _sphereRaycastPoint = new THREE.Vector3();
const _sphereRaycastScale = new THREE.Vector3();
function applyAnalyticSphereRaycast(mesh, baseRadius) {
  mesh.raycast = function (raycaster, intersects) {
    _sphereRaycastScale.setFromMatrixScale(this.matrixWorld);
    const worldRadius = baseRadius * Math.max(
      _sphereRaycastScale.x, _sphereRaycastScale.y, _sphereRaycastScale.z
    );
    _sphereRaycastSphere.center.setFromMatrixPosition(this.matrixWorld);
    _sphereRaycastSphere.radius = worldRadius;
    const hit = raycaster.ray.intersectSphere(_sphereRaycastSphere, _sphereRaycastPoint);
    if (!hit) return;
    const distance = raycaster.ray.origin.distanceTo(hit);
    if (distance < raycaster.near || distance > raycaster.far) return;
    intersects.push({ distance, point: hit.clone(), object: this });
  };
}

const _sunAngularScratch = new THREE.Vector3();
function getSunAngularRadiusAt(body) {
  if (!body) return 0;
  const dist = body.getWorldPosition(_sunAngularScratch).length();
  if (dist < 1e-3) return 0;
  const sunR = realisticScaleMode
    ? (getRealisticBodyRadius('sun') || celestialData.sun.radius)
    : celestialData.sun.radius;
  return Math.min(0.04, sunR / dist);
}

// The bloom stays off the Sun's own disc, so its sunspots keep their
// contrast, and the glow round it is as before (src/sunbloom.js).
let sunDiscBloomPass = null;

// The Sun's glare (src/shaders/sunglare.js) as a camera photographs it from
// the Earth at true scale, in degrees from the Sun: the core's width and peak
// (the power-law PSF Andre approved, whose peak always clips); the glow
// (width, peak, where its tail is cut off in widths, and its level round a
// disc that shows its surface) and the veil round it; the rays (line and
// root widths, the root's share and fade, the knee where their fall begins,
// peak, where the longest has faded to 1/e, and the least that is, in
// reaches of the white core); the streak and ghosts; and the lens's colour,
// which the glow and rays take: white, as the Sun is from space (Andre asked
// for it at both scales on 1 Nov 2026, over the gold he first had). The disc
// counts as resolved from resolvedFrom to resolvedBy degrees in radius,
// where a camera stops overexposing it: the core, rays and streak go and the
// glow stays round its limb. The Detailed Sun is drawn for its surface, so
// it shows it from a smaller disc (detailedFrom, detailedBy).
// The values are Andre's, set with the tuner dock's Sun tab on 31 Oct 2026
// (shorter rays and 27 strands on 1 Nov), except glowGrow: grown with an
// artistic disc in full, his wider glow fogged the frame and buried Venus
// beside the Sun, so it grows by the root of the enlargement, as the veil
// does (true scale is unchanged).
// window.__debug.sunGlare and that tab tune it live.
const SUN_GLARE = {
  core: 0.06, coreGain: 114,
  glow: 1.1, glowGain: 1.35, glowEnd: 4.3, limbGlow: 0.82, glowGrow: 0.5,
  veil: 4.3, veilEnd: 15, veilGain: 0.076,
  rayWidth: 0.007, rayRoot: 0.18, rayRootShare: 0.33, rayRootFade: 0.78, rayKnee: 0.085, rayGain: 1020, rayLength: 1.4, rayPastCore: 1.8,
  rayCount: 20, rayTurn: 0.37,
  strandGain: 1590, strandCount: 27, strandLength: 0.8, strandSpread: 1.15, strandWidth: 0.8,
  strandJitter: 0.8, strandShortest: 0.11, strandDim: 0.7, strandScaleFall: 0.95,
  strandShift: 0.8, strandShiftRate: 1.5,
  streakGain: 0.07,
  ghostGain: 0.016,
  warm: [1, 1, 1], rayWarmth: 1,
  resolvedFrom: 4, resolvedBy: 10,
  detailedFrom: 0.5, detailedBy: 1.5,
};
// How far the artistic disc's own brightness is pushed while it stands for
// an overexposed point: white with a hint of its limb.
const SUN_DISC_BLOWOUT = 3;

// The light the Sun keeps on its way to the camera: white in space. Seen
// past the Earth's limb from outside the air, a ray crosses the atmosphere
// down to its tangent height h, through a slant optical depth of
// tau0 x sqrt(2 pi R / H) x e^(-h/H) (Chapman's grazing air mass, H = 8 km,
// so about 71 air masses at the ground), with Rayleigh's tau0 at 650, 550
// and 450 nm (0.049, 0.096, 0.214, as lambda^-4 from 0.096 at 550 nm): the
// Sun dims and reddens over the last 40 km above the limb, as in sunrise
// photographs from the ISS. The disc takes it per pixel (uEarthAir in
// src/shaders/sun.js, so only the part of a large artistic disc that is in
// the air reddens); the glare takes the light of the whole disc
// (measureSunLight).
const SUN_AIR_TAU = [0.049, 0.096, 0.214];
function sunAirTransmission(hKm, out) {
  if (hKm >= 120) return out.setRGB(1, 1, 1);
  const slant = 70.7 * Math.exp(-Math.max(hKm, 0) / 8);
  return out.setRGB(Math.exp(-SUN_AIR_TAU[0] * slant), Math.exp(-SUN_AIR_TAU[1] * slant), Math.exp(-SUN_AIR_TAU[2] * slant));
}
// The Sun's material: a gain (the artistic disc blown out white while it is
// a bright point, updateLensFlare) and the Earth's centre and drawn radius
// for the per-pixel air (w = 0 when no ray to the Sun crosses it).
const sunTintUniform = { value: new THREE.Color(1, 1, 1) };
const sunEarthAirUniform = { value: new THREE.Vector4(0, 0, 0, 0) };
// Artistic distances are not to scale, so the glare there takes the Sun's
// brightness from where the camera sits among the planets: on the Earth's
// orbit as from 1 AU, on Neptune's as from 30 AU, log-log between orbits,
// in proportion inside Mercury's, and past Neptune's log-log to Pluto's rung
// of the ladder the probes are drawn by (artisticDistanceForAu) and on it
// past that, so at Voyager 1 as from its 172 AU. The distance readout reads
// the same.
const ARTISTIC_SUN_AU_LADDER = [
  ['mercury', 0.387], ['venus', 0.723], ['earth', 1.0], ['mars', 1.524],
  ['jupiter', 5.203], ['saturn', 9.537], ['uranus', 19.19], ['neptune', 30.07],
];
const _artisticSunAuScratch = new THREE.Vector3();
function artisticSunAu(distance) {
  let prevD = 0;
  let prevAu = 0;
  for (const [name, au] of ARTISTIC_SUN_AU_LADDER) {
    const body = planets && planets[name];
    if (!body) continue;
    const d = body.getWorldPosition(_artisticSunAuScratch).length();
    if (!(d > prevD)) continue;
    if (distance <= d) {
      if (!prevD) return au * distance / d;
      return prevAu * Math.pow(au / prevAu, Math.log(distance / prevD) / Math.log(d / prevD));
    }
    prevD = d;
    prevAu = au;
  }
  if (!prevD) return distance / AU_TO_SCENE;
  const rungs = artisticLadder();
  const pluto = rungs[rungs.length - 1];
  const dP = pluto.d * scaleMultiplier;
  if (distance <= dP) return prevAu * Math.pow(pluto.au / prevAu, Math.log(distance / prevD) / Math.log(dP / prevD));
  return pluto.au * Math.pow(distance / dP, 1 / ARTISTIC_DEEP_POWER);
}

// How far a planet's moons reach from it as drawn (their orbits' distance,
// with room for eccentricity), for the planet points at artistic scale.
function planetMoonReach(name) {
  let reach = 0;
  for (const moon of moons[name] || []) reach = Math.max(reach, moon.userData.distance || 0);
  return reach * 1.2;
}

const _lensFlareCentre = new THREE.Vector3();
const _lensFlareCutter = new THREE.Vector3();
const _lensFlareAir = new THREE.Vector3();
function updateLensFlare(delta) {
  // Rocketry draws its own sun glare (src/rocketry/sunglare.js), occluded by
  // the true-scale Earth and the vessel and reddened through the limb.
  if (!cinematicPass || !cinematicPass.uniforms || !graphicsConfig.lensFlare || !sun || !camera || scaleModeActive || rocketryActive || giantShown) {
    setLensFlareHidden(delta);
    return;
  }

  sun.getWorldPosition(lensFlareSunPos);
  lensFlareToSun.copy(lensFlareSunPos).sub(camera.position);
  const distanceToSun = lensFlareToSun.length();
  if (distanceToSun <= camera.near) {
    setLensFlareHidden(delta);
    return;
  }

  const directionToSun = lensFlareToSun.normalize();
  const light = measureSunLight(directionToSun, distanceToSun);
  camera.getWorldDirection(lensFlareCameraDir);
  const facing = directionToSun.dot(lensFlareCameraDir);
  if (facing <= 0.02) {
    setLensFlareHidden(delta);
    return;
  }

  lensFlareProjected.copy(lensFlareSunPos).project(camera);
  const maxNdc = Math.max(Math.abs(lensFlareProjected.x), Math.abs(lensFlareProjected.y));
  const screenFade = 1 - THREE.MathUtils.smoothstep(maxNdc, 0.94, 1.58);
  const facingFade = THREE.MathUtils.smoothstep(facing, 0.04, 0.28);
  // From outside the galaxy the Sun is one faint star among hundreds of
  // billions, so its glare goes as the 3D galaxy comes in.
  let visibility = light.share * eclipseGlare() * (1 - galaxyViewShare);
  // Effective sun radius accounts for realistic-mode body scaling so
  // the sun's apparent UV size is computed against what's actually
  // drawn on screen rather than the authored artistic radius.
  const baseSunRadius = celestialData.sun.radius || 12;
  const effectiveSunRadius = realisticScaleMode
    ? (getRealisticBodyRadius('sun') || baseSunRadius)
    : baseSunRadius;
  // Apparent UV radius of the sun on screen. UV space spans 0..1
  // vertically, NDC -1..1, so vUv-space radius = world-radius /
  // (distance * tan(fovHalf)) / 2.
  const fovHalf = (camera.fov || 45) * 0.5 * Math.PI / 180;
  const sunUVRadius = THREE.MathUtils.clamp(
    effectiveSunRadius / (distanceToSun * Math.max(Math.tan(fovHalf), 0.0001)) * 0.5,
    0.0008,
    0.18
  );
  // While the close sky draws the eclipsed Sun, a glare centred on the
  // Sun's centre would sit on the Moon: it stays only while the disc is a
  // speck in the frame (under about a hundredth of its height across), where
  // the crescent is a point. Closer in the bloom spreads the crescent from
  // where it is and the sky draws the last light's glare (eclipse/totality.js).
  if (earthCloseView && earthCloseView.eclipse.disc) {
    visibility *= 1 - THREE.MathUtils.smoothstep(sunUVRadius, 0.004, 0.012);
  }
  const discAngle = Math.asin(Math.min(1, effectiveSunRadius / distanceToSun));
  const discUv = { x: lensFlareProjected.x * 0.5 + 0.5, y: lensFlareProjected.y * 0.5 + 0.5 };
  updateSunOccluders(
    directionToSun, distanceToSun, effectiveSunRadius / distanceToSun, discUv,
    effectiveSunRadius / (distanceToSun * Math.max(Math.tan(fovHalf), 0.0001)) * 0.5 * (renderer.domElement.clientHeight || window.innerHeight)
  );
  // How bright the Sun is where the camera is: its flux in units of the
  // Earth's. At true scale from the distance itself; in artistic mode, whose
  // distances are not to scale, from where the camera sits among the
  // planets' orbits (artisticSunAu), so by Saturn it is the Sun seen from
  // Saturn in either scale.
  const sunAu = realisticScaleMode ? distanceToSun / AU_TO_SCENE : artisticSunAu(distanceToSun);
  const flux = 1 / Math.max(sunAu, 0.05) ** 2;
  const flareIntensity = (graphicsConfig.lensFlareIntensity || 0.5) / 0.54;
  // Once the disc fills the view there is no point source left to flare
  // (close over the surface the ghosts only washed the frame), so they go
  // as the disc grows past about 30 degrees across.
  const discFill = 1 - THREE.MathUtils.smoothstep(discAngle, 0.26, 0.5);
  // How bright the god rays round an artistic Sun cut by a limb are.
  const targetOpacity = THREE.MathUtils.clamp(
    (graphicsConfig.lensFlareIntensity || 0.5) * screenFade * facingFade * visibility * Math.min(1, Math.sqrt(flux)) * discFill,
    0,
    0.62
  );

  const alpha = dampingFactor(delta, 12);
  lensFlareOpacity += (targetOpacity - lensFlareOpacity) * alpha;
  if (lensFlareOpacity < 0.002) lensFlareOpacity = 0;
  const spaceTarget = THREE.MathUtils.clamp(screenFade * facingFade, 0, 1);
  const G = SUN_GLARE;
  // The glare is the lens's response to the light that reaches it: the
  // flux times the share past any limb and through any air, compressed
  // (to the 0.6 power) and floored so the Sun from Pluto still clips. From
  // the Earth its white reaches just past the true disc and its glow is
  // gone by about 1.5 degrees, from Jupiter a bright point with a small
  // glow, past Saturn a star. A Sun setting behind a limb keeps its glare,
  // smaller and redder, until the last of it goes.
  const seen = flux * visibility;
  // A camera overexposes the Sun while it is small in the frame; once the
  // disc is a few degrees across it shows its surface (blowout 0), the same
  // in both scales: so the artistic Sun blazes from the Earth's orbit out
  // and shows its face from about Mercury's in. The Detailed Sun shows its
  // plasma from about a degree across.
  const blowout = 1 - THREE.MathUtils.smoothstep(discAngle * 180 / Math.PI,
    realisticSunMode ? G.resolvedFrom : G.detailedFrom, realisticSunMode ? G.resolvedBy : G.detailedBy);
  // The disc's projected edge in the shader's measure (degrees as fov x uv,
  // linear across the frame), tan(angle) / tan(half fov). Its angle itself
  // fell a few pixels outside the drawn edge, more the bigger the disc.
  const limbMeasure = (camera.fov || 45) * 0.5 * Math.tan(discAngle) / Math.max(Math.tan(fovHalf), 0.0001);
  // Where the core reads bright round a point for a peak (degrees): just
  // past the true disc from the Earth.
  const coreReach = (peak) => G.core * Math.sqrt(Math.max((peak / 0.4) ** (2 / 3) - 1, 0));
  // A disc wider than that (the Sun from Mercury at true scale, every
  // artistic disc from the Earth in) showed its edge with the rays stuck on,
  // where a camera shows a round glare with the rays growing out of it. So
  // the whole photograph is enlarged until its core covers the disc by the
  // margin it has from the Earth (30% of the radius), at the clear Sun's
  // brightness so a limb cutting the disc shrinks the core within it.
  const peakClear = Math.max(G.coreGain * flux ** 0.6, 6) * flareIntensity;
  const glareScale = Math.max(1, 1.3 * limbMeasure / Math.max(coreReach(peakClear), 1e-4));
  const peak = peakClear * Math.pow(Math.max(visibility, 0), 0.6);
  const near = Math.min(seen, 2);
  const u = cinematicPass.uniforms;
  u.lensFlareGain.value = peak;
  u.lensFlareScale.value = glareScale;
  u.lensFlareFarScale.value = Math.sqrt(glareScale);
  u.lensFlareGlowScale.value = glareScale ** G.glowGrow;
  u.lensFlareGlowEnd.value = G.glowEnd;
  u.lensFlareShape.value.set(G.core, G.glow, G.veil, G.veilEnd);
  // The glow and veil follow the square root of the light: plain round a
  // near Sun, gone by the outer planets. Round a disc that shows its
  // surface the glow is that surface spread past the limb: the shader
  // (sgLimbLight) starts it at limbGlow of the limb's own light as this
  // pass draws it, in its colour, and joins it to the bloom as a smooth
  // maximum, so the edge is dimmer than the limb at any exposure and in
  // either style. As a fixed amount added after the exposure it outshone
  // a disc the auto exposure had dimmed, and the disc read as a dark
  // silhouette in a white halo (2 Nov 2026).
  const pointGlow = G.glowGain * Math.sqrt(Math.min(seen, 4)) * flareIntensity;
  u.lensFlareGlow.value.set(pointGlow, G.veilGain * Math.sqrt(Math.min(seen, 4)) * flareIntensity);
  // A gentler lens setting (Medium, Low, subtle) takes it down with the
  // rest of the glare; above High it stays at the tuned share, which over 1
  // would draw the edge brighter than the limb.
  u.lensFlareLimbGlow.value = G.limbGlow * Math.min(flareIntensity, 1);
  // The rays and streak follow the light (capped at twice the Earth's),
  // the rays' length its square root: from the Earth the longest end about
  // 5 degrees out, from Jupiter about 1, and from Saturn on the Sun is a
  // star with short rays, as the brightest star in a photograph has (it is
  // still magnitude -19 from Pluto). Round an enlarged glare they grow by
  // the root of the enlargement, so a large artistic disc does not throw
  // them across the frame, but never end inside the white they grow from:
  // the artistic Sun from Jupiter out was a plain white ball. The Detailed
  // Sun has none at all.
  const glint = Math.max(near, 0.01 * visibility);
  u.lensFlareSpikes.value = realisticSunMode ? G.rayGain * glint ** 0.6 * flareIntensity : 0;
  u.lensFlareSpikeLength.value = Math.max(
    G.rayLength * Math.max(Math.sqrt(Math.min(flux, 2)), 0.12) / Math.sqrt(glareScale),
    G.rayPastCore * coreReach(peakClear));
  u.lensFlareRayShape.value.set(G.rayWidth / Math.sqrt(glareScale), G.rayKnee, G.rayRoot, G.rayRootShare);
  u.lensFlareRayCount.value = Math.max(2, Math.round(G.rayCount / 2) * 2);
  u.lensFlareRayTurn.value = G.rayTurn;
  // The fine strands between the rays (sgStrand), Realistic style only as
  // the rays are. Round an artistic disc the glare is drawn glareScale times
  // larger, and at full strength the strands filled the frame there.
  u.lensFlareStrands.value = realisticSunMode
    ? G.strandGain * glint ** 0.6 * flareIntensity / glareScale ** G.strandScaleFall : 0;
  u.lensFlareStrandShape.value.set(Math.max(3, Math.round(G.strandCount)), G.strandLength, G.strandSpread, G.strandWidth);
  u.lensFlareStrandMix.value.set(G.strandJitter, G.strandShortest, G.strandDim, 0);
  u.lensFlareStrandShift.value.set(G.strandShift, G.strandShiftRate);
  u.lensFlareRootFade.value = G.rayRootFade;
  u.lensFlareStreak.value = realisticSunMode ? G.streakGain * glint ** 0.6 * flareIntensity : 0;
  u.lensFlareGhosts.value = G.ghostGain * Math.min(1, Math.sqrt(seen)) * flareIntensity * discFill * blowout * spaceTarget;
  u.lensFlareWarm.value.setRGB(G.warm[0], G.warm[1], G.warm[2]);
  u.lensFlareRayTint.value.setRGB(1, 0.97, 0.93).lerp(u.lensFlareWarm.value, G.rayWarmth);
  u.lensFlareTint.value.copy(light.tint);
  u.lensFlareDisc.value.set(limbMeasure, blowout);
  u.lensFlareDiscUv.value.set(discUv.x, discUv.y);
  // The core stays off the body cutting the disc.
  u.lensFlareBodyMask.value = light.cutter ? 1 : 0;
  if (light.cutter) {
    _lensFlareCutter.copy(light.cutterPosition).applyMatrix4(camera.matrixWorldInverse);
    const d = _lensFlareCutter.length();
    _lensFlareCutter.multiplyScalar(1 / Math.max(d, 1e-9));
    u.lensFlareBody.value.set(_lensFlareCutter.x, _lensFlareCutter.y, _lensFlareCutter.z,
      Math.cos(Math.asin(Math.min(1, light.cutterRadius / Math.max(d, 1e-9)))));
    u.lensFlareInvProj.value.copy(camera.projectionMatrixInverse);
  }
  // Through the Earth's air the core is drawn round where the light left
  // the Sun and bent up over the limb as the air bends it (sgSourceUv in
  // src/shaders/sunglare.js): the Earth's centre in view space and the sine
  // of its angular radius.
  u.lensFlareRefract.value = light.refract ? AIR_GRAZING_BEND : 0;
  if (light.refract) {
    const air = sunEarthAirUniform.value;
    _lensFlareAir.set(air.x, air.y, air.z).applyMatrix4(camera.matrixWorldInverse);
    const d = _lensFlareAir.length();
    _lensFlareAir.multiplyScalar(1 / Math.max(d, 1e-9));
    u.lensFlareAir.value.set(_lensFlareAir.x, _lensFlareAir.y, _lensFlareAir.z, Math.min(1, air.w / Math.max(d, 1e-9)));
    u.lensFlareInvProj.value.copy(camera.projectionMatrixInverse);
    u.lensFlareProj.value.copy(camera.projectionMatrix);
    _lensFlareCentre.copy(camera.position).addScaledVector(light.source, distanceToSun).project(camera);
    u.lensFlareSunSrc.value.set(_lensFlareCentre.x * 0.5 + 0.5, _lensFlareCentre.y * 0.5 + 0.5);
  }
  // The artistic disc blows out white on its own mesh while it stands for
  // an overexposed point, which whatever is in front of it hides (a fill
  // drawn over the frame painted it over the Earth).
  sunTintUniform.value.setScalar(realisticScaleMode ? 1 : 1 + SUN_DISC_BLOWOUT * blowout);
  u.lensFlareViewPx.value = renderer.domElement.clientHeight || window.innerHeight || 900;
  // The god rays past a planet's limb stay an artistic-scale effect, only
  // round a Sun that is still a point on screen (the diamond ring as a limb
  // cuts it). Round a resolved artistic disc the radial walk smeared its lit
  // part over the planet in front and drew a column above it.
  const discLarge = realisticScaleMode ? 0 : THREE.MathUtils.smoothstep(discAngle, 0.004, 0.02);
  u.godRayStrength.value = realisticScaleMode ? 0 : 0.55 * (1 - discLarge);
  lensFlareSpaceAmount += (spaceTarget - lensFlareSpaceAmount) * alpha;
  if (lensFlareSpaceAmount < 0.002) lensFlareSpaceAmount = 0;
  u.lensFlareSpace.value = lensFlareSpaceAmount;
  u.lensFlareDegPerUv.value = camera.fov / Math.max(camera.zoom || 1, 1e-3);
  // Post-pass UVs run bottom to top like NDC. Negating y drew the flare
  // and the god rays at the sun's mirror image across the horizontal
  // centre line. The glare centres on the light of what shows of the disc.
  _lensFlareCentre.copy(camera.position).addScaledVector(light.centre, distanceToSun).project(camera);
  u.lensFlareSun.value.set(_lensFlareCentre.x * 0.5 + 0.5, _lensFlareCentre.y * 0.5 + 0.5);
  u.lensFlareAspect.value = (window.innerWidth || 1) / Math.max(window.innerHeight || 1, 1);
  u.lensFlareAmount.value = lensFlareOpacity;
  u.lensFlareSunSize.value = sunUVRadius;
  // Visibility (1 = clear) as the share hidden (1 = blocked).
  u.lensFlareOcclusion.value = THREE.MathUtils.clamp(1 - visibility, 0, 1);
}

// The video renderer (voidmode-youtube/3dsolarsystem/scripts/video) installs window.__vt, its virtual clock,
// before any of the page's scripts; it never exists on the site. These two
// read it, so what they change applies to rendered videos only.
//
// Film grain on whole output frames. The live grain takes a new pattern at
// 24 Hz, which at 60 fps lands in a 3:2 cadence (three frames of one
// pattern, two of the next) and reads as a stutter in the grain. Each
// pattern is held for floor(fps / 24) frames instead: 2 at 60 fps, an even
// 30 Hz, the closest to the live look. Null without the renderer.
function videoGrainTime() {
  const vt = typeof window !== 'undefined' ? window.__vt : null;
  if (!vt || !(vt.fps > 0)) return null;
  const frame = Math.round((vt.now * vt.fps) / 1000);
  const hold = Math.max(1, Math.floor(vt.fps / 24));
  // The shader's pattern index is floor(grainTime * 24); the half keeps the
  // product clear of a whole number.
  return ((Math.floor(frame / hold) % 2400) + 0.5) / 24;
}

function applyCinematicUniforms() {
  if (!cinematicPass || !cinematicPass.uniforms) return;
  const uniforms = cinematicPass.uniforms;
  // A famous photo's own exposure while it is held (src/photos/mode.js).
  if (uniforms.exposure) uniforms.exposure.value = adaptiveExposure * famousPhotoExposure * eclipseExposure;
  if (uniforms.photoBlackPoint) uniforms.photoBlackPoint.value = famousPhotoBlackPoint;
  if (uniforms.adaptiveGlare) uniforms.adaptiveGlare.value = adaptiveGlare;
  if (uniforms.contrast) uniforms.contrast.value = adaptiveContrast;
  if (uniforms.saturation) uniforms.saturation.value = adaptiveSaturation * famousPhotoSaturation;
  if (uniforms.vignetteIntensity) uniforms.vignetteIntensity.value = adaptiveVignette;
  // Drive the film-grain noise pattern with a slowly-evolving phase.
  // Modulo wrap so the value stays within float32 precision over a
  // long session.
  if (uniforms.grainTime) {
    uniforms.grainTime.value = videoGrainTime() ?? (performance.now() % 100000) * 0.001;
  }
}

function settleAdaptiveExposure(delta, glareTarget = 0) {
  if (!cinematicPass) return;
  const alpha = dampingFactor(delta, 3.2);
  adaptiveGlare += (glareTarget - adaptiveGlare) * alpha;
  adaptiveExposure += (1 - adaptiveExposure) * alpha;
  adaptiveContrast += ((graphicsConfig.cinematic ? 1.05 : 1) - adaptiveContrast) * alpha;
  adaptiveSaturation += ((graphicsConfig.cinematic ? 1.05 : 1) - adaptiveSaturation) * alpha;
  adaptiveVignette += ((graphicsConfig.cinematic ? 0.3 : 0) - adaptiveVignette) * alpha;
  applyCinematicUniforms();
}

// The eye opening up as an eclipse deepens, from the share of the Sun
// showing (the close sky's eclipse): it makes up a little under half of the
// light lost in magnitudes, up to five and a half stops in totality, whose
// sky is about a thousandth of daylight's, eased over a second or two.
// ?whatif=future can hold a shot's own exposure instead (futureState.exposure).
let eclipseExposure = 1;
const ECLIPSE_ADAPTED_EXPOSURE = 40;
// The bloom's firefly filter would take a bead for noise; it stands aside
// with the last of the Sun's brightness (eclipsePhotoBoost). Its ceiling
// stays: above it the bloom's truncated kernels draw one bright source as a
// square, so the diamond ring's glare is the sky's own (totality.js).
let bloomStabilizerPass = null;
function updateEclipseBloom(ecl) {
  if (!bloomStabilizerPass) return;
  const f = ecl && ecl.disc ? eclipsePhotoBoost(ecl.visible) : 1;
  bloomStabilizerPass.uniforms.fireflyThreshold.value = 2.75 * f * f;
}

function updateEclipseExposure(delta) {
  const ecl = earthCloseView && earthCloseView.eclipse;
  updateEclipseBloom(ecl);
  if (sunDrawnBySky) keepSunAside();
  let target = 1;
  if (ecl && ecl.on) target = Math.min(ECLIPSE_ADAPTED_EXPOSURE, Math.pow(Math.max(ecl.visible, 1e-6), -0.4));
  if (FUTURE_ON && futureState.exposure != null) { eclipseExposure = futureState.exposure; return; }
  eclipseExposure += (target - eclipseExposure) * dampingFactor(delta, 1.4);
}

function updateAdaptiveExposure(delta) {
  if (!cinematicPass) return;
  updateEclipseExposure(delta);
  // Photo mode hands manual control of exposure / contrast / saturation
  // / vignette to the user via the panel sliders. The adaptive loop
  // would otherwise lerp those values back toward the auto-exposure
  // baseline every frame, clobbering whatever the user just set. Skip
  // the whole adaptive pass while photo mode is engaged; settle back
  // to baseline on exit (handled in exitPhotoMode).
  if (photoModeActive) return;
  if (!graphicsConfig.autoExposure || !sun || !camera || scaleModeActive) {
    settleAdaptiveExposure(delta);
    return;
  }

  sun.getWorldPosition(exposureSunPos);
  exposureToSun.copy(exposureSunPos).sub(camera.position);
  const distanceToSun = exposureToSun.length();
  if (distanceToSun <= camera.near) {
    settleAdaptiveExposure(delta);
    return;
  }

  const directionToSun = exposureToSun.normalize();
  camera.getWorldDirection(exposureCameraDir);
  const facing = directionToSun.dot(exposureCameraDir);
  if (facing <= 0.04) {
    settleAdaptiveExposure(delta);
    return;
  }

  exposureProjected.copy(exposureSunPos).project(camera);
  const maxNdc = Math.max(Math.abs(exposureProjected.x), Math.abs(exposureProjected.y));
  const inFrameFactor = 1 - THREE.MathUtils.smoothstep(maxNdc, 0.82, 1.55);
  const gazeFactor = THREE.MathUtils.smoothstep(facing, 0.55, 0.985);
  const occlusion = estimateSunLineOcclusion(directionToSun, distanceToSun);
  const sunRadius = celestialData.sun.radius || 12;
  const apparentFactor = THREE.MathUtils.clamp((sunRadius / distanceToSun) * 30, 0.42, 1);
  const glareTarget = THREE.MathUtils.clamp(
    inFrameFactor * gazeFactor * occlusion * apparentFactor,
    0,
    1
  );
  const strength = graphicsConfig.autoExposureStrength || 0.24;
  const targetExposure = 1 - glareTarget * strength;
  const targetContrast = (graphicsConfig.cinematic ? 1.05 : 1) + glareTarget * 0.055;
  const targetSaturation = (graphicsConfig.cinematic ? 1.05 : 1) - glareTarget * 0.035;
  const targetVignette = (graphicsConfig.cinematic ? 0.3 : 0) + glareTarget * 0.05;
  const alpha = dampingFactor(delta, 2.4 + glareTarget * 3.8);

  adaptiveGlare += (glareTarget - adaptiveGlare) * alpha;
  adaptiveExposure += (targetExposure - adaptiveExposure) * alpha;
  adaptiveContrast += (targetContrast - adaptiveContrast) * alpha;
  adaptiveSaturation += (targetSaturation - adaptiveSaturation) * alpha;
  adaptiveVignette += (targetVignette - adaptiveVignette) * alpha;
  applyCinematicUniforms();
}

// DOM Elements
const loadingScreen = document.getElementById('loading-screen');
const loadingBar = document.getElementById('loading-bar');
const loadingText = document.getElementById('loading-text');
const backButton = document.getElementById('back-button');
const timePresetsEl = document.getElementById('time-presets');
const scaleSlider = document.getElementById('scale-slider');
const speedValue = document.getElementById('speed-value');
const scaleValue = document.getElementById('scale-value');
const prevPlanetBtn = document.getElementById('prev-planet');
const nextPlanetBtn = document.getElementById('next-planet');
const navLabel = document.getElementById('nav-label');
const audioToggle = document.getElementById('audio-toggle');
const audioLabel = document.getElementById('audio-label');
const audioIconOn = document.getElementById('audio-icon-on');
const audioIconOff = document.getElementById('audio-icon-off');
const musicPick = document.getElementById('music-pick');
const orbitsToggle = document.getElementById('orbits-toggle');
const orbitsLabel = document.getElementById('orbits-label');
const constellationsToggle = document.getElementById('constellations-toggle');
const constellationsLabel = document.getElementById('constellations-label');
const sunStyleToggle = document.getElementById('sun-style-toggle');
const sunStyleLabel = document.getElementById('sun-style-label');
const photoModeToggle = document.getElementById('photo-mode-toggle');
const saveWallpaperToggle = document.getElementById('pm-wallpaper');
const photoModeLabel = document.getElementById('photo-mode-label');
const photoModePanel = document.getElementById('photo-mode-panel');
const photoModeExitBtn = document.getElementById('photo-mode-exit');
const flyToggle = document.getElementById('fly-toggle');
const flyLabel = document.getElementById('fly-label');
const flyControlsOverlay = document.getElementById('fly-controls');
const exitFlyModeBtn = document.getElementById('exit-fly-mode');
const propulsionSelect = document.getElementById('propulsion-type');
const earthAtmosphereBtn = document.getElementById('earth-atmosphere-btn');
const alienHudFlyToggle = document.getElementById('alien-hud-fly-toggle');
const fastTravelBtn = document.getElementById('fast-travel-btn');
const fastTravelMenu = document.getElementById('fast-travel-menu');
const fastTravelList = document.getElementById('fast-travel-list');
const fastTravelCloseBtn = document.getElementById('fast-travel-close');
const realisticScaleToggle = document.getElementById('realistic-scale-toggle');
const realisticLabel = document.getElementById('realistic-label');
const hudSpeed = document.getElementById('hud-speed');
const hudCoords = document.getElementById('hud-coords');
// Cache the unit suffix span once. Looking it up per-frame inside
// the fly loop was triggering a layout query each frame and adding
// measurable input lag on lower-end devices.
const hudUnit = document.querySelector('.hud-unit');
// HUD text updates throttle. We refresh the speed/coords readouts
// at ~12 Hz instead of full frame rate so the text invalidations
// don't fight with the render loop on slower devices.
let hudLastUpdate = 0;
const alienScanHud = document.getElementById('alien-scan-hud');
const alienScanTitle = document.getElementById('alien-scan-title');
const alienScanGlyphs = document.getElementById('alien-scan-glyphs');
const alienScanReadout = document.getElementById('alien-scan-readout');
const distanceValue = document.getElementById('distance-value');
const distanceIndicator = document.getElementById('distance-indicator');
const scienceToggle = document.getElementById('science-toggle');
const scienceLabel = document.getElementById('science-label');
const sciencePanel = document.getElementById('science-panel');
const scienceTarget = document.getElementById('science-target');
const scienceType = document.getElementById('science-type');
const scienceDistance = document.getElementById('science-distance');
const scienceCameraRange = document.getElementById('science-camera-range');
const sciencePhase = document.getElementById('science-phase');
const scienceIllumination = document.getElementById('science-illumination');
const scienceIncidence = document.getElementById('science-incidence');
const scienceApparent = document.getElementById('science-apparent');
const scienceDiameter = document.getElementById('science-diameter');
const scienceOrbit = document.getElementById('science-orbit');
const scienceModel = document.getElementById('science-model');
const scienceLinks = document.getElementById('science-links');
const qualityPicker = document.getElementById('quality-picker');
const qualityTrigger = document.getElementById('quality-trigger');
const qualityTriggerLabel = document.getElementById('quality-trigger-label');
const qualityMenu = document.getElementById('quality-menu');
const deepSpaceToggle = document.getElementById('deep-space-toggle');
const deepSpaceLabel = document.getElementById('deep-space-label');
const objectBrowserToggle = document.getElementById('object-browser-toggle');
const objectBrowserPanel = document.getElementById('object-browser');
const objectBrowserClose = document.getElementById('object-browser-close');
const objectBrowserSearch = document.getElementById('object-browser-search');
const objectBrowserSections = document.getElementById('object-browser-sections');
const spaceEventsToggle = document.getElementById('space-events-toggle');
const spaceEventsPanel = document.getElementById('space-events');
const spaceEventsClose = document.getElementById('space-events-close');
const spaceEventsList = document.getElementById('space-events-list');
const eventCaption = document.getElementById('event-caption');
const eventCaptionTitle = document.getElementById('event-caption-title');
const eventCaptionCopy = document.getElementById('event-caption-copy');
const coolWorldsSequenceToggle = document.getElementById('cool-worlds-sequence-toggle');
const coolWorldsSequenceLabel = document.getElementById('cool-worlds-sequence-label');
const coolWorldsSequenceModalToggle = document.getElementById('cool-worlds-sequence-modal-toggle');
const shareToggle = document.getElementById('share-toggle');
const shareSheet = document.getElementById('share-sheet');
const shareSheetClose = document.getElementById('share-sheet-close');
const shareSheetCopy = document.getElementById('share-sheet-copy');
const shareSheetCopyBtn = document.getElementById('share-sheet-copy-btn');
const shareSheetNote = document.getElementById('share-sheet-note');
const shareSheetX = document.getElementById('share-sheet-x');
const shareSheetBluesky = document.getElementById('share-sheet-bluesky');
const shareSheetReddit = document.getElementById('share-sheet-reddit');
const shareSheetFacebook = document.getElementById('share-sheet-facebook');

syncQualityPicker();

// Orbit visibility state - off by default
let orbitsVisible = false;
let scienceMode = false;
let lastScienceLinkTarget = null;
let objectBrowserOpen = false;
let spaceEventsOpen = false;
let eventCaptionTimer = null;
let coolWorldsShowreelActive = false;
let coolWorldsShowreelTimers = [];
let coolWorldsShowreelRestore = null;
let shareSheetOpen = false;
let alienHudEnabled = false;

// Rocketry (Kerbal-style build/launch/orbit) mode. The module is loaded
// on first activation so the website bundle never pays for it.
let earthCloseView = null;
let rocketry = null;
let rocketryActive = false;
let rocketryLoading = false;

// Fly mode state
let flyMode = false;
const FLY_MODE_CAMERA_NEAR = 0.01;
let flySavedCameraNear = null;
let flySpeed = 5;
let flyVelocity = new THREE.Vector3();
let flyEuler = new THREE.Euler(0, 0, 0, 'YXZ');
// Target euler the camera eases TOWARD. Mouse-look writes its
// rotation deltas here; the per-frame fly loop lerps flyEuler
// toward this so camera turns read as a smoothed cinematic flow
// instead of locked-to-mouse 1:1 snapping. Keyboard rotation also
// goes through this target so both input modes share the same
// damping curve.
let flyTargetEuler = new THREE.Euler(0, 0, 0, 'YXZ');
let flyAngularVelocity = new THREE.Vector2(0, 0); // x = pitch, y = yaw
let flyKeys = createDefaultFlyKeys();
let isMiddleMouseDown = false;

// Chase-direction snapshot taken when the user enters the orbit
// inspection mode (middle-click). On chase resume (thrust release of
// orbitParked) we restore flyTargetEuler from this so the ship's
// heading returns to where it was before the inspection. Without it,
// the orbit lookAt(ship) bakes the inspection angle into flyEuler,
// chase mode then does ship.quaternion.copy(camera.quaternion), and
// the ship flips to face whatever side the user orbited around to —
// which the user reports as "wheel-click changes the vehicle
// orientation."
const _orbitSavedChaseEuler = new THREE.Euler(0, 0, 0, 'YXZ');
let _orbitChaseEulerSaved = false;

// S-key dual-mode (brake / reverse) state. Mode latches at the rising
// edge of the S key based on the ship's forward speed at that instant:
//   'brake'   → ship was moving forward, S damps forward velocity to 0
//               and HOLDS there (no auto-rollover into reverse)
//   'reverse' → ship was at rest or already reversing, S applies
//               standard reverse thrust
//   'idle'    → S not pressed
// User releases S → 'idle' → next press picks mode again. So "tap S
// to stop, tap S again to back up" works naturally.
let _sKeyMode = 'idle';
let _sKeyWasDown = false;

// Flight assist, dead-stop brake and target lock.
//
// The old brake only shed the forward component of the velocity, at
// the drive's acceleration rate, so a fast ship took many seconds to
// stop and any lateral drift survived it. Now S (or the mobile BRAKE
// button, which latches until at rest) sheds the WHOLE velocity vector
// at whichever is larger: the drive's acceleration or the rate that
// stops the ship within FLY_BRAKE_STOP_TIME. Flight assist (Z toggles,
// on by default) eases the ship to rest over FLY_ASSIST_STOP_TIME
// whenever the pilot lets go of the thrust keys in space, instead of
// letting it coast on across a quarter of the solar system. Rates are
// per-tick velocity shed per real second (see the units note on
// SPEED_RANGES).
let flightAssist = true;
let _faArmed = false;          // a thrust input arms it; rest, or another system owning the velocity, clears it
let _faEaseT = 0;              // seconds into the current coast-down
let _brakeLatch = false;       // BRAKE button: keep braking until the ship is at rest
let _brakeDecelPerSec = 0;
const FLY_BRAKE_STOP_TIME = 1.6;
const FLY_ASSIST_STOP_TIME = 3;
const FLY_REST_SPEED = 2e-5;
// The coast-down's speed, as a share of the speed at release, x of the
// way through FLY_ASSIST_STOP_TIME. Its slope is zero at both ends, so
// the slowing builds from nothing as the throttle lets go, pulls
// hardest a third of the way in and fades out as the ship settles. The
// old straight ramp ran at the drive's own acceleration, which stopped
// the saucer from full speed in 0.4 s and read as hitting a wall.
const flyAssistEase = (x) => (x >= 1 ? 0 : (1 - x) ** 3 * (1 + 3 * x));
// Locked navigation target (sun, planet or moon). Drives the approach
// speed cap, the TGT readout and the on-screen bracket. T locks the
// body nearest the crosshair, T on the locked body unlocks.
let flyTarget = null;
let flyTargetMarkerEl = null;
let hudTargetEl = null;
const FLY_TARGET_LOCK_CONE_DEG = 25;
// Interstellar navigation (src/nav): galaxy map, plotted route, jumps.
// Lazy-loaded on first use, like rocketry.
let navigation = null;
let navigationLoading = null;

// Fly-mode ambient-dim snapshot. The scene ambient sits at 0.42 in
// overview so the dark hemispheres of texture-based planets (Venus,
// Mars, gas giants) stay readable under ACES tonemap. In fly mode the
// user is up close and expects the dark side to look like real space
// imagery (essentially black, save for atmospheric scatter / lightning
// / city lights handled inside their own shaders). We save the
// overview ambient intensity on enterFlyMode, dim to a tiny lift, and
// restore on exit so the overview view isn't permanently darkened.
const FLY_MODE_DARK_AMBIENT = 0.05;
let _ambientLightRef = null;
// Zodiacal light disc handle — created in createSun, intensity and
// visibility driven per frame from animate().
let zodiacalLightMesh = null;
let _flyModeAmbientSaved = null;
let flyMouseLookActive = false;
let lastMouseX = 0;
let lastMouseY = 0;
let orbitMiddlePanActive = false;
let orbitMiddlePanPointerId = null;
const ORBIT_MIDDLE_PAN_RIGHT = new THREE.Vector3();
const ORBIT_MIDDLE_PAN_UP = new THREE.Vector3();
const ORBIT_MIDDLE_PAN_VIEW = new THREE.Vector3();
const ORBIT_MIDDLE_PAN_OFFSET = new THREE.Vector3();
let cameraOrbitAngle = 0; // Camera horizontal orbit around UFO
let cameraOrbitPitch = 0.3; // Camera vertical orbit around UFO
let cameraOrbitDistance = 1.5; // Camera distance from UFO (close for tiny ship)
// Smoothing targets for wheel-driven zoom — the actual values lerp
// toward these each frame, so a single mousewheel tick reads as a
// continuous dolly rather than a snap.
let _cameraOrbitDistanceTarget = 1.5;
let _followDistanceTarget = null;          // set lazily once we know the active craft
let _flyDollyPending = 0;                   // wheel zoom accumulator (forward dolly)
const _flyDollyDir = new THREE.Vector3();   // scratch: camera forward for the dolly
let _parkedZoomPending = 0;                // accumulated wheel delta for parked mode
let ufoWorldPos = new THREE.Vector3(); // UFO's world position when orbiting
let targetOrbitAngle = 0; // Smooth target for orbit
let targetOrbitPitch = 0.3; // Smooth target for pitch
let ufoTiltX = 0; // Smooth tilt for pitch
let ufoTiltZ = 0; // Smooth tilt for roll
let ufoTiltVelX = 0;
let ufoTiltVelZ = 0;
let orbitStabilized = false; // Orbit stabilization mode
let orbitTarget = null; // Target body to orbit
let orbitRadius = 0; // Distance to maintain
let orbitAngle = 0; // Current angle in orbit
let flyUiInputLockUntil = 0;

const CRAFT_FLIGHT_PROFILES = {
  saucer: {
    // Saucer scale dropped to 0.025 (~0.025-unit diameter) so the
    // ship feels appropriately tiny against planets. Chase distances
    // tightened proportionally so the ship still reads clearly in
    // the frame at a sensible angular size, but the surrounding
    // scene (planet, atmosphere, surface) dominates when flying in
    // close.
    followY: -0.012,
    followDistance: 0.08,
    orbitDistance: 0.08,
    minOrbitDistance: 0.03,
    maxOrbitDistance: 0.4,
    basePitch: Math.PI * 0.15,
    maxPitchTilt: 0.18,
    maxBankTilt: 0.24,
    attitudeDamping: 9,
    wobble: true,
    hover: true,
    // chaseLag = position-tracking damping (higher = snappier, lower =
    // more spring/lag). 999 effectively means rigid follow — keeps
    // the saucer's existing crisp behaviour. bobAmplitude / swayAmplitude
    // add subtle sinusoidal drift on Y / X so the ship breathes.
    chaseLag: 999,
    bobAmplitude: 0,
    swayAmplitude: 0
  },
  sphere: {
    followY: -0.24,
    followDistance: 2.4,
    orbitDistance: 2,
    minOrbitDistance: 0.7,
    maxOrbitDistance: 6,
    basePitch: 0,
    maxPitchTilt: 0.11,
    maxBankTilt: 0.16,
    attitudeDamping: 8,
    wobble: false,
    hover: false,
    chaseLag: 11,
    bobAmplitude: 0.012,
    swayAmplitude: 0.04
  },
  enterprise: {
    // Tuned for the smaller-scale Enterprise (targetSize 0.09 in
    // enterprise.js — ~4× smaller than the previous 0.35). Camera
    // distances scaled down to match so the chase framing fills the
    // same screen area, and the orbit min lets you push in close
    // enough that the ship genuinely fills the view. Default chase
    // and orbit distances land at the close-up framing — the ship
    // dominates the frame on first focus, which reads as the
    // intended hero shot per user feedback. The min/max range
    // still lets the wheel pull back to a wider view.
    followY: -0.07,
    followDistance: 0.26,
    defaultFollowDistance: 0.26,
    orbitDistance: 0.30,
    minOrbitDistance: 0.12,
    maxOrbitDistance: 0.65,
    portraitDistanceBoost: 1.85,
    basePitch: 0,
    maxPitchTilt: 0.055,
    maxBankTilt: 0.12,
    attitudeDamping: 6,
    wobble: false,
    hover: false,
    fillLight: true,
    fillLightIntensity: 1.45,
    // chaseLagSensitivity controls how much the ship lags backward
    // when accelerating forward; small values keep the ship near its
    // rest position even at high speed.
    chaseLag: 17.3,
    chaseLagSensitivity: 0.013,
    bobAmplitude: 0.001,
    swayAmplitude: 0.01
  },
  'enterprise-refit': {
    // Constitution-class refit (NCC-1701, movie era). Roughly half
    // the length of the Galaxy-class NCC-1701-D, so the chase
    // profile sits between the Enterprise and the Ranger: a little
    // closer hero framing than the -D but more capital-ship weight
    // than the shuttle-scale Ranger.
    followY: -0.04,
    followDistance: 0.18,
    defaultFollowDistance: 0.18,
    orbitDistance: 0.22,
    minOrbitDistance: 0.10,
    maxOrbitDistance: 1.1,
    portraitDistanceBoost: 1.8,
    basePitch: 0,
    maxPitchTilt: 0.07,
    maxBankTilt: 0.14,
    attitudeDamping: 6.5,
    wobble: false,
    hover: false,
    fillLight: true,
    fillLightIntensity: 1.35,
    chaseLag: 16,
    chaseLagSensitivity: 0.013,
    bobAmplitude: 0.0012,
    swayAmplitude: 0.011
  },
  ranger: {
    // Tuned for the Interstellar Ranger (targetSize 0.04 in
    // ranger.js — roughly half the Enterprise's hero size). The
    // Ranger is canonically an atmospheric shuttle, so the chase
    // profile is tighter and more responsive than the Enterprise's
    // capital-ship feel, with a wider zoom-out ceiling so the user
    // can pull back to see a planet limb alongside the shuttle.
    followY: -0.024,
    followDistance: 0.13,
    defaultFollowDistance: 0.13,
    orbitDistance: 0.16,
    minOrbitDistance: 0.06,
    maxOrbitDistance: 0.9,
    portraitDistanceBoost: 1.7,
    basePitch: 0,
    maxPitchTilt: 0.10,
    maxBankTilt: 0.18,
    attitudeDamping: 7,
    wobble: false,
    hover: false,
    fillLight: true,
    fillLightIntensity: 1.2,
    chaseLag: 15,
    chaseLagSensitivity: 0.012,
    bobAmplitude: 0.0015,
    swayAmplitude: 0.012
  },
  hatak: {
    // Goa'uld Ha'tak (targetSize 0.10 in hatak.js — a hair larger
    // than Enterprise). Pyramid-shaped capital warship, so the
    // chase profile sits between the Enterprise's hero framing and
    // the refit's slightly tighter feel, with extra mass-induced
    // damping so turns and stops feel weighty. Goa'uld ships also
    // famously bank harder than Starfleet on direction changes —
    // maxBankTilt bumped above the Federation ships.
    followY: -0.06,
    followDistance: 0.28,
    defaultFollowDistance: 0.28,
    orbitDistance: 0.34,
    minOrbitDistance: 0.13,
    maxOrbitDistance: 0.72,
    portraitDistanceBoost: 1.85,
    basePitch: 0,
    maxPitchTilt: 0.06,
    maxBankTilt: 0.18,
    attitudeDamping: 5.5,
    wobble: false,
    hover: false,
    fillLight: true,
    fillLightIntensity: 1.4,
    chaseLag: 16,
    chaseLagSensitivity: 0.012,
    bobAmplitude: 0.0012,
    swayAmplitude: 0.011
  },
  bc303: {
    // Tau'ri BC-303 Prometheus (targetSize 0.07 in bc303.js). Sits
    // between the Ranger (atmospheric shuttle) and the Enterprise
    // capitals, both in geometry and in handling — heavier than the
    // Ranger but not yet capital-mass, so the chase profile is a
    // touch closer than enterprise-refit's. Tau'ri engineering is
    // pre-Asgard-shield era so turn tilt reads as "industrial,
    // moderately responsive" — between the Ranger's nimbleness and
    // the Ha'tak's weight.
    followY: -0.04,
    followDistance: 0.20,
    defaultFollowDistance: 0.20,
    orbitDistance: 0.24,
    minOrbitDistance: 0.10,
    maxOrbitDistance: 0.85,
    portraitDistanceBoost: 1.75,
    basePitch: 0,
    maxPitchTilt: 0.08,
    maxBankTilt: 0.14,
    attitudeDamping: 6.5,
    wobble: false,
    hover: false,
    fillLight: true,
    fillLightIntensity: 1.3,
    chaseLag: 15.5,
    chaseLagSensitivity: 0.013,
    bobAmplitude: 0.0014,
    swayAmplitude: 0.012
  },
  alkesh: {
    // Goa'uld Alkesh mid-bomber (targetSize 0.06). Sits between
    // the Ranger (0.04) and the BC-303 (0.07) reflecting its
    // ~90 m canon length — strike-craft scale, not a capital.
    // Chase profile somewhere between dogfighter and capital:
    // closer than Ha'tak, more weight than the X-wing class.
    followY: -0.032,
    followDistance: 0.17,
    defaultFollowDistance: 0.17,
    orbitDistance: 0.21,
    minOrbitDistance: 0.08,
    maxOrbitDistance: 0.85,
    portraitDistanceBoost: 1.75,
    basePitch: 0,
    maxPitchTilt: 0.09,
    maxBankTilt: 0.16,
    attitudeDamping: 7,
    wobble: false,
    hover: false,
    fillLight: true,
    fillLightIntensity: 1.3,
    chaseLag: 15,
    chaseLagSensitivity: 0.012,
    bobAmplitude: 0.0014,
    swayAmplitude: 0.012
  },
  'death-glider': {
    // Goa'uld Death Glider (targetSize 0.036). Wingspan ~12.8 m —
    // same dogfighter bracket as the N-1 (0.035) and X-wing (0.038).
    // Agile carrier-launched fighter, so tight chase and snappy
    // attitude, with the hard Goa'uld banking character the Ha'tak
    // shows writ small — maxBankTilt a notch above the X-wing.
    followY: -0.020,
    followDistance: 0.11,
    defaultFollowDistance: 0.11,
    orbitDistance: 0.14,
    minOrbitDistance: 0.05,
    maxOrbitDistance: 0.70,
    portraitDistanceBoost: 1.7,
    basePitch: 0,
    maxPitchTilt: 0.13,
    maxBankTilt: 0.24,
    attitudeDamping: 7.8,
    wobble: false,
    hover: false,
    fillLight: true,
    fillLightIntensity: 1.2,
    chaseLag: 13.5,
    chaseLagSensitivity: 0.011,
    bobAmplitude: 0.0016,
    swayAmplitude: 0.013
  },
  xwing: {
    // T-65 X-wing starfighter (targetSize 0.038). Just above the
    // N-1 in size (X-wing is 12.5 m canon vs N-1's 11 m). Same
    // dogfighter handling bracket — tight chase, snappy attitude,
    // higher bank tilt. Slight extra wobble vs N-1 for that
    // "Rebel jalopy" hand-flown character.
    followY: -0.020,
    followDistance: 0.11,
    defaultFollowDistance: 0.11,
    orbitDistance: 0.14,
    minOrbitDistance: 0.05,
    maxOrbitDistance: 0.70,
    portraitDistanceBoost: 1.7,
    basePitch: 0,
    maxPitchTilt: 0.13,
    maxBankTilt: 0.23,
    attitudeDamping: 7.8,
    wobble: false,
    hover: false,
    fillLight: true,
    fillLightIntensity: 1.15,
    chaseLag: 13.5,
    chaseLagSensitivity: 0.011,
    bobAmplitude: 0.0016,
    swayAmplitude: 0.013
  },
  'mando-n1': {
    // Mando's N-1 starfighter (targetSize 0.035). Smallest dogfighter
    // in the roster — N-1 is ~11 m long, even smaller than the
    // Interstellar Ranger's 14 m. Tight chase, nimble attitude,
    // fast pulse cadence — feels like a hot rod compared to the
    // capital ships.
    followY: -0.018,
    followDistance: 0.10,
    defaultFollowDistance: 0.10,
    orbitDistance: 0.13,
    minOrbitDistance: 0.05,
    maxOrbitDistance: 0.65,
    portraitDistanceBoost: 1.65,
    basePitch: 0,
    maxPitchTilt: 0.13,
    maxBankTilt: 0.22,
    attitudeDamping: 8,
    wobble: false,
    hover: false,
    fillLight: true,
    fillLightIntensity: 1.15,
    chaseLag: 13,
    chaseLagSensitivity: 0.011,
    bobAmplitude: 0.0014,
    swayAmplitude: 0.012
  },
  naboo: {
    // Naboo N-1 starfighter (targetSize 0.035) — same ~11 m hull class
    // as Mando's N-1, so it shares the small-dogfighter chase profile:
    // tight chase, nimble attitude, fast pulse cadence.
    followY: -0.018,
    followDistance: 0.10,
    defaultFollowDistance: 0.10,
    orbitDistance: 0.13,
    minOrbitDistance: 0.05,
    maxOrbitDistance: 0.65,
    portraitDistanceBoost: 1.65,
    basePitch: 0,
    maxPitchTilt: 0.13,
    maxBankTilt: 0.22,
    attitudeDamping: 8,
    wobble: false,
    hover: false,
    fillLight: true,
    fillLightIntensity: 1.15,
    chaseLag: 13,
    chaseLagSensitivity: 0.011,
    bobAmplitude: 0.0014,
    swayAmplitude: 0.012
  },
  'alien-saucer': {
    // Alien flying saucer (targetSize 0.05). Compact disc craft —
    // sized between the Shuttle and the BC-303. Flies nimble:
    // tighter chase distance than the capital ships, more
    // responsive attitude damping, slightly more wobble/sway for
    // that hovering-UFO character.
    followY: -0.025,
    followDistance: 0.13,
    defaultFollowDistance: 0.13,
    orbitDistance: 0.16,
    minOrbitDistance: 0.07,
    maxOrbitDistance: 0.7,
    portraitDistanceBoost: 1.7,
    basePitch: 0,
    maxPitchTilt: 0.08,
    maxBankTilt: 0.20,
    attitudeDamping: 7.5,
    wobble: true,
    hover: true,
    fillLight: true,
    fillLightIntensity: 1.25,
    chaseLag: 14.5,
    chaseLagSensitivity: 0.012,
    bobAmplitude: 0.0020,
    swayAmplitude: 0.016
  },
  shuttle: {
    // NASA Space Shuttle (targetSize 0.05 in spaceShuttle.js). Small
    // atmospheric-orbit lifter, so the chase profile is the tightest
    // of the realistic-craft lineup — closer than even the Ranger.
    // Chemical-rocket physics means attitude response is sluggish
    // compared to the Federation ships (no inertial dampers in
    // 1981 era), so wobble/sway amplitudes get a slight bump for
    // hand-flown character.
    followY: -0.022,
    followDistance: 0.11,
    defaultFollowDistance: 0.11,
    orbitDistance: 0.13,
    minOrbitDistance: 0.05,
    maxOrbitDistance: 0.7,
    portraitDistanceBoost: 1.65,
    basePitch: 0,
    maxPitchTilt: 0.12,
    maxBankTilt: 0.20,
    attitudeDamping: 7.5,
    wobble: false,
    hover: false,
    fillLight: true,
    fillLightIntensity: 1.15,
    chaseLag: 14,
    chaseLagSensitivity: 0.012,
    bobAmplitude: 0.0018,
    swayAmplitude: 0.014
  },
  'enterprise-b': {
    // Excelsior-class refit Enterprise NCC-1701-B (targetSize 0.075).
    // Sits between Constitution refit (0.06) and Galaxy-class
    // Enterprise-D (0.09) in size, with the elongated Excelsior
    // hull profile. Chase profile matches the mid-mass class.
    followY: -0.05,
    followDistance: 0.22,
    defaultFollowDistance: 0.22,
    orbitDistance: 0.27,
    minOrbitDistance: 0.11,
    maxOrbitDistance: 0.95,
    portraitDistanceBoost: 1.8,
    basePitch: 0,
    maxPitchTilt: 0.065,
    maxBankTilt: 0.13,
    attitudeDamping: 6.2,
    wobble: false,
    hover: false,
    fillLight: true,
    fillLightIntensity: 1.4,
    chaseLag: 16.5,
    chaseLagSensitivity: 0.013,
    bobAmplitude: 0.0013,
    swayAmplitude: 0.011
  },
  skennenrahawi: {
    // USS Skennenrahawi (targetSize 0.085). Mid-large post-TNG
    // explorer. Chase profile a touch closer than Enterprise-D's
    // since the hull is leaner than the saucer-plus-secondary
    // Galaxy-class silhouette.
    followY: -0.058,
    followDistance: 0.25,
    defaultFollowDistance: 0.25,
    orbitDistance: 0.30,
    minOrbitDistance: 0.12,
    maxOrbitDistance: 0.90,
    portraitDistanceBoost: 1.85,
    basePitch: 0,
    maxPitchTilt: 0.06,
    maxBankTilt: 0.13,
    attitudeDamping: 6.0,
    wobble: false,
    hover: false,
    fillLight: true,
    fillLightIntensity: 1.42,
    chaseLag: 16.8,
    chaseLagSensitivity: 0.013,
    bobAmplitude: 0.0012,
    swayAmplitude: 0.011
  },
  warbird: {
    // Romulan D'deridex-class Warbird (targetSize 0.12 in warbird.js).
    // Largest capital in the flyable roster (~1041 m canon, ~1.6x
    // Enterprise-D). Chase profile sits between Enterprise and
    // Ha'tak — capital-ship weight but the bird-of-prey silhouette
    // needs more standoff than the saucer-on-secondary-hull
    // Federation profile.
    followY: -0.07,
    followDistance: 0.32,
    defaultFollowDistance: 0.32,
    orbitDistance: 0.38,
    minOrbitDistance: 0.14,
    maxOrbitDistance: 0.80,
    portraitDistanceBoost: 1.9,
    basePitch: 0,
    maxPitchTilt: 0.055,
    maxBankTilt: 0.16,
    attitudeDamping: 5.5,
    wobble: false,
    hover: false,
    fillLight: true,
    fillLightIntensity: 1.45,
    chaseLag: 17.0,
    chaseLagSensitivity: 0.013,
    bobAmplitude: 0.0011,
    swayAmplitude: 0.010
  },
  bc304: {
    // Tau'ri BC-304 Daedalus-class (targetSize 0.08 in bc304.js).
    // ~15% bigger than the Prometheus and carrying Asgard tech, so
    // chase distances bump up slightly and attitude response gets a
    // touch quicker — the Daedalus class is faster + more agile
    // than the Prometheus in canon thanks to the shield + drive
    // upgrades.
    followY: -0.05,
    followDistance: 0.23,
    defaultFollowDistance: 0.23,
    orbitDistance: 0.28,
    minOrbitDistance: 0.11,
    maxOrbitDistance: 0.85,
    portraitDistanceBoost: 1.8,
    basePitch: 0,
    maxPitchTilt: 0.075,
    maxBankTilt: 0.14,
    attitudeDamping: 6,
    wobble: false,
    hover: false,
    fillLight: true,
    fillLightIntensity: 1.35,
    chaseLag: 15.8,
    chaseLagSensitivity: 0.013,
    bobAmplitude: 0.0013,
    swayAmplitude: 0.012
  }
};

const DEFAULT_CRAFT_FLIGHT_PROFILE = CRAFT_FLIGHT_PROFILES.saucer;

function createDefaultFlyKeys() {
  return { forward: false, backward: false, left: false, right: false, up: false, down: false, rotateLeft: false, rotateRight: false, rotateUp: false, rotateDown: false };
}

function lockFlyUiInput(duration = 120) {
  flyUiInputLockUntil = performance.now() + duration;
}

function isFlyUiInputLocked() {
  return performance.now() < flyUiInputLockUntil;
}

function getActiveCraftProfile() {
  return CRAFT_FLIGHT_PROFILES[activeCraftKey] || DEFAULT_CRAFT_FLIGHT_PROFILE;
}

function getCraftFollowDistance(profile = getActiveCraftProfile()) {
  if (!camera) return profile.followDistance;
  const aspect = THREE.MathUtils.clamp(camera.aspect || 1, 0.45, 1.4);
  const portraitBoost = profile.portraitDistanceBoost
    ? THREE.MathUtils.clamp(0.75 / aspect, 1, profile.portraitDistanceBoost)
    : 1;
  // Chase rig shrinks with the craft during atmospheric entry so the
  // framing (ship size in frame) stays constant while the world grows.
  return profile.followDistance * portraitBoost * atmoCraftScaleLive;
}

function getCraftFollowOffset(target, profile = getActiveCraftProfile()) {
  const followDist = getCraftFollowDistance(profile);
  // Scale the vertical lift in proportion to the live follow distance so
  // the ship holds a CONSTANT screen position as the wheel dollies the
  // chase rig in and out. With a fixed lift, zooming changes the angle the
  // lift subtends (lift/distance), so the ship drifted up when zooming out
  // and down when zooming in — the "zoom moves the craft up and down" bug.
  // baseFollow is a stable reference (the dolly mutates profile.followDistance,
  // so we anchor to defaultFollowDistance / orbitDistance instead); at the
  // home distance the ratio is 1 so the default framing is unchanged.
  const baseFollow = profile.defaultFollowDistance != null
    ? profile.defaultFollowDistance
    : (profile.orbitDistance != null ? profile.orbitDistance : profile.followDistance);
  const liftRatio = baseFollow > 1e-5 ? profile.followDistance / baseFollow : 1;
  target.set(0, profile.followY * atmoCraftScaleLive * liftRatio, -followDist);
  return target;
}

function clampCraftOrbitDistance(value, profile = getActiveCraftProfile()) {
  // Orbit-cam bounds shrink with the atmospheric craft scale so the
  // middle-mouse orbit stays framed on the (much smaller) ship.
  return THREE.MathUtils.clamp(
    value,
    profile.minOrbitDistance * atmoCraftScaleLive,
    profile.maxOrbitDistance * atmoCraftScaleLive
  );
}

// Apply true-radius scaling to every planet (and the sun) so realistic
// mode shows them at their correct relative sizes. The "tiltGroup"
// child of each planetGroup holds the body mesh + ring, so scaling
// that uniformly shrinks the body without disturbing moon orbits or
// the world-space planet position. Same idea for the sun's body.
// Ring shadow uniforms are world-space radii. Scaling a planet's tiltGroup
// shrinks the ring's world positions but leaves planetRadius at its authored
// value, so every fragment ends up inside the shadow cylinder and the whole
// ring renders fully shadowed. Saturn in realistic mode had a ring outer edge
// at 4.45 world units against a planetRadius uniform of 7.
function applyRingShadowScale(tiltGroup, scale) {
  if (!tiltGroup) return;
  tiltGroup.traverse((o) => {
    const base = o.userData && o.userData.baseRingPlanetRadius;
    const u = o.material && o.material.uniforms;
    if (base && u && u.planetRadius) u.planetRadius.value = base * scale;
  });
}

// Draws a planet body at `scale` times its authored radius. The tilt
// group (surface and rings) and the atmosphere shell, its sibling, scale
// together, and the shaders' world-space radii follow them. Realistic
// mode and the To-Scale lineup both size planets through here.
function setPlanetBodyScale(planetGroup, scale) {
  const tiltGroup = planetGroup.getObjectByName('tiltGroup');
  if (tiltGroup) {
    tiltGroup.scale.setScalar(scale);
    applyRingShadowScale(tiltGroup, scale);
  }
  planetGroup.children.forEach((child) => {
    if (child.name === 'atmosphere' || (child.userData && child.userData.isAtmosphere)) {
      child.scale.setScalar(scale);
      // The shell shader's limb reddening + planet shadow cone
      // compute against world-space radii, so the uniforms must
      // scale with the mesh or both effects use the wrong radii.
      const base = child.userData && child.userData.baseAtmoRadii;
      const u = child.material && child.material.uniforms;
      if (base && u) {
        if (u.planetRadius) u.planetRadius.value = base.planet * scale;
        if (u.atmosphereRadius) u.atmosphereRadius.value = base.shell * scale;
      }
    }
  });
}

// Same for a Sun built by buildSunBody: the photosphere, corona shell and
// the prominence group scale; the glow billboard is sized per frame.
function setSunBodyScale(sunGroup, scale) {
  sunGroup.children.forEach((child) => {
    if (child.userData && child.userData.sunScaleAware) {
      child.scale.setScalar(scale);
    } else if (child.isMesh && child.geometry && child.geometry.type === 'SphereGeometry') {
      child.scale.setScalar(scale);
    }
  });
}

function applyRealisticBodyScales() {
  const sunRealistic = getRealisticBodyRadius('sun');
  if (sun && sunRealistic) {
    const baseSunRadius = celestialData.sun.radius || 15;
    setSunBodyScale(sun, sunRealistic / baseSunRadius);
    const sunGlowSprite = sun.getObjectByName('sunGlowSprite');
    if (sunGlowSprite) sunGlowSprite.userData.realisticBaseScale = sunRealistic * 4;
  }
  Object.entries(planets).forEach(([name, planetGroup]) => {
    const realistic = getRealisticBodyRadius(name);
    const baseRadius = celestialData[name] && celestialData[name].radius;
    if (!realistic || !baseRadius) return;
    setPlanetBodyScale(planetGroup, realistic / baseRadius);
  });
  updateEarthSatelliteScales(getRealisticBodyRadius('earth') / celestialData.earth.radius);
  setMoonBodyScales(true);
}

// Moons at true size in realistic scale: named moons from their data, the
// procedural swarm spread over 3 to 60 km (most real minor moons are small
// captured rocks). The moon group scales as a whole; its position is its
// orbit, which its own scale leaves alone.
function setMoonBodyScales(realistic) {
  Object.values(moons).forEach((list) => list.forEach((moon) => {
    const r = moon.userData.radius;
    if (!r) return;
    if (!realistic) { moon.scale.setScalar(1); return; }
    const km = moon.userData.isMinorMoon
      ? 3 + THREE.MathUtils.clamp((r - 0.04) / 0.1, 0, 1) * 57
      : (parseDistanceKm(moon.userData.realDiameter) || 0) / 2;
    if (km > 0) moon.scale.setScalar((km * REAL_KM_TO_SCENE) / r);
  }));
}

// Reset planet/sun body scales back to their authored artistic radii.
function resetArtisticBodyScales() {
  if (sun) setSunBodyScale(sun, 1);
  Object.values(planets).forEach((planetGroup) => setPlanetBodyScale(planetGroup, 1));
  updateEarthSatelliteScales(1);
  setMoonBodyScales(false);
}

function resetCraftOrbitCamera(profile = getActiveCraftProfile()) {
  if (profile.defaultFollowDistance != null) {
    profile.followDistance = profile.defaultFollowDistance;
  }
  targetOrbitAngle = 0;
  targetOrbitPitch = 0.3;
  cameraOrbitAngle = 0;
  cameraOrbitPitch = 0.3;
  cameraOrbitDistance = clampCraftOrbitDistance(profile.orbitDistance * atmoCraftScaleLive, profile);
  _cameraOrbitDistanceTarget = cameraOrbitDistance;
  _followDistanceTarget = profile.followDistance;
  _parkedZoomPending = 0;
  cameraPanOffsetX = 0;
  cameraPanOffsetY = 0;
}

// The panel's one shop link leads with VR and AR mode, the unlock people
// buy (src/xr/unlock.js, the /vr/ page). Off a headset it opens the /vr/
// page, and reads as unlocked for someone whose browser already holds a VR
// licence. On a headset setupWebXR hands it the unlock module: a press
// opens the unlock panel there and then, and the link goes once unlocked,
// since Enter VR is on screen. Counted as shop_link_click beside the sales
// pages' begin_checkout.
let shopLinkXr = null;
function initShopLink() {
  const link = document.getElementById('shop-link');
  if (!link) return;
  const label = link.querySelector('[data-shop-label]');
  label.textContent = vt('controls.shop.unlock', { mode: vt('controls.shop.vr'), price: priceOf('xr', 299).label });
  // Bought on this browser: the card stays and says so, still leading to
  // the /vr/ page for how to open it on a headset.
  let ownsVr = false;
  try { ownsVr = Boolean(localStorage.getItem('xr.license')); } catch { /* storage blocked */ }
  if (ownsVr) {
    label.textContent = vt('controls.shop.owned', { mode: vt('controls.shop.vr') });
    link.title = vt('controls.shop.ownedTitle');
    link.dataset.i18nTitle = 'controls.shop.ownedTitle';
  }
  link.addEventListener('click', (e) => {
    track('shop_link_click', { product: link.dataset.product, placement: 'controls', headset: Boolean(shopLinkXr), transport_type: 'beacon' });
    if (link.dataset.product === 'xr' && shopLinkXr) {
      e.preventDefault();
      shopLinkXr.xr.openXrPaywall({ mode: shopLinkXr.mode });
    }
  });
  initDesktopLink();
}
// The desktop products under the VR card, as one button and on computers
// only: the live wallpapers (Mac and Windows) and the screensaver (Mac,
// Windows, Linux). While the wallpapers' launch offer brings the screensaver
// free it sells the wallpapers, which is then the cheaper way to both;
// otherwise the desktop bundle. Prices and offers follow their dates.
function initDesktopLink() {
  const link = document.getElementById('desktop-link');
  if (!link || RUNNING_ON_MOBILE || isMobileLikeDevice()) return;
  const offer = activeOffer('wallpaper');
  const viaWallpaper = Boolean(offer && offer.free.includes('screensaver-mac'));
  const product = viaWallpaper ? 'wallpaper' : 'bundle-desktop';
  link.href = viaWallpaper ? '/live-wallpaper/' : '/bundles/desktop/';
  link.dataset.product = product;
  link.querySelector('[data-shop-label]').textContent = vt('controls.shop.desktop', { price: priceOf(product, 499).label });
  link.closest('.control-row').hidden = false;
  link.addEventListener('click', () => {
    track('shop_link_click', { product, placement: 'controls', transport_type: 'beacon' });
  });
}
function shopLinkOnHeadset(xr, vr) {
  const link = document.getElementById('shop-link');
  if (!link) return;
  shopLinkXr = { xr, mode: vr ? 'immersive-vr' : 'immersive-ar' };
  // A headset has no desktop for the wallpaper or the screensaver.
  document.getElementById('desktop-link')?.closest('.control-row')?.setAttribute('hidden', '');
  link.dataset.product = 'xr';
  link.querySelector('[data-shop-label]').textContent = vt('controls.shop.unlock', { mode: vt(vr ? 'controls.shop.vr' : 'controls.shop.ar'), price: xr.XR_PRICE });
  const row = link.closest('.control-row');
  const sync = () => { if (row) row.hidden = xr.xrUnlocked(); };
  sync();
  xr.onXrUnlockChange(sync);
}

// Occasional tips about what the folded controls hide (src/ui/tips.js):
// only in the plain viewer with nothing open, and each opens its place.
function initTips() {
  if (IS_SCREENSAVER) return;
  initShopLink();
  // The panel is position: fixed, so offsetParent is always null; read its
  // collapsed class and whether it is drawn at all (photo mode hides it).
  const panelOpen = () => {
    const panel = document.getElementById('controls-panel');
    return Boolean(panel && !panel.classList.contains('collapsed') && panel.getClientRects().length > 0);
  };
  const point = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    // On the rail (index.html) the part of the panel that holds it opens
    // beside the icons.
    const revealed = Boolean(window.__controlsPanel?.reveal(el));
    if (!revealed && !panelOpen()) {
      const compact = window.matchMedia(COMPACT_LAYOUT_QUERY).matches;
      document.getElementById(compact ? 'mobile-menu-fab' : 'controls-toggle')?.click();
    }
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    el.classList.remove('tip-pulse');
    void el.offsetWidth;
    el.classList.add('tip-pulse');
    setTimeout(() => el.classList.remove('tip-pulse'), 2600);
  };
  startTips({
    // Beside the controls panel when it is open and there is room, so the
    // tip never covers its buttons.
    left: () => {
      const panel = document.getElementById('controls-panel');
      if (!panelOpen()) return null;
      const right = window.__controlsPanel ? window.__controlsPanel.rightEdge() : panel.getBoundingClientRect().right;
      return right + 360 < window.innerWidth ? Math.round(right + 14) : null;
    },
    canShow: () => !flyMode && !rocketryActive && !photoModeActive && !documentaryActive && !scaleModeActive && !lessonActive() && !astroModeActive()
      && !coolWorldsShowreelActive && !objectBrowserOpen && !spaceEventsOpen && !shareSheetOpen && !matryoshka.isOpen()
      && !wormholeTunnelInFlight && !(cupolaView && cupolaView.active) && !document.querySelector('dialog[open]'),
    actions: {
      focus: (key) => {
        const obj = findLoadedSceneObjectByName(key);
        if (obj) focusOnObject(obj, { via: 'tip' });
      },
      // The Events panel's scenes load what they need first (the wormhole
      // exists only once deep space is on).
      scene: (id) => runSpaceEvent(id),
      click: (id) => document.getElementById(id)?.click(),
      point,
      openGroup: (group, id) => {
        const toggle = document.querySelector(`.control-group[data-group="${group}"] .control-group-toggle`);
        if (toggle && toggle.getAttribute('aria-expanded') !== 'true') toggle.click();
        point(id);
      },
    },
  });
}

function closeTransientPanels(except = null) {
  if (except !== 'objectBrowser' && objectBrowserOpen) {
    closeObjectBrowser();
  }
  if (except !== 'spaceEvents' && spaceEventsOpen) {
    closeSpaceEvents();
  }
  if (except !== 'shareSheet' && shareSheetOpen) {
    closeShareSheet();
  }
  if (except !== 'matryoshka' && matryoshka.isOpen()) {
    matryoshka.close();
  }
  // Close the mobile bottom-sheet menu (controls panel) so it doesn't
  // sit on top of cinematic / fly / documentary modes blurring the
  // scene behind its backdrop-filter.
  if (document.body.classList.contains('mobile-menu-open')) {
    document.body.classList.remove('mobile-menu-open');
    const panel = document.getElementById('controls-panel');
    if (panel) panel.classList.add('collapsed');
    const fab = document.getElementById('mobile-menu-fab');
    if (fab) fab.setAttribute('aria-expanded', 'false');
  }
}

function syncAlienHudToggle() {
  document.body.classList.toggle('alien-hud-disabled', !alienHudEnabled);
  if (alienHudFlyToggle) {
    alienHudFlyToggle.classList.toggle('active', alienHudEnabled);
    alienHudFlyToggle.textContent = alienHudEnabled ? 'HUD on' : 'HUD off';
    alienHudFlyToggle.setAttribute('aria-pressed', alienHudEnabled ? 'true' : 'false');
  }

  if (!alienHudEnabled) {
    hideAlienScanHud(true);
    stopAlienScanAudio();
  }
}

function toggleAlienHud() {
  alienHudEnabled = !alienHudEnabled;
  syncAlienHudToggle();
}

// Propulsion types with realistic characteristics
// Realistic propulsion characteristics based on real physics
// ISP (specific impulse) affects efficiency, thrust affects acceleration
const propulsionTypes = {
  chemical: {
    acceleration: 0.08,     // Reduced - slower acceleration
    maxSpeed: 1,            // Reduced max speed
    name: 'Chemical Rocket',
    drag: 0.995,            // More drag for easier control
    rotateSpeed: 2.5,       // Fast RCS thrusters
    thrustCurve: 'instant', // Instant on/off
    affectedByGravity: true
  },
  ion: {
    acceleration: 0.02,     // Low thrust (0.1 N typical)
    maxSpeed: 3,            // High ISP = high eventual speed
    name: 'Ion Drive',
    drag: 0.998,            // Almost no drag - super efficient
    rotateSpeed: 0.6,       // Slow rotation (momentum wheels)
    thrustCurve: 'gradual', // Builds up slowly
    affectedByGravity: true
  },
  nuclear: {
    acceleration: 0.6,      // Good thrust
    maxSpeed: 12,           // Nuclear thermal ~900s ISP
    name: 'Nuclear Thermal',
    drag: 0.997,            // Efficient
    rotateSpeed: 1.5,       // Moderate maneuvering
    thrustCurve: 'rampup',  // Reactor takes time to heat
    affectedByGravity: true
  },
  fusion: {
    acceleration: 1.5,      // Excellent thrust
    maxSpeed: 40,           // Very high delta-v potential
    name: 'Fusion Drive',
    drag: 0.995,            // Good efficiency
    rotateSpeed: 2.0,       // Good maneuvering
    thrustCurve: 'pulse',   // Pulsed fusion reactions
    affectedByGravity: true
  },
  warp: {
    acceleration: 10,       // Extreme (bends spacetime)
    // maxSpeed extended so the asymptotic warp-factor scale can climb
    // past warp 9 into 9.9999 territory. The HUD readout maps live
    // flyVelocity to a TNG-style warp factor (1.0 - 9.9999) via
    // speedToWarpFactor below; this number is the speed at which the
    // factor crosses 9.9999. The actual sub-warp threshold ("warp 1")
    // sits at flyVelocity ≈ 5 in our scene units.
    maxSpeed: 10000,
    name: 'Warp Drive',
    drag: 0.92,             // Warp bubble decay
    rotateSpeed: 3.0,       // Inertial dampeners
    thrustCurve: 'instant',
    affectedByGravity: false // Warp bubble shields from gravity
  },
  // Star Trek impulse: sub-light fusion drive. In canon, "full
  // impulse" maxes at ~0.25c so the ship is fast but firmly sub-
  // light. Maps to a maxSpeed similar to fusion in our abstract
  // scale, with a softer acceleration curve so the user has to ease
  // up to top speed rather than jamming it.
  impulse: {
    acceleration: 1.2,
    maxSpeed: 40,
    name: 'Impulse',
    drag: 0.994,
    rotateSpeed: 2.2,
    thrustCurve: 'rampup',
    affectedByGravity: true
  },
  // Saucer anti-gravity drive. Functionally equivalent to impulse
  // (sub-light cruise) but with quicker acceleration since hover-
  // craft are typically more responsive than capital-ship impulse
  // banks. Same affectedByGravity behaviour since it's still sub-
  // light and would feel the well of nearby bodies.
  antigravity: {
    acceleration: 1.6,
    maxSpeed: 40,
    name: 'Anti-gravity',
    drag: 0.992,
    rotateSpeed: 2.6,
    thrustCurve: 'instant',
    affectedByGravity: true
  },
  // Abnormal Sphere's exotic drift drive. Slower than the others
  // (the sphere doesn't really "fly" so much as drift), with high
  // damping so it stops hard when you release the throttle.
  drift: {
    acceleration: 0.7,
    maxSpeed: 22,
    name: 'Drift',
    drag: 0.985,
    rotateSpeed: 1.8,
    thrustCurve: 'gradual',
    affectedByGravity: true
  }
};

// Per-craft allowed propulsion lists. Drives the propulsion dropdown
// when a craft is selected so the Enterprise can't accidentally fly
// on Ion engines and the Saucer never offers Chemical / Nuclear /
// Fusion (which would feel wrong for a UFO). The first entry is the
// craft's default — applied on craft switch so a brand-new ship comes
// up on its hero propulsion mode without the user having to pick.
const CRAFT_PROPULSIONS = {
  saucer:              ['antigravity', 'warp'],
  sphere:              ['drift', 'warp'],
  ranger:              ['chemical', 'fusion', 'impulse'],
  enterprise:          ['impulse', 'warp'],
  'enterprise-refit':  ['impulse', 'warp'],
  // Goa'uld ships use sublight engines for in-system travel and
  // hyperdrive windows for FTL. Functionally we map those to the
  // existing 'impulse' (in-system) and 'warp' (FTL) options — the
  // propulsion-name UI swap can come later when we have proper
  // per-craft labels for each mode.
  hatak:               ['impulse', 'warp'],
  // BC-303 Prometheus runs sublight engines + a (reverse-engineered
  // Goa'uld) hyperdrive. Same mapping as Ha'tak — impulse for
  // sublight cruising, warp for FTL — since the FTL math is
  // identical from the player's POV.
  bc303:               ['impulse', 'warp'],
  // BC-304 Daedalus adds Asgard intergalactic hyperdrive to the
  // BC-303 platform. Same propulsion abstraction; the in-game
  // difference is the chase + light tuning, not new propulsion modes.
  bc304:               ['impulse', 'warp'],
  // NASA Space Shuttle is a chemical-rocket lifter; no fusion, no
  // FTL, no ion. Single chemical option preserves the in-character
  // limitation that the orbiter never went above LEO in real life.
  shuttle:             ['chemical'],
  // Romulan warbird is powered by a forced quantum singularity, not
  // matter-antimatter, but the in-game propulsion abstraction maps
  // to the same impulse + warp options Federation ships use — the
  // singularity-vs-warp-core difference is a power-source thing,
  // not a propulsion-type thing.
  warbird:             ['impulse', 'warp'],
  // Excelsior-refit Enterprise-B: standard Starfleet matter-antimatter
  // warp core, impulse engines for sublight. Same prop set as the
  // rest of the Federation roster.
  'enterprise-b':      ['impulse', 'warp'],
  // USS Skennenrahawi: post-TNG-era Starfleet explorer, standard
  // Federation propulsion stack.
  skennenrahawi:       ['impulse', 'warp'],
  // Alien saucer: exotic-physics craft, full propulsion menu since
  // alien tech is canonically capable of every flight mode.
  'alien-saucer':      ['antigravity', 'drift', 'warp'],
  // Mando's N-1: twin jet sublight engines + retrofitted hyperdrive.
  // 'impulse' maps to the sublight nacelle thrust, 'warp' to the
  // hyperdrive jump.
  'mando-n1':          ['impulse', 'warp'],
  naboo:               ['impulse', 'warp'],
  // T-65 X-wing: four sublight ion engines + Class 1 hyperdrive.
  // Same propulsion mapping as the N-1.
  xwing:               ['impulse', 'warp'],
  // Goa'uld Alkesh: sublight + hyperdrive, same Goa'uld propulsion
  // stack as the Ha'tak.
  alkesh:              ['impulse', 'warp'],
  // Goa'uld Death Glider: sublight only — canon gliders carry no
  // hyperdrive and deploy from Ha'tak motherships (in "Tangent" a
  // glider that leaves orbit just drifts). Same in-character
  // limitation the Space Shuttle gets with its chemical-only entry.
  'death-glider':      ['impulse']
};

function getCraftPropulsions(key) {
  return CRAFT_PROPULSIONS[key] || ['ion', 'chemical', 'nuclear', 'fusion', 'warp'];
}

// Rebuild the propulsion dropdown when craft changes so each ship
// only offers its in-character propulsion options. Picks the craft's
// default (first entry) if the current selection isn't allowed for
// the new ship, otherwise preserves the selection across switches.
function refreshPropulsionSelectForCraft(craftKey) {
  const select = document.getElementById('propulsion-type');
  if (!select) return;
  const allowed = getCraftPropulsions(craftKey);
  const previous = currentPropulsion;
  const keepPrevious = allowed.includes(previous);
  const next = keepPrevious ? previous : allowed[0];

  select.innerHTML = '';
  for (const key of allowed) {
    const prop = propulsionTypes[key];
    if (!prop) continue;
    const option = document.createElement('option');
    option.value = key;
    option.textContent = prop.name || key;
    if (key === next) option.selected = true;
    select.appendChild(option);
  }
  if (next !== currentPropulsion) {
    currentPropulsion = next;
    const nextProp = propulsionTypes[currentPropulsion];
    if (nextProp) flySpeed = Math.min(flySpeed, nextProp.maxSpeed);
  }
}

// Gravitational constants for simulation (scaled for gameplay)
const G_CONSTANT = 0.0001; // Gravitational constant (scaled)
const celestialMasses = {
  sun: 100000,
  mercury: 0.055,
  venus: 0.815,
  earth: 1,
  mars: 0.107,
  jupiter: 317.8,
  saturn: 95.2,
  uranus: 14.5,
  neptune: 17.1,
  pluto: 0.002
};
let currentPropulsion = 'ion';

// Realistic scale mode
let realisticScaleMode = false;

// Sun visual mode. Detailed (default) = stylised warm/orange palette
// with full procedural plasma. Realistic = brilliant cream-white as
// the sun actually reads from orbit. The shader gates realistic vs
// detailed against closeLod, so close-range views always show the
// detailed surface regardless of this toggle. Default to realistic
// (clean cream-white orbital-photo disc) since that's the look that
// matches every reference image of the sun from space; the detailed
// plasma palette is still available via the toggle for stylized
// close-up views, and from the start with ?sun=detailed (the videos' To
// Scale lineup uses it).
let realisticSunMode = !(typeof window !== 'undefined' && /[?&]sun=detailed\b/.test(window.location.search));

// Sun surface LOD. octaveLod falls from 1 at SUN_LOD_NEAR to 0.5 at
// SUN_LOD_FAR (sun radii, so it holds in both scale modes); the shader no
// longer reads it since the far field was baked (src/sunfarfield.js), and
// its `time` advances every frame, since the plasma's churn and the
// granules visibly move.
const SUN_LOD_NEAR = 12;
const SUN_LOD_FAR = 40;
// The Sun's disc, in drawing-buffer pixels across, past which its far
// field's cubes are made (src/sunfarfield.js); below it the photosphere
// works the far field out per pixel, a few thousand pixels at most.
const SUN_FAR_FIELD_WANT_PX = 64;
const _sunLodSunPos = new THREE.Vector3();
// The simulation's date in days from J2000 for the sunspot groups, one
// uniform shared by every Sun material (the viewer's and the lineup's).
const sunDaysUniform = { value: 0 };

// Venus procedural cloud shader. Replaces the legacy texture +
// UV-offset super-rotation with a fully procedural cloud top (OKLCh
// sulphuric palette + double domain warp + Y-chevron + polar collar).
// Stashed at module scope so the animate loop can advance uTime /
// uSunDir without re-querying the planet mesh each frame.
let venusShaderMaterial = null;

// Ephemeris-driven simulation Julian Date. Initialized to "now" so the
// solar system opens with planets exactly where they are in the real sky
// at the user's local moment. Advances each frame by delta * timeSpeed *
// rate, where rate is 1 in realistic mode (real wall-clock pace) and a
// big multiplier in artistic mode so orbits are visible without cranking
// the time slider. This drives both modes — only the radial scaling and
// time rate differ between them, so switching modes never causes a
// discontinuous jump in planet positions.
let simulationJD = dateToJD(new Date());
const ARTISTIC_TIME_RATE = 25000; // sim seconds per real second in artistic mode
// Realistic mode: scene units per AU. AU_TO_SCENE = 5000 gives an
// Earth-to-Sun distance of 5000 units, paired with true planet radii
// (canonical AU/Earth-radius ratio of 23 481:1). Earth ends up at
// 5000 with radius ~0.213 (a tiny dot), Pluto at ~197 400 with radius
// 0.04. Exactly the proportions you'd photograph from outside the
// solar system at this zoom.
const AU_TO_SCENE = 5000;
const KM_PER_AU = 149597870.7;
// Real-world equatorial radii in kilometres. Realistic-mode body
// sizes are derived as REAL_RADII_KM[name] / KM_PER_AU * AU_TO_SCENE,
// so updating AU_TO_SCENE automatically rescales every body to match.
const REAL_RADII_KM = {
  sun:     695700,
  mercury: 2439.7,
  venus:   6051.8,
  earth:   6371.0,
  mars:    3389.5,
  jupiter: 69911,
  saturn:  58232,
  uranus:  25362,
  neptune: 24622,
  pluto:   1188.3
};
function getRealisticBodyRadius(name) {
  const km = REAL_RADII_KM[name];
  return km != null ? AU_TO_SCENE * (km / KM_PER_AU) : null;
}

// Named moons join REAL_RADII_KM as createMoon builds them, from the
// realDiameter in their data, so every realistic-radius lookup (focus,
// framing, fly-mode collision) sees a moon's true size as well.
function registerRealMoonRadius(moonData) {
  const key = (moonData.name || '').toLowerCase();
  const km = parseDistanceKm(moonData.realDiameter);
  if (key && km && REAL_RADII_KM[key] == null) REAL_RADII_KM[key] = km / 2;
}

// A moon's true orbital distance in realistic scene units. Named moons
// carry it in their data; the procedural swarm keeps its place relative
// to the parent's outermost named moon, real and artistic alike.
function getRealisticMoonDistance(moon) {
  const km = parseDistanceKm(moon.userData.realDistance);
  if (km) return AU_TO_SCENE * (km / KM_PER_AU);
  const siblings = (moons[moon.userData.parentPlanet] || []).filter((m) => !m.userData.isMinorMoon);
  let outer = null;
  siblings.forEach((m) => { if (!outer || m.userData.baseDistance > outer.userData.baseDistance) outer = m; });
  const outerKm = outer && parseDistanceKm(outer.userData.realDistance);
  if (!outerKm) return null;
  return AU_TO_SCENE * (outerKm / KM_PER_AU) * (moon.userData.baseDistance / outer.userData.baseDistance);
}

// Earth's Moon in realistic scale sits where it really is (Meeus ch. 47),
// so its phase, distance and eclipses follow the date. Ecliptic km to
// scene: (x, y, z) -> (x, z, -y), the planets' remap. moonGeocentricJ2000
// carries the Moon into the planets' J2000 frame and Terrestrial Time; Meeus
// alone is ecliptic of date, 0.4 deg off by 2027, enough for an eclipse's
// shadow to miss the Earth (src/eclipse/geometry.js).
// In eclipse mode the artistic scale keeps its Moon distance but takes the
// real direction, so the Sun, the Moon and the Earth still line up.
const REAL_KM_TO_SCENE = AU_TO_SCENE / KM_PER_AU;
// The far future's sky (?whatif=future, src/whatiffuture.js): the Moon
// farther out or placed for a staged eclipse, and the Sun's size, for
// episode 6's renders.
const FUTURE_ON = futureRequested(window.location.search);
if (FUTURE_ON) installFutureHook();
// The Earth stood up straight (?whatif=notilt, src/whatiftilt.js), episode
// 7's scene: the axis on the ecliptic pole, or where a render's keys put it.
const TILT_ON = tiltRequested(window.location.search);
if (TILT_ON) installTiltHook();
function earthMoonKm(jd) {
  const p = moonGeocentricJ2000(jd);
  return FUTURE_ON ? futureMoonKm(jd, p) : p;
}
function placeEarthMoonFromEphemeris(moon, jd, artisticDistance = 0) {
  const p = earthMoonKm(jd);
  const k = artisticDistance > 0 ? artisticDistance / p.km : REAL_KM_TO_SCENE;
  moon.position.set(p.x * k, p.z * k, -p.y * k);
  moon.userData.orbitAngle = Math.atan2(-moon.position.z, moon.position.x);
}
function isEarthsMoon(moon) {
  return moon.userData.parentPlanet === 'earth' && moon.userData.name === 'Moon';
}

// Jupiter's four large moons at true scale, where they really are: Meeus
// ch. 44 (src/astro/galilean.js, within 0.123 Jupiter radii of Horizons) in
// Jupiter's IAU equator, so Io's shadow crosses the disc when it does. Its u
// is as seen from the Earth, light time included, so it is taken at jd plus
// the light time: the moons' places at jd itself.
const GALILEAN_INDEX = { Io: 0, Europa: 1, Ganymede: 2, Callisto: 3 };
const JUPITER_EQUATORIAL_KM = 71492; // Meeus' unit for r
const _galOffsets = [[0, 0, 0], [0, 0, 0], [0, 0, 0], [0, 0, 0]];
const _galPole = [0, 0, 0];
const _galJupiter = new THREE.Vector3();
const _galEarth = new THREE.Vector3();
const _galV = new THREE.Vector3();
let _galJD = NaN;
function galileanIndex(moon) {
  return moon.userData.parentPlanet === 'jupiter' ? GALILEAN_INDEX[moon.userData.name] ?? -1 : -1;
}
function placeGalileanFromEphemeris(moon, index, jd) {
  if (!planets.jupiter || !planets.earth) return false;
  planets.jupiter.getWorldPosition(_galJupiter);
  if (jd !== _galJD) {
    _galV.copy(planets.earth.getWorldPosition(_galEarth)).sub(_galJupiter).applyMatrix3(SCENE_TO_EQUATORIAL);
    const lightDays = (_galV.length() / AU_TO_SCENE) / 173.1446;
    _galV.normalize();
    galileanOffsets(jd + lightDays, [_galV.x, _galV.y, _galV.z], jupiterPole(jd, _galPole), _galOffsets);
    _galJD = jd;
  }
  const o = _galOffsets[index];
  equatorialToScene(o[0], o[1], o[2], _galV).multiplyScalar(JUPITER_EQUATORIAL_KM * REAL_KM_TO_SCENE).add(_galJupiter);
  moon.parent.updateWorldMatrix(true, false);
  moon.position.copy(moon.parent.worldToLocal(_galV));
  return true;
}

// The Moon's shadow on the Earth and the Earth's on the Moon, from the real
// geometry (src/eclipse/geometry.js). One set of uniforms, shared by the
// Earth's surface, its atmosphere shell and the Moon's surface: the two
// centres in world space, and the Moon and the Sun relative to the Earth in
// kilometres along the world axes. The directions come from the scene, so
// whatever the scene draws casts the shadow (the artistic Moon, the Events
// panel's staged eclipse); the distances come from the ephemeris, so the
// shadows are their real size in either scale.
// Saturn's rings round the Earth (?whatif=rings, src/earthrings.js).
const EARTH_RINGS_ON = earthRingsRequested(window.location.search);

const earthEclipseUniforms = {
  uEclipseOn: { value: 0 },
  uEclipseEarthCenter: { value: new THREE.Vector3() },
  uEclipseMoonCenter: { value: new THREE.Vector3() },
  uEclipseMoonKm: { value: new THREE.Vector3() },
  uEclipseSunKm: { value: new THREE.Vector3() },
  // How bright the light in the Earth's umbra is drawn on the Moon. A camera
  // or an eye exposes for what is bright: in a partial eclipse the sunlit
  // sliver, so the umbra looks dark; in totality the red Moon itself.
  uEclipseUmbraGain: { value: 1 },
  // The skylight under the Moon's shadow on the Earth (earth.js), a share of
  // full sunlight: 2.5 per cent keeps the ground under totality a deep
  // twilight. ?whatif=future can set its own (futureState.skyFloor).
  uEclipseSkyFloor: { value: 0.025 },
};
const _eclipseEarthWorld = new THREE.Vector3();
const _eclipseMoonWorld = new THREE.Vector3();
const _eclipseSunWorld = new THREE.Vector3();
const _eclipseCross = new THREE.Vector3();
function updateEarthEclipseUniforms(earthGroup) {
  const moon = (moons.earth || []).find(isEarthsMoon);
  const u = earthEclipseUniforms;
  if (!moon || !moon.visible) { u.uEclipseOn.value = 0; return; }
  earthGroup.getWorldPosition(_eclipseEarthWorld);
  moon.getWorldPosition(_eclipseMoonWorld);
  if (sun) sun.getWorldPosition(_eclipseSunWorld); else _eclipseSunWorld.set(0, 0, 0);
  const moonKm = earthMoonKm(simulationJD).km;
  const s = sunGeocentricJ2000(simulationJD);
  const sunKm = Math.sqrt(s.x * s.x + s.y * s.y + s.z * s.z);
  u.uEclipseEarthCenter.value.copy(_eclipseEarthWorld);
  u.uEclipseMoonCenter.value.copy(_eclipseMoonWorld);
  u.uEclipseMoonKm.value.subVectors(_eclipseMoonWorld, _eclipseEarthWorld).normalize().multiplyScalar(moonKm);
  u.uEclipseSunKm.value.subVectors(_eclipseSunWorld, _eclipseEarthWorld).normalize().multiplyScalar(sunKm);
  // The umbral magnitude from the same geometry (src/eclipse/geometry.js
  // lunarUmbralMagnitude): over 1 the whole Moon is in the umbra.
  const mKm = u.uEclipseMoonKm.value;
  const sKm = u.uEclipseSunKm.value;
  const sM = Math.asin(1737.4 / moonKm);
  const umbra = (1 + 1 / 85) * Math.asin(R_EARTH_KM / moonKm) - Math.asin(695700 / sunKm) + Math.asin(R_EARTH_KM / sunKm);
  const sep = Math.atan2(_eclipseCross.crossVectors(mKm, sKm).length(), -mKm.dot(sKm));
  const magnitude = (umbra + sM - sep) / (2 * sM);
  u.uEclipseUmbraGain.value = 0.45 + 0.55 * THREE.MathUtils.smoothstep(magnitude, 0.92, 1.02);
  u.uEclipseSkyFloor.value = FUTURE_ON && futureState.skyFloor != null ? futureState.skyFloor : 0.025;
  u.uEclipseOn.value = 1;
}

// Eclipse mode (src/eclipse/mode.js) sets this while it shows an eclipse.
let eclipseMode = null;
function eclipseModeActive() {
  return !!(eclipseMode && eclipseMode.active);
}

// JD → ISO-ish display string. Cached display element + throttled update
// so we don't churn the DOM every frame.
const simDateEl = document.getElementById('sim-date-display');
let lastSimDateUpdate = 0;
// Same throttle pattern for the science panel readout (~8 textContent
// writes per refresh — per-frame churn the user can't perceive).
let lastSciencePanelUpdate = 0;
function jdToDateString(jd) {
  const ms = (jd - 2440587.5) * 86400000;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
}

// Screensaver camera (?mode=screensaver). The shows and their transitions
// live in src/screensaver/tour.js; this hands it the scene objects.
let screensaverTour = null;
let screensaverStarted = false;
// The ISS show playing (src/screensaver/issshow.js), if any.
let screensaverIssShow = null;
// The famous photos show (?show=photos, src/screensaver/photoshow.js).
const SCREENSAVER_PHOTO_SHOW = IS_SCREENSAVER && readScreensaverOptions(window.location.search).show === 'photos';
let screensaverPhotoShow = null;
let screensaverPhotoShowLoading = false;
// The galaxies show (?show=galaxies, src/screensaver/galaxyshow.js): the
// deep-sky galaxy photos one at a time, slewing between them.
const SCREENSAVER_GALAXIES_SHOW = IS_SCREENSAVER && readScreensaverOptions(window.location.search).show === 'galaxies';
let screensaverGalaxyShow = null;
let screensaverGalaxyShowLoading = false;

// ?show=blackhole plays the supermassive black hole with its accretion disc
// (src/gargantua.js). The screensaver enters that system directly, with no
// wormhole flight, and hides the planets and the return wormhole that come
// from the film's story: the paid screensaver shows only our own render.
const SCREENSAVER_BLACKHOLE_SHOW = IS_SCREENSAVER && readScreensaverOptions(window.location.search).show === 'blackhole';
function enterScreensaverBlackHoleSystem() {
  const handle = remoteSystems.gargantua;
  if (!handle || !handle.bodies || !handle.bodies.gargantua) return;
  showSystem('gargantua', { teleport: true, animate: false });
  for (const id in handle.bodies) {
    if (id !== 'gargantua' && handle.bodies[id]) handle.bodies[id].visible = false;
  }
  if (gargantuaReturnMouth) gargantuaReturnMouth.visible = false;
  // The disc's raymarch is the frame (~16 ms at 2880 x 1800 on an M4 Max
  // at full resolution), so the show marches a pixel budget at reduced
  // resolution, stepping down further on a GPU that misses 60 Hz, and caps
  // the pixel ratio at 1.5 (src/gargantua.js, setMarchBudget). ?bhpr=2 is
  // the full-quality path for the site's media: that pixel ratio and a
  // full-resolution march.
  const params = new URLSearchParams(window.location.search);
  const fullQuality = Number(params.get('bhpr')) || 0;
  const raymarch = handle.bodies.gargantua.raymarch;
  if (raymarch) {
    if (fullQuality) raymarch.setMarchScale(1);
    // The live wallpaper draws at most the pixels its Quality allows, at
    // its own frame cap (30 fps unless set), so it marches all of them and
    // steps down only where a GPU misses the cap.
    else if (params.get('wallpaper') === '1') raymarch.setMarchBudget(16e6, { minScale: 0.35 });
    else raymarch.setMarchBudget(600_000);
    raymarch.setFrameTarget(() => frameCapMs);
  }
  const cap = fullQuality || 1.5;
  if (renderer && renderer.getPixelRatio() > cap) {
    renderer.setPixelRatio(cap);
    if (composer) composer.setPixelRatio(cap);
    onWindowResize();
  }
}

// The world the camera is framing, as a wallpaper shot names it (the curate
// panel and "Make this view your Mac wallpaper"): the Sun, a planet by its
// key, or a moon or the ISS by name.
function framedWorld() {
  const o = selectedObject;
  if (!o) return null;
  const key = o === sun ? 'sun' : Object.keys(planets).find((k) => planets[k] === o);
  const name = (o.userData && o.userData.name) || o.name || key;
  const id = key || String(name || '').toLowerCase();
  return id ? { object: o, id, name } : null;
}

// The frame a ground-locked wallpaper shot rides (src/screensaver/tour.js,
// src/ui/curate.js): a planet's spinning surface mesh inside its tiltGroup,
// or the object itself (the ISS turns with its own attitude).
const _groundFrame = new THREE.Quaternion();
function groundFrameOf(object) {
  if (!object) return null;
  const tilt = object.getObjectByName('tiltGroup');
  const spin = (tilt && tilt.children.find((c) => c.isMesh && !c.name)) || object;
  return spin.getWorldQuaternion(_groundFrame);
}

// The same world's tilt without its spin: its equator and rings
// (scene.equator in src/screensaver/tour.js).
const _equatorFrame = new THREE.Quaternion();
function equatorFrameOf(object) {
  const tilt = object && object.getObjectByName('tiltGroup');
  return tilt ? tilt.getWorldQuaternion(_equatorFrame) : null;
}

function resolveScreensaverBody(id) {
  if (id === 'sun') return sun || null;
  if (id === 'milky-way') return (galaxy && galaxy.object) || null;
  if (id === 'blackhole') return (blackHole && blackHole.object) || null;
  if (id === 'supermassive') {
    const handle = remoteSystems.gargantua;
    return (handle && handle.root.visible && handle.bodies && handle.bodies.gargantua) || null;
  }
  return planets[id] || findLoadedSceneObjectByName(id) || null;
}

// The shows move the camera without focusing anything, so the viewer's
// focus-time material pass (ensureFocusedDetail, which rebuilds bodies through
// applyDynamicSurfaceMaterial) never runs here. createPlanets builds Uranus and
// Neptune on the procedural ice-giant shader because their maps are in the boot
// set, and the viewer replaces it with the textured surface the first time any
// body is focused. Without the same swap the shows, and the site media recorded
// from them, drew a deep-blue procedural Neptune that /viewer/#neptune never
// shows.
function useViewerIceGiantSurfaces() {
  for (const key of Object.keys(proceduralPlanetSurfaces)) {
    if (planets[key]) applyDynamicSurfaceMaterial(planets[key], celestialData[key]);
    delete proceduralPlanetSurfaces[key];
  }
}

// Width of the map that carries a body's surface detail, or 0 for a
// procedural surface (the Sun), which does not blur up close.
function screensaverSurfaceTextureWidth(body) {
  const key = (body.userData && body.userData.name || '').toLowerCase();
  let surface = null;
  body.traverse((o) => {
    if (!surface && o.isMesh && o.userData && o.userData.isPlanetSurface && o.userData.planetKey === key) surface = o;
  });
  // Moons are a group around one textured mesh.
  if (!surface) {
    body.traverse((o) => {
      if (!surface && o.isMesh && o.material && o.material.map) surface = o;
    });
  }
  const mat = surface && surface.material;
  if (!mat) return 0;
  const tex = mat.map || (mat.uniforms && mat.uniforms.dayTexture && mat.uniforms.dayTexture.value);
  return (tex && tex.image && tex.image.width) || 0;
}

function screensaverDetailDistance(body, fovDeg = camera.fov) {
  const radius = getBodyBaseRadius(body);
  const width = screensaverSurfaceTextureWidth(body);
  // No surface map. The Sun's shader stays sharp, but closer than 2.2 radii
  // its disc fills the frame edge to edge. Any other map-less surface keeps
  // the 3.4 radii measured on the procedural ice-giant bands, which speckle
  // and show rim facets closer in.
  if (!width) return radius * (body === sun ? 2.2 : 3.4);
  return detailLimitDistance(radius, width, renderer.domElement.height, THREE.MathUtils.degToRad(fovDeg));
}

// The live wallpaper's clock speed (?pace=, seconds of sky a second; 1 is
// real time, the default): from the sky right now, faster, so the Earth can
// be seen to turn. The ISS shows keep real time, their framing following
// the station round its 92-minute orbit, and so do eclipse pages.
function applyScreensaverPace(value) {
  const pace = Math.min(86400, Math.max(1, Number(value) || 1));
  setTimePreset('real');
  if (pace === 1 || SCREENSAVER_ISS_SHOW || eclipseModeActive()) return;
  timePreset = null;
  timeSpeed = pace / timePresetSpeed('real');
  syncTimeControls();
}

// Whether tickAutoTour placed the camera through the planet tour this frame,
// so animate() places it again once the bodies have moved.
let screensaverTourTicked = false;
function tickAutoTour(elapsed) {
  screensaverTourTicked = false;
  if (!IS_SCREENSAVER) return;
  // The sky event pages embed an eclipse in the screensaver page (no UI);
  // eclipse mode holds the camera and the clock until it is left.
  if (eclipseModeActive()) return;
  if (!screensaverStarted) {
    screensaverStarted = true;
    // The screensaver shows the system at its true proportions.
    if (window.__setRealisticScaleMode) window.__setRealisticScaleMode(true);
    applyScreensaverPace(new URLSearchParams(window.location.search).get('pace'));
    useViewerIceGiantSurfaces();
    if (SCREENSAVER_BLACKHOLE_SHOW) enterScreensaverBlackHoleSystem();
    if (SCREENSAVER_ISS_SHOW) {
      const options = readScreensaverOptions(window.location.search);
      screensaverIssShow = createIssScreensaverShow({
        show: options.show,
        drift: options.drift,
        camera,
        controls,
        getIss: () => findLoadedSceneObjectByName('iss'),
        getEarth: () => planets.earth || null,
        getSatrec: () => issSatrec,
      });
    }
  }
  // Famous photos as live views (src/screensaver/photoshow.js).
  if (SCREENSAVER_PHOTO_SHOW) {
    if (!screensaverPhotoShow && !screensaverPhotoShowLoading) {
      screensaverPhotoShowLoading = true;
      Promise.all([famousPhotoModules(), import('./screensaver/photoshow.js')]).then(([lib, show]) => {
        if (!famousPhotoPoints) famousPhotoPoints = lib.createPhotoPoints(scene);
        screensaverPhotoShow = show.createPhotoScreensaverShow({
          photos: lib.FAMOUS_PHOTOS,
          place: placeFamousPhoto,
          setPlaying: (on) => applyViewClock(simulationJD, on ? 'real' : 0),
          isHeld: () => Boolean(famousPhotoHold),
          sunNdc: () => (sun ? famousPhotoNdc(sun) : null),
          targetNdc: () => (famousPhotoHold ? famousPhotoNdc(famousPhotoHold.target) || [0, 0] : [0, 0]),
        }, show.readPhotoShowOptions(window.location.search));
      }).catch((err) => console.error('[screensaver] no photos', err));
    }
    if (screensaverPhotoShow) screensaverPhotoShow.tick(elapsed);
    return;
  }
  if (SCREENSAVER_GALAXIES_SHOW) {
    if (!screensaverGalaxyShow && !screensaverGalaxyShowLoading && deepSkyObjects) {
      screensaverGalaxyShowLoading = true;
      import('./screensaver/galaxyshow.js').then((show) => {
        screensaverGalaxyShow = show.createGalaxyScreensaverShow({
          camera,
          sprite: (name) => deepSkyObjects.group.children.find((s) => s.name === name) || null,
          sun: () => sun,
        }, show.readGalaxyShowOptions(window.location.search));
      }).catch((err) => console.error('[screensaver] no galaxies', err));
    }
    if (screensaverGalaxyShow) screensaverGalaxyShow.tick(elapsed);
    return;
  }
  // The station's shows, until one gives up (the station never loaded): then
  // the Earth's.
  if (screensaverIssShow) {
    if (screensaverIssShow.tick(elapsed)) return;
    console.warn('[screensaver] no ISS, playing the Earth instead');
    screensaverIssShow = null;
  }
  if (!screensaverTour) {
    const options = readScreensaverOptions(window.location.search);
    if (SCREENSAVER_ISS_SHOW) Object.assign(options, { show: 'planet', body: 'earth' });
    screensaverTour = createScreensaverTour({
      camera,
      options,
      resolve: resolveScreensaverBody,
      // The galaxy's framings are written in its own axes (src/galaxy.js).
      frame: (id, scene) => (scene && scene.ground
        ? groundFrameOf(resolveScreensaverBody(id))
        : scene && scene.equator ? equatorFrameOf(resolveScreensaverBody(id))
          : ((id === 'milky-way' || id === 'blackhole') && galaxy && galaxy.framingQuaternion) || null),
      radiusOf: getBodyBaseRadius,
      sun: () => sun,
      detailDistance: screensaverDetailDistance,
      lookTarget: () => controls.target,
    });
  }
  // A capture script may hold the camera on a pose of its own instead of the
  // show (scripts/site/capture-footer-earth.mjs). Nothing sets it otherwise.
  if (typeof window.__ssPose === 'function') {
    window.__ssPose(camera, {
      resolve: resolveScreensaverBody, radiusOf: getBodyBaseRadius, sun, lineup: scaleLineupLayout,
      // Spacetime mode held by a video shot: where each body will be drawn.
      shift: spacetimeMode ? spacetimeMode.poseShiftOf : null,
    });
    return;
  }
  screensaverTour.tick(elapsed);
  screensaverTourTicked = true;
}

// Non-linear slider mapping so the low end of the range gets more slider real
// estate. Slider positions are 0-1000 integers; real values curve through them.
const SCALE_MIN = 0.5;
const SCALE_MAX = 500;
const SCALE_CURVE = 2.5;
let shiftHeld = false;
let lastScalePos = 0;

// Time panel presets (#time-presets). A number is a timeSpeed multiplier: the
// clock advances delta * timeSpeed * rate, where rate is ARTISTIC_TIME_RATE in
// artistic mode and 1 in realistic mode. 'real' is one simulated second per
// real second in either mode, so it follows a scale-mode switch.
const TIME_PRESETS = [0, 'real', 0.01, 0.1, 1, 10, 100, 1000];
let timePreset = 'real';
// While Real is on, the clock is the wall clock plus this offset (zero after
// pressing Real, so the sky is the sky right now). Reading the wall clock
// every frame instead of adding frame times keeps it exact through a hidden
// tab or a slow frame. lockedJD notices anything else moving the date.
let realClockOffsetJD = 0;
let realClockLockedJD = null;
let syncedTimeSpeed = NaN;
let syncedRealisticMode = null;
// The time machine's reverse (src/ui/timemachine.js): the presets keep their
// speeds and the clock runs them backwards. Real (the sky right now) ends it.
let timeReverse = false;
// Real is on but the clock was moved to another date: real pace, not now.
function clockAwayFromNow() {
  return timePreset === 'real' && Math.abs(realClockOffsetJD) > 1 / 1440;
}

function parseTimePreset(value) {
  return value === 'real' ? 'real' : Number(value);
}

function timePresetSpeed(preset) {
  if (preset !== 'real') return preset;
  return realisticScaleMode ? 1 : 1 / ARTISTIC_TIME_RATE;
}

// The pace a timeSpeed gives in the current scale mode, in the unit a person
// reads at a glance: "1 s = 42 min" in artistic mode at 0.1x.
// Words from the viewer's language (controls.time.pace in viewer.json),
// numbers in its digits.
function paceNumber(v, digits) {
  return vnum(v, { minimumFractionDigits: digits, maximumFractionDigits: digits, useGrouping: false });
}
function formatTimePace(speed) {
  const simPerReal = Math.abs(speed) * (realisticScaleMode ? 1 : ARTISTIC_TIME_RATE);
  const realTime = Math.abs(simPerReal - 1) < 1e-6;
  if (speed < 0) {
    return realTime ? vt('controls.time.pace.realBackwards') : vt('controls.time.pace.backwards', { pace: formatTimePace(-speed) });
  }
  if (simPerReal <= 0) return vt('controls.time.pace.paused');
  if (realTime) return vt('controls.time.pace.real');
  if (simPerReal < 1) return vt('controls.time.pace.slower', { value: paceNumber(simPerReal, simPerReal >= 0.1 ? 1 : 2) });
  const units = [[31557600, 'yr'], [86400, 'd'], [3600, 'h'], [60, 'min'], [1, 's']];
  for (const [secs, name] of units) {
    if (simPerReal >= secs) {
      const v = simPerReal / secs;
      return vt('controls.time.pace.faster', { value: v >= 10 ? paceNumber(Math.round(v), 0) : paceNumber(v, 1), unit: vt(`controls.time.pace.unit.${name}`) });
    }
  }
  return vt('controls.time.pace.real');
}

// Highlight the preset that matches timeSpeed and show its pace. Photo mode,
// the showreel and rocketry's warp set timeSpeed directly, so this reads the
// speed back instead of trusting the last click.
function syncTimeControls() {
  // Backwards, the preset of the same speed lights (in amber) but timePreset
  // stays null, so Real's wall-clock lock never takes a reversed clock.
  const pace = Math.abs(timeSpeed);
  const matches = (p) => Math.abs(timePresetSpeed(p) - pace) <= pace * 1e-6;
  let shown;
  if (timeSpeed < 0) {
    timePreset = null;
    const found = TIME_PRESETS.find((p) => p !== 0 && matches(p));
    shown = found === undefined ? null : found;
  } else {
    if (timePreset === null || !matches(timePreset)) {
      const found = TIME_PRESETS.find(matches);
      timePreset = found === undefined ? null : found;
    }
    shown = timePreset;
  }
  if (timePresetsEl) {
    timePresetsEl.classList.toggle('is-reverse', timeSpeed < 0);
    for (const btn of timePresetsEl.querySelectorAll('.time-preset')) {
      const preset = parseTimePreset(btn.dataset.speed);
      const on = preset === shown;
      btn.classList.toggle('active', on);
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
      btn.title = formatTimePace(timePresetSpeed(preset));
    }
  }
  if (speedValue) speedValue.textContent = clockAwayFromNow() ? vt('controls.time.pace.realPace') : formatTimePace(timeSpeed);
  syncedTimeSpeed = timeSpeed;
  syncedRealisticMode = realisticScaleMode;
}

function setTimePreset(preset) {
  // Real time is the live sky: it ends an eclipse being shown.
  if (preset === 'real' && eclipseModeActive()) { eclipseMode.exit(); return; }
  if (preset === 'real' && famousPhotoModeActive()) { famousPhotoMode.exit(); return; }
  timePreset = preset;
  timeSpeed = timePresetSpeed(preset);
  // A speed pressed while reversed runs backwards; Real is the sky now.
  if (preset === 'real') timeReverse = false;
  else if (timeReverse && timeSpeed > 0) { timePreset = null; timeSpeed = -timeSpeed; }
  if (preset === 'real') {
    simulationJD = dateToJD(new Date());
    realClockOffsetJD = 0;
    realClockLockedJD = simulationJD;
  }
  syncTimeControls();
}

function initTimePresets() {
  if (timePresetsEl) {
    timePresetsEl.addEventListener('click', (e) => {
      const btn = e.target.closest('.time-preset');
      if (!btn) return;
      setTimePreset(parseTimePreset(btn.dataset.speed));
      // A lesson or the remote sets the speed with a synthetic click.
      if (e.isTrusted) explore('time_travel', 'speed', { via: 'speed', speed: btn.dataset.speed });
    });
  }
  setInterval(() => {
    // Real time keeps meaning real time across a scale-mode switch, unless
    // something else changed the speed in the same tick.
    if (realisticScaleMode !== syncedRealisticMode && timePreset === 'real' && timeSpeed === syncedTimeSpeed) {
      timeSpeed = timePresetSpeed('real');
    }
    if (timeSpeed !== syncedTimeSpeed || realisticScaleMode !== syncedRealisticMode) syncTimeControls();
  }, 250);
  // Everyone starts on the sky as it is right now (the screensaver too).
  setTimePreset('real');
}

function sliderToScale(pos) {
  const t = Math.max(0, Math.min(1, pos / 1000));
  return SCALE_MIN + (SCALE_MAX - SCALE_MIN) * Math.pow(t, SCALE_CURVE);
}

function scaleToSlider(scale) {
  const t = Math.pow(Math.max(0, scale - SCALE_MIN) / (SCALE_MAX - SCALE_MIN), 1 / SCALE_CURVE);
  return Math.round(t * 1000);
}

function formatScale(v) {
  const digits = v >= 100 ? 0 : 1;
  return `${vnum(v, { minimumFractionDigits: digits, maximumFractionDigits: digits, useGrouping: false })}x`;
}

let currentNavIndex = 0;

/**
 * Resolve the prev/next nav cycle for the currently-active system.
 * For Sol it is the Objects browser's whole list in its order: the Sun,
 * the planets, then moons, Earth orbit, the belt, deep space, beyond and
 * the deep sky. Each entry resolves when it is reached (resolveNavEntry),
 * since the deep-space bodies are built on first use. For remote
 * systems, walks the system handle's bodies map and emits
 * stars-then-planets in catalog order. Centralises the "what does
 * the bottom-of-screen arrow cycle through?" question so the
 * navigatePlanet handler doesn't have to branch on system id.
 */
function getActiveNavBodies() {
  if (activeSystemId === 'sol' || !remoteSystems[activeSystemId]) {
    const entries = [];
    buildObjectBrowserCatalog().forEach((section) => {
      section.items.forEach((item) => {
        // A system jump flies through the wormhole into another system,
        // a journey rather than the next body.
        if (item.systemJump) return;
        entries.push({ id: item.label.toLowerCase(), displayName: item.label, item });
      });
    });
    return entries;
  }
  const handle = remoteSystems[activeSystemId];
  const data = handle.data;
  const out = [];
  for (const star of data.stars) {
    const obj = handle.bodies[star.id];
    if (obj) out.push({ id: star.id, object: obj, displayName: star.name });
  }
  for (const planet of data.planets) {
    const obj = handle.bodies[planet.id];
    if (obj) out.push({ id: planet.id, object: obj, displayName: planet.name });
  }
  return out;
}
const STARTUP_FOCUS_IDS = ['earth', 'saturn', 'jupiter'];
const STARTUP_FRAMING = {
  // Hero framing tuned via the localhost cinematic tuner. Per-body
  // overrides for sideFactor, upFactor and sunBias unlock arbitrary
  // composition: negative side/up flip the camera to the opposite
  // hemisphere, sunBias 0 looks dead at the planet (vs 0.12 default
  // which lerps the lookAt toward the sun).
  earth: {
    // Keep the hero shot relative to Earth's current ephemeris position.
    // Captured world coordinates go stale as the date changes, leaving the
    // Earth panel selected while the camera points into empty space.
    fov: 45,
    bodyDistance: 4.55,
    sideFactor: -0.22,
    upFactor: -0.02,
    sunBias: 0.0,
    targetSide: 0.0036,
    targetUp: 0.0,
    postprocess: {
      exposure: 0.96,
      contrast: 1.06,
      saturation: 1.05,
      vignette: 0.31,
      grain: 0.13
    }
  },
  jupiter: {
    // 1.2 put the camera ~0.6 radii above the cloud tops and the
    // planet overflowed the frame on load. 3.4 frames the full disc
    // at roughly Earth/Saturn hero-shot proportions (~65% of frame
    // height at fov 40 with the side/up offsets below).
    bodyDistance: 3.4,
    sideFactor: -0.46,
    upFactor: -0.70,
    sunBias: 0.0,
    targetSide: 0.0007,
    targetUp: 0.0,
    fov: 40,
    orbitAngle: 4.66387427,
    postprocess: {
      exposure: 1.00,
      contrast: 1.05,
      saturation: 1.05,
      vignette: 0.30,
      grain: 0.13
    }
  },
  saturn: {
    bodyDistance: 3.8,
    sideFactor: -0.74,
    upFactor: -0.48,
    sunBias: 0.0,
    targetSide: 0.0004,
    targetUp: 0.0,
    fov: 39,
    orbitAngle: 2.90205476,
    // Per-body post-process overrides applied via frameStartupFocus
    // when the hero body is picked. Lets each body have its own
    // tuned look (exposure / contrast / etc.) without dragging the
    // others to match.
    postprocess: {
      exposure: 0.94,
      contrast: 1.06,
      saturation: 1.05,
      vignette: 0.31,
      grain: 0.13
    }
  }
};

function pickStartupFocusId() {
  const roll = Math.random();
  return roll < 0.60 ? 'earth' : (roll < 0.85 ? 'saturn' : 'jupiter');
}

function isStartupFocusId(id) {
  return STARTUP_FOCUS_IDS.includes(id);
}

function isSpecialHashFocusId(id) {
  return id === 'wow' || id === 'cool-worlds' || id === 'coolworlds' || id === 'launch' || id === 'to-scale';
}

function setFocusHash(id) {
  if (!id || typeof window === 'undefined' || !window.history || !window.history.replaceState) return;
  try {
    const url = new URL(window.location.href);
    url.hash = '#' + id;
    window.history.replaceState({}, '', url.toString());
  } catch (_) { /* ignore malformed URL */ }
}

function clearFocusHash() {
  if (typeof window === 'undefined' || !window.history || !window.history.replaceState) return;
  try {
    const url = new URL(window.location.href);
    url.hash = '';
    window.history.replaceState({}, '', url.pathname + url.search);
  } catch (_) { /* ignore malformed URL */ }
}

function configureControlsForFocusedBody(object, distance) {
  const data = object && object.userData ? object.userData : {};
  const radius = data.radius || 1;
  const effectiveRadius = realisticScaleMode
    ? (getRealisticBodyRadius((data.name || '').toLowerCase()) || radius)
    : radius;

  // A small body with a shape model (src/smallbodies/) is a fraction of the
  // artistic near plane across, which held the camera ten radii out; its
  // near plane follows its size so its surface can be seen up close (at the
  // zoom floor its highest ground is 3% of its reach away).
  const artisticNear = Math.max(0.1, scaleMultiplier * 0.1);
  setCameraNearPlane(realisticScaleMode
    ? Math.max(0.0001, effectiveRadius * 0.005)
    : data.shapeReach ? Math.min(artisticNear, effectiveRadius * data.shapeReach * 0.01) : artisticNear);
  controls.minDistance = data.isSun
    ? getSunZoomFloor(effectiveRadius)
    : Math.max(effectiveRadius * (realisticScaleMode ? 1.005 : 1.03) * bodyShapeReach(data), camera.near * 1.2);
  controls.maxDistance = Math.max(effectiveRadius * 800, distance * 80, 1200);
  controls.minPolarAngle = Math.PI * 0.15;
  controls.maxPolarAngle = Math.PI * 0.85;
  controls.dampingFactor = 0.08;
  controls.panSpeed = 0.8;
  controls.zoomSpeed = 1.2;
  controls.rotateSpeed = 0.8;
}

function getStartupFocusDistance(id, data) {
  const framing = STARTUP_FRAMING[id] || {};
  // Tuner emits bodyDistance (in body-radii units) regardless of
  // whether the body has rings; prefer that when present so the
  // captured value applies cleanly. Fall back to ringDistance for
  // legacy ringed-body configs, then to a body-radii default.
  const radius = data.radius || 2.5;
  if (framing.bodyDistance != null) {
    return radius * framing.bodyDistance;
  }
  if (data.hasRings && data.ringOuterRadius) {
    return data.ringOuterRadius * (framing.ringDistance || 3.0);
  }
  return radius * (framing.bodyDistance || 6.5);
}

function getStartupFocusOffset(id, object, distance) {
  // Backlit hero composition for the startup landing shot. Camera
  // sits on the FAR side of the planet from the sun, so looking back
  // at the planet shows its dark hemisphere as the subject and the
  // sun appears past the planet's limb as a small bright source.
  // Composition uses planetDir (sun-to-planet) for the main outward
  // push plus small perpendicular offsets so the planet doesn't
  // fully eclipse the sun.
  const heroPos = new THREE.Vector3();
  object.getWorldPosition(heroPos);
  let planetDir = heroPos.clone();
  if (planetDir.lengthSq() < 1e-4) {
    // Sun is at origin; if planet happens to be at origin too,
    // fall back to an arbitrary direction so we don't NaN.
    planetDir.set(1, 0, 0);
  }
  planetDir.normalize();
  const worldUp = new THREE.Vector3(0, 1, 0);
  let sideTan = new THREE.Vector3().crossVectors(worldUp, planetDir);
  if (sideTan.lengthSq() < 1e-4) sideTan.set(1, 0, 0);
  sideTan.normalize();
  const upTan = new THREE.Vector3().crossVectors(planetDir, sideTan).normalize();
  // Per-body offset factors. The cinematic tuner can override these
  // via STARTUP_FRAMING entries for hand-tuned compositions; falls
  // back to general defaults (wider tilt for ringed bodies).
  const data = object.userData || {};
  const framing = STARTUP_FRAMING[id] || {};
  const upFactor = framing.upFactor != null
    ? framing.upFactor
    : (data.hasRings ? 0.50 : 0.18);
  const sideFactor = framing.sideFactor != null
    ? framing.sideFactor
    : (data.hasRings ? 0.40 : 0.30);
  return planetDir.clone().multiplyScalar(distance * 1.0)
    .addScaledVector(sideTan, distance * sideFactor)
    .addScaledVector(upTan, distance * upFactor);
}

function frameStartupFocus(id, options = {}) {
  const object = id === 'sun' ? sun : planets[id];
  if (!object || !camera || !controls) return false;

  if (activeCameraTween) {
    try { activeCameraTween.kill(); } catch (_) {}
    activeCameraTween = null;
  }
  isAnimating = false;
  _focusPrevObj = null;

  const data = object.userData || celestialData[id] || {};
  const framingConfig = STARTUP_FRAMING[id] || {};

  // If the framing config captured a specific orbital angle (via the
  // cinematic tuner's COPY), snap the body to that angle so the
  // camera-relative composition lands the planet in the SAME
  // absolute world position the dev framed at capture time. Without
  // this snap, the body has drifted to a different orbit position
  // by reload time, so even though the body-relative camera offset
  // is correct, the absolute view (background stars, sun direction
  // in world space) differs from what was captured.
  if (framingConfig.orbitAngle != null && object.userData) {
    object.userData.orbitAngle = framingConfig.orbitAngle;
    updateScale();
    // Force a fresh matrix cascade so getWorldPosition reads the
    // newly-positioned planet, not a stale cached matrixWorld.
    scene.updateMatrixWorld(true);
  }
  // Snap any moons of this body to their captured orbital angles.
  // Moons orbit much faster than their parent planets, so even a few
  // seconds of drift puts them in completely different positions.
  // Without this, a hero shot composed with the moon framed in the
  // scene would lose the moon on every reload.
  if (framingConfig.moonAngles && moons[id]) {
    Object.entries(framingConfig.moonAngles).forEach(([moonName, angle]) => {
      const moonGroup = moons[id].find((mg) => mg.userData && mg.userData.name === moonName);
      if (moonGroup && moonGroup.userData) {
        moonGroup.userData.orbitAngle = angle;
      }
    });
    // Re-run the moon position update so the snap takes effect
    // immediately rather than waiting for the next animate tick.
    updateMoonSystemsScale();
    scene.updateMatrixWorld(true);
  }

  // Planets start at random angles (createPlanets) until the first frame puts
  // them on the ephemeris and the follow carries the camera across, which left
  // the Sun in a random direction for the backlit shot. Put the body where the
  // clock has it first, so the composition is the one tuned.
  const ephemerisDistance = object.userData.scaledDistance || (celestialData[id] && celestialData[id].distance);
  if (object !== sun && placePlanetOnEphemeris(id, object, ephemerisDistance)) {
    scene.updateMatrixWorld(true);
  }

  // The framing is tuned in body radii of the authored (artistic) size, so at
  // true scale it follows the drawn body instead (getBodyBaseRadius).
  const radiusScale = data.radius ? getBodyBaseRadius(object) / data.radius : 1;
  const distance = getStartupFocusDistance(id, data) * radiusScale;
  const offset = getStartupFocusOffset(id, object, distance);
  const objectPosition = new THREE.Vector3();
  object.getWorldPosition(objectPosition);

  // EXACT-VIEW path. If the framing config has captured raw world-
  // space cameraPos and lookAt arrays, use those directly for pixel-
  // perfect reproduction. Combined with the orbital-angle snaps
  // above (planet + moons), the scene is in the exact configuration
  // the dev had at copy time. The body-relative basis path is kept
  // as a fallback for configs that pre-date this format.
  const useExactView = Array.isArray(framingConfig.cameraPos)
                    && Array.isArray(framingConfig.lookAt);
  if (useExactView) {
    camera.position.set(
      framingConfig.cameraPos[0],
      framingConfig.cameraPos[1],
      framingConfig.cameraPos[2]
    );
  } else {
    camera.position.copy(objectPosition).add(offset);
  }

  if (isLocalEnvironment && isLocalEnvironment()) {
    const camPos = useExactView
      ? new THREE.Vector3(framingConfig.cameraPos[0], framingConfig.cameraPos[1], framingConfig.cameraPos[2])
      : objectPosition.clone().add(offset);
    console.log('[frameStartupFocus]', id, {
      orbitAngle: framingConfig.orbitAngle,
      heroPos: [objectPosition.x.toFixed(3), objectPosition.y.toFixed(3), objectPosition.z.toFixed(3)],
      cameraPos: [camPos.x.toFixed(3), camPos.y.toFixed(3), camPos.z.toFixed(3)],
      exactView: useExactView
    });
  }
  // Compose the look-at target: start with the sun-biased lerp
  // (planetDir-axis bias), then add perpendicular target offsets
  // (targetSide and targetUp) so any panning the dev did when
  // tuning is reproduced exactly. Without targetSide/targetUp, a
  // captured composition with off-axis target lookup would land
  // back centered on the planet on reload.
  const framingForLookAt = STARTUP_FRAMING[id] || {};
  let lookAtPos;
  if (Array.isArray(framingForLookAt.lookAt)) {
    // EXACT-VIEW path: use the captured world-space lookAt directly.
    lookAtPos = new THREE.Vector3(
      framingForLookAt.lookAt[0],
      framingForLookAt.lookAt[1],
      framingForLookAt.lookAt[2]
    );
  } else {
    const sunBias = id === 'sun'
      ? 0
      : (framingForLookAt.sunBias != null ? framingForLookAt.sunBias : 0.12);
    lookAtPos = objectPosition.clone().lerp(new THREE.Vector3(0, 0, 0), sunBias);
  }
  if (!Array.isArray(framingForLookAt.lookAt)
      && (framingForLookAt.targetSide != null || framingForLookAt.targetUp != null)) {
    // Recompute the planet basis to add perpendicular target offset.
    let planetDir = objectPosition.clone();
    if (planetDir.lengthSq() < 1e-4) planetDir.set(1, 0, 0);
    planetDir.normalize();
    const worldUp = new THREE.Vector3(0, 1, 0);
    let sideTan = new THREE.Vector3().crossVectors(worldUp, planetDir);
    if (sideTan.lengthSq() < 1e-4) sideTan.set(1, 0, 0);
    sideTan.normalize();
    const upTan = new THREE.Vector3().crossVectors(planetDir, sideTan).normalize();
    if (framingForLookAt.targetSide) lookAtPos.addScaledVector(sideTan, framingForLookAt.targetSide * radiusScale);
    if (framingForLookAt.targetUp) lookAtPos.addScaledVector(upTan, framingForLookAt.targetUp * radiusScale);
  }
  camera.lookAt(lookAtPos);
  controls.target.copy(lookAtPos);
  if (framingForLookAt.fov != null) {
    camera.fov = framingForLookAt.fov;
    camera.updateProjectionMatrix();
  }
  // Per-body post-process overrides. If the framing config carries
  // a postprocess block, apply it so each hero body can keep its
  // own tuned exposure/contrast/etc. without polluting the others.
  if (framingForLookAt.postprocess && cinematicPass && cinematicPass.uniforms) {
    const pp = framingForLookAt.postprocess;
    if (pp.exposure != null) cinematicPass.uniforms.exposure.value = pp.exposure;
    if (pp.contrast != null) cinematicPass.uniforms.contrast.value = pp.contrast;
    if (pp.saturation != null) cinematicPass.uniforms.saturation.value = pp.saturation;
    if (pp.vignette != null) cinematicPass.uniforms.vignetteIntensity.value = pp.vignette;
    if (pp.grain != null) cinematicPass.uniforms.grainStrength.value = pp.grain;
  }
  configureControlsForFocusedBody(object, distance);
  controls.update();

  selectedObject = object;
  // The first animation frame may update the ephemeris again. Seed the
  // rigid follow from the position this framing actually used.
  _focusPrevObj = object;
  _focusPrevObjPos.copy(objectPosition);
  syncNavToBody(data.name, id);
  if (navLabel) navLabel.textContent = data.name || id.charAt(0).toUpperCase() + id.slice(1);

  if (options.persistHash) setFocusHash(id);
  return true;
}

function applyDefaultScale() {
  // The artistic scale the scene is built at, with a slight boost for
  // breathing room. The viewer then opens at true scale (init), and the
  // Artistic switch comes back to the slider's 3x.
  scaleMultiplier = 4.5;
  if (scaleSlider) {
    const sliderPos = scaleToSlider(scaleMultiplier);
    scaleSlider.value = sliderPos;
    lastScalePos = sliderPos;
  }
  if (scaleValue) scaleValue.textContent = formatScale(scaleMultiplier);
  updateScale();
}

// Initialize the application
async function init() {
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.dataset.mainBoot = 'init';
  }
  applyLocalEnvironmentChrome();
  clock = new THREE.Clock();
  raycaster = new THREE.Raycaster();
  mouse = new THREE.Vector2();
  hoverLabel = document.getElementById('hover-label');
  fpsLabel = document.getElementById('fps-overlay');

  matryoshka.init();
  // Live stats in the info panels read the viewer's own clock.
  matryoshka.setClock(() => simulationJD);

  // Panel call-to-action buttons (matryoshka 'action' payload blocks).
  // The Wormhole panel's "Enter wormhole" button dispatches the flight
  // here instead of the wormhole transporting automatically on focus.
  window.addEventListener('matryoshka-action', (e) => {
    const action = e.detail && e.detail.action;
    if (action === 'enter-wormhole') {
      const dest = activeSystemId === 'gargantua' ? 'sol' : 'gargantua';
      triggerWormholeFlight(dest);
    }
  });

  setupScene();
  setupCamera();
  setupRenderer();
  startReloadWatch(reloadWatchState);
  setupControls();
  holdControlsOffSun();
  zoomOnlySunPinch();
  steadyTwoFingerUpdates();
  setupPostProcessing();
  setupEventListeners();
  syncAlienHudToggle();

  // The music's module, loaded behind the loading screen (MUSIC_AT_LOAD):
  // it makes the audio context, 0.2 s of main thread, which the first
  // click on the scene otherwise waited for.
  if (MUSIC_AT_LOAD) loadSpaceAmbientModule().catch((err) => console.warn('[audio] failed to load ambient module', err));
  await loadTextures();
  createStarfield();
  galaxy = createGalaxy(scene, renderer);
  // TNG-style 3D warp field. Streams instanced bright spheres past
  // the camera with Z-axis scale-stretch proportional to warp factor.
  // The mesh follows the camera in update() each frame.
  warpEffect = createWarpEffect();
  scene.add(warpEffect.mesh);
  hyperspace = createHyperspace({ quality: activeGraphicsPreset === 'low' ? 'low' : 'high' });
  scene.add(hyperspace.group);

  // Wormhole flythrough — bidirectional Sol ↔ Gargantua transition.
  // The tunnel mesh stays detached until play() is invoked, then
  // wraps the camera with a scrolling tube and ferries it to the
  // destination system at the midpoint of the flight.
  wormholeTunnel = createWormholeTunnel({
    camera,
    scene,
    onMidpoint: () => doWormholeMidpointSwap(),
    onComplete: () => {
      const completedFlight = activeWormholeFlight;
      if (completedFlight && completedFlight.destinationId && activeSystemId !== completedFlight.destinationId) {
        // Defensive latch: if a midpoint callback failed or was
        // interrupted, the user's selected destination still wins.
        showSystem(completedFlight.destinationId, { teleport: true, animate: false });
      }
      wormholeTunnelInFlight = false;
      if (completedFlight && completedFlight.fromStarMap) {
        starMapJumpInFlight = false;
      }
      activeWormholeFlight = null;
      // Brief grace period so the player can pull away from the
      // destination mouth before re-triggering the reverse flight.
      lastWormholeTriggerMs = performance.now();
    }
  });
  // Park an invisible vista anchor at the galactic centre. Autopilot
  // approach math is targetPos + approachOffset where approachOffset
  // scales with userData.radius (approach = max(radius * 6, 14)), so a
  // 12M-unit "radius" puts the arrival camera ~72M outside the disc.
  // Disc radius is 25M, sun-to-galaxy-centre is 12.5M; arrival ends up
  // beyond the disc on the away-from-sun side, looking back at the
  // spiral structure. The orbit phase then sweeps the camera around at
  // that radius for a cinematic 100s sweep.
  if (galaxy && galaxy.object) {
    milkyWayVista = new THREE.Object3D();
    milkyWayVista.name = 'milky-way';
    milkyWayVista.position.copy(galaxy.object.position);
    milkyWayVista.userData = {
      name: 'Milky Way',
      radius: 12_000_000,
      isMilkyWayVista: true
    };
    scene.add(milkyWayVista);
  }
  // Stellar-mass black hole (~100 M☉, r_s ≈ 295 km). Sized down from
  // a supermassive scale so it reads as a small dark object in deep
  // space rather than something rivaling the Milky Way bulge.
  blackHole = createBlackHole({
    visualRs: 4_000,
    renderer,
    // The sky as drawn, for the lensing, once the camera is near enough
    // for the hole to matter (lensSkyCube).
    getSkyboxTexture: () => lensSkyCube(!!blackHole && camera.position.distanceTo(blackHole.object.position) < 25_000_000),
    // The Milky Way, lensed when it stands behind the hole.
    getBackdrop: () => (galaxy && galaxy.object) || null,
  });
  // Place at a position well outside the galaxy disc so it presents
  // against a clean dark sky. Far enough that it doesn't appear in
  // typical solar-system views, close enough to reach via cinematic
  // transition.
  // Placed where it stood against the galaxy before the galaxy turned to its
  // real orientation (src/galaxy.js fromOldFrame), so it keeps a clean dark
  // sky round it and its shots keep the galaxy behind it.
  blackHole.object.position.set(48_000_000, 8_000_000, -28_000_000);
  if (galaxy && galaxy.fromOldFrame) galaxy.fromOldFrame(blackHole.object.position, blackHole.object.position);
  // Mark as a navigable autopilot destination. radius drives the
  // arrival distance via fastTravelApproachDistance (= max(r*6, 14)).
  // visualRs is 4000 units, so the BH disc is only meaningful at
  // angular sizes the user can read; 12k radius → 72k arrival means
  // the disc subtends ~3.2° and the photon ring at b_crit=2.598 r_s
  // subtends ~8.2°. From farther out the silhouette becomes a tiny
  // dot and the gravitational lensing of the surrounding starfield
  // becomes the dominant visual instead. Tuned so the autopilot
  // arrival sits in the "Interstellar shot" zone where the lensed
  // ring genuinely warps the background.
  blackHole.object.userData = {
    ...(blackHole.object.userData || {}),
    name: 'Black Hole',
    radius: 12_000,
    isBlackHole: true
  };
  // Slight tilt so the disc reads as oblique from the cinematic
  // approach angle, not edge-on or face-on.
  blackHole.object.rotation.set(
    THREE.MathUtils.degToRad(58),
    THREE.MathUtils.degToRad(12),
    THREE.MathUtils.degToRad(22)
  );
  scene.add(blackHole.object);
  createSun();
  createPlanets();
  createAsteroidBelt();
  createAsteroidBeltBodies();
  // Scattered drifting debris (meteoroids, asteroids, comet). Skipped
  // in the game shell, which runs its own lightweight scene. A phone builds
  // it once someone goes out to it (updateMobileDebris).
  if (!IS_GAME && !RUNNING_ON_MOBILE) {
    debrisField = createDebrisField(scene, { renderer, camera });
  }
  createOrbitLines();
  updateScale();
  createMonolith(); // 2001 Space Odyssey easter egg
  createSatellites(); // ISS in low Earth orbit
  // Real satellites at their true positions for simulationJD, in Earth's
  // frame. Off until the sidebar toggle (or ?satellites=1) turns them on.
  if (!IS_SCREENSAVER) {
    realSatellites = createRealSatellites({
      getEarth: () => planets.earth,
      getSun: () => sun,
      getSimJD: () => simulationJD,
      camera,
      domElement: renderer.domElement,
      isSuppressed: () => rocketryActive || activeSystemId !== 'sol',
    });
  }
  createPlayerShip(); // 3D UFO for fly mode
  // The tuner is dev-only and must never break the boot flow. Failing
  // silently is the right behaviour because this code path runs on
  // every visitor's machine; a bug here would otherwise stall init.
  try {
    setupTunerDock();
    setupCraftAdminTuner();
    setupCometAdminTuner();
    setupPlanetAdminTuner();
    setupSunTuner();
    setupGalaxyTuner();
    setupCinematicDirector();
  } catch (err) {
    console.warn('[craft-admin] setup failed:', err);
  }
  createSecretUfos(); // 7 hovering UFOs scattered over mysterious Earth locations
  applyDefaultScale();

  // Build any non-Sol systems registered in the catalog and park them
  // at their scenePosition. Sol uses the legacy createSun / createPlanets
  // path above; the multi-system registry is the future home of all
  // bodies once the data-driven refactor lands.
  initRemoteSystems();

  // Secret procedural planet. Lives in the Sol scene at a fixed point
  // beyond Pluto, off the ecliptic. No entry in the planet picker,
  // autopilot menu, or info HUD — reachable via the URL hash
  // `#nibiru`, the dev shortcut `window.__nibiru.goto()`, or by flying
  // there manually if you know where to look. Doubles as a working
  // proof of the procedural-gas-giant pipeline that could replace the
  // textured Jupiter path later.
  try {
    nibiruHandle = createNibiru();
    scene.add(nibiruHandle.group);
  } catch (err) {
    console.warn('[nibiru] build failed:', err);
    nibiruHandle = null;
  }

  // The viewer opens at true scale, with Artistic one click away on the
  // toggle. A view link or lesson scene that asks for the artistic scale
  // still gets it (applyViewScale); the screensaver switches itself when its
  // show starts and the game shell boots into the hangar.
  if (!IS_GAME && !IS_SCREENSAVER && window.__setRealisticScaleMode) {
    window.__setRealisticScaleMode(true, { instant: true });
  }

  const initialHash = getHashFocusId();
  // matryoshka.applyHash drives the nested-scale comparison view; it
  // expects a real Sol body in the hash, and Nibiru aliases aren't.
  // Skip it for any Nibiru hash so matryoshka doesn't fail-lookup.
  // Aliases: 'nibiru' (canonical Sitchin spelling) + 'niburu' (the
  // user's typo, kept as an alias for sharable URLs).
  const NIBIRU_HASH_ALIASES = new Set(['nibiru', 'niburu']);
  // GAME MODE: matryoshka is website chrome; the hangar owns startup.
  // #qmax opens the viewer straight into rocketry (Qmax); the sales page's
  // Launch button links here.
  const openQmax = initialHash === 'qmax';
  if (!IS_GAME && !openQmax && initialHash && initialHash !== 'sun' && initialHash !== 'launch' && !NIBIRU_HASH_ALIASES.has(initialHash)) {
    matryoshka.applyHash();
  }
  if (openQmax && !IS_SCREENSAVER) setTimeout(() => enterRocketryMode(), 400);

  // Short-circuit: any Nibiru hash variant routes directly to the
  // procedural easter egg body, bypassing the regular focus pipeline
  // (it isn't a registered Sol body, so syncFocusToHash would either
  // fail silently or land the camera on whatever the fallback is).
  // The actual teleport happens after Nibiru is fully built; here we
  // just skip the Sol focus logic so the camera doesn't flash through
  // a startup body before snapping to Nibiru.
  const goingToNibiru = NIBIRU_HASH_ALIASES.has(initialHash);

  // GAME MODE: skip the startup hero focus entirely — the player boots into
  // the hangar (src/game/hangar.js) and only sees this scene after LAUNCH.
  if (!IS_GAME && !goingToNibiru) {
    if (isSpecialHashFocusId(initialHash)) {
      syncFocusToHash({ interrupt: true, preservePanel: true, force: true });
    } else if (!IS_SCREENSAVER) {
      if (isStartupFocusId(initialHash)) {
        frameStartupFocus(initialHash);
      } else if (initialHash === 'climate') {
        // A #climate link opens on the Earth; src/climate/ opens the data
        // once the camera has landed.
        frameStartupFocus('earth');
      } else if (!initialHash || initialHash === 'sun') {
        if (initialHash === 'sun') clearFocusHash();
        frameStartupFocus(resolveBootFocusId(), { persistHash: true });
      } else {
        syncFocusToHash({ interrupt: true, preservePanel: true, force: true });
      }
    } else if (initialHash && initialHash !== 'sun') {
      syncFocusToHash({ interrupt: true, preservePanel: true, force: true });
    }
  }

  // An explicit Sol-system body in the URL hash (e.g. #earth) is a
  // direct request that has to win over the persisted-system resume.
  // Without this, opening #earth after a prior Gargantua visit reloads
  // straight back into Gargantua because resumeActiveSystemPostFocus()
  // teleports to the localStorage-persisted system, stomping the hash.
  // Reset the persisted system to Sol so the resume below is a no-op
  // and the rest of the app stays consistent with what's on screen.
  const hashRequestsSolBody = !!initialHash
    && !goingToNibiru
    && (initialHash === 'sun' || initialHash === 'view' || !!planets[initialHash]);
  if (hashRequestsSolBody && activeSystemId !== 'sol') {
    activeSystemId = 'sol';
    setCurrentSystem('sol');
    markClickablesDirty();
  }

  // If localStorage said the player was in a non-Sol system last
  // session, teleport them back now. Has to come AFTER the Sol focus
  // logic above so the camera doesn't get snapped back to a Sol body
  // immediately after we move it.
  // GAME MODE: skip the resume — every game session starts in the hangar at
  // Sol, not wherever the last website visit was persisted.
  if (!IS_GAME) resumeActiveSystemPostFocus();

  // Hash-triggered teleport to the secret procedural body. Happens
  // here so the Sol focus logic above has already been skipped (see
  // goingToNibiru) and the body has been built into the scene a few
  // lines further up.
  if (goingToNibiru) {
    gotoNibiru();
  }

  if (fpsLabel) {
    fpsLabel.classList.toggle('visible', !!graphicsConfig.showFps);
  }

  // The first frame's shaders, all compiled at once behind the loading
  // screen (src/gpuwarm.js): left to the first frame, the driver compiled
  // them one by one with the main thread held, and taps waited. Where the
  // driver is slow (Edge on Windows on a first visit) this is the long part
  // of the load, so the loading screen says so, and its watchdog waits for
  // the compile's own limit.
  prepareEarthWeather(renderer);
  prepareSunOccluders();
  clearTimeout(loadingScreenFailsafe);
  loadingScreenFailsafe = setTimeout(() => {
    if (!loadingScreenDismissed) hideLoadingScreen();
  }, BOOT_COMPILE_LIMIT_MS + 5000);
  await compileShownNow({
    renderer,
    scene,
    camera,
    composer,
    limitMs: BOOT_COMPILE_LIMIT_MS,
    onSlow: () => { if (loadingText && !loadingScreenDismissed) loadingText.textContent = 'Preparing the graphics for this computer, only on a first visit'; },
  });

  hideLoadingScreen();
  // Everything the loading screen did not wait for. Deliberately not
  // awaited: the user is already looking at the hero body while the
  // rest of the system fills in behind them.
  streamDeferredTextures();
  // setAnimationLoop is the WebXR-compatible replacement for the
  // requestAnimationFrame-driven loop. When an XR session is active,
  // the XR runtime drives the loop at the headset's refresh rate
  // (typically 90/120 Hz) and synchronises to the device's display.
  // When no XR session is active, this falls back to rAF behaviour.
  renderer.setAnimationLoop(animate);

  // The live wallpaper app (wallpaper/) drives these: a lower frame cap on
  // battery, and a pause while the screen is locked, asleep or covered by a
  // full-screen app. Resuming drops the paused time from the clock, so the
  // show carries on where it was instead of jumping ahead.
  if (IS_SCREENSAVER) {
    let paused = false;
    window.__wallpaper = {
      setFps: (fps) => { frameCapMs = Number(fps) > 0 ? 1000 / Number(fps) : 0; },
      setPace: (pace) => applyScreensaverPace(pace),
      pause: () => { if (!paused) { paused = true; renderer.setAnimationLoop(null); } },
      resume: () => {
        if (!paused) return;
        paused = false;
        clock.oldTime = performance.now();
        renderer.setAnimationLoop(animate);
      },
      state: () => ({ paused, fps: frameCapMs ? Math.round(1000 / frameCapMs) : 0, pixelRatio: renderer.getPixelRatio(), preset: activeGraphicsPreset }),
    };
  }

  // Localhost-only cinematic tuner. Lets the dev manually drag/zoom
  // the camera and see live slider readouts for every parameter the
  // default landing-shot uses, then copy a paste-ready config block.
  if (isLocalEnvironment() && !IS_GAME) {
    setupCinematicTuner();
  }

  // GAME MODE: hand control to the game shell (src/game/index.js). The
  // bridge is the only sanctioned surface between game code and this
  // module — keep it tiny. pause/resume let the hangar (which runs its own
  // renderer + rAF loop on a layer above) stop the hidden solar system from
  // burning GPU while it is on screen.
  if (IS_GAME) {
    window.__gameBridge = {
      setActiveCraft,
      enterFlyMode,
      exitFlyMode,
      pauseMainLoop: () => renderer.setAnimationLoop(null),
      resumeMainLoop: () => renderer.setAnimationLoop(animate)
    };
    window.dispatchEvent(new CustomEvent('game:bridge-ready'));
  }
}

// === CINEMATIC TUNER (localhost-only) ===
// Two-way binding between the camera/post-process state and a panel
// of sliders. Manual camera moves via OrbitControls update the slider
// values in real time so the dev can see what's happening; slider
// changes update the camera and post-process uniforms so the dev can
// dial in the exact look they want. COPY button outputs a JSON-like
// block with all current values for me to bake as the new default.
// Look tuners in the tuner dock (localhost only): a slider for each setting,
// applied live, kept in localStorage across reloads, and copied out as the
// settings that differ from the code's, to bake back in. Double-click a
// slider to put it back. A spec is [[group title, rows, colour?]], a row
// [key, label, min, max, step, help, rebuild], the key a dotted path into
// the tuner's root object. A row marked rebuild applies when the slider is
// let go, through onRebuild; a group with a colour shows it as a swatch.
function setupLookTuner({ paneId, storageKey, root, spec, source, onRebuild, views }) {
  if (!isLocalhostHost()) return;
  const defaults = JSON.parse(JSON.stringify(root));
  const walk = (key, from) => { const path = key.split('.'); const last = path.pop(); return [path.reduce((o, k) => o[k], from), last]; };
  const read = (key, from = root) => { const [o, k] = walk(key, from); return o[k]; };
  const write = (key, v) => { const [o, k] = walk(key, root); o[k] = v; };
  const rows = spec.flatMap(([, list]) => list);
  const keys = rows.map((row) => row[0]);
  const rebuilds = new Set(rows.filter((row) => row[6]).map((row) => row[0]));
  const changed = () => {
    const out = {};
    for (const key of keys) if (read(key) !== read(key, defaults)) out[key] = read(key);
    return out;
  };
  const save = () => { try { localStorage.setItem(storageKey, JSON.stringify(changed())); } catch (e) { /* storage off */ } };
  // Settings kept from an earlier visit apply whether or not the tab opens.
  let keptRebuild = false;
  try {
    const kept = JSON.parse(localStorage.getItem(storageKey) || '{}') || {};
    for (const key of keys) {
      if (typeof kept[key] !== 'number' || !Number.isFinite(kept[key])) continue;
      write(key, kept[key]);
      if (rebuilds.has(key)) keptRebuild = true;
    }
  } catch (e) { /* storage off or unreadable */ }

  const pane = document.getElementById(paneId);
  const make = (tag, cls, text) => {
    const el = document.createElement(tag);
    if (cls) el.className = cls;
    if (text != null) el.textContent = text;
    return el;
  };
  let status = null;
  let pending = 0;
  const rebuild = () => {
    if (!onRebuild || pending) return;
    pending = requestAnimationFrame(() => {
      pending = 0;
      const ms = onRebuild();
      if (status && Number.isFinite(ms)) status.textContent = `Rebuilt in ${Math.round(ms)} ms.`;
    });
  };
  if (keptRebuild) rebuild();
  if (!pane) return;

  const fields = [];
  const swatches = [];
  let output = null;
  const report = () => {
    if (!output) return;
    const c = changed();
    output.value = Object.keys(c).length
      ? `${source} changes:\n${JSON.stringify(c, null, 2)}`
      : 'No changes from the settings in the code.';
  };
  const paint = () => { for (const f of swatches) f(); };
  for (const [title, list, colour] of spec) {
    const group = make('div', 'tuner-group');
    const head = make('div', 'camera-tuner-section-title', title);
    if (colour) {
      const sw = make('span', 'look-tuner-swatch');
      head.prepend(sw);
      swatches.push(() => { const c = read(colour); sw.style.background = `oklch(${c.L} ${c.C} ${c.h})`; });
    }
    group.appendChild(head);
    for (const [key, label, min, max, step, help, needsRebuild] of list) {
      const field = make('div', 'camera-tuner-field');
      const lab = make('label', null, `${label} `);
      const val = make('span');
      lab.appendChild(val);
      const input = make('input');
      input.type = 'range';
      input.id = `${paneId}-${key.replace(/\./g, '-')}`;
      input.min = min;
      input.max = max;
      input.step = step;
      if (help) TUNER_HELP[input.id] = help;
      const places = (String(step).split('.')[1] || '').length;
      const show = () => {
        val.textContent = (+read(key)).toFixed(places);
        val.classList.toggle('look-tuner-changed', read(key) !== read(key, defaults));
      };
      const apply = () => { show(); paint(); save(); report(); if (needsRebuild) rebuild(); };
      input.addEventListener('input', () => {
        write(key, parseFloat(input.value));
        if (needsRebuild) { show(); paint(); } else apply();
      });
      if (needsRebuild) input.addEventListener('change', apply);
      input.addEventListener('dblclick', () => {
        write(key, read(key, defaults));
        input.value = read(key);
        paintTunerRangeFill(input);
        apply();
      });
      field.append(lab, input);
      group.appendChild(field);
      fields.push({ key, input, show });
    }
    pane.appendChild(group);
  }
  const sync = () => {
    for (const f of fields) { f.input.value = read(f.key); f.show(); }
    repaintTunerFills(pane);
    paint();
    report();
  };

  const group = make('div', 'tuner-group');
  if (views && views.length) {
    group.appendChild(make('div', 'camera-tuner-section-title', 'Look from'));
    // A null in the list starts a new row of buttons.
    let row = make('div', 'camera-tuner-row');
    for (const view of views) {
      if (!view) {
        group.appendChild(row);
        row = make('div', 'camera-tuner-row');
        continue;
      }
      const [label, go] = view;
      const b = make('button', 'camera-tuner-btn', label);
      b.type = 'button';
      // A view that returns settings ({key: value}) has them kept, as a
      // slider would.
      b.addEventListener('click', () => {
        const set = go();
        if (!set || typeof set !== 'object') return;
        for (const [key, v] of Object.entries(set)) if (keys.includes(key) && Number.isFinite(v)) write(key, v);
        save();
        sync();
      });
      row.appendChild(b);
    }
    group.appendChild(row);
  }
  group.appendChild(make('div', 'camera-tuner-section-title', 'Your settings'));
  const row = make('div', 'camera-tuner-row');
  const copy = make('button', 'camera-tuner-btn primary', 'Copy settings');
  const reset = make('button', 'camera-tuner-btn', 'Reset all');
  copy.type = reset.type = 'button';
  row.append(copy, reset);
  output = make('textarea', 'camera-tuner-output');
  output.readOnly = true;
  const meta = make('div', 'camera-tuner-meta',
    'Changes show live and stay after a reload. Double-click a slider to put it back. Copy settings puts the changed values on the clipboard to send over.'
    + (onRebuild ? ' Sliders marked (rebuild) apply when you let go.' : ''));
  group.append(row, output, meta);
  if (onRebuild) { status = make('div', 'camera-tuner-meta'); group.appendChild(status); }
  // First in the pane, so the views and the copy are always in reach.
  pane.prepend(group);
  copy.addEventListener('click', () => {
    report();
    const done = () => { copy.textContent = 'Copied'; setTimeout(() => { copy.textContent = 'Copy settings'; }, 1500); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(output.value).then(done, () => output.select());
    } else {
      output.select();
    }
  });
  reset.addEventListener('click', () => {
    const hadRebuild = keys.some((key) => rebuilds.has(key) && read(key) !== read(key, defaults));
    for (const key of keys) write(key, read(key, defaults));
    save();
    sync();
    if (hadRebuild) rebuild();
  });
  sync();
  setupTunerHelp(pane);
}

// The Sun tab: SUN_GLARE, read by updateLensFlare every frame.
const SUN_TUNER_STORAGE = 'sunTuner.v3';
const SUN_TUNER_SPEC = [
  ['Core and glow', [
    ['core', 'Core size', 0.01, 0.3, 0.005, 'Width of the white centre, in degrees round the true Sun.'],
    ['coreGain', 'Core brightness', 10, 400, 1, 'How hard the centre clips white, so how far the white reaches.'],
    ['glow', 'Glow size', 0.05, 2, 0.01, 'Width of the warm glow round the core, in degrees.'],
    ['glowGain', 'Glow brightness', 0, 8, 0.05, 'Brightness of the warm glow.'],
    ['glowEnd', 'Glow reach', 0.5, 10, 0.1, 'How many glow widths out it fades to nothing.'],
    ['limbGlow', 'Glow round a close disc', 0, 2, 0.01, 'The glow kept round the disc once it shows its surface, at the limb as a share of the limb\'s own light (under 1 keeps it dimmer than the disc).'],
    ['glowGrow', 'Glow growth at artistic scale', 0, 1, 0.01, 'Round an artistic disc the glare is drawn several times larger. 1 grows the glow with it, 0.5 by its square root (as the haze does), 0 not at all.'],
    ['veil', 'Haze size', 0.2, 6, 0.05, 'Width of the faint wide haze, in degrees.'],
    ['veilGain', 'Haze brightness', 0, 0.3, 0.002, 'Brightness of the wide haze.'],
    ['veilEnd', 'Haze reach', 1, 30, 0.5, 'Where the haze has faded, in degrees.'],
  ]],
  ['Rays', [
    ['rayCount', 'Number of rays', 4, 24, 2, 'An aperture of n blades makes 2n rays when n is odd, n when even.'],
    ['rayTurn', 'Ray angle', 0, 1.57, 0.01, 'Turns the rays (and the ghosts) in the frame, in radians.'],
    ['rayGain', 'Ray brightness', 0, 4000, 10, 'Brightness of the main rays.'],
    ['rayLength', 'Ray length', 0.2, 6, 0.05, 'How far the longest ray reaches, in degrees from the Earth.'],
    ['rayPastCore', 'Shortest reach', 0.5, 5, 0.05, 'The rays always end at least this many core reaches out.'],
    ['rayWidth', 'Ray width', 0.005, 0.1, 0.001, 'Width of a ray line, in degrees (drawn at least a pixel wide).'],
    ['rayKnee', 'Ray falloff start', 0.01, 0.4, 0.005, 'Where a ray starts to fade along its length.'],
    ['rayRoot', 'Ray base width', 0.01, 0.5, 0.005, 'Width of the broad base the rays leave the core with.'],
    ['rayRootShare', 'Ray base brightness', 0, 1, 0.01, 'How bright that base is.'],
    ['rayRootFade', 'Ray base length', 0.05, 1.5, 0.01, 'How far out the base narrows into the line.'],
    ['rayWarmth', 'Ray warmth', 0, 1, 0.01, 'How much of the lens colour below the rays take.'],
  ]],
  ['Strands', [
    ['strandGain', 'Strand brightness', 0, 8000, 10, 'Brightness of the fine strands between the rays. 0 turns them off.'],
    ['strandCount', 'Number of strands', 20, 600, 1, 'How many strands go round the light.'],
    ['strandLength', 'Longest strand', 0.1, 4, 0.05, 'The longest strand, in ray lengths.'],
    ['strandShortest', 'Shortest strand', 0, 1, 0.01, 'The shortest strand, as a share of the longest.'],
    ['strandSpread', 'Short to long', 0.3, 6, 0.05, 'Higher makes most strands short and a few long; 1 spreads them evenly.'],
    ['strandWidth', 'Strand width', 0.2, 3, 0.05, 'Width in ray widths (each strand varies by half either way).'],
    ['strandJitter', 'Spacing randomness', 0, 1, 0.01, 'How far a strand strays from even spacing, in gaps.'],
    ['strandDim', 'Brightness variety', 0, 1, 0.01, 'How much dimmer the faintest strands are than the brightest.'],
    ['strandShift', 'Change as you pan', 0, 1, 0.01, 'How much each strand brightens, fades, grows and shrinks as the Sun moves across the frame, as a lens\'s dust and scratches catch the light differently. 0 keeps them fixed.'],
    ['strandShiftRate', 'How fast they change', 0.2, 6, 0.05, 'How many times a strand comes and goes as the Sun crosses the height of the frame.'],
    ['strandScaleFall', 'Artistic scale dimming', 0, 3, 0.05, 'At artistic scale the glare is drawn several times larger; the strands are divided by that size to this power. 0 keeps them as bright as at true scale.'],
  ]],
  ['Streak, ghosts and colour', [
    ['streakGain', 'Horizontal streak', 0, 0.6, 0.005, 'The thin horizontal line through the Sun.'],
    ['ghostGain', 'Lens ghosts', 0, 0.3, 0.002, 'The coloured shapes on the line through the middle of the frame.'],
    ['warm.0', 'Lens colour red', 0, 1, 0.01, 'The colour of the glow and rays (white by default): red.'],
    ['warm.1', 'Lens colour green', 0, 1, 0.01, 'The colour of the glow and rays (white by default): green.'],
    ['warm.2', 'Lens colour blue', 0, 1, 0.01, 'The colour of the glow and rays (white by default): blue.'],
  ]],
  ['Close to the Sun', [
    ['resolvedFrom', 'Face shows from', 0.5, 20, 0.1, 'Realistic style: the disc radius (degrees) where the rays start to go and the surface shows.'],
    ['resolvedBy', 'Face fully shown by', 1, 30, 0.1, 'Realistic style: the radius by which the rays are gone.'],
    ['detailedFrom', 'Detailed: face from', 0.1, 10, 0.05, 'Detailed style: where its surface starts to show.'],
    ['detailedBy', 'Detailed: fully by', 0.2, 15, 0.05, 'Detailed style: where the surface fully shows.'],
  ]],
];

function setupSunTuner() {
  setupLookTuner({ paneId: 'tuner-pane-sun', storageKey: SUN_TUNER_STORAGE, root: SUN_GLARE, spec: SUN_TUNER_SPEC, source: 'SUN_GLARE (src/main.js)' });
}

// The Galaxy tab: GALAXY_LOOK, GALAXY_PALETTE and MW_STRUCTURE in
// src/galaxyParticles.js. Gains and exposure are drawn live; light, dust,
// shape and colour regenerate the particles (about a second).
const GALAXY_TUNER_STORAGE = 'galaxyTuner.v3';
const GALAXY_COLOURS = [
  ['oldIn', 'Old disc, inner'], ['oldOut', 'Old disc, outer'], ['thick', 'Thick disc'],
  ['hazeWarm', 'Haze, warm'], ['hazeBlue', 'Haze, blue (outer disc)'],
  ['young', 'Young arm light'], ['cluster', 'Young clusters'], ['hii', 'HII knots (pink)'],
  ['core', 'Bulge core'], ['dust', 'Dust'],
  ['starYoung', 'Young stars'], ['starOld', 'Old disc stars'], ['starBulge', 'Bulge stars'],
];
const GALAXY_TUNER_SPEC = [
  ['Exposure and gains (live)', [
    ['look.exposure', 'Exposure', 0.1, 4, 0.01, 'Overall brightness of the galaxy.'],
    ['look.starGain', 'Stars', 0, 12, 0.01, 'Brightness of the point stars.'],
    ['look.cloudGain', 'Glowing clouds', 0, 4, 0.01, 'Brightness of the smooth light: disc, bulge, arms, haze, HII.'],
    ['look.dustGain', 'Dust darkness', 0, 16, 0.05, 'How much light the dust takes out.'],
    ['look.dustGlow', 'Dust brightness', 0, 0.5, 0.005, 'The warm light the dust scatters back, so lanes read brown rather than black.'],
    ['look.edgeOnExposure', 'Edge-on exposure', 0.1, 1, 0.01, 'Exposure seen edge-on, as a share of face-on (an edge-on disc is brighter, so it is stopped down).'],
    ['look.insideDim', 'Dimming from inside', 0, 1, 0.01, 'How far the exposure drops with the camera inside the disc.'],
  ]],
  ['Light (rebuild)', [
    ['look.discLight', 'Old disc glow', 0, 400, 1, 'The smooth light of the old disc.', true],
    ['look.bulgeLight', 'Bulge glow', 0, 40, 0.1, 'The smooth light of the bulge and bar.', true],
    ['look.bulgeEnvLight', 'Bulge halo', 0, 90, 0.1, 'The soft round envelope round the bulge.', true],
    ['look.nucleusLight', 'Nucleus', 0, 5, 0.05, 'The small bright core at the centre.', true],
    ['look.armLight', 'Arm light', 0, 120, 0.5, 'The continuous young light along the arms.', true],
    ['look.complexLight', 'Star-forming clumps', 0, 40, 0.1, 'The clumps of young stars and gas strung along the arms.', true],
    ['look.hazeLight', 'Haze', 0, 60, 0.5, 'The diffuse light between and round the arms, warm inside and blue outside.', true],
    ['look.youngStarLight', 'Young stars', 0, 60, 0.1, 'The total light of the young (blue) point stars.', true],
    ['look.oldStarLight', 'Old disc stars', 0, 40, 0.1, 'The total light of the old disc point stars.', true],
    ['look.bulgeStarLight', 'Bulge stars', 0, 15, 0.05, 'The total light of the bulge point stars.', true],
  ]],
  ['Dust (rebuild)', [
    ['look.laneDust', 'Arm lanes', 0, 4, 0.01, 'The dust lanes along the arms\' inner edges, with their feathers.', true],
    ['look.smoothDust', 'Smooth dust disc', 0, 4, 0.01, 'The even dust layer across the disc (it makes the edge-on lane).', true],
    ['look.barDust', 'Bar lanes', 0, 4, 0.01, 'The lanes along the bar\'s leading edges.', true],
    ['look.spurDust', 'Spurs', 0, 4, 0.01, 'The dust in the short spurs off the arms.', true],
    ['structure.dustH', 'Dust layer thickness', 0.03, 1.5, 0.005, 'Scale height of the dust, kpc.', true],
  ]],
  ['Default view', [
    ['view.elevation', 'Height above the disc', -90, 90, 0.1, 'Degrees above the disc where the Milky Way is first shown from: 90 face-on, 0 edge-on, below 0 from underneath. Default view goes there, Use this view takes it from where you are.'],
    ['view.azimuth', 'Round the disc', -180, 180, 0.1, 'Degrees round the disc from the side the Sun is on (180 is the far side).'],
    ['view.distance', 'Distance', 0.6, 4, 0.01, 'From the centre, in disc radii.'],
  ]],
  ['Shape (rebuild)', [
    ['look.armWidth', 'Arm width', 0.3, 2.5, 0.01, 'Widens or narrows every arm.', true],
    ['look.armWander', 'Arm wander', 0, 3, 0.01, 'How far the arms drift off a perfect spiral.', true],
    ['look.hiiShare', 'Pink HII share', 0, 1, 0.01, 'The share of clumps with pink HII knots.', true],
    ['structure.thinH', 'Thin disc thickness', 0.05, 3, 0.01, 'Scale height of the thin disc, kpc.', true],
    ['structure.thinL', 'Disc scale length', 1, 5, 0.05, 'How quickly the disc fades outward, kpc.', true],
    ['structure.thickH', 'Thick disc thickness', 0.2, 2.5, 0.01, 'Scale height of the thick disc, kpc.', true],
    ['structure.thickShare', 'Thick disc share', 0, 0.5, 0.01, 'The share of old stars in the thick disc.', true],
    ['structure.youngH', 'Young layer thickness', 0.02, 0.4, 0.005, 'Scale height of the young stars, kpc.', true],
    ['structure.warpK', 'Warp', 0, 0.1, 0.001, 'How much the outer disc bends.', true],
    ['look.particleCount', 'Particle count', 0.25, 2, 0.05, 'More particles means a finer grain and a slower frame; the brightness stays.', true],
  ]],
  ...GALAXY_COLOURS.map(([key, title]) => [`${title} (rebuild)`, [
    [`palette.${key}.L`, 'Lightness', 0.1, 1, 0.005, 'OKLCh lightness.', true],
    [`palette.${key}.C`, 'Colourfulness', 0, 0.3, 0.001, 'OKLCh chroma.', true],
    [`palette.${key}.h`, 'Hue', 0, 360, 1, 'OKLCh hue in degrees: about 30 red, 60 orange, 100 yellow, 145 green, 230 blue, 300 violet.', true],
  ], `palette.${key}`]),
];

// A camera offset from the galaxy's centre: elevation above the disc,
// azimuth from the centre's line to the Sun (180 is the far side), distance
// in scene units (the disc's radius is 25M). The galaxy's local frame has x
// toward the Sun and z the disc's normal (src/galaxy.js).
function galaxyViewOffset(elevDeg, azDeg, dist, out = new THREE.Vector3()) {
  const q = galaxy.object.quaternion;
  const e = THREE.MathUtils.degToRad(elevDeg), a = THREE.MathUtils.degToRad(azDeg);
  return out.set(Math.cos(e) * Math.cos(a), Math.cos(e) * Math.sin(a), Math.sin(e))
    .applyQuaternion(q)
    .multiplyScalar(dist);
}

function viewGalaxyAt(elevDeg, azDeg, dist) {
  if (!galaxy || !galaxy.object) return;
  focusOnObject(galaxy.object, { interrupt: true, force: true, offset: galaxyViewOffset(elevDeg, azDeg, dist) });
}

// The camera's place as a GALAXY_VIEW, for the Galaxy tab's "Use this view".
function galaxyViewFromCamera() {
  if (!galaxy || !galaxy.object) return null;
  const local = camera.position.clone().sub(galaxy.object.position)
    .applyQuaternion(galaxy.object.quaternion.clone().invert());
  const len = local.length();
  const round = (v, places) => Math.round(v * 10 ** places) / 10 ** places;
  return {
    'view.elevation': round(THREE.MathUtils.radToDeg(Math.asin(local.z / len)), 1),
    'view.azimuth': round(THREE.MathUtils.radToDeg(Math.atan2(local.y, local.x)), 1),
    'view.distance': round(len / galaxy.object.userData.galaxyRadius, 2),
  };
}

function setupGalaxyTuner() {
  setupLookTuner({
    paneId: 'tuner-pane-galaxy',
    storageKey: GALAXY_TUNER_STORAGE,
    root: { look: GALAXY_LOOK, palette: GALAXY_PALETTE, structure: MW_STRUCTURE, view: GALAXY_VIEW },
    spec: GALAXY_TUNER_SPEC,
    source: 'Galaxy (look = GALAXY_LOOK, palette = GALAXY_PALETTE, structure = MW_STRUCTURE, src/galaxyParticles.js; view = GALAXY_VIEW, src/galaxy.js)',
    onRebuild: () => (galaxy ? galaxy.rebuild() : NaN),
    views: [
      ['Face-on', () => viewGalaxyAt(88, 0, 6.0e7)],
      ['Tilted', () => viewGalaxyAt(30, 200, 5.4e7)],
      ['Low', () => viewGalaxyAt(18, 180, 2.6e7)],
      ['Edge-on', () => viewGalaxyAt(2, 90, 5.0e7)],
      null,
      ['Default view', () => galaxy && focusOnObject(galaxy.object, { interrupt: true, force: true })],
      ['Use this view', galaxyViewFromCamera],
    ],
  });
}

function setupCinematicTuner() {
  // Lives inside the tuner dock's Cinematic pane now. The dock is
  // removed on non-localhost, so this null-checks out there.
  const root = document.getElementById('tuner-pane-cinematic');
  if (!root) return;
  const bodySelect = document.getElementById('tuner-body');
  const distSlider = document.getElementById('tuner-distance');
  const sideSlider = document.getElementById('tuner-side');
  const upSlider = document.getElementById('tuner-up');
  const biasSlider = document.getElementById('tuner-bias');
  const fovSlider = document.getElementById('tuner-fov');
  const exposureSlider = document.getElementById('tuner-exposure');
  const contrastSlider = document.getElementById('tuner-contrast');
  const saturationSlider = document.getElementById('tuner-saturation');
  const vignetteSlider = document.getElementById('tuner-vignette');
  const grainSlider = document.getElementById('tuner-grain');
  const ambientSlider = document.getElementById('tuner-ambient');
  const sunlightSlider = document.getElementById('tuner-sunlight');
  const copyBtn = document.getElementById('camera-tuner-copy');
  const resetBtn = document.getElementById('camera-tuner-reset');
  const output = document.getElementById('camera-tuner-output');

  // Find the AmbientLight and the sun PointLight so we can tune
  // their intensities. They were created during setupScene / createSun.
  let ambientLight = null;
  let sunLight = null;
  scene.traverse((node) => {
    if (node.isAmbientLight && !ambientLight) ambientLight = node;
    if (node.isPointLight && !sunLight) sunLight = node;
  });

  // Read the slider value display elements and set them up.
  const labelMap = {
    'tuner-distance': 'tuner-distance-val',
    'tuner-side': 'tuner-side-val',
    'tuner-up': 'tuner-up-val',
    'tuner-bias': 'tuner-bias-val',
    'tuner-fov': 'tuner-fov-val',
    'tuner-exposure': 'tuner-exposure-val',
    'tuner-contrast': 'tuner-contrast-val',
    'tuner-saturation': 'tuner-saturation-val',
    'tuner-vignette': 'tuner-vignette-val',
    'tuner-grain': 'tuner-grain-val',
    'tuner-ambient': 'tuner-ambient-val',
    'tuner-sunlight': 'tuner-sunlight-val'
  };
  function updateLabel(sliderId) {
    const slider = document.getElementById(sliderId);
    const label = document.getElementById(labelMap[sliderId]);
    if (slider && label) {
      const v = parseFloat(slider.value);
      label.textContent = v.toFixed(slider.step.includes('.') ? slider.step.split('.')[1].length : 0);
    }
  }
  function setSliderSilently(slider, value) {
    if (!slider) return;
    slider.value = value;
    updateLabel(slider.id);
  }

  // Track which body the panel is targeting. Default to whatever is
  // currently selectedObject if it's a planet.
  function bodyKey() {
    return bodySelect.value;
  }
  function bodyObject() {
    const key = bodyKey();
    return planets[key] || null;
  }
  function bodyRadius() {
    const data = celestialData[bodyKey()] || {};
    return data.radius || 2.5;
  }

  // --- Math helpers --------------------------------------------------
  // Build the planet-relative basis: planetDir (sun → planet), sideTan
  // (worldUp × planetDir), upTan (planetDir × sideTan).
  function planetBasis(body) {
    const heroPos = new THREE.Vector3();
    body.getWorldPosition(heroPos);
    let planetDir = heroPos.clone();
    if (planetDir.lengthSq() < 1e-4) planetDir.set(1, 0, 0);
    planetDir.normalize();
    const worldUp = new THREE.Vector3(0, 1, 0);
    let sideTan = new THREE.Vector3().crossVectors(worldUp, planetDir);
    if (sideTan.lengthSq() < 1e-4) sideTan.set(1, 0, 0);
    sideTan.normalize();
    const upTan = new THREE.Vector3().crossVectors(planetDir, sideTan).normalize();
    return { heroPos, planetDir, sideTan, upTan };
  }

  // Apply slider values to the camera + post-process uniforms.
  function applyFromSliders() {
    const body = bodyObject();
    if (!body) return;
    const radius = bodyRadius();
    const distance = parseFloat(distSlider.value) * radius;
    const sideFactor = parseFloat(sideSlider.value);
    const upFactor = parseFloat(upSlider.value);
    const sunBias = parseFloat(biasSlider.value);
    const fov = parseFloat(fovSlider.value);

    const { heroPos, planetDir, sideTan, upTan } = planetBasis(body);
    const offset = planetDir.clone().multiplyScalar(distance)
      .addScaledVector(sideTan, distance * sideFactor)
      .addScaledVector(upTan, distance * upFactor);

    camera.position.copy(heroPos).add(offset);
    const lookAt = heroPos.clone().lerp(new THREE.Vector3(0, 0, 0), sunBias);
    camera.lookAt(lookAt);
    controls.target.copy(lookAt);
    camera.fov = fov;
    camera.updateProjectionMatrix();
    selectedObject = body;

    if (cinematicPass && cinematicPass.uniforms) {
      cinematicPass.uniforms.exposure.value = parseFloat(exposureSlider.value);
      cinematicPass.uniforms.contrast.value = parseFloat(contrastSlider.value);
      cinematicPass.uniforms.saturation.value = parseFloat(saturationSlider.value);
      cinematicPass.uniforms.vignetteIntensity.value = parseFloat(vignetteSlider.value);
      cinematicPass.uniforms.grainStrength.value = parseFloat(grainSlider.value);
    }
    if (ambientLight) ambientLight.intensity = parseFloat(ambientSlider.value);
    if (sunLight) sunLight.intensity = parseFloat(sunlightSlider.value);
  }

  // Capture the FULL controls.target offset in the body-relative
  // basis (planetDir, sideTan, upTan) so panning is reproduced
  // exactly. The slider only exposes the sunward bias for UI
  // simplicity but the side/up components are stored on a hidden
  // object and emitted into the COPY config.
  const targetOffsetState = { side: 0, up: 0 };

  // Read the current camera + scene state and reflect it back into
  // the sliders. Called on every OrbitControls 'change' event so
  // manual camera moves update the panel in real time. Full
  // precision is passed to the sliders (was previously truncated to
  // 0.01 which compounded with COPY's 4-decimal output to look
  // higher precision than it actually was).
  let syncingFromCamera = false;
  function syncFromCamera() {
    const body = bodyObject();
    if (!body) return;
    syncingFromCamera = true;
    const radius = bodyRadius();
    const { heroPos, planetDir, sideTan, upTan } = planetBasis(body);
    const offset = camera.position.clone().sub(heroPos);
    const planetDirAmount = offset.dot(planetDir);
    const sideAmount = offset.dot(sideTan);
    const upAmount = offset.dot(upTan);
    const bodyDist = planetDirAmount / Math.max(radius, 1e-4);
    setSliderSilently(distSlider, bodyDist);
    const denom = Math.max(Math.abs(planetDirAmount), 1e-4);
    setSliderSilently(sideSlider, sideAmount / denom);
    setSliderSilently(upSlider, upAmount / denom);
    // Decompose controls.target offset from heroPos in body basis.
    // The sun-bias slider continues to drive ONLY the sunward
    // component along planetDir; perpendicular components are
    // recorded on targetOffsetState for the COPY output so any pan
    // is preserved.
    const targetOffset = controls.target.clone().sub(heroPos);
    targetOffsetState.side = targetOffset.dot(sideTan);
    targetOffsetState.up = targetOffset.dot(upTan);
    const heroDistFromSun = heroPos.length();
    if (heroDistFromSun > 1e-4) {
      // sunBias = how much target is biased toward sun along
      // planetDir axis. target.dot(planetDir) / |heroPos|, then
      // 1 - that = bias (0 = at planet, 1 = at sun).
      const targetPlanetDirAmount = controls.target.dot(planetDir);
      const sunBias = 1.0 - (targetPlanetDirAmount / heroDistFromSun);
      setSliderSilently(biasSlider, sunBias);
    }
    setSliderSilently(fovSlider, camera.fov);
    if (cinematicPass && cinematicPass.uniforms) {
      setSliderSilently(exposureSlider, cinematicPass.uniforms.exposure.value);
      setSliderSilently(contrastSlider, cinematicPass.uniforms.contrast.value);
      setSliderSilently(saturationSlider, cinematicPass.uniforms.saturation.value);
      setSliderSilently(vignetteSlider, cinematicPass.uniforms.vignetteIntensity.value);
      setSliderSilently(grainSlider, cinematicPass.uniforms.grainStrength.value);
    }
    if (ambientLight) setSliderSilently(ambientSlider, ambientLight.intensity);
    if (sunLight) setSliderSilently(sunlightSlider, sunLight.intensity);
    syncingFromCamera = false;
  }

  // Wire slider input → applyFromSliders. Throttled so we don't
  // recompute the camera 60 times per slider movement frame.
  [
    distSlider, sideSlider, upSlider, biasSlider, fovSlider,
    exposureSlider, contrastSlider, saturationSlider, vignetteSlider,
    grainSlider, ambientSlider, sunlightSlider
  ].forEach((slider) => {
    slider.addEventListener('input', () => {
      if (syncingFromCamera) return;
      updateLabel(slider.id);
      applyFromSliders();
    });
  });

  // Body selector change: switch camera focus to the new body using
  // the CURRENT slider values, then sync sliders back (in case the
  // body's radius / position made some values clamp). This way the
  // dropdown actually flies the camera to the new planet so the dev
  // can immediately tune from that body's perspective.
  bodySelect.addEventListener('change', () => {
    applyFromSliders();
    // Small delay so the camera + controls settle before re-reading.
    setTimeout(() => syncFromCamera(), 80);
  });

  // Listen to OrbitControls change. Throttled to ~30Hz. Also keeps
  // the body dropdown in sync with the main scene's selectedObject —
  // if the user clicks a different planet in the main UI, the
  // tuner should follow so the slider readouts decompose against
  // the actually-focused body.
  let lastSync = 0;
  function syncBodyDropdown() {
    if (!selectedObject || !selectedObject.userData) return;
    // Find the planet key whose group matches selectedObject.
    for (const key of Object.keys(planets)) {
      if (planets[key] === selectedObject) {
        if (bodySelect.value !== key) bodySelect.value = key;
        return;
      }
    }
  }
  controls.addEventListener('change', () => {
    const now = performance.now();
    if (now - lastSync < 33) return;
    lastSync = now;
    syncBodyDropdown();
    syncFromCamera();
  });

  // COPY button. Outputs a paste-ready config block.
  // Force a fresh syncFromCamera BEFORE reading values so the sliders
  // reflect the LATEST camera state (the OrbitControls 'change'
  // listener is throttled to 30Hz and can drop the final update if
  // the user stops dragging within the throttle window).
  copyBtn.addEventListener('click', () => {
    syncFromCamera();
    const block = formatTunerConfig();
    output.value = block;
    output.select();
    try {
      navigator.clipboard.writeText(block);
    } catch (_) {
      try { document.execCommand('copy'); } catch (_) {}
    }
  });

  function formatTunerConfig() {
    const key = bodyKey();
    const body = bodyObject();
    // Capture the RAW camera world position and lookAt target. This
    // bypasses any body-relative basis decomposition (which had
    // precision losses, sign flips at orbital corners, and missing
    // dimensions for off-axis target panning). When paired with the
    // captured planet/moon orbital angles, restoring these exact
    // world coords gives the EXACT view the dev saw at copy time.
    const cp = camera.position;
    const ct = controls.target;
    // Capture the body's orbital angle so the on-reload reproduction
    // can snap the planet to the SAME orbital position. Without this,
    // the body has drifted by reload time and the captured camera-
    // relative composition no longer matches the absolute scene
    // (background stars, sun direction in world frame).
    const orbitAngle = body && body.userData && body.userData.orbitAngle != null
      ? body.userData.orbitAngle
      : null;
    // Also capture orbital angles of any moons of this body so the
    // moon's position in frame is reproduced on reload (the moon
    // orbits its parent at a much faster rate than the planet
    // orbits the sun, so even a few seconds of drift puts it
    // somewhere completely different in the composition).
    const moonGroups = moons[key] || [];
    const moonAngles = {};
    moonGroups.forEach((moonGroup) => {
      const ud = moonGroup && moonGroup.userData;
      if (ud && ud.name && ud.orbitAngle != null) {
        moonAngles[ud.name] = ud.orbitAngle;
      }
    });
    return [
      'CINEMATIC TUNER CONFIG',
      '======================',
      `body:        ${key}`,
      `bodyDistance: ${parseFloat(distSlider.value).toFixed(4)}  // body radii`,
      `sideFactor:  ${parseFloat(sideSlider.value).toFixed(4)}`,
      `upFactor:    ${parseFloat(upSlider.value).toFixed(4)}`,
      `sunBias:     ${parseFloat(biasSlider.value).toFixed(4)}`,
      `targetSide:  ${targetOffsetState.side.toFixed(4)}  // scene units (controls.target perpendicular)`,
      `targetUp:    ${targetOffsetState.up.toFixed(4)}  // scene units (controls.target up)`,
      `fov:         ${parseFloat(fovSlider.value).toFixed(2)}`,
      orbitAngle != null ? `orbitAngle:  ${orbitAngle.toFixed(8)}  // radians (snap on load)` : null,
      Object.keys(moonAngles).length > 0
        ? `moonAngles:  ${JSON.stringify(Object.fromEntries(
            Object.entries(moonAngles).map(([n, a]) => [n, Number(a.toFixed(8))])
          ))}  // per-moon orbital snap`
        : null,
      '',
      'EXACT-VIEW (use these for pixel-perfect reproduction)',
      `cameraPos:   [${cp.x.toFixed(6)}, ${cp.y.toFixed(6)}, ${cp.z.toFixed(6)}]`,
      `lookAt:      [${ct.x.toFixed(6)}, ${ct.y.toFixed(6)}, ${ct.z.toFixed(6)}]`,
      '',
      'POST-PROCESS',
      `exposure:    ${parseFloat(exposureSlider.value).toFixed(3)}`,
      `contrast:    ${parseFloat(contrastSlider.value).toFixed(3)}`,
      `saturation:  ${parseFloat(saturationSlider.value).toFixed(3)}`,
      `vignette:    ${parseFloat(vignetteSlider.value).toFixed(3)}`,
      `grain:       ${parseFloat(grainSlider.value).toFixed(4)}`,
      '',
      'SCENE',
      `ambient:     ${parseFloat(ambientSlider.value).toFixed(3)}`,
      `sunLight:    ${parseFloat(sunlightSlider.value).toFixed(3)}`
    ].filter(Boolean).join('\n');
  }

  // RESET button — reload current code defaults into the sliders.
  resetBtn.addEventListener('click', () => {
    setSliderSilently(distSlider, 3.0);
    setSliderSilently(sideSlider, 0.30);
    setSliderSilently(upSlider, 0.18);
    setSliderSilently(biasSlider, 0.12);
    setSliderSilently(fovSlider, 45);
    setSliderSilently(exposureSlider, 1.00);
    setSliderSilently(contrastSlider, 1.05);
    setSliderSilently(saturationSlider, 1.05);
    setSliderSilently(vignetteSlider, 0.30);
    setSliderSilently(grainSlider, 0.13);
    setSliderSilently(ambientSlider, 0.42);
    setSliderSilently(sunlightSlider, 3.10);
    applyFromSliders();
  });

  // The dock owns visibility (Shift+T toggles its Cinematic tab).
  // Register the "became visible" hook so slider readouts refresh
  // from the live camera, and wire the sidebar button as a launcher.
  tunerDockRegister('cinematic', { onShow: () => syncFromCamera() });
  const sidebarTunerBtn = document.getElementById('admin-cinematic-tuner');
  if (sidebarTunerBtn) {
    sidebarTunerBtn.addEventListener('click', () => tunerDockToggleTab('cinematic'));
  }

  // Initial sync once the scene has settled.
  setTimeout(() => syncFromCamera(), 600);
}

// Scene setup
function setupScene() {
  scene = new THREE.Scene();
  scene.background = new THREE.Color(0x000005);
}

// Camera setup - reduced FOV to minimize fisheye distortion
function setupCamera() {
  camera = new THREE.PerspectiveCamera(
    45,  // Reduced from 60 to minimize perspective distortion
    window.innerWidth / window.innerHeight,
    1,   // Increased near plane for better depth precision
    50000000  // Very far for 10,000x scale
  );
  camera.position.set(350, 220, 550);
  camera.lookAt(0, 0, 0);
}

// Renderer setup - optimized for maximum GPU performance
function setupRenderer() {
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.dataset.mainBoot = 'renderer';
  }
  releaseLoadingEarth();
  // Let Three.js handle context creation for better browser compatibility
  renderer = new THREE.WebGLRenderer({
    antialias: true,
    powerPreference: 'high-performance',
    precision: 'highp',
    // Log depth buffer would help at planetary scales but breaks our
    // many custom ShaderMaterials (earth, atmosphere, sun, aurora,
    // comet, dso, galaxy, wormhole, etc.) because they don't include
    // the <logdepthbuf_*> shader chunks Three.js's built-in pipeline
    // injects automatically. Result was see-through planets. Leaving
    // it off until we either retrofit every custom shader or migrate
    // to a different depth-precision strategy (camera-near tweaks,
    // origin shift, etc.).
    logarithmicDepthBuffer: false,
    stencil: false,
    alpha: false,
    depth: true,
    failIfMajorPerformanceCaveat: false
  });

  // Check if WebGL is available
  if (!renderer.getContext()) {
    console.error('WebGL not available');
    document.body.innerHTML = '<div style="color: white; padding: 20px; text-align: center;"><h1>WebGL Not Supported</h1><p>Please enable WebGL in your browser or try a different browser.</p></div>';
    return;
  }

  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(graphicsConfig.pixelRatio);
  // Big maps go to the GPU a band a frame, not whole on the frame that
  // draws them (src/bandupload.js).
  if (BAND_UPLOADS) enableBandUploads(renderer);

  // WebGL context-loss recovery. Mobile Safari, backgrounded tabs, or
  // driver hiccups can kill the context. Default Three.js behaviour
  // is to render black forever.
  //
  // PREVIOUS behaviour was to reload immediately. On mobile (especially
  // iOS Safari with low memory) the context can be lost repeatedly,
  // and a reload triggered another lost-context on the next paint, so
  // the page sat in an infinite reload loop. The fix:
  //   1. Try the standard recovery path first — preventDefault on the
  //      lost event lets webglcontextrestored fire, and the renderer
  //      re-uploads resources. What lived only on the GPU (the streamed
  //      imagery) does not come back, so a reload follows 6 s later,
  //      on the same preset and view when the context did come back.
  //   2. Count those reloads (sessionStorage tally), and drop to the Low
  //      preset only when the context never came back.
  //   3. Cap at 2 reloads. After that, show a "tap to retry" prompt so
  //      the user is in control instead of trapped in a reload spiral.
  // The page comes back on the view it lost (a view link in the address,
  // src/ui/viewlink.js) when the address holds nothing, a view link or a
  // planet; anything else names a mode to reopen (#eclipse/..., #iss).
  const keepViewAcrossReload = () => {
    try {
      const hash = window.location.hash;
      if (hash && !isViewHash(hash) && !celestialData[hash.slice(1).toLowerCase()]) return;
      const state = getViewState();
      if (state) history.replaceState(history.state, '', `${window.location.pathname}${window.location.search}${encodeView(state)}`);
    } catch (err) { /* reload as it is */ }
  };
  const MOBILE_RESTORE_WAIT_MS = 20000;
  const whenShown = (fn) => {
    const onVisible = () => {
      if (document.hidden) return;
      document.removeEventListener('visibilitychange', onVisible);
      fn();
    };
    document.addEventListener('visibilitychange', onVisible);
  };
  const CONTEXT_LOSS_COUNTER_KEY = 'webglContextLossReloads';
  const CONTEXT_LOSS_LAST_RELOAD_KEY = 'webglContextLossLastReload';
  const readReloadCount = () => {
    try { return parseInt(sessionStorage.getItem(CONTEXT_LOSS_COUNTER_KEY) || '0', 10) || 0; }
    catch (err) { return 0; }
  };
  const writeReloadCount = (n) => {
    try { sessionStorage.setItem(CONTEXT_LOSS_COUNTER_KEY, String(n)); }
    catch (err) {}
  };
  const readLastReloadTime = () => {
    try { return parseInt(sessionStorage.getItem(CONTEXT_LOSS_LAST_RELOAD_KEY) || '0', 10) || 0; }
    catch (err) { return 0; }
  };
  // Decay the counter after 60 s of stable rendering so a single
  // backgrounded-tab restoration doesn't permanently block future
  // recovery on the same session.
  const lastReload = readLastReloadTime();
  if (lastReload && Date.now() - lastReload > 60_000) {
    writeReloadCount(0);
  }
  let contextLossRecoveryAttempted = false;
  // Whether the browser gave the context back after the last loss
  // (webglcontextrestored). This used to be read from renderer.info's frame
  // count against a snapshot taken at the loss, but three replaces
  // renderer.info on a restore (initGLContext) and the new count starts at
  // 0, so a restore always read as lost for good; nor does a hidden tab
  // draw at all.
  let contextRestoredSinceLoss = false;
  // When the tab last came on screen (a phone's longer wait below).
  let shownAt = performance.now();
  document.addEventListener('visibilitychange', () => { if (!document.hidden) shownAt = performance.now(); });
  renderer.domElement.addEventListener('webglcontextlost', (e) => {
    e.preventDefault();
    noteContextLoss();
    console.warn('[webgl] context lost — attempting recovery');
    contextLossRecoveryAttempted = true;
    contextRestoredSinceLoss = false;
    // Pause the animation loop until the context is restored so we
    // don't spam the driver with draw calls into a dead context.
    if (renderer.setAnimationLoop) renderer.setAnimationLoop(null);
  });
  renderer.domElement.addEventListener('webglcontextrestored', () => {
    console.warn('[webgl] context restored — resuming render loop');
    contextRestoredSinceLoss = true;
    if (renderer.setAnimationLoop && typeof animate === 'function') {
      renderer.setAnimationLoop(animate);
    }
  });
  // The reload 6 s after a loss: on Low only when the browser has not fired
  // webglcontextrestored by then. Cap at 2 reloads/session so a flaky
  // device doesn't trap the user in a refresh loop.
  renderer.domElement.addEventListener('webglcontextlost', () => {
    const settle = (ms = 6000) => setTimeout(() => {
      if (!contextLossRecoveryAttempted) return;
      // A phone hidden again while it waited waits for the screen again.
      if (RUNNING_ON_MOBILE && document.hidden) { whenShown(() => settle(MOBILE_RESTORE_WAIT_MS)); return; }
      // A context that came back draws again, but what lived only on the
      // GPU is gone: the streamed Earth, night and cloud imagery, whose
      // streamers still count their pages as loaded. So the page reloads
      // either way, and keeps its preset and view when the context came
      // back, since the GPU is fine. Before, a restore read as lost for good
      // and every desktop that lost a context reloaded on Low and kept it.
      const restored = contextRestoredSinceLoss;
      // The stream has nobody to press Retry and must not fall to the Low
      // preset for good: reload as it is, at most once every two minutes.
      if (STREAM_MODE) {
        let last = 0;
        try { last = Number(sessionStorage.getItem(CONTEXT_LOSS_LAST_RELOAD_KEY)) || 0; } catch (err) {}
        setTimeout(() => {
          try { sessionStorage.setItem(CONTEXT_LOSS_LAST_RELOAD_KEY, String(Date.now())); } catch (err) {}
          markOwnReload('stream');
          window.location.reload();
        }, Math.max(0, last + 120000 - Date.now()));
        return;
      }
      const reloadCount = readReloadCount();
      if (reloadCount >= 2) {
        // A screensaver has nobody to press a button (and on the Mac a
        // click ends it); both reloads have already been tried, so it stays
        // quietly black.
        if (IS_SCREENSAVER) return;
        // Stop reloading — show a recovery prompt instead.
        const prompt = document.createElement('div');
        prompt.style.cssText = `
          position: fixed; inset: 0; z-index: 100000;
          display: flex; align-items: center; justify-content: center;
          background: rgba(0,0,0,0.92); color: #e9e4d6;
          font-family: 'Inter', sans-serif; text-align: center;
          padding: 32px;
        `;
        prompt.innerHTML = `
          <div>
            <div style="font-size: 18px; letter-spacing: 0.1em; text-transform: uppercase; opacity: 0.7; margin-bottom: 16px;">Graphics context lost</div>
            <div style="font-size: 15px; opacity: 0.6; margin-bottom: 24px; max-width: 360px; line-height: 1.5;">Your device dropped the WebGL context twice. Tap to retry on the Low preset, or close and reopen the tab.</div>
            <button style="background: #4a9eff; color: white; border: none; padding: 12px 28px; border-radius: 4px; font-size: 14px; letter-spacing: 0.08em; text-transform: uppercase; cursor: pointer;" id="webgl-retry-btn">Retry on Low</button>
          </div>
        `;
        document.body.appendChild(prompt);
        const retryBtn = document.getElementById('webgl-retry-btn');
        if (retryBtn) {
          retryBtn.addEventListener('click', () => {
            try {
              localStorage.setItem('graphicsPreset', 'low');
              localStorage.setItem('graphicsSafetyFallback', '1');
              sessionStorage.removeItem(CONTEXT_LOSS_COUNTER_KEY);
              sessionStorage.removeItem(CONTEXT_LOSS_LAST_RELOAD_KEY);
            } catch (err) {}
            markOwnReload('retry_low');
            window.location.reload();
          });
        }
        return;
      }
      if (RUNNING_ON_MOBILE) {
        // A phone loses its context to memory, which the Low preset barely
        // changes there (the mobile clamp already draws it at a pixel ratio
        // of 1 with no MSAA or bloom), so it keeps its preset and its view.
        // A second loss soon after runs Low for this tab only
        // (MOBILE_SESSION_PRESET_KEY); the saved choice is never touched.
        if (reloadCount >= 1) {
          try { sessionStorage.setItem(MOBILE_SESSION_PRESET_KEY, 'low'); } catch (err) {}
        }
        keepViewAcrossReload();
      } else if (restored) {
        keepViewAcrossReload();
      } else {
        try {
          localStorage.setItem('graphicsPreset', 'low');
          localStorage.setItem('graphicsSafetyFallback', '1');
        } catch (err) {}
      }
      writeReloadCount(reloadCount + 1);
      try { sessionStorage.setItem(CONTEXT_LOSS_LAST_RELOAD_KEY, String(Date.now())); }
      catch (err) {}
      console.warn(`[webgl] context ${restored ? 'restored' : 'not restored in time'}, reload ${reloadCount + 1}/2`);
      markOwnReload(`context_lost_${reloadCount + 1}`);
      window.location.reload();
    }, ms);
    // iOS frees a hidden tab's GPU memory and loses its context there, and
    // can deliver the loss as the tab comes back. A phone counts only time on
    // screen, and gives a loss while hidden or within 10 s of coming back
    // MOBILE_RESTORE_WAIT_MS: the GPU process starting again and every shader
    // compiling again can take longer than 6 s on a phone.
    if (!RUNNING_ON_MOBILE) settle();
    else if (document.hidden) whenShown(() => settle(MOBILE_RESTORE_WAIT_MS));
    else settle(performance.now() - shownAt < 10000 ? MOBILE_RESTORE_WAIT_MS : 6000);
  });
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.7;

  // Disable shadow maps to prevent shading artifacts at this scale
  renderer.shadowMap.enabled = false;

  // Enable physically correct lighting model
  renderer.useLegacyLights = false;

  // Enable WebXR. The browser's runtime decides whether the device
  // is VR-capable (Quest, Vision Pro), AR-capable (Quest passthrough,
  // Vision Pro spatial mode), or both. Three.js handles the per-eye
  // render automatically once an XR session is active.
  renderer.xr.enabled = true;

  // GPU optimization flags
  renderer.info.autoReset = true;
  renderer.sortObjects = true;

  // Set output encoding for correct color
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  document.getElementById('canvas-container').appendChild(renderer.domElement);

  setupWebXR();
}

// WebXR enable + entry buttons. Two distinct experiences:
//   immersive-vr  → fully immersive (Quest 3, Vision Pro VR mode).
//                   The user is floating inside the scene; the room
//                   is replaced with the solar system / galaxy.
//   immersive-ar  → mixed reality with passthrough (Vision Pro
//                   spatial computing mode, Quest passthrough). The
//                   solar system appears around / over the real
//                   world.
// Both go through Three.js's WebXR manager so the same scene drives
// both. EffectComposer is bypassed in XR mode (see render dispatch in
// animate()) because some passes mismatch the multi-layer XR
// framebuffer layout.
function setupWebXR() {
  if (!navigator.xr || IS_APP) return; // no WebXR here (the App Store apps' WKWebView has none)

  const buttonHost = document.createElement('div');
  buttonHost.id = 'xr-buttons';
  // Top centre, under the distance readout: the bottom edge holds the body
  // navigation, the control hints and (locally) the tuner dock.
  buttonHost.style.cssText = `
    position: fixed;
    top: 78px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 8px;
    z-index: 10;
    pointer-events: auto;
  `;
  document.body.appendChild(buttonHost);

  // Probe support for each session type. Show only the buttons the
  // current device can actually enter; otherwise the user gets a
  // "session not supported" error on click.
  //
  // VR and AR mode is a paid unlock (src/xr/unlock.js, the /vr/ page): the
  // stock three.js buttons (which also offer the browser's own entry
  // prompt) appear only once it is unlocked, and a locked button opens the
  // unlock panel instead. The module loads only where a session is supported.
  // The Meta Quest app (?app=quest) has no buttons: it goes straight into VR.
  //
  // Chrome on Android says yes to both on a phone (src/xr/device.js), which
  // is no headset: there VR is never offered, and Start AR shows only to
  // someone who already has the unlock (an owner or an admin) until the AR
  // mode has been tried on a phone.
  if (IS_QUEST_APP) { buttonHost.remove(); xrQuestBoot(); }
  else Promise.all([
    navigator.xr.isSessionSupported('immersive-vr').catch(() => false),
    navigator.xr.isSessionSupported('immersive-ar').catch(() => false),
  ]).then(async ([vrOk, ar]) => {
    if (!vrOk && !ar) return;
    const phone = await onPhoneOrTablet();
    const vr = vrOk && !phone;
    if (!vr && !ar) return;
    const xr = await import('./xr/unlock.js');
    await xr.whenXrStateKnown();
    if (!phone) shopLinkOnHeadset(xr, vr);
    const draw = () => {
      if (renderer.xr.isPresenting) return;
      buttonHost.replaceChildren();
      if (xr.xrUnlocked()) {
        if (vr) buttonHost.appendChild(VRButton.createButton(renderer));
        if (ar) buttonHost.appendChild(ARButton.createButton(renderer, { requiredFeatures: ['local-floor'], optionalFeatures: ['hand-tracking'] }));
        return;
      }
      if (phone) return; // nothing to sell a phone
      for (const [on, mode, label] of [[vr, 'immersive-vr', 'ENTER VR'], [ar, 'immersive-ar', 'START AR']]) {
        if (!on) continue;
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'xr-locked';
        b.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"></rect><path d="M8 11V8a4 4 0 0 1 8 0v3"></path></svg><span>${label}</span>`;
        b.title = `VR and AR mode: ${xr.XR_PRICE} once`;
        b.addEventListener('click', () => xr.openXrPaywall({ mode }));
        buttonHost.appendChild(b);
      }
    };
    // An unlocked headset keeps a copy for use without Wi-Fi (src/xr/offline.js);
    // not an AR-only phone, which has no use for 260 MB of VR.
    const keepOffline = () => {
      if (!import.meta.env.PROD || !vr) return;
      import('./xr/offline.js').then((m) => (xr.xrUnlocked() ? m.keepOfflineCopy() : m.dropOfflineCopy())).catch(() => {});
    };
    draw();
    keepOffline();
    xr.onXrUnlockChange(() => { draw(); keepOffline(); });
    if (xr.xrUnlocked()) xrLoadExperience().catch(() => {}); // ready by the press
  });

  // When a session starts/ends we hide/show the regular HTML UI so
  // it doesn't render on top of the XR stereo view. We also rescale
  // the entire scene so the solar system fits at human-comfortable
  // distances (planets at 1-30 m instead of arbitrary scene units),
  // and reposition so the user starts floating just outside the sun
  // looking toward the inner planets.
  // The placement in enterXrPresentation is measured from the head where the
  // session starts. three's default 'local-floor' puts the origin on the
  // floor, which stood the Sun at the user's feet in VR and half a metre
  // under the floor in AR.
  renderer.xr.setReferenceSpaceType('local');
  // The two controller slots, claimed before any session: three r160 gives
  // an input source a slot only if one exists when the source is announced,
  // and a session that starts while the page loads (the Quest app) announces
  // its controllers or hands before src/xr/experience.js asks for the slots.
  // Each slot keeps the source three routed to it, for the experience.
  for (const i of [0, 1]) {
    const slot = renderer.xr.getController(i);
    slot.addEventListener('connected', (e) => { slot.userData.xrSource = e.data; });
    slot.addEventListener('disconnected', () => { slot.userData.xrSource = null; });
  }
  // Both read when the session's layer is made, so set before any session.
  renderer.xr.setFramebufferScaleFactor(XR_FRAMEBUFFER_SCALE[xrStartTier()]);
  renderer.xr.setFoveation(1);
  renderer.xr.addEventListener('sessionstart', () => {
    document.documentElement.classList.add('xr-active');
    enterXrPresentation();
    // VR and AR mode in use: the product's activation event, and the time in
    // the headset as mode 'xr' (docs/analytics.md).
    const session = renderer.xr.getSession();
    const xrMode = session && session.environmentBlendMode !== 'opaque' ? 'ar' : 'vr'; // as enterXrPresentation decides
    track('xr_session_start', { xr_mode: xrMode });
    modeEnter('xr', { xr_mode: xrMode });
  });
  renderer.xr.addEventListener('sessionend', () => {
    document.documentElement.classList.remove('xr-active');
    modeExit('xr');
    exitXrPresentation();
    if (IS_QUEST_APP) xrQuestScreen().show(xrQuestLeaving ? 'leaving' : 'back');
  });
}

// ------------------------------------------------------------------
// The Meta Quest app (docs/quest-store.md): the Horizon Store packages this
// page as a WebXR PWA that opens /viewer/?app=quest. It goes straight into
// VR (the tap on the app's icon is the user activation requestSession
// needs), shows "Back into space" when a session ends, and closes on
// "Leave VR". It is a paid app, so owning it is the unlock: no paywall,
// Stripe, licence, account, invite or tips UI appears in it (index.html
// hides the 2D chrome under html.quest-app).
let xrQuestLeaving = false;
let _xrQuestScreen = null;
const XR_QUEST_PACKAGE = 'online.threedsolarsystem.quest';
// Owned when launched by the Store's package (a Trusted Web Activity opens
// the page with an android-app:// referrer, kept for reloads in this tab),
// or when Meta's billing service answers (it exists only in Store installs),
// or on localhost for development. A browser that types ?app=quest gets none
// of these and is sent to the website's own VR mode instead.
async function xrQuestOwned() {
  try {
    if (String(document.referrer || '').startsWith(`android-app://${XR_QUEST_PACKAGE}`)) sessionStorage.setItem('quest.owned', '1');
    if (sessionStorage.getItem('quest.owned') === '1') return true;
  } catch { /* storage off */ }
  if (isLocalEnvironment()) return true;
  if (typeof window.getDigitalGoodsService === 'function') {
    try { if (await window.getDigitalGoodsService('https://quest.meta.com/billing')) return true; } catch { /* not a Store install */ }
  }
  return false;
}
function xrQuestScreen() {
  if (_xrQuestScreen) return _xrQuestScreen;
  const root = document.createElement('div');
  root.id = 'quest-screen';
  root.style.cssText = 'position:fixed;inset:0;z-index:100000;display:none;flex-direction:column;align-items:center;justify-content:center;gap:28px;'
    + 'background:radial-gradient(ellipse at 50% 120%,rgba(70,110,180,0.35),rgba(0,0,0,0.92) 60%),#000;color:#f5f5f7;'
    + "font:500 22px/1.4 Inter,-apple-system,'Segoe UI',sans-serif;text-align:center;padding:24px;";
  root.innerHTML = '<div style="font:700 44px/1.1 Inter,-apple-system,sans-serif;letter-spacing:-0.02em">3D Solar System</div>'
    + '<div data-msg style="color:#c7c7cc;max-width:640px"></div>'
    + '<button type="button" data-go style="font:600 28px Inter,-apple-system,sans-serif;padding:22px 48px;border-radius:999px;border:0;background:#f5f5f7;color:#000;cursor:pointer"></button>';
  document.body.appendChild(root);
  const msg = root.querySelector('[data-msg]');
  const go = root.querySelector('[data-go]');
  go.addEventListener('click', () => xrQuestEnter());
  _xrQuestScreen = {
    show(state) {
      root.style.display = 'flex';
      const text = {
        start: ['Opening space...', 'Enter space'],
        back: ['You are back on the ground.', 'Back into space'],
        leaving: ['See you among the planets.', 'Back into space'],
        failed: ['The headset did not open the session. Press to try again.', 'Enter space'],
        browser: ['This page is the Meta Quest app. In the browser, VR mode is at 3dsolarsystem.online/viewer.', ''],
      }[state] || ['', 'Enter space'];
      msg.textContent = text[0];
      go.textContent = text[1];
      go.style.display = text[1] ? '' : 'none';
    },
    hide() { root.style.display = 'none'; },
  };
  return _xrQuestScreen;
}
async function xrQuestEnter() {
  if (!renderer || renderer.xr.isPresenting) return;
  xrQuestLeaving = false;
  try {
    xrLoadExperience().catch(() => {});
    const session = await navigator.xr.requestSession('immersive-vr', { optionalFeatures: ['local-floor', 'bounded-floor', 'hand-tracking', 'layers'] });
    await renderer.xr.setSession(session);
    xrQuestScreen().hide();
    track('quest_session_start', {});
  } catch (err) {
    console.warn('[quest] session', err);
    xrQuestScreen().show('failed');
  }
}
async function xrQuestBoot() {
  xrQuestScreen().show('start');
  if (!(await xrQuestOwned())) { xrQuestScreen().show('browser'); return; }
  xrLoadExperience().catch(() => {});
  if (import.meta.env.PROD) import('./xr/offline.js').then((m) => m.keepOfflineCopy({ statusLine: false })).catch(() => {});
  xrQuestEnter();
}

// Saved scene transform restored when the user exits XR. Without
// this, a quick VR session would corrupt the scene scale / position
// for the regular 2D web view they return to.
const xrSavedSceneState = {
  active: false,
  scale: new THREE.Vector3(1, 1, 1),
  position: new THREE.Vector3(0, 0, 0),
  rotation: new THREE.Euler(),
  cameraPosition: new THREE.Vector3(),
  cameraQuaternion: new THREE.Quaternion(),
  cameraNear: 1,
  cameraFar: 50_000_000,
  cameraFov: 45,
  cameraZoom: 1,
  cameraUp: new THREE.Vector3(0, 1, 0),
  controlsTarget: new THREE.Vector3(),
  // The planes the session draws with, in metres. Per-frame writers in the
  // 2D view (Earth inspection, the Sun's close approach, a famous photo's
  // hold) keep setting camera.near; renderFrame pins these instead.
  xrNear: 0.05,
  xrFar: 5_000,
};
const _xrKeep = new Set();
const _xrCentre = new THREE.Vector3();
const _xrLook = new THREE.Vector3();
const _xrBasis = new THREE.Matrix4();

// Where a session puts the solar system: the body the 2D view was on, in
// front of the user at a size that reads as a world (a globe about 36 cm
// across at arm's length), the rest of the system around it at the same
// scale. The old fixed scale left every planet a centimetre across, metres
// away. In metres, from the head where the session starts ('local' space).
const XR_FOCUS = {
  vr: { radius: 0.18, distance: 0.85, drop: 0.08 },
  ar: { radius: 0.11, distance: 0.6, drop: 0.3 },
  sun: { radius: 0.3, distance: 1.6, drop: 0.1 },
};

// XR quality. While presenting, the heaviest work is skipped outright (the
// Low preset's Earth clouds, the raymarched volumes, Gargantua and the
// wormhole lens, the galaxy's slab march, the cinematic pass's exposure and
// flare upkeep) and so is what is sub-pixel at any XR scale: the satellite
// models (the ISS alone is 361 meshes, 722 draw calls in stereo, where three
// r160 draws each eye on its own) and the 300 minor moons. Measured in the
// emulator at Max with Jupiter in view: 577 draw calls and 4.3M triangles
// per eye before, 55 and 0.93M after; at Medium (what a Quest is given)
// 55-73 calls and 0.5-0.7M triangles per eye, inside a Quest 3's ~250
// calls and ~1M triangles per eye at 72 Hz. Each tier past 0 hides a layer more, and the
// next session's framebuffer scale drops with it. The session steps down
// on sustained dropped frames (xrQualityTick); the next one starts a tier
// lighter than where the last settled, so it probes again.
const XR_TIER_KEY = 'xr.tier';
const XR_FRAMEBUFFER_SCALE = [0.8, 0.7, 0.6];
const XR_TIER_LAYERS = [
  ['earthAuroras', 'jupiterAuroras', 'cometVolume', 'gargantua', 'gargantuaReturnWormhole', 'wormhole', 'debrisField', 'milkyWayDiscSlab'],
  ['asteroidBelt', 'dust-rings', 'deep-sky-objects', 'vanAllenBelts', 'zodiacalLight', 'comaCloud'],
  ['star-catalog-faint', 'constellations'],
];
// Passthrough has to show through: the sky shells and anything else out at
// the celestial sphere are hidden in AR.
const XR_SKY_LAYERS = ['skybox', 'star-catalog', 'star-catalog-faint', 'planet-points', 'constellations', 'deep-sky-objects'];
let xrTier = 0;
let xrExperience = null; // src/xr/experience.js while a session runs
let xrEarthCarve = false; // the cloud-top carve at Low quality in XR (measured below)
// The bodies the VR layer labels and picks: the Sun, the planets and their
// named moons (the minor ones are hidden in XR).
function xrBodyList() {
  const out = [];
  const add = (id, object, name, parentId) => { if (object) out.push({ id, name, object, radius: getBodyBaseRadius(object), parentId }); };
  add('sun', sun, 'Sun');
  for (const key of ['mercury', 'venus', 'earth', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune', 'pluto']) {
    const p = planets[key];
    if (!p) continue;
    add(key, p, (p.userData && p.userData.name) || key[0].toUpperCase() + key.slice(1));
    for (const m of moons[key] || []) {
      if (m.userData && m.userData.name && !m.userData.isMinorMoon) add(m.userData.name.toLowerCase(), m, m.userData.name, key);
    }
  }
  return out;
}
const xrHidden = new Map(); // object -> its visibility before the session
const xrPace = { start: 0, last: 0, nominal: 1000 / 72, frames: 0, misses: 0, badWindows: 0 };
function readXrTier() {
  try { return THREE.MathUtils.clamp(Number(localStorage.getItem(XR_TIER_KEY)) || 0, 0, XR_TIER_LAYERS.length - 1); } catch { return 0; }
}
// spaceAmbient.js exports isPlaying as a live flag.
const xrMusicPlaying = (m) => Boolean(m && (typeof m.isPlaying === 'function' ? m.isPlaying() : m.isPlaying));
function xrUnhide(obj) {
  if (!obj || !xrHidden.has(obj)) return;
  obj.visible = xrHidden.get(obj);
  xrHidden.delete(obj);
}
function xrHide(obj) {
  if (!obj || xrHidden.has(obj)) return;
  xrHidden.set(obj, obj.visible);
  obj.visible = false;
}
function setXrTier(tier) {
  xrTier = THREE.MathUtils.clamp(tier, 0, XR_TIER_LAYERS.length - 1);
  const names = new Set(XR_TIER_LAYERS.slice(0, xrTier + 1).flat());
  scene.traverse((o) => { if (names.has(o.name)) xrHide(o); });
  for (const list of Object.values(moons)) for (const m of list) if (m.userData && m.userData.isMinorMoon) xrHide(m);
  for (const s of satelliteObjects) xrHide(s.mesh);
  try { localStorage.setItem(XR_TIER_KEY, String(xrTier)); } catch { /* private window */ }
}
// The tier a session starts at: one lighter than where the last settled.
function xrStartTier() {
  return Math.max(0, readXrTier() - 1);
}
// Once per XR frame: more than one frame in ten past 1.5 frame intervals,
// two windows of 90 running, steps a tier down. The first 3 s (shader
// compiles, uploads) and pauses (headset off the face) do not count.
function xrQualityTick(now) {
  if (window.__xrHoldTier) return; // captures: the emulator drops frames while it records
  const dt = xrPace.last ? now - xrPace.last : 0;
  xrPace.last = now;
  if (!dt || dt > 250 || now - xrPace.start < 3000) return;
  xrPace.frames++;
  if (dt > xrPace.nominal * 1.5) xrPace.misses++;
  if (xrPace.frames < 90) return;
  xrPace.badWindows = xrPace.misses > xrPace.frames * 0.1 ? xrPace.badWindows + 1 : 0;
  xrPace.frames = 0;
  xrPace.misses = 0;
  if (xrPace.badWindows >= 2 && xrTier < XR_TIER_LAYERS.length - 1) {
    xrPace.badWindows = 0;
    setXrTier(xrTier + 1);
  }
}
// For checks on a headset (remote devtools) and the emulator harness.
if (typeof window !== 'undefined') {
  window.__xrQuality = {
    get tier() { return xrTier; },
    setTier: (t) => setXrTier(t),
    get pace() { return { ...xrPace }; },
    get hidden() { return xrHidden.size; },
    get experience() { return xrExperience; },
    set earthCarve(on) { xrEarthCarve = !!on; },
    get shiftExtras() { return { vec: _xrShiftVec.length, mat: _xrShiftMat.length }; },
    catalogDump: () => xrCatalog().map((s) => ({ title: s.title, items: s.items.map((i) => i.label) })),
  };
}

// The body the session centres on: the focused planet, moon or Sun; a
// satellite's planet (the ISS is micrometres across at any XR scale).
// Holding the solar system, the headset owns the camera; in space and the
// Cupola the 2D camera keeps running and the head rides it.
function xrHoldsCamera() {
  return Boolean(renderer && renderer.xr.isPresenting && xrExperience && xrExperience.mode === 'hold');
}

function xrFocusBody() {
  const ud = selectedObject && selectedObject.userData;
  if (!ud) return null;
  if (selectedObject === sun || ud.isPlanet || ud.isMoon) return selectedObject;
  if (ud.isSatellite && planets.earth) return ud.atL2 ? null : planets.earth;
  return null;
}

function enterXrPresentation() {
  if (xrSavedSceneState.active) return;
  xrSavedSceneState.active = true;
  xrSavedSceneState.scale.copy(scene.scale);
  xrSavedSceneState.position.copy(scene.position);
  xrSavedSceneState.rotation.copy(scene.rotation);
  xrSavedSceneState.cameraNear = camera.near;
  xrSavedSceneState.cameraFar = camera.far;
  // three writes the headset's fov into the user camera every XR frame.
  xrSavedSceneState.cameraFov = camera.fov;
  xrSavedSceneState.cameraZoom = camera.zoom;
  xrSavedSceneState.cameraUp.copy(camera.up);
  xrSavedSceneState.realistic = realisticScaleMode;

  const session = renderer.xr.getSession();
  const isAR = session && session.environmentBlendMode !== 'opaque';

  // The quality tier, and in AR the sky taken away so the room shows.
  xrHidden.clear();
  setXrTier(xrStartTier());
  if (isAR) scene.traverse((o) => { if (XR_SKY_LAYERS.includes(o.name) || /^(milkyWay|galaxy)/.test(o.name)) xrHide(o); });
  renderer.xr.setFoveation(1);
  const rate = session && session.frameRate;
  Object.assign(xrPace, { start: performance.now(), last: 0, frames: 0, misses: 0, badWindows: 0, nominal: 1000 / (rate > 0 ? rate : 72) });

  // In space at true scale: the head rides the 2D camera, which starts in
  // low Earth orbit (src/xr/experience.js draws it, from black until then).
  // The Quest app's session can begin while the page is still building the
  // scene: until it is ready the headset shows black (renderFrame).
  xrViewerReady().then(() => {
    if (!xrSavedSceneState.active) return null;
    if (!realisticScaleMode && realisticScaleToggle) realisticScaleToggle.click(); // the 2D toggle: true scale
    return xrLoadExperience();
  }).then((mod) => {
    if (!mod) return;
    const { createXrExperience } = mod;
    if (!xrSavedSceneState.active || xrExperience) return;
    const u = cinematicPass && cinematicPass.uniforms;
    xrExperience = createXrExperience({
      renderer, scene, camera, sun,
      grade: {
        exposure: u && u.exposure ? u.exposure.value : 0.82,
        blackPoint: u && u.photoBlackPoint ? u.photoBlackPoint.value : 0,
        contrast: u && u.contrast ? u.contrast.value : 1.07,
        saturation: u && u.saturation ? u.saturation.value : 1.03,
      },
      bodies: xrBodyList,
      isHidden: (o) => { for (let p = o; p; p = p.parent) if (!p.visible) return true; return false; },
      api: xrApi(isAR),
    });
    xrExperience.holdBlack(xrStartInOrbit().catch((err) => console.error('[xr] orbit start failed', err)));
    // The ambient score, quiet, from the press that started the session.
    loadSpaceAmbientModule().then((m) => {
      xrSavedSceneState.musicWas = xrMusicPlaying(m);
      if (!xrSavedSceneState.musicWas) startAmbient().catch(() => {});
      if (m.setVolume) m.setVolume(0.35);
    }).catch(() => {});
  }).catch((err) => console.error('[xr] the VR layer did not load', err));
}

function exitXrPresentation() {
  if (!xrSavedSceneState.active) return;
  xrSavedSceneState.active = false;
  if (xrExperience) { xrExperience.dispose(); xrExperience = null; }
  if (flyMode) exitFlyMode(); // a VR flight ends with the session
  // The score back to its 2D level, and off again if it was off before.
  if (spaceAmbientModule) {
    if (spaceAmbientModule.setVolume) spaceAmbientModule.setVolume(1);
    if (xrSavedSceneState.musicWas === false && xrMusicPlaying(spaceAmbientModule)) stopAmbient().catch(() => {});
  }
  if (cupolaView && cupolaView.active) cupolaView.abort ? cupolaView.abort() : cupolaView.leave();
  scene.scale.copy(xrSavedSceneState.scale);
  scene.position.copy(xrSavedSceneState.position);
  scene.rotation.copy(xrSavedSceneState.rotation);
  // The 2D camera stayed the truth all session (the head rode it), so it
  // stays where the travel left it; only the lens comes back.
  camera.fov = xrSavedSceneState.cameraFov;
  camera.zoom = xrSavedSceneState.cameraZoom;
  camera.updateProjectionMatrix();
  for (const [obj, visible] of xrHidden) obj.visible = visible;
  xrHidden.clear();
  if (realisticScaleMode !== xrSavedSceneState.realistic && realisticScaleToggle) realisticScaleToggle.click();
  // For the next session (it is made with the session's layer).
  renderer.xr.setFramebufferScaleFactor(XR_FRAMEBUFFER_SCALE[xrStartTier()]);
}

// Loaded once, ahead of the first session where VR is unlocked.
let xrExperienceModule = null;
function xrLoadExperience() {
  if (!xrExperienceModule) xrExperienceModule = import('./xr/experience.js');
  return xrExperienceModule;
}

// The viewer's first frames: a session that starts while the page is still
// loading (the Quest app goes straight in) waits for the scene and the
// loader to finish and the opening camera move to begin, so that move does
// not land on top of the orbit start.
function xrViewerReady() {
  if (loadingScreenDismissed && planets.earth && controls) return Promise.resolve();
  return new Promise((resolve) => {
    const t0 = performance.now();
    const check = () => {
      if (loadingScreenDismissed && planets.earth && controls) setTimeout(resolve, 2500);
      else if (performance.now() - t0 > 30000) resolve();
      else setTimeout(check, 100);
    };
    check();
  });
}

// Until the 2D view's camera move (focus, route, jump, the Cupola) lands.
function xrSettle(limitMs = 10000) {
  return new Promise((resolve) => {
    const t0 = performance.now();
    const check = () => ((!isAnimating && !starMapJumpInFlight && !wormholeTunnelInFlight) || performance.now() - t0 > limitMs ? resolve() : setTimeout(check, 50));
    check();
  });
}

// Low Earth orbit, 400 km up. By day (the default): over the afternoon side
// with the Sun 25 degrees up behind you, looking away from it along the
// ground toward the terminator, the lit Earth and its clouds below and the
// limb's blue line across. At dawn ('sunrise'): at the dawn line facing the
// Sun 4 degrees over the limb. The 2D camera goes there with the Earth
// focused, so the 2D follow carries it on with the Earth.
async function xrStartInOrbit(kind = 'day') {
  const earth = planets.earth;
  if (!earth || !controls) return;
  await xrViewerReady();
  await xrSettle(); // the scale switch's own camera move, if one is running
  if (selectedObject !== earth) { focusOnObject(earth, { interrupt: true, duration: 0.05 }); await xrSettle(); }
  const E = earth.getWorldPosition(new THREE.Vector3());
  const R = getBodyBaseRadius(earth);
  const h = 400 / 6371;
  const s = (sun ? sun.getWorldPosition(new THREE.Vector3()) : new THREE.Vector3()).sub(E).normalize();
  const t = new THREE.Vector3().crossVectors(s, new THREE.Vector3(0, 1, 0)).normalize();
  const deg = THREE.MathUtils.degToRad;
  let d;
  let f;
  let down;
  if (kind === 'sunrise') {
    const below = Math.acos(1 / (1 + h)) - deg(4);
    d = t.clone().multiplyScalar(Math.cos(below)).addScaledVector(s, -Math.sin(below)).normalize();
    f = s.clone().addScaledVector(d, -s.dot(d)).normalize();
    down = 0.2;
  } else {
    d = t.clone().multiplyScalar(Math.cos(deg(25))).addScaledVector(s, Math.sin(deg(25))).normalize();
    f = s.clone().addScaledVector(d, -s.dot(d)).normalize().negate();
    down = deg(16);
  }
  const P = E.clone().addScaledVector(d, R * (1 + h));
  const look = f.clone().multiplyScalar(Math.cos(down)).addScaledVector(d, -Math.sin(down)).normalize();
  camera.position.copy(P);
  camera.up.copy(d);
  controls.target.copy(P).addScaledVector(look, R * 1.5);
  camera.lookAt(controls.target);
  camera.updateMatrixWorld(true);
  _focusPrevObj = null;
}

// Where a VR arrival stands, after the 2D focus has landed: on the lit side
// (phase angle about 30 degrees, the Sun behind your shoulder), Jupiter and
// the other giants about half the view across, and Saturn from just under
// its rings at 1.6 radii, the B ring arching overhead, the globe ahead.
function xrArrivalFraming(obj) {
  const ud = obj && obj.userData;
  if (!ud || !controls || !(ud.isPlanet || ud.isMoon) || obj === sun) return;
  const C = obj.getWorldPosition(new THREE.Vector3());
  const R = getBodyBaseRadius(obj);
  if (!(R > 0)) return;
  const toSun = (sun ? sun.getWorldPosition(new THREE.Vector3()) : new THREE.Vector3()).sub(C).normalize();
  const name = String(ud.name || '').toLowerCase();
  let pos;
  let up = new THREE.Vector3(0, 1, 0);
  if (name === 'saturn') {
    const ring = obj.getObjectByName('ring');
    const n = ring ? new THREE.Vector3(0, 0, 1).applyQuaternion(ring.getWorldQuaternion(new THREE.Quaternion())).normalize() : up.clone();
    const hDir = toSun.clone().addScaledVector(n, -toSun.dot(n));
    if (hDir.lengthSq() < 1e-12) hDir.set(1, 0, 0).addScaledVector(n, -n.x);
    hDir.normalize();
    const below = toSun.dot(n) >= 0 ? -1 : 1; // the side the Sun lights from
    pos = C.clone().addScaledVector(hDir, R * 1.6).addScaledVector(n, below * R * 0.04);
    up = n.clone().multiplyScalar(-below);
  } else {
    const across = ud.isPlanet && R > getBodyBaseRadius(planets.earth) * 3 ? Math.sin(THREE.MathUtils.degToRad(25)) : null;
    const dist = across ? R / across : Math.max(camera.position.distanceTo(C), R * 2.5);
    const side = new THREE.Vector3().crossVectors(toSun, up);
    if (side.lengthSq() < 1e-12) side.set(1, 0, 0);
    const dir = toSun.clone().applyAxisAngle(up, THREE.MathUtils.degToRad(30)).normalize();
    pos = C.clone().addScaledVector(dir, dist);
  }
  camera.position.copy(pos);
  camera.up.copy(up);
  controls.target.copy(C);
  camera.lookAt(C);
  camera.updateMatrixWorld(true);
  _focusPrevObj = null;
}

// The hold mode's starting frame (reference space from scene): the body the
// 2D view is on, ahead at a size that reads as a world (XR_FOCUS), the rest
// round it at the same scale, seen from where the 2D camera sees it; in AR
// turned only about the vertical, so the ecliptic stays level with the table.
function xrHoldPlacement(out, isAR) {
  const body = xrFocusBody();
  const place = body === sun ? XR_FOCUS.sun : isAR ? XR_FOCUS.ar : XR_FOCUS.vr;
  const centre = _xrCentre;
  let k;
  if (body) {
    body.getWorldPosition(centre);
    k = place.radius / Math.max(getBodyBaseRadius(body), 1e-12);
  } else {
    centre.copy(controls ? controls.target : _xrLook.set(0, 0, 0));
    k = place.distance / Math.max(camera.position.distanceTo(centre), 1e-12);
  }
  const q = new THREE.Quaternion();
  if (isAR) {
    _xrLook.copy(centre).sub(camera.position);
    q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.atan2(_xrLook.x, -_xrLook.z));
  } else {
    _xrBasis.lookAt(camera.position, centre, camera.up);
    q.setFromRotationMatrix(_xrBasis).invert();
  }
  const p = new THREE.Vector3(0, -place.drop, -place.distance).sub(_xrLook.copy(centre).multiplyScalar(k).applyQuaternion(q));
  return out.compose(p, q, new THREE.Vector3(k, k, k));
}

// The 2D Objects browser's catalogue, each entry going where its button does.
// Not in the headset yet: the two black holes (their lens marches are 2D
// camera work: in the emulator the stellar one draws off-axis in one eye and
// Gargantua not at all, at 65 ms of GPU on an M-class Mac) and the wormhole
// (a cube render of the sky every frame). Back when they draw per eye
// within a Quest's budget.
const XR_HIDDEN_DESTINATIONS = new Set(['Black Hole', 'Gargantua', 'Wormhole']);
function xrCatalog() {
  return buildObjectBrowserCatalog().map((section) => ({
    title: section.title,
    items: section.items.filter((item) => !XR_HIDDEN_DESTINATIONS.has(item.label)).map((item) => ({
      label: item.label,
      detail: item.detail,
      cardId: String(item.label).toLowerCase(),
      go: () => xrBrowse(item),
    })),
  }));
}
async function xrBrowse(item) {
  // A shot of the same body would hold on: focusing what is focused moves
  // nothing, so the hold never sees the selection change.
  stopShotFlight();
  stopShot();
  if (cupolaView && cupolaView.active) { cupolaView.leave(); await xrSettle(); }
  if (item.systemJump) { jumpToSystem(item.systemJump); return; }
  if (item.lazy && !deepSpaceBodiesLoaded) ensureDeepSpaceBodies();
  let target = item.resolve();
  if (!target && item.lazy && !deepSpaceBodiesVisible) { setDeepSpaceBodiesVisible(true); target = item.resolve(); }
  if (!target) return;
  if (item.category === 'dso') focusOnDSO(target, { interrupt: true });
  else focusOnObject(target, { interrupt: true, duration: 0.05 });
  await xrSettle();
  if (item.category !== 'dso') xrArrivalFraming(target);
}
async function xrEnterCupola() {
  if (!cupolaView) return;
  stopShotFlight();
  stopShot();
  const iss = satelliteObjects.find((s) => s.mesh && s.mesh.userData && s.mesh.userData.name === 'ISS');
  if (!iss) return;
  if (selectedObject !== iss.mesh) { focusOnObject(iss.mesh, { interrupt: true, duration: 0.05 }); await xrSettle(); }
  if (!cupolaView.active) cupolaView.enter();
  const t0 = performance.now();
  while (cupolaView.active && cupolaView.phase !== 'inside' && performance.now() - t0 < 10000) await new Promise((r) => setTimeout(r, 50));
  // From inside, the station's big parts carry the view: its 40 largest
  // meshes stay, the other 320 hide while the session runs (draw calls).
  const parts = [];
  const scale = new THREE.Vector3();
  iss.mesh.traverse((o) => {
    if (!o.isMesh || !o.geometry) return;
    if (!o.geometry.boundingSphere) o.geometry.computeBoundingSphere();
    o.getWorldScale(scale);
    parts.push([o, o.geometry.boundingSphere.radius * Math.max(scale.x, scale.y, scale.z)]);
  });
  parts.sort((a, b) => b[1] - a[1]);
  for (const [o] of parts.slice(XR_ISS_PARTS)) xrHide(o);
}
const XR_ISS_PARTS = 40;
let xrShiftCollected = false;
// World positions the 2D shift never needed (it only shifts near the Earth):
// the black holes' centres and the stellar one's backdrop view.
const _xrShiftVec = [];
const _xrShiftMat = [];
const _xrShiftT = new THREE.Matrix4();
function xrCollectShiftExtras() {
  _xrShiftVec.length = 0;
  _xrShiftMat.length = 0;
  scene.traverse((o) => {
    for (const m of [].concat(o.material || [])) {
      const u = m && m.uniforms;
      if (!u) continue;
      if (u.uBhWorldPos && u.uBhWorldPos.value.isVector3 && !_xrShiftVec.includes(u.uBhWorldPos)) _xrShiftVec.push(u.uBhWorldPos);
      if (u.uBackdropView && u.uBackdropView.value.isMatrix4 && !_xrShiftMat.some((x) => x.u === u.uBackdropView)) _xrShiftMat.push({ u: u.uBackdropView, saved: new THREE.Matrix4() });
    }
  });
}
function xrApi(isAR) {
  return {
    unitsPerMetre: () => getBodyBaseRadius(planets.earth) / 6371000,
    catalog: xrCatalog,
    focus: async (object) => { stopShotFlight(); stopShot(); if (cupolaView && cupolaView.active) { cupolaView.leave(); await xrSettle(); } focusOnObject(object, { interrupt: true, duration: 0.05 }); await xrSettle(); xrArrivalFraming(object); },
    travelling: () => isAnimating || starMapJumpInFlight || wormholeTunnelInFlight || !!(cupolaView && cupolaView.active && cupolaView.phase !== 'inside'),
    startInOrbit: (kind) => { stopShotFlight(); stopShot(); return xrStartInOrbit(kind); },
    shot: (id) => startShot(id),
    flyToShot: (id) => flyToShot(id, { cut: true }),
    shotHeld: () => !!shotHold,
    shotFlightBlink: () => (shotFlight ? shotFlight.blink : 0),
    stopShot: () => { stopShotFlight(); stopShot(); },
    holdPlacement: (out) => xrHoldPlacement(out, isAR),
    cupola: {
      enter: xrEnterCupola,
      leave: () => { if (cupolaView && cupolaView.active) cupolaView.leave(); },
      active: () => !!(cupolaView && cupolaView.active),
      frame: () => (cupolaView && cupolaView.active && cupolaView.phase === 'inside' ? cupolaView.xr : null),
    },
    setTime: (speed) => { const b = document.querySelector(`.time-preset[data-speed="${speed}"]`); if (b) b.click(); },
    timeSpeed: () => (timeSpeed === 0 ? '0' : timePreset === 'real' ? 'real' : String(timePreset)),
    orbitsOn: () => orbitsVisible,
    toggleOrbits: () => { if (orbitsToggle) orbitsToggle.click(); },
    exit: () => {
      const s = renderer.xr.getSession();
      if (IS_QUEST_APP) xrQuestLeaving = true;
      if (s) s.end();
      // The Quest app has nothing to return to: Leave VR closes it.
      if (IS_QUEST_APP) setTimeout(() => window.close(), 400);
    },
    // The 2D fly mode, flown from the controllers: its physics, approach
    // cap, brake, flight assist and target lock (docs/fly-handling.md). The
    // craft model stays hidden (no licensed ships in the paid VR mode).
    fly: {
      active: () => flyMode,
      enter: () => { if (!flyMode) enterFlyMode(); if (playerShip) xrHide(playerShip); },
      exit: () => { if (flyMode) exitFlyMode(); if (playerShip) xrUnhide(playerShip); },
      keys: (k) => { if (flyMode) Object.assign(flyKeys, k); },
      turn: (yaw) => {
        if (!flyMode) return;
        flyEuler.y += yaw;
        flyTargetEuler.y += yaw;
        camera.quaternion.setFromEuler(flyEuler);
      },
      brake: () => engageBrakeLatch(),
      toggleAssist: () => setFlightAssist(!flightAssist),
      lock: (object) => setFlyTarget(object),
      hud: () => {
        const text = (id) => { const el = document.getElementById(id); return el ? el.textContent.trim() : ''; };
        const unit = document.querySelector('#hud-speed + .hud-unit');
        return { speed: `${text('hud-speed')} ${unit ? unit.textContent.trim() : ''}`.trim(), target: text('hud-target'), range: text('fh-range'), eta: text('fh-eta'), assist: flightAssist };
      },
    },
    // The 2D view's origin shift (applyOriginShift), round each XR frame.
    shift: {
      begin(v, collect) {
        if (collect || !xrShiftCollected) { collectShiftableUniforms(); xrCollectShiftExtras(); xrShiftCollected = true; }
        applyOriginShift(v);
        for (const u of _xrShiftVec) u.value.sub(v);
        _xrShiftT.makeTranslation(v.x, v.y, v.z);
        for (const m of _xrShiftMat) { m.saved.copy(m.u.value); m.u.value.multiply(_xrShiftT); }
        scene.position.sub(v);
      },
      end(v) {
        scene.position.add(v);
        unapplyOriginShift(v);
        for (const u of _xrShiftVec) u.value.add(v);
        for (const m of _xrShiftMat) m.u.value.copy(m.saved);
        scene.updateMatrixWorld(true);
      },
    },
  };
}

// Controls setup - optimized for smooth interactions
// OrbitControls takes its orbit axis from camera.up once, when it is built
// (world up), and only its lookAt reads camera.up afterwards. With the up on
// the ISS's local vertical a drag turned the camera about the ecliptic pole
// while the screen stayed level to the station: a sideways drag tipped the
// view over the station and an upward one ran into the ecliptic pole and
// stuck there. While that up is active each update runs in a frame where it
// is world up, so the drag turns about the station's vertical. Its 'change'
// event waits until the camera is back in the world frame.
const _orbitUpQuat = new THREE.Quaternion();
const _orbitUpOffset = new THREE.Vector3();
const _orbitUpSaved = new THREE.Vector3();
function orbitAboutCameraUp(baseUpdate, baseUp) {
  return function update(deltaTime) {
    if (!_issUpActive || camera.up.dot(baseUp) > 1 - 1e-12) return baseUpdate(deltaTime);
    _orbitUpQuat.setFromUnitVectors(_orbitUpSaved.copy(camera.up).normalize(), baseUp);
    camera.position.sub(controls.target).applyQuaternion(_orbitUpQuat).add(controls.target);
    camera.up.copy(baseUp);
    let changeEvent = null;
    controls.dispatchEvent = (event) => { changeEvent = event; };
    let changed;
    try {
      changed = baseUpdate(deltaTime);
    } finally {
      delete controls.dispatchEvent;
      camera.up.copy(_orbitUpSaved);
      _orbitUpOffset.copy(camera.position).sub(controls.target)
        .applyQuaternion(_orbitUpQuat.invert());
      camera.position.copy(controls.target).add(_orbitUpOffset);
      camera.lookAt(controls.target);
    }
    if (changeEvent) controls.dispatchEvent(changeEvent);
    return changed;
  };
}

function setupControls() {
  controls = new OrbitControls(camera, renderer.domElement);
  controls.update = orbitAboutCameraUp(controls.update, camera.up.clone());
  controls.enableDamping = true;
  controls.dampingFactor = 0.08; // Slightly higher for smoother stops
  controls.minDistance = 20;
  controls.maxDistance = 50000000; // Very far for 10,000x scale
  controls.enablePan = true;
  controls.autoRotate = false;
  controls.autoRotateSpeed = 0.2;
  // Screensaver: disable manual camera control entirely. The auto-tour
  // drives the camera via focusOnObject's GSAP tweens, not OrbitControls.
  if (IS_SCREENSAVER) {
    controls.enabled = false;
    controls.enablePan = false;
    controls.enableZoom = false;
    controls.enableRotate = false;
  }

  // Smoother rotation and zoom
  controls.rotateSpeed = 0.8;
  controls.zoomSpeed = 1.2;
  controls.panSpeed = 0.8;
  controls.screenSpacePanning = true;
  controls.mouseButtons = {
    LEFT: THREE.MOUSE.ROTATE,
    MIDDLE: THREE.MOUSE.PAN,
    RIGHT: THREE.MOUSE.PAN
  };

  // Wheel zoom eases in over a quarter second (src/ui/smoothwheel.js).
  controls.enableZoom = true;
  controls.zoomToCursor = false;
  smoothWheelZoom(controls);

  // Limit vertical rotation
  controls.maxPolarAngle = Math.PI * 0.85;
  controls.minPolarAngle = Math.PI * 0.15;

  // Touch gestures: one finger rotates, two fingers pinch-to-zoom and
  // pan (only zoom close to the Sun, see zoomOnlySunPinch). Three.js
  // defaults support touch already but we set them explicitly so mobile
  // behaviour is predictable.
  controls.touches = {
    ONE: THREE.TOUCH.ROTATE,
    TWO: THREE.TOUCH.DOLLY_PAN
  };
}

// Post-processing setup with optimized bloom
function setupPostProcessing() {
  cinematicPass = null;
  composerRenderPass = null;
  // HDR half-float targets are expensive on mobile GPUs and some iOS
  // Safari builds can't allocate them at all (WebGL context dies). When
  // bloom is disabled (Low preset, mobile default) we don't need HDR
  // precision, so fall back to the much cheaper 8-bit render target.
  const useHDR = !!graphicsConfig.bloom;
  const renderTargetParams = {
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    format: THREE.RGBAFormat,
    type: useHDR ? THREE.HalfFloatType : THREE.UnsignedByteType,
    samples: graphicsConfig.msaa
  };

  // Super-sampling multiplier: render to an internally larger target
  // and let the output pass downsample to the canvas. This is the only
  // thing that cleans up aliasing on bloom fringes and transparent
  // edges (atmosphere shells, rings, orbit lines) which MSAA can't
  // touch. Default 1 so nothing changes unless the user opts in via
  // the Advanced modal.
  const renderScale = Math.max(1, graphicsConfig.renderScale || 1);
  // composerW/H are the TRUE physical pixel dimensions of the internal
  // targets — used for the render-target allocation and the texel-size
  // uniforms below (which need real pixels). composer.setSize(), however,
  // multiplies whatever it's given by renderer.getPixelRatio() internally,
  // so it must receive CSS dimensions. Passing it composerW (already
  // ×pixelRatio) double-applied the ratio: on a 2× Retina display the HDR
  // targets ballooned to 4× the pixel count, blowing past Chrome's GPU
  // memory limit and rendering the whole canvas black (Safari's limits
  // absorbed it, so it only reproduced in Chrome). Shrinking the viewport
  // — DevTools open or browser zoom — made the oversized target fit again,
  // which is why those "fixed" it.
  const composerW = window.innerWidth * renderer.getPixelRatio() * renderScale;
  const composerH = window.innerHeight * renderer.getPixelRatio() * renderScale;
  const composerCssW = window.innerWidth * renderScale;
  const composerCssH = window.innerHeight * renderScale;

  composer = new EffectComposer(renderer, new THREE.WebGLRenderTarget(
    composerW,
    composerH,
    renderTargetParams
  ));
  composer.setSize(composerCssW, composerCssH);

  composerRenderPass = new RenderPass(scene, camera);
  composer.addPass(composerRenderPass);

  sunDiscBloomPass = null;
  bloomStabilizerPass = null;
  if (graphicsConfig.bloom) {
    bloomStabilizerPass = new ShaderPass(BloomStabilizerShader);
    bloomStabilizerPass.uniforms.texelSize.value.set(1 / composerW, 1 / composerH);
    composer.addPass(bloomStabilizerPass);
    // Bloom resolution must match the composer's internal target size,
    // including any super-sampling multiplier. Passing smaller
    // dimensions makes UnrealBloomPass build its mipmap chain at
    // sub-resolution and then upsample back — which introduces the
    // aliased bright fringes that only show on the lit side of
    // planets (because that's where bloom fires).
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(composerW, composerH),
      0.28,   // strength
      1.2,    // radius
      0.92    // threshold
    );
    composer.addPass(bloomPass);
    sunDiscBloomPass = createSunDiscBloomPass(bloomPass);
    composer.addPass(sunDiscBloomPass);
  }

  if (graphicsConfig.cinematic || graphicsConfig.autoExposure || graphicsConfig.lensFlare) {
    cinematicPass = new ShaderPass(CinematicShader);
    if (!graphicsConfig.cinematic) {
      cinematicPass.uniforms.vignetteIntensity.value = 0;
      cinematicPass.uniforms.vignetteSmoothness.value = 1;
      cinematicPass.uniforms.contrast.value = 1;
      cinematicPass.uniforms.saturation.value = 1;
      adaptiveContrast = 1;
      adaptiveSaturation = 1;
      adaptiveVignette = 0;
    }
    applyCinematicUniforms();
    composer.addPass(cinematicPass);
  }

  if (graphicsConfig.smaa) {
    composer.addPass(new SMAAPass(composerW, composerH));
  }

  if (graphicsConfig.fxaa) {
    const fxaaPass = new ShaderPass(FXAAShader);
    fxaaPass.uniforms['resolution'].value.set(1 / composerW, 1 / composerH);
    composer.addPass(fxaaPass);
  }

  composer.addPass(new OutputPass());
}

function isMiddleButtonEvent(e) {
  return e.button === 1 || ((e.buttons & 4) !== 0);
}

function handleOrbitMiddlePanStart(e) {
  if (!isMiddleButtonEvent(e)) return;
  if (!renderer || e.target !== renderer.domElement || flyMode) return;

  // Own the real scroll-wheel-click gesture on the canvas. Native
  // browser autoscroll can otherwise steal the exact button chord
  // before OrbitControls sees a usable pan drag.
  e.preventDefault();
  e.stopPropagation();

  if (
    orbitMiddlePanActive ||
    !controls ||
    !controls.enabled ||
    !controls.enablePan
  ) {
    return;
  }

  orbitMiddlePanActive = true;
  orbitMiddlePanPointerId = typeof e.pointerId === 'number' ? e.pointerId : null;
  lastMouseX = e.clientX;
  lastMouseY = e.clientY;
  renderer.domElement.style.cursor = 'grabbing';

  if (orbitMiddlePanPointerId != null && renderer.domElement.setPointerCapture) {
    try {
      renderer.domElement.setPointerCapture(orbitMiddlePanPointerId);
    } catch {
      // Some browsers reject capture if the pointer ended between
      // pointerdown and this handler; the window fallback still works.
    }
  }
}

function blockOrbitMiddleAuxClick(e) {
  if (!isMiddleButtonEvent(e)) return;
  if (!renderer || e.target !== renderer.domElement || flyMode) return;
  e.preventDefault();
  e.stopPropagation();
}

function panOrbitCameraByPixels(deltaX, deltaY) {
  if (!camera || !controls || !renderer) return;
  const element = renderer.domElement;
  const viewportHeight = element.clientHeight || window.innerHeight || 1;
  let worldPerPixel = 0;

  if (camera.isPerspectiveCamera) {
    const distance = Math.max(
      camera.position.distanceTo(controls.target),
      camera.near || 0.001
    );
    const fov = THREE.MathUtils.degToRad(camera.fov || 50);
    worldPerPixel = 2 * Math.tan(fov / 2) * distance / viewportHeight;
  } else if (camera.isOrthographicCamera) {
    const zoom = Math.max(camera.zoom || 1, 1e-6);
    worldPerPixel = (camera.top - camera.bottom) / zoom / viewportHeight;
  }

  if (!Number.isFinite(worldPerPixel) || worldPerPixel <= 0) return;

  const panScale = worldPerPixel * (controls.panSpeed ?? 1);
  camera.getWorldDirection(ORBIT_MIDDLE_PAN_VIEW).normalize();
  ORBIT_MIDDLE_PAN_RIGHT.crossVectors(ORBIT_MIDDLE_PAN_VIEW, camera.up).normalize();
  if (ORBIT_MIDDLE_PAN_RIGHT.lengthSq() < 1e-8) {
    ORBIT_MIDDLE_PAN_RIGHT.setFromMatrixColumn(camera.matrix, 0).normalize();
  }
  ORBIT_MIDDLE_PAN_UP.crossVectors(ORBIT_MIDDLE_PAN_RIGHT, ORBIT_MIDDLE_PAN_VIEW).normalize();

  ORBIT_MIDDLE_PAN_OFFSET
    .copy(ORBIT_MIDDLE_PAN_RIGHT)
    .multiplyScalar(-deltaX * panScale)
    .addScaledVector(ORBIT_MIDDLE_PAN_UP, deltaY * panScale);

  camera.position.add(ORBIT_MIDDLE_PAN_OFFSET);
  controls.target.add(ORBIT_MIDDLE_PAN_OFFSET);
  controls.update();
}

function applyOrbitMiddlePan(e) {
  if (!orbitMiddlePanActive) return;
  if (flyMode || !controls || !controls.enabled || !controls.enablePan) {
    endOrbitMiddlePan(e);
    return;
  }

  e.preventDefault();
  const deltaX = e.clientX - lastMouseX;
  const deltaY = e.clientY - lastMouseY;
  lastMouseX = e.clientX;
  lastMouseY = e.clientY;

  if (deltaX !== 0 || deltaY !== 0) {
    panOrbitCameraByPixels(deltaX, deltaY);
  }
}

function endOrbitMiddlePan(e) {
  if (!orbitMiddlePanActive) return;
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }

  const pointerId = orbitMiddlePanPointerId;
  orbitMiddlePanActive = false;
  orbitMiddlePanPointerId = null;
  if (renderer?.domElement) {
    renderer.domElement.style.cursor = '';
    if (pointerId != null && renderer.domElement.releasePointerCapture) {
      try {
        if (!renderer.domElement.hasPointerCapture || renderer.domElement.hasPointerCapture(pointerId)) {
          renderer.domElement.releasePointerCapture(pointerId);
        }
      } catch {
        // Pointer capture may already be gone after browser-level cancel.
      }
    }
  }
}

// Event listeners
function setupEventListeners() {
  // Replace native select popups with the HUD-styled custom dropdown
  // (touch devices keep the OS picker; see src/ui/dropdown.js).
  enhanceAllSelects();

  window.addEventListener('resize', onWindowResize);
  window.addEventListener('click', onMouseClick);
  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('hashchange', () => {
    syncFocusToHash({ interrupt: true, preservePanel: true, force: true });
  });
  backButton.addEventListener('click', returnToOverview);
  renderer.domElement.addEventListener('pointerdown', handleOrbitMiddlePanStart, { capture: true });
  renderer.domElement.addEventListener('mousedown', handleOrbitMiddlePanStart, { capture: true });
  renderer.domElement.addEventListener('auxclick', blockOrbitMiddleAuxClick, { capture: true });

  // Middle mouse button:
  //   - in fly mode: orbits the camera around the active craft
  //   - in orbit mode: scroll-wheel-click drag pans the view in
  //     screen space, handled explicitly on the canvas above.
  window.addEventListener('mousedown', (e) => {
    if (e.button === 0) {
      sceneClickStart = { x: e.clientX, y: e.clientY };
    }
    if (flyMode && isFlyUiInputLocked()) return;

    // Robust button detection. e.button reports the PRIMARY button of
    // the event, but on first interaction after entering fly mode some
    // pointer setups (Mac trackpad emulation, gesture-based middle
    // click, browser pointer-capture quirks) misreport this as 0 even
    // when the user pressed the wheel. We treat the event as a middle
    // click if EITHER signal indicates middle: e.button === 1, OR the
    // middle bit (4) is set in e.buttons. Same redundancy for right.
    const middlePressed = e.button === 1 || (e.buttons & 4) !== 0;
    const rightPressed = e.button === 2 || (e.buttons & 2) !== 0;
    const leftPressed = !middlePressed && !rightPressed && (e.button === 0 || (e.buttons & 1) !== 0);

    // Middle-click handling FIRST so a misreported button=0 with
    // buttons=4 is routed to orbit rather than left-click mouse-look.
    if (middlePressed) {
      if (flyMode && isFlyControlsEvent(e)) return;
      if (flyMode && playerShip) {
        e.preventDefault();
        isMiddleMouseDown = true;
        lastMouseX = e.clientX;
        lastMouseY = e.clientY;
        playerShip.getWorldPosition(ufoWorldPos);
        // Snapshot chase euler so chase-resume restores heading.
        _orbitSavedChaseEuler.copy(flyEuler);
        _orbitChaseEulerSaved = true;
        // Seed orbit spherical coords from current camera-to-ship.
        const profileMM = getActiveCraftProfile();
        const dxMM = camera.position.x - ufoWorldPos.x;
        const dyMM = camera.position.y - ufoWorldPos.y;
        const dzMM = camera.position.z - ufoWorldPos.z;
        const distMM = Math.hypot(dxMM, dyMM, dzMM);
        if (distMM > 1e-4) {
          cameraOrbitDistance = clampCraftOrbitDistance(distMM, profileMM);
          _cameraOrbitDistanceTarget = cameraOrbitDistance;
          cameraOrbitPitch = Math.asin(THREE.MathUtils.clamp(dyMM / distMM, -1, 1));
          cameraOrbitAngle = Math.atan2(dxMM, dzMM);
          targetOrbitAngle = cameraOrbitAngle;
          targetOrbitPitch = THREE.MathUtils.clamp(
            cameraOrbitPitch,
            -Math.PI * 0.4,
            Math.PI * 0.4
          );
        }
        cameraPanOffsetX = 0;
        cameraPanOffsetY = 0;
      }
      return;
    }

    if (flyMode && leftPressed && !isFlyControlsEvent(e)) {
      startFlyMouseLook(e);
      return;
    }
    if (flyMode && isFlyControlsEvent(e)) return;

    // Right-click in fly mode: while middle-click is held, this turns
    // the orbit-pan into a TRANSLATE-pan (slide camera laterally to
    // offset the ship in frame). Otherwise it's a no-op (context menu
    // is already suppressed).
    if (rightPressed && flyMode) {
      isRightMouseDown = true;
      lastMouseX = e.clientX;
      lastMouseY = e.clientY;
      e.preventDefault();
      return;
    }

    // The middle-pressed / right-pressed / left-pressed branches above
    // already handled the routing — no further button-specific logic
    // needed here.
  });

  window.addEventListener('mouseup', (e) => {
    if (orbitMiddlePanActive && (e.button === 1 || (e.buttons & 4) === 0)) {
      endOrbitMiddlePan(e);
      return;
    }
    if (flyMode && isFlyUiInputLocked()) {
      if (flyMouseLookActive) endFlyMouseLook();
      if (isMiddleMouseDown) isMiddleMouseDown = false;
      return;
    }
    if (e.button === 0 && flyMouseLookActive) {
      endFlyMouseLook();
      // A left press that did not drag is a click: pick a body.
      if (flyMode && isScenePickEvent(e) && !sceneClickMoved(e)) flyPickBody(e);
      return;
    }
    if (e.button === 2 && isRightMouseDown) {
      isRightMouseDown = false;
      return;
    }
    if (e.button !== 1) return;
    if (isMiddleMouseDown) {
      isMiddleMouseDown = false;
      if (flyMode) parkFlyOrbitCamera();
    }
  });

  window.addEventListener('pointermove', (e) => {
    if (!orbitMiddlePanActive || orbitMiddlePanPointerId == null) return;
    applyOrbitMiddlePan(e);
  });

  window.addEventListener('pointerup', (e) => {
    if (!orbitMiddlePanActive || orbitMiddlePanPointerId == null) return;
    if (e.pointerId !== orbitMiddlePanPointerId) return;
    endOrbitMiddlePan(e);
  });

  window.addEventListener('pointercancel', (e) => {
    if (!orbitMiddlePanActive || orbitMiddlePanPointerId == null) return;
    if (e.pointerId !== orbitMiddlePanPointerId) return;
    endOrbitMiddlePan(e);
  });

  window.addEventListener('mousemove', (e) => {
    if (flyMode && isFlyUiInputLocked()) return;
    if (orbitMiddlePanActive && orbitMiddlePanPointerId == null) {
      applyOrbitMiddlePan(e);
      return;
    }
    if (flyMouseLookActive) {
      applyFlyMouseLook(e);
      return;
    }
    if (isMiddleMouseDown && flyMode && playerShip) {
      const deltaX = e.clientX - lastMouseX;
      const deltaY = e.clientY - lastMouseY;
      // Middle + right held → translate the orbit anchor laterally so
      // the user can compose offset shots. Otherwise (middle only)
      // the drag rotates the orbit angle/pitch.
      if (isRightMouseDown) {
        const k = cameraOrbitDistance * 0.0028;
        cameraPanOffsetX -= deltaX * k;
        cameraPanOffsetY += deltaY * k;
      } else {
        targetOrbitAngle += deltaX * 0.008;
        targetOrbitPitch = Math.max(-Math.PI * 0.4, Math.min(Math.PI * 0.4, targetOrbitPitch - deltaY * 0.008));
      }
      lastMouseX = e.clientX;
      lastMouseY = e.clientY;
      return;
    }
    // Right-click drag without middle = also pan, but only when parked
    // (so users can offset their screenshot framing). In active fly
    // mode without middle held, ignore so right-click drag doesn't
    // accidentally move the camera.
    if (isRightMouseDown && orbitParked && flyMode && playerShip) {
      const deltaX = e.clientX - lastMouseX;
      const deltaY = e.clientY - lastMouseY;
      const dist = camera.position.distanceTo(playerShip.position);
      const k = dist * 0.0028;
      cameraPanOffsetX -= deltaX * k;
      cameraPanOffsetY += deltaY * k;
      lastMouseX = e.clientX;
      lastMouseY = e.clientY;
      return;
    }
  });

  window.addEventListener('blur', () => {
    if (orbitMiddlePanActive) endOrbitMiddlePan();
    if (flyMouseLookActive) endFlyMouseLook();
    if (isMiddleMouseDown) {
      isMiddleMouseDown = false;
      // Park the camera on focus loss so chase mode doesn't suddenly
      // adopt the orbited heading next frame, and restore the saved
      // chase euler so any future thrust resumes the pre-orbit
      // direction (matches the deliberate mouseup → parkFlyOrbitCamera
      // flow).
      if (flyMode) parkFlyOrbitCamera();
    }
  });

  // Prevent context menu on middle click in either mode (some browsers
  // open the autoscroll affordance otherwise)
  window.addEventListener('contextmenu', (e) => {
    if (flyMode) e.preventDefault();
  });

  // Track shift key state for precision mode (scales slider motion down)
  shiftHeld = false;
  lastScalePos = parseFloat(scaleSlider.value);

  window.addEventListener('keydown', (e) => {
    if (e.key === 'Shift') shiftHeld = true;
    if (e.key === 'Escape' && qualityPicker && qualityPicker.classList.contains('open')) {
      closeQualityPicker();
      return;
    }
    if (e.key === 'Escape' && shareSheetOpen) {
      closeShareSheet();
      return;
    }
    if (e.key === 'Escape' && objectBrowserOpen) {
      closeObjectBrowser();
      return;
    }
    if (e.key === 'Escape' && spaceEventsOpen) {
      closeSpaceEvents();
      return;
    }
    if (isLocalEnvironment() && e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey && e.key.toLowerCase() === 'c') {
      e.preventDefault();
      runCoolWorldsShowreel();
      return;
    }
    // Scale mode: Left/Right (or A/D) step through the lineup, Home and
    // End jump to the first body and the overview, Escape exits. These
    // take precedence over everything else when active so they don't
    // conflict with matryoshka / fly / main nav.
    if (scaleModeActive && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const step = e.key === 'ArrowRight' || e.code === 'KeyD' ? 1
        : e.key === 'ArrowLeft' || e.code === 'KeyA' ? -1
        : 0;
      if (step) {
        e.preventDefault();
        scaleStep(step);
        return;
      }
      if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        scaleGoTo(e.key === 'Home' ? 0 : SCALE_OVERVIEW_INDEX);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        exitScaleMode();
        return;
      }
    }
    // Escape closes the matryoshka panel without changing camera focus.
    // Fly mode has its own Escape handler (exitFlyMode) so don't steal it.
    if (e.key === 'Escape' && !flyMode && matryoshka.isOpen()) {
      matryoshka.close();
    }
  });
  window.addEventListener('keyup', (e) => {
    if (e.key === 'Shift') shiftHeld = false;
  });

  // Sync initial displayed values with the curve
  scaleMultiplier = sliderToScale(lastScalePos);
  scaleValue.textContent = formatScale(scaleMultiplier);
  initTimePresets();
  if (!IS_QUEST_APP) initTips(); // nothing pops up in the Quest app
  // Curating live wallpaper shots (/viewer/?curate=1, src/ui/curate.js):
  // frame a world, capture it as a sun-relative tour scene, preview it as the
  // wallpaper plays it, export the list for src/screensaver/shots.js.
  // ?curate=1 (before or after the #) turns it on and it stays on in this
  // browser until the panel's Close.
  const curateAsked = /[?&]curate=1\b/.test(window.location.search + window.location.hash);
  let curateOn = curateAsked;
  try {
    if (curateAsked) localStorage.setItem('ss.curate', '1');
    curateOn = curateOn || localStorage.getItem('ss.curate') === '1';
  } catch { /* storage blocked */ }
  if (!IS_SCREENSAVER && curateOn) {
    Promise.all([import('./ui/curate.js'), import('./screensaver/tour.js')]).then(([{ initCurate }, { encodeShot }]) => initCurate({
      camera,
      focused: () => {
        const f = framedWorld();
        return f ? { ...f, name: realisticScaleMode ? f.name : `${f.name} (switch to true scale to see it as the wallpaper does)` } : null;
      },
      radiusOf: getBodyBaseRadius,
      groundFrame: (object) => { const q = groundFrameOf(object); return q ? q.clone() : null; },
      sunPosition: () => sun.getWorldPosition(new THREE.Vector3()),
      previewUrl: (shot) => `${window.location.pathname}?mode=screensaver&wallpaper=1&shot=${encodeShot(shot)}&pr=1&preset=high&fps=30`,
    })).catch((err) => console.warn('Curate panel failed:', err));
  }
  // "Make this view your Mac wallpaper" (src/ui/wallpapercta.js): after a
  // minute on the page, ready to offer the framing someone makes of a world.
  if (!IS_SCREENSAVER && !IS_QUEST_APP && !curateOn) {
    setTimeout(() => import('./ui/wallpapercta.js').then(({ initWallpaperCta }) => initWallpaperCta({
      camera,
      controls,
      canShow: () => !flyMode && !rocketryActive && !photoModeActive && !documentaryActive && !scaleModeActive && !lessonActive() && !astroModeActive()
        && !coolWorldsShowreelActive && !objectBrowserOpen && !spaceEventsOpen && !shareSheetOpen
        && !wormholeTunnelInFlight && !(cupolaView && cupolaView.active) && !eclipseModeActive() && !famousPhotoModeActive()
        && (!activeSystemId || activeSystemId === 'sol') && !document.querySelector('dialog[open]'),
      focused: framedWorld,
      radiusOf: getBodyBaseRadius,
      sunPosition: () => sun.getWorldPosition(new THREE.Vector3()),
      track: (event, params) => track(event, params),
      owns: () => fetch('/api/me', { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : null))
        .then((me) => !!(me && me.entitlements && me.entitlements.wallpaper))
        .catch(() => false),
    })).catch((err) => console.warn('Wallpaper offer failed:', err)), /[?&]offer=now\b/.test(window.location.search) ? 0 : 60000);
  }
  // "Never miss a big night in the sky" (src/ui/emailcapture.js): sky alerts
  // by email, offered once after a few minutes of use at a quiet moment in
  // the plain viewer. Never in the apps, the screensaver, ?mode=, ?app= or
  // ?stream= pages (the module's own rule, captureAllowedHere); one nudge a
  // session with the tips and the wallpaper card (src/ui/nudges.js).
  // The plain viewer with nothing open, for the offers below.
  const plainViewerQuiet = () => loadingScreenDismissed && !flyMode && !rocketryActive && !rocketryLoading && !photoModeActive && !documentaryActive
    && !scaleModeActive && !lessonActive() && !coolWorldsShowreelActive && !objectBrowserOpen && !spaceEventsOpen && !shareSheetOpen
    && !matryoshka.isOpen() && !wormholeTunnelInFlight && !(cupolaView && cupolaView.active) && !eclipseModeActive()
    && !famousPhotoModeActive() && !astroModeActive() && !(renderer && renderer.xr && renderer.xr.isPresenting)
    && (!activeSystemId || activeSystemId === 'sol') && !document.querySelector('dialog[open]')
    && !document.body.classList.contains('mobile-menu-open') && !document.body.classList.contains('iss-tracking');
  if (!IS_SCREENSAVER && !IS_IMMERSIVE && !IS_QUEST_APP && !IS_APP && !IS_GAME && !STREAM_MODE && !curateOn) {
    import('./ui/emailcapture.js').then(({ startEmailCapture }) => startEmailCapture({
      canShow: plainViewerQuiet,
    })).catch((err) => console.warn('Sky alerts offer failed:', err));
    // The control panel in the reader's own language (src/ui/langoffer.js),
    // offered once a few seconds in to a browser that asks for one of the
    // viewer's other languages.
    import('./ui/langoffer.js').then(({ initLanguageOffer }) => initLanguageOffer({
      canShow: plainViewerQuiet,
      track: (event, params) => track(event, params),
    })).catch((err) => console.warn('Language offer failed:', err));
    // No graphics card in use (src/softwaregpu.js): ask for hardware
    // acceleration (src/ui/gpunotice.js).
    if (SOFTWARE_RENDERING) {
      import('./ui/gpunotice.js').then(({ initGpuNotice }) => initGpuNotice({
        canShow: plainViewerQuiet,
        track: (event, params) => track(event, params),
      })).catch((err) => console.warn('GPU notice failed:', err));
    }
  }

  // Space weather (src/spaceweather/): the live aurora and Sun, the Explore
  // entry and its card, the storm banner, #aurora and #space-weather.
  startSpaceWeather();
  installSpaceWeatherDebug();
  if (!IS_SCREENSAVER) initSpacetimeButton();
  // A video render's shot holds spacetime mode (src/video/shotcam.js) when its
  // url asks for it (&spacetime); nothing else opens it in screensaver mode.
  else if (window.__vt && new URLSearchParams(window.location.search).has('spacetime')) loadSpacetimeMode();
  if (!IS_SCREENSAVER) initMagnetosphereButton();
  if (!IS_SCREENSAVER) spaceWeatherUI = initSpaceWeatherUI({
    showView: (v) => applyViewLink({ ...v, realistic: realisticScaleMode, spread: null, overlays: readViewOverlays() }),
    canShow: () => !flyMode && !scaleModeActive && !rocketryActive && (!activeSystemId || activeSystemId === 'sol'),
    showMagnetosphere: () => setMagnetosphereOn(true),
  });
  // Track the ISS: the panel's first-class entry, its live card and #iss
  // (src/ui/isstrack.js).
  if (!IS_SCREENSAVER || SCREENSAVER_ISS_SHOW) initIssTracking({
    getIss: () => findLoadedSceneObjectByName('iss'),
    getSatrec: () => issSatrec,
    getSimDate: () => new Date((simulationJD - 2440587.5) * 86400000),
    getSelected: () => selectedObject,
    isAnimating: () => isAnimating,
    getEarth: () => planets.earth || null,
    focus: (object, via) => focusOnObject(object, { interrupt: true, via }),
    isRealistic: () => realisticScaleMode,
    setRealistic: (on) => window.__setRealisticScaleMode && window.__setRealisticScaleMode(on),
    isRealTime: () => timePreset === 'real' && !clockAwayFromNow(),
    setRealTime: () => { if (timePreset !== 'real' || clockAwayFromNow()) setTimePreset('real'); },
  });
  // The mode that owns the view, if any: the climate card and the Sun's style
  // switch step aside for it.
  const viewModeOwner = () => (flyMode ? 'fly' : rocketryActive || rocketryLoading ? 'rocketry' : scaleModeActive ? 'to-scale'
    : lessonActive() ? 'lesson' : eclipseModeActive() ? 'eclipse' : documentaryActive ? 'documentary'
    : photoModeActive ? 'photo' : astroModeActive() ? 'astro' : (cupolaView && cupolaView.active) ? 'cupola' : null);
  // Climate mode: the /climate/ page's data on the Earth, from the Climate
  // button while the Earth is focused or a #climate link (src/climate/).
  if (!IS_SCREENSAVER && !IS_GAME) climateMode = initClimateMode({
    getEarthMesh: getEarthMeshForTerrain,
    getSunWorld: (out) => (sun ? sun.getWorldPosition(out) : null),
    getCamera: () => camera,
    getControls: () => controls,
    isEarthFocused: () => Boolean(planets.earth) && selectedObject === planets.earth && activeSystemId === 'sol',
    isAnimating: () => isAnimating,
    focusEarth: () => planets.earth && focusOnObject(planets.earth, { interrupt: true, via: 'climate' }),
    getSimDate: () => new Date((simulationJD - 2440587.5) * 86400000),
    asset: publicAssetUrl,
    phone: RUNNING_ON_MOBILE,
    blockedBy: viewModeOwner,
  });
  // The Cupola: inside the station looking down, from the ISS card or C
  // while the ISS is focused (src/ui/cupola.js). It owns the camera while
  // it is open and steps aside for any other mode.
  if ((!IS_SCREENSAVER || SCREENSAVER_ISS_SHOW) && !IS_GAME) cupolaView = createCupolaView({
    getCamera: () => camera,
    getControls: () => controls,
    getRenderer: () => renderer,
    getComposer: () => composer,
    getComposerRenderPass: () => composerRenderPass,
    getCinematicPass: () => cinematicPass,
    getIss: () => findLoadedSceneObjectByName('iss'),
    getEarth: () => planets.earth || null,
    getEarthRadius: () => (planets.earth ? getBodyBaseRadius(planets.earth) : 0),
    getSunPosition: (out) => (sun ? sun.getWorldPosition(out) : null),
    getSatrec: () => issSatrec,
    getSimDate: () => new Date((simulationJD - 2440587.5) * 86400000),
    modelUrl: publicAssetUrl('models/cupola-nasa.glb'),
    dracoUrl: publicAssetUrl('vendor/draco/gltf/'),
    isIssFocused: () => isIssObject(selectedObject) && !isAnimating,
    isRealistic: () => realisticScaleMode,
    setRealistic: (on) => window.__setRealisticScaleMode && window.__setRealisticScaleMode(on),
    blockedBy: () => (flyMode ? 'fly' : rocketryActive || rocketryLoading ? 'rocketry' : scaleModeActive ? 'to-scale'
      : lessonActive() ? 'lesson' : eclipseModeActive() ? 'eclipse' : documentaryActive ? 'documentary'
      : photoModeActive ? 'photo' : coolWorldsShowreelActive ? 'showreel' : activeSystemId !== 'sol' ? 'system'
      : renderer.xr.isPresenting && !xrExperience ? 'xr' : null), // the VR layer stands you in it
  });
  if (STREAM_MODE === 'cupola' && cupolaView) {
    import('./stream/cupolastream.js')
      .then((m) => m.startCupolaStream({
        getSatrec: () => issSatrec,
        getSimDate: () => new Date((simulationJD - 2440587.5) * 86400000),
      }))
      .catch((err) => console.warn('[stream] failed to start', err));
  }

  // Shaders and maps for what the scene can show, uploaded while the page is
  // quiet, so a camera move that brings a body into view for the first time
  // does not stall on it (src/gpuwarm.js). Desktop-class only.
  if (!IS_SCREENSAVER && !isMobileLikeDevice() && activeGraphicsPreset !== 'low') {
    startGpuWarmup({
      renderer,
      scene,
      camera,
      isBusy: () => !loadingScreenDismissed || isAnimating || flyMode || rocketryActive || rocketryLoading || astroModeActive()
        || Boolean(renderer.xr && renderer.xr.isPresenting),
    });
  }

  // The time machine under the presets and the share sheet's view link and
  // moment picture (src/ui/timemachine.js).
  if (!IS_SCREENSAVER) initTimeMachine({
    getJD: () => simulationJD,
    setJD: (jd) => {
      if (!Number.isFinite(jd)) return;
      simulationJD = jd;
      realClockLockedJD = null;
      setTimeout(syncTimeControls, 60);
    },
    getSpeedState: () => ({ preset: timePreset, speed: timeSpeed, reverse: timeReverse }),
    restoreSpeedState: (st) => {
      if (!st) return;
      timeReverse = st.reverse;
      timePreset = st.preset;
      timeSpeed = st.preset === 'real' ? timePresetSpeed('real') : st.speed;
      realClockLockedJD = null;
      syncTimeControls();
    },
    pause: () => { timePreset = 0; timeSpeed = 0; syncTimeControls(); },
    isReverse: () => timeReverse || timeSpeed < 0,
    setReverse: (on) => {
      timeReverse = on;
      if (timePreset === 'real' && on) timeSpeed = timePresetSpeed('real');
      if (timeSpeed !== 0) timeSpeed = (on ? -1 : 1) * Math.abs(timeSpeed);
      timePreset = null;
      syncTimeControls();
    },
    goNow: () => { timeReverse = false; setTimePreset('real'); },
    startEclipse: (event) => startEclipseMode(event),
    getViewState,
    getFocusId: () => (selectedObject ? getSceneObjectId(selectedObject) : 'sun'),
    getFocusName: () => {
      const body = selectedObject;
      if (!body) return 'The solar system';
      return (body.userData && body.userData.name) || body.name || 'The solar system';
    },
    getSatrec: () => issSatrec,
    getEarthPointBelowCamera: () => {
      const mesh = getEarthMeshForTerrain();
      if (!mesh || !camera) return null;
      mesh.updateWorldMatrix(true, false);
      const local = camera.position.clone().applyMatrix4(new THREE.Matrix4().copy(mesh.matrixWorld).invert()).normalize();
      const uv = earthSurfaceUV(local);
      return { lat: 90 - uv.y * 180, lon: uv.x * 360 - 180 };
    },
    // A frame drawn now, so the canvas holds this view when it is read.
    renderCanvas: () => { renderFrame(); return renderer.domElement; },
    track: (method) => track('share', { method, content_type: 'view', item_id: selectedObject ? getSceneObjectId(selectedObject) : 'sun' }),
  });

  scaleSlider.addEventListener('input', (e) => {
    let pos = parseFloat(e.target.value);
    if (shiftHeld) {
      pos = lastScalePos + (pos - lastScalePos) * 0.15;
      scaleSlider.value = pos;
    }
    lastScalePos = parseFloat(scaleSlider.value);
    scaleMultiplier = sliderToScale(lastScalePos);
    scaleValue.textContent = formatScale(scaleMultiplier);
    updateScale();
  });

  // Collapsible controls sidebar. On mobile it starts collapsed so the
  // canvas gets the full viewport; user taps the chevron to reveal.
  const controlsPanel = document.getElementById('controls-panel');
  const controlsToggle = document.getElementById('controls-toggle');
  const mobileMenuFab = document.getElementById('mobile-menu-fab');
  const mobilePanelBackdrop = document.getElementById('mobile-panel-backdrop');
  if (controlsPanel) {
    if (window.matchMedia(COMPACT_LAYOUT_QUERY).matches) {
      controlsPanel.classList.add('collapsed');
    }
    if (controlsToggle) {
      controlsToggle.addEventListener('click', () => {
        controlsPanel.classList.toggle('collapsed');
      });
    }
    // Mobile floating-menu button + backdrop → bottom-sheet pattern.
    const setMobileOpen = (open) => {
      if (open) {
        controlsPanel.classList.remove('collapsed');
        document.body.classList.add('mobile-menu-open');
        if (mobileMenuFab) mobileMenuFab.setAttribute('aria-expanded', 'true');
      } else {
        controlsPanel.classList.add('collapsed');
        document.body.classList.remove('mobile-menu-open');
        if (mobileMenuFab) mobileMenuFab.setAttribute('aria-expanded', 'false');
      }
    };
    if (mobileMenuFab) {
      mobileMenuFab.addEventListener('click', () => {
        const open = controlsPanel.classList.contains('collapsed');
        setMobileOpen(open);
      });
    }
    if (mobilePanelBackdrop) {
      mobilePanelBackdrop.addEventListener('click', () => setMobileOpen(false));
    }
    // A tablet turned across the line between the layouts: the phone layout
    // starts with the sheet shut, as at load, and the desktop one drops the
    // sheet's backdrop.
    window.matchMedia(COMPACT_LAYOUT_QUERY).addEventListener?.('change', (e) => {
      if (e.matches) { setMobileOpen(false); return; }
      document.body.classList.remove('mobile-menu-open');
      if (mobileMenuFab) mobileMenuFab.setAttribute('aria-expanded', 'false');
    });
    // Esc closes the sheet on mobile.
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && document.body.classList.contains('mobile-menu-open')) {
        setMobileOpen(false);
      }
    });
  }

  // Orbit lines toggle
  orbitsToggle.addEventListener('click', () => {
    orbitsVisible = !orbitsVisible;
    updateOrbitsVisibility();
    orbitsLabel.textContent = vt(orbitsVisible ? 'controls.orbits.on' : 'controls.orbits.off');
    if (orbitsVisible) {
      orbitsToggle.classList.add('playing');
    } else {
      orbitsToggle.classList.remove('playing');
    }
  });

  // Constellation lines toggle. Off by default; flipping shows the
  // classical IAU stick-figure overlay across the celestial sphere.
  if (constellationsToggle) {
    constellationsToggle.addEventListener('click', () => {
      if (!constellations) return;
      const next = !constellations.isVisible();
      constellations.setVisible(next);
      if (constellationsLabel) {
        constellationsLabel.textContent = vt(next ? 'controls.constellations.on' : 'controls.constellations.off');
      }
      constellationsToggle.classList.toggle('playing', next);
    });
  }

  // The Milky Way's band and dust as a long exposure records them; off, the
  // band is at its true brightness against the stars (createStarfield).
  const milkyWayToggle = document.getElementById('milkyway-toggle');
  const milkyWayLabel = document.getElementById('milkyway-label');
  const showMilkyWay = () => {
    if (milkyWayLabel) milkyWayLabel.textContent = vt(milkyWayBand ? 'controls.milkyWay.on' : 'controls.milkyWay.off');
    if (milkyWayToggle) milkyWayToggle.classList.toggle('playing', milkyWayBand);
  };
  if (milkyWayToggle) {
    showMilkyWay();
    milkyWayToggle.addEventListener('click', () => {
      milkyWayBand = !milkyWayBand;
      try { localStorage.setItem('ss.milkyWay', milkyWayBand ? '1' : '0'); } catch { /* storage blocked */ }
      showMilkyWay();
    });
  }

  // The asteroids lit by size (ASTEROID_LIT_OFFSET); off, at their real
  // brightness. One choice per scale, shown for the scale in use.
  const asteroidsToggle = document.getElementById('asteroids-toggle');
  const asteroidsLabel = document.getElementById('asteroids-label');
  showAsteroidsToggle = () => {
    const on = asteroidsLit();
    if (asteroidsLabel) asteroidsLabel.textContent = vt(on ? 'controls.asteroids.on' : 'controls.asteroids.off');
    if (asteroidsToggle) asteroidsToggle.classList.toggle('playing', on);
  };
  if (asteroidsToggle) {
    showAsteroidsToggle();
    asteroidsToggle.addEventListener('click', () => {
      const on = !asteroidsLit();
      if (realisticScaleMode) asteroidsLitReal = on; else asteroidsLitArtistic = on;
      try { localStorage.setItem(realisticScaleMode ? 'ss.asteroids.real' : 'ss.asteroids.artistic', on ? '1' : '0'); } catch { /* storage blocked */ }
      showAsteroidsToggle();
    });
  }

  // Sun visual style, Realistic or Detailed, set from the Graphics group's
  // Sun button and from the switch that shows close to the Sun
  // (src/ui/sunswitch.js). Drives the realisticSun uniform on the sun shader.
  function setSunStyle(realistic) {
    realisticSunMode = realistic;
    if (sunStyleLabel) {
      sunStyleLabel.textContent = vt(realistic ? 'controls.sunStyle.realistic' : 'controls.sunStyle.detailed');
    }
    if (sunStyleToggle) sunStyleToggle.classList.toggle('playing', realistic);
    if (sun) {
      const sunMesh = sun.children.find((child) =>
        child.isMesh && child.material && child.material.uniforms && child.material.uniforms.realisticSun
      );
      if (sunMesh) sunMesh.material.uniforms.realisticSun.value = realistic ? 1 : 0;
    }
    if (sunSwitch) sunSwitch.sync();
  }
  if (!IS_SCREENSAVER && !IS_GAME) sunSwitch = initSunSwitch({
    isRealistic: () => realisticSunMode,
    setRealistic: setSunStyle,
    blocked: () => activeSystemId !== 'sol' || Boolean(viewModeOwner()),
  });
  if (sunStyleToggle) {
    if (!realisticSunMode) setSunStyle(false);
    sunStyleToggle.addEventListener('click', () => {
      setSunStyle(!realisticSunMode);
    });
  }

  // Film grain toggle. Sets the cinematic shader's grainStrength
  // uniform to either the on-value (0.13, sensor-grain feel) or 0
  // (completely clean digital output). Default is ON since the
  // grain is part of the "space photograph" aesthetic we're chasing,
  // but provided as a toggle for users who prefer the cleaner look
  // or are taking screenshots.
  const grainToggle = document.getElementById('grain-toggle');
  const grainLabel = document.getElementById('grain-label');
  const GRAIN_ON_STRENGTH = 0.13;
  let grainEnabled = true;
  if (grainToggle) {
    grainToggle.addEventListener('click', () => {
      grainEnabled = !grainEnabled;
      if (grainLabel) {
        grainLabel.textContent = vt(grainEnabled ? 'controls.grain.on' : 'controls.grain.off');
      }
      grainToggle.classList.toggle('playing', grainEnabled);
      if (cinematicPass && cinematicPass.uniforms && cinematicPass.uniforms.grainStrength) {
        cinematicPass.uniforms.grainStrength.value = grainEnabled ? GRAIN_ON_STRENGTH : 0.0;
      }
    });
  }

  if (photoModeToggle) {
    photoModeToggle.addEventListener('click', () => {
      if (photoModeActive) exitPhotoMode();
      else enterPhotoMode();
    });
  }
  // Photo-mode entry from inside fly mode. The main #photo-mode-toggle
  // lives in #controls-panel which enterFlyMode() hides — without
  // this twin in the fly-controls panel there's no way to start photo
  // composition while flying, which is exactly when you'd want it.
  const flyPhotoBtn = document.getElementById('fly-photo-mode-btn');
  if (flyPhotoBtn) {
    flyPhotoBtn.addEventListener('click', () => {
      if (photoModeActive) exitPhotoMode();
      else enterPhotoMode();
    });
  }
  if (photoModeExitBtn) {
    photoModeExitBtn.addEventListener('click', exitPhotoMode);
  }

  // Photo mode's Wallpaper action: the same view rendered at the screen's
  // full resolution (saveWallpaper), next to Capture.
  if (saveWallpaperToggle) {
    let busy = false;
    saveWallpaperToggle.addEventListener('click', () => {
      if (busy) return;
      busy = true;
      saveWallpaperToggle.textContent = 'Saving…';
      // Defer a frame so the label paints before the (briefly blocking)
      // hi-res render runs.
      requestAnimationFrame(() => {
        saveWallpaper();
        saveWallpaperToggle.textContent = 'Saved';
        setTimeout(() => {
          saveWallpaperToggle.textContent = 'Wallpaper';
          busy = false;
        }, 1400);
      });
    });
  }

  setupPhotoModeControls();

  // Fly mode toggle
  flyToggle.addEventListener('click', () => {
    if (rocketryActive) exitRocketryMode();
    enterFlyModeSmoothly();
  });
  // Launches panel: the next launch and the ones just flown, in 3D.
  const launchesToggle = document.getElementById('launches-toggle');
  if (launchesToggle) launchesToggle.addEventListener('click', () => launchWatch().then((w) => w.togglePanel()));
  // Famous photos: the picker (src/photos, loaded on first use).
  const famousPhotosToggle = document.getElementById('famous-photos-toggle');
  if (famousPhotosToggle) {
    famousPhotosToggle.addEventListener('click', () => {
      startFamousPhoto(null).catch((err) => console.error('[photos] unavailable', err));
    });
  }
  // Astronomy: the real sky from where you stand (src/astro, loaded on first use).
  const astronomyToggle = document.getElementById('astronomy-toggle');
  if (astronomyToggle) {
    astronomyToggle.addEventListener('click', () => {
      openAstronomyMode().catch((err) => console.error('[astro] unavailable', err));
    });
  }
  // Teachers: the lesson picker (src/lessons, loaded on first use).
  const teachersToggle = document.getElementById('teachers-toggle');
  if (teachersToggle) {
    teachersToggle.addEventListener('click', () => {
      lessons().then((l) => l.openMenu()).catch((err) => console.error('[lessons] unavailable', err));
    });
  }
  // Rocketry mode toggle
  const rocketToggle = document.getElementById('rocket-toggle');
  if (rocketToggle) {
    rocketToggle.addEventListener('click', () => {
      if (rocketryActive) exitRocketryMode();
      else enterRocketryMode();
    });
  }
  if (alienHudFlyToggle) {
    alienHudFlyToggle.addEventListener('click', toggleAlienHud);
  }
  // Tip jar. The link itself lives in index.html; this only counts the click.
  document.querySelectorAll('[data-support-link]').forEach((el) => {
    el.addEventListener('click', () => track('support_click', { placement: el.dataset.supportLink || 'dock' }));
  });
  if (earthAtmosphereBtn) {
    earthAtmosphereBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      lockFlyUiInput();
      enterEarthAtmosphere();
    });
  }
  if (fastTravelBtn) {
    fastTravelBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      lockFlyUiInput();
      if (fastTravelMenu && fastTravelMenu.classList.contains('open')) {
        closeFastTravelMenu();
      } else {
        openFastTravelMenu();
      }
    });
  }
  if (fastTravelCloseBtn) {
    fastTravelCloseBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      lockFlyUiInput();
      closeFastTravelMenu();
    });
  }
  if (autopilotBtn) {
    autopilotBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      lockFlyUiInput();
      if (autopilotMenu && autopilotMenu.classList.contains('open')) {
        closeAutopilotMenu();
      } else {
        closeFastTravelMenu();
        openAutopilotMenu();
      }
    });
  }
  if (autopilotMenuClose) {
    autopilotMenuClose.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      lockFlyUiInput();
      closeAutopilotMenu();
    });
  }
  if (autopilotCancelBtn) {
    autopilotCancelBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      lockFlyUiInput();
      cancelAutopilot();
    });
  }

  [flyControlsOverlay, fastTravelMenu, autopilotMenu, autopilotStatus].forEach((el) => {
    if (!el) return;
    ['pointerdown', 'mousedown', 'mouseup', 'click'].forEach((type) => {
      el.addEventListener(type, (e) => {
        if (!isFlyControlsEvent(e)) return;
        lockFlyUiInput();
        e.stopPropagation();
      });
    });
  });

  if (scienceToggle) {
    scienceToggle.addEventListener('click', toggleScienceMode);
  }

  if (deepSpaceToggle) {
    deepSpaceToggle.addEventListener('click', toggleDeepSpaceBodies);
  }

  if (objectBrowserToggle) {
    objectBrowserToggle.addEventListener('click', toggleObjectBrowser);
  }
  if (objectBrowserClose) {
    objectBrowserClose.addEventListener('click', closeObjectBrowser);
  }
  if (objectBrowserSearch) {
    objectBrowserSearch.addEventListener('input', renderObjectBrowser);
  }
  initGoSearch({
    getCatalog: buildObjectBrowserCatalog,
    onPick: (item, query) => {
      closeObjectBrowser();
      goToCatalogItem(item, query);
    }
  });
  if (spaceEventsToggle) {
    spaceEventsToggle.addEventListener('click', toggleSpaceEvents);
  }
  if (spaceEventsClose) {
    spaceEventsClose.addEventListener('click', closeSpaceEvents);
  }
  if (coolWorldsSequenceToggle) {
    coolWorldsSequenceToggle.addEventListener('click', () => {
      // Toggle: clicking while running stops it instead of restarting,
      // so the user has a way out of the cinematic without waiting.
      if (coolWorldsShowreelActive) {
        clearCoolWorldsShowreel();
      } else {
        runCoolWorldsShowreel();
      }
    });
  }

  // Floating Exit Tour button shown while the WOW cinematic runs.
  const wowExitBtn = document.getElementById('wow-exit-btn');
  if (wowExitBtn) {
    wowExitBtn.addEventListener('click', () => {
      if (coolWorldsShowreelActive) clearCoolWorldsShowreel();
    });
  }
  if (coolWorldsSequenceModalToggle && isLocalEnvironment()) {
    coolWorldsSequenceModalToggle.addEventListener('click', () => {
      closeAdvancedGraphics();
      if (coolWorldsShowreelActive) {
        clearCoolWorldsShowreel();
      } else {
        runCoolWorldsShowreel();
      }
    });
  }
  if (shareToggle) {
    shareToggle.addEventListener('click', toggleShareSheet);
  }
  if (shareSheetClose) {
    shareSheetClose.addEventListener('click', closeShareSheet);
  }
  if (shareSheetCopyBtn) {
    shareSheetCopyBtn.addEventListener('click', copyShareLink);
  }
  [[shareSheetX, 'x'], [shareSheetBluesky, 'bluesky'], [shareSheetReddit, 'reddit'], [shareSheetFacebook, 'facebook']].forEach(([link, method]) => {
    if (link) link.addEventListener('click', () => trackShare(method));
  });
  if (shareSheet) {
    shareSheet.addEventListener('click', (e) => {
      if (e.target === shareSheet) closeShareSheet();
    });
  }

  // Documentary mode wiring
  const docToggle = document.getElementById('documentary-toggle');
  const docExit = document.getElementById('doc-exit');
  const docPlayPause = document.getElementById('doc-play-pause');
  if (docToggle) docToggle.addEventListener('click', enterDocumentary);
  if (docExit) docExit.addEventListener('click', exitDocumentary);
  if (docPlayPause) docPlayPause.addEventListener('click', toggleDocumentaryPause);

  // To-Scale mode wiring — builds a side-by-side lineup of planets at
  // true relative diameter and runs a cinematic dolly from smallest
  // to largest. Hands back to the main scene on exit.
  const scaleToggle = document.getElementById('scale-toggle');
  const scaleExit = document.getElementById('scale-exit');
  const scalePrev = document.getElementById('scale-prev');
  const scaleNext = document.getElementById('scale-next');
  if (scaleToggle) scaleToggle.addEventListener('click', () => enterScaleMode());
  if (scaleExit) scaleExit.addEventListener('click', exitScaleMode);
  if (scalePrev) scalePrev.addEventListener('click', () => scaleStep(-1));
  if (scaleNext) scaleNext.addEventListener('click', () => scaleStep(1));
  // A horizontal swipe or drag across the scene steps the lineup, the
  // way a carousel does: drag left to move on to the bigger bodies.
  let scaleSwipe = null;
  window.addEventListener('pointerdown', (e) => {
    scaleSwipe = scaleModeActive && !(e.target.closest && e.target.closest('button'))
      ? { x: e.clientX, y: e.clientY }
      : null;
  });
  window.addEventListener('pointerup', (e) => {
    if (!scaleSwipe || !scaleModeActive) return;
    const dx = e.clientX - scaleSwipe.x;
    const dy = e.clientY - scaleSwipe.y;
    scaleSwipe = null;
    if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.5) scaleStep(dx < 0 ? 1 : -1);
  });


  // Graphics quality preset popover — select a preset without using
  // the browser's native dropdown, which keeps the panel visually
  // consistent with the rest of the UI.
  if (qualityTrigger) {
    qualityTrigger.addEventListener('click', toggleQualityPicker);
    qualityTrigger.addEventListener('keydown', (e) => {
      if (!qualityPicker) return;
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!qualityPicker.classList.contains('open')) openQualityPicker();
        else focusQualityOption(activeGraphicsPreset);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (!qualityPicker.classList.contains('open')) openQualityPicker();
        requestAnimationFrame(() => {
          const options = qualityOptionButtons();
          options[options.length - 1]?.focus();
        });
      }
    });
  }
  if (qualityMenu) {
    qualityMenu.addEventListener('click', (e) => {
      const btn = e.target.closest('.quality-option');
      if (!btn) return;
      chooseQualityPreset(btn.dataset.preset);
    });
    qualityMenu.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        moveQualityFocus(1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        moveQualityFocus(-1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        qualityOptionButtons()[0]?.focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        const options = qualityOptionButtons();
        options[options.length - 1]?.focus();
      } else if (e.key === 'Enter' || e.key === ' ') {
        const btn = e.target.closest('.quality-option');
        if (!btn) return;
        e.preventDefault();
        chooseQualityPreset(btn.dataset.preset);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closeQualityPicker(true);
      }
    });
  }
  document.addEventListener('click', (e) => {
    if (!qualityPicker || !qualityPicker.classList.contains('open')) return;
    if (!qualityPicker.contains(e.target)) closeQualityPicker();
  });
  if (qualityPicker) {
    qualityPicker.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeQualityPicker();
    });
  }

  // Advanced graphics modal wiring.
  const advBtn = document.getElementById('advanced-graphics-toggle');
  const advClose = document.getElementById('graphics-modal-close');
  const advSave = document.getElementById('graphics-save');
  const advReset = document.getElementById('graphics-reset');
  const advModal = document.getElementById('graphics-modal');
  if (advBtn) advBtn.addEventListener('click', openAdvancedGraphics);
  if (advClose) advClose.addEventListener('click', closeAdvancedGraphics);
  if (advSave) advSave.addEventListener('click', saveAdvancedGraphics);
  if (advReset) advReset.addEventListener('click', resetAdvancedGraphics);
  if (advModal) {
    advModal.querySelectorAll('[data-close="1"]').forEach((el) =>
      el.addEventListener('click', closeAdvancedGraphics)
    );
  }
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && advModal && advModal.classList.contains('visible')) {
      closeAdvancedGraphics();
    }
  });

  exitFlyModeBtn.addEventListener('click', () => {
    exitFlyMode();
  });

  // Orbit stabilization button
  const stabilizeBtn = document.getElementById('stabilize-orbit');
  if (stabilizeBtn) {
    stabilizeBtn.addEventListener('click', () => {
      toggleOrbitStabilization();
    });
  }
  // Galaxy map (route plotting) and the flight-assist chip.
  const flyNavBtn = document.getElementById('fly-nav-btn');
  if (flyNavBtn) flyNavBtn.addEventListener('click', () => toggleGalaxyMap());
  const faChip = document.getElementById('fh-fa');
  if (faChip) faChip.addEventListener('click', () => setFlightAssist(!flightAssist));

  // Fly mode keyboard controls
  window.addEventListener('keydown', (e) => {
    if (!flyMode) return;
    // Don't capture vehicle controls when a system modifier is held
    // (CMD/Ctrl/Alt). Otherwise CMD+W tries to thrust forward AND
    // close the tab, CMD+Tab leaves keys "stuck on" because keyup
    // never fires while focus is gone, and Ctrl-shortcuts would
    // unexpectedly steer the ship.
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    // With the galaxy map open the map owns the keyboard; M or Escape
    // close it and nothing steers the ship.
    if (navigation && navigation.isMapOpen()) {
      if (e.code === 'KeyM' || e.key === 'Escape') {
        navigation.closeMap();
        e.preventDefault();
      }
      return;
    }
    switch (e.code) {
      case 'KeyW': flyKeys.forward = true; break;
      case 'KeyS': flyKeys.backward = true; break;
      case 'KeyA': flyKeys.left = true; break;
      case 'KeyD': flyKeys.right = true; break;
      case 'Space': flyKeys.up = true; e.preventDefault(); break;
      // Down was bound to Shift but Shift is a system modifier on
      // macOS screenshot shortcuts (Cmd+Shift+3/4/5) — pressing it
      // standalone was firing flyKeys.down before the user could
      // chord it with Cmd. Remapping to KeyC (the classic "crouch"
      // key) frees Shift entirely so the OS screenshot tools work
      // without the ship lurching downward.
      case 'KeyC': flyKeys.down = true; break;
      case 'ArrowLeft':
      case 'KeyQ': flyKeys.rotateLeft = true; e.preventDefault(); break;
      case 'ArrowRight':
      case 'KeyE': flyKeys.rotateRight = true; e.preventDefault(); break;
      case 'ArrowUp': flyKeys.rotateUp = true; e.preventDefault(); break;
      case 'ArrowDown': flyKeys.rotateDown = true; e.preventDefault(); break;
      case 'KeyX':
        // Emergency full stop: drop both linear and angular velocity
        // to zero so the ship holds station instead of drifting. S is
        // the flown brake; this is the instant one.
        flyFullStop();
        e.preventDefault();
        break;
      case 'KeyT':
        if (e.shiftKey) break; // Shift+T is the tuner's cinematic tab
        toggleFlyTarget();
        e.preventDefault();
        break;
      case 'KeyZ':
        if (e.shiftKey) break;
        setFlightAssist(!flightAssist);
        e.preventDefault();
        break;
      case 'KeyM':
        if (e.shiftKey) break;
        toggleGalaxyMap();
        e.preventDefault();
        break;
      case 'KeyJ':
        if (e.shiftKey) break;
        engageNextJump();
        e.preventDefault();
        break;
      case 'Escape':
        if (autopilotMenu && autopilotMenu.classList.contains('open')) {
          closeAutopilotMenu();
        } else if (autopilot.active) {
          cancelAutopilot();
        } else {
          exitFlyMode();
          // GAME MODE: ESC from free flight docks back at the hangar
          // instead of the website's orbit-overview return.
          if (IS_GAME && typeof window.__gameReturnToHangar === 'function') {
            window.__gameReturnToHangar();
          }
        }
        break;
    }
  });

  window.addEventListener('keyup', (e) => {
    if (!flyMode) return;
    // Mirror the keydown gate so a held modifier doesn't lock keys on.
    if (e.metaKey || e.ctrlKey || e.altKey) {
      // But still release any keys that might have been pressed
      // moments before the modifier engaged — defensively reset all
      // movement flags so the ship doesn't drift.
      flyKeys.forward = flyKeys.backward = flyKeys.left = flyKeys.right
        = flyKeys.up = flyKeys.down = flyKeys.rotateLeft = flyKeys.rotateRight
        = flyKeys.rotateUp = flyKeys.rotateDown = false;
      return;
    }
    switch (e.code) {
      case 'KeyW': flyKeys.forward = false; break;
      case 'KeyS': flyKeys.backward = false; break;
      case 'KeyA': flyKeys.left = false; break;
      case 'KeyD': flyKeys.right = false; break;
      case 'Space': flyKeys.up = false; break;
      case 'KeyC': flyKeys.down = false; break;
      case 'ArrowLeft':
      case 'KeyQ': flyKeys.rotateLeft = false; break;
      case 'ArrowRight':
      case 'KeyE': flyKeys.rotateRight = false; break;
      case 'ArrowUp': flyKeys.rotateUp = false; break;
      case 'ArrowDown': flyKeys.rotateDown = false; break;
    }
  });

  // ---- Mobile fly-mode controls ----
  // Two virtual analog joysticks (translation + rotation) and a small
  // button cluster (UP, DOWN, BRAKE, EXIT). Each touch input maps to
  // the same flyKeys flags the keyboard sets, so the rest of the
  // simulation logic doesn't need to know mobile exists.
  (function initMobileFlyControls() {
    const overlay = document.getElementById('mobile-fly-controls');
    if (!overlay) return;

    function makeStick(stickEl, onUpdate) {
      if (!stickEl) return;
      const knob = stickEl.querySelector('.mfly-stick-knob');
      let activeId = null;
      let cx = 0, cy = 0;
      const MAX_DIST = 50;

      function snapTo(touch) {
        const r = stickEl.getBoundingClientRect();
        cx = r.left + r.width / 2;
        cy = r.top + r.height / 2;
        update(touch);
      }
      function update(touch) {
        const dx = touch.clientX - cx;
        const dy = touch.clientY - cy;
        const dist = Math.hypot(dx, dy);
        const clamped = Math.min(MAX_DIST, dist);
        const ang = Math.atan2(dy, dx);
        const kx = Math.cos(ang) * clamped;
        const ky = Math.sin(ang) * clamped;
        knob.style.transform = `translate(${kx}px, ${ky}px)`;
        onUpdate(kx / MAX_DIST, ky / MAX_DIST);
      }
      function reset() {
        knob.style.transform = 'translate(0, 0)';
        onUpdate(0, 0);
        stickEl.classList.remove('active');
      }
      stickEl.addEventListener('touchstart', (e) => {
        if (activeId !== null) return;
        const t = e.changedTouches[0];
        activeId = t.identifier;
        stickEl.classList.add('active');
        snapTo(t);
        e.preventDefault();
      }, { passive: false });
      stickEl.addEventListener('touchmove', (e) => {
        for (const t of e.changedTouches) {
          if (t.identifier !== activeId) continue;
          update(t);
          e.preventDefault();
          break;
        }
      }, { passive: false });
      function handleEnd(e) {
        for (const t of e.changedTouches) {
          if (t.identifier !== activeId) continue;
          activeId = null;
          reset();
          break;
        }
      }
      stickEl.addEventListener('touchend', handleEnd);
      stickEl.addEventListener('touchcancel', handleEnd);
    }

    const DZ = 0.3; // deadzone (avoids accidental drift on light touches)

    makeStick(document.getElementById('mfly-stick-left'), (nx, ny) => {
      flyKeys.forward  = ny < -DZ;
      flyKeys.backward = ny >  DZ;
      flyKeys.left     = nx < -DZ;
      flyKeys.right    = nx >  DZ;
    });

    makeStick(document.getElementById('mfly-stick-right'), (nx, ny) => {
      flyKeys.rotateUp    = ny < -DZ;
      flyKeys.rotateDown  = ny >  DZ;
      flyKeys.rotateLeft  = nx < -DZ;
      flyKeys.rotateRight = nx >  DZ;
    });

    function makeHoldButton(id, key) {
      const btn = document.getElementById(id);
      if (!btn) return;
      const press = (e) => {
        flyKeys[key] = true;
        btn.classList.add('pressed');
        e.preventDefault();
      };
      const release = () => {
        flyKeys[key] = false;
        btn.classList.remove('pressed');
      };
      btn.addEventListener('touchstart', press, { passive: false });
      btn.addEventListener('touchend', release);
      btn.addEventListener('touchcancel', release);
      // Mouse fallback for desktop testing
      btn.addEventListener('mousedown', press);
      window.addEventListener('mouseup', release);
    }
    makeHoldButton('mfly-up', 'up');
    makeHoldButton('mfly-down', 'down');

    const brakeBtn = document.getElementById('mfly-brake');
    if (brakeBtn) {
      // A tap latches the brake on: the ship sheds its whole velocity
      // within FLY_BRAKE_STOP_TIME and the latch releases at rest, so
      // it reads as a brake rather than a teleport to zero.
      brakeBtn.addEventListener('click', () => engageBrakeLatch());
    }
    const exitBtn = document.getElementById('mfly-exit');
    if (exitBtn) {
      exitBtn.addEventListener('click', () => exitFlyMode());
    }
  })();

  // Fly mode wheel — plain scroll zooms (universal chase-cam
  // expectation); shift+scroll adjusts thrust speed.
  //   chasing  → adjust profile.followDistance
  //   orbiting → adjust cameraOrbitDistance
  //   parked   → slide camera along its current ship-relative axis
  //              so zoom works for the screenshot framing without
  //              fighting mouse-look the user did to compose the angle
  window.addEventListener('wheel', (e) => {
    if (!flyMode) return;
    // Don't hijack the wheel when the cursor is over a fly-mode UI
    // panel (autopilot menu, fast-travel list, craft-admin, etc.).
    // Without this, scrolling inside the autopilot destination list
    // also adjusts the chase-camera follow distance, which the user
    // reads as "scroll in the menu is changing my ship view."
    if (isFlyControlsEvent(e)) return;

    if (e.shiftKey) {
      const prop = propulsionTypes[currentPropulsion];
      flySpeed = Math.max(0.1, Math.min(prop.maxSpeed, flySpeed - e.deltaY * 0.05));
      return;
    }

    const profile = getActiveCraftProfile();
    const min = profile.minOrbitDistance != null ? profile.minOrbitDistance : 1;
    const max = profile.maxOrbitDistance != null ? profile.maxOrbitDistance : 20;

    // Wheel writes to TARGET values; the animation loop's smoothing
    // pass lerps the live values toward them, so a quick scroll
    // tick produces a smooth dolly rather than a discrete step.
    if (isMiddleMouseDown) {
      _cameraOrbitDistanceTarget = clampCraftOrbitDistance(
        _cameraOrbitDistanceTarget + e.deltaY * 0.006 * atmoCraftScaleLive
      );
    } else {
      // Forward dolly = zoom in/out along the view direction. Scroll up
      // (deltaY < 0) dollies forward (zoom in), scroll down pulls back.
      // Previously the wheel scaled the camera→ship offset, which carried
      // the chase-cam's vertical followY component, so zooming drifted up
      // and down instead of moving straight toward what you're looking at.
      _flyDollyPending += -e.deltaY * 0.0016;
    }
  });

  // Propulsion type change
  if (propulsionSelect) {
    const handlePropulsionChange = (e) => {
      const nextKey = e.target.value;
      if (!propulsionTypes[nextKey]) return;
      currentPropulsion = nextKey;
      const prop = propulsionTypes[currentPropulsion];
      flySpeed = Math.min(flySpeed, prop.maxSpeed);
      // Clamp actual velocity too. Without this, a warp → impulse
      // switch leaves the ship coasting at warp-speed flyVelocity
      // for several seconds while the brake catches up — which
      // visually reads as "warp didn't actually disengage" because
      // the camera is still streaming past stars at 1000+ u/s.
      const speed = flyVelocity.length();
      if (speed > prop.maxSpeed) {
        flyVelocity.multiplyScalar(prop.maxSpeed / speed);
      }
    };
    propulsionSelect.addEventListener('change', handlePropulsionChange);
    // Some pointer / mobile-browser stacks dispatch 'input' but not
    // 'change' on the first selection. Wire both so the second pick
    // after a craft refresh always commits.
    propulsionSelect.addEventListener('input', handlePropulsionChange);
  }

  // Throttle range. Auto leaves the contextual system in charge;
  // Precision / Approach / Cruise impose hard upper-speed clamps so
  // the pilot can dial in fine control without changing propulsion.
  const speedRangeSelect = document.getElementById('speed-range');
  if (speedRangeSelect) {
    speedRangeSelect.addEventListener('change', (e) => {
      setSpeedRange(e.target.value);
    });
  }

  // Craft picker. Saucer is built at boot; Sphere shader and Enterprise
  // GLB load on first pick so users who never switch never pay the
  // download/compile cost.
  const craftSelect = document.getElementById('craft-type');
  if (craftSelect && IS_APP) {
    for (const option of [...craftSelect.options]) if (!APP_CRAFT.has(option.value)) option.remove();
  }
  if (craftSelect) {
    craftSelect.addEventListener('change', async (e) => {
      const key = e.target.value;
      const previousLabel = craftSelect.dataset.lastValue || 'saucer';
      try {
        // Visual hint while a heavy asset is fetching.
        if (key !== 'saucer' && !loadedCraft[key]) {
          craftSelect.disabled = true;
        }
        await setActiveCraft(key);
        craftSelect.dataset.lastValue = key;
      } catch (err) {
        console.warn('[craft] failed to load craft:', key, err);
        craftSelect.value = previousLabel;
      } finally {
        craftSelect.disabled = false;
      }
    });
  }

  // Localhost-only craft tuner is wired up in init() after
  // createPlayerShip runs (loadedCraft / activeCraftKey are declared
  // further down in the file, so calling it here would hit the
  // temporal dead zone).

  // Realistic scale toggle. Drops a focused body, repositions the
  // camera to fit the new layout, and rebuilds orbit lines + asteroid
  // belt + moon orbits so everything matches the new radial scaling.
  if (realisticScaleToggle) {
    realisticScaleToggle.addEventListener('click', () => {
      if (spacetimeMode) spacetimeMode.scaleChangedByUser();
      setRealisticScaleMode(!realisticScaleMode);
    });
  }
  // Split out so rocketry mode can force true-scale bodies and distances
  // programmatically (real orbital mechanics need the real Moon distance).
  // opts.instant: keep the focused body's framing by moving the camera with
  // it at once, instead of flying back to it (a caller with its own camera
  // move, the ISS focus, starts from there).
  function setRealisticScaleMode(on, opts = {}) {
    if (on === realisticScaleMode) return;
    const flightBody = flyMode ? findNearestBodySurfaceUncached()?.body : null;
    const flightRadius = flightBody ? getBodyBaseRadius(flightBody) : 0;
    const flightOffset = flightBody ? camera.position.clone().sub(flightBody.getWorldPosition(new THREE.Vector3())) : null;
    // Where the camera sits from the focused body, measured before the
    // body is resized, so the switch keeps the zoom the user had in body
    // radii instead of jumping to the default focus distance.
    const framedBody = !flightBody ? selectedObject : null;
    const framedRadius = framedBody ? getBodyBaseRadius(framedBody) : 0;
    const framedOffset = framedBody ? camera.position.clone().sub(framedBody.getWorldPosition(new THREE.Vector3())) : null;
    realisticScaleMode = on;
    showAsteroidsToggle();
    {
      if (realisticScaleMode) {
        realisticScaleToggle.classList.add('playing');
        realisticLabel.textContent = vt('controls.artistic');
        scaleMultiplier = 100;
      } else {
        realisticScaleToggle.classList.remove('playing');
        realisticLabel.textContent = vt('controls.realistic');
        scaleMultiplier = 3;
      }
      const pos = scaleToSlider(scaleMultiplier);
      scaleSlider.value = pos;
      lastScalePos = pos;
      scaleValue.textContent = formatScale(scaleMultiplier);

      // Capture the body the user was focused on so we can re-track
      // it after the scale change. Without this, when toggling from
      // a planet view the planet moves to a wildly different world
      // position and the user is stranded looking at empty space.
      const previousSelected = selectedObject;

      // Loosen any per-body zoom clamp so the new scale can fit.
      if (controls) {
        controls.minDistance = 0.05;
        controls.maxDistance = 50_000_000;
      }
      // Drop the camera near plane immediately so users who haven't
      // focused on a planet (and so won't trigger focusOnObject's
      // near-plane reset) can still zoom close to a tiny realistic
      // body. updateScale also handles this, but only when
      // !selectedObject; this guarantees the near plane is sane
      // regardless of the current selection state.
      setCameraNearPlane(getOverviewCameraNear());

      updateScale();
      // Apply / undo true-radius body scaling. In realistic mode
      // every planet (and the sun) is shrunk to its real proportional
      // size relative to AU_TO_SCENE; in artistic the bodies use the
      // authored radii from celestialData.
      if (realisticScaleMode) {
        applyRealisticBodyScales();
      } else {
        resetArtisticBodyScales();
      }
      // Orbit ring lines are scale-mode-dependent (realistic uses
      // AU-scaled positions, artistic compresses to data.distance), so
      // rebuild them whenever the toggle flips.
      rebuildOrbitLines();

      if (flightBody && flightOffset) {
        // A display-scale switch is not an autopilot command. Preserve the
        // body's relative flight position and the complete chase framing.
        activeCameraTween?.kill(); activeCameraTween = null; isAnimating = false;
        const ratio = getBodyBaseRadius(flightBody) / flightRadius;
        flightBody.getWorldPosition(_atmoCarryNow);
        camera.position.copy(_atmoCarryNow).addScaledVector(flightOffset, ratio);
        flyVelocity.multiplyScalar(ratio);
        _nearestBodyCacheFrame = -1;
        computeAtmosphericEntry(atmoEntry, findNearestBodySurfaceUncached(), camera.position);
        atmoCraftScaleLive = atmoEntry.craftScale;
        _atmoCarryBody = flightBody; _atmoCarryPrev.copy(_atmoCarryNow);
        _atmoCarrySpinMesh = null;
        controls.enabled = false;
        if (flyParkedBody) setFlyParkedBody(flyParkedBody);
        syncPlayerShipToCamera(); updateFlyCameraNear();
      } else if (previousSelected) {
        // The selected body just teleported to a new world position
        // (artistic compressed → realistic AU coords or vice versa).
        // Re-run focusOnObject so the camera animates to the body's
        // new location, at the same framing it had before the switch.
        let offset;
        if (framedOffset && framedRadius > 0) {
          const radius = getBodyBaseRadius(previousSelected);
          offset = framedOffset.multiplyScalar(radius / framedRadius);
          // Clear of the follow code's 1.35-radius clearance bubble, which
          // would otherwise shove the camera out after it lands. The Sun has
          // no bubble, only its zoom floor, so it keeps the zoom it had.
          const clearance = previousSelected.userData?.isSun ? getSunZoomFloor(radius) : radius * 1.4;
          if (offset.length() < clearance) offset.setLength(clearance);
        }
        if (offset && previousSelected.userData?.isSun && !opts.instant) {
          // The Sun stays where it is and only changes size, so a fly-in
          // from the old camera position started inside the true-scale Sun
          // from any artistic framing under 1.55 radii (a black half second
          // from the floor). It starts from the kept framing instead, and
          // the tween only brings a panned target home.
          previousSelected.getWorldPosition(_scaleSwitchBodyPos);
          camera.position.copy(_scaleSwitchBodyPos).add(offset);
          camera.lookAt(controls.target);
        }
        if (opts.instant) {
          activeCameraTween?.kill(); activeCameraTween = null; isAnimating = false;
          previousSelected.getWorldPosition(_scaleSwitchBodyPos);
          const anchorOffset = offset || _scaleSwitchFallback.set(0, 0, getBodyBaseRadius(previousSelected) * 4);
          camera.position.copy(_scaleSwitchBodyPos).add(anchorOffset);
          controls.target.copy(_scaleSwitchBodyPos);
          camera.lookAt(controls.target);
          setScaleAnchor(previousSelected, anchorOffset);
          _focusPrevObj = null;
        } else {
          focusOnObject(previousSelected, {
            interrupt: true,
            preservePanel: true,
            force: true,
            duration: 1.2,
            offset
          });
        }
      } else if (camera && controls) {
        // No focused body — give the user a sensible overview camera
        // for the new scale. Realistic spreads Pluto to ~197k units
        // so put the camera at ~28k for a wide-angle shot; artistic
        // sits closer at 1100.
        const target = realisticScaleMode ? 28_000 : 1100;
        const camLen = camera.position.length();
        const dir = camLen > 1
          ? camera.position.clone().normalize()
          : new THREE.Vector3(0.45, 0.32, 0.85).normalize();
        camera.position.copy(dir.multiplyScalar(target));
        controls.target.set(0, 0, 0);
      }
    }
  }
  window.__setRealisticScaleMode = setRealisticScaleMode;

  // Planet navigation
  prevPlanetBtn.addEventListener('click', () => navigatePlanet(-1, 'nav'));
  nextPlanetBtn.addEventListener('click', () => navigatePlanet(1, 'nav'));

  // Audio toggle
  audioToggle.addEventListener('click', async () => {
    try {
      await audioGesture();
      audioPlaying = await toggleAmbient();
    } catch (err) {
      console.warn('[audio] ambient unavailable in this environment', err);
      audioPlaying = false;
    }
    updateAudioUI();
  });

  // The music switch: picking a piece crossfades to it, and turns the music
  // on with it when it was off. Counted as music_piece in GA4, to see which
  // of the two people choose.
  musicPick.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-piece]');
    if (!button) return;
    const changed = button.dataset.piece !== musicPiece;
    musicPiece = button.dataset.piece;
    try { localStorage.setItem(MUSIC_KEY, musicPiece); } catch { /* storage blocked */ }
    if (changed) track('music_piece', { piece: musicPiece });
    updateAudioUI();
    try {
      await audioGesture();
      const mod = await loadSpaceAmbientModule();
      mod.setMusicPiece(musicPiece);
      if (!audioPlaying) {
        audioAutoStarted = true;
        if (!rocketryActive) await mod.startAmbient();
        audioPlaying = true;
      }
    } catch (err) {
      console.warn('[audio] ambient unavailable in this environment', err);
    }
    updateAudioUI();
  });
  updateAudioUI();

  // Auto-start music on first user interaction
  const autoStartAudio = async (event) => {
    if (event && event.type === 'click' && event.target && event.target.closest) {
      const isInterfaceClick = event.target.closest('button, input, select, a, aside, #controls-panel, #planet-nav');
      if (isInterfaceClick) return;
    }
    if (!audioAutoStarted) {
      audioAutoStarted = true;
      try {
        // Where audio may start later (AUDIO_STARTS_AFTER_USE), the music
        // waits for the scene to be still: building it (and making the audio
        // context, where it was not made at load) held the main thread 50 to
        // 250 ms, and the first click on the scene is usually a drag.
        if (AUDIO_STARTS_AFTER_USE) await waitForStillScene();
        await audioGesture();
        // Rocketry mode plays its own score: the gesture still turns audio
        // on, and the ambient waits for rocketry to hand it back on exit.
        if (!rocketryActive) await startAmbient();
        audioPlaying = true;
      } catch (err) {
        console.warn('[audio] ambient auto-start unavailable in this environment', err);
        audioPlaying = false;
      }
      updateAudioUI();
      document.removeEventListener('click', autoStartAudio);
      document.removeEventListener('keydown', autoStartAudio);
    }
  };
  document.addEventListener('click', autoStartAudio, { once: false });
  document.addEventListener('keydown', autoStartAudio, { once: false });
}

// Update audio button UI
function updateAudioUI() {
  musicPick.classList.toggle('is-on', audioPlaying);
  for (const button of musicPick.querySelectorAll('[data-piece]')) {
    button.setAttribute('aria-checked', String(button.dataset.piece === musicPiece));
  }
  if (audioPlaying) {
    audioToggle.classList.add('playing');
    audioLabel.textContent = vt('controls.ambient.on');
    audioIconOn.style.display = 'block';
    audioIconOff.style.display = 'none';
  } else {
    audioToggle.classList.remove('playing');
    audioLabel.textContent = vt('controls.ambient.off');
    audioIconOn.style.display = 'none';
    audioIconOff.style.display = 'block';
  }
}

// Navigate to previous/next planet
function navigatePlanet(direction, via) {
  // Bail if a focus animation is already in flight. Otherwise the index
  // increments while focusOnObject early-returns, drifting the nav state
  // out of sync with what's actually focused (rapid clicks would skip
  // bodies entirely).
  if (isAnimating) return;

  const entries = getActiveNavBodies();
  if (entries.length === 0) return;

  // Step past an entry with nothing to focus (a layer that is not built).
  for (let tries = 0; tries < entries.length; tries++) {
    currentNavIndex = (currentNavIndex + direction + entries.length) % entries.length;
    const entry = entries[currentNavIndex];
    const target = resolveNavEntry(entry);
    if (!target) continue;
    const focus = entry.item && entry.item.category === 'dso' ? focusOnDSO : focusOnObject;
    focus(target, { via });
    if (navLabel) navLabel.textContent = entry.displayName;
    return;
  }
}

// The object a nav entry stands for, found the way the Objects browser
// finds it: deep-space bodies are built and shown on first use.
function resolveNavEntry(entry) {
  if (!entry) return null;
  const item = entry.item;
  if (!item) return entry.object || null;
  if (item.lazy && !deepSpaceBodiesLoaded) ensureDeepSpaceBodies();
  let target = item.resolve();
  if (!target && item.lazy && !deepSpaceBodiesVisible) {
    setDeepSpaceBodiesVisible(true);
    target = item.resolve();
  }
  return target || null;
}

// Point the nav at a focused body however it was reached, so the arrows
// carry on from there. False when the body is not in the list.
function syncNavToBody(...names) {
  const entries = getActiveNavBodies();
  for (const name of names) {
    if (!name) continue;
    const index = entries.findIndex((entry) => entry.id === String(name).toLowerCase());
    if (index !== -1) {
      currentNavIndex = index;
      return true;
    }
  }
  return false;
}

function clearOrbitStabilization() {
  orbitStabilized = false;
  orbitTarget = null;
  const stabilizeBtn = document.getElementById('stabilize-orbit');
  if (stabilizeBtn) {
    stabilizeBtn.classList.remove('active');
    stabilizeBtn.textContent = 'Orbit';
  }
}

function resetFlyInputState({ resetKeys = false } = {}) {
  flyVelocity.set(0, 0, 0);
  flyAngularVelocity.set(0, 0);
  flyTargetEuler.copy(flyEuler);
  thrustBuildup = 0;
  FLY_SMOOTHED_THRUST.set(0, 0, 0);
  ufoTiltX = 0;
  ufoTiltZ = 0;
  ufoTiltVelX = 0;
  ufoTiltVelZ = 0;
  isMiddleMouseDown = false;
  if (orbitMiddlePanActive) endOrbitMiddlePan();
  resetCraftOrbitCamera();
  document.body.classList.remove('fly-mouse-look');
  flyMouseLookActive = false;
  if (resetKeys) flyKeys = createDefaultFlyKeys();
}

function applyCraftFlightScale() {
  if (!playerShip) return;

  // Atmospheric scale illusion: shrink the craft (and, via the
  // follow-distance multipliers, the whole chase rig) as it descends
  // into an atmosphere, so an artistic-scale planet reads as
  // planet-sized from inside. Base scale is captured on first
  // descent and released on exit so the dev tuner's live scale edits
  // aren't fought outside the atmosphere.
  const shipUd = playerShip.userData;
  if (atmoCraftScaleLive < 0.999) {
    if (!shipUd.atmoBaseScale) shipUd.atmoBaseScale = playerShip.scale.clone();
    playerShip.scale.copy(shipUd.atmoBaseScale).multiplyScalar(atmoCraftScaleLive);
    shipUd.atmoScaled = true;
  } else if (shipUd.atmoScaled) {
    playerShip.scale.copy(shipUd.atmoBaseScale);
    shipUd.atmoScaled = false;
    shipUd.atmoBaseScale = null;
  }

}

function syncPlayerShipToCamera() {
  if (!playerShip || !camera) return;
  applyCraftFlightScale();
  const profile = getActiveCraftProfile();
  const offset = getCraftFollowOffset(FLY_SCRATCH_CRAFT_OFFSET, profile);
  offset.applyQuaternion(camera.quaternion);
  playerShip.visible = flyMode;
  playerShip.position.copy(camera.position).add(offset);
  playerShip.quaternion.copy(camera.quaternion);
  if (profile.basePitch) playerShip.rotateX(profile.basePitch);
  ufoWorldPos.copy(playerShip.position);
  clampShipAboveTerrain();
  updateCraftFillLight();
}

function startFlyMouseLook(e) {
  if (!flyMode) return;
  e.preventDefault();
  if (orbitStabilized) clearOrbitStabilization();
  flyMouseLookActive = true;
  lastMouseX = e.clientX;
  lastMouseY = e.clientY;
  flyEuler.setFromQuaternion(camera.quaternion);
  flyTargetEuler.copy(flyEuler);
  flyAngularVelocity.set(0, 0);
  document.body.classList.add('fly-mouse-look');
}

function applyFlyMouseLook(e) {
  if (!flyMouseLookActive || !flyMode) return;
  e.preventDefault();
  const deltaX = e.clientX - lastMouseX;
  const deltaY = e.clientY - lastMouseY;
  lastMouseX = e.clientX;
  lastMouseY = e.clientY;

  // Write mouse deltas into the TARGET euler rather than the live
  // camera euler. The per-frame loop lerps flyEuler toward this
  // target with a short time constant, which produces the smooth
  // cinematic feel of a virtual camera with weight, instead of the
  // 1:1 mouse-to-camera snap mapping that read as "twitchy."
  const sensitivity = 0.0036;
  // Inside an atmosphere the world-frame euler is the WRONG basis:
  // yawing about world-Y when the local up points elsewhere reads as
  // ROLL against the horizon. Divert the deltas into the
  // local-horizon look state instead; updateFlyMovement builds a
  // horizon-locked orientation from them (yaw about local zenith,
  // pitch against the local horizon).
  if (atmoEntry.immersion > 0.3) {
    atmoLookPending.yaw -= deltaX * sensitivity;
    atmoLookPending.pitch -= deltaY * sensitivity;
    return;
  }
  flyTargetEuler.y -= deltaX * sensitivity;
  flyTargetEuler.x -= deltaY * sensitivity;
  flyTargetEuler.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, flyTargetEuler.x));
}

function endFlyMouseLook() {
  if (!flyMouseLookActive) return;
  flyMouseLookActive = false;
  document.body.classList.remove('fly-mouse-look');
}

function updateMoonSystemsScale() {
  Object.values(moons).forEach((planetMoons) => {
    planetMoons.forEach((moon) => {
      const baseDistance = moon.userData.baseDistance ?? moon.userData.distance;
      if (!baseDistance) return;

      moon.userData.baseDistance = baseDistance;
      // Realistic scale puts every moon at its true distance; the scale
      // slider only spreads the artistic view.
      moon.userData.distance = (realisticScaleMode && getRealisticMoonDistance(moon)) || baseDistance * scaleMultiplier;
      if (realisticScaleMode && isEarthsMoon(moon)) {
        placeEarthMoonFromEphemeris(moon, simulationJD);
        return;
      }

      const angle = moon.userData.orbitAngle || 0;
      // -sin on z matches the per-frame moon driver's orbit sense
      moon.position.x = Math.cos(angle) * moon.userData.distance;
      moon.position.z = -Math.sin(angle) * moon.userData.distance;
      if (moon.userData.inclination) {
        moon.position.y = Math.sin(angle) * moon.userData.distance *
          Math.sin(THREE.MathUtils.degToRad(moon.userData.inclination)) * 0.3;
      } else {
        moon.position.y = 0;
      }
    });
  });

  Object.values(moonOrbitLines).forEach((moonLines) => {
    moonLines.forEach((line) => {
      const moon = line.userData && line.userData.moonRef;
      if (moon && isEarthsMoon(moon)) setEarthMoonOrbitLine(line, realisticScaleMode);
      const s = moon ? moon.userData.distance / line.userData.baseDistance : scaleMultiplier;
      if (line.userData.ephemerisJD != null) line.scale.set(1, 1, 1);
      else line.scale.set(s, 1, s);
    });
  });
}

// Earth's Moon's guide ring in realistic scale: one sidereal month of the
// ephemeris around the current date, so the ring runs through the Moon
// (its orbit is inclined 5.1 degrees and 5% eccentric). Resampled in
// animate() once the date has moved a day. Artistic mode gets its circle back.
const SIDEREAL_MONTH_DAYS = 27.321661;
function setEarthMoonOrbitLine(line, fromEphemeris) {
  if (!fromEphemeris) {
    if (line.userData.ephemerisJD == null) return;
    line.userData.ephemerisJD = null;
    line.geometry.setPositions(line.userData.circlePositions);
    line.computeLineDistances();
    return;
  }
  const center = simulationJD;
  const SAMPLES = 256;
  const pts = [];
  for (let k = 0; k <= SAMPLES; k++) {
    const p = moonGeocentricJ2000(center + (k / SAMPLES - 0.5) * SIDEREAL_MONTH_DAYS);
    pts.push(p.x * REAL_KM_TO_SCENE, p.z * REAL_KM_TO_SCENE, -p.y * REAL_KM_TO_SCENE);
  }
  line.geometry.setPositions(pts);
  line.computeLineDistances();
  line.userData.ephemerisJD = center;
}

// Update scale of the solar system
function updateScale() {
  // Update planet positions based on scale
  Object.keys(planets).forEach((planetName) => {
    const planet = planets[planetName];
    const baseDistance = baseDistances[planetName];
    if (baseDistance === undefined) return;
    const scaledDistance = baseDistance * scaleMultiplier;

    // Update current position (-sin: counterclockwise from +Y, the
    // same sense every orbit driver uses)
    const currentAngle = planet.userData.orbitAngle;
    planet.position.x = Math.cos(currentAngle) * scaledDistance;
    planet.position.z = -Math.sin(currentAngle) * scaledDistance;

    // Store scaled distance for animation
    planet.userData.scaledDistance = scaledDistance;
  });

  // Adjust camera if not focused on an object
  if (!selectedObject && camera && controls) {
    // Move camera proportionally with scale to keep things visible
    const baseDistance = 700; // Base camera distance at scale 1
    const cameraDistance = baseDistance * Math.sqrt(scaleMultiplier);
    const direction = camera.position.clone().normalize();
    if (direction.length() > 0) {
      camera.position.copy(direction.multiplyScalar(cameraDistance));
    }

    // Update camera far plane for extreme scales. Floor is high enough
    // that the procedural galaxy (~25M radius offset from origin) stays
    // inside the frustum even at scale 1, so the user can pull all the
    // way out to the exterior view without the disk getting clipped.
    const galaxyAwareFar = 120_000_000;
    camera.far = Math.max(galaxyAwareFar, 50000000 * scaleMultiplier);
    // In realistic mode, planet bodies are sub-unit-radius (Earth ~
    // 0.213). Pinning near at scaleMultiplier*0.1 = 10 made it
    // impossible to zoom near them — the surface clipped before the
    // user could see it. Use a tiny near plane in realistic so close
    // planet inspection works; rely on logarithmic z mapping for
    // depth precision across the huge near/far range.
    camera.near = getOverviewCameraNear();
    camera.updateProjectionMatrix();

    // Update controls max distance
    controls.maxDistance = camera.far * 0.9;
  }

  // Update orbit lines — regenerate from the FULL ephemeris elements
  // each planet uses at runtime (longitude of ascending node, argument
  // of perihelion, inclination, eccentricity, semi-major axis), then
  // map ecliptic → scene coordinates and apply scaleMultiplier. The
  // previous simplified Kepler path here ignored Ω and ω so orbit
  // ellipses ended up rotated wrong relative to the planet positions
  // — the planet would visibly drift off its line after a scale change.
  const SAMPLES = 256;
  orbitLines.forEach((line, index) => {
    const planetName = planetOrder[index];
    const data = celestialData[planetName];
    const positions = [];

    if (SUPPORTED_PLANETS.includes(planetName)) {
      const state = getOrbitState(planetName, simulationJD);
      const scale = realisticScaleMode
        ? AU_TO_SCENE
        : (baseDistances[planetName] * scaleMultiplier / state.a);
      const cosO = Math.cos(state.longNode);
      const sinO = Math.sin(state.longNode);
      const cosI = Math.cos(state.i);
      const sinI = Math.sin(state.i);
      const cosW = Math.cos(state.argPeri);
      const sinW = Math.sin(state.argPeri);
      for (let k = 0; k <= SAMPLES; k++) {
        const theta = (k / SAMPLES) * 2 * Math.PI;
        const r = state.a * (1 - state.e * state.e) / (1 + state.e * Math.cos(theta));
        const xp = r * Math.cos(theta);
        const yp = r * Math.sin(theta);
        const xEcl =
          xp * (cosW * cosO - sinW * sinO * cosI) +
          yp * (-sinW * cosO - cosW * sinO * cosI);
        const yEcl =
          xp * (cosW * sinO + sinW * cosO * cosI) +
          yp * (-sinW * sinO + cosW * cosO * cosI);
        const zEcl = xp * (sinW * sinI) + yp * (cosW * sinI);
        // Ecliptic (x, y, z) → scene (x, z, -y), matching the planet remap.
        positions.push(xEcl * scale, zEcl * scale, -yEcl * scale);
      }
    } else {
      const a = baseDistances[planetName] * scaleMultiplier;
      const e = (data && data.eccentricity) || 0;
      const incl = (data && data.orbitalInclination) || 0;
      const cosI = Math.cos(incl);
      const sinI = Math.sin(incl);
      for (let i = 0; i <= SAMPLES; i++) {
        const theta = (i / SAMPLES) * 2 * Math.PI;
        const r = a * (1 - e * e) / (1 + e * Math.cos(theta));
        // -sin: counterclockwise from +Y, same sense as the planets.
        positions.push(
          Math.cos(theta) * r,
          Math.sin(theta) * r * sinI,
          -Math.sin(theta) * r * cosI
        );
      }
    }

    line.geometry.dispose();
    const newGeometry = new LineGeometry();
    newGeometry.setPositions(positions);
    line.geometry = newGeometry;
    line.computeLineDistances();
  });

  updateMoonSystemsScale();
  // The probes and dwarf planets move at once, so a focus made right after a
  // scale switch flies to where they now are.
  placeSpaceProbes();
  dwarfPlanets.forEach((dwarf) => {
    const planet = planets[dwarf.name.toLowerCase()];
    if (planet && planet.userData && planet.userData.isDwarfPlanet) placeDwarfPlanet(planet);
  });
}

// Update orbit lines visibility
function updateOrbitsVisibility() {
  // Planet orbit lines
  orbitLines.forEach((line) => {
    line.visible = orbitsVisible;
  });

  updateLocalOrbitGuideVisibility();

  cometOrbitLines.forEach((line) => {
    line.visible = orbitsVisible && deepSpaceBodiesVisible;
  });

  probePaths.forEach((path) => {
    path.line.visible = probePathVisible(path);
  });
}

function isPlanetCloseViewForOrbitGuides(planet) {
  if (!orbitsVisible || !camera || !planet) return false;
  if (selectedObject && selectedObject.userData && selectedObject.userData.isSatellite) return false;

  const planetPosition = getWorldPosition(planet);
  const radius = planet.userData && planet.userData.radius ? planet.userData.radius : 1;
  const cameraDistance = camera.position.distanceTo(planetPosition);
  const targetDistance = controls ? controls.target.distanceTo(planetPosition) : Infinity;
  const isFocusedPlanet = selectedObject === planet || targetDistance < radius * 4.8;

  return isFocusedPlanet && cameraDistance < radius * 34;
}

function shouldShowLocalOrbitGuide(parentPlanet) {
  return orbitsVisible && !isPlanetCloseViewForOrbitGuides(parentPlanet);
}

function updateLocalOrbitGuideVisibility() {
  Object.values(moonOrbitLines).forEach((moonLines) => {
    moonLines.forEach((line) => {
      const moon = line.userData && line.userData.moonRef;
      const parentName = moon && moon.userData ? moon.userData.parentPlanet : null;
      const parentPlanet = parentName ? planets[parentName] : null;
      line.visible = shouldShowLocalOrbitGuide(parentPlanet);
    });
  });

  satelliteOrbitLines.forEach((line) => {
    line.visible = !line.userData.craftHidden && shouldShowLocalOrbitGuide(planets.earth);
  });
}

function setDeepSpaceBodiesVisible(visible) {
  deepSpaceBodiesVisible = visible;

  if (!visible && selectedObject && selectedObject.userData && selectedObject.userData.isDeepSpaceBody) {
    selectedObject = null;
    matryoshka.close();
  }

  cometObjects.forEach(({ mesh }) => {
    mesh.visible = visible;
  });

  neoObjects.forEach(({ mesh }) => {
    mesh.visible = visible;
  });

  satelliteObjects.forEach(({ mesh, isProbe }) => {
    if (isProbe) mesh.visible = visible && mesh.userData.onPath === true;
  });
  interstellarEntries.forEach(({ mesh }) => {
    mesh.visible = visible && mesh.userData.onPath === true;
  });
  markClickablesDirty();

  Object.values(planets).forEach((planet) => {
    if (planet && planet.userData && planet.userData.isDwarfPlanet) {
      planet.visible = visible;
    }
  });

  if (wormholeObject) {
    wormholeObject.visible = visible;
  }

  updateOrbitsVisibility();
  if (deepSpaceToggle) deepSpaceToggle.classList.toggle('playing', visible);
  if (deepSpaceLabel) deepSpaceLabel.textContent = vt(visible ? 'controls.deepSpace.on' : 'controls.deepSpace.off');
  if (objectBrowserOpen) renderObjectBrowser();
}

function ensureDeepSpaceBodies() {
  if (deepSpaceBodiesLoaded) return;
  deepSpaceBodiesLoaded = true;
  createWormhole();
  createSpaceProbes();
  createComets();
  createNEOs();
  createDwarfPlanets();
  createInterstellarObjects();
  setDeepSpaceBodiesVisible(true);
}

function toggleDeepSpaceBodies() {
  if (!deepSpaceBodiesLoaded) {
    ensureDeepSpaceBodies();
    return;
  }
  setDeepSpaceBodiesVisible(!deepSpaceBodiesVisible);
}

function findLoadedSceneObjectByName(name) {
  const key = String(name || '').toLowerCase();
  if (!key) return null;
  // Remote-system bodies: 'remote:<bodyId>' keys from the menus, or a
  // body's display name while that system is active.
  const remoteHandle = activeSystemId !== 'sol' ? remoteSystems[activeSystemId] : null;
  if (remoteHandle) {
    if (key.startsWith('remote:')) {
      const id = name.slice(7);
      return remoteHandle.bodies[id] || null;
    }
    for (const id in remoteHandle.bodies) {
      const body = remoteHandle.bodies[id];
      if (String(body.userData?.name || '').toLowerCase() === key) return body;
    }
  }
  if (key === 'sun') return sun;
  if (planets[key]) return planets[key];

  for (const moonArray of Object.values(moons)) {
    const moon = moonArray.find((moonGroup) => (moonGroup.userData?.name || moonGroup.name || '').toLowerCase() === key);
    if (moon) return moon;
  }

  for (const { mesh } of cometObjects) {
    if ((mesh.userData?.name || mesh.name || '').toLowerCase() === key) return mesh;
  }

  for (const { mesh } of neoObjects) {
    if ((mesh.userData?.name || mesh.name || '').toLowerCase() === key) return mesh;
  }

  for (const { mesh, isProbe } of satelliteObjects) {
    // A probe not yet launched on the sim date is nowhere to go, nor is
    // Hubble before 1990 or Webb off its path.
    if (mesh.userData.onPath === false && (!isProbe || probeTracks)) continue;
    if ((mesh.userData?.name || mesh.name || '').toLowerCase() === key) return mesh;
  }

  for (const { mesh } of interstellarEntries) {
    if (mesh.userData.onPath === false && interstellarTracks) continue;
    if (mesh.userData.bodyId === key || (mesh.userData.name || '').toLowerCase() === key) return mesh;
  }

  for (const body of beltBodyObjects) {
    if ((body.userData?.name || body.name || '').toLowerCase() === key) return body;
  }

  if (wormholeObject && (wormholeObject.userData?.name || wormholeObject.name || '').toLowerCase() === key) {
    return wormholeObject;
  }

  if (milkyWayVista && (key === 'milky way' || key === 'milky-way' || key === 'galactic vista')) {
    return milkyWayVista;
  }

  if (blackHole && blackHole.object && (key === 'black hole' || key === 'black-hole' || key === 'blackhole')) {
    return blackHole.object;
  }

  if (nibiruHandle && nibiruHandle.group && (key === 'nibiru' || key === 'niburu')) {
    return nibiruHandle.group;
  }

  return null;
}

function browserItem(label, detail, category, resolve, options = {}) {
  return {
    id: `${category}:${label}`.toLowerCase(),
    label,
    detail,
    category,
    resolve,
    lazy: !!options.lazy,
    systemJump: options.systemJump || null
  };
}

function buildObjectBrowserCatalog() {
  const sections = [];

  // In another star system the browser lists that system's bodies
  // (focusing a Sol body from 300k units out is not a destination).
  const remoteHandle = activeSystemId !== 'sol' && activeSystemId !== 'gargantua' ? remoteSystems[activeSystemId] : null;
  if (remoteHandle) {
    const items = getRemoteSystemTargets(remoteHandle).map((t) => browserItem(
      t.label, t.detail, t.systemJump ? 'system' : 'planet',
      () => findLoadedSceneObjectByName(t.key),
      { systemJump: t.systemJump }
    ));
    sections.push({ title: remoteHandle.data.name, items });
    return sections;
  }

  sections.push({
    title: 'Planets',
    items: [
      browserItem('Sun', 'Star', 'planet', () => sun),
      ...planetOrder.map((name) => browserItem(
        celestialData[name].name,
        celestialData[name].type,
        'planet',
        () => planets[name]
      ))
    ]
  });

  const moonItems = [];
  planetOrder.forEach((planetName) => {
    const data = celestialData[planetName];
    if (data.moon) {
      moonItems.push(browserItem(
        data.moon.name,
        `${data.moon.type} · ${data.name}`,
        'moon',
        () => {
          const moonArray = moons[planetName] || [];
          return moonArray.find((moonGroup) => (moonGroup.userData?.name || moonGroup.name) === data.moon.name) || null;
        }
      ));
    }
    if (data.majorMoons) {
      data.majorMoons.forEach((moonData) => {
        moonItems.push(browserItem(
          moonData.name,
          `${moonData.type} · ${data.name}`,
          'moon',
          () => {
            const moonArray = moons[planetName] || [];
            return moonArray.find((moonGroup) => (moonGroup.userData?.name || moonGroup.name) === moonData.name) || null;
          }
        ));
      });
    }
  });
  sections.push({ title: 'Moons', items: moonItems });

  sections.push({
    title: 'Earth Orbit',
    items: satellites
      .filter((sat) => sat.name === 'ISS' || sat.name === 'Hubble' || sat.name === 'JWST')
      .map((sat) => browserItem(
        sat.name,
        `${sat.type} · ${sat.realAltitude}`,
        'satellite',
        () => findLoadedSceneObjectByName(sat.name)
      ))
  });

  sections.push({
    title: 'Main Belt',
    items: asteroidBeltBodies.map((body) => browserItem(
      body.name,
      body.type,
      'belt',
      () => findLoadedSceneObjectByName(body.name),
      { lazy: false }
    ))
  });

  const deepSpaceItems = [
    browserItem(
      'Wormhole',
      'Speculative spacetime feature · near Saturn',
      'wormhole',
      () => findLoadedSceneObjectByName('wormhole'),
      { lazy: true }
    ),
    ...comets.map((comet) => browserItem(
      comet.name,
      comet.type,
      'comet',
      () => findLoadedSceneObjectByName(comet.name),
      { lazy: true }
    )),
    ...interstellarObjects.map((body) => browserItem(
      body.name,
      `${body.type} · ${body.designation}`,
      body.isComet ? 'comet' : 'neo',
      () => findLoadedSceneObjectByName(body.bodyId),
      { lazy: true }
    )),
    ...nearEarthObjects.map((neo) => browserItem(
      neo.name,
      neo.type,
      'neo',
      () => findLoadedSceneObjectByName(neo.name),
      { lazy: true }
    )),
    ...spaceProbes.map((probe) => browserItem(
      probe.name,
      probe.type,
      'probe',
      () => findLoadedSceneObjectByName(probe.name),
      { lazy: true }
    )),
    ...dwarfPlanets.filter((body) => body.name !== 'Ceres').map((body) => browserItem(
      body.name,
      body.type,
      'dwarf',
      () => findLoadedSceneObjectByName(body.name),
      { lazy: true }
    ))
  ];
  sections.push({ title: 'Deep Space', items: deepSpaceItems });

  const beyondItems = [];
  if (galaxy && galaxy.object) {
    beyondItems.push(browserItem(
      'Milky Way',
      'Spiral galaxy · Our home',
      'galaxy',
      () => galaxy.object
    ));
  }
  if (blackHole && blackHole.object) {
    beyondItems.push(browserItem(
      'Black Hole',
      'Stellar-mass black hole · ~100 solar masses',
      'blackhole',
      () => blackHole.object
    ));
  }
  // Gargantua — remote system reached through the Saturn-orbit
  // wormhole rather than by normal-space cruise. Clicking routes
  // through jumpToSystem() (the wormhole flythrough) instead of
  // focusOnObject, mirroring the fast-travel menu — standard focus
  // can't cross between systems. systemJump flags that dispatch.
  if (remoteSystems['gargantua']) {
    if (activeSystemId === 'sol') {
      beyondItems.push(browserItem(
        'Gargantua',
        'Supermassive black hole · via wormhole',
        'system',
        () => null,
        { systemJump: 'gargantua' }
      ));
    } else if (activeSystemId === 'gargantua') {
      beyondItems.push(browserItem(
        'Sol (home)',
        'Return through the wormhole',
        'system',
        () => null,
        { systemJump: 'sol' }
      ));
    }
  }
  if (beyondItems.length) {
    sections.push({
      title: 'Beyond the Solar System',
      items: beyondItems,
    });
  }

  // Named deep-sky objects. Clicking these uses focusOnDSO (telescope
  // view: rotate + zoom in via FOV) rather than focusOnObject — they
  // sit on the celestial shell and aren't physical destinations.
  if (deepSkyObjects && deepSkyObjects.group) {
    const dsoItems = deepSkyObjects.group.children.map((sprite) => browserItem(
      sprite.name,
      dsoDetailFor(sprite.name),
      'dso',
      () => sprite
    ));
    if (dsoItems.length) {
      sections.push({ title: 'Deep Sky', items: dsoItems });
    }
  }

  return sections;
}

// One-line descriptors shown next to each DSO entry in the browser.
// Static map rather than threading detail strings through dso.js so
// the browser metadata stays in main.js with its UI siblings.
function dsoDetailFor(name) {
  const map = {
    'M31 Andromeda':     'Spiral galaxy · 2.5 Mly',
    'M42 Orion Nebula':  'Emission nebula · 1,350 ly',
    'M45 Pleiades':      'Open cluster · 444 ly',
    'M8 Lagoon Nebula':  'Emission nebula · 5,200 ly',
    'M16 Eagle Nebula':  'Emission nebula · 7,000 ly',
    'M20 Trifid Nebula': 'Emission nebula · 5,000 ly',
    'LMC':               'Satellite galaxy · 163 kly',
    'SMC':               'Satellite galaxy · 200 kly',
    'M104 Sombrero':     'Spiral galaxy · 28 Mly',
    'NGC 7293 Helix':    'Planetary nebula · 650 ly · "Eye of God"',
    'M1 Crab Nebula':    'Supernova remnant · 6,500 ly',
    'M51 Whirlpool':     'Spiral galaxy · 31 Mly',
    'M57 Ring Nebula':   'Planetary nebula · 2,500 ly',
    'Horsehead Nebula':  'Dark nebula · 1,375 ly',
    'Veil Nebula':       'Supernova remnant · 2,400 ly',
    'Carina Nebula':     'Cosmic Cliffs · JWST · 7,600 ly',
    "NGC 6543 Cat's Eye": 'Planetary nebula · 3,000 ly',
    "M81 Bode's Galaxy": 'Spiral galaxy · 12 Mly',
    'M82 Cigar Galaxy':  'Starburst galaxy · 12 Mly',
    'M33 Triangulum':    'Spiral galaxy · 2.7 Mly',
    'M101 Pinwheel':     'Spiral galaxy · 21 Mly',
    'M27 Dumbbell':      'Planetary nebula · 1,270 ly',
    'Centaurus A':       'Peculiar galaxy · 13 Mly',
    'M87':               'Elliptical galaxy · 54 Mly · M87* black hole',
    'NGC 6302 Butterfly': 'Bipolar planetary · 3,400 ly',
    'Rosette Nebula':    'Emission nebula · 5,200 ly',
    'Hubble Ultra Deep Field': '~10,000 galaxies · edge of observable universe',
    'Hubble Deep Field': 'The original deep exposure · 1995',
    'M64 Black Eye':     'Spiral galaxy · 17 Mly · dramatic dust band',
    'NGC 4565 Needle':   'Edge-on spiral · 40 Mly',
    'M83 Southern Pinwheel': 'Spiral galaxy · 15 Mly',
    'Antennae Galaxies': 'Colliding pair · 45 Mly',
    'NGC 1300 Barred Spiral': 'Barred spiral · 61 Mly'
  };
  return map[name] || 'Deep-sky object';
}

function renderObjectBrowser() {
  if (!objectBrowserSections) return;
  const query = (objectBrowserSearch && objectBrowserSearch.value || '').trim().toLowerCase();
  const sections = buildObjectBrowserCatalog();
  objectBrowserSections.replaceChildren();

  sections.forEach((section) => {
    const filtered = section.items.filter((item) => {
      if (!query) return true;
      return `${item.label} ${item.detail} ${section.title}`.toLowerCase().includes(query);
    });
    if (!filtered.length) return;

    const block = document.createElement('section');
    block.className = 'object-browser-section';

    const heading = document.createElement('h3');
    heading.textContent = section.title;
    block.appendChild(heading);

    const list = document.createElement('div');
    list.className = 'object-browser-list';

    filtered.forEach((item) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'object-browser-item';
      button.dataset.objectId = item.id;
      button.innerHTML = `<span class="object-browser-name">${item.label}</span><span class="object-browser-detail">${item.detail}</span>`;
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        const query = (objectBrowserSearch && objectBrowserSearch.value || '').trim().toLowerCase();
        if (goToCatalogItem(item, query)) closeObjectBrowser();
      });
      list.appendChild(button);
    });

    block.appendChild(list);
    objectBrowserSections.appendChild(block);
  });
}

// Go to an entry of the Objects index (the Objects panel and the search in
// the distance readout). A pick from a filtered list is a committed search.
// Returns whether it went anywhere.
function goToCatalogItem(item, query = '') {
  // Cross-system destinations (Gargantua, Sol return) warp through the
  // wormhole rather than focusing an in-scene object, so they bypass the
  // resolve() lookup entirely.
  if (item.systemJump) {
    jumpToSystem(item.systemJump);
    return true;
  }
  if (item.lazy && !deepSpaceBodiesLoaded) {
    ensureDeepSpaceBodies();
  }
  const focus = item.category === 'dso' ? focusOnDSO : focusOnObject;
  const term = (query || '').trim().toLowerCase().slice(0, 60);
  if (term) explore('search', term, { search_term: term });
  const via = term ? 'search' : 'browser';
  let target = item.resolve();
  if (!target && item.lazy && !deepSpaceBodiesVisible) {
    setDeepSpaceBodiesVisible(true);
    target = item.resolve();
  }
  if (!target) return false;
  focus(target, { interrupt: true, via });
  return true;
}

function openObjectBrowser() {
  closeTransientPanels('objectBrowser');
  objectBrowserOpen = true;
  if (objectBrowserPanel) objectBrowserPanel.classList.add('visible');
  renderObjectBrowser();
  if (objectBrowserSearch) objectBrowserSearch.focus({ preventScroll: true });
}

function closeObjectBrowser() {
  objectBrowserOpen = false;
  if (objectBrowserPanel) objectBrowserPanel.classList.remove('visible');
}

function toggleObjectBrowser() {
  if (objectBrowserOpen) closeObjectBrowser();
  else openObjectBrowser();
}

const SPACE_EVENT_SCENES = [
  {
    id: 'io-transit',
    kicker: 'Live phenomenon',
    title: 'Io shadow on Jupiter',
    copy: 'Stages Io on the sunward side so its tiny umbra reads against Jupiter cloud bands.'
  },
  {
    id: 'solar-eclipse',
    kicker: 'Earth-Moon-Sun',
    title: 'Solar eclipse geometry',
    copy: 'Places the Moon between Earth and the Sun, then frames the alignment from near Earth.'
  },
  {
    id: 'earthrise',
    kicker: 'Lunar surface',
    title: 'Earthrise from the Moon',
    copy: 'Drops the camera just above the near-side lunar surface and looks back toward Earth.'
  },
  {
    id: 'aurora-over-earth',
    kicker: 'Upper atmosphere',
    title: 'Aurora over Earth',
    copy: 'Frames the night-side polar oval so the green oxygen curtains shimmer above the limb.'
  },
  {
    id: 'saturn-ring-sweep',
    kicker: 'Cassini-style',
    title: 'Saturn ring-plane sweep',
    copy: 'Skims Saturn close to the ring plane so the rings feel enormous and paper-thin.'
  },
  {
    id: 'wormhole-approach',
    kicker: 'Speculative physics',
    title: 'Wormhole near Saturn',
    copy: 'Loads a spherical Einstein-Rosen bridge and frames its bright gravitational-lens rim.'
  },
  {
    id: 'halley-flyby',
    kicker: 'Deep space',
    title: "Halley's Comet flyby",
    copy: 'Loads the comet layer on demand and frames the nucleus, coma, and dust tail together.'
  },
  {
    id: 'asteroid-crossing',
    kicker: 'Scale reveal',
    title: 'Asteroid belt crossing',
    copy: 'Moves into the main belt so it reads as sparse, cold, and huge rather than sci-fi dense.'
  },
  {
    id: 'voyager-edge',
    kicker: 'Human artifact',
    title: 'Voyager leaving the planets',
    copy: 'Tracks Voyager 1 far outside the planetary system with the Sun reduced to a distant star.'
  }
];

function renderSpaceEvents() {
  if (!spaceEventsList || spaceEventsList.children.length) return;
  SPACE_EVENT_SCENES.forEach((event) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'space-event-card';
    button.dataset.eventId = event.id;
    button.innerHTML = `
      <span class="space-event-kicker">${event.kicker}</span>
      <span class="space-event-title">${event.title}</span>
      <span class="space-event-copy">${event.copy}</span>
    `;
    button.addEventListener('click', () => {
      runSpaceEvent(event.id);
      closeSpaceEvents();
    });
    spaceEventsList.appendChild(button);
  });
}

// The panel's "In the sky soon" list, loaded with the panel because it
// brings the account modules (and the bot check) only when someone looks.
function renderSkySoon() {
  const list = document.getElementById('sky-soon-list');
  if (!list) return;
  import('./sky/skysoon.js').then(({ mountSkySoon }) => {
    mountSkySoon(list, {
      source: 'viewer',
      onShow: (event) => {
        closeSpaceEvents();
        if (window.location.hash === `#${event.body}`) syncFocusToHash({ interrupt: true, preservePanel: true, force: true });
        else window.location.hash = event.body;
      },
    });
  }).catch((err) => console.warn('[sky-soon] unavailable', err));
}

function openSpaceEvents() {
  closeTransientPanels('spaceEvents');
  spaceEventsOpen = true;
  renderSpaceEvents();
  renderSkySoon();
  if (spaceEventsPanel) spaceEventsPanel.classList.add('visible');
  const first = spaceEventsList && spaceEventsList.querySelector('.space-event-card');
  if (first) first.focus({ preventScroll: true });
}

function closeSpaceEvents() {
  spaceEventsOpen = false;
  if (spaceEventsPanel) spaceEventsPanel.classList.remove('visible');
}

function toggleSpaceEvents() {
  if (spaceEventsOpen) closeSpaceEvents();
  else openSpaceEvents();
}

function findMoon(parentPlanetName, moonName) {
  const list = moons[parentPlanetName] || [];
  const key = String(moonName || '').toLowerCase();
  return list.find((moon) => String(moon.userData?.name || moon.name || '').toLowerCase() === key) || null;
}

function getWorldPosition(object) {
  const position = new THREE.Vector3();
  if (object) object.getWorldPosition(position);
  return position;
}

function getSafeAxisFromSun(objectOrPosition) {
  const position = objectOrPosition instanceof THREE.Vector3
    ? objectOrPosition.clone()
    : getWorldPosition(objectOrPosition);
  if (position.lengthSq() < 0.0001) return new THREE.Vector3(1, 0, 0);
  return position.normalize();
}

function getAxisBasis(axis) {
  const normal = axis.clone().normalize();
  const referenceUp = Math.abs(normal.dot(new THREE.Vector3(0, 1, 0))) > 0.92
    ? new THREE.Vector3(0, 0, 1)
    : new THREE.Vector3(0, 1, 0);
  const side = new THREE.Vector3().crossVectors(referenceUp, normal).normalize();
  const up = new THREE.Vector3().crossVectors(normal, side).normalize();
  return { axis: normal, side, up };
}

const _moonSunwardQuat = new THREE.Quaternion();

function setMoonSunward(parentPlanetName, moonName) {
  const planet = planets[parentPlanetName];
  const moon = findMoon(parentPlanetName, moonName);
  if (!planet || !moon) return null;

  const axis = getSafeAxisFromSun(planet);
  const distance = moon.userData.distance || moon.userData.baseDistance || 10;
  const local = axis.multiplyScalar(-distance);
  // Moons of tilted planets live inside the axial-tilt moonFrame —
  // bring the world-space offset into the parent frame before deriving
  // the orbit angle, or the snap lands off the sun-planet line.
  if (moon.parent) {
    moon.parent.getWorldQuaternion(_moonSunwardQuat).invert();
    local.applyQuaternion(_moonSunwardQuat);
  }
  // Orbits run x = cos(θ)·d, z = −sin(θ)·d (counterclockwise from +Y).
  moon.userData.orbitAngle = Math.atan2(-local.z, local.x);
  moon.position.set(local.x, 0, local.z);
  updateMoonTransitShadows(parentPlanetName, planet);
  return moon;
}

function interruptCameraMotion() {
  if (activeCameraTween) activeCameraTween.kill();
  activeCameraTween = null;
  controls.enabled = true;
  isAnimating = false;
}

function runEventCameraPan({
  getLookAt,
  duration,
  angle = Math.PI * 0.18,
  lift = 0.04,
  ease = 'sine.inOut',
  minDistance = 2,
  maxDistance = 6000,
  panAxisFactory = null
}) {
  const startLookAt = getLookAt();
  const startOffset = camera.position.clone().sub(startLookAt);
  const distance = Math.max(startOffset.length(), minDistance);
  const fallbackAxis = getAxisBasis(getSafeAxisFromSun(startLookAt)).up;
  const panAxis = (panAxisFactory ? panAxisFactory(startLookAt) : fallbackAxis).clone().normalize();
  const liftAxis = getAxisBasis(getSafeAxisFromSun(startLookAt)).up;
  const animationState = { progress: 0 };

  activeCameraTween = gsap.to(animationState, {
    progress: 1,
    duration,
    ease,
    onUpdate: () => {
      const t = animationState.progress;
      const lookAt = getLookAt();
      const rotatedOffset = startOffset.clone().applyAxisAngle(panAxis, angle * t);
      const liftOffset = liftAxis.clone().multiplyScalar(Math.sin(t * Math.PI) * distance * lift);
      controls.target.copy(lookAt);
      camera.position.copy(lookAt).add(rotatedOffset).add(liftOffset);
      camera.lookAt(lookAt);
    },
    onComplete: () => {
      controls.target.copy(getLookAt());
      controls.minDistance = minDistance;
      controls.maxDistance = maxDistance;
      controls.enabled = true;
      isAnimating = false;
      activeCameraTween = null;
    }
  });
}

function animateEventCamera({
  focusObject = null,
  getLookAt,
  getCameraPosition,
  navText,
  duration = 3.2,
  ease = 'power3.inOut',
  minDistance = 2,
  maxDistance = 6000,
  panDuration = 0,
  panAngle = Math.PI * 0.18,
  panLift = 0.04,
  panEase = 'sine.inOut',
  panAxisFactory = null
}) {
  if (!getLookAt || !getCameraPosition) return;
  if (scaleModeActive) exitScaleMode();
  if (flyMode) return;

  interruptCameraMotion();
  closeTransientPanels();
  const controlsPanel = document.getElementById('controls-panel');
  if (controlsPanel) controlsPanel.classList.add('collapsed');

  selectedObject = focusObject;
  if (navText && navLabel) navLabel.textContent = navText;
  if (focusObject) void ensureFocusedDetail(focusObject);

  const startLookAt = controls.target.clone();
  const startCameraPos = camera.position.clone();
  const animationState = { progress: 0 };
  isAnimating = true;
  controls.enabled = false;

  leaseCameraToTween();
  activeCameraTween = gsap.to(animationState, {
    progress: 1,
    duration,
    ease,
    onUpdate: () => {
      const smoothT = animationState.progress * animationState.progress * (3 - 2 * animationState.progress);
      const lookAt = getLookAt();
      const cameraTarget = getCameraPosition(lookAt);
      controls.target.lerpVectors(startLookAt, lookAt, smoothT);
      lerpRoundSun(camera.position, startCameraPos, cameraTarget, smoothT);
    },
    onComplete: () => {
      controls.target.copy(getLookAt());
      controls.minDistance = minDistance;
      controls.maxDistance = maxDistance;
      activeCameraTween = null;
      if (panDuration > 0) {
        runEventCameraPan({
          getLookAt,
          duration: panDuration,
          angle: panAngle,
          lift: panLift,
          ease: panEase,
          minDistance,
          maxDistance,
          panAxisFactory
        });
        return;
      }
      controls.enabled = true;
      isAnimating = false;
    }
  });
}

function showEventCaption(event) {
  if (!eventCaption || !eventCaptionTitle || !eventCaptionCopy) return;
  eventCaptionTitle.textContent = event.title;
  eventCaptionCopy.textContent = event.copy;
  eventCaption.classList.add('visible');
  clearTimeout(eventCaptionTimer);
  eventCaptionTimer = setTimeout(() => {
    eventCaption.classList.remove('visible');
  }, event.duration || 7200);
}

function runSpaceEvent(eventId) {
  const event = SPACE_EVENT_SCENES.find((item) => item.id === eventId);
  if (event) showEventCaption(event);

  switch (eventId) {
    case 'io-transit':
      runIoTransitEvent();
      break;
    case 'solar-eclipse':
      runSolarEclipseEvent();
      break;
    case 'earthrise':
      runEarthriseEvent();
      break;
    case 'aurora-over-earth':
      runAuroraOverEarthEvent();
      break;
    case 'saturn-ring-sweep':
      runSaturnRingSweepEvent();
      break;
    case 'wormhole-approach':
      runWormholeApproachEvent();
      break;
    case 'halley-flyby':
      runHalleyFlybyEvent();
      break;
    case 'asteroid-crossing':
      runAsteroidCrossingEvent();
      break;
    case 'voyager-edge':
      runVoyagerEdgeEvent();
      break;
    default:
      break;
  }
}

function runIoTransitEvent() {
  const jupiter = planets.jupiter;
  if (!jupiter) return;
  const io = setMoonSunward('jupiter', 'Io');
  const radius = jupiter.userData.radius || 10;

  animateEventCamera({
    focusObject: jupiter,
    navText: 'Io transit',
    minDistance: radius * 2.2,
    maxDistance: radius * 180,
    getLookAt: () => getWorldPosition(jupiter),
    getCameraPosition: (lookAt) => {
      const basis = getAxisBasis(getSafeAxisFromSun(lookAt));
      const moonLift = io ? Math.min(radius * 0.6, (io.userData.radius || 0.5) * 4) : 0;
      return lookAt.clone()
        .add(basis.axis.clone().multiplyScalar(-radius * 8.5))
        .add(basis.side.clone().multiplyScalar(radius * 1.25))
        .add(basis.up.clone().multiplyScalar(radius * (1.7 + moonLift * 0.02)));
    }
  });
}

// Astronomy mode (src/astro/): a planetarium of the real sky from the
// observer's place, its own scene drawn in place of the solar system while it
// is on. It reads and drives the viewer's clock, so Real, a chosen moment and
// the speeds work there as here.
let astroMode = null;
let astroLoader = null;
let astroOpenedAtBoot = false;
let astroAskedForIss = false;
function astroModeActive() {
  return !!(astroMode && astroMode.active);
}
function astroSimRate() {
  return timeSpeed * (realisticScaleMode ? 1 : ARTISTIC_TIME_RATE);
}
function setAstroSimRate(simSecondsPerSecond) {
  timePreset = null;
  timeReverse = simSecondsPerSecond < 0;
  timeSpeed = simSecondsPerSecond / (realisticScaleMode ? 1 : ARTISTIC_TIME_RATE);
  realClockLockedJD = null;
  syncTimeControls();
}
function openAstronomyMode(opts = {}) {
  if (rocketryActive || rocketryLoading || (renderer && renderer.xr && renderer.xr.isPresenting)) return Promise.resolve(null);
  if (!loadingScreenDismissed && opts.hash) astroOpenedAtBoot = true;
  if (flyMode) exitFlyMode();
  if (!astroLoader) {
    astroLoader = import('./astro/index.js').then(({ createAstronomyMode }) => {
      astroMode = createAstronomyMode({
        renderer,
        bloom: !!graphicsConfig.bloom,
        msaa: renderer.capabilities.isWebGL2 && !isMobileLikeDevice() ? 4 : 0,
        liteSky: activeGraphicsPreset === 'low',
        getJD: () => simulationJD,
        setJD: (jd) => {
          if (!Number.isFinite(jd)) return;
          simulationJD = jd;
          realClockLockedJD = null;
          syncTimeControls();
        },
        setRate: (rate) => setAstroSimRate(rate),
        goNow: () => { timeReverse = false; setTimePreset('real'); },
        getClock: () => ({
          live: timePreset === 'real' && !clockAwayFromNow() && timeSpeed > 0,
          rate: astroSimRate(),
        }),
        getStarLayers: () => ({ bright: starCatalog, faint: faintStars }),
        // The solar system's current frame, for the entrance's cross-fade.
        snapshot: () => {
          try {
            renderFrame();
            const src = renderer.domElement;
            const c = document.createElement('canvas');
            c.width = src.width; c.height = src.height;
            c.getContext('2d').drawImage(src, 0, 0);
            return c;
          } catch { return null; }
        },
        // The ISS's elements (the same set the viewer flies its model on).
        getIssSatrec: () => {
          if (!issSatrec && !astroAskedForIss) { astroAskedForIss = true; loadIssElements(); }
          return issSatrec;
        },
        getSkyMap: () => {
          const u = starfieldMaterial && starfieldMaterial.uniforms;
          if (!u || !u.skyTexture.value) return null;
          return { texture: u.skyTexture.value, L0: u.skyL0.value, logMax: u.skyLogMax.value, radianceScale: u.skyRadianceScale.value };
        },
        onEnter: () => {
          // The 8K map for the Milky Way where the preset allows it.
          upgradeSkyMap();
          // A pace that spins the sky past reading is brought to real time.
          if (Math.abs(astroSimRate()) > 86400) setAstroSimRate(1);
        },
        onExit: () => {
          if (astroOpenedAtBoot) {
            astroOpenedAtBoot = false;
            frameStartupFocus(resolveBootFocusId(), { persistHash: true });
          }
        },
      });
      if (typeof window !== 'undefined') window.__astro = astroMode;
      return astroMode;
    });
  }
  return astroLoader.then((mode) => {
    if (eclipseModeActive()) eclipseMode.exit();
    // The entrance's cross-fade only from the running viewer (a link opened
    // at load has no solar system frame to leave).
    mode.enter({ transition: loadingScreenDismissed, ...opts });
    countStep('astro_open', { once: true });
    return mode;
  });
}

// Eclipse mode (src/eclipse/mode.js): the clock, the scale and the camera.
let eclipseSceneState = null;
function startEclipseMode(event) {
  if (!planets.earth || !findMoon('earth', 'Moon') || rocketryActive) return;
  if (!eclipseMode) {
    eclipseMode = createEclipseMode({
      getJD: () => simulationJD,
      setJD: (jd) => { simulationJD = jd; realClockLockedJD = null; },
      setRate: (simSecondsPerSecond) => {
        timePreset = null;
        timeSpeed = simSecondsPerSecond / (realisticScaleMode ? 1 : ARTISTIC_TIME_RATE);
        syncTimeControls();
      },
      // True scale, where the Moon and its shadow are where they really are.
      enterScene: () => {
        eclipseSceneState = { realistic: realisticScaleMode };
        if (!realisticScaleMode && window.__setRealisticScaleMode) window.__setRealisticScaleMode(true);
      },
      // The scale first: Real time's speed depends on it.
      leaveScene: () => {
        const previous = eclipseSceneState;
        eclipseSceneState = null;
        if (previous && !previous.realistic && realisticScaleMode && window.__setRealisticScaleMode) {
          window.__setRealisticScaleMode(false);
        }
        setTimePreset('real');
      },
      frame: (e) => frameEclipse(e),
    });
  }
  eclipseMode.enter(event);
}

// Famous photos as live views (src/photos/mode.js, the photos in
// src/data/famousPhotos.js): true scale, the clock at the photo's moment and
// the camera held where the spacecraft was, with its lens and the picture's
// orientation, until a drag or the wheel takes it back. Bodies smaller than a
// pixel there (the Earth from Voyager 1) are drawn as points
// (src/photos/points.js). The modules load on first use.
let famousPhotoMode = null;
let famousPhotoLib = null;
let famousPhotoLoader = null;
let famousPhotoHold = null;
let famousPhotoRelease = null;
let famousPhotoPoints = null;
let famousPhotoSceneState = null;
const FAMOUS_PHOTO_RELEASE_S = 0.9;
const _fpTarget = new THREE.Vector3();
const _fpDir = new THREE.Vector3();
const _fpRight = new THREE.Vector3();
const _fpUp = new THREE.Vector3();
const _fpBack = new THREE.Vector3();
const _fpNdc = new THREE.Vector3();
const _fpLook = new THREE.Vector3();
const _fpY = new THREE.Vector3(0, 1, 0);
const _fpBasis = new THREE.Matrix4();
const _fpQuat = new THREE.Quaternion();

// For checks: window.__famousPhoto.hold, .lib.
if (typeof window !== 'undefined') {
  window.__famousPhoto = {
    get hold() { return famousPhotoHold; },
    get lib() { return famousPhotoLib; },
    start: (slug) => startFamousPhoto(slug),
  };
}

function famousPhotoModeActive() {
  return !!(famousPhotoMode && famousPhotoMode.active);
}

function famousPhotoModules() {
  if (!famousPhotoLoader) {
    famousPhotoLoader = Promise.all([
      import('./photos/mode.js'), import('./data/famousPhotos.js'), import('./photos/geometry.js'), import('./photos/points.js'),
    ]).then(([mode, data, geometry, points]) => {
      famousPhotoLib = { ...mode, ...data, ...geometry, ...points };
      return famousPhotoLib;
    });
  }
  return famousPhotoLoader;
}

// slug null opens the picker.
async function startFamousPhoto(slug) {
  if (rocketryActive || flyMode || scaleModeActive || lessonActive()) return false;
  const lib = await famousPhotoModules();
  if (!famousPhotoMode) {
    famousPhotoMode = lib.createPhotoMode({
      photos: lib.FAMOUS_PHOTOS,
      enterScene: enterFamousPhotoScene,
      leaveScene: leaveFamousPhotoScene,
      place: placeFamousPhoto,
      setPlaying: (on) => applyViewClock(simulationJD, on ? 'real' : 0),
      getJD: () => simulationJD,
      release: releaseFamousPhotoHold,
      isHeld: () => Boolean(famousPhotoHold),
      sunNdc: () => (sun ? famousPhotoNdc(sun) : null),
      targetNdc: () => (famousPhotoHold ? famousPhotoNdc(famousPhotoHold.target) || [0, 0] : [0, 0]),
    });
    // A drag or a wheel zoom hands the camera back for looking around.
    const release = () => { if (famousPhotoHold && !isAnimating) releaseFamousPhotoHold(); };
    renderer.domElement.addEventListener('pointerdown', release, { capture: true });
    renderer.domElement.addEventListener('wheel', release, { capture: true, passive: true });
  }
  if (!slug) { famousPhotoMode.openPicker(); return true; }
  return famousPhotoMode.enter(slug);
}

function enterFamousPhotoScene() {
  famousPhotoSceneState = { realistic: realisticScaleMode, fov: camera.fov };
  if (eclipseModeActive()) eclipseMode.exit();
  if (documentaryActive) exitDocumentary({ returnHome: false });
  if (photoModeActive) exitPhotoMode();
  if (coolWorldsShowreelActive) clearCoolWorldsShowreel();
  if (dsoFocusActive) disableDsoZoomMode();
  if (cupolaView && cupolaView.active) cupolaView.leave();
  closeTransientPanels();
  controls.autoRotate = false;
  releaseIssCameraUp();
  if (!famousPhotoPoints) famousPhotoPoints = famousPhotoLib.createPhotoPoints(scene);
}

function leaveFamousPhotoScene(photo) {
  const state = famousPhotoSceneState;
  famousPhotoSceneState = null;
  restoreFamousPhotoOrientation(famousPhotoHold);
  famousPhotoHold = null;
  famousPhotoRelease = null;
  setFamousPhotoStarsHidden(false);
  if (famousPhotoPoints) famousPhotoPoints.clear();
  camera.up.set(0, 1, 0);
  camera.fov = (state && state.fov) || 45;
  camera.updateProjectionMatrix();
  if (state && !state.realistic && realisticScaleMode && window.__setRealisticScaleMode) window.__setRealisticScaleMode(false);
  setTimePreset('real');
  const target = photo && resolveViewBody(photo.target);
  if (target) afterBodiesPlaced(() => focusOnObject(target, { interrupt: true, force: true, preservePanel: true }));
}

// The clock to the photo's moment, then (once the bodies are there) the
// target in focus, which loads its detail maps, and the camera held on the
// spacecraft. Resolves true once it is held.
function placeFamousPhoto(photo) {
  const lib = famousPhotoLib;
  restoreFamousPhotoOrientation(famousPhotoHold);
  famousPhotoHold = null;
  famousPhotoRelease = null;
  killExitFlyTweens();
  interruptCameraMotion();
  applyViewScale(true);
  applyViewClock(dateToJD(Date.parse(photo.at)), 0);
  return new Promise((resolve) => {
    afterBodiesPlaced(() => {
      const target = resolveViewBody(photo.target);
      // The body the spacecraft's vector is measured from (the Moon for
      // Earthrise, taken from lunar orbit), and the one focused, whose
      // detail maps load (the nearer of the two).
      const origin = photo.origin ? resolveViewBody(photo.origin) : target;
      const focus = photo.focus ? resolveViewBody(photo.focus) : origin;
      if (!target || !origin || !focus || !famousPhotoModeActive() && !IS_SCREENSAVER) { resolve(false); return; }
      const offset = new THREE.Vector3().fromArray(lib.eclipticToScene(photo.from, AU_TO_SCENE / lib.KM_PER_AU));
      famousPhotoPoints.set((photo.points || []).map((p) => {
        const object = resolveViewBody(p.body);
        return object ? { object, radius: getBodyBaseRadius(object), color: new THREE.Color(p.color || '#ffffff'), gain: p.gain || 1 } : null;
      }).filter(Boolean));
      const hold = () => {
        famousPhotoHold = { photo, target, origin, offset, radius: getBodyBaseRadius(origin), oriented: famousPhotoOrient(photo) };
        resolve(true);
      };
      const focusOffset = offset.clone();
      if (focus !== origin) focusOffset.add(origin.getWorldPosition(_fpTarget)).sub(focus.getWorldPosition(_fpLook));
      focusOnObject(focus, { interrupt: true, force: true, preservePanel: true, duration: 0.05, offset: focusOffset, onComplete: hold });
      // Some focus paths skip the tween (already focused): hold anyway.
      if (!isAnimating) hold();
    });
  });
}

// A held photo's bodies on their IAU poles and meridians (src/photos/
// geometry.js IAU_ROTATION): the tilt group is set from the body-fixed axes
// and the surface mesh inside it squared up, every frame while held, and both
// are put back on release. SphereGeometry lays map longitude L at local
// (-cos L, 0, sin L), so the tilt group's local -X is the meridian, +Z east
// and +Y the pole.
function famousPhotoOrient(photo) {
  const out = [];
  for (const id of photo.iau || []) {
    const body = resolveViewBody(id);
    const tilt = body && body.getObjectByName('tiltGroup');
    const mesh = tilt && tilt.children.find((c) => c.isMesh);
    if (!tilt || !mesh) continue;
    out.push({ id, tilt, mesh, tiltQ: tilt.quaternion.clone(), meshQ: mesh.quaternion.clone() });
  }
  return out;
}
const _fpAxisX = new THREE.Vector3();
const _fpAxisY = new THREE.Vector3();
const _fpAxisZ = new THREE.Vector3();
// A held photo's ringshine on Saturn (src/shaders/gasGiant.js), 0 otherwise.
function setFamousPhotoRingShine(value) {
  const saturn = planets.saturn;
  saturn && saturn.traverse((o) => {
    const u = o.isMesh && o.material && o.material.userData && o.material.userData.ringShadowUniforms;
    if (u && u.ringShineStrength) u.ringShineStrength.value = value;
  });
}
function applyFamousPhotoOrientation(hold) {
  for (const o of hold.oriented || []) {
    const axes = famousPhotoLib.iauAxes(o.id, simulationJD);
    if (!axes) continue;
    // Pluto's map has longitude 0 at its left edge; Saturn's (Hubble and
    // Cassini, October 2026) and Jupiter's (Cassini, docs/textures/giants.md)
    // have it in the middle, half a turn round the pole.
    const centred = o.id === 'saturn' || o.id === 'jupiter';
    _fpAxisX.fromArray(axes.meridian);
    if (!centred) _fpAxisX.negate();
    _fpAxisY.fromArray(axes.pole);
    _fpAxisZ.fromArray(axes.east);
    if (centred) _fpAxisZ.negate();
    _fpBasis.makeBasis(_fpAxisX, _fpAxisY, _fpAxisZ);
    o.tilt.quaternion.setFromRotationMatrix(_fpBasis);
    o.mesh.quaternion.identity();
    o.tilt.updateMatrixWorld(true);
  }
}
function restoreFamousPhotoOrientation(hold) {
  if (hold && hold.photo.ringshine) setFamousPhotoRingShine(0);
  for (const o of (hold && hold.oriented) || []) {
    o.tilt.quaternion.copy(o.tiltQ);
    o.mesh.quaternion.copy(o.meshQ);
  }
}

// Shots (src/xr/shots.js): a real moment at true scale with the camera held
// where the shot puts it round its body, every frame after the follow code,
// and the bodies in its `iau` on their IAU poles. The museum sessions are
// made of them; in VR the head rides the held camera. It lets go when
// another body is focused or, in 2D, on a drag. window.__shots for checks.
let shotHold = null;
let shotFlight = null;
let shotReleaseWired = false;
const _shotC = new THREE.Vector3();
const _shotSun = new THREE.Vector3();
const _shotPole = new THREE.Vector3();
const _shotQ = new THREE.Quaternion();
const _shotM = new THREE.Matrix4();
const _shotPose = { position: new THREE.Vector3(), target: new THREE.Vector3(), up: new THREE.Vector3() };
function wireShotRelease() {
  if (shotReleaseWired) return;
  shotReleaseWired = true;
  renderer.domElement.addEventListener('pointerdown', () => {
    if (renderer.xr.isPresenting) return;
    if (shotFlight) stopShotFlight();
    else if (shotHold) stopShot();
  }, { capture: true });
}
async function startShot(idOrShot) {
  const shot = typeof idOrShot === 'string' ? SHOTS[idOrShot] : idOrShot;
  if (!shot || rocketryActive || flyMode) return false;
  await famousPhotoModules(); // iauAxes and the orientation helpers
  stopShotFlight();
  stopShot();
  wireShotRelease();
  killExitFlyTweens();
  interruptCameraMotion();
  applyViewScale(true);
  applyViewClock(dateToJD(Date.parse(shot.at)), shot.speed ?? 0);
  await new Promise((resolve) => afterBodiesPlaced(resolve));
  const body = resolveViewBody(shot.body);
  if (!body) return false;
  if (selectedObject !== body) {
    focusOnObject(body, { interrupt: true, force: true, preservePanel: true, duration: 0.05 });
    await xrSettle();
  }
  const spec = { ...shot, iau: shot.iau || [shot.body] };
  // `photo` because the orientation helpers read the ringshine from it.
  shotHold = { id: typeof idOrShot === 'string' ? idOrShot : null, shot: spec, photo: spec, body, oriented: famousPhotoOrient(spec) };
  updateShotFrame();
  return true;
}
function stopShot() {
  if (!shotHold) return;
  restoreFamousPhotoOrientation(shotHold);
  shotHold = null;
}
// Where a shot puts the camera round its body now, in _shotPose.
function shotLivePose(shot, body) {
  body.getWorldPosition(_shotC);
  if (sun) sun.getWorldPosition(_shotSun).sub(_shotC).normalize();
  else _shotSun.copy(_shotC).negate().normalize();
  const axes = shot.frame === 'equator' ? famousPhotoLib.iauAxes(shot.body, simulationJD) : null;
  const tilt = shot.frame === 'equator' && !axes ? body.getObjectByName('tiltGroup') : null;
  if (axes) _shotPole.fromArray(axes.pole);
  else if (tilt) _shotPole.set(0, 1, 0).applyQuaternion(tilt.getWorldQuaternion(_shotQ));
  else _shotPole.set(0, 1, 0);
  return shotPose(shot, _shotC, getBodyBaseRadius(body), _shotSun, _shotPole.normalize(), _shotPose);
}
function updateShotFrame() {
  const hold = shotHold;
  if (!hold || isAnimating || flyMode || rocketryActive) return;
  if (selectedObject !== hold.body) { stopShot(); return; }
  const { shot, body } = hold;
  applyFamousPhotoOrientation(hold);
  if (shot.ringshine) setFamousPhotoRingShine(shot.ringshine);
  shotLivePose(shot, body);
  camera.position.copy(_shotPose.position);
  camera.up.copy(_shotPose.up);
  controls.target.copy(_shotPose.target);
  camera.lookAt(_shotPose.target);
  camera.updateMatrixWorld(true);
  _focusPrevObj = null;
}

// Travel between shots (planFlight in src/xr/shots.js): from the shot held,
// or the camera round the focused body, straight back, across and in to the
// next shot, which then holds. The clock keeps each end's own pace on the
// legs; the shot left keeps its poles, ringshine and the focus until halfway
// across. In the headset, and in 2D when the next shot is at another moment
// (the bodies would drag the path round the Sun as the clock ran), the turn
// and the clock's change are a cut under a blink: xrApi.shotFlightBlink, and
// in 2D a black layer over the canvas. It lets go like a hold.
// window.__shots.fly(id).
const flightEnd = () => ({ centre: new THREE.Vector3(), position: new THREE.Vector3(), forward: new THREE.Vector3(), quaternion: new THREE.Quaternion(), dist: 1, radius: 1 });
const _flightA = flightEnd();
const _flightB = flightEnd();
const _flightFwd = new THREE.Vector3();
const _flightAt = new THREE.Vector3();
const _flightUp = new THREE.Vector3();
const _flightQA = new THREE.Quaternion();
const _flightQB = new THREE.Quaternion();
// Looking from the camera at what an end's shot looks at, with its up: on
// the legs that is the shot's own orientation, and across it keeps the
// subject in view while the camera swings round.
function flightLook(end, out) {
  _flightAt.copy(end.position).addScaledVector(end.forward, end.dist);
  _flightUp.set(0, 1, 0).applyQuaternion(end.quaternion);
  return out.setFromRotationMatrix(_shotM.lookAt(camera.position, _flightAt, _flightUp));
}
let shotFlightVeil = null;
function setShotFlightVeil(k) {
  if (!shotFlightVeil) {
    if (!k) return;
    shotFlightVeil = document.createElement('div');
    shotFlightVeil.style.cssText = 'position:fixed;inset:0;background:#000;pointer-events:none;opacity:0';
    renderer.domElement.after(shotFlightVeil);
  }
  shotFlightVeil.style.opacity = String(k);
}
// A flight's end as it stands now: a shot round its body, or a camera pose
// carried along with one.
function placeFlightEnd(end, out) {
  end.body.getWorldPosition(out.centre);
  out.radius = getBodyBaseRadius(end.body);
  if (end.shot) {
    shotLivePose(end.shot, end.body);
    out.position.copy(_shotPose.position);
    out.forward.subVectors(_shotPose.target, _shotPose.position);
    out.dist = out.forward.length();
    out.forward.divideScalar(out.dist);
    out.quaternion.setFromRotationMatrix(_shotM.lookAt(_shotPose.position, _shotPose.target, _shotPose.up));
  } else {
    out.position.copy(out.centre).add(end.offset);
    out.quaternion.copy(end.quaternion);
    out.forward.set(0, 0, -1).applyQuaternion(end.quaternion);
    out.dist = end.dist;
  }
  return out;
}
// A shot's pace in sim days a real second (true scale, so 1x is real time).
const shotPace = (shot) => (!shot || !shot.speed ? 0 : shot.speed === 'real' ? 1 : shot.speed) / 86400;
async function flyToShot(id, { cut } = {}) {
  const shot = SHOTS[id];
  if (!shot || rocketryActive || flyMode) return false;
  stopShotFlight();
  if (shotHold && shotHold.id === id) return true;
  // Nothing to leave from (the overview, the drawn scale, a move under
  // way): there at once.
  const fromBody = shotHold ? shotHold.body : selectedObject;
  if (!realisticScaleMode || !fromBody || isAnimating) return startShot(id);
  await famousPhotoModules();
  const body = resolveViewBody(shot.body);
  if (!body) return false;
  wireShotRelease();
  killExitFlyTweens();
  interruptCameraMotion();
  let from;
  if (shotHold) {
    from = { body: shotHold.body, shot: shotHold.shot, hold: shotHold };
  } else {
    const centre = fromBody.getWorldPosition(new THREE.Vector3());
    from = { body: fromBody, offset: camera.position.clone().sub(centre), quaternion: camera.quaternion.clone(), dist: camera.position.distanceTo(centre) };
  }
  shotHold = null; // the flight carries its poles until halfway
  const to = { id, body, shot: { ...shot, iau: shot.iau || [shot.body] } };
  const jdB = dateToJD(Date.parse(shot.at));
  const plan = planFlight(placeFlightEnd(from, _flightA), placeFlightEnd(to, _flightB), { cut: cut ?? (renderer.xr.isPresenting || Math.abs(jdB - simulationJD) > 10 / 1440) });
  const f = { from, to, plan, t0: performance.now(), jd0: simulationJD, jdB, paceA: shotPace(from.shot), paceB: shotPace(to.shot), switched: false, holdB: null, blink: 0, part: 'out' };
  f.jdOut = f.jd0 + plan.tAcross * f.paceA;
  f.jdIn = f.jdB - (plan.T - plan.tIn) * f.paceB;
  applyViewClock(simulationJD, 0);
  shotFlight = f;
  updateShotFlight();
  return true;
}
function stopShotFlight() {
  const f = shotFlight;
  if (!f) return;
  shotFlight = null;
  const hold = f.switched ? f.holdB : f.from.hold;
  if (hold) restoreFamousPhotoOrientation(hold);
  setShotFlightVeil(0);
}
function updateShotFlight() {
  const f = shotFlight;
  if (!f) return;
  if (flyMode || rocketryActive) { stopShotFlight(); return; }
  const t = (performance.now() - f.t0) / 1000;
  const p = flightProgress(f.plan, t);
  f.blink = p.blink;
  f.part = p.part;
  setShotFlightVeil(renderer.xr.isPresenting ? 0 : p.blink);
  const crossed = p.part === 'in' || p.across >= 0.5;
  simulationJD = p.part === 'out' ? f.jd0 + t * f.paceA
    : p.part === 'in' ? f.jdB - (f.plan.T - t) * f.paceB
      : f.plan.cut ? (crossed ? f.jdIn : f.jdOut) : f.jdOut + (f.jdIn - f.jdOut) * THREE.MathUtils.smootherstep(p.across, 0, 1);
  // Halfway across: the next shot's poles, ringshine and focus.
  if (!f.switched && crossed) {
    f.switched = true;
    if (f.from.hold) restoreFamousPhotoOrientation(f.from.hold);
    f.holdB = { id: f.to.id, shot: f.to.shot, photo: f.to.shot, body: f.to.body, oriented: famousPhotoOrient(f.to.shot) };
    if (selectedObject !== f.to.body) focusOnObject(f.to.body, { interrupt: true, force: true, preservePanel: true, duration: 0.05 });
  }
  if (selectedObject !== (f.switched ? f.to.body : f.from.body)) { stopShotFlight(); return; }
  const hold = f.switched ? f.holdB : f.from.hold;
  if (hold) {
    applyFamousPhotoOrientation(hold);
    if (hold.shot.ringshine) setFamousPhotoRingShine(hold.shot.ringshine);
  }
  placeFlightEnd(f.from, _flightA);
  placeFlightEnd(f.to, _flightB);
  flightPoint(f.plan, p.sigma, _flightA, _flightB, camera.position);
  camera.quaternion.slerpQuaternions(flightLook(_flightA, _flightQA), flightLook(_flightB, _flightQB), p.turn);
  camera.up.set(0, 1, 0).applyQuaternion(camera.quaternion);
  _flightFwd.set(0, 0, -1).applyQuaternion(camera.quaternion);
  controls.target.copy(camera.position).addScaledVector(_flightFwd, f.switched ? _flightB.dist : _flightA.dist);
  camera.updateMatrixWorld(true);
  _focusPrevObj = null;
  if (p.done) {
    shotFlight = null;
    setShotFlightVeil(0);
    shotHold = f.holdB;
    applyViewClock(f.jdB, f.to.shot.speed ?? 0);
  }
}

if (typeof window !== 'undefined') {
  window.__shots = {
    start: (id) => startShot(id),
    stop: () => { stopShotFlight(); stopShot(); },
    fly: (id, opts) => flyToShot(id, opts),
    get list() { return Object.keys(SHOTS); },
    get hold() { return shotHold && { id: shotHold.id, body: shotHold.shot.body }; },
    get flight() { return shotFlight && { to: shotFlight.to.id, part: shotFlight.part, blink: shotFlight.blink, seconds: shotFlight.plan.T, t: (performance.now() - shotFlight.t0) / 1000 }; },
  };
}

function releaseFamousPhotoHold() {
  if (!famousPhotoHold) return;
  restoreFamousPhotoOrientation(famousPhotoHold);
  famousPhotoHold = null;
  famousPhotoRelease = {
    t: 0, fromFov: camera.fov, fromUp: camera.up.clone(),
    toFov: (famousPhotoSceneState && famousPhotoSceneState.fov) || 45,
  };
  // The orbit controls take over at the distance the spacecraft was.
  controls.minDistance = Math.min(controls.minDistance, camera.position.distanceTo(controls.target) * 0.5);
}

// A body on screen in device coordinates, or null behind the camera.
function famousPhotoNdc(object) {
  object.getWorldPosition(_fpNdc);
  camera.getWorldDirection(_fpLook);
  if (_fpLook.dot(_fpDir.copy(_fpNdc).sub(camera.position)) <= 0) return null;
  _fpNdc.project(camera);
  return [_fpNdc.x, _fpNdc.y];
}

// The spin axis of a body in world axes (the Earth's tilt group, or the body).
function famousPhotoBodyNorth(target) {
  const tilt = target.getObjectByName('tiltGroup') || target;
  return _fpUp.set(0, 1, 0).applyQuaternion(tilt.getWorldQuaternion(_fpQuat)).toArray();
}

// Photographs of sunlit worlds show no stars: the exposure that holds a
// lit surface leaves them far under the film's floor. The sky's star layers
// are hidden while a photo is held (a photo with `stars: true` keeps them),
// and so are the viewer's own additions no camera could see: the asteroids
// drawn at a visible size, the zodiacal glow, the radiation belts and the
// procedural swarm of small moons.
const FAMOUS_PHOTO_STAR_LAYERS = ['skybox', 'star-catalog', 'star-catalog-faint', 'planet-points'];
const FAMOUS_PHOTO_DECOR_LAYERS = ['asteroidBelt', 'zodiacalLight', 'vanAllenBelts'];
let famousPhotoStarsHidden = null;
function setFamousPhotoStarsHidden(hidden, keepStars = false) {
  if (hidden) {
    famousPhotoStarsHidden = new Map();
    const hide = (layer) => { if (layer && !famousPhotoStarsHidden.has(layer)) { famousPhotoStarsHidden.set(layer, layer.visible); layer.visible = false; } };
    // By name, every object that has it.
    const names = new Set(keepStars ? FAMOUS_PHOTO_DECOR_LAYERS : [...FAMOUS_PHOTO_STAR_LAYERS, ...FAMOUS_PHOTO_DECOR_LAYERS]);
    if (scene) scene.traverse((o) => { if (names.has(o.name)) hide(o); });
    for (const list of Object.values(moons)) for (const m of list) if (m.userData && m.userData.isMinorMoon) hide(m);
    // Voyager 1 and New Horizons took three of the photos, and the camera
    // sits where the probe is drawn.
    for (const { mesh, isProbe } of satelliteObjects) if (isProbe) hide(mesh);
    for (const path of probePaths) hide(path.line);
  } else if (famousPhotoStarsHidden) {
    for (const [layer, visible] of famousPhotoStarsHidden) layer.visible = visible;
    famousPhotoStarsHidden = null;
  }
}

// Each frame once the bodies and the follow code have moved: the held
// camera, or the ease back to the viewer's own up and lens after a release.
function updateFamousPhotoFrame(delta) {
  const hold = famousPhotoHold;
  const lib = famousPhotoLib;
  famousPhotoExposure = hold ? 2 ** (hold.photo.exposure || 0) : 1;
  famousPhotoBlackPoint = hold ? hold.photo.blackPoint || 0 : 0;
  famousPhotoSaturation = hold && hold.photo.saturation != null ? hold.photo.saturation : 1;
  const hideLayers = Boolean(hold);
  if (hideLayers !== Boolean(famousPhotoStarsHidden)) setFamousPhotoStarsHidden(hideLayers, Boolean(hold && hold.photo.stars === true));
  if (hold && lib && !isAnimating && !flyMode && !rocketryActive) {
    const { photo, target, origin } = hold;
    applyFamousPhotoOrientation(hold);
    // Every frame: the focus rebuild swaps Saturn's material once its detail
    // map lands, and the new one starts at 0.
    if (photo.ringshine) setFamousPhotoRingShine(photo.ringshine);
    camera.position.copy(origin.getWorldPosition(_fpTarget)).add(hold.offset);
    const t = target.getWorldPosition(_fpTarget);
    _fpDir.copy(t).sub(camera.position);
    const upHint = lib.upDirection(photo.up, photo.up === 'body' ? famousPhotoBodyNorth(target) : null);
    const frame = photo.frame || {};
    const fov = lib.containFov(photo.fov, frame.aspect || 1, camera.aspect);
    // The solved pointing when the photo has it, else the target placed
    // where the picture has it.
    // photo.shift moves the picture's content by that much in device
    // coordinates, measured against the original where the solved pointing
    // and the published crop's centre differ.
    const b = photo.look
      ? (photo.shift
        ? lib.photoBasis(lib.eclipticToScene(photo.look), upHint, photo.shift, fov, camera.aspect, photo.roll || 0)
        : lib.lookBasis(lib.eclipticToScene(photo.look), upHint, photo.roll || 0))
      : lib.photoBasis(_fpDir.toArray(), upHint, lib.screenOffset(frame.at, frame.aspect || 1, camera.aspect), fov, camera.aspect, photo.roll || 0);
    _fpRight.fromArray(b.right);
    _fpUp.fromArray(b.up);
    _fpBack.fromArray(b.back);
    _fpBasis.makeBasis(_fpRight, _fpUp, _fpBack);
    camera.quaternion.setFromRotationMatrix(_fpBasis);
    camera.up.copy(_fpUp);
    if (Math.abs(camera.fov - fov) > 1e-6) {
      camera.fov = fov;
      camera.updateProjectionMatrix();
    }
    const surface = Math.max(1e-9, hold.offset.length() - hold.radius);
    const near = Math.min(getOverviewCameraNear(), Math.max(1e-6, surface * 0.05));
    if (Math.abs(camera.near - near) > near * 0.01) setCameraNearPlane(near);
    controls.target.copy(t);
  } else if (famousPhotoRelease) {
    const r = famousPhotoRelease;
    r.t = Math.min(1, r.t + Math.min(delta, 0.1) / FAMOUS_PHOTO_RELEASE_S);
    const k = r.t * r.t * (3 - 2 * r.t);
    camera.up.copy(r.fromUp).lerp(_fpY, k).normalize();
    camera.fov = Math.exp(THREE.MathUtils.lerp(Math.log(r.fromFov), Math.log(r.toFov), k));
    camera.updateProjectionMatrix();
    if (r.t >= 1) famousPhotoRelease = null;
  }
  if (famousPhotoPoints) famousPhotoPoints.update(camera, renderer.domElement.height, 1);
}

// Where the Moon's shadow axis meets the Earth (a unit vector from the
// Earth's centre, world axes), or the point nearest the axis when it misses,
// from this frame's eclipse uniforms.
function eclipseGroundDirection() {
  const moonKm = earthEclipseUniforms.uEclipseMoonKm.value;
  const sunKm = earthEclipseUniforms.uEclipseSunKm.value;
  const u = moonKm.clone().sub(sunKm).normalize();
  const b = moonKm.dot(u);
  const c = moonKm.lengthSq() - R_EARTH_KM * R_EARTH_KM;
  const disc = b * b - c;
  const t = disc >= 0 ? -b - Math.sqrt(disc) : -b;
  return moonKm.clone().addScaledVector(u, t).normalize();
}

function frameEclipse(event) {
  const earth = planets.earth;
  const moon = findMoon('earth', 'Moon');
  if (!earth || !moon) return;
  if (event.kind === 'eclipse-lunar') {
    // The Moon's face toward the Earth, a little off the line so the limb
    // keeps some relief, close enough for the shadow's edge to read.
    const moonR = getBodyBaseRadius(moon);
    animateEventCamera({
      focusObject: moon,
      navText: event.title,
      minDistance: moonR * 1.3,
      maxDistance: moonR * 2000,
      getLookAt: () => getWorldPosition(moon),
      getCameraPosition: (lookAt) => {
        const toEarth = getWorldPosition(earth).sub(lookAt).normalize();
        const basis = getAxisBasis(toEarth);
        const dir = toEarth.clone().addScaledVector(basis.side, 0.22).addScaledVector(basis.up, 0.1).normalize();
        return lookAt.clone().addScaledVector(dir, moonR * 4.2);
      },
    });
    return;
  }
  // The day side with the shadow near the middle: from over the ground
  // point under the shadow, leaning toward the Sun.
  const earthR = getBodyBaseRadius(earth);
  animateEventCamera({
    focusObject: earth,
    navText: event.title,
    minDistance: earthR * 1.2,
    maxDistance: earthR * 2000,
    getLookAt: () => getWorldPosition(earth),
    getCameraPosition: (lookAt) => {
      const sunDir = earthEclipseUniforms.uEclipseSunKm.value.clone().normalize();
      const dir = eclipseGroundDirection().multiplyScalar(0.62).addScaledVector(sunDir, 0.38).normalize();
      return lookAt.clone().addScaledVector(dir, earthR * 3.6);
    },
  });
}

function runSolarEclipseEvent() {
  const earth = planets.earth;
  if (!earth) return;
  const moon = setMoonSunward('earth', 'Moon');
  const radius = earth.userData.radius || 4;

  animateEventCamera({
    focusObject: earth,
    navText: 'Solar eclipse',
    minDistance: radius * 1.6,
    maxDistance: radius * 150,
    getLookAt: () => moon ? getWorldPosition(moon) : getWorldPosition(earth),
    getCameraPosition: () => {
      const earthPos = getWorldPosition(earth);
      const basis = getAxisBasis(getSafeAxisFromSun(earthPos));
      return earthPos.clone()
        .add(basis.axis.clone().multiplyScalar(-radius * 5.4))
        .add(basis.side.clone().multiplyScalar(radius * 0.65))
        .add(basis.up.clone().multiplyScalar(radius * 0.85));
    }
  });
}

function runEarthriseEvent() {
  const earth = planets.earth;
  const moon = findMoon('earth', 'Moon');
  if (!earth || !moon) return;
  const moonRadius = moon.userData.radius || 1;

  animateEventCamera({
    focusObject: null,
    navText: 'Earthrise',
    minDistance: 0.8,
    maxDistance: 900,
    getLookAt: () => getWorldPosition(earth),
    getCameraPosition: () => {
      const moonPos = getWorldPosition(moon);
      const earthPos = getWorldPosition(earth);
      const toEarth = earthPos.clone().sub(moonPos).normalize();
      const basis = getAxisBasis(getSafeAxisFromSun(earthPos));
      return moonPos.clone()
        .add(toEarth.multiplyScalar(moonRadius * 2.5))
        .add(basis.side.clone().multiplyScalar(moonRadius * 1.4))
        .add(basis.up.clone().multiplyScalar(moonRadius * 1.1));
    }
  });
}

function getEarthNorthAuroraWorldPoint(scale = 1.08) {
  const earth = planets.earth;
  if (!earth) return null;
  const tiltGroup = earth.getObjectByName('tiltGroup');
  if (!tiltGroup) return null;
  const radius = earth.userData.radius || 2.5;
  const point = new THREE.Vector3(0, radius * scale, 0);
  return tiltGroup.localToWorld(point);
}

function runAuroraOverEarthEvent() {
  const earth = planets.earth;
  if (!earth) return;
  const radius = earth.userData.radius || 2.5;
  if (earthAuroras && earthAuroras.root) {
    earthAuroras.root.visible = true;
  }

  animateEventCamera({
    focusObject: earth,
    navText: 'Aurora',
    minDistance: radius * 1.5,
    maxDistance: radius * 120,
    getLookAt: () => getEarthNorthAuroraWorldPoint(1.08) || getWorldPosition(earth),
    getCameraPosition: (lookAt) => {
      const earthPos = getWorldPosition(earth);
      const poleNormal = lookAt.clone().sub(earthPos).normalize();
      const nightAxis = getSafeAxisFromSun(earthPos);
      const side = new THREE.Vector3().crossVectors(poleNormal, nightAxis).normalize();
      return lookAt.clone()
        .add(poleNormal.multiplyScalar(radius * 2.1))
        .add(nightAxis.multiplyScalar(radius * 4.2))
        .add(side.multiplyScalar(radius * 0.8));
    }
  });
}

function runSaturnRingSweepEvent() {
  const saturn = planets.saturn;
  if (!saturn) return;
  const ring = saturn.getObjectByName('ring');
  const radius = saturn.userData.radius || 7;
  const ringOuter = saturn.userData.ringOuterRadius || radius * 2.3;

  animateEventCamera({
    focusObject: saturn,
    navText: 'Ring-plane',
    minDistance: radius * 2,
    maxDistance: radius * 220,
    getLookAt: () => getWorldPosition(saturn),
    getCameraPosition: (lookAt) => {
      if (!ring) return lookAt.clone().add(new THREE.Vector3(ringOuter * 3, ringOuter * 0.35, ringOuter * 2));
      const ringQuaternion = new THREE.Quaternion();
      const normal = new THREE.Vector3(0, 0, 1).applyQuaternion(ring.getWorldQuaternion(ringQuaternion)).normalize();
      let inPlane = new THREE.Vector3(1, 0.15, 1).projectOnPlane(normal);
      if (inPlane.lengthSq() < 0.001) inPlane = new THREE.Vector3(1, 0, 0).projectOnPlane(normal);
      inPlane.normalize();
      return lookAt.clone()
        .add(inPlane.multiplyScalar(ringOuter * 3.2))
        .add(normal.multiplyScalar(ringOuter * 0.28));
    }
  });
}

function runWormholeApproachEvent() {
  ensureDeepSpaceBodies();
  if (!deepSpaceBodiesVisible) setDeepSpaceBodiesVisible(true);
  if (!wormholeObject) return;
  const radius = wormholeObject.userData.radius || 18;

  animateEventCamera({
    focusObject: wormholeObject,
    navText: 'Wormhole',
    minDistance: radius * 1.8,
    maxDistance: 8000,
    getLookAt: () => getWorldPosition(wormholeObject),
    getCameraPosition: (lookAt) => {
      const basis = getAxisBasis(getSafeAxisFromSun(lookAt));
      return lookAt.clone()
        .add(basis.axis.clone().multiplyScalar(radius * 6.2))
        .add(basis.side.clone().multiplyScalar(radius * 1.7))
        .add(basis.up.clone().multiplyScalar(radius * 0.9));
    }
  });
}

function runHalleyFlybyEvent() {
  ensureDeepSpaceBodies();
  if (!deepSpaceBodiesVisible) setDeepSpaceBodiesVisible(true);
  const halley = findLoadedSceneObjectByName("Halley's Comet");
  if (!halley) return;

  animateEventCamera({
    focusObject: halley,
    navText: 'Halley flyby',
    minDistance: 2,
    maxDistance: 1600,
    getLookAt: () => getWorldPosition(halley),
    getCameraPosition: (lookAt) => {
      const basis = getAxisBasis(getSafeAxisFromSun(lookAt));
      return lookAt.clone()
        .add(basis.axis.clone().multiplyScalar(34))
        .add(basis.side.clone().multiplyScalar(18))
        .add(basis.up.clone().multiplyScalar(9));
    }
  });
}

function runAsteroidCrossingEvent() {
  // The middle of the belt (2.7 AU) at whichever scale is on.
  const r = realisticScaleMode ? 2.7 * AU_TO_SCENE : asteroidArtisticDistance(2.7) * scaleMultiplier;
  const lookAt = new THREE.Vector3(r, 0.0076 * r, 0);
  animateEventCamera({
    focusObject: null,
    navText: 'Asteroid belt',
    minDistance: 0.015 * r,
    maxDistance: 19 * r,
    getLookAt: () => lookAt.clone(),
    getCameraPosition: () => new THREE.Vector3(1.09 * r, 0.045 * r, 0.16 * r)
  });
}

function runVoyagerEdgeEvent() {
  ensureDeepSpaceBodies();
  if (!deepSpaceBodiesVisible) setDeepSpaceBodiesVisible(true);
  const voyager = findLoadedSceneObjectByName('Voyager 1');
  if (!voyager) return;

  animateEventCamera({
    focusObject: voyager,
    navText: 'Voyager 1',
    minDistance: 1,
    maxDistance: 9000,
    getLookAt: () => getWorldPosition(voyager),
    getCameraPosition: (lookAt) => {
      const basis = getAxisBasis(getSafeAxisFromSun(lookAt));
      return lookAt.clone()
        .add(basis.axis.clone().multiplyScalar(24))
        .add(basis.side.clone().multiplyScalar(9))
        .add(basis.up.clone().multiplyScalar(5));
    }
  });
}

function restoreCoolWorldsShowreelState() {
  if (!coolWorldsShowreelRestore) return;

  timeSpeed = coolWorldsShowreelRestore.timeSpeed;
  syncTimeControls();

  orbitsVisible = coolWorldsShowreelRestore.orbitsVisible;
  updateOrbitsVisibility();
  if (orbitsLabel) orbitsLabel.textContent = vt(orbitsVisible ? 'controls.orbits.on' : 'controls.orbits.off');
  if (orbitsToggle) orbitsToggle.classList.toggle('playing', orbitsVisible);

  setVanAllenBeltsVisible(coolWorldsShowreelRestore.vanAllenVisible);
  if (!coolWorldsShowreelRestore.deepSpaceVisible) setDeepSpaceBodiesVisible(false);

  coolWorldsShowreelRestore = null;
}

function clearCoolWorldsShowreel({ restore = true } = {}) {
  coolWorldsShowreelTimers.forEach((timer) => clearTimeout(timer));
  coolWorldsShowreelTimers = [];
  if (restore) restoreCoolWorldsShowreelState();
  coolWorldsShowreelActive = false;
  updateCoolWorldsShowreelButton();
}

function scheduleCoolWorldsShot(delay, callback) {
  const timer = setTimeout(() => {
    coolWorldsShowreelTimers = coolWorldsShowreelTimers.filter((entry) => entry !== timer);
    callback();
  }, delay);
  coolWorldsShowreelTimers.push(timer);
}

function showCoolWorldsCaption(title, copy, duration = 6400) {
  showEventCaption({ title, copy, duration });
}

function updateCoolWorldsShowreelButton() {
  if (coolWorldsSequenceToggle) {
    coolWorldsSequenceToggle.classList.toggle('playing', coolWorldsShowreelActive);
  }
  if (coolWorldsSequenceModalToggle) {
    coolWorldsSequenceModalToggle.textContent = coolWorldsShowreelActive
      ? 'The WOW sequence is running'
      : 'Run the WOW sequence';
  }
  if (coolWorldsSequenceLabel) {
    coolWorldsSequenceLabel.textContent = vt(coolWorldsShowreelActive ? 'controls.wow.running' : 'controls.wow.label');
  }
  // Floating Exit Tour button — visible only while the cinematic is
  // running so the user always has a one-click way out.
  const wowExitBtn = document.getElementById('wow-exit-btn');
  if (wowExitBtn) {
    wowExitBtn.classList.toggle('is-visible', coolWorldsShowreelActive);
  }
}

function runCoolWorldsShowreel() {
  clearCoolWorldsShowreel();
  if (flyMode) exitFlyMode();
  if (scaleModeActive) exitScaleMode();

  coolWorldsShowreelRestore = {
    orbitsVisible,
    deepSpaceVisible: deepSpaceBodiesVisible,
    vanAllenVisible: vanAllenBeltsOn,
    timeSpeed
  };
  coolWorldsShowreelActive = true;
  updateCoolWorldsShowreelButton();

  ensureDeepSpaceBodies();
  setDeepSpaceBodiesVisible(true);
  setVanAllenBeltsVisible(false);
  timeSpeed = Math.min(timeSpeed, 0.35);
  syncTimeControls();
  orbitsVisible = false;
  updateOrbitsVisibility();
  if (orbitsLabel) orbitsLabel.textContent = vt('controls.orbits.off');
  if (orbitsToggle) orbitsToggle.classList.remove('playing');

  closeTransientPanels();

  scheduleCoolWorldsShot(0, () => {
    const earth = planets.earth;
    if (!earth) return;
    const radius = earth.userData.radius || 2.5;
    showCoolWorldsCaption(
      'Earth: the living opening shot',
      'Clouds, atmosphere, city lights, and ocean colour all come alive as the camera drifts around the limb.',
      10400
    );
    animateEventCamera({
      focusObject: earth,
      navText: 'WOW',
      duration: 6.4,
      ease: 'sine.inOut',
      minDistance: radius * 1.45,
      maxDistance: radius * 160,
      panDuration: 5.2,
      panAngle: Math.PI * 0.19,
      panLift: 0.035,
      getLookAt: () => getWorldPosition(earth),
      getCameraPosition: (lookAt) => {
        const basis = getAxisBasis(getSafeAxisFromSun(lookAt));
        return lookAt.clone()
          .add(basis.axis.clone().multiplyScalar(radius * 4.85))
          .add(basis.side.clone().multiplyScalar(radius * 1.18))
          .add(basis.up.clone().multiplyScalar(radius * 0.94));
      }
    });
  });

  scheduleCoolWorldsShot(13500, () => {
    const jupiter = planets.jupiter;
    if (!jupiter) return;
    const io = setMoonSunward('jupiter', 'Io');
    const radius = jupiter.userData.radius || 10;
    showCoolWorldsCaption(
      'Jupiter: scale you can feel',
      'A moon shadow crossing the cloud deck makes the planet feel enormous, then the pan reveals the bands in motion.',
      11600
    );
    animateEventCamera({
      focusObject: jupiter,
      navText: 'Jupiter shadow',
      duration: 7.0,
      ease: 'sine.inOut',
      minDistance: radius * 2.0,
      maxDistance: radius * 180,
      panDuration: 6.2,
      panAngle: -Math.PI * 0.16,
      panLift: 0.028,
      getLookAt: () => getWorldPosition(jupiter),
      getCameraPosition: (lookAt) => {
        const basis = getAxisBasis(getSafeAxisFromSun(lookAt));
        const moonLift = io ? Math.min(radius * 0.45, (io.userData.radius || 0.5) * 3.5) : 0;
        return lookAt.clone()
          .add(basis.axis.clone().multiplyScalar(-radius * 8.35))
          .add(basis.side.clone().multiplyScalar(radius * 0.92))
          .add(basis.up.clone().multiplyScalar(radius * (1.32 + moonLift * 0.018)));
      }
    });
  });

  scheduleCoolWorldsShot(29000, () => {
    const saturn = planets.saturn;
    if (!saturn) return;
    const ring = saturn.getObjectByName('ring');
    const radius = saturn.userData.radius || 7;
    const ringOuter = saturn.userData.ringOuterRadius || radius * 2.3;
    showCoolWorldsCaption(
      'Saturn: the cinematic pass',
      'A low ring-plane angle catches the shadow first, then the camera slides enough to show the rings as a physical structure.',
      12400
    );
    animateEventCamera({
      focusObject: saturn,
      navText: 'Saturn pass',
      duration: 7.4,
      ease: 'sine.inOut',
      minDistance: radius * 2,
      maxDistance: radius * 220,
      panDuration: 6.6,
      panAngle: Math.PI * 0.15,
      panLift: 0.02,
      panAxisFactory: () => {
        if (!ring) return new THREE.Vector3(0, 1, 0);
        const ringQuaternion = new THREE.Quaternion();
        return new THREE.Vector3(0, 0, 1).applyQuaternion(ring.getWorldQuaternion(ringQuaternion)).normalize();
      },
      getLookAt: () => getWorldPosition(saturn),
      getCameraPosition: (lookAt) => {
        if (!ring) return lookAt.clone().add(new THREE.Vector3(ringOuter * 3, ringOuter * 0.35, ringOuter * 2));
        const ringQuaternion = new THREE.Quaternion();
        const normal = new THREE.Vector3(0, 0, 1).applyQuaternion(ring.getWorldQuaternion(ringQuaternion)).normalize();
        let inPlane = new THREE.Vector3(1, 0.12, 1).projectOnPlane(normal);
        if (inPlane.lengthSq() < 0.001) inPlane = new THREE.Vector3(1, 0, 0).projectOnPlane(normal);
        inPlane.normalize();
        return lookAt.clone()
          .add(inPlane.multiplyScalar(ringOuter * 3.45))
          .add(normal.multiplyScalar(ringOuter * 0.18));
      }
    });
  });

  scheduleCoolWorldsShot(46500, () => {
    const halley = findLoadedSceneObjectByName("Halley's Comet");
    if (!halley) return;
    showCoolWorldsCaption(
      "Halley's Comet: motion in the dark",
      'The coma and tail point away from the Sun; the slow pan lets the tail read as direction, not decoration.',
      10400
    );
    animateEventCamera({
      focusObject: halley,
      navText: 'Halley flyby',
      duration: 6.6,
      ease: 'sine.inOut',
      minDistance: 2,
      maxDistance: 1600,
      panDuration: 4.8,
      panAngle: -Math.PI * 0.14,
      panLift: 0.08,
      getLookAt: () => getWorldPosition(halley),
      getCameraPosition: (lookAt) => {
        const basis = getAxisBasis(getSafeAxisFromSun(lookAt));
        return lookAt.clone()
          .add(basis.axis.clone().multiplyScalar(38))
          .add(basis.side.clone().multiplyScalar(24))
          .add(basis.up.clone().multiplyScalar(12));
      }
    });
  });

  scheduleCoolWorldsShot(60000, () => {
    showCoolWorldsCaption(
      'Guided scenes, not a pre-render',
      'The same live universe can jump to eclipses, transits, comets, probes, and scale reveals.',
      8200
    );
    openSpaceEvents();
  });

  // Deep-sky showcase. Closes the local-events panel, then steps
  // through four iconic photos at correct celestial coordinates,
  // each focused via the same telescope-view tween that the objects
  // menu uses (rotate + FOV pulldown). 6 seconds per shot.
  scheduleCoolWorldsShot(68000, () => {
    closeSpaceEvents();
  });

  const showcaseDso = (name, title, copy) => {
    if (!deepSkyObjects || !deepSkyObjects.group) return;
    const sprite = deepSkyObjects.group.children.find((s) => s.name === name);
    if (!sprite) return;
    showCoolWorldsCaption(title, copy, 5800);
    focusOnDSO(sprite, { interrupt: true, preservePanel: true });
  };

  scheduleCoolWorldsShot(70000, () => {
    showcaseDso(
      'M31 Andromeda',
      'Andromeda Galaxy · 2.5 Mly',
      "Our nearest spiral neighbour. In four billion years it'll merge with the Milky Way."
    );
  });

  scheduleCoolWorldsShot(76000, () => {
    showcaseDso(
      'M16 Eagle Nebula',
      "Pillars of Creation · 7,000 ly",
      'Hubble made these towers of star-forming dust the most famous image in astronomy.'
    );
  });

  scheduleCoolWorldsShot(82000, () => {
    showcaseDso(
      'M51 Whirlpool',
      'Whirlpool Galaxy · 31 Mly',
      'A face-on spiral interacting with its smaller companion — a textbook of galactic dynamics.'
    );
  });

  scheduleCoolWorldsShot(88000, () => {
    showcaseDso(
      'Carina Nebula',
      'Carina Nebula · 8,500 ly',
      'A vast star factory; its sculpted dust and ionised hydrogen mark some of the most violent young stars known.'
    );
  });

  scheduleCoolWorldsShot(94000, () => {
    if (!wormholeObject) return;
    const radius = wormholeObject.userData.radius || 4.2;
    showCoolWorldsCaption(
      'Final hook: the wormhole',
      'A compact reflective spacetime lens near Saturn turns the solar system into a cinematic playground.',
      12600
    );
    animateEventCamera({
      focusObject: wormholeObject,
      navText: 'Wormhole',
      duration: 7.4,
      ease: 'sine.inOut',
      minDistance: radius * 1.6,
      maxDistance: 8000,
      panDuration: 6.4,
      panAngle: Math.PI * 0.22,
      panLift: 0.045,
      getLookAt: () => getWorldPosition(wormholeObject),
      getCameraPosition: (lookAt) => {
        const basis = getAxisBasis(getSafeAxisFromSun(lookAt));
        return lookAt.clone()
          .add(basis.axis.clone().multiplyScalar(radius * 8.4))
          .add(basis.side.clone().multiplyScalar(radius * 2.2))
          .add(basis.up.clone().multiplyScalar(radius * 1.0));
      }
    });
  });

  scheduleCoolWorldsShot(111500, () => {
    showCoolWorldsCaption(
      'Now fly it yourself',
      'Tap Fly Mode after the sequence and the solar system becomes your cockpit.',
      7200
    );
  });

  scheduleCoolWorldsShot(121000, () => {
    coolWorldsShowreelActive = false;
    updateCoolWorldsShowreelButton();
    restoreCoolWorldsShowreelState();
  });
}

if (typeof window !== 'undefined') {
  window.runCoolWorldsShowreel = runCoolWorldsShowreel;
}

function getShareContext() {
  const object = selectedObject || sun;
  const name = object && object.userData && object.userData.name
    ? object.userData.name
    : (object && object.name ? object.name : 'Our Solar System');
  const title = object && object.userData && object.userData.isSun
    ? 'Our Solar System'
    : `${name} in 3D`;
  // A visitor who came from a video (/v/ links, api/v.js) has utm_* in the
  // address; their own share goes out without it, so it is not credited
  // to the video.
  const shareUrl = new URL(window.location.href);
  [...shareUrl.searchParams.keys()].filter((k) => k.startsWith('utm_')).forEach((k) => shareUrl.searchParams.delete(k));
  const url = shareUrl.toString();
  const text = object && object.userData && object.userData.name
    ? `Check out ${name} in this photorealistic solar system viewer.`
    : 'Check out this photorealistic solar system viewer.';
  return { name, title, url, text };
}

function buildShareUrl(base, params) {
  const url = new URL(base);
  Object.entries(params).forEach(([key, value]) => {
    if (value) url.searchParams.set(key, value);
  });
  return url.toString();
}

function updateShareSheet() {
  if (!shareSheetCopy || !shareSheetNote) return;
  const { name, title, url, text } = getShareContext();
  const shareText = `${text} ${url}`;
  shareSheetCopy.value = url;
  shareSheetNote.textContent = `${title} is ready to share. Copy the link or post it straight to a social platform.`;

  const encodedUrl = encodeURIComponent(url);
  if (shareSheetX) {
    shareSheetX.href = buildShareUrl('https://twitter.com/intent/tweet', {
      text: shareText,
      url
    });
  }
  if (shareSheetBluesky) {
    shareSheetBluesky.href = buildShareUrl('https://bsky.app/intent/compose', {
      text: shareText
    });
  }
  if (shareSheetReddit) {
    shareSheetReddit.href = buildShareUrl('https://www.reddit.com/submit', {
      url,
      title
    });
  }
  if (shareSheetFacebook) {
    shareSheetFacebook.href = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
  }
  if (shareSheetCopyBtn) {
    shareSheetCopyBtn.textContent = `Copy ${name === 'Our Solar System' ? 'link' : `${name} link`}`;
  }
}

// GA4's recommended share event: how, and which body the link points at.
function trackShare(method) {
  const { name } = getShareContext();
  track('share', { method, content_type: 'body', item_id: String(name).toLowerCase() });
}

async function copyShareLink() {
  const { url } = getShareContext();
  trackShare('copy_link');
  try {
    await navigator.clipboard.writeText(url);
    if (shareSheetCopyBtn) {
      const prev = shareSheetCopyBtn.textContent;
      shareSheetCopyBtn.textContent = 'Copied';
      setTimeout(() => {
        if (shareSheetCopyBtn) shareSheetCopyBtn.textContent = prev;
      }, 1200);
    }
  } catch (err) {
    shareSheetCopy?.select();
    document.execCommand('copy');
  }
}

function openShareSheet() {
  closeTransientPanels('shareSheet');
  shareSheetOpen = true;
  updateShareSheet();
  if (shareSheet) shareSheet.classList.add('visible');
  if (shareSheetCopy) shareSheetCopy.focus({ preventScroll: true });
  if (shareSheetCopy) shareSheetCopy.select();
}

function closeShareSheet() {
  shareSheetOpen = false;
  if (shareSheet) shareSheet.classList.remove('visible');
}

function toggleShareSheet() {
  if (shareSheetOpen) closeShareSheet();
  else openShareSheet();
}

function formatDegrees(value) {
  if (!Number.isFinite(value)) return '--';
  return `${value.toFixed(value < 10 ? 1 : 0)}°`;
}

function formatPercent(value) {
  if (!Number.isFinite(value)) return '--';
  return `${THREE.MathUtils.clamp(value, 0, 100).toFixed(0)}%`;
}

function formatSceneDistance(sceneUnits) {
  if (!Number.isFinite(sceneUnits)) return '--';
  const au = sceneUnits / (SCENE_UNITS_PER_AU * scaleMultiplier);
  if (au < 0.001) return `${(au * 149597870.7).toFixed(0)} km model`;
  if (au < 0.1) return `${(au * 1000).toFixed(1)} mAU`;
  if (au < 10) return `${au.toFixed(3)} AU`;
  return `${au.toFixed(1)} AU`;
}

function hashAlienSeed(seed) {
  const value = String(seed || 'void');
  let hash = 2166136261;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function alienGlyphLine(seed, length, offset = 0) {
  const base = hashAlienSeed(`${seed}:${offset}:${alienScanGlyphPhase}`);
  let line = '';
  for (let i = 0; i < length; i++) {
    const index = (base + i * 17 + ((base >>> (i % 13)) & 15)) % ALIEN_GLYPHS.length;
    line += ALIEN_GLYPHS[index];
    if ((i + 1) % 4 === 0 && i !== length - 1) line += ' ';
  }
  return line;
}

function getScanRadiusForBody(data) {
  if (!data) return 1;
  return Math.max(data.radius || 1, data.ringOuterRadius || 0);
}

function considerAlienScanTarget(currentBest, object, data, key) {
  if (!object || !data || data.isSun || object.userData && object.userData.isSun) return currentBest;
  const radius = getScanRadiusForBody(data);
  const outerRange = Math.max(UFO_SCAN_MIN_RANGE, radius * UFO_SCAN_RADIUS_MULTIPLIER);
  const innerRange = Math.max(radius * 1.45, 3.5);
  object.getWorldPosition(alienScanBodyPos);
  const distance = camera.position.distanceTo(alienScanBodyPos);
  if (distance > outerRange) return currentBest;

  const strength = 1 - THREE.MathUtils.smoothstep(distance, innerRange, outerRange);
  const score = strength * 1000 + radius / Math.max(distance, 0.001);
  if (!currentBest || score > currentBest.score) {
    return {
      object,
      data,
      key,
      distance,
      radius,
      strength,
      score
    };
  }

  return currentBest;
}

function findAlienScanTarget() {
  if (!flyMode || !camera) return null;
  let best = null;

  Object.entries(planets).forEach(([key, planet]) => {
    const data = celestialData[key] || planet.userData;
    best = considerAlienScanTarget(best, planet, data, key);
  });

  Object.values(moons).forEach((planetMoons) => {
    planetMoons.forEach((moon) => {
      best = considerAlienScanTarget(best, moon, moon.userData, moon.userData.name || moon.name);
    });
  });

  return best;
}

function getProjectedScanLimbSize(center, radius, projectedCenter, viewportWidth, viewportHeight) {
  const safeRadius = Math.max(radius || 1, 0.001);
  const fallback = 126 + alienScanStrength * 86;

  alienScanCameraRight.set(1, 0, 0).applyQuaternion(camera.quaternion);
  alienScanCameraUp.set(0, 1, 0).applyQuaternion(camera.quaternion);

  alienScanEdgeProjected.copy(center).addScaledVector(alienScanCameraRight, safeRadius).project(camera);
  const radiusX = Math.abs(alienScanEdgeProjected.x - projectedCenter.x) * viewportWidth * 0.5;

  alienScanEdgeProjected.copy(center).addScaledVector(alienScanCameraUp, safeRadius).project(camera);
  const radiusY = Math.abs(alienScanEdgeProjected.y - projectedCenter.y) * viewportHeight * 0.5;

  const validRadiusX = Number.isFinite(radiusX) && radiusX > 1 ? radiusX : fallback * 0.5;
  const validRadiusY = Number.isFinite(radiusY) && radiusY > 1 ? radiusY : fallback * 0.5;
  const padding = 26 + alienScanStrength * 34;

  return {
    width: THREE.MathUtils.clamp(validRadiusX * 2.08 + padding, fallback, viewportWidth * 1.85),
    height: THREE.MathUtils.clamp(validRadiusY * 2.08 + padding, fallback, viewportHeight * 1.85)
  };
}

function hideAlienScanHud(immediate = false) {
  if (!alienScanHud) return;
  stopAlienScanAudio();
  if (immediate) {
    alienScanStrength = 0;
    alienScanSightLock = 0;
    ufoPlanetReflectStrength = 0;
    if (ufoStarReflectionMaterial && ufoStarReflectionMaterial.uniforms && ufoStarReflectionMaterial.uniforms.planetReflectStrength) {
      ufoStarReflectionMaterial.uniforms.planetReflectStrength.value = 0;
    }
  }
  alienScanCurrentTarget = null;
  alienScanHud.classList.remove('active');
  alienScanHud.style.setProperty('--scan-strength', '0');
  alienScanHud.style.setProperty('--scan-lock', '0');
}

function updateAlienScanGlyphs(target) {
  if (!target) return;
  const seed = target.key || target.data.name || 'scan';

  // Starfleet variant: when the user is flying the Enterprise, the
  // scan HUD speaks LCARS English instead of alien glyphs. Same data
  // pipe, different presentation.
  if (activeCraftKey === 'enterprise') {
    const designation = (target.data && target.data.name)
      ? String(target.data.name).toUpperCase()
      : 'UNKNOWN';
    const range = target.distance.toFixed(1);
    const radius = target.radius.toFixed(2);
    const intensity = (target.strength * 100).toFixed(1);

    if (alienScanTitle) alienScanTitle.textContent = `Σ TARGET LOCK · ${designation}`;
    if (alienScanGlyphs) {
      alienScanGlyphs.textContent = [
        `BRG  ${range} u`,
        `RAD  ${radius} u`,
        `INT  ${intensity}%`
      ].join('\n');
    }
    if (alienScanReadout) {
      alienScanReadout.textContent = [
        `LRS  ${target.strength > 0.6 ? 'LOCK' : 'SCAN'}`,
        `TAC  ${target.strength > 0.4 ? 'GREEN' : 'STBY'}`,
        `BIO  ${(target.data && target.data.type) ? String(target.data.type).slice(0, 9).toUpperCase() : '—'}`
      ].join('\n');
    }
    return;
  }

  const proximityCode = Math.round(target.strength * 4095).toString(16).toUpperCase().padStart(3, '0');
  const rangeCode = Math.max(0, Math.round(target.distance * 13)).toString(36).toUpperCase().padStart(4, '0');
  const radiusCode = Math.max(0, Math.round(target.radius * 97)).toString(36).toUpperCase().padStart(3, '0');

  if (alienScanTitle) {
    alienScanTitle.textContent = alienGlyphLine(seed, 11, 1);
  }
  if (alienScanGlyphs) {
    alienScanGlyphs.textContent = [
      `${alienGlyphLine(seed, 12, 2)} ${proximityCode}`,
      `${alienGlyphLine(seed, 12, 3)} ${rangeCode}`,
      `${alienGlyphLine(seed, 12, 4)} ${radiusCode}`
    ].join('\n');
  }
  if (alienScanReadout) {
    alienScanReadout.textContent = [
      `${alienGlyphLine(seed, 7, 5)} ${alienGlyphLine(rangeCode, 5, 6)}`,
      `${alienGlyphLine(seed, 7, 7)} ${alienGlyphLine(proximityCode, 5, 8)}`,
      `${alienGlyphLine(seed, 7, 9)} ${alienGlyphLine(radiusCode, 5, 10)}`
    ].join('\n');
  }
}

function updateAlienScanHud(delta) {
  if (!alienScanHud) return;
  if (!alienHudEnabled) {
    if (alienScanCurrentTarget || alienScanStrength > 0 || alienScanHud.classList.contains('active')) {
      hideAlienScanHud(true);
    }
    return;
  }
  // Suppress while the user is orbit-panning or has parked the camera
  // for a screenshot. Otherwise the HUD anchors to a target outside
  // the new view direction and snaps to screen-centre, plonking a
  // bright reticle right in the middle of every screenshot.
  if (isMiddleMouseDown || orbitParked) {
    if (alienScanCurrentTarget || alienScanStrength > 0 || alienScanHud.classList.contains('active')) {
      hideAlienScanHud(true);
    }
    return;
  }
  const target = findAlienScanTarget();
  alienScanCurrentTarget = target;
  const targetStrength = target ? target.strength : 0;
  const alpha = dampingFactor(delta, target ? 9 : 13);
  alienScanStrength += (targetStrength - alienScanStrength) * alpha;

  if (!target && alienScanStrength < 0.025) {
    hideAlienScanHud();
    return;
  }

  alienScanHud.classList.add('active');
  alienScanHud.style.setProperty('--scan-strength', alienScanStrength.toFixed(3));

  if (!target) {
    alienScanSightLock += (0 - alienScanSightLock) * dampingFactor(delta, 18);
    alienScanHud.style.setProperty('--scan-lock', alienScanSightLock.toFixed(3));
    stopAlienScanAudio();
    return;
  }

  target.object.getWorldPosition(alienScanBodyPos);
  camera.getWorldDirection(alienScanCameraDir);
  alienScanToBody.copy(alienScanBodyPos).sub(camera.position);
  const inFront = alienScanToBody.dot(alienScanCameraDir) > 0;
  alienScanProjected.copy(alienScanBodyPos).project(camera);

  const width = window.innerWidth || 1;
  const height = window.innerHeight || 1;
  const margin = 96;
  const rawX = (alienScanProjected.x * 0.5 + 0.5) * width;
  const rawY = (-alienScanProjected.y * 0.5 + 0.5) * height;
  const x = inFront
    ? THREE.MathUtils.clamp(rawX, margin, width - margin)
    : width * 0.5;
  const y = inFront
    ? THREE.MathUtils.clamp(rawY, margin, height - margin)
    : height * 0.5;

  const scanSize = getProjectedScanLimbSize(
    alienScanBodyPos,
    target.radius,
    alienScanProjected,
    width,
    height
  );
  const apparentRadius = Math.max(scanSize.width, scanSize.height) * 0.5;
  const visibleInViewport = inFront
    && rawX > -apparentRadius
    && rawX < width + apparentRadius
    && rawY > -apparentRadius
    && rawY < height + apparentRadius;
  const aimDistance = Math.hypot(rawX - width * 0.5, rawY - height * 0.5);
  const aimError = Math.max(0, aimDistance - apparentRadius * 0.55);
  const lockTarget = visibleInViewport
    ? 1 - THREE.MathUtils.smoothstep(aimError, Math.min(width, height) * 0.06, Math.min(width, height) * 0.42)
    : 0;
  alienScanSightLock += (lockTarget - alienScanSightLock) * dampingFactor(delta, lockTarget > alienScanSightLock ? 12 : 18);

  alienScanHud.style.setProperty('--scan-x', `${x.toFixed(1)}px`);
  alienScanHud.style.setProperty('--scan-y', `${y.toFixed(1)}px`);
  alienScanHud.style.setProperty('--scan-w', `${scanSize.width.toFixed(1)}px`);
  alienScanHud.style.setProperty('--scan-h', `${scanSize.height.toFixed(1)}px`);
  alienScanHud.style.setProperty('--scan-lock', alienScanSightLock.toFixed(3));

  alienScanTicker += delta;
  const targetName = target.key || target.data.name || '';
  setAlienScanAudioState({
    active: visibleInViewport && alienScanSightLock > 0.04,
    strength: alienScanStrength,
    lock: alienScanSightLock,
    targetKey: targetName
  });
  if (targetName !== alienScanLastTarget || alienScanTicker > 0.12) {
    alienScanTicker = 0;
    alienScanGlyphPhase = (alienScanGlyphPhase + 1) % 997;
    alienScanLastTarget = targetName;
    updateAlienScanGlyphs(target);
  }
}

function findReferenceLinks(bodyTree) {
  if (!bodyTree || !bodyTree.children) return [];
  const references = bodyTree.children.find((child) => child.id === 'references');
  const linksPayload = references && references.payload
    ? references.payload.find((payload) => payload.type === 'links')
    : null;
  return linksPayload && linksPayload.entries ? linksPayload.entries.slice(0, 3) : [];
}

function getScienceBodyId(object) {
  if (!object || !object.userData) return null;
  if (object.userData.isMoon) return object.userData.parentPlanet || null;
  return object.userData.isSun ? 'sun' : (object.userData.name || object.name);
}

function updateScienceLinks(object) {
  if (!scienceLinks) return;
  const bodyId = getScienceBodyId(object) || 'sun';
  if (lastScienceLinkTarget === bodyId) return;
  lastScienceLinkTarget = bodyId;

  scienceLinks.replaceChildren();
  const links = findReferenceLinks(getBody(bodyId));
  const effectiveLinks = links.length
    ? links
    : [{ label: 'JPL Horizons', url: 'https://ssd.jpl.nasa.gov/horizons/' }];

  effectiveLinks.forEach((link) => {
    const anchor = document.createElement('a');
    anchor.href = link.url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.textContent = link.label.replace(/^NASA\s+/i, 'NASA ');
    scienceLinks.appendChild(anchor);
  });
}

// Scratch — updateSciencePanel runs on a 4 Hz tick from animate.
const SCIENCE_SCRATCH_TARGET_POS = new THREE.Vector3();
const SCIENCE_SCRATCH_TO_CAMERA = new THREE.Vector3();

function updateSciencePanel() {
  if (!scienceMode || !sciencePanel) return;
  const object = selectedObject || sun;
  if (!object || !object.userData) return;

  const data = object.userData;
  const targetPos = object.getWorldPosition(SCIENCE_SCRATCH_TARGET_POS);

  const targetToCamera = SCIENCE_SCRATCH_TO_CAMERA.copy(camera.position).sub(targetPos);
  const cameraRange = targetToCamera.length();
  const radius = data.radius || 1;
  const apparent = apparentDiameterDeg(radius, cameraRange);
  const phaseAngle = data.isSun
    ? null
    : phaseAngleDeg(
        { x: 0, y: 0, z: 0 },
        { x: targetPos.x, y: targetPos.y, z: targetPos.z },
        { x: camera.position.x, y: camera.position.y, z: camera.position.z }
      );
  const illumination = phaseAngle === null ? null : illuminationPercent(phaseAngle);

  if (scienceTarget) scienceTarget.textContent = data.name || object.name || 'Object';
  if (scienceType) scienceType.textContent = data.isMoon && data.parentPlanet
    ? `${data.type || 'Moon'} of ${data.parentPlanet}`
    : (data.type || (data.isSun ? 'Star' : 'Scene body'));
  if (scienceDistance) scienceDistance.textContent = data.isSun ? '0 AU' : formatSceneDistance(targetPos.length());
  if (scienceCameraRange) scienceCameraRange.textContent = formatSceneDistance(cameraRange);
  if (sciencePhase) sciencePhase.textContent = phaseAngle === null ? 'N/A' : formatDegrees(phaseAngle);
  if (scienceIllumination) scienceIllumination.textContent = illumination === null ? 'Self luminous' : formatPercent(illumination);
  if (scienceIncidence) scienceIncidence.textContent = phaseAngle === null ? 'N/A' : formatDegrees(phaseAngle);
  if (scienceApparent) scienceApparent.textContent = Number.isFinite(apparent)
    ? formatDegrees(apparent)
    : 'Full frame';
  if (scienceDiameter) scienceDiameter.textContent = data.realDiameter || data.realSize || 'Reference pending';
  if (scienceOrbit) scienceOrbit.textContent = data.orbitalPeriod || data.realDistance || 'Reference pending';
  if (scienceModel) {
    scienceModel.textContent = data.isMoon
      ? 'Moon telemetry is scene-derived in the parent system; reference facts link to the parent body until moon-specific source pages are expanded.'
      : (data.isComet || data.isNEO || data.isSpaceProbe || data.isDwarfPlanet || data.isSatellite
        || data.isWormhole
        ? 'Deep-space telemetry is scene-derived and loaded on demand; reference facts fall back to mission or catalog sources when available.'
        : 'Live telemetry is scene-derived, not JPL Horizons ephemeris yet. Reference fields use curated NASA/JPL-style body data.');
  }

  updateScienceLinks(object);
}

function toggleScienceMode() {
  scienceMode = !scienceMode;
  if (sciencePanel) sciencePanel.classList.toggle('visible', scienceMode);
  if (scienceToggle) scienceToggle.classList.toggle('playing', scienceMode);
  if (scienceLabel) scienceLabel.textContent = vt(scienceMode ? 'controls.science.on' : 'controls.science.off');
  setVanAllenBeltsVisible(scienceMode);
  lastScienceLinkTarget = null;
  updateSciencePanel();
}

// Enter fly mode
// fly_mode_start and rocketry_start fire once per page load, so they can be
// GA4 key events counting the visits that tried each mode; mode_enter keeps
// counting every entry.
let flyStartTracked = false;
let rocketryStartTracked = false;
function enterFlyMode() {
  flyMode = true;
  modeEnter('fly');
  if (!flyStartTracked) { flyStartTracked = true; track('fly_mode_start'); }
  // A leftover exitFlyMode return-to-overview tween would drag the
  // camera while the fly physics loop writes it — kill it on entry.
  killExitFlyTweens();
  activeCameraTween?.kill(); activeCameraTween = null; isAnimating = false;
  flyToggle.classList.add('playing');
  flyLabel.textContent = vt('controls.fly.flying');
  flyControlsOverlay.style.display = 'block';
  document.body.classList.add('fly-mode');
  document.body.style.cursor = '';
  closeTransientPanels();

  // Dim the scene ambient so planet dark hemispheres read as space-
  // imagery dark rather than the overview's deliberately-lifted gray.
  // Earth keeps its own night-lights shader so it isn't affected;
  // Venus / Mars / gas-giants / moons go to a near-black dark side
  // with only the per-shader phenomena (atmosphere scatter, gas-giant
  // lightning) lifting them. Saved so exitFlyMode can restore the
  // overview value the user dialed in.
  if (_ambientLightRef && _flyModeAmbientSaved == null) {
    _flyModeAmbientSaved = _ambientLightRef.intensity;
    _ambientLightRef.intensity = FLY_MODE_DARK_AMBIENT;
  }

  // If the user had a DSO focused before entering fly mode, the DSO
  // zoom mode installs its own wheel listener that pulls the camera
  // FOV in on scroll. That listener persists across mode switches and
  // would hijack the fly-mode chase-distance scroll. Tear it down on
  // entry so the wheel handler defined for fly mode (followDistance /
  // orbit distance) gets to handle the event cleanly.
  disableDsoZoomMode();

  // Disable orbit controls
  controls.enabled = false;
  if (camera && flySavedCameraNear == null) {
    flySavedCameraNear = camera.near;
    setCameraNearPlane(FLY_MODE_CAMERA_NEAR);
  }

  // Initialize camera euler from current rotation
  flyEuler.setFromQuaternion(camera.quaternion);
  flyTargetEuler.copy(flyEuler);

  // Reset velocities for fresh start
  resetFlyInputState({ resetKeys: true });
  // Park the ship in a stable co-orbit with whatever body fly mode
  // was activated on. flyParkedBody is a rigid follow target: each
  // frame, while no user input is present, the camera translates by
  // the body's per-frame motion so the ship stays visually still
  // relative to it. The moment the user thrusts or looks around, the
  // park state clears and free flight resumes with the body's
  // instantaneous velocity already baked into flyVelocity — so the
  // ship feels like it was already coasting with the planet.
  flyVelocity.set(0, 0, 0);
  setFlyParkedBody(selectedObject || null);
  hideAlienScanHud(true);
  resetCraftOrbitCamera();
  syncPlayerShipToCamera();

  // Hide other UI elements
  document.getElementById('controls-hint').style.display = 'none';
  document.getElementById('planet-nav').style.display = 'none';
  document.getElementById('controls-panel').style.display = 'none';
  if (distanceIndicator) distanceIndicator.style.display = 'none';
  matryoshka.close();

  // Start UFO hum
  startUfoHum();
}

// Fly mode's first frames still do some one-off work (the ship's reflection
// meets maps and shaders it has not drawn before), a pause of a few hundred
// ms on a first entry that read as the page hanging. A dip to black covers
// it and the HUD's arrival: the veil starts fading in before the work, and
// lifts once frames come at a steady pace again.
const FLY_VEIL_MIN_MS = 260;
const FLY_VEIL_MAX_MS = 2500;
let flyEntryPending = false;
function enterFlyModeSmoothly() {
  if (flyMode || flyEntryPending) return;
  flyEntryPending = true;
  let veil = document.getElementById('fly-entry-veil');
  if (!veil) {
    veil = document.createElement('div');
    veil.id = 'fly-entry-veil';
    veil.setAttribute('aria-hidden', 'true');
    document.body.appendChild(veil);
  }
  veil.classList.add('is-on');
  const started = performance.now();
  // Two frames, so the fade is on screen before enterFlyMode's work lands.
  requestAnimationFrame(() => requestAnimationFrame(() => {
    flyEntryPending = false;
    if (!flyMode) enterFlyMode();
    let last = performance.now();
    let steady = 0;
    const settle = (now) => {
      steady = now - last < 40 ? steady + 1 : 0;
      last = now;
      const held = now - started;
      if ((steady >= 3 && held >= FLY_VEIL_MIN_MS) || held >= FLY_VEIL_MAX_MS) veil.classList.remove('is-on');
      else requestAnimationFrame(settle);
    };
    requestAnimationFrame(settle);
  }));
}

// Exit fly mode
function exitFlyMode() {
  flyMode = false;
  modeExit('fly');
  closeFastTravelMenu();
  closeAutopilotMenu();
  cancelAutopilot();
  clearFlyParkedBody({ inheritVelocity: false });
  if (fastTravelAnim) { fastTravelAnim.kill(); fastTravelAnim = null; }
  flyToggle.classList.remove('playing');
  flyLabel.textContent = vt('controls.fly.label');
  flyControlsOverlay.style.display = 'none';
  document.body.classList.remove('fly-mode');
  document.body.classList.remove('fly-mouse-look');
  document.body.classList.remove('warp-active');
  endFlyMouseLook();
  isMiddleMouseDown = false;
  _orbitChaseEulerSaved = false;
  orbitParked = false;
  _brakeLatch = false;
  _faArmed = false;
  setFlyTarget(null);
  if (navigation) navigation.closeMap();
  hideAlienScanHud(true);

  // Restore the overview ambient lift so texture-based planet dark
  // hemispheres read clearly again when zoomed out.
  if (_ambientLightRef && _flyModeAmbientSaved != null) {
    _ambientLightRef.intensity = _flyModeAmbientSaved;
    _flyModeAmbientSaved = null;
  }

  // Stop UFO hum
  stopUfoHum();

  // Reset orbit stabilization
  clearOrbitStabilization();

  // Re-enable orbit controls
  controls.enabled = true;
  // The near plane goes back to the one the view had before fly mode, here
  // and only here. The dynamic fly near saved the plane on its first fly
  // frame, which was already enterFlyMode's 0.01, and wrote that back on the
  // next frame, so every body was left at 0.01 after flying.
  _flyDynamicNearActive = false;
  _flyDynamicNearSavedValue = null;
  if (flySavedCameraNear != null) {
    setCameraNearPlane(flySavedCameraNear);
    flySavedCameraNear = null;
  }

  // Reset fly input so re-entering starts from a clean cockpit state.
  resetFlyInputState({ resetKeys: true });

  // Show other UI elements
  document.getElementById('controls-hint').style.display = 'flex';
  document.getElementById('planet-nav').style.display = 'flex';
  document.getElementById('controls-panel').style.display = 'flex';
  if (distanceIndicator) distanceIndicator.style.display = 'flex';

  // Animate back to overview looking at sun. Tweens are stored (and
  // generation-tagged) so re-entering fly mode or focusing a body
  // during the 2 s window kills them instead of letting the delayed
  // onComplete null out the fresh selection.
  killExitFlyTweens();
  const gen = exitFlyTweenGen;
  leaseCameraToTween();
  // Round the Sun from its far side (lerpRoundSun). The start is taken when
  // the tween first runs, as gsap took it from the position it tweened.
  const exitFrom = camera.position.clone();
  const exitTo = new THREE.Vector3(350, 220, 550);
  const exitState = { t: 0 };
  exitFlyTweens.push(gsap.to(exitState, {
    t: 1,
    duration: 2,
    ease: 'power2.inOut',
    onStart: () => { exitFrom.copy(camera.position); },
    onUpdate: () => { lerpRoundSun(camera.position, exitFrom, exitTo, exitState.t); }
  }));

  exitFlyTweens.push(gsap.to(controls.target, {
    x: 0,
    y: 0,
    z: 0,
    duration: 2,
    ease: 'power2.inOut',
    onComplete: () => {
      if (gen !== exitFlyTweenGen) return; // superseded by a new selection
      exitFlyTweens = [];
      selectedObject = null;
      currentNavIndex = 0;
      navLabel.textContent = 'Sun';
      // Landed on the overview, so its own near plane, as the back button
      // sets: the one from before fly mode was the body's, 0.0004 after an
      // Earth close-up, which the overview kept from 700 units out.
      setCameraNearPlane(getOverviewCameraNear());
    }
  }));
}

// === Rocketry mode ===
// Mirrors the fly-mode entry/exit shape: disable orbit controls, hide the
// overview chrome, hand the camera and the clock to the module, and
// restore everything on exit. The module itself lives in src/rocketry/.
function setRocketryAppUIVisible(visible) {
  const disp = visible ? 'flex' : 'none';
  const hint = document.getElementById('controls-hint');
  const nav = document.getElementById('planet-nav');
  const panel = document.getElementById('controls-panel');
  if (hint) hint.style.display = disp;
  if (nav) nav.style.display = disp;
  if (panel) panel.style.display = disp;
  if (distanceIndicator) distanceIndicator.style.display = disp;
  if (!visible) matryoshka.close();
}

// opts.spectate: a launch record to watch in 3D (src/rocketry/spectator.js)
// instead of opening the Space Center.
async function enterRocketryMode(opts = {}) {
  if (rocketryActive || rocketryLoading) return;
  rocketryLoading = true;
  earthCloseView?.stop();
  // Rocketry's budget installs its own scene target from the composer's
  // sample count, so the viewer's comes off first (renderFrame puts it back
  // once rocketry has gone).
  viewerSceneTarget.uninstall();
  try {
    if (flyMode) exitFlyMode();
    killExitFlyTweens();
    if (!rocketry) {
      const mod = await import('./rocketry/index.js');
      rocketry = mod.createRocketry({
        scene, camera, renderer, controls,
        get composer() { return composer; },
        get composerRenderPass() { return composerRenderPass; },
        getPlanets: () => planets,
        getMoons: () => moons,
        getSun: () => sun,
        getSimulationJD: () => simulationJD,
        setSimulationJD: (jd) => { simulationJD = jd; },
        getTimeSpeed: () => timeSpeed,
        setTimeSpeed: (v) => { timeSpeed = v; },
        getEphemerisRate: () => (realisticScaleMode ? 1 : ARTISTIC_TIME_RATE),
        isRealisticScale: () => realisticScaleMode,
        setRealisticScale: (on) => { if (window.__setRealisticScaleMode) window.__setRealisticScaleMode(on); },
        getBodyBaseRadius,
        getLoadedTextures: () => loadedTextures,
        formatDate: (jd) => jdToDateString(jd),
        closeTransientPanels,
        // Rocketry has its own score, so it needs to know whether the user
        // wants audio at all and to park the Tone.js ambient while it plays.
        isAudioOn: () => audioPlaying,
        // Idempotent both ways, so a repeated park never restarts it.
        suspendAmbient: async (on) => {
          try {
            if (on) await stopAmbient();
            else if (audioPlaying) await startAmbient();
          } catch (err) { console.warn('[rocketry] ambient handover failed', err); }
        },
        clearSelection: () => { selectedObject = null; clearFocusHash(); },
        setAppUIVisible: setRocketryAppUIVisible,
        returnToOverview,
        onExit: () => exitRocketryMode()
      });
      observeRocketryEvents(rocketry.events);
    }
    rocketryActive = true;
    modeEnter('rocketry');
    if (!rocketryStartTracked) { rocketryStartTracked = true; track('rocketry_start'); }
    const rocketToggle = document.getElementById('rocket-toggle');
    if (rocketToggle) rocketToggle.classList.add('playing');
    if (opts.spectate) rocketry.spectate(opts.spectate);
    else rocketry.enter();
  } catch (err) {
    console.error('[rocketry] failed to start', err);
    rocketryActive = false;
  } finally {
    rocketryLoading = false;
  }
}

// Launches: the viewer's Launches panel and #launch/<slug> watch a real
// launch in 3D (src/launches/watch.js, src/rocketry/spectator.js).
let launchWatchModule = null;
function launchWatch() {
  if (!launchWatchModule) {
    launchWatchModule = import('./launches/watch.js').then((m) => m.launchWatch({
      async spectate(launch) {
        if (rocketryActive && rocketry) { rocketry.spectate(launch); return rocketry; }
        await enterRocketryMode({ spectate: launch });
        return rocketryActive ? rocketry : null;
      }
    }));
  }
  return launchWatchModule;
}

function exitRocketryMode() {
  if (!rocketryActive || !rocketry) return;
  rocketryActive = false;
  // An experience is a sub-mode of rocketry, so leaving rocketry ends it too.
  modeExit('experience');
  modeExit('rocketry');
  const rocketToggle = document.getElementById('rocket-toggle');
  if (rocketToggle) rocketToggle.classList.remove('playing');
  rocketry.exit();
}

// Rocketry milestones for GA4. Subscribed once, when the module first loads,
// on the bus that outlives enter/exit. Only names and keys go out, never
// positions or saves.
function observeRocketryEvents(events) {
  if (!events || typeof events.on !== 'function') return;
  const vesselName = (e) => e?.vessel?.name || undefined;
  events.on('launch', (e) => track('rocketry_launch', { site: e?.site || 'pad', vessel: vesselName(e) }));
  events.on('liftoff', (e) => track('rocketry_liftoff', { vessel: vesselName(e) }));
  events.on('orbit', (e) => {
    track('rocketry_orbit', { body: e?.vessel?.body, vessel: vesselName(e) });
    // The player's first orbit ever in this browser: the game's key milestone.
    try {
      if (!localStorage.getItem('analytics.firstOrbit')) {
        localStorage.setItem('analytics.firstOrbit', '1');
        track('first_orbit', { vessel: vesselName(e) });
      }
    } catch { /* storage blocked: skip the milestone */ }
  });
  events.on('landed', (e) => track('rocketry_landed', { body: e?.body, splashed: e?.splashed ? 1 : 0, speed: Math.round(e?.speed ?? 0) }));
  events.on('crash', (e) => track('rocketry_crash', { body: e?.vessel?.body, speed: Math.round(e?.speed ?? 0) }));
  events.on('docked', (e) => track('rocketry_docked', { vessel: vesselName(e) }));
  events.on('recovered', (e) => track('rocketry_recovered', { vessel: vesselName(e) }));
  // Experiences (Moon rover, Mars rover) are timed as their own mode, keyed by
  // id in the params so mode_time rows split per experience.
  events.on('experience', (e) => {
    modeExit('experience');
    modeEnter('experience', { experience_id: e?.id });
    track('experience_start', { experience_id: e?.id });
  });
}

// === Fast travel ===
// Lets the user jump the UFO straight to any major destination — sun,
// planets, moon, comet, probe, or the wormhole. Instant snap rather than
// a tween, because the fly-mode physics loop writes camera.position and
// camera.quaternion every frame and would stomp a per-frame interp.
let fastTravelAnim = null;

// World position of the active system's primary star (Sol's sun at home).
function activeStarWorldPosition(out) {
  const handle = activeSystemId !== 'sol' ? remoteSystems[activeSystemId] : null;
  if (handle) {
    const primary = handle.data.stars.find((st) => st.isPrimary) || handle.data.stars[0];
    const obj = primary && handle.bodies[primary.id];
    if (obj) return obj.getWorldPosition(out);
  }
  if (sun) return sun.getWorldPosition(out);
  return out.set(0, 0, 0);
}

// Destinations inside a generated or authored remote system: its stars
// and planets, keyed 'remote:<bodyId>' so they never collide with Sol
// names, plus the jump home. Sol's own list would teleport the ship
// back across the galaxy.
function getRemoteSystemTargets(handle) {
  const list = [];
  for (const star of handle.data.stars) {
    if (!handle.bodies[star.id]) continue;
    list.push({ key: `remote:${star.id}`, label: star.name, detail: `Star · ${star.spectralClass || star.type || ''}`.trim(), isStar: true });
  }
  for (const planet of handle.data.planets) {
    if (!handle.bodies[planet.id]) continue;
    list.push({ key: `remote:${planet.id}`, label: planet.name, detail: `${planet.type || 'Planet'}${planet.realDistance ? ` · ${planet.realDistance}` : ''}` });
  }
  if (activeSystemId !== 'gargantua') {
    list.push({ key: 'sol-home', label: 'Sol (home)', detail: 'Hyperspace jump home', special: true, systemJump: 'sol' });
  }
  return list;
}

function getFastTravelTargets() {
  const remoteHandle = activeSystemId !== 'sol' ? remoteSystems[activeSystemId] : null;
  if (remoteHandle && activeSystemId !== 'gargantua') return getRemoteSystemTargets(remoteHandle);
  const list = [];
  list.push({ key: 'sun', label: 'Sun', detail: 'Star · 0 AU' });
  const planetDistances = {
    mercury: '0.39 AU', venus: '0.72 AU', earth: '1.00 AU', mars: '1.52 AU',
    jupiter: '5.20 AU', saturn: '9.58 AU', uranus: '19.2 AU', neptune: '30.1 AU',
    pluto: '39.5 AU'
  };
  planetOrder.forEach((name) => {
    const data = celestialData[name];
    if (!data) return;
    list.push({
      key: name,
      label: data.name,
      detail: `${data.type || 'Planet'} · ${planetDistances[name] || ''}`.trim()
    });
  });

  // Every moon registered in celestialData, not just Earth's. Walk
  // each planet's primary moon plus any secondary moons captured in
  // the runtime moons map so the menu mirrors the objects-browser.
  planetOrder.forEach((planetName) => {
    const data = celestialData[planetName];
    if (data && data.moon) {
      list.push({
        key: data.moon.name.toLowerCase(),
        label: data.moon.name,
        detail: `Moon · ${data.name}`
      });
    }
    const secondaryMoons = moons[planetName] || [];
    secondaryMoons.forEach((moonGroup) => {
      const ud = moonGroup.userData || {};
      if (!ud.name) return;
      if (data && data.moon && data.moon.name === ud.name) return; // already added
      if (ud.isMinorMoon) return;
      list.push({
        key: ud.name.toLowerCase(),
        label: ud.name,
        detail: `Moon · ${data ? data.name : planetName}`
      });
    });
  });

  // ISS + named space probes (Voyager 1 etc).
  satellites.forEach((sat) => {
    list.push({
      key: sat.name.toLowerCase(),
      label: sat.name,
      detail: `${sat.type || 'Satellite'} · ${sat.realAltitude || 'LEO'}`
    });
  });
  spaceProbes.forEach((probe) => {
    list.push({
      key: probe.name.toLowerCase(),
      label: probe.name,
      detail: probe.type || 'Probe'
    });
  });

  // Comets (Halley's + any others authored), near-Earth objects, and
  // named main-belt + dwarf-planet bodies. All have geometry built on
  // ensureDeepSpaceBodies / belt-body construction, so they're
  // genuine visitable destinations.
  comets.forEach((comet) => {
    list.push({
      key: comet.name.toLowerCase(),
      label: comet.name,
      detail: comet.type || 'Comet'
    });
  });
  nearEarthObjects.forEach((neo) => {
    list.push({
      key: neo.name.toLowerCase(),
      label: neo.name,
      detail: neo.type || 'NEO'
    });
  });
  asteroidBeltBodies.forEach((body) => {
    list.push({
      key: body.name.toLowerCase(),
      label: body.name,
      detail: body.type || 'Belt body'
    });
  });
  dwarfPlanets.forEach((body) => {
    if (body.name === 'Pluto') return; // already in planet list
    list.push({
      key: body.name.toLowerCase(),
      label: body.name,
      detail: body.type || 'Dwarf planet'
    });
  });

  // Special-feature destinations.
  if (wormholeObject) {
    list.push({ key: 'wormhole', label: 'Wormhole', detail: 'Anomaly · gateway', special: true });
  }
  if (monolith) {
    list.push({ key: 'monolith', label: 'Monolith', detail: 'TMA-1 · Jupiter system', special: true });
  }
  if (milkyWayVista) {
    list.push({
      key: 'milky-way',
      label: 'Milky Way',
      detail: 'Vista · outside the galactic disc',
      special: true
    });
  }
  if (blackHole && blackHole.object) {
    list.push({
      key: 'black hole',
      label: 'Black Hole',
      detail: 'Stellar-mass anomaly · accretion disc',
      special: true
    });
  }
  if (nibiruHandle && nibiruHandle.group) {
    // Secret procedural body. Surfaced in autopilot per user request
    // so it's reachable without the URL hash, but kept off the main
    // planet list / info HUD — it still reads as an out-of-canon
    // wanderer rather than a regular tenth planet.
    list.push({
      key: 'nibiru',
      label: 'Nibiru',
      detail: 'Wandering gas giant · 3,600-year orbit',
      special: true
    });
  }

  // Gargantua — the supermassive black hole, reached through the
  // Saturn-orbit wormhole rather than by normal-space cruise. Now that
  // the Star Map UI is hidden, this is the entry point: selecting it
  // routes through jumpToSystem() (the wormhole flythrough), not the
  // standard fast-travel teleport / autopilot cruise, which can't
  // cross between systems. systemJump flags that special dispatch.
  if (remoteSystems['gargantua']) {
    if (activeSystemId === 'sol') {
      list.push({
        key: 'gargantua',
        label: 'Gargantua',
        detail: 'Supermassive black hole · via wormhole',
        special: true,
        systemJump: 'gargantua'
      });
    } else if (activeSystemId === 'gargantua') {
      list.push({
        key: 'sol-home',
        label: 'Sol (home)',
        detail: 'Return through the wormhole',
        special: true,
        systemJump: 'sol'
      });
    }
  }

  // Named deep-sky objects (Andromeda + bright nebulae). These live
  // on a celestial shell at ~40M units, way past the solar system —
  // the autopilot cruise just heads outward toward them and switches
  // to orbiting their direction vector once close.
  if (deepSkyObjects && deepSkyObjects.group) {
    deepSkyObjects.group.children.forEach((sprite) => {
      if (!sprite.name) return;
      list.push({
        key: sprite.name.toLowerCase(),
        label: sprite.name,
        detail: 'Deep sky',
        special: true
      });
    });
  }

  return list;
}

function populateFastTravelMenu() {
  if (!fastTravelList) return;
  // Wormhole, comets, and probes are deep-space bodies that load lazily
  // on first toggle. Force them in here so every destination is real and
  // visible the moment the menu opens.
  ensureDeepSpaceBodies();
  if (!deepSpaceBodiesVisible) setDeepSpaceBodiesVisible(true);
  fastTravelList.innerHTML = '';
  const targets = getFastTravelTargets();
  targets.forEach((target) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'fast-travel-item' + (target.special ? ' special' : '');
    btn.dataset.key = target.key;
    btn.innerHTML = `
      <span class="fast-travel-item-name">${target.label}</span>
      <span class="fast-travel-item-detail">${target.detail || ''}</span>
    `;
    // Cross-system destinations (Gargantua, Sol return) warp through
    // the wormhole rather than cruising/teleporting within the current
    // system, so they bypass the findLoadedSceneObjectByName lookup.
    if (target.systemJump) {
      const jump = (e) => {
        if (e.button !== undefined && e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        lockFlyUiInput();
        closeFastTravelMenu();
        jumpToSystem(target.systemJump);
      };
      btn.addEventListener('pointerdown', jump);
      btn.addEventListener('click', jump);
      fastTravelList.appendChild(btn);
      return;
    }
    const obj = findLoadedSceneObjectByName(target.key);
    if (!obj) {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      const blockDisabled = (e) => {
        e.preventDefault();
        e.stopPropagation();
        lockFlyUiInput();
      };
      btn.addEventListener('pointerdown', blockDisabled);
      btn.addEventListener('click', blockDisabled);
    } else {
      let selectionHandled = false;
      const selectDestination = (e) => {
        if (e.button !== undefined && e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        lockFlyUiInput();
        if (selectionHandled) return;
        selectionHandled = true;
        closeFastTravelMenu();
        fastTravelTo(obj, target);
      };
      btn.addEventListener('pointerdown', selectDestination);
      btn.addEventListener('click', selectDestination);
    }
    fastTravelList.appendChild(btn);
  });
}

function openFastTravelMenu() {
  if (!flyMode || !fastTravelMenu) return;
  populateFastTravelMenu();
  fastTravelMenu.classList.add('open');
  fastTravelMenu.setAttribute('aria-hidden', 'false');
  if (fastTravelBtn) fastTravelBtn.classList.add('active');
}

function closeFastTravelMenu() {
  if (!fastTravelMenu) return;
  fastTravelMenu.classList.remove('open');
  fastTravelMenu.setAttribute('aria-hidden', 'true');
  if (fastTravelBtn) fastTravelBtn.classList.remove('active');
}

// === AUTOPILOT ===
// Cinematic cruise to a celestial body, followed by a slow circular
// orbit at viewing distance. Differs from fast travel in two ways:
// the journey itself is visible (5-9 seconds eased motion) rather
// than an instant teleport, and on arrival the ship transitions into
// an automatic orbital sweep so you can study the body without
// touching the controls. Any user input (WASD, mouse drag, etc.)
// cancels autopilot and returns manual control.
const autopilotBtn = document.getElementById('autopilot-btn');
const autopilotMenu = document.getElementById('autopilot-menu');
const autopilotMenuClose = document.getElementById('autopilot-close');
const autopilotList = document.getElementById('autopilot-list');
const autopilotStatus = document.getElementById('autopilot-status');
const autopilotStatusText = document.getElementById('autopilot-status-text');
const autopilotCancelBtn = document.getElementById('autopilot-cancel');

const autopilot = {
  active: false,
  phase: 'idle', // 'cruising' | 'orbiting'
  target: null, // celestial target descriptor from getAutopilotTargets
  object: null, // Three.js scene object for the body
  cruiseT: 0,
  cruiseDuration: 7.0,
  startPos: new THREE.Vector3(),
  startQuat: new THREE.Quaternion(),
  endPos: new THREE.Vector3(),
  endQuat: new THREE.Quaternion(),
  // Orbit state (set on arrival).
  orbitCenter: new THREE.Vector3(),
  orbitRadius: 0,
  // Orbit basis vectors. The camera position is
  //   center + cos(angle) * radius * orbitU + sin(angle) * radius * orbitV.
  // U is the in-plane east, V is the in-plane north, computed once at
  // arrival so we have a consistent plane to sweep around.
  orbitU: new THREE.Vector3(),
  orbitV: new THREE.Vector3(),
  orbitAngle: 0,
  // Camera offset from orbitCenter for the current frame (radius·(cosA·U
  // + sinA·V)). Captured each orbit frame so reframeAutopilotOrbit() can
  // re-place the camera against the body's post-motion world position.
  orbitOffset: new THREE.Vector3(),
  orbitAngularSpeed: 0.06, // rad/sec — about 105 seconds per revolution
  // Cruise→orbit radius blend so arrival doesn't snap. transitionT
  // counts from 0 to transitionDuration; the orbit phase reads
  // transitionFromRadius and eases toward orbitRadius across this
  // window. Initialised so a cold orbit phase (without a cruise
  // handoff) reads as "already converged" and skips the blend.
  transitionT: 9999,
  transitionDuration: 0,
  transitionFromRadius: 0
};

function openAutopilotMenu() {
  if (!flyMode || !autopilotMenu) return;
  populateAutopilotMenu();
  autopilotMenu.classList.add('open');
  autopilotMenu.setAttribute('aria-hidden', 'false');
  if (autopilotBtn) autopilotBtn.classList.add('active');
}

function closeAutopilotMenu() {
  if (!autopilotMenu) return;
  autopilotMenu.classList.remove('open');
  autopilotMenu.setAttribute('aria-hidden', 'true');
  if (autopilotBtn) autopilotBtn.classList.remove('active');
}

function populateAutopilotMenu() {
  if (!autopilotList) return;
  ensureDeepSpaceBodies();
  if (!deepSpaceBodiesVisible) setDeepSpaceBodiesVisible(true);
  autopilotList.innerHTML = '';
  const targets = getFastTravelTargets();
  targets.forEach((target) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'fast-travel-item' + (target.special ? ' special' : '');
    btn.dataset.key = target.key;
    btn.innerHTML = `
      <span class="fast-travel-item-name">${target.label}</span>
      <span class="fast-travel-item-detail">${target.detail || ''}</span>
    `;
    // Cross-system destinations warp through the wormhole — there's no
    // in-system cruise that crosses the bridge, so route them through
    // jumpToSystem() the same way the fast-travel menu does.
    if (target.systemJump) {
      const jump = (e) => {
        if (e.button !== undefined && e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        lockFlyUiInput();
        closeAutopilotMenu();
        jumpToSystem(target.systemJump);
      };
      btn.addEventListener('pointerdown', jump);
      btn.addEventListener('click', jump);
      autopilotList.appendChild(btn);
      return;
    }
    const obj = findLoadedSceneObjectByName(target.key);
    if (!obj) {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
    } else {
      const selectDest = (e) => {
        if (e.button !== undefined && e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        lockFlyUiInput();
        closeAutopilotMenu();
        startAutopilotCruise(obj, target);
      };
      btn.addEventListener('pointerdown', selectDest);
      btn.addEventListener('click', selectDest);
    }
    autopilotList.appendChild(btn);
  });
}

function showAutopilotStatus(text) {
  if (!autopilotStatus) return;
  autopilotStatus.classList.add('active');
  autopilotStatus.setAttribute('aria-hidden', 'false');
  if (autopilotStatusText) autopilotStatusText.textContent = text;
}
function hideAutopilotStatus() {
  if (!autopilotStatus) return;
  autopilotStatus.classList.remove('active');
  autopilotStatus.setAttribute('aria-hidden', 'true');
}

function startAutopilotCruise(object, target) {
  if (!object || !flyMode) return;
  // Tear down anything else that writes to the camera.
  if (fastTravelAnim) { fastTravelAnim.kill(); fastTravelAnim = null; }
  clearOrbitStabilization();
  // Release any body-park lock so the cruise can move freely.
  clearFlyParkedBody({ inheritVelocity: false });
  resetFlyInputState({ resetKeys: false });

  // Resolve the body's world position and a viewing-orbit point near
  // it. The approach offset is biased away from the sun so the body
  // stays lit when we get there.
  const targetPos = new THREE.Vector3();
  object.getWorldPosition(targetPos);
  const radius = (object.userData && (object.userData.radius || object.userData.size))
    || (target && target.key === 'wormhole' ? 18 : 1);
  const approach = fastTravelApproachDistance(target, radius);

  const sunPos = activeStarWorldPosition(new THREE.Vector3());
  const fromSun = new THREE.Vector3().subVectors(targetPos, sunPos);
  if (fromSun.lengthSq() < 1e-4) fromSun.set(0, 0.2, 1);
  fromSun.normalize();

  const lift = new THREE.Vector3(0, 1, 0);
  const sideAxis = new THREE.Vector3().crossVectors(fromSun, lift);
  if (sideAxis.lengthSq() < 1e-4) sideAxis.set(1, 0, 0);
  sideAxis.normalize();

  const approachOffset = fromSun.clone().multiplyScalar(approach * 0.9)
    .addScaledVector(lift, approach * 0.18)
    .addScaledVector(sideAxis, approach * 0.12);
  const arrivalPos = targetPos.clone().add(approachOffset);

  // Build the target look quaternion (camera at arrival, facing body).
  const lookM = new THREE.Matrix4().lookAt(arrivalPos, targetPos, camera.up);
  const arrivalQuat = new THREE.Quaternion().setFromRotationMatrix(lookM);

  // Cruise duration scales gently with distance so distant bodies feel
  // longer to reach without dragging on for nearby ones.
  const cruiseDist = camera.position.distanceTo(arrivalPos);
  const baseDuration = 6.0;
  const distFactor = Math.min(1.6, 1.0 + cruiseDist / 5000);
  autopilot.cruiseDuration = baseDuration * distFactor;

  autopilot.active = true;
  autopilot.phase = 'cruising';
  autopilot.target = target;
  autopilot.object = object;
  autopilot.cruiseT = 0;
  autopilot.startPos.copy(camera.position);
  autopilot.startQuat.copy(camera.quaternion);
  autopilot.endPos.copy(arrivalPos);
  autopilot.endQuat.copy(arrivalQuat);
  autopilot.orbitCenter.copy(targetPos);
  autopilot.orbitRadius = approach;
  // Build a stable orbit plane: U points from body toward arrivalPos
  // (the camera's initial radial direction), V completes a right-
  // handed basis with world-up so the orbit drifts mostly in
  // longitude rather than tumbling weirdly.
  autopilot.orbitU.copy(arrivalPos).sub(targetPos).normalize();
  autopilot.orbitV.crossVectors(autopilot.orbitU, lift);
  if (autopilot.orbitV.lengthSq() < 1e-4) {
    autopilot.orbitV.set(1, 0, 0);
  } else {
    autopilot.orbitV.normalize();
  }
  autopilot.orbitAngle = 0;

  showAutopilotStatus(`Autopilot to ${target.label}`);
}

// Scratch vectors for the collision-avoidance probe. Reused across the
// per-body loop so no per-frame allocations leak into the hot path.
const _autopilotProbePos = new THREE.Vector3();
const _autopilotFromBody = new THREE.Vector3();
// Scratch vectors for the swept-sphere collision check used by
// enforceCollisionAvoidance to prevent warp-speed tunneling.
const _sweepPrev = new THREE.Vector3();
const _sweepSegment = new THREE.Vector3();
const _sweepToCenter = new THREE.Vector3();
const _sweepClosest = new THREE.Vector3();
const _sweepOutward = new THREE.Vector3();
const _autopilotLookMatrix = new THREE.Matrix4();
const _autopilotLookQuat = new THREE.Quaternion();
const _autopilotHeading = new THREE.Vector3();

// Per-body classification used by the boundary system. Drives how
// tight the safety bubble can be — rocky bodies have a real surface
// just above the bump-map elevation, gas/ice giants have no surface
// (just denser cloud decks) so the ship can sink further, the sun
// keeps a thick hot-zone so close approach doesn't broil. Type
// strings come from celestialData (see Sun/Star, Terrestrial Planet,
// Gas Giant, Ice Giant, Dwarf Planet).
function getBodyKind(body) {
  if (!body) return 'other';
  if (body === sun) return 'star';
  const t = ((body.userData && body.userData.type) || '').toLowerCase();
  if (t.includes('star')) return 'star';
  if (t.includes('gas giant') || t.includes('ice giant') || t.includes('jupiter')) return 'giant';
  if (t.includes('terrestrial') || t.includes('dwarf') || t.includes('satellite') || t.includes('world')) return 'rocky';
  return 'other';
}

function getBodyBaseRadius(body) {
  const artistic = (body && body.userData && (body.userData.radius || body.userData.size)) || 1;
  // In realistic-scale mode the rendered mesh is ~12× smaller than the
  // authored artistic radius (same lookup the lens flare uses), so all
  // collision math — safe bubble, damping zone, death radius — must
  // follow the visible surface, not the artistic one. Bodies without a
  // real-radius entry (moons, probes) aren't rescaled by
  // applyRealisticBodyScales, so they keep the artistic value.
  if (realisticScaleMode && body && body.userData) {
    const real = getRealisticBodyRadius((body.userData.name || '').toLowerCase());
    if (real) return real;
  }
  return artistic;
}

// Earth terrain sampling for fly-mode surface clearance. The render
// shader displaces the Earth mesh from the bump map; matching that
// approximately on the CPU lets the collision shell follow mountains
// and ocean basins instead of a perfectly smooth sphere.
let earthBumpSampler = null;
let earthBumpReading = null; // the bump texture a read is under way for
let earthBumpFailed = null;  // the one whose read failed, not retried
const _terrainInvMatrix = new THREE.Matrix4();
const _terrainLocalDir = new THREE.Vector3();
const _terrainUV = new THREE.Vector2();

// The heights (the bump map's red channel, a byte a texel) are read in a
// worker: on the main thread the 8K map stalled the first frame of fly mode
// for about 150 ms. Until they arrive the shell is the smooth sphere.
function getEarthBumpSampler() {
  const tex = loadedTextures.earth_bump;
  if (earthBumpSampler && earthBumpSampler.texture === tex) return earthBumpSampler;
  const img = tex && tex.image;
  // A late detail texture must be retried; caching false permanently left
  // mountain clearance on a sea-level sphere after a cold load.
  if (!img || !img.width || !img.height || typeof document === 'undefined') return null;
  if (earthBumpReading !== tex && earthBumpFailed !== tex) {
    earthBumpReading = tex;
    readEarthHeights(img).then((data) => {
      if (earthBumpReading !== tex) return;
      earthBumpReading = null;
      earthBumpSampler = { texture: tex, width: img.width, height: img.height, data };
    }).catch(() => {
      if (earthBumpReading === tex) earthBumpReading = null;
      earthBumpFailed = tex;
    });
  }
  return null;
}

function readEarthHeights(img) {
  if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined' || typeof createImageBitmap === 'undefined') {
    return Promise.resolve().then(() => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0);
      const rgba = ctx.getImageData(0, 0, img.width, img.height).data;
      const red = new Uint8Array(rgba.length / 4);
      for (let i = 0; i < red.length; i++) red[i] = rgba[i * 4];
      return red;
    });
  }
  const inWorker = (message, transfer) => new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./earthheights.worker.js', import.meta.url));
    worker.onmessage = (e) => {
      worker.terminate();
      if (e.data) resolve(e.data);
      else reject(new Error('heights unread'));
    };
    worker.onerror = (e) => { worker.terminate(); reject(e); };
    worker.postMessage(message, transfer);
  });
  // The worker fetches the file (from the HTTP cache) and decodes it itself:
  // a bitmap made here from the <img> was a copy on the main thread, 124 ms
  // for the 8K map.
  const url = imageFileUrl(img);
  const fromImage = () => createImageBitmap(img).then((bitmap) => inWorker(bitmap, [bitmap]));
  if (!url) return fromImage();
  return inWorker({ url, width: img.naturalWidth, height: img.naturalHeight }, []).catch(fromImage);
}

function getEarthMeshForTerrain() {
  const earth = planets && planets.earth;
  if (!earth) return null;
  const tiltGroup = earth.getObjectByName('tiltGroup');
  if (!tiltGroup) return null;
  return tiltGroup.children.find((child) => child.isMesh) || null;
}

function sampleEarthTerrainDisplacement(body, outwardWorldDir) {
  if (!body || body.name !== 'earth' || !outwardWorldDir) return 0;
  const mesh = getEarthMeshForTerrain();
  const sampler = getEarthBumpSampler();
  if (!mesh || !sampler || !mesh.material || !mesh.material.uniforms) return 0;

  _terrainInvMatrix.copy(mesh.matrixWorld).invert();
  _terrainLocalDir.copy(outwardWorldDir).transformDirection(_terrainInvMatrix).normalize();

  earthSurfaceUV(_terrainLocalDir, _terrainUV);
  const x = Math.min(sampler.width - 1, Math.max(0, Math.floor(_terrainUV.x * sampler.width)));
  const y = Math.min(sampler.height - 1, Math.max(0, Math.floor(_terrainUV.y * sampler.height)));
  const red = sampler.data[y * sampler.width + x] / 255;
  const displacementScale = mesh.material.uniforms.displacementScale
    ? mesh.material.uniforms.displacementScale.value
    : body.userData.radius * EARTH_ELEVATION_METRES / EARTH_METRES;
  return red * displacementScale;
}

function getBodyVisualSurfaceRadius(body, outwardWorldDir = null) {
  const radius = getBodyBaseRadius(body);
  // Terrain displacement is authored in artistic mesh-local units; in
  // realistic mode the tiltGroup scale shrinks the rendered terrain
  // along with the body, so scale the CPU sample by the same factor
  // (radius/artistic = 1 in artistic mode).
  const artistic = (body && body.userData && (body.userData.radius || body.userData.size)) || 1;
  const displacement = sampleEarthTerrainDisplacement(body, outwardWorldDir) * (radius / artistic);
  return Math.max(0.001, radius + displacement);
}

function getBodyAtmosphereTopRadius(body, surfaceRadius = getBodyBaseRadius(body)) {
  const data = body && body.userData;
  if (!data || !data.hasAtmosphere) return surfaceRadius;
  const shellScale = data.atmosphereScale || 1.025;
  const visualTop = getBodyBaseRadius(body) * shellScale;
  // The visible shell is intentionally thin, but manual atmospheric
  // flight needs a usable airspace. Earth gets a deeper pilotable
  // envelope so transition-from-space has time to decelerate and
  // level out before the ground.
  if (body.name === 'earth') return Math.max(visualTop, surfaceRadius + getBodyBaseRadius(body) * 0.6);
  return Math.max(visualTop, surfaceRadius + getBodyBaseRadius(body) * 0.12);
}

function getBodySurfaceClearanceDistance(body) {
  const kind = getBodyKind(body);
  const radius = getBodyBaseRadius(body);
  if (kind === 'star') return radius * 0.8;    // hot-zone, keeps the ship outside the corona
  if (kind === 'giant') return radius * 0.06;  // just above visible cloud tops
  if (kind === 'rocky') {
    // Collision distances must follow the live rig, not its space-view
    // display size (which amounts to hundreds of kilometres on Earth).
    let rigDepth = 0;
    if (flyMode && playerShip) {
      const profile = getActiveCraftProfile();
      getCraftFollowOffset(FLY_SCRATCH_CRAFT_OFFSET, profile);
      rigDepth = Math.abs(Math.min(FLY_SCRATCH_CRAFT_OFFSET.y, 0))
        + getActiveCraftBoundRadius() * 0.7 + 0.004 * atmoCraftScaleLive;
    }
    // Margin over the sampled terrain. Earth's was 250 m, which put an
    // invisible floor a quarter of a kilometre up: the ship could never get
    // near the ground, and holding the descent against that wall is what
    // read as the shake. The terrain height is sampled from the elevation
    // map directly, so a few metres of margin is enough.
    const groundBuffer = body.name === 'earth' ? radius * 15 / EARTH_METRES : Math.max(radius * 0.005, 0.012);
    return Math.max(groundBuffer, rigDepth);
  }
  return radius * 0.5;                         // unknown body type, conservative
}

// Body safety bubble. Replaces the old uniform 2.5× radius wall
// with a body-specific surface clearance so the ship can enter
// the atmosphere on rocky planets but still can't punch through
// the body. The target-body variant returns a tighter bubble so
// an autopilot can land at a close arrival distance without being
// blocked by its own destination's safety zone — the bubble still
// prevents the ship from clipping into the body itself.
const _safeShipOutward = new THREE.Vector3();
function getBodySafeRadius(body, isTarget = false) {
  void isTarget;
  const bodyPos = _autopilotProbePos;
  body.getWorldPosition(bodyPos);
  _autopilotFromBody.copy(camera.position).sub(bodyPos);
  const dist = _autopilotFromBody.length();
  const outward = dist > 1e-6 ? _autopilotFromBody.divideScalar(dist) : null;
  let surfaceRadius = getBodyVisualSurfaceRadius(body, outward);
  // In atmospheric flight the chase slot rides terrain AHEAD of the
  // camera. The pilot's floor has to respect THAT terrain too, or a
  // ridge under the slot hoists the ship above the pilot's eye line
  // while the camera legally hugs a valley behind it.
  if (flyMode && playerShip && atmoEntry.body === body && atmoEntry.state !== FLIGHT_STATES.SPACE) {
    _safeShipOutward.copy(playerShip.position).sub(bodyPos);
    const shipDist = _safeShipOutward.length();
    if (shipDist > 1e-6) {
      _safeShipOutward.divideScalar(shipDist);
      surfaceRadius = Math.max(surfaceRadius, getBodyVisualSurfaceRadius(body, _safeShipOutward));
    }
  }
  return Math.max(surfaceRadius + getBodySurfaceClearanceDistance(body), 0.05);
}

// Outer radius of the soft velocity-damping zone (× body radius).
// Inside this zone (but outside the hard bubble), the ship's inward
// radial velocity is progressively reduced so manual approach
// decelerates naturally instead of slamming into an invisible wall.
function getBodyDampingRadius(body) {
  const radius = getBodyBaseRadius(body);
  const hard = getBodySafeRadius(body);
  const atmosphereTop = getBodyAtmosphereTopRadius(body, radius);
  // Wide enough to feel like atmospheric drag (~0.3 radii of soft
  // shell), and for atmospheric bodies at least covers the pilotable
  // envelope so re-entry slows before the surface.
  return Math.max(hard + radius * 0.3, atmosphereTop + radius * 0.15);
}

// Hard collision deflection. After the camera has been moved (by
// fly-mode thrust or autopilot velocity), this pushes it back outside
// any body's safety bubble. The optional velocity vector gets its
// inward component zeroed so the ship glides tangent to the bubble
// instead of fighting back into it the next frame — what gives manual
// flight the "force-field" feel where you can't slam into a planet.
// targetObject (if given) gets a TIGHTER bubble instead of being
// skipped, so an autopilot can land close to its destination but
// still can't punch through the body's surface.
// The flight body's hard floor, followed over time. getBodySafeRadius takes
// the higher of the terrain under the camera and the terrain under the chase
// slot, and which of those two is higher swaps as the rig swings, so the raw
// value steps between frames. The camera is snapped onto it every frame, so
// each step shows as a jolt, and the bigger the craft the further apart the
// two samples sit and the worse it gets. Rising is followed quickly, since
// that is the direction that keeps the ship out of a hillside; falling is
// followed over about a quarter of a second, which is what takes the
// high-frequency chatter out without letting the floor lag a descent.
let _flyFloorBody = null, _flyFloorRadius = 0;
function enforceCollisionAvoidance(velocity = null, targetObject = null, stepVec = null) {
  if (!camera) return;

  const checkBody = (body) => {
    if (!body) return;
    body.getWorldPosition(_autopilotProbePos);
    const isTarget = body === targetObject;
    let safeRadius = getBodySafeRadius(body, isTarget);
    if (flyMode && !isTarget && body === atmoEntry.body) {
      if (_flyFloorBody !== body) { _flyFloorBody = body; _flyFloorRadius = safeRadius; }
      _flyFloorRadius += (safeRadius - _flyFloorRadius) * (safeRadius > _flyFloorRadius ? 0.35 : 0.08);
      safeRadius = _flyFloorRadius;
    } else if (_flyFloorBody === body && !flyMode) {
      _flyFloorBody = null;
    }

    // SWEPT-SPHERE TUNNELING GUARD. At very high warp speeds the
    // per-frame movement can exceed a body's safe radius in a single
    // integration step, so the post-step point check sees the camera
    // already on the FAR side of the body and fires no collision —
    // the ship effectively tunneled through. To stop that, treat
    // the step as a line segment from [prevPos → currentPos] and
    // intersect it with the safe sphere. If the segment passes
    // inside, snap to the entry point and zero inward velocity.
    // This is what makes "never crash into the sun at warp" actually
    // hold up against W-held-at-warp-9.9999 attempts.
    if (stepVec && velocity && stepVec.lengthSq() > 1e-10 && !isTarget) {
      _sweepPrev.copy(camera.position).sub(stepVec);
      const segLen = stepVec.length();
      _sweepSegment.copy(stepVec).divideScalar(segLen);
      _sweepToCenter.copy(_autopilotProbePos).sub(_sweepPrev);
      const tProj = _sweepToCenter.dot(_sweepSegment);
      // Only checking forward along the step (tProj in [0, segLen])
      // — backwards intersections are last-frame's problem already.
      if (tProj > 0 && tProj < segLen) {
        _sweepClosest.copy(_sweepPrev).addScaledVector(_sweepSegment, tProj);
        const closestDist = _sweepClosest.distanceTo(_autopilotProbePos);
        if (closestDist < safeRadius) {
          // Segment crosses the safe sphere. Compute the entry-point
          // parameter along the segment (ray-sphere intersection
          // closer root), clamp non-negative.
          const halfChord = Math.sqrt(Math.max(0, safeRadius * safeRadius - closestDist * closestDist));
          const tEntry = Math.max(0, tProj - halfChord);
          camera.position.copy(_sweepPrev).addScaledVector(_sweepSegment, tEntry);
          _sweepOutward.copy(camera.position).sub(_autopilotProbePos);
          if (_sweepOutward.lengthSq() > 1e-6) {
            _sweepOutward.normalize();
            // Nudge camera slightly OUTSIDE the safe sphere along
            // outward, so the next frame's point check doesn't pick
            // us up as inside-the-bubble and fight the snap.
            camera.position.copy(_autopilotProbePos).addScaledVector(_sweepOutward, safeRadius + getBodyBaseRadius(body) * 1e-7);
            const radialDot = velocity.dot(_sweepOutward);
            if (radialDot < 0) {
              velocity.addScaledVector(_sweepOutward, -radialDot);
            }
          }
          return; // tunneling resolved, skip the redundant point check
        }
      }
    }

    _autopilotFromBody.copy(camera.position).sub(_autopilotProbePos);
    const dist = _autopilotFromBody.length();

    // HARD COLLISION: inside the bubble — snap to the surface and zero
    // any inward velocity so the ship glides tangent to the body.
    if (dist < safeRadius) {
      if (dist > 1e-6) {
        _autopilotFromBody.divideScalar(dist); // normalize without re-length
      } else {
        _autopilotFromBody.set(1, 0.2, 0.5).normalize();
      }
      camera.position.copy(_autopilotProbePos)
        .addScaledVector(_autopilotFromBody, safeRadius);

      if (velocity) {
        const radialDot = velocity.dot(_autopilotFromBody);
        if (radialDot < 0) {
          velocity.addScaledVector(_autopilotFromBody, -radialDot);
        }
      }
      return;
    }

    // SOFT DAMPING: between the hard bubble and the damping outer
    // radius, progressively reduce inward velocity so manual approach
    // feels like aerobraking rather than slamming a wall. Skip on the
    // autopilot target so arrivals aren't slowed to a crawl.
    if (!velocity || isTarget) return;
    const dampOuter = getBodyDampingRadius(body);
    if (dist >= dampOuter) return;
    // Inside an atmosphere the air is the brake (the density drag and the
    // time-to-surface cap in updateFlyMovement), and the hard bubble above
    // still keeps the ship off the ground. This zone was built to stop
    // crashes into airless bodies; over Earth it spanned the whole 0.6-radius
    // pilotable envelope and took most of the inward speed every frame, so
    // the ship stalled halfway down and could never reach the air it was
    // meant to fly in. The band above the envelope keeps its gentle braking.
    if (body.userData && body.userData.hasAtmosphere && dist < getBodyAtmosphereTopRadius(body)) return;

    if (dist > 1e-6) {
      // getBodyDampingRadius calls getBodySafeRadius, which reuses this
      // scratch vector. Reconstruct the unit direction; dividing its
      // already-normalized value again reverses braking on small planets.
      _autopilotFromBody.copy(camera.position).sub(_autopilotProbePos).divideScalar(dist);
    } else {
      return;
    }
    // t = 0 at the outer edge of the soft zone, 1 right at the hard
    // bubble. Quadratic ramp so far-out approach is barely affected
    // and the deceleration ramps up sharply near the surface.
    const t = (dampOuter - dist) / Math.max(1e-6, dampOuter - safeRadius);
    const radialDot = velocity.dot(_autopilotFromBody);
    if (radialDot < 0) {
      const dampStrength = t * t * 0.75;
      velocity.addScaledVector(_autopilotFromBody, -radialDot * dampStrength);
    }
  };

  forEachFlightBody(checkBody);
}

// Flight regime system. Five named zones blend smoothly so movement
// feels controllable at every scale:
//   NEAR   (< 0.45 body radii from surface): docking / surface skim
//   ATMOSPHERE (inside an authored atmosphere shell): scenic flight
//   APPROACH (0.45 - 3 radii): low-orbit maneuvering
//   PLANETARY (3 - 24 radii): cislunar / system-internal cruise
//   INTERPLANETARY (24+ radii): deep-space, full propulsion max
// On top of the static zone curve we layer a TIME-TO-SURFACE auto-
// brake: when the ship is heading at a body, the cap drops fast
// enough that we can decelerate before impact, independent of the
// static distance curve. Combined via min(), so whichever ceiling is
// tighter wins.
const FLIGHT_REGIMES = {
  NEAR:           { speedMul: 0.00012, label: 'SURFACE'       },
  ATMOSPHERE:     { speedMul: 0.0012,  label: 'ATMOSPHERE'    },
  APPROACH:       { speedMul: 0.0032,  label: 'APPROACH'      },
  PLANETARY:      { speedMul: 0.0048,  label: 'PLANETARY'      },
  INTERPLANETARY: { speedMul: 1.0,    label: 'INTERPLANETARY' }
};

// Optional throttle range. Auto = no extra clamp (the contextual cap
// + propulsion max decide). Lower presets impose an ABSOLUTE max
// speed regardless of propulsion + context, so the pilot can dial in
// precision flight without switching drive. The fast-travel path is
// the autopilot, which bypasses this entirely.
// NOTE ON UNITS: flyVelocity (and therefore these caps) is in scene
// units per 1/60 s — the integrator multiplies by delta*60. A cap of
// 0.08 is 4.8 u/s of REAL motion, which crosses Earth's whole 1.5-unit
// atmosphere envelope in a third of a second. The atmosphere cap was
// originally authored as if units were per-second (the classic 60×
// bug); 0.0015 ≈ 0.09 u/s actual, which crosses the visible Earth
// disc in about a minute — flight-sim pacing.
const SPEED_RANGES = {
  auto:      { label: 'Auto',      max: Infinity },
  atmosphere:{ label: 'Atmosphere',max: 0.0015   },
  precision: { label: 'Precision', max: 0.3      },
  approach:  { label: 'Approach',  max: 2        },
  cruise:    { label: 'Cruise',    max: 15       }
};
let currentSpeedRange = 'auto';

function setSpeedRange(rangeKey) {
  if (!SPEED_RANGES[rangeKey]) return;
  currentSpeedRange = rangeKey;
  const select = document.getElementById('speed-range');
  if (select && select.value !== rangeKey) select.value = rangeKey;
}

const _speedCapProbe = new THREE.Vector3();
const _speedCapToBody = new THREE.Vector3();

function computeAtmosphericDensity(nearest) {
  if (!nearest || !nearest.hasAtmosphere || nearest.atmosphereDepth <= 0) return 0;
  if (nearest.dist < 0 || nearest.dist > nearest.atmosphereDepth) return 0;
  const altitude01 = THREE.MathUtils.clamp(nearest.dist / nearest.atmosphereDepth, 0, 1);
  return Math.pow(1 - altitude01, 1.8);
}

// Returns the static regime factor (0..1) — how much of full
// propulsion max the ship can do at this position, ignoring approach
// velocity. Piecewise so each zone has a recognizable feel; smooth-
// stepped at each boundary so transitions don't snap.
function computeRegimeFactor(nearestSurfaceDist, nearestRadius, nearest = null) {
  if (!isFinite(nearestSurfaceDist) || nearestRadius <= 0) {
    return FLIGHT_REGIMES.INTERPLANETARY.speedMul;
  }
  const r = nearestRadius;
  const sd = Math.max(nearestSurfaceDist, 0);
  if (nearest && nearest.hasAtmosphere && nearest.atmosphereDepth > 0 && sd < nearest.atmosphereDepth) {
    const t = sd / nearest.atmosphereDepth;
    const surfaceEase = THREE.MathUtils.smoothstep(t, 0, 0.32);
    const upperEase = THREE.MathUtils.smoothstep(t, 0.55, 1);
    const lowAtmo = THREE.MathUtils.lerp(
      FLIGHT_REGIMES.NEAR.speedMul,
      FLIGHT_REGIMES.ATMOSPHERE.speedMul,
      surfaceEase
    );
    return THREE.MathUtils.lerp(
      lowAtmo,
      FLIGHT_REGIMES.APPROACH.speedMul,
      upperEase
    );
  }
  // Widened zone boundaries so the slowdown reaches further out from
  // each body. NEAR through PLANETARY together cover 0–24 body radii;
  // INTERPLANETARY only opens once the planet is scenery behind you.
  if (sd >= r * 24) return FLIGHT_REGIMES.INTERPLANETARY.speedMul;
  if (sd < r * 0.45) {
    // NEAR zone (0 - 0.45 radii): docking / surface skim. Linear so the
    // approach into the surface tightens without an inflection bump.
    const t = sd / (r * 0.45);
    return FLIGHT_REGIMES.NEAR.speedMul
         + (FLIGHT_REGIMES.APPROACH.speedMul - FLIGHT_REGIMES.NEAR.speedMul) * t;
  }
  if (sd < r * 3) {
    // APPROACH zone (0.45 - 3 radii): low-orbit maneuvering. Smoothstep
    // to keep the transition out of NEAR feeling continuous.
    const t = (sd - r * 0.45) / (r * 2.55);
    return FLIGHT_REGIMES.APPROACH.speedMul
         + (FLIGHT_REGIMES.PLANETARY.speedMul - FLIGHT_REGIMES.APPROACH.speedMul)
           * THREE.MathUtils.smoothstep(t, 0, 1);
  }
  // PLANETARY zone (3 - 24 radii): cislunar / system-internal cruise.
  // Quartic-of-smoothstep blend so the cap stays low through most of
  // the zone and only opens up to full INTERPLANETARY near the outer
  // boundary. With a plain smoothstep, mid-zone was already at half
  // the speed-mul gap, which read as "fast at 6 radii out" — exactly
  // the "still too fast near planets" complaint.
  const t = (sd - r * 3) / (r * 21);
  const s = THREE.MathUtils.smoothstep(t, 0, 1);
  const eased = s * s * s * s * s; // quintic concave-up
  return FLIGHT_REGIMES.PLANETARY.speedMul
       + (FLIGHT_REGIMES.INTERPLANETARY.speedMul - FLIGHT_REGIMES.PLANETARY.speedMul)
         * eased;
}

// Atmospheric entry flight state (SPACE / ENTRY / ATMOSPHERIC) plus
// smoothed presentation factors. Raw state is recomputed per frame in
// the fly section of animate(); the *Live values exponentially chase
// the targets so descent reads as a continuous transition, not steps.
const atmoEntry = createAtmosphericEntryState();
let atmoCraftScaleLive = 1;
let atmoSkyOcclusionLive = 0;

// Local-horizon look state for atmospheric flight. Mouse / rotation
// input accumulates here as pending yaw (about the local zenith) and
// pitch (against the local horizon) instead of the world-frame euler,
// which reads as roll near a planet. Consumed each frame by
// applyAtmosphericHorizonLook().
const atmoLookPending = { yaw: 0, pitch: 0 };
const _horizonZenith = new THREE.Vector3();
const _horizonFwd = new THREE.Vector3();
const _horizonFwdH = new THREE.Vector3();
const _horizonLookTarget = new THREE.Vector3();
const _horizonBodyPos = new THREE.Vector3();
const _horizonMatrix = new THREE.Matrix4();
const _horizonQuat = new THREE.Quaternion();

// Keeps the chase slot above the local terrain. The slot hangs below
// and ahead of the camera (followY is negative by design), so at
// minimum flight altitude the SHIP would bury into the surface while
// the camera floor only protects the camera. Pushes the ship radially
// out to a half-hull clearance above the displaced surface — the
// standard flight-sim ground-proximity guard.
const _shipClampBody = new THREE.Vector3();
const _shipClampOut = new THREE.Vector3();

function getActiveCraftBoundRadius() {
  if (!playerShip) return 0.02;
  const ud = playerShip.userData;
  if (!ud.atmoBoundRadius) {
    // Opaque hull meshes only — glow sprites and transparent beam
    // cones can dwarf the hull, and an inflated radius makes the
    // terrain clamp hoist the ship above the pilot.
    const box = new THREE.Box3();
    const tmp = new THREE.Box3();
    playerShip.updateWorldMatrix(true, true);
    playerShip.traverse((o) => {
      if (!o.isMesh || !o.geometry || o.isSprite) return;
      const mat = Array.isArray(o.material) ? o.material[0] : o.material;
      if (mat && mat.transparent) return;
      if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
      tmp.copy(o.geometry.boundingBox).applyMatrix4(o.matrixWorld);
      box.union(tmp);
    });
    const size = new THREE.Vector3();
    if (!box.isEmpty()) box.getSize(size);
    ud.atmoBoundRadius = THREE.MathUtils.clamp(
      (Math.max(size.x, size.y, size.z) * 0.5 / (ud.atmoScaled ? atmoCraftScaleLive : 1)) || 0.02, 0.01, 0.08
    );
  }
  return ud.atmoBoundRadius * atmoCraftScaleLive;
}

function clampShipAboveTerrain() {
  if (!playerShip || !atmoEntry.body || atmoEntry.state === FLIGHT_STATES.SPACE) return;
  atmoEntry.body.getWorldPosition(_shipClampBody);
  _shipClampOut.copy(playerShip.position).sub(_shipClampBody);
  const dist = _shipClampOut.length();
  if (dist < 1e-6) return;
  _shipClampOut.divideScalar(dist);
  const minR = getBodyVisualSurfaceRadius(atmoEntry.body, _shipClampOut)
    + getActiveCraftBoundRadius() * 0.55 + 0.002 * atmoCraftScaleLive;
  if (dist < minR) {
    const excess = minR - dist;
    playerShip.position.copy(_shipClampBody).addScaledVector(_shipClampOut, minR);
    // Terrain-following lift: the slot samples terrain AHEAD of the
    // camera, so a rising ridge would otherwise hoist the ship above
    // the pilot's eye line. Carry the camera up with a share of the
    // excess each frame — the rig climbs the terrain as one unit,
    // eased so a ridge reads as a climb rather than a jolt.
    camera.position.addScaledVector(_shipClampOut, excess * 0.35);
  }
}

// Rebuilds the camera orientation in the local-horizon frame: yaw
// rotates the heading about the zenith, pitch tilts it against the
// horizon plane, and "up" stays zenith-aligned so the horizon never
// rolls. Frame-transported from the camera's current forward, so
// entering the atmosphere causes no snap — only the roll levels out
// over a short blend.
function applyAtmosphericHorizonLook(delta) {
  if (!atmoEntry.body) return;
  atmoEntry.body.getWorldPosition(_horizonBodyPos);
  _horizonZenith.copy(camera.position).sub(_horizonBodyPos).normalize();

  // INPUT APPLIES 1:1 — never through the smoothing slerp (routing
  // input through the blend ate ~95% of every mouse event).
  // Yaw rotates the whole orientation about the local zenith.
  if (atmoLookPending.yaw !== 0) {
    _horizonQuat.setFromAxisAngle(_horizonZenith, atmoLookPending.yaw);
    camera.quaternion.premultiply(_horizonQuat);
    atmoLookPending.yaw = 0;
  }
  // Pitch rotates about the camera's right axis, clamped short of
  // the zenith singularity so the horizon frame never degenerates.
  if (atmoLookPending.pitch !== 0) {
    _horizonFwd.set(0, 0, -1).applyQuaternion(camera.quaternion);
    const cur = Math.asin(THREE.MathUtils.clamp(_horizonFwd.dot(_horizonZenith), -1, 1));
    const next = THREE.MathUtils.clamp(cur + atmoLookPending.pitch, -1.45, 1.45);
    const dPitch = next - cur;
    atmoLookPending.pitch = 0;
    if (dPitch !== 0) {
      _horizonFwdH.set(1, 0, 0).applyQuaternion(camera.quaternion); // camera right
      _horizonQuat.setFromAxisAngle(_horizonFwdH, dPitch);
      camera.quaternion.premultiply(_horizonQuat);
    }
  }

  // ROLL-LEVELING is the only smoothed part: rebuild the SAME
  // forward with up = zenith and ease toward it, so entering the
  // atmosphere levels the horizon over ~⅙ s instead of snapping.
  _horizonFwd.set(0, 0, -1).applyQuaternion(camera.quaternion);
  _horizonFwdH.copy(_horizonFwd).addScaledVector(_horizonZenith, -_horizonFwd.dot(_horizonZenith));
  if (_horizonFwdH.lengthSq() < 1e-8) return; // staring at zenith/nadir; skip leveling
  _horizonLookTarget.copy(camera.position).add(_horizonFwd);
  _horizonMatrix.lookAt(camera.position, _horizonLookTarget, _horizonZenith);
  _horizonQuat.setFromRotationMatrix(_horizonMatrix);
  const blend = 1 - Math.exp(-delta * 6.0);
  camera.quaternion.slerp(_horizonQuat, blend);

  // Keep the world-frame euler pipeline coherent with what we just
  // did so exiting the atmosphere hands control back continuously.
  flyEuler.setFromQuaternion(camera.quaternion);
  flyTargetEuler.copy(flyEuler);
}

// Planet-frame carry. A camera hovering in a planet's atmosphere is
// world-fixed while the planet keeps moving along its orbit — without
// this, the body slides out from under a parked ship at ~0.04 u/s and
// the pilot gets silently "ejected" into space. While the flight
// state is inside the envelope, the camera rides the planet's
// reference frame by inheriting its per-frame world displacement.
// This is deliberately the first piece of the local-frame
// architecture the streamed-terrain phase needs.
const _atmoCarryNow = new THREE.Vector3();
const _atmoCarryPrev = new THREE.Vector3();
const _atmoSpinNow = new THREE.Quaternion();
const _atmoSpinPrev = new THREE.Quaternion();
const _atmoSpinDelta = new THREE.Quaternion();
const _atmoCarryRel = new THREE.Vector3();
let _atmoCarryBody = null;
let _atmoCarrySpinMesh = null;

// The spinning surface mesh of a body (the planet group itself never
// rotates; the daily spin lives on the tilt group's first child).
function getBodySpinMesh(body) {
  if (!body) return null;
  if (body.userData._atmoSpinMesh === undefined) {
    const tilt = body.getObjectByName('tiltGroup');
    body.userData._atmoSpinMesh = (tilt && tilt.children[0]) || null;
  }
  return body.userData._atmoSpinMesh;
}

// How much of the nearest body's orbital motion the ship rides along with:
// nothing beyond three radii out, all of it inside one and a half. In
// artistic mode Earth covers about 0.012 units a frame along its orbit,
// more than the capped ship can fly near the surface, so a ship that is not
// carried is run over by the planet or left behind by it, and at the edge
// of the atmosphere it used to flip between the two every frame.
function flyBodyCarryWeight(nearest) {
  if (!nearest || !nearest.body) return 0;
  return 1 - THREE.MathUtils.smoothstep(nearest.dist / Math.max(nearest.radius, 1e-9), 1.5, 3);
}
const _atmoSpinIdentity = new THREE.Quaternion();

function updateAtmosphericEntry(delta) {
  if (flyMode && camera) {
    const nearest = findNearestBodySurface();
    computeAtmosphericEntry(atmoEntry, nearest, camera.position);
    // The parked co-orbit in updateFlyMovement carries the ship itself.
    const carryBody = nearest && !flyParkedBody ? nearest.body : null;
    const carryW = carryBody ? flyBodyCarryWeight(nearest) : 0;
    // Surface co-rotation only once well inside the air, ramped from
    // nothing at the envelope's edge: the artistic spin is fast, and a
    // full carry switched on at the boundary yanked the view sideways.
    const spinW = carryBody && atmoEntry.body === carryBody
      ? THREE.MathUtils.smoothstep(atmoEntry.immersion, 0, 0.5) : 0;
    if (carryBody && carryW > 0) {
      carryBody.getWorldPosition(_atmoCarryNow);
      const spinMesh = getBodySpinMesh(carryBody);
      if (spinMesh) spinMesh.getWorldQuaternion(_atmoSpinNow);
      if (_atmoCarryBody === carryBody) {
        // Orbital translation: ride the planet along its orbit.
        camera.position.x += (_atmoCarryNow.x - _atmoCarryPrev.x) * carryW;
        camera.position.y += (_atmoCarryNow.y - _atmoCarryPrev.y) * carryW;
        camera.position.z += (_atmoCarryNow.z - _atmoCarryPrev.z) * carryW;
        // Surface co-rotation: the artistic spin sweeps terrain under
        // a hovering ship at ~1.5 u/s, which strobed the terrain
        // clamps into constant jitter once flight speeds became sane.
        // Rotating the camera (position, orientation, AND velocity)
        // with the surface puts the pilot in the rotating frame every
        // flight sim uses — the ground only moves if you fly.
        if (spinMesh && _atmoCarrySpinMesh === spinMesh && spinW > 0) {
          _atmoSpinDelta.copy(_atmoSpinPrev).invert().premultiply(_atmoSpinNow);
          if (spinW < 1) _atmoSpinDelta.copy(_atmoSpinIdentity.identity().slerp(_atmoSpinDelta, spinW));
          _atmoCarryRel.copy(camera.position).sub(_atmoCarryNow);
          _atmoCarryRel.applyQuaternion(_atmoSpinDelta);
          camera.position.copy(_atmoCarryNow).add(_atmoCarryRel);
          camera.quaternion.premultiply(_atmoSpinDelta);
          flyVelocity.applyQuaternion(_atmoSpinDelta);
          flyEuler.setFromQuaternion(camera.quaternion);
          flyTargetEuler.copy(flyEuler);
        }
      }
      _atmoCarryBody = carryBody;
      _atmoCarryPrev.copy(_atmoCarryNow);
      _atmoCarrySpinMesh = spinMesh;
      if (spinMesh) _atmoSpinPrev.copy(_atmoSpinNow);
    } else {
      _atmoCarryBody = null;
      _atmoCarrySpinMesh = null;
    }
  } else if (atmoEntry.state !== FLIGHT_STATES.SPACE) {
    atmoEntry.state = FLIGHT_STATES.SPACE;
    atmoEntry.immersion = 0;
    atmoEntry.dayLight = 0;
    atmoEntry.skyOcclusion = 0;
    atmoEntry.craftScale = 1;
    atmoEntry.body = null;
    _atmoCarryBody = null;
  }
  // Exponential chase so descent reads as one continuous transition.
  const k = 1 - Math.exp(-3.5 * delta);
  atmoCraftScaleLive += (atmoEntry.craftScale - atmoCraftScaleLive) * k;
  atmoSkyOcclusionLive += (atmoEntry.skyOcclusion - atmoSkyOcclusionLive) * k;
}

// Find the body whose surface the ship is currently closest to.
// Returns { dist, radius, pos } or null if no bodies in scene.
// Frame-cached: speed cap, acceleration, and the atmospheric entry
// state all sample it each frame, and each probe walks every body.
let _nearestBodyCacheFrame = -1;
let _nearestBodyCache = null;

function findNearestBodySurface() {
  if (_nearestBodyCacheFrame === frameCount) return _nearestBodyCache;
  _nearestBodyCache = findNearestBodySurfaceUncached();
  _nearestBodyCacheFrame = frameCount;
  return _nearestBodyCache;
}

// Bodies the flight model sees (approach cap, collision bubble, nearest
// surface, target lock): the Sun and planets at home, the active remote
// system's stars and planets anywhere else.
function forEachFlightBody(fn) {
  if (activeSystemId === 'sol' || !remoteSystems[activeSystemId]) {
    if (sun) fn(sun);
    for (const name in planets) fn(planets[name]);
    return;
  }
  const handle = remoteSystems[activeSystemId];
  for (const id in handle.bodies) {
    const body = handle.bodies[id];
    if (body) fn(body);
  }
}

function findNearestBodySurfaceUncached() {
  if (!camera) return null;
  let nearestSurfaceDist = Infinity;
  let nearestRadius = 1;
  let nearestBodyPos = null;
  let nearestBody = null;
  let nearestSurfaceRadius = 1;
  let nearestAtmosphereTop = 1;
  const probe = (body) => {
    if (!body) return;
    body.getWorldPosition(_speedCapProbe);
    _speedCapToBody.copy(camera.position).sub(_speedCapProbe);
    const centerDist = _speedCapToBody.length();
    const outward = centerDist > 1e-6 ? _speedCapToBody.divideScalar(centerDist) : null;
    const radius = getBodyBaseRadius(body);
    const surfaceRadius = getBodyVisualSurfaceRadius(body, outward);
    const atmosphereTop = getBodyAtmosphereTopRadius(body, surfaceRadius);
    const sd = centerDist - surfaceRadius;
    if (sd < nearestSurfaceDist) {
      nearestSurfaceDist = sd;
      nearestRadius = radius;
      nearestSurfaceRadius = surfaceRadius;
      nearestAtmosphereTop = atmosphereTop;
      nearestBody = body;
      if (!nearestBodyPos) nearestBodyPos = new THREE.Vector3();
      nearestBodyPos.copy(_speedCapProbe);
    }
  };
  forEachFlightBody(probe);
  if (!isFinite(nearestSurfaceDist)) return null;
  const atmosphereDepth = Math.max(0, nearestAtmosphereTop - nearestSurfaceRadius);
  return {
    dist: nearestSurfaceDist,
    radius: nearestRadius,
    surfaceRadius: nearestSurfaceRadius,
    atmosphereTop: nearestAtmosphereTop,
    atmosphereDepth,
    hasAtmosphere: !!(nearestBody && nearestBody.userData && nearestBody.userData.hasAtmosphere),
    body: nearestBody,
    pos: nearestBodyPos
  };
}

// Contextual speed cap. Combines static regime factor (distance-based)
// with a time-to-surface auto-brake (velocity-aware) and the user's
// throttle range preset. Whichever is tighter wins, so you can still
// cruise at warp in deep space but auto-decelerate cleanly when
// heading at a planet, and the user can manually clamp the absolute
// ceiling further via the Range dropdown.
const TIME_TO_SURFACE_SAFETY = 6; // seconds we want as buffer before impact
// Bodies whose predicted closest approach lies beyond this many radii
// are not in the ship's path and do not slow it. The locked target
// always does: the pilot means to arrive there.
const FLY_APPROACH_MISS_RADII = 4;
// At the surface the allowed closing speed bottoms out here (× body
// radius, per tick): about eight seconds per radius, matching NEAR.
const FLY_APPROACH_FLOOR_PER_RADIUS = 0.002;
const _approachBodyPos = new THREE.Vector3();
const _approachToBody = new THREE.Vector3();

// Speed cap (per tick) imposed by the bodies the ship is closing on,
// or Infinity when nothing is in its path. For each body the allowed
// closing speed is surfaceDist / safetyTime (real seconds, hence ÷60
// to per tick), floored near the surface. The cap on the ship's total
// speed is scaled by speed / closing, so a body off to the side only
// limits the component heading at it. The result is the Elite-style
// approach: speed falls in step with distance and the ETA holds at
// about the safety time until arrival, whatever the drive.
function computeApproachSpeedCap(isWarpDrive) {
  const speed = flyVelocity.length();
  if (speed <= FLY_REST_SPEED) return Infinity;
  const safetyTime = isWarpDrive ? TIME_TO_SURFACE_SAFETY * 2 : TIME_TO_SURFACE_SAFETY;
  let cap = Infinity;
  let targetSeen = false;
  const consider = (body, isTarget) => {
    if (!body) return;
    if (isTarget) targetSeen = true;
    body.getWorldPosition(_approachBodyPos);
    _approachToBody.copy(_approachBodyPos).sub(camera.position);
    const centreDist = _approachToBody.length();
    if (centreDist < 1e-6) return;
    const closing = flyVelocity.dot(_approachToBody) / centreDist;
    if (closing <= 0) return;
    const radius = getBodyBaseRadius(body);
    if (!isTarget) {
      // Impact parameter: the miss distance at closest approach on the
      // current heading. Bodies the ship will pass wide of don't brake it.
      const cosA = Math.min(1, closing / speed);
      const missSq = centreDist * centreDist * (1 - cosA * cosA);
      const corridor = FLY_APPROACH_MISS_RADII * radius;
      if (missSq > corridor * corridor) return;
    }
    const surfaceDist = Math.max(0, centreDist - radius);
    const allowedClosing = Math.max(surfaceDist / (safetyTime * 60), radius * FLY_APPROACH_FLOOR_PER_RADIUS);
    if (closing > allowedClosing) {
      cap = Math.min(cap, allowedClosing * speed / closing);
    }
  };
  forEachFlightBody((body) => consider(body, body === flyTarget));
  if (flyTarget && !targetSeen) consider(flyTarget, true);
  return cap;
}

function flyFullStop() {
  flyVelocity.set(0, 0, 0);
  flyAngularVelocity.set(0, 0);
  thrustBuildup = 0;
  FLY_SMOOTHED_THRUST.set(0, 0, 0);
  _brakeLatch = false;
  _faArmed = false;
}

function engageBrakeLatch() {
  if (flyVelocity.lengthSq() <= FLY_REST_SPEED * FLY_REST_SPEED) return;
  _brakeLatch = true;
  _brakeDecelPerSec = 0;
}

function setFlightAssist(on) {
  flightAssist = !!on;
  if (!flightAssist) _faArmed = false;
  const el = document.getElementById('hud-fa');
  if (el) {
    el.textContent = flightAssist ? 'on' : 'off';
    el.classList.toggle('is-off', !flightAssist);
  }
  const chip = document.getElementById('fh-fa');
  if (chip) chip.classList.toggle('on', flightAssist);
}

// ---- Target lock -------------------------------------------------------
const _targetPickPos = new THREE.Vector3();
const _targetPickDir = new THREE.Vector3();
const _targetHudPos = new THREE.Vector3();
const _targetHudNdc = new THREE.Vector3();

function forEachFlyTargetCandidate(fn) {
  forEachFlightBody(fn);
  if (activeSystemId !== 'sol') return;
  Object.values(moons).forEach((list) => {
    if (Array.isArray(list)) list.forEach((moon) => fn(moon));
  });
}

function flyTargetName(body) {
  const name = body && body.userData && body.userData.name;
  return String(name || body?.name || 'Target');
}

// T: lock the body nearest the crosshair (inside a cone), else the
// nearest body by surface distance; T on the locked body unlocks.
function toggleFlyTarget() {
  if (!flyMode || !camera) return;
  camera.getWorldDirection(_targetPickDir);
  let bestInCone = null;
  let bestCos = Math.cos(THREE.MathUtils.degToRad(FLY_TARGET_LOCK_CONE_DEG));
  let nearest = null;
  let nearestDist = Infinity;
  forEachFlyTargetCandidate((body) => {
    body.getWorldPosition(_targetPickPos);
    _targetPickPos.sub(camera.position);
    const dist = _targetPickPos.length();
    if (dist < 1e-6) return;
    const cos = _targetPickPos.dot(_targetPickDir) / dist;
    if (cos > bestCos) { bestCos = cos; bestInCone = body; }
    const surface = dist - getBodyBaseRadius(body);
    if (surface < nearestDist) { nearestDist = surface; nearest = body; }
  });
  const pick = bestInCone || nearest;
  setFlyTarget(pick === flyTarget ? null : pick);
}

// Fly-mode click picking: one click locks the body as the nav target,
// a second click on the same body within DOUBLE_CLICK_MS engages the
// autopilot to it. Works for Sol and for any remote system, since both
// go through the shared clickable cache.
const FLY_PICK_DOUBLE_CLICK_MS = 450;
let _flyPickLast = { body: null, at: 0 };
function flyPickBody(event) {
  if (!camera || !raycaster) return;
  mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
  mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
  raycaster.setFromCamera(mouse, camera);
  const body = pickCelestialAtMouse(getClickableObjects());
  if (!body || !body.userData || body instanceof THREE.Sprite) return;
  const name = body.userData.name || body.name || 'Target';
  const now = performance.now();
  if (_flyPickLast.body === body && now - _flyPickLast.at < FLY_PICK_DOUBLE_CLICK_MS) {
    _flyPickLast = { body: null, at: 0 };
    const key = body.userData.bodyId && activeSystemId !== 'sol'
      ? `remote:${body.userData.bodyId}`
      : String(name).toLowerCase();
    startAutopilotCruise(body, { key, label: name, isStar: !!(body.userData.isStar || body.userData.isSun) });
    return;
  }
  _flyPickLast = { body, at: now };
  setFlyTarget(body);
  showStarMapToast(`Target: ${name} · double-click to autopilot`);
}

function setFlyTarget(body) {
  flyTarget = body || null;
  updateFlyTargetHud(true);
}

function formatFlyEta(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '--';
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

function ensureFlyTargetMarker() {
  if (flyTargetMarkerEl) return flyTargetMarkerEl;
  // Own container rather than #planet-hud-container: that one is part
  // of the optional alien HUD overlay (off by default), and the target
  // bracket is a navigation aid that has to show regardless.
  const overlay = document.getElementById('fly-controls');
  if (!overlay) return null;
  let container = document.getElementById('nav-target-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'nav-target-container';
    overlay.appendChild(container);
  }
  const el = document.createElement('div');
  el.className = 'planet-hud-marker fly-target-marker';
  el.innerHTML = '<div class="planet-hud-bracket"><span class="corner-bl"></span><span class="corner-br"></span></div>'
    + '<div class="planet-hud-label"><span class="planet-hud-line"></span>'
    + '<span class="planet-hud-name"></span><span class="planet-hud-distance"></span></div>';
  el.style.display = 'none';
  container.appendChild(el);
  flyTargetMarkerEl = el;
  return el;
}

// TGT readout (throttled with the rest of the HUD text) and the
// on-screen bracket (every frame, so it tracks the body smoothly).
// Off-screen targets pin to the viewport edge along the direction
// from screen centre, in the smaller bracket the CSS already defines.
let _flyTargetTextLast = 0;
function updateFlyTargetHud(force = false) {
  if (!hudTargetEl) hudTargetEl = document.getElementById('hud-target');
  const marker = ensureFlyTargetMarker();
  const plate = navPlateEls();
  if (!flyMode || !flyTarget || !camera) {
    if (hudTargetEl && (force || hudTargetEl.dataset.state !== 'none')) {
      hudTargetEl.textContent = 'No lock';
      hudTargetEl.dataset.state = 'none';
      if (plate.targetSub) plate.targetSub.textContent = 'T to lock';
      if (plate.range) plate.range.textContent = '—';
      if (plate.eta) plate.eta.textContent = '—';
    }
    if (marker && marker.style.display !== 'none') marker.style.display = 'none';
    return;
  }
  flyTarget.getWorldPosition(_targetHudPos);
  _targetHudNdc.copy(_targetHudPos).sub(camera.position);
  const centreDist = _targetHudNdc.length();
  const radius = getBodyBaseRadius(flyTarget);
  const surfaceDist = Math.max(0, centreDist - radius);
  const closing = centreDist > 1e-6 ? flyVelocity.dot(_targetHudNdc) / centreDist : 0;
  const eta = closing > FLY_REST_SPEED ? surfaceDist / (closing * 60) : Infinity;

  const now = performance.now();
  const textDue = force || now - _flyTargetTextLast > 80;
  if (textDue) _flyTargetTextLast = now;
  if (hudTargetEl && textDue) {
    const motion = closing > FLY_REST_SPEED
      ? `ETA ${formatFlyEta(eta)}`
      : (closing < -FLY_REST_SPEED ? 'Opening' : 'Holding');
    hudTargetEl.textContent = (flyTarget.userData && flyTarget.userData.name) || flyTargetName(flyTarget);
    hudTargetEl.dataset.state = 'lock';
    if (plate.targetSub) plate.targetSub.textContent = (flyTarget.userData && flyTarget.userData.type) || 'Body';
    if (plate.range) plate.range.textContent = formatSceneDistance(surfaceDist).replace(' model', '');
    if (plate.eta) {
      plate.eta.textContent = motion;
      plate.eta.classList.toggle('warn', closing > FLY_REST_SPEED && eta < 3);
    }
  }

  if (!marker) return;
  _targetHudNdc.copy(_targetHudPos).project(camera);
  const w = window.innerWidth;
  const h = window.innerHeight;
  const behind = _targetHudNdc.z > 1;
  let nx = behind ? -_targetHudNdc.x : _targetHudNdc.x;
  let ny = behind ? -_targetHudNdc.y : _targetHudNdc.y;
  const offScreen = behind || Math.abs(nx) > 0.96 || Math.abs(ny) > 0.94;
  if (offScreen) {
    // Pin to the edge along the centre → target direction.
    const len = Math.max(Math.abs(nx) / 0.96, Math.abs(ny) / 0.94, 1e-6);
    nx /= len;
    ny /= len;
    if (behind && Math.abs(nx) < 0.96 && Math.abs(ny) < 0.94) {
      // Directly behind: park at the bottom edge.
      ny = -0.94;
    }
  }
  const px = (nx * 0.5 + 0.5) * w;
  const py = (1 - (ny * 0.5 + 0.5)) * h;
  marker.style.display = 'block';
  marker.style.left = `${px.toFixed(1)}px`;
  marker.style.top = `${py.toFixed(1)}px`;
  marker.classList.toggle('off-screen', offScreen);
  const bracket = marker.firstElementChild;
  if (bracket) {
    let size = 60;
    if (!offScreen) {
      // Size the bracket to the body's projected disc, floored so a
      // distant target still reads as a marker.
      const angular = Math.atan2(radius, Math.max(centreDist, 1e-6));
      const fovY = THREE.MathUtils.degToRad(camera.fov);
      const projected = (angular / (fovY * 0.5)) * h;
      size = THREE.MathUtils.clamp(projected + 18, 44, Math.max(w, h) * 1.5);
    }
    bracket.style.width = `${size.toFixed(0)}px`;
    bracket.style.height = `${size.toFixed(0)}px`;
  }
  if (textDue) {
    const nameEl = marker.querySelector('.planet-hud-name');
    const distEl = marker.querySelector('.planet-hud-distance');
    if (nameEl) nameEl.textContent = flyTargetName(flyTarget);
    if (distEl) distEl.textContent = formatSceneDistance(surfaceDist);
  }
}

// ---- Interstellar navigation (src/nav) -------------------------------------
// Jump range by drive, in light-years: what one hyperspace leg can cover.
const JUMP_RANGE_BY_DRIVE = { warp: 28, fusion: 16, impulse: 16, antigravity: 16, nuclear: 11, drift: 11, chemical: 8, ion: 8 };
function getJumpRangeLy() {
  return JUMP_RANGE_BY_DRIVE[currentPropulsion] || 12;
}

// Register and build a generated system so jumpToSystem can land there.
function registerProceduralSystem(data) {
  if (!scene || !data) return null;
  if (remoteSystems[data.id]) return remoteSystems[data.id];
  registerSystem(data);
  return mountRemoteSystem(data);
}

function ensureNavigation() {
  if (navigation) return Promise.resolve(navigation);
  if (!navigationLoading) {
    navigationLoading = import('./nav/index.js')
      .then((mod) => mod.createNavigation({
        listSystems,
        getCurrentSystemId: () => activeSystemId,
        jumpToSystem,
        registerProceduralSystem,
        isJumpInFlight: () => starMapJumpInFlight || wormholeTunnelInFlight,
        getJumpRangeLy,
        toast: showStarMapToast,
        onRouteChange: () => updateFlyNavPlate()
      }))
      .then((nav) => { navigation = nav; return nav; })
      .catch((err) => {
        navigationLoading = null;
        console.error('[nav] failed to load', err);
        showStarMapToast('Galaxy map failed to load');
        return null;
      });
  }
  return navigationLoading;
}

function toggleGalaxyMap(forceOpen = false) {
  ensureNavigation().then((nav) => {
    if (!nav) return;
    if (forceOpen) nav.openMap();
    else nav.toggleMap();
  });
}

function engageNextJump() {
  if (!navigation || !navigation.getStatus().route) {
    showStarMapToast('No route plotted. Open the galaxy map (M)');
    return false;
  }
  if (!navigation.canEngage()) {
    showStarMapToast('Jump in progress');
    return false;
  }
  return navigation.engageNext();
}

// Navigation plate (top centre): system, drive, regime, and the route.
// The target cells are written by updateFlyTargetHud.
let _navPlateElsCache = null;
const _navPlateStar = new THREE.Vector3();
let _navPlateLast = 0;
function navPlateEls() {
  if (_navPlateElsCache) return _navPlateElsCache;
  const byId = (id) => document.getElementById(id);
  _navPlateElsCache = {
    system: byId('fh-system'), systemSub: byId('fh-system-sub'), targetSub: byId('fh-target-sub'),
    range: byId('fh-range'), eta: byId('fh-eta'), route: byId('fh-route'), routeSub: byId('fh-route-sub'),
    routeCell: byId('fh-route-cell'), drive: byId('fh-drive'), regime: byId('fh-regime')
  };
  return _navPlateElsCache;
}

function flightRegimeLabel(nearest) {
  if (!nearest || !isFinite(nearest.dist)) return 'Deep space';
  if (nearest.hasAtmosphere && nearest.atmosphereDepth > 0 && nearest.dist < nearest.atmosphereDepth) return 'Atmosphere';
  const r = nearest.dist / Math.max(nearest.radius, 1e-6);
  if (r < 0.45) return 'Surface';
  if (r < 3) return 'Approach';
  if (r < 24) return 'Planetary';
  return 'Interplanetary';
}

function updateFlyNavPlate() {
  const els = navPlateEls();
  if (!els.system || !camera) return;
  const sys = getSystem(activeSystemId);
  els.system.textContent = sys ? sys.name : activeSystemId;
  let starPos = null;
  if (activeSystemId === 'sol' && sun) {
    starPos = sun.getWorldPosition(_navPlateStar);
  } else if (activeSystemTarget && activeSystemTarget.data) {
    const primary = activeSystemTarget.data.stars.find((st) => st.isPrimary) || activeSystemTarget.data.stars[0];
    const obj = primary && activeSystemTarget.bodies[primary.id];
    if (obj) starPos = obj.getWorldPosition(_navPlateStar);
  }
  if (els.systemSub) {
    els.systemSub.textContent = starPos
      ? `${formatSceneDistance(camera.position.distanceTo(starPos))} from ${activeSystemId === 'sol' ? 'the Sun' : 'the star'}`
      : '—';
  }
  const prop = propulsionTypes[currentPropulsion];
  if (els.drive) els.drive.textContent = `${prop ? prop.name : currentPropulsion} · ${getJumpRangeLy()} ly`;
  if (els.regime) els.regime.textContent = flightRegimeLabel(findNearestBodySurface());
  const status = navigation ? navigation.getStatus() : null;
  if (status && status.next) {
    els.route.textContent = status.next.star.name;
    els.routeSub.textContent = `Jump ${status.next.index + 1} of ${status.next.total} · ${status.next.dist.toFixed(1)} ly · J to engage`;
    els.routeCell.classList.add('active');
  } else {
    els.route.textContent = 'None';
    els.routeSub.textContent = 'M for the galaxy map';
    els.routeCell.classList.remove('active');
  }
}

function computeContextualSpeedCap(propulsionMax) {
  if (!camera || !propulsionMax) return propulsionMax || 1;
  const nearest = findNearestBodySurface();
  let cap = propulsionMax;
  // Warp drive bends spacetime — the static regime cap (which keeps
  // sub-light drives slow near bodies so they don't ram things) was
  // also clamping warp to ~warp 5 anywhere inside the Sun's 24-radius
  // PLANETARY zone (the entire inner solar system). The time-to-
  // surface cap still applies so warp can't tunnel straight into a
  // planet; the sweep-sphere guard in enforceCollisionAvoidance is
  // the hard backstop. The pilot's throttle range preset also still
  // bites if they've dialed it in.
  const isWarpDrive = currentPropulsion === 'warp';

  if (nearest) {
    // 1. Static regime cap. Skipped for warp so the inner system
    // doesn't choke FTL travel — the only reason to be at warp inside
    // a planet's PLANETARY zone is to get out of it.
    if (!isWarpDrive) {
      const factor = computeRegimeFactor(nearest.dist, nearest.radius, nearest);
      cap = propulsionMax * factor;
    }

    // 2. Approach cap: the ship never closes on a body faster than it
    // can stop in TIME_TO_SURFACE_SAFETY seconds. This used to compare
    // a per-tick velocity against a per-second distance, 60× too loose,
    // so it was inert: a ship aimed at a planet arrived at cruise speed
    // and blew straight past. It is now computed against every body in
    // the ship's path plus the locked target (computeApproachSpeedCap).
    // Inside an atmosphere the air is the brake, so this stays out of
    // that tuned regime.
    const inAir = nearest.hasAtmosphere && nearest.atmosphereDepth > 0 && nearest.dist < nearest.atmosphereDepth;
    if (!inAir) {
      cap = Math.min(cap, computeApproachSpeedCap(isWarpDrive));
    }
  }

  // 3. User-selected throttle range. Absolute max in scene units/sec.
  // Auto = Infinity = no extra clamp. Precision / Approach / Cruise
  // impose hard ceilings the pilot can dial in for fine control.
  // Autopilot bypasses this whole function via direct camera writes
  // so fast travel is still available.
  const rangeSpec = SPEED_RANGES[currentSpeedRange] || SPEED_RANGES.auto;
  if (Number.isFinite(rangeSpec.max)) {
    cap = Math.min(cap, rangeSpec.max);
  }

  // Inside Earth's air the craft and its chase rig are at metre scale, so
  // the cap is a real speed, set from the height above the ground: about
  // seventeen seconds to the surface at any height, floored at 60 m/s.
  // It used to be the regime curve times the craft scale, a double
  // reduction that left 336 m/s at 100 km and a ten-minute descent. The
  // envelope top is 0.6 radii up, so the two caps are blended over the
  // first third of the way down. Warp keeps its escape speed.
  if (nearest?.body?.name === 'earth' && !isWarpDrive && nearest.hasAtmosphere && nearest.dist < nearest.atmosphereDepth) {
    const unitPerMetre = nearest.radius / EARTH_METRES;
    const altitudeM = Math.max(0, nearest.dist / unitPerMetre);
    const drive = THREE.MathUtils.clamp(propulsionMax / 40, 0.3, 1);
    const airCap = (60 + altitudeM * 0.06) * drive * unitPerMetre / 60;
    const immersion = 1 - nearest.dist / nearest.atmosphereDepth;
    cap = THREE.MathUtils.lerp(cap, Math.min(cap, airCap), THREE.MathUtils.smoothstep(immersion, 0, 0.35));
  }
  return cap;
}

// Acceleration multiplier mirrored from the regime factor, but on a
// gentler curve (sqrt) so the ship still feels responsive on a tight
// throttle — at NEAR-zone the max speed drops to 1.2% but accel only
// drops to ~11%, which means the ship reaches that low cap quickly
// and the user feels precise control rather than sluggish drift.
function computeContextualAcceleration(propulsionAccel) {
  if (!camera || !propulsionAccel) return propulsionAccel || 0;
  // Warp bypasses the regime-based acceleration drop for the same
  // reason it bypasses the speed cap: the drive is the user's only
  // way out of a planet's well, so throttling its acceleration there
  // turned departures into a long crawl up the speed curve.
  if (currentPropulsion === 'warp') return propulsionAccel;
  const nearest = findNearestBodySurface();
  if (!nearest) return propulsionAccel;
  const factor = computeRegimeFactor(nearest.dist, nearest.radius, nearest);
  // sqrt softens the drop — accel doesn't fall as steeply as max speed.
  const atmosphericDensity = computeAtmosphericDensity(nearest);
  return propulsionAccel * Math.sqrt(factor) * (1 + atmosphericDensity * 0.45);
}

// Dynamic near-plane for fly mode. Standard perspective projection
// gives the most depth precision near the near plane and degrades
// hyperbolically with distance, so for "approach Earth, see the
// ground" to work without z-fighting on close shells (clouds,
// atmosphere, surface, displacement), the near plane has to shrink
// when the camera approaches a body. Computed from the nearest body
// SURFACE distance (centre distance minus body radius), clamped to a
// safe range, and applied with a small dead-zone so it doesn't churn
// the projection matrix on every minor camera move.
const _flyNearProbe = new THREE.Vector3();
let _flyDynamicNearActive = false;
let _flyDynamicNearSavedValue = null;

function updateFlyCameraNear() {
  if (!camera) return;
  if (!flyMode) {
    // Restore the original near plane on fly-mode exit so the
    // solar-system view doesn't keep the close-approach precision
    // (which would z-fight at intersolar distances).
    if (_flyDynamicNearActive && _flyDynamicNearSavedValue != null) {
      camera.near = _flyDynamicNearSavedValue;
      camera.updateProjectionMatrix();
      _flyDynamicNearActive = false;
      _flyDynamicNearSavedValue = null;
    }
    return;
  }
  if (!_flyDynamicNearActive) {
    _flyDynamicNearSavedValue = camera.near;
    _flyDynamicNearActive = true;
  }

  let nearestSurfaceDist = Infinity;
  const probe = (body) => {
    if (!body) return;
    body.getWorldPosition(_flyNearProbe);
    _speedCapToBody.copy(camera.position).sub(_flyNearProbe);
    const centerDist = _speedCapToBody.length();
    const outward = centerDist > 1e-6 ? _speedCapToBody.divideScalar(centerDist) : null;
    const d = centerDist - getBodyVisualSurfaceRadius(body, outward);
    if (d < nearestSurfaceDist) nearestSurfaceDist = d;
  };
  if (sun) probe(sun);
  for (const planetName in planets) probe(planets[planetName]);
  if (!isFinite(nearestSurfaceDist)) return;

  // Map surface-distance to near-plane:
  //   at the surface (dist ~ 0): near = 0.0008 (~3 km in Earth-scale units)
  //   1 radius above: near = 0.05
  //   10+ radii out: near = the saved default (artistic/realistic mode value)
  // Clamp keeps near safely above 0 so the projection doesn't degenerate.
  const baseline = _flyDynamicNearSavedValue || 0.1;
  const fromSurface = Math.max(0, nearestSurfaceDist);
  const closeNear = Math.max(1e-9, Math.min(
    THREE.MathUtils.clamp(0.0008 + fromSurface * 0.05, 0.0008, baseline),
    getCraftFollowDistance() * 0.05
  ));
  // Apply only when the change is meaningful (>5% of current near).
  // updateProjectionMatrix is cheap but it invalidates frustum culling
  // caches so we avoid calling it on every frame.
  if (Math.abs(closeNear - camera.near) / Math.max(camera.near, 1e-6) > 0.05) {
    camera.near = closeNear;
    camera.updateProjectionMatrix();
  }
}

// Rigid co-orbit park. While fly mode is engaged and no user input
// has fired, the camera translates by the parked body's per-frame
// world motion — so the ship sits visually still relative to whatever
// it was focused on. Cleared the moment the user thrusts, rotates, or
// activates autopilot; on release, the body's instantaneous velocity
// is baked into flyVelocity so free flight starts already co-moving.
let flyParkedBody = null;
const _flyParkedPrevPos = new THREE.Vector3();
const _flyParkedDelta = new THREE.Vector3();

function setFlyParkedBody(body) {
  // Only park on bodies with a meaningful world transform (sun,
  // planets, moons, satellites). Things like the wormhole or
  // monolith are also valid since they have getWorldPosition.
  if (!body || typeof body.getWorldPosition !== 'function') {
    flyParkedBody = null;
    return;
  }
  flyParkedBody = body;
  body.getWorldPosition(_flyParkedPrevPos);
}

function clearFlyParkedBody({ inheritVelocity = true, delta = 1 / 60 } = {}) {
  if (!flyParkedBody) return;
  if (inheritVelocity) {
    // Bake the body's instantaneous world velocity into flyVelocity
    // so the user starts co-moving with the planet — no jolt at
    // release.
    const body = flyParkedBody;
    body.getWorldPosition(_flyParkedDelta);
    _flyParkedDelta.sub(_flyParkedPrevPos);
    // Near the body the frame carry (updateAtmosphericEntry) supplies its
    // orbital motion, so only the share the carry leaves out is inherited,
    // or the ship would fly off at the planet's own speed relative to it.
    const nearestAtRelease = findNearestBodySurface();
    const carried = nearestAtRelease && nearestAtRelease.body === body ? flyBodyCarryWeight(nearestAtRelease) : 0;
    if (delta > 1e-6 && carried < 1) {
      _flyParkedDelta.divideScalar(delta * 60);
      flyVelocity.addScaledVector(_flyParkedDelta, 1 - carried);
    }

    // Also add a circular-orbit tangent around the body so the ship
    // doesn't immediately plunge in under the body's gravity the
    // moment the park releases. Without this, body-velocity alone
    // gives the ship Earth's solar orbital velocity but zero
    // velocity RELATIVE to Earth — and Earth's own gravity then
    // pulls it straight down. The tangent magnitude is the standard
    // circular speed sqrt(G·M/r); direction is the camera's
    // forward projected onto the tangent plane so the user feels
    // they continue in whatever direction they were looking. Only
    // applies to bodies with a registered mass; ISS/comets/etc.
    // (no mass) skip this and just inherit body velocity.
    const bodyName = (body.userData && body.userData.name || '').toLowerCase();
    const mass = body === sun
      ? celestialMasses.sun
      : (celestialMasses[bodyName] || 0);
    if (mass > 0) {
      body.getWorldPosition(_autopilotProbePos);
      _autopilotFromBody.copy(camera.position).sub(_autopilotProbePos);
      const dist = _autopilotFromBody.length();
      if (dist > 1e-4) {
        _autopilotFromBody.divideScalar(dist);
        _autopilotHeading.set(0, 0, -1).applyQuaternion(camera.quaternion);
        const radialComp = _autopilotHeading.dot(_autopilotFromBody);
        _autopilotHeading.addScaledVector(_autopilotFromBody, -radialComp);
        if (_autopilotHeading.lengthSq() < 1e-6) {
          _autopilotHeading.set(1, 0, 0).applyQuaternion(camera.quaternion);
          const radial2 = _autopilotHeading.dot(_autopilotFromBody);
          _autopilotHeading.addScaledVector(_autopilotFromBody, -radial2);
          if (_autopilotHeading.lengthSq() < 1e-6) {
            _autopilotHeading.crossVectors(new THREE.Vector3(0, 1, 0), _autopilotFromBody);
          }
        }
        _autopilotHeading.normalize();
        const orbitalSpeed = Math.sqrt((G_CONSTANT * mass) / dist);
        flyVelocity.addScaledVector(_autopilotHeading, orbitalSpeed);
      }
    }
  }
  flyParkedBody = null;
}

function updateAutopilot(delta) {
  if (!autopilot.active) return false;
  if (autopilot.phase === 'cruising') {
    autopilot.cruiseT += delta;
    const tRaw = Math.min(autopilot.cruiseT / autopilot.cruiseDuration, 1.0);

    // Re-resolve the body's world position each frame so we still
    // arrive correctly if the target moved during the cruise.
    if (autopilot.object) {
      autopilot.object.getWorldPosition(autopilot.orbitCenter);
      const sunPos = activeStarWorldPosition(_autopilotProbePos);
      const fromSun = _autopilotHeading.subVectors(autopilot.orbitCenter, sunPos).normalize();
      const lift = new THREE.Vector3(0, 1, 0);
      const sideAxis = new THREE.Vector3().crossVectors(fromSun, lift).normalize();
      autopilot.endPos.copy(autopilot.orbitCenter)
        .addScaledVector(fromSun, autopilot.orbitRadius * 0.9)
        .addScaledVector(lift, autopilot.orbitRadius * 0.18)
        .addScaledVector(sideAxis, autopilot.orbitRadius * 0.12);
    }

    // Velocity-based cruise. Instead of a straight lerp from start to
    // end (which punches through anything in the way) we steer toward
    // endPos with a cubic-eased speed curve and let
    // enforceCollisionAvoidance bend the trajectory around any body's
    // safety bubble — the ship glides past obstacles automatically.
    const totalDist = Math.max(autopilot.startPos.distanceTo(autopilot.endPos), 0.001);
    const nominalSpeed = totalDist / autopilot.cruiseDuration;
    // Cubic in/out derivative gives a soft-start / soft-arrival speed
    // profile peaking near the midpoint, with a 0.25 floor so we
    // never stall and never overshoot wildly past arrival.
    const speedFactor = 6 * tRaw * (1 - tRaw) + 0.25;
    // Decelerate as we enter the orbit zone so the cruise eases to a
    // near-stop AT the orbit radius rather than arriving at ~25% speed and
    // snapping into the sweep — that velocity jump read as an abrupt
    // arrival. smoothstep is 0 at the orbit radius, 1 at 2.6× out.
    const distToBodyNow = camera.position.distanceTo(autopilot.orbitCenter);
    const decelZone = autopilot.orbitRadius * 2.6;
    const arrivalEase = distToBodyNow < decelZone
      ? THREE.MathUtils.smoothstep(distToBodyNow, autopilot.orbitRadius, decelZone)
      : 1;
    const stepSize = nominalSpeed * speedFactor * arrivalEase * delta;

    _autopilotHeading.copy(autopilot.endPos).sub(camera.position);
    const distToEnd = _autopilotHeading.length();
    if (distToEnd > 1e-6) _autopilotHeading.divideScalar(distToEnd);

    // Step toward target then deflect for any obstacle bubbles. The
    // destination is excluded so its own bubble doesn't block the
    // final approach.
    camera.position.addScaledVector(_autopilotHeading, Math.min(stepSize, distToEnd));
    enforceCollisionAvoidance(null, autopilot.object);

    // Orient toward destination. Slerp gives a smooth turn into the
    // arrival framing even as the path bends around obstacles.
    _autopilotLookMatrix.lookAt(camera.position, autopilot.orbitCenter, camera.up);
    _autopilotLookQuat.setFromRotationMatrix(_autopilotLookMatrix);
    const rotAlpha = 1 - Math.exp(-3.5 * delta);
    camera.quaternion.slerp(_autopilotLookQuat, rotAlpha);
    flyEuler.setFromQuaternion(camera.quaternion);
    flyTargetEuler.copy(flyEuler);
    syncPlayerShipToCamera();

    // Arrival check uses distance to the TARGET BODY (not the
    // approach point). The earlier endPos-based check fired far too
    // late for fly-overs where the cruise path crossed the body —
    // e.g. autopiloting Mars from the inner system, endPos sits on
    // the far side of Mars, so the heading dives straight through
    // the planet to get there. Switching the gate to body proximity
    // means orbit phase fires the moment the ship reaches its orbit
    // radius, regardless of which side it approached from. The
    // tRaw>=1 fallback covers slow approaches that never quite
    // touched the orbit radius (e.g. heavy deflection around a
    // crowded inner system).
    const distToTarget = camera.position.distanceTo(autopilot.orbitCenter);
    const orbitTrigger = autopilot.orbitRadius * 1.05;
    if (distToTarget <= orbitTrigger || tRaw >= 1.0) {
      autopilot.phase = 'orbiting';
      // Anchor orbitU on the camera's current direction from the
      // body. If we somehow ended up inside the body (zero offset)
      // pick an arbitrary outward vector so the math doesn't
      // degenerate.
      autopilot.orbitU.copy(camera.position).sub(autopilot.orbitCenter);
      if (autopilot.orbitU.lengthSq() < 1e-4) autopilot.orbitU.set(1, 0.2, 0.5);
      autopilot.orbitU.normalize();
      // Orbit plane aligned with the cruise approach. _autopilotHeading
      // points toward the body (the velocity direction at arrival);
      // its lateral component (perpendicular to orbitU) tells us
      // which way the ship was already moving sideways relative to
      // the body. Using that as orbitV continues the orbit in the
      // SAME direction the ship was carving in — reads like a
      // satellite naturally settling into the orbit that fits its
      // approach trajectory, instead of an arbitrary world-up plane.
      // Negate the lateral component so the orbit motion sweeps
      // FORWARD along the approach direction (orbitV is the +sin
      // axis, and we want angle 0 at arrival sweeping forward).
      autopilot.orbitV.copy(_autopilotHeading);
      const radialComponent = autopilot.orbitV.dot(autopilot.orbitU);
      autopilot.orbitV.addScaledVector(autopilot.orbitU, -radialComponent);
      if (autopilot.orbitV.lengthSq() < 1e-4) {
        // Head-on approach with no lateral motion — fall back to a
        // world-up plane so the orbit still has a defined axis.
        const lift = new THREE.Vector3(0, 1, 0);
        autopilot.orbitV.crossVectors(autopilot.orbitU, lift);
        if (autopilot.orbitV.lengthSq() < 1e-4) autopilot.orbitV.set(1, 0, 0);
      }
      autopilot.orbitV.normalize();
      autopilot.orbitAngle = 0;
      // Seamless cruise→orbit handoff: instead of snapping the
      // camera to the configured orbit radius (which produced the
      // visible pop), record the CURRENT distance and blend it
      // toward the configured orbit radius over the transition
      // duration. The orbit phase reads transitionRadius each
      // frame so the radius smoothly eases from arrival distance
      // to the target radius while the angular sweep is already
      // running — feels like one continuous arc, not a snap.
      autopilot.transitionFromRadius = Math.max(distToTarget, 1e-3);
      autopilot.transitionDuration = 1.8;
      autopilot.transitionT = 0;
      showAutopilotStatus(`Orbiting ${autopilot.target.label}`);
    }
    return true;
  }
  if (autopilot.phase === 'orbiting') {
    // Ramp the sweep up from zero over the transition so the orbit eases
    // in from the near-stopped arrival instead of jumping to full speed.
    const angRamp = autopilot.transitionDuration > 0
      ? THREE.MathUtils.smoothstep(autopilot.transitionT, 0, autopilot.transitionDuration)
      : 1;
    autopilot.orbitAngle += autopilot.orbitAngularSpeed * angRamp * delta;
    // Refresh orbit center (body may be moving along its orbit).
    if (autopilot.object) {
      autopilot.object.getWorldPosition(autopilot.orbitCenter);
    }
    // Radius blend for seamless cruise→orbit handoff. The cruise
    // arrival sets transitionFromRadius to the arrival distance; we
    // ease that toward the configured orbitRadius over
    // transitionDuration so the camera smoothly carves outward (or
    // inward) into the final orbit instead of snapping.
    let currentRadius = autopilot.orbitRadius;
    if (autopilot.transitionT < autopilot.transitionDuration) {
      autopilot.transitionT += delta;
      const tBlend = Math.min(autopilot.transitionT / autopilot.transitionDuration, 1);
      // Smoothstep ease — soft on both ends, peaks in the middle.
      const eased = tBlend * tBlend * (3 - 2 * tBlend);
      currentRadius = THREE.MathUtils.lerp(
        autopilot.transitionFromRadius,
        autopilot.orbitRadius,
        eased
      );
    }
    const cosA = Math.cos(autopilot.orbitAngle);
    const sinA = Math.sin(autopilot.orbitAngle);
    // Capture the center→camera offset so the post-body-motion reframe can
    // re-place the camera against the body's CURRENT position this frame.
    autopilot.orbitOffset.set(0, 0, 0)
      .addScaledVector(autopilot.orbitU, currentRadius * cosA)
      .addScaledVector(autopilot.orbitV, currentRadius * sinA);
    camera.position.copy(autopilot.orbitCenter).add(autopilot.orbitOffset);
    // Slerp the quaternion toward the look-at target instead of
    // hard-setting it. Combined with the radius blend, this keeps
    // the rotation cinematic during the cruise→orbit transition.
    _autopilotLookMatrix.lookAt(camera.position, autopilot.orbitCenter, camera.up);
    _autopilotLookQuat.setFromRotationMatrix(_autopilotLookMatrix);
    const rotAlpha = 1 - Math.exp(-6 * delta);
    camera.quaternion.slerp(_autopilotLookQuat, rotAlpha);
    flyEuler.setFromQuaternion(camera.quaternion);
    flyTargetEuler.copy(flyEuler);
    syncPlayerShipToCamera();
    return true;
  }
  return false;
}

function cancelAutopilot() {
  if (!autopilot.active) return;
  autopilot.active = false;
  autopilot.phase = 'idle';
  autopilot.target = null;
  autopilot.object = null;
  flyEuler.setFromQuaternion(camera.quaternion);
  flyTargetEuler.copy(flyEuler);
  hideAutopilotStatus();
}

// Re-place the autopilot ORBIT camera after all per-frame body/orbit
// motion has been applied. updateAutopilot runs inside updateFlyMovement,
// which is BEFORE the moon/planet orbital position update in the animate
// loop — so it framed the camera against a one-frame-stale body position.
// For fast-moving moons (their own orbit compounded with their planet's)
// that lag jittered the body in frame. Reading the body's now-current
// world position and re-placing camera + aim against it locks the body
// rock-steady. Mirrors updateFocusedObjectFollow, which fixes the same
// trailing-by-one-orbit-step problem for the non-fly inspection camera.
function reframeAutopilotOrbit() {
  if (!autopilot.active || autopilot.phase !== 'orbiting' || !autopilot.object || !flyMode) return;
  autopilot.object.updateWorldMatrix(true, false);
  autopilot.object.getWorldPosition(autopilot.orbitCenter);
  camera.position.copy(autopilot.orbitCenter).add(autopilot.orbitOffset);
  _autopilotLookMatrix.lookAt(camera.position, autopilot.orbitCenter, camera.up);
  _autopilotLookQuat.setFromRotationMatrix(_autopilotLookMatrix);
  camera.quaternion.copy(_autopilotLookQuat);
  flyEuler.setFromQuaternion(camera.quaternion);
  flyTargetEuler.copy(flyEuler);
  syncPlayerShipToCamera();
}

// ============================================================
// CINEMATIC DIRECTOR (localhost) — drive the camera through a
// route of bodies with cinematic framing + hard cuts, optionally
// orbit each body (sight-seeing), and record the canvas to a
// .webm for sharing. The player ship is sub-pixel at any framing
// distance, so this is a first-person / free cinematic camera
// flythrough: every shot frames the BODY (always looks at it),
// only the camera vantage varies, so it reads as an intentional
// planetary documentary rather than aimless drift.
// ============================================================
const _dirRight  = new THREE.Vector3();
const _dirUp     = new THREE.Vector3();
const _dirCamPos = new THREE.Vector3();
const _dirBodyPos = new THREE.Vector3();
const _dirIncoming = new THREE.Vector3();
const _dirLookM  = new THREE.Matrix4();
const _dirLookQ  = new THREE.Quaternion();
const _DIR_WORLD_UP = new THREE.Vector3(0, 1, 0);

// Shot library. Each entry is a vantage style; the camera always looks
// at the body, so the planet stays framed and only the angle changes on
// a cut. `place(out, ctx)` writes the camera world position into `out`.
// ctx = { body (Vec3), radius, approach (A), incoming (unit dir we came
// FROM, body→start), t (0..1 leg/orbit progress), time (shot seconds) }.
const CINEMATIC_SHOTS = {
  // Forward push-in: camera sits on the incoming side and dollies from
  // far to near as the leg progresses — feels like flying up to the body.
  approach(out, c) {
    const dist = THREE.MathUtils.lerp(c.approach * 2.4, c.approach * 0.85, c.t);
    dirBasis(c);
    out.copy(c.body)
      .addScaledVector(_dirIncoming, dist)
      .addScaledVector(_dirUp, c.approach * 0.12);
  },
  // Wide establishing: body big and off-centre, camera high to one side
  // with a slow lateral drift.
  wide(out, c) {
    dirBasis(c);
    const drift = Math.sin(c.time * 0.12) * c.approach * 0.18;
    out.copy(c.body)
      .addScaledVector(_dirIncoming, c.approach * 1.15)
      .addScaledVector(_dirRight, c.approach * 1.25 + drift)
      .addScaledVector(_dirUp, c.approach * 0.55);
  },
  // Banking fly-by: camera arcs from one side to the other across the
  // shot so the body sweeps past — the signature "fly-by".
  flyby(out, c) {
    dirBasis(c);
    const a = (c.t - 0.5) * Math.PI * 1.05;
    out.copy(c.body)
      .addScaledVector(_dirRight, Math.sin(a) * c.approach * 1.0)
      .addScaledVector(_dirIncoming, Math.cos(a) * c.approach * 0.9)
      .addScaledVector(_dirUp, c.approach * 0.16);
  },
  // Low hero angle: camera below the body's equator looking up past the
  // limb, dramatic and poster-like.
  lowhero(out, c) {
    dirBasis(c);
    out.copy(c.body)
      .addScaledVector(_dirIncoming, c.approach * 0.95)
      .addScaledVector(_dirUp, -c.approach * 0.42)
      .addScaledVector(_dirRight, c.approach * 0.28);
  },
  // Top-down drift: looking down on the body from above and a little
  // back, slowly creeping forward.
  topdown(out, c) {
    dirBasis(c);
    out.copy(c.body)
      .addScaledVector(_dirUp, c.approach * 1.15)
      .addScaledVector(_dirIncoming, c.approach * 0.5 - c.time * c.approach * 0.01);
  },
  // Orbit reveal: camera revolves around the body (used for the sight-
  // seeing lap). Angle driven by leg/orbit progress.
  orbit(out, c) {
    dirBasis(c);
    const ang = c.orbitAngle != null ? c.orbitAngle : c.t * Math.PI * 2;
    out.copy(c.body)
      .addScaledVector(_dirRight, Math.cos(ang) * c.approach)
      .addScaledVector(_dirIncoming, Math.sin(ang) * c.approach)
      .addScaledVector(_dirUp, c.approach * 0.22);
  }
};

// Build an orthonormal basis from the incoming direction (body→start) so
// the shots have stable right/up axes regardless of where we approached.
function dirBasis(c) {
  _dirRight.crossVectors(c.incoming, _DIR_WORLD_UP);
  if (_dirRight.lengthSq() < 1e-6) _dirRight.set(1, 0, 0);
  _dirRight.normalize();
  _dirUp.crossVectors(_dirRight, c.incoming).normalize();
}

// Per-body cut menus. Transit cuts through travel-style shots; the orbit
// phase uses the revolving reveal (optionally interleaved).
const DIRECTOR_TRANSIT_SHOTS = ['approach', 'wide', 'flyby', 'lowhero', 'topdown'];

// Baked-in routes. Keys match getFastTravelTargets(); entries may carry a
// label (for waypoints not in the body picker) and a systemJump (cross-
// system hops routed through the wormhole flythrough, e.g. Gargantua).
const CINEMATIC_PRESETS = {
  grand: {
    label: 'Grand Tour',
    route: [
      { key: 'sun' }, { key: 'mercury' }, { key: 'venus' }, { key: 'earth' },
      { key: 'mars' }, { key: 'jupiter' }, { key: 'saturn' }, { key: 'uranus' },
      { key: 'neptune' },
      { key: 'milky-way', label: 'Milky Way' },
      { key: 'black hole', label: 'Black Hole' },
      { key: 'wormhole', label: 'Wormhole' },
      { key: 'gargantua', label: 'Gargantua', systemJump: 'gargantua' }
    ]
  },
  planets: {
    label: 'The Planets',
    route: [
      { key: 'sun' }, { key: 'mercury' }, { key: 'venus' }, { key: 'earth' },
      { key: 'mars' }, { key: 'jupiter' }, { key: 'saturn' }, { key: 'uranus' }, { key: 'neptune' }
    ]
  },
  inner: {
    label: 'Inner System',
    route: [{ key: 'sun' }, { key: 'mercury' }, { key: 'venus' }, { key: 'earth' }, { key: 'mars' }]
  },
  giants: {
    label: 'Gas & Ice Giants',
    route: [{ key: 'jupiter' }, { key: 'saturn' }, { key: 'uranus' }, { key: 'neptune' }]
  }
};

const director = {
  active: false,
  route: [],            // [{ key, label }]
  mode: 'cutscene',     // 'cutscene' | 'single'
  singleShot: 'approach',
  orbitEach: true,
  recordOnRun: true,
  shotSeconds: 5.5,     // cutscene cut cadence
  // runtime
  recorder: null,
  hudWasHidden: false,
  legIndex: 0,
  phase: 'transit',     // 'transit' | 'orbit' | 'jumping'
  jumpTarget: null,     // system id we're warping to (Gargantua)
  jumpElapsed: 0,
  object: null,
  fromPos: new THREE.Vector3(),
  toPos: new THREE.Vector3(),
  incoming: new THREE.Vector3(),
  lead: new THREE.Vector3(),
  approach: 14,
  bodyRadius: 1,
  transitT: 0,
  transitDuration: 9,
  orbitT: 0,
  orbitDuration: 11,
  orbitAngle: 0,
  shot: 'approach',
  shotT: 0,
  shotIndex: 0
};

function directorResolve(key) {
  const obj = findLoadedSceneObjectByName(key);
  return obj || null;
}

function directorApproachFor(object, key) {
  const radius = (object.userData && (object.userData.radius || object.userData.size)) || 1;
  return {
    radius,
    approach: fastTravelApproachDistance({ key }, radius)
  };
}

// Configure the next leg: where we are now → arrival framing of the body.
function directorSetupLeg(index) {
  const wp = director.route[index];
  if (!wp) return false;
  const object = directorResolve(wp.key);
  if (!object) return false;
  director.object = object;
  object.updateWorldMatrix(true, false);
  object.getWorldPosition(_dirBodyPos);
  const { radius, approach } = directorApproachFor(object, wp.key);
  director.bodyRadius = radius;
  director.approach = approach;

  // Start the leg from wherever the camera currently is.
  director.fromPos.copy(camera.position);
  // Incoming direction = from the body toward our start point (the side
  // we approach from), so shots frame the lit, near hemisphere.
  director.incoming.copy(director.fromPos).sub(_dirBodyPos);
  if (director.incoming.lengthSq() < 1e-4) {
    // Already on top of the body — bias outward from the sun so it's lit.
    if (sun) sun.getWorldPosition(director.incoming);
    director.incoming.subVectors(_dirBodyPos, director.incoming);
    if (director.incoming.lengthSq() < 1e-4) director.incoming.set(0, 0.3, 1);
  }
  director.incoming.normalize();

  // Arrival point = body + approach along incoming (a lit vantage).
  director.toPos.copy(_dirBodyPos).addScaledVector(director.incoming, approach);

  // Duration scales gently with distance so far hops feel longer.
  const dist = director.fromPos.distanceTo(director.toPos);
  director.transitDuration = THREE.MathUtils.clamp(6 + dist / 60, 6, 16);
  director.orbitDuration = director.orbitEach ? 11 : 0;
  director.transitT = 0;
  director.orbitT = 0;
  director.orbitAngle = 0;
  director.phase = 'transit';
  director.shotT = 0;
  director.shotIndex = 0;
  director.shot = director.mode === 'single' ? director.singleShot : DIRECTOR_TRANSIT_SHOTS[0];
  return true;
}

function directorAdvanceShot() {
  if (director.mode === 'single') { director.shot = director.singleShot; return; }
  if (director.phase === 'orbit') { director.shot = 'orbit'; return; }
  director.shotIndex = (director.shotIndex + 1) % DIRECTOR_TRANSIT_SHOTS.length;
  director.shot = DIRECTOR_TRANSIT_SHOTS[director.shotIndex];
  director.shotT = 0;
}

// Easing for the leg progress — soft start and stop.
function directorEase(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

const _dirShotCtx = {
  body: _dirBodyPos, radius: 1, approach: 14, incoming: _dirIncoming,
  t: 0, time: 0, orbitAngle: null
};

// Per-frame driver. Runs AFTER all body motion (like reframeAutopilotOrbit)
// so body positions are current — no one-frame jitter. Returns true while
// it owns the camera.
function updateCinematicDirector(delta) {
  if (!director.active) return false;

  // Cross-system jump in progress: the wormhole flythrough owns the
  // camera, so we don't touch it — just wait for the target system to
  // finish loading, then frame the destination (Gargantua) and resume.
  if (director.phase === 'jumping') {
    director.jumpElapsed += delta;
    const arrived = activeSystemId === director.jumpTarget
      && !wormholeTunnelInFlight && !starMapJumpInFlight;
    if (arrived && flyMode) {
      const obj = directorResolve('gargantua')
        || directorResolve('black hole')
        || directorResolve('black-hole');
      if (obj) {
        obj.updateWorldMatrix(true, false);
        obj.getWorldPosition(_dirBodyPos);
        const info = directorApproachFor(obj, 'gargantua');
        director.object = obj;
        director.bodyRadius = info.radius;
        // Orbit at wherever the wormhole flight dropped us (a known-good
        // cinematic distance) rather than guessing the black hole's scale.
        director.approach = Math.max(camera.position.distanceTo(_dirBodyPos), info.radius * 2);
        director.incoming.copy(camera.position).sub(_dirBodyPos);
        if (director.incoming.lengthSq() < 1e-4) director.incoming.set(0, 0.3, 1);
        director.incoming.normalize();
        director.phase = 'orbit';
        director.orbitT = 0;
        director.orbitAngle = 0;
        director.orbitDuration = 16; // a long, lingering Gargantua reveal
        director.shotT = 0;
        director.shot = 'orbit';
        if (typeof onCinematicDirectorState === 'function') onCinematicDirectorState();
      } else {
        // Arrived at Gargantua but couldn't resolve a body to orbit — the
        // wormhole flight already left a dramatic view, so end cleanly.
        stopCinematicDirector({ completed: true });
      }
      return true;
    }
    // Safety: never hang forever waiting on the jump.
    if (director.jumpElapsed > 30) stopCinematicDirector({ completed: true });
    return true;
  }

  if (!flyMode) return false;

  // Refresh the current body's world position this frame.
  if (director.object) {
    director.object.updateWorldMatrix(true, false);
    director.object.getWorldPosition(_dirBodyPos);
  }

  director.shotT += delta;

  if (director.phase === 'transit') {
    director.transitT += delta;
    const raw = Math.min(director.transitT / director.transitDuration, 1);
    const eased = directorEase(raw);
    // Lead point flies fromPos → arrival, tracking the (moving) body by
    // keeping the arrival point body-relative.
    director.toPos.copy(_dirBodyPos).addScaledVector(director.incoming, director.approach);
    director.lead.copy(director.fromPos).lerp(director.toPos, eased);
    // Cut cadence
    if (director.mode === 'cutscene' && director.shotT >= director.shotSeconds) {
      directorAdvanceShot();
    }
    if (raw >= 1) {
      // Arrived. Orbit the body, or jump to the next leg.
      if (director.orbitEach && director.orbitDuration > 0) {
        director.phase = 'orbit';
        director.orbitT = 0;
        director.orbitAngle = 0;
        director.shotT = 0;
        if (director.mode === 'cutscene') director.shot = 'orbit';
      } else {
        directorNextLeg();
        return true;
      }
    }
  } else if (director.phase === 'orbit') {
    director.orbitT += delta;
    // One slow revolution (or whatever fits orbitDuration).
    director.orbitAngle += (Math.PI * 2 / Math.max(director.orbitDuration, 1)) * delta;
    director.lead.copy(_dirBodyPos)
      .addScaledVector(director.incoming, Math.cos(director.orbitAngle) * director.approach)
      .addScaledVector(_dirRight, Math.sin(director.orbitAngle) * director.approach);
    if (director.orbitT >= director.orbitDuration) {
      directorNextLeg();
      return true;
    }
  }

  // Place the camera via the active shot (always looking at the body).
  // Sync the scratch incoming the shots/dirBasis read from — without this
  // every shot offset multiplied a ZERO vector and the camera sat exactly
  // at the body centre (you were inside the planet, seeing nothing).
  _dirIncoming.copy(director.incoming);
  _dirShotCtx.radius = director.bodyRadius;
  _dirShotCtx.approach = director.approach;
  _dirShotCtx.t = director.phase === 'orbit'
    ? Math.min(director.orbitT / Math.max(director.orbitDuration, 1), 1)
    : Math.min(director.transitT / director.transitDuration, 1);
  _dirShotCtx.time = director.shotT;
  _dirShotCtx.orbitAngle = director.phase === 'orbit' ? director.orbitAngle : null;
  const shotFn = CINEMATIC_SHOTS[director.shot] || CINEMATIC_SHOTS.approach;
  shotFn(_dirCamPos, _dirShotCtx);

  // NOTE: no sun clamp here. An earlier radial clamp used
  // getBodyBaseRadius(sun) * 1.8, which under realistic-sun mode (the sun
  // is rendered huge) produced a ~600-unit safe radius that pinned the
  // camera far off the origin — the director never reached the inner
  // planets ("START does nothing / flies but breaks"). The director is
  // already exempt from the incineration death (checkCollisions), and
  // every shot frames its target at the body's own approach distance, so
  // the camera stays near the body it's filming, not at the sun.

  camera.position.copy(_dirCamPos);
  _dirLookM.lookAt(camera.position, _dirBodyPos, _DIR_WORLD_UP);
  _dirLookQ.setFromRotationMatrix(_dirLookM);
  camera.quaternion.copy(_dirLookQ);
  flyEuler.setFromQuaternion(camera.quaternion);
  flyTargetEuler.copy(flyEuler);
  // Park the (invisible) ship at the lead point so any HUD/markers track
  // the journey, not the camera vantage.
  if (playerShip) {
    playerShip.position.copy(director.lead);
    playerShip.quaternion.copy(camera.quaternion);
  }
  return true;
}

function directorNextLeg() {
  director.legIndex += 1;
  if (director.legIndex >= director.route.length) {
    stopCinematicDirector({ completed: true });
    return;
  }
  const wp = director.route[director.legIndex];
  // Cross-system waypoint (Gargantua): warp through the wormhole and let
  // the 'jumping' phase pick up framing once the new system has loaded.
  if (wp && wp.systemJump && activeSystemId !== wp.systemJump) {
    director.phase = 'jumping';
    director.jumpTarget = wp.systemJump;
    director.jumpElapsed = 0;
    jumpToSystem(wp.systemJump);
    if (typeof onCinematicDirectorState === 'function') onCinematicDirectorState();
    return;
  }
  if (!directorSetupLeg(director.legIndex)) {
    // Skip an unresolved body rather than stalling.
    directorNextLeg();
    return;
  }
  if (typeof onCinematicDirectorState === 'function') onCinematicDirectorState();
}

function startCinematicDirector(opts = {}) {
  if (!director.route.length) return false;
  if (opts.mode) director.mode = opts.mode;
  if (opts.singleShot) director.singleShot = opts.singleShot;
  if (typeof opts.orbitEach === 'boolean') director.orbitEach = opts.orbitEach;
  if (typeof opts.record === 'boolean') director.recordOnRun = opts.record;

  if (!flyMode) enterFlyMode();
  cancelAutopilot();
  clearOrbitStabilization();

  director.legIndex = 0;
  if (!directorSetupLeg(0)) return false;
  director.active = true;

  // Clean frame for recording: hide all HUD.
  director.hudWasHidden = document.body.classList.contains('photo-mode-clean');
  if (!director.hudWasHidden) document.body.classList.add('photo-mode-clean');

  if (director.recordOnRun) {
    if (!director.recorder) director.recorder = createCanvasRecorder(renderer.domElement);
    director.recorder.start();
  }
  if (typeof onCinematicDirectorState === 'function') onCinematicDirectorState();
  return true;
}

function stopCinematicDirector(opts = {}) {
  if (!director.active) return;
  director.active = false;
  director.phase = 'transit';
  director.object = null;
  // Restore HUD unless it was already hidden before the run.
  if (!director.hudWasHidden) document.body.classList.remove('photo-mode-clean');
  // Hand control back to manual flight from the current camera pose.
  flyEuler.setFromQuaternion(camera.quaternion);
  flyTargetEuler.copy(flyEuler);
  if (director.recorder && director.recorder.isRecording()) {
    director.recorder.stopAndDownload('solar-flythrough');
  }
  if (typeof onCinematicDirectorState === 'function') onCinematicDirectorState();
}

// UI hook assigned when the panel is wired (localhost only).
let onCinematicDirectorState = null;

// Wire the localhost cinematic-director panel: body picker, route list
// (add / reorder / remove), mode + shot + toggle controls, start/stop,
// and a state read-out. No-op (panel stays hidden) off localhost.
function setupCinematicDirector() {
  if (!isLocalEnvironment()) return;
  // Lives in the tuner dock's Director tab now (no standalone panel).
  const bodyPick = document.getElementById('cine-body-pick');
  const addBtn = document.getElementById('cine-add');
  const presetSel = document.getElementById('cine-preset');
  const loadBtn = document.getElementById('cine-load');
  const routeList = document.getElementById('cine-route');
  const singleRow = document.getElementById('cine-single-row');
  const shotSel = document.getElementById('cine-shot');
  const orbitChk = document.getElementById('cine-orbit');
  const recordChk = document.getElementById('cine-record');
  const startBtn = document.getElementById('cine-start');
  const stopBtn = document.getElementById('cine-stop');
  const statusEl = document.getElementById('cine-status');
  if (!bodyPick || !addBtn || !routeList || !startBtn || !stopBtn || !statusEl) return;

  // The renderer is created after this setup runs, so only disable the
  // record toggle if the renderer already exists AND capture is genuinely
  // unsupported; otherwise leave it on (start time re-checks via the
  // recorder, which no-ops gracefully if capture isn't available).
  if (recordChk && renderer && renderer.domElement && !isRecordingSupported(renderer.domElement)) {
    recordChk.checked = false;
    recordChk.disabled = true;
  }

  // Body picker from fast-travel targets, minus system-jump specials so
  // the route stays inside the active system.
  const targets = (typeof getFastTravelTargets === 'function' ? getFastTravelTargets() : [])
    .filter((t) => t && !t.systemJump);
  bodyPick.innerHTML = '';
  targets.forEach((t) => {
    const opt = document.createElement('option');
    opt.value = t.key;
    opt.textContent = t.label;
    bodyPick.appendChild(opt);
  });
  const labelFor = (key) => {
    const t = targets.find((x) => x.key === key);
    return t ? t.label : key;
  };

  const route = []; // array of { key, label, systemJump? }; copied into director.route on start

  function renderRoute() {
    routeList.innerHTML = '';
    route.forEach((wp, i) => {
      const li = document.createElement('li');
      const idx = document.createElement('span'); idx.className = 'idx'; idx.textContent = String(i + 1);
      const nm = document.createElement('span'); nm.className = 'nm'; nm.textContent = wp.label || labelFor(wp.key);
      const up = document.createElement('button'); up.className = 'mv'; up.textContent = '↑'; up.title = 'Move up';
      up.addEventListener('click', () => { if (i > 0) { [route[i - 1], route[i]] = [route[i], route[i - 1]]; renderRoute(); } });
      const dn = document.createElement('button'); dn.className = 'mv'; dn.textContent = '↓'; dn.title = 'Move down';
      dn.addEventListener('click', () => { if (i < route.length - 1) { [route[i + 1], route[i]] = [route[i], route[i + 1]]; renderRoute(); } });
      const rm = document.createElement('button'); rm.className = 'rm'; rm.textContent = '×'; rm.title = 'Remove';
      rm.addEventListener('click', () => { route.splice(i, 1); renderRoute(); });
      li.append(idx, nm, up, dn, rm);
      routeList.appendChild(li);
    });
    if (!director.active) {
      statusEl.textContent = route.length
        ? `${route.length} waypoint${route.length > 1 ? 's' : ''} · ready`
        : 'Add bodies to build a route.';
    }
    startBtn.disabled = director.active || route.length === 0;
  }

  addBtn.addEventListener('click', () => {
    if (bodyPick.value) { route.push({ key: bodyPick.value, label: labelFor(bodyPick.value) }); renderRoute(); }
  });

  if (loadBtn && presetSel) {
    loadBtn.addEventListener('click', () => {
      const preset = CINEMATIC_PRESETS[presetSel.value];
      if (!preset) return;
      route.length = 0;
      preset.route.forEach((e) => route.push({
        key: e.key,
        label: e.label || labelFor(e.key),
        systemJump: e.systemJump || undefined
      }));
      renderRoute();
    });
  }

  document.querySelectorAll('input[name="cine-mode"]').forEach((r) => {
    r.addEventListener('change', () => {
      const single = document.querySelector('input[name="cine-mode"]:checked').value === 'single';
      singleRow.style.display = single ? '' : 'none';
    });
  });

  startBtn.addEventListener('click', () => {
    if (!route.length || director.active) return;
    const mode = document.querySelector('input[name="cine-mode"]:checked').value;
    director.route = route.map((wp) => ({ key: wp.key, label: wp.label || labelFor(wp.key), systemJump: wp.systemJump }));
    const ok = startCinematicDirector({
      mode,
      singleShot: shotSel ? shotSel.value : 'approach',
      orbitEach: orbitChk ? orbitChk.checked : true,
      record: recordChk ? recordChk.checked : false
    });
    if (!ok) statusEl.textContent = 'Could not start — is the body loaded?';
  });

  stopBtn.addEventListener('click', () => stopCinematicDirector());

  onCinematicDirectorState = () => {
    const running = director.active;
    startBtn.disabled = running || route.length === 0;
    stopBtn.disabled = !running;
    addBtn.disabled = running;
    if (running) {
      const wp = director.route[director.legIndex];
      const phase = director.phase === 'orbit' ? 'orbiting' : 'flying to';
      const rec = director.recorder && director.recorder.isRecording() ? ' · ●REC' : '';
      statusEl.textContent = `▶ ${phase} ${wp ? wp.label : '—'} (${director.legIndex + 1}/${director.route.length})${rec}`;
    } else {
      statusEl.textContent = route.length
        ? (recordChk && recordChk.checked ? 'Done · .webm saved.' : 'Done · ready.')
        : 'Add bodies to build a route.';
    }
  };

  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && director.active) stopCinematicDirector();
  });

  renderRoute();
}

function fastTravelApproachDistance(target, radius) {
  const r = Math.max(radius || 1, 0.001);
  if (target && (target.key === 'sun' || target.isStar)) return Math.max(r * 4.5, 60);
  if (target && target.key === 'wormhole') return Math.max(r * 3.5, 32);
  if (target && (target.key === "halley's comet" || target.key === 'voyager 1')) {
    return Math.max(r * 12, 8);
  }
  return Math.max(r * 6, 14);
}

function fastTravelTo(object, target) {
  if (!object || !flyMode) return;
  if (fastTravelAnim) { fastTravelAnim.kill(); fastTravelAnim = null; }

  // Fast travel takes over the cockpit for one snap. Clear any mode that
  // can keep writing camera position/quaternion after the jump.
  clearOrbitStabilization();
  resetFlyInputState();

  const targetPos = new THREE.Vector3();
  object.getWorldPosition(targetPos);

  const radius = (object.userData && (object.userData.radius || object.userData.size))
    || (target && target.key === 'wormhole' ? 18 : 1);
  const approach = fastTravelApproachDistance(target, radius);

  // Approach from outward of the sun so the body stays lit. Voyager and
  // Halley sit far from origin, but the same vector still works.
  const sunPos = activeStarWorldPosition(new THREE.Vector3());
  const fromSun = new THREE.Vector3().subVectors(targetPos, sunPos);
  if (fromSun.lengthSq() < 1e-4) fromSun.set(0, 0.2, 1);
  fromSun.normalize();

  const lift = new THREE.Vector3(0, 1, 0);
  const sideAxis = new THREE.Vector3().crossVectors(fromSun, lift);
  if (sideAxis.lengthSq() < 1e-4) sideAxis.set(1, 0, 0);
  sideAxis.normalize();

  const approachOffset = fromSun.clone().multiplyScalar(approach * 0.9)
    .addScaledVector(lift, approach * 0.18)
    .addScaledVector(sideAxis, approach * 0.12);
  const arrivalPos = targetPos.clone().add(approachOffset);

  // Brief warp flash for feel, but the move itself is instant — that
  // sidesteps the per-frame physics writes (gravity into velocity,
  // setFromEuler into quaternion) that were stomping on the tween.
  document.body.classList.add('warp-active');

  // Snap camera into place and re-derive flyEuler so the existing
  // physics loop keeps WASD oriented from the new attitude.
  camera.position.copy(arrivalPos);
  const lookM = new THREE.Matrix4().lookAt(camera.position, targetPos, camera.up);
  camera.quaternion.setFromRotationMatrix(lookM);
  flyEuler.setFromQuaternion(camera.quaternion);
  flyTargetEuler.copy(flyEuler);
  syncPlayerShipToCamera();
  controls.target.copy(targetPos);

  // Drop the warp overlay after a short flash so the user sees it for
  // half a beat without us blocking control during the transition.
  setTimeout(() => {
    document.body.classList.remove('warp-active');
  }, 280);
}

const EARTH_ATMO_UP = new THREE.Vector3();
const EARTH_ATMO_EAST = new THREE.Vector3();
const EARTH_ATMO_LOOK = new THREE.Vector3();
const EARTH_ATMO_WORLD_UP = new THREE.Vector3(0, 1, 0);
const EARTH_ATMO_LOOK_MATRIX = new THREE.Matrix4();
let earthAtmosphereStatusTimer = null;

function selectAtmosphericPropulsion() {
  const allowed = getCraftPropulsions(activeCraftKey);
  if (currentPropulsion !== 'warp' && allowed.includes(currentPropulsion)) return;
  const preference = ['antigravity', 'impulse', 'fusion', 'chemical', 'ion', 'nuclear', 'drift'];
  const next = preference.find((key) => allowed.includes(key)) || allowed[0];
  if (!next || !propulsionTypes[next]) return;
  currentPropulsion = next;
  const select = document.getElementById('propulsion-type');
  if (select) select.value = next;
  flySpeed = Math.min(flySpeed, propulsionTypes[next].maxSpeed);
}

function enterEarthAtmosphere() {
  const earth = planets && planets.earth;
  if (!earth || !camera) return;
  if (!flyMode) enterFlyMode();
  activeCameraTween?.kill(); activeCameraTween = null; isAnimating = false;
  controls.enabled = false;
  _atmoCarryBody = null;
  _atmoCarrySpinMesh = null;
  _nearestBodyCacheFrame = -1;

  cancelAutopilot();
  closeAutopilotMenu();
  closeFastTravelMenu();
  clearOrbitStabilization();
  clearFlyParkedBody({ inheritVelocity: false });
  orbitParked = false;
  isMiddleMouseDown = false;
  resetFlyInputState({ resetKeys: true });
  selectAtmosphericPropulsion();
  setSpeedRange('atmosphere');

  earth.updateMatrixWorld(true);
  earth.getWorldPosition(FLY_SCRATCH_BODY_POS);

  if (sun) {
    sun.getWorldPosition(EARTH_ATMO_UP);
    EARTH_ATMO_UP.sub(FLY_SCRATCH_BODY_POS);
  } else {
    EARTH_ATMO_UP.set(0.4, 0.35, 0.85);
  }
  if (EARTH_ATMO_UP.lengthSq() < 1e-6) EARTH_ATMO_UP.set(0.4, 0.35, 0.85);
  EARTH_ATMO_UP.normalize();

  const surfaceRadius = getBodyVisualSurfaceRadius(earth, EARTH_ATMO_UP);
  const lowAtmosphereAltitude = getBodyBaseRadius(earth) * 12000 / EARTH_METRES;
  camera.position.copy(FLY_SCRATCH_BODY_POS)
    .addScaledVector(EARTH_ATMO_UP, surfaceRadius + lowAtmosphereAltitude);

  EARTH_ATMO_EAST.crossVectors(EARTH_ATMO_WORLD_UP, EARTH_ATMO_UP);
  if (EARTH_ATMO_EAST.lengthSq() < 1e-6) {
    EARTH_ATMO_EAST.set(1, 0, 0).cross(EARTH_ATMO_UP);
  }
  EARTH_ATMO_EAST.normalize();

  EARTH_ATMO_LOOK.copy(camera.position)
    .addScaledVector(EARTH_ATMO_EAST, earth.userData.radius * 1.8)
    .addScaledVector(EARTH_ATMO_UP, -earth.userData.radius * 0.08);
  EARTH_ATMO_LOOK_MATRIX.lookAt(camera.position, EARTH_ATMO_LOOK, EARTH_ATMO_UP);
  camera.quaternion.setFromRotationMatrix(EARTH_ATMO_LOOK_MATRIX);
  flyEuler.setFromQuaternion(camera.quaternion);
  flyTargetEuler.copy(flyEuler);

  // A 250 m/s tangent cruise, converted to the current display scale.
  // This explicit jump initializes the complete rig before terrain checks.
  flyVelocity.copy(EARTH_ATMO_EAST).multiplyScalar(getBodyBaseRadius(earth) * 250 / EARTH_METRES / 60);
  computeAtmosphericEntry(atmoEntry, findNearestBodySurfaceUncached(), camera.position);
  atmoCraftScaleLive = atmoEntry.craftScale;
  controls.target.copy(camera.position).addScaledVector(EARTH_ATMO_EAST, earth.userData.radius * 2);
  syncPlayerShipToCamera();
  updateFlyCameraNear();

  showAutopilotStatus('Earth atmosphere');
  if (earthAtmosphereStatusTimer) clearTimeout(earthAtmosphereStatusTimer);
  earthAtmosphereStatusTimer = setTimeout(() => {
    if (!autopilot.active) hideAutopilotStatus();
  }, 1600);
}

// Update fly mode movement with realistic physics
// Thrust buildup for gradual propulsion types
let thrustBuildup = 0;

// Scratch vectors for fly-mode physics so we never allocate inside
// the per-frame loop. GC pressure from new THREE.Vector3() in a
// 60-120 Hz loop was a measurable contributor to the laggy feel.
const FLY_SCRATCH_THRUST = new THREE.Vector3();
const FLY_SMOOTHED_THRUST = new THREE.Vector3();
const FLY_SCRATCH_GRAVITY = new THREE.Vector3();
const FLY_SCRATCH_BODY_POS = new THREE.Vector3();
const FLY_SCRATCH_TO_BODY = new THREE.Vector3();
const FLY_SCRATCH_DELTA_POS = new THREE.Vector3();
const FLY_SCRATCH_CRAFT_OFFSET = new THREE.Vector3();
const FLY_SCRATCH_BOB = new THREE.Vector3();
const FLY_SCRATCH_CAM_FWD = new THREE.Vector3();
// Smoothed forward-lag distance in camera-local space — grows when
// the ship accelerates, decays back to zero at rest. Operating in the
// camera-local frame means turning the camera doesn't drift the ship
// sideways out of view; only forward thrust pushes it backward
// relative to the camera.
let chaseLagForwardDistance = 0;
// "Parked" inspection mode: when the user releases middle-click after
// orbiting around the ship, the camera and ship freeze in place so
// they can compose the angle for a screenshot. Any movement input
// (WASD / Space / Shift) snaps back to the chase camera.
let orbitParked = false;
// Right-mouse-while-middle-mouse drag translates the orbit camera
// laterally (in camera-local right/up axes) so the user can compose
// off-centre framing. The offset is preserved through release so a
// parked screenshot still has the chosen offset; cleared by
// resetCraftOrbitCamera().
let isRightMouseDown = false;
let cameraPanOffsetX = 0;
let cameraPanOffsetY = 0;
const FLY_SCRATCH_PAN_RIGHT = new THREE.Vector3();
const FLY_SCRATCH_PAN_UP = new THREE.Vector3();
const FLY_SCRATCH_PARK_REL = new THREE.Vector3();
// Smooth resume: when transitioning out of parked mode, the ship
// lerps from its parked world position to the chase slot over the
// duration below instead of snapping. resumeProgress=1 means no
// transition active.
const CHASE_RESUME_DURATION = 0.55;
let chaseResumeProgress = 1;
const FLY_SCRATCH_RESUME_FROM = new THREE.Vector3();
const FLY_SCRATCH_LOCAL_VEL = new THREE.Vector3();
const FLY_SCRATCH_ATTITUDE = new THREE.Vector3();
const FLY_SCRATCH_CAMERA_INV_QUAT = new THREE.Quaternion();

function parkFlyOrbitCamera() {
  if (!flyMode || !playerShip) return;
  orbitParked = true;
  playerShip.getWorldPosition(ufoWorldPos);

  FLY_SCRATCH_PARK_REL.copy(camera.position).sub(ufoWorldPos);
  const dist = FLY_SCRATCH_PARK_REL.length();
  if (dist > 1e-4) {
    const profile = getActiveCraftProfile();
    cameraOrbitDistance = clampCraftOrbitDistance(dist, profile);
    _cameraOrbitDistanceTarget = cameraOrbitDistance;
    cameraOrbitPitch = Math.asin(THREE.MathUtils.clamp(FLY_SCRATCH_PARK_REL.y / dist, -1, 1));
    cameraOrbitAngle = Math.atan2(FLY_SCRATCH_PARK_REL.x, FLY_SCRATCH_PARK_REL.z);
    targetOrbitAngle = cameraOrbitAngle;
    targetOrbitPitch = cameraOrbitPitch;
  }

  flyVelocity.set(0, 0, 0);
  flyAngularVelocity.set(0, 0);
  thrustBuildup = 0;
  FLY_SMOOTHED_THRUST.set(0, 0, 0);
  chaseLagForwardDistance = 0;
  flyEuler.setFromQuaternion(camera.quaternion);
  flyTargetEuler.copy(flyEuler);
}

function updateFlyMovement(delta) {
  if (!flyMode) return;
  // Wormhole flythrough owns the camera while it runs; skip fly input
  // so the player's WASD doesn't fight the tunnel's scripted motion.
  if (wormholeTunnelInFlight) return;
  // Cinematic director fully owns the camera + ship; it runs later in the
  // frame (after body motion) so just skip manual physics and the ship
  // sync here, otherwise they'd fight the directed camera.
  if (director.active) return;

  // Smooth wheel-driven zoom. The wheel handler writes to TARGET
  // values; here each frame we lerp the live values toward them so a
  // tick of the scroll wheel reads as a continuous cinematic dolly
  // rather than a discrete step. ~150 ms time constant (1 - exp(-6Δ))
  // — fast enough to feel responsive, slow enough to read as smooth.
  const wheelEase = 1.0 - Math.exp(-delta * 6.0);
  const profile = getActiveCraftProfile();
  if (_followDistanceTarget == null) _followDistanceTarget = profile.followDistance;
  const minFollowDistance = profile.minOrbitDistance != null ? profile.minOrbitDistance : 1;
  const maxFollowDistance = profile.maxOrbitDistance != null ? profile.maxOrbitDistance : 20;
  _followDistanceTarget = THREE.MathUtils.clamp(_followDistanceTarget, minFollowDistance, maxFollowDistance);
  cameraOrbitDistance += (_cameraOrbitDistanceTarget - cameraOrbitDistance) * wheelEase;
  profile.followDistance += (_followDistanceTarget - profile.followDistance) * wheelEase;
  profile.followDistance = THREE.MathUtils.clamp(profile.followDistance, minFollowDistance, maxFollowDistance);
  // Wheel zoom = dolly the camera IN/OUT on the craft while the craft
  // stays put in space. syncPlayerShipToCamera rigidly places the craft at
  // camera + followDistance·forward, so just moving the camera drags the
  // craft with it (it never appears to zoom — it only relocates). The fix:
  // move the camera forward by `move` AND shrink followDistance by the
  // same `move`, so the sync re-pins the craft at the EXACT same world
  // position and the camera dollies onto a stationary craft. Scroll up
  // (drained > 0) zooms in; bounded by the craft's orbit-distance limits.
  if (Math.abs(_flyDollyPending) > 1e-5) {
    const drained = _flyDollyPending * wheelEase;
    _flyDollyPending -= drained;
    const reach = THREE.MathUtils.clamp(profile.followDistance, minFollowDistance, maxFollowDistance);
    const desired = drained * reach * 0.9;
    const newFollow = THREE.MathUtils.clamp(profile.followDistance - desired, minFollowDistance, maxFollowDistance);
    const move = profile.followDistance - newFollow; // honour the clamp
    if (Math.abs(move) > 1e-6) {
      camera.getWorldDirection(_flyDollyDir); // unit forward = toward the craft
      camera.position.addScaledVector(_flyDollyDir, move * atmoCraftScaleLive);
      profile.followDistance = newFollow;
      _followDistanceTarget = newFollow; // keep the smoothing pass in sync
    }
  }

  // Autopilot takes over the camera if active. Any thrust/rotation
  // input cancels it and returns control to the manual fly path.
  if (autopilot.active) {
    const userInput =
      flyKeys.forward || flyKeys.backward || flyKeys.left || flyKeys.right ||
      flyKeys.up || flyKeys.down ||
      flyKeys.rotateLeft || flyKeys.rotateRight ||
      flyKeys.rotateUp || flyKeys.rotateDown ||
      flyMouseLookActive;
    if (userInput) {
      cancelAutopilot();
    } else {
      updateAutopilot(delta);
      return;
    }
  }

  // Parked co-orbit: rigid-follow the body fly mode was activated on,
  // so the ship reads as stationary relative to that body until the
  // user touches a control. Release on any user input.
  if (flyParkedBody) {
    const parkedUserInput =
      flyKeys.forward || flyKeys.backward || flyKeys.left || flyKeys.right ||
      flyKeys.up || flyKeys.down ||
      flyKeys.rotateLeft || flyKeys.rotateRight ||
      flyKeys.rotateUp || flyKeys.rotateDown ||
      flyMouseLookActive;
    if (parkedUserInput) {
      clearFlyParkedBody({ inheritVelocity: true, delta });
    } else {
      flyParkedBody.getWorldPosition(_flyParkedDelta);
      _flyParkedDelta.sub(_flyParkedPrevPos);
      // Translate camera by body's per-frame world motion so the
      // ship coasts in lockstep with the planet — no orbit decay,
      // no sideways drift, just visually still relative to the
      // body. Skip the rest of the fly-physics update (no thrust,
      // no gravity drift) so the lock stays clean.
      camera.position.add(_flyParkedDelta);
      _flyParkedPrevPos.add(_flyParkedDelta);
      // Anchor the parked ship to the BODY (co-move it with the planet)
      // rather than syncing it to the camera. With the camera-sync, a
      // scroll dolly dragged the whole vehicle along ("zoom moved the
      // vehicle"); body-anchored, the ship holds its spot near the planet
      // and the wheel dollies the camera in/out of the object instead.
      if (playerShip) playerShip.position.add(_flyParkedDelta);
      return;
    }
  }

  if (orbitParked) {
    const parkedReleaseInput =
      flyKeys.forward || flyKeys.backward || flyKeys.left || flyKeys.right ||
      flyKeys.up || flyKeys.down ||
      flyKeys.rotateLeft || flyKeys.rotateRight ||
      flyKeys.rotateUp || flyKeys.rotateDown ||
      flyMouseLookActive;
    if (!parkedReleaseInput) {
      flyVelocity.set(0, 0, 0);
      flyAngularVelocity.set(0, 0);
      flyEuler.setFromQuaternion(camera.quaternion);
      flyTargetEuler.copy(flyEuler);
      return;
    }
    orbitParked = false;
    if (playerShip) {
      FLY_SCRATCH_RESUME_FROM.copy(playerShip.position);
      chaseResumeProgress = 0;
    }
  }

  const prop = propulsionTypes[currentPropulsion];
  // Contextual acceleration: scales with proximity-to-body factor so
  // the ship can't punch up to the (now-lowered) cap in a fraction of
  // a second when near a planet. Uses sqrt(regimeFactor) under the
  // hood so the response stays snappier than the speed cap drops —
  // ship still feels alive on a tight throttle, just constrained.
  let effectiveAcceleration = computeContextualAcceleration(prop.acceleration);

  // Calculate local-space thrust intent. Input is eased below so
  // keyboard/touch controls feel analog instead of binary on/off.
  const thrust = FLY_SCRATCH_THRUST.set(0, 0, 0);
  const hasThrustKey = flyKeys.forward || flyKeys.backward || flyKeys.left || flyKeys.right || flyKeys.up || flyKeys.down;

  // Any thrust input releases the parked-inspection lock so the chase
  // camera resumes following normally — but capture the ship's current
  // world position first so the chase code can lerp it back to its
  // chase slot smoothly instead of snapping.
  if (hasThrustKey && orbitParked) {
    orbitParked = false;
    if (playerShip) {
      FLY_SCRATCH_RESUME_FROM.copy(playerShip.position);
      chaseResumeProgress = 0;
      // Pan during orbit/parked (middle+right or right-drag) baked a
      // lateral offset into camera.position. Undo just that offset so
      // the chase math doesn't inherit it, without snapping the
      // camera to a completely new position (which fought with the
      // user's steering inputs during the chase-direction lerp).
      if (cameraPanOffsetX !== 0 || cameraPanOffsetY !== 0) {
        FLY_SCRATCH_PAN_RIGHT.set(1, 0, 0).applyQuaternion(camera.quaternion);
        FLY_SCRATCH_PAN_UP.set(0, 1, 0).applyQuaternion(camera.quaternion);
        camera.position.addScaledVector(FLY_SCRATCH_PAN_RIGHT, -cameraPanOffsetX);
        camera.position.addScaledVector(FLY_SCRATCH_PAN_UP, -cameraPanOffsetY);
        cameraPanOffsetX = 0;
        cameraPanOffsetY = 0;
      }
    }
    // Restore the pre-orbit chase direction into flyTargetEuler so
    // the cinematic ease lerps the camera (and therefore the ship,
    // since ship.quaternion = camera.quaternion in chase mode) back
    // to where the user was originally flying. Without this, the
    // ship keeps the orbited heading and effectively flips when the
    // user starts thrusting again.
    if (_orbitChaseEulerSaved) {
      flyTargetEuler.copy(_orbitSavedChaseEuler);
      // Snap flyEuler to match so the user's Q/E steering input
      // during the resume frame reads as "yaw from the restored
      // direction" rather than fighting an in-flight quaternion
      // lerp that was actively turning back to pre-orbit.
      flyEuler.copy(_orbitSavedChaseEuler);
      camera.quaternion.setFromEuler(flyEuler);
      _orbitChaseEulerSaved = false;
    }
  }

  if (flyKeys.forward) thrust.z -= 1;
  if (flyKeys.left) thrust.x -= 1;
  if (flyKeys.right) thrust.x += 1;
  if (flyKeys.up) thrust.y += 1;
  if (flyKeys.down) thrust.y -= 1;

  // S key has dual behavior: brake-then-reverse with latched mode.
  // The mode latches on the rising edge (S press) based on the ship's
  // forward speed at that instant:
  //   - moving forward → BRAKE mode for this entire press
  //   - already stopped or reversing → REVERSE mode
  // BRAKE mode damps forward velocity toward zero and STAYS at zero;
  // the user has to release S and re-press to get reverse thrust.
  // This matches "tap S to stop, tap S again to back up" rather than
  // the old behaviour where a held S key would brake then auto-roll
  // into reverse the instant it hit zero.
  if (flyKeys.backward && !_sKeyWasDown) {
    // Rising edge: pick mode based on current motion. Any motion that
    // is not already backwards gets the brake, so a sideways or
    // vertical drift stops too; the old forward-only test sent S
    // straight to reverse thrust while the ship slid past its target.
    FLY_SCRATCH_CAM_FWD.set(0, 0, -1).applyQuaternion(camera.quaternion);
    const speedAtPress = flyVelocity.length();
    const forwardSpeedAtPress = flyVelocity.dot(FLY_SCRATCH_CAM_FWD);
    const movingBackward = forwardSpeedAtPress < -0.3 * speedAtPress;
    _sKeyMode = (speedAtPress > FLY_REST_SPEED * 10 && !movingBackward) ? 'brake' : 'reverse';
    if (_sKeyMode === 'brake') _brakeDecelPerSec = 0;
  } else if (!flyKeys.backward) {
    _sKeyMode = 'idle';
  }
  _sKeyWasDown = flyKeys.backward;

  // Any drive input other than S releases a latched BRAKE.
  if (flyKeys.forward || flyKeys.left || flyKeys.right || flyKeys.up || flyKeys.down) _brakeLatch = false;

  const braking = _sKeyMode === 'brake' || _brakeLatch;
  if (braking) {
    // Dead-stop brake: shed the whole velocity vector on a straight
    // ramp, at the drive's own deceleration or fast enough to stop
    // within FLY_BRAKE_STOP_TIME, whichever is stronger. The rate is
    // set at onset and only ever raised (gravity can add speed mid-
    // brake), so the stop is linear to zero rather than an exponential
    // tail that never quite arrives. Holds at zero while S stays down.
    const speed = flyVelocity.length();
    _brakeDecelPerSec = Math.max(_brakeDecelPerSec, effectiveAcceleration * 60, speed / FLY_BRAKE_STOP_TIME);
    const newSpeed = Math.max(0, speed - _brakeDecelPerSec * delta);
    if (newSpeed <= FLY_REST_SPEED) {
      flyVelocity.set(0, 0, 0);
      _brakeLatch = false;
      _faArmed = false;
      thrustBuildup = 0;
    } else {
      flyVelocity.multiplyScalar(newSpeed / speed);
    }
  } else if (_sKeyMode === 'reverse') {
    // REVERSE: standard reverse-thrust path.
    thrust.z += 1;
  }

  if (thrust.lengthSq() > 1) thrust.normalize();
  const isThrusting = thrust.lengthSq() > 1e-5;
  const intentResponse = isThrusting ? 9.5 : 5.8;
  const intentAlpha = 1 - Math.exp(-intentResponse * delta);
  FLY_SMOOTHED_THRUST.lerp(thrust, intentAlpha);
  if (!isThrusting && FLY_SMOOTHED_THRUST.lengthSq() < 1e-5) {
    FLY_SMOOTHED_THRUST.set(0, 0, 0);
  }
  // With the brake on the input tail is cut: a lingering tail
  // re-accelerated the ship after the brake had brought it to rest. The
  // assist's coast-down keeps it, so the throttle winds down into the
  // slowing; the tail is gone within a second and the coast-down only
  // declares rest at its end.
  if (!isThrusting && braking) FLY_SMOOTHED_THRUST.set(0, 0, 0);

  // Apply thrust curve based on propulsion type
  if (isThrusting || FLY_SMOOTHED_THRUST.lengthSq() > 1e-5) {
    switch (prop.thrustCurve) {
      case 'gradual': // Ion drive - slow buildup
        thrustBuildup = Math.min(1, thrustBuildup + delta * 0.3);
        effectiveAcceleration *= thrustBuildup;
        break;
      case 'rampup': // Nuclear - medium buildup
        thrustBuildup = Math.min(1, thrustBuildup + delta * 0.8);
        effectiveAcceleration *= thrustBuildup;
        break;
      case 'pulse': // Fusion - pulsed thrust
        thrustBuildup += delta * 3;
        effectiveAcceleration *= (0.7 + 0.3 * Math.sin(thrustBuildup * 10));
        break;
      default: // Instant thrust
        thrustBuildup = 1;
        break;
    }
  } else {
    // Decay thrust buildup when not thrusting
    thrustBuildup = Math.max(0, thrustBuildup - delta * 2);
  }

  // Apply thrust in camera direction. Thrust saturates at the
  // contextual speed cap instead of punching through it: without the
  // saturation, sustained forward thrust out-muscles the soft brake,
  // climbs through the grace band to the hard-clamp boundary
  // (1.5× cap), snaps back to 1.2× cap, and climbs again — an ~11 Hz
  // speed sawtooth that fed the chase-lag distance and read as the
  // ship jittering fore/aft under acceleration. Letting thrust top
  // out exactly at the cap keeps steady-state speed constant; the
  // two-stage brake below still handles genuine overspeed (gravity
  // wells, sudden regime drops on approach).
  if (FLY_SMOOTHED_THRUST.lengthSq() > 1e-5) {
    thrust.copy(FLY_SMOOTHED_THRUST);
    thrust.applyQuaternion(camera.quaternion);
    thrust.multiplyScalar(effectiveAcceleration * delta * 60);
    const preThrustSpeed = flyVelocity.length();
    flyVelocity.add(thrust);
    const thrustCap = computeContextualSpeedCap(prop.maxSpeed);
    const postThrustSpeed = flyVelocity.length();
    if (postThrustSpeed > thrustCap && postThrustSpeed > preThrustSpeed) {
      // Already overspeed (gravity, regime change): thrust may steer
      // but not add magnitude. Below the cap: thrust tops out at it.
      const allowed = Math.max(preThrustSpeed, thrustCap);
      flyVelocity.multiplyScalar(allowed / Math.max(postThrustSpeed, 1e-9));
    }
  }
  // A thrust input arms flight assist; the coast-down below runs once
  // the keys are released.
  if (isThrusting) _faArmed = true;

  // Frame-rate-independent drag. Old form (`multiplyScalar(drag)`)
  // applied drag per frame regardless of frame timing, which made
  // the UFO feel sluggish at low FPS and twitchy at high FPS. The
  // pow form makes drag a per-second decay, identical at any frame
  // rate.
  const dragPerFrame = Math.pow(prop.drag, delta * 60);
  flyVelocity.multiplyScalar(dragPerFrame);
  const nearestForAir = findNearestBodySurface();
  const atmosphericDensity = computeAtmosphericDensity(nearestForAir);
  if (atmosphericDensity > 0) {
    // Atmospheric flight should feel usable, not like frictionless
    // orbital mechanics at treetop height. Density rises toward the
    // surface, adding strong but smooth damping so re-entry bleeds
    // off speed and takeoff can still climb back out.
    const airDrag = Math.exp(-(0.35 + atmosphericDensity * 2.8) * atmosphericDensity * delta);
    flyVelocity.multiplyScalar(airDrag);
  }

  // Flight assist coast-down: with the thrust keys released in space
  // the ship's speed follows flyAssistEase to rest, scaled frame by
  // frame by the curve's own step, so the frame rate doesn't change it
  // and anything else that slows the ship (the approach cap) carries
  // through. Skipped inside an atmosphere (air drag and gravity own
  // that tuned regime) and while orbit stabilisation writes the
  // velocity, so it never fights an orbit; parked co-orbit and
  // autopilot return before this point. An interrupted coast-down
  // starts again from the speed it is handed.
  if (flightAssist && _faArmed && !isThrusting && !braking && atmosphericDensity <= 0 && !orbitStabilized) {
    const before = flyAssistEase(_faEaseT / FLY_ASSIST_STOP_TIME);
    _faEaseT += delta;
    const after = flyAssistEase(_faEaseT / FLY_ASSIST_STOP_TIME);
    if (after <= 0) {
      flyVelocity.set(0, 0, 0);
      _faArmed = false;
      _faEaseT = 0;
    } else {
      flyVelocity.multiplyScalar(after / before);
    }
  } else {
    _faEaseT = 0;
  }
  // Station keeping: while the brake or flight assist is bringing the
  // ship to rest, or holding it there, solar gravity is cancelled too.
  // The Sun pulls at about 0.2 u/s² out at Earth's orbit, so without
  // this the ship never reads as stopped: it creeps sunward from the
  // moment the keys are released. Thrust, coasting with assist off,
  // orbit stabilisation and atmospheric flight still feel gravity.
  const assistHolding = !isThrusting && atmosphericDensity <= 0 && !orbitStabilized
    && (braking || (flightAssist && (_faArmed || flyVelocity.lengthSq() <= FLY_REST_SPEED * FLY_REST_SPEED)));

  // Apply gravitational effects from all celestial bodies. Using
  // pre-allocated scratch vectors so we don't churn the GC every
  // frame iterating planets.
  const earthFrame = nearestForAir?.body?.name === 'earth' && atmoEntry.body === nearestForAir.body && atmoEntry.state !== FLIGHT_STATES.SPACE;
  if (prop.affectedByGravity && earthFrame && currentPropulsion !== 'antigravity') {
    // In the carried Earth frame, solar-system display gravity would pull
    // the craft away while Earth is held fixed. Apply local gravity in
    // metres instead. The hovering anti-gravity drive balances this term.
    FLY_SCRATCH_GRAVITY.copy(nearestForAir.pos).sub(camera.position).normalize();
    const height = Math.max(0, nearestForAir.dist / nearestForAir.radius * EARTH_METRES);
    const gravity = 9.80665 / Math.pow(1 + height / EARTH_METRES, 2);
    flyVelocity.addScaledVector(FLY_SCRATCH_GRAVITY, gravity * nearestForAir.radius / EARTH_METRES * delta / 60);
  } else if (prop.affectedByGravity && !earthFrame && !assistHolding) {
    FLY_SCRATCH_GRAVITY.set(0, 0, 0);

    // Sun gravity
    if (sun) {
      sun.getWorldPosition(FLY_SCRATCH_BODY_POS);
      FLY_SCRATCH_TO_BODY.copy(FLY_SCRATCH_BODY_POS).sub(camera.position);
      const distToSun = FLY_SCRATCH_TO_BODY.length();
      if (distToSun > 10) {
        const sunGravity = G_CONSTANT * celestialMasses.sun / (distToSun * distToSun);
        FLY_SCRATCH_TO_BODY.multiplyScalar(sunGravity / distToSun);
        FLY_SCRATCH_GRAVITY.add(FLY_SCRATCH_TO_BODY);
      }
    }

    // Planet gravity. Skip planets so far away that their
    // contribution would be < 1e-6 — saves the sqrt and the
    // accumulator add for negligible bodies.
    const planetEntries = Object.keys(planets);
    for (let i = 0; i < planetEntries.length; i++) {
      const name = planetEntries[i];
      const planet = planets[name];
      planet.getWorldPosition(FLY_SCRATCH_BODY_POS);
      FLY_SCRATCH_TO_BODY.copy(FLY_SCRATCH_BODY_POS).sub(camera.position);
      const distSq = FLY_SCRATCH_TO_BODY.lengthSq();
      const mass = celestialMasses[name] || 1;
      // Skip if gravitational pull would be effectively zero.
      // distance^2 in scene units; threshold tuned empirically.
      if (distSq < 4 || distSq > 1e10) continue;
      const dist = Math.sqrt(distSq);
      const planetGravity = G_CONSTANT * mass / distSq;
      FLY_SCRATCH_TO_BODY.multiplyScalar(planetGravity / dist);
      FLY_SCRATCH_GRAVITY.add(FLY_SCRATCH_TO_BODY);
    }

    // Apply accumulated gravity to velocity, frame-time scaled.
    FLY_SCRATCH_GRAVITY.multiplyScalar(delta * 60);
    flyVelocity.add(FLY_SCRATCH_GRAVITY);
  }

  // Context-aware max speed. computeContextualSpeedCap combines the
  // static regime curve (distance-based) with a time-to-surface
  // auto-brake (velocity-aware). The brake below has two stages:
  // hard-clamp when seriously overspeed (cap < currentSpeed / 1.5),
  // soft-glide when cruising at or slightly above cap. The hard
  // stage is what prevents "warp into a planet, blow right past"
  // because by the time the regime drops, the soft brake's
  // exponential decay can't shed enough energy in the few frames
  // before the ship has already crossed several body radii.
  const contextualCap = computeContextualSpeedCap(prop.maxSpeed);
  const currentSpeed = flyVelocity.length();
  if (currentSpeed > contextualCap) {
    const overshoot = (currentSpeed - contextualCap) / Math.max(contextualCap, 1e-12);
    if (overshoot > 0.5) {
      // Hard clamp the egregious overshoot to 1.2× cap first, then
      // let the soft brake handle the final convergence. 1.2× rather
      // than 1.0× so the user doesn't feel a perfectly rigid wall;
      // there's a small grace band where engines glide down.
      const clampSpeed = contextualCap * 1.2;
      flyVelocity.multiplyScalar(clampSpeed / Math.max(currentSpeed, 1e-12));
    } else {
      // Soft brake: time-corrected exponential decay toward cap.
      // Gentle at small overshoot so cruising at 1.05× doesn't pulse.
      const brakeRate = THREE.MathUtils.clamp(1.5 + overshoot * 4.0, 1.5, 8.0);
      const alpha = 1 - Math.exp(-brakeRate * delta);
      const newSpeed = currentSpeed + (contextualCap - currentSpeed) * alpha;
      flyVelocity.multiplyScalar(newSpeed / Math.max(currentSpeed, 1e-12));
    }
  }

  // Apply velocity to position via the scratch delta-position
  // vector — avoid clone() per frame. Skip while parked so the
  // inspection camera holds its frozen pose even if there's residual
  // velocity from previous thrust.
  FLY_SCRATCH_DELTA_POS.copy(flyVelocity).multiplyScalar(delta * 60);
  if (!orbitParked) {
    camera.position.add(FLY_SCRATCH_DELTA_POS);
    // Hard collision guard: swept-sphere check against every body so
    // tunneling at warp speeds doesn't fly the ship straight through
    // the sun, plus post-step point check so the ship glides tangent
    // to the bubble surface instead of fighting back into it next
    // frame. The step vector is the same delta we just integrated,
    // so the swept check uses the exact trajectory the camera took.
    enforceCollisionAvoidance(flyVelocity, null, FLY_SCRATCH_DELTA_POS);
  }

  // Handle keyboard rotation with smooth angular velocity. The
  // range-aware multiplier slows steering when the user has dialed
  // in a tighter speed cap (Precision / Approach / Cruise), so the
  // arrow-key yaw matches the deliberate pace of low-cap flight
  // rather than spinning the ship like it's still on full throttle.
  let _rangeRotateMul = 1.0;
  if (currentSpeedRange === 'atmosphere') _rangeRotateMul = 0.22;
  else if (currentSpeedRange === 'precision') _rangeRotateMul = 0.35;
  else if (currentSpeedRange === 'approach') _rangeRotateMul = 0.55;
  else if (currentSpeedRange === 'cruise') _rangeRotateMul = 0.8;
  const rotateAccel = prop.rotateSpeed * 0.04 * delta * _rangeRotateMul;
  const maxAngularSpeed = prop.rotateSpeed * 0.015 * _rangeRotateMul;
  const angularDamping = 0.97; // Smooth deceleration

  // Apply angular acceleration based on input
  if (flyKeys.rotateLeft) flyAngularVelocity.y += rotateAccel;
  if (flyKeys.rotateRight) flyAngularVelocity.y -= rotateAccel;
  if (flyKeys.rotateUp) flyAngularVelocity.x += rotateAccel;
  if (flyKeys.rotateDown) flyAngularVelocity.x -= rotateAccel;

  // Clamp angular velocity to max
  flyAngularVelocity.x = Math.max(-maxAngularSpeed, Math.min(maxAngularSpeed, flyAngularVelocity.x));
  flyAngularVelocity.y = Math.max(-maxAngularSpeed, Math.min(maxAngularSpeed, flyAngularVelocity.y));

  // Frame-rate-independent angular damping. Same fix as linear drag
  // above, old form decayed at varying rates per frame, made
  // pitch/yaw feel sluggish at low FPS.
  flyAngularVelocity.multiplyScalar(Math.pow(angularDamping, delta * 60));

  // Apply angular velocity to the TARGET euler, not the live euler.
  // The lerp below smooths the visual catch-up so keyboard rotation
  // reads cinematic rather than 1:1. Inside an atmosphere the
  // rotation input feeds the local-horizon frame instead (same
  // reason as the mouse path: world-frame yaw reads as roll there).
  const horizonFlight = atmoEntry.immersion > 0.3 && atmoEntry.body;
  if (horizonFlight) {
    atmoLookPending.yaw += flyAngularVelocity.y;
    atmoLookPending.pitch += flyAngularVelocity.x;
  } else {
    flyTargetEuler.x += flyAngularVelocity.x;
    flyTargetEuler.y += flyAngularVelocity.y;
    flyTargetEuler.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, flyTargetEuler.x));
  }

  // Cinematic camera ease. flyEuler chases flyTargetEuler with an
  // exponential time constant of ~140ms — longer than the previous
  // 70ms so quick mouse flicks read as fluid sweeps rather than
  // raw 1:1 snaps. The frame-rate-independent exp form keeps the
  // effective time constant stable at any FPS.
  const cameraEase = 1.0 - Math.exp(-delta / 0.14);
  flyEuler.x += (flyTargetEuler.x - flyEuler.x) * cameraEase;
  flyEuler.y += (flyTargetEuler.y - flyEuler.y) * cameraEase;
  flyEuler.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, flyEuler.x));
  camera.quaternion.setFromEuler(flyEuler);

  // Horizon-locked orientation inside the atmosphere: consumes the
  // pending yaw/pitch, keeps "up" zenith-aligned (no roll), and
  // writes the result back through flyEuler for a continuous handoff
  // when leaving the envelope.
  if (horizonFlight) applyAtmosphericHorizonLook(delta);

  // Throttle HUD text updates to ~12 Hz. Per-frame textContent
  // assignment was triggering layout invalidations every frame and
  // contributing to the laggy feel — the fly loop physics ran fine
  // but the DOM work was eating frame time. The hudUnit ref is now
  // cached at module level instead of querySelector'd per frame.
  const now = performance.now();
  const shouldUpdateHud = (now - hudLastUpdate) > 80;
  if (shouldUpdateHud) hudLastUpdate = now;

  // Update HUD with propulsion-specific display
  if (hudSpeed && shouldUpdateHud) {
    let speedDisplay;

    if (currentPropulsion === 'warp') {
      // Warp factors (1-10 scale based on speed)
      const warpFactor = Math.min(10, currentSpeed / 30).toFixed(2);
      speedDisplay = `Warp ${warpFactor}`;
      if (hudUnit) hudUnit.textContent = '';
    } else {
      // Realistic units based on propulsion type
      // Scale: 1 scene unit = ~1000 km, display in km/s
      const kms = currentSpeed * 100; // Scaled for display (~km/s equivalent)

      if (currentPropulsion === 'chemical') {
        // Chemical rockets max ~11 km/s (Apollo-era)
        speedDisplay = Number(kms.toFixed(1)).toLocaleString();
        if (hudUnit) hudUnit.textContent = 'km/s';
      } else if (currentPropulsion === 'ion') {
        // Ion drives - very slow, show m/s for precision
        speedDisplay = Number((kms * 1000).toFixed(0)).toLocaleString();
        if (hudUnit) hudUnit.textContent = 'm/s';
      } else if (currentPropulsion === 'nuclear') {
        // Nuclear thermal - moderate speeds
        speedDisplay = Number(kms.toFixed(0)).toLocaleString();
        if (hudUnit) hudUnit.textContent = 'km/s';
      } else {
        // Fusion - high speeds
        speedDisplay = Number(kms.toFixed(0)).toLocaleString();
        if (hudUnit) hudUnit.textContent = 'km/s';
      }
    }
    hudSpeed.textContent = speedDisplay;
  }

  if (hudCoords && shouldUpdateHud) {
    // Coordinates relative to the active system's primary star: in a
    // remote system the world origin is Sol, hundreds of thousands of
    // units away, which read as "1998 AU from Sun" over a Sirius planet.
    let px = camera.position.x, py = camera.position.y, pz = camera.position.z;
    if (activeSystemId !== 'sol' && activeSystemTarget && activeSystemTarget.root) {
      px -= activeSystemTarget.root.position.x;
      py -= activeSystemTarget.root.position.y;
      pz -= activeSystemTarget.root.position.z;
    }
    const distFromStar = Math.hypot(px, py, pz);
    if (distFromStar > 1000) {
      const au = distFromStar / 150;
      hudCoords.textContent = `${au.toFixed(2)} AU from ${activeSystemId === 'sol' ? 'Sun' : 'star'}`;
    } else {
      hudCoords.textContent = `X: ${px.toFixed(0)} Y: ${py.toFixed(0)} Z: ${pz.toFixed(0)}`;
    }
  }
}

// Load all textures
const EAGER_TEXTURES = new Set([
  'sun',
  'mercury',
  'venus_atmosphere',
  'venus_surface',
  'earth',
  'earth_night',
  'earth_clouds',
  'earth_specular',
  'earth_ocean',
  'earth_normal',
  'earth_bump',
  'moon',
  'moon_bump',
  'mars_bump',
  'mercury_bump',
  'mars',
  'jupiter',
  'io',
  'europa',
  'europa_bump',
  'ganymede',
  'callisto',
  // Galilean moons get bumped anisotropy too (added in
  // earthDetailTextures-equivalent set below) so their close-up
  // sample stays sharp.
  'saturn',
  'saturn_ring',
  'uranus',
  'neptune',
  'pluto',
  'ceres'
]);

// Bump/height maps carry elevation data, not color — sRGB-decoding them
// crushes the values the shaders threshold against (e.g. Earth's 0.4
// shoreline cut), so every *_bump entry must stay linear.
const linearTextures = new Set([
  'earth_clouds', // density control data, not display colour
  'earth_normal',
  'earth_specular',
  'earth_ocean', // coverage, encoded depth and Kd490 (src/shaders/earthocean.js)
  'earth_bump',
  'moon_bump',
  'mars_bump',
  'mercury_bump',
  'europa_bump',
  'triton_normal',
  'triton_height',
  'ed138_normal',
  'ed138_height',
  'sedna_normal',
  'sedna_height',
  'eris_normal',
  'eris_height',
  'makemake_normal',
  'makemake_height',
  'haumea_normal',
  'haumea_height'
]);
const earthDetailTextures = new Set(['earth', 'earth_night', 'earth_clouds', 'earth_specular', 'earth_normal', 'earth_ocean']);

const SEAMLESS_TEXTURE_EDGE_FIXES = {
  jupiter: { width: 96 },
  // Earth takes no blend. earth.jpg already tiles: its first and last
  // columns differ by 4.41 levels, against 4.47 between neighbouring
  // columns, and RepeatWrapping (configureLoadedTexture) filters across the
  // edge, which is what keeps the 180 degree meridian seam-free. The 512 px
  // blend baked from the planet tuner in June mirrored each side onto the
  // other: 45% ghosting within 5 degrees of the date line and 12% at 17
  // degrees, Kamchatka showing faintly over western Alaska, and a day map
  // that no longer lined up with the ocean texture's coastline there.
  // earth:   { width: 512 },
  // Uranus' Solar System Scope source texture doesn't tile cleanly at
  // its meridian — at close zoom you see a one-to-two-pixel vertical
  // stripe where the left/right edges meet. The makeHorizontally-
  // SeamlessTexture pass blends `width` pixels across the seam so the
  // gradient continues smoothly. 64px is enough for Uranus' near-
  // uniform cyan surface; wider would start to smear actual subtle
  // banding without help.
  // Bumped 64 → 192 because the SSS Uranus texture has more than a
  // one-pixel meridian artifact — the entire ~100-pixel band at the
  // seam shifts in tone from the rest of the disc. 192 fully spans
  // that shift so the cyan reads uniform around the planet. Uranus
  // is the most featureless gas-giant disc, so a wide blend doesn't
  // smear any real banding.
  // Io's source is a crop rather than a full 360 degree map, so its two
  // edges sit ~44 levels apart in mean brightness even after trimming the
  // dead columns off the right. Measured edge delta was 53x the normal
  // column-to-column delta, by far the worst in the set. 128 px on a
  // 2466 px map is ~5% of the width, wide enough to ramp 44 levels
  // invisibly and narrow enough to leave the volcanic detail alone.
  io:      { width: 128 },
  // Small but measurable: Saturn's edges differ by ~3x the interior
  // delta. The disc is smooth banding, so a narrow blend costs nothing.
  saturn:  { width: 64 },
  uranus:  { width: 192 },
  // Neptune ships with the same seam from the SSS source. Bumped a
  // touch over Uranus because Neptune has more visible zonal banding
  // — a wider blend lets the bands continue across the seam without
  // a hard cut. Cloud-band features wider than 80px stay intact.
  neptune: { width: 80 }
};

function smoothstep01(t) {
  const x = THREE.MathUtils.clamp(t, 0, 1);
  return x * x * (3 - 2 * x);
}

// Cache of original loaded HTMLImageElements for textures that go
// through the seam-blend pass. Lets the localhost planet tuner
// re-blend at a new width at runtime without disposing the source.
const _planetSourceImages = {};

/**
 * Run the smoothstep-feathered seam blend on a source image at a
 * given width. Pure pixel math, no Three.js types. Returns a new
 * HTMLCanvasElement with the blended pixels ready to wrap in a
 * CanvasTexture. Extracted from makeHorizontallySeamlessTexture so
 * both load-time and live-tuner paths share the same algorithm.
 */
const seamDrawCopies = new WeakMap();
function buildSeamlessCanvasFromImage(image, seamWidth) {
  const width = image && (image.naturalWidth || image.videoWidth || image.width);
  const height = image && (image.naturalHeight || image.videoHeight || image.height);
  if (!image || !width || !height || typeof document === 'undefined') return null;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;

  // Drawn from the copy decoded off the main thread where the loader made
  // one (seamDrawCopies, loadTextureByName), the same pixels: from the <img>
  // the canvas decoded the file again here, 34 to 50 ms for a 4096 x 2048 map.
  const decoded = seamDrawCopies.get(image);
  ctx.drawImage(decoded || image, 0, 0, width, height);
  if (decoded) {
    seamDrawCopies.delete(image);
    decoded.close();
  }

  // seamWidth=0 means "no blend at all" — just return the raw image
  // canvas. Useful in the tuner for confirming "this is what it
  // looks like without help" without removing the texture from the
  // pipeline.
  if (seamWidth > 0) {
    // Only the two edge strips change, so only they are read and written
    // back: reading the whole 4096 x 2048 map held the main thread 60 ms.
    const featherWidth = Math.max(1, Math.min(seamWidth, Math.floor(width / 4)));
    const leftData = ctx.getImageData(0, 0, featherWidth, height);
    const rightData = ctx.getImageData(width - featherWidth, 0, featherWidth, height);
    const leftPx = leftData.data;
    const rightPx = rightData.data;

    for (let y = 0; y < height; y++) {
      const row = y * featherWidth;
      for (let x = 0; x < featherWidth; x++) {
        const left = (row + x) * 4;
        const right = (row + featherWidth - 1 - x) * 4;
        const edgeWeight = 1 - smoothstep01(x / Math.max(1, featherWidth - 1));

        for (let channel = 0; channel < 3; channel++) {
          const avg = (leftPx[left + channel] + rightPx[right + channel]) * 0.5;
          leftPx[left + channel] = Math.round(leftPx[left + channel] * (1 - edgeWeight) + avg * edgeWeight);
          rightPx[right + channel] = Math.round(rightPx[right + channel] * (1 - edgeWeight) + avg * edgeWeight);
        }
      }
    }

    ctx.putImageData(leftData, 0, 0);
    ctx.putImageData(rightData, width - featherWidth, 0);
  }
  return canvas;
}

function makeHorizontallySeamlessTexture(name, texture, seamWidth) {
  const image = texture && texture.image;
  if (!image) return texture;
  // Stash the source image so the localhost planet tuner can re-run
  // the blend at a different width without needing to re-fetch the
  // network asset. Texture.dispose() below frees the GPU side; the
  // HTMLImageElement stays alive in browser memory either way. A phone
  // keeps only the blended copy (the tuner is a desktop tool).
  if (!RUNNING_ON_MOBILE) _planetSourceImages[name] = image;

  try {
    const canvas = buildSeamlessCanvasFromImage(image, seamWidth);
    if (!canvas) return texture;

    const seamlessTexture = new THREE.CanvasTexture(canvas);
    seamlessTexture.name = `${name}-seamless`;
    seamlessTexture.flipY = texture.flipY;
    // NO mipmaps. On an equirectangular sphere any mip filtering puts a
    // visible meridian back on the disc: GPU mipmaps don't wrap across
    // the u=0/1 edge (a hard colour seam), and even a hand-built wrapped
    // chain needs the sampler's LOD clamped at the seam, which leaves a
    // faint vertical BLUR stripe there. Both read as a seam. Base-level
    // linear filtering is the only seam-free option, so we keep it and
    // lean on the shader unsharp mask for close-up crispness; distance
    // softness is the accepted tradeoff.
    seamlessTexture.generateMipmaps = false;
    seamlessTexture.minFilter = THREE.LinearFilter;
    seamlessTexture.magFilter = texture.magFilter;
    seamlessTexture.wrapS = texture.wrapS;
    seamlessTexture.wrapT = texture.wrapT;
    seamlessTexture.needsUpdate = true;
    texture.dispose();
    return seamlessTexture;
  } catch (err) {
    console.warn(`Unable to make ${name} texture seamless`, err);
    return texture;
  }
}

/**
 * Re-run the seam blend for a planet at runtime with a new width
 * and swap the freshly blended texture into the planet's surface
 * material. Used by the localhost planet tuner so the user can dial
 * the blend width live and copy the right value back into
 * SEAMLESS_TEXTURE_EDGE_FIXES once it looks correct.
 *
 * No-op if the source image wasn't cached at load time (i.e. the
 * planet didn't go through makeHorizontallySeamlessTexture
 * originally — in that case width=0 is the canonical state).
 */
function reblendPlanetSeam(planetKey, seamWidth) {
  const image = _planetSourceImages[planetKey];
  if (!image) {
    console.warn(`[planet-tuner] no cached source image for ${planetKey}, can't reblend`);
    return false;
  }
  const planet = planets[planetKey];
  if (!planet) {
    console.warn(`[planet-tuner] no planet group registered for ${planetKey}`);
    return false;
  }

  // Find the tagged surface mesh. Falls back to first SphereGeometry
  // mesh with a `.map` if the tag is missing (shouldn't happen, but
  // keeps legacy planets working without re-create).
  let surfaceMesh = null;
  planet.traverse((child) => {
    if (surfaceMesh) return;
    if (child.userData && child.userData.isPlanetSurface) surfaceMesh = child;
  });
  if (!surfaceMesh) {
    planet.traverse((child) => {
      if (surfaceMesh) return;
      if (!child.isMesh || !child.material || !child.material.map) return;
      if (child.name === 'atmosphere') return;
      if (child.geometry && child.geometry.type === 'SphereGeometry') {
        surfaceMesh = child;
      }
    });
  }
  if (!surfaceMesh) {
    console.warn(`[planet-tuner] couldn't locate surface mesh for ${planetKey}`);
    return false;
  }
  if (!surfaceMesh.material || !('map' in surfaceMesh.material)) {
    console.warn(`[planet-tuner] surface mesh for ${planetKey} has no map slot (ShaderMaterial?). Live re-blend not supported for this body without a shader refactor.`);
    return false;
  }

  const canvas = buildSeamlessCanvasFromImage(image, seamWidth);
  if (!canvas) {
    console.warn(`[planet-tuner] canvas build failed for ${planetKey}`);
    return false;
  }

  // Copy the configuration from the current map (whether it's the
  // load-time seamless texture or a previous reblend) so the new
  // texture keeps the same colorSpace / anisotropy / filtering. That
  // way the visible change really is only the seam blend width, not
  // a tone shift from a misconfigured texture.
  const reference = surfaceMesh.material.map;
  const newTexture = new THREE.CanvasTexture(canvas);
  newTexture.name = `${planetKey}-seamless-tuned-${seamWidth}`;
  newTexture.colorSpace = reference ? reference.colorSpace : THREE.SRGBColorSpace;
  newTexture.wrapS = reference ? reference.wrapS : THREE.RepeatWrapping;
  newTexture.wrapT = reference ? reference.wrapT : THREE.ClampToEdgeWrapping;
  newTexture.minFilter = reference ? reference.minFilter : THREE.LinearFilter;
  newTexture.magFilter = reference ? reference.magFilter : THREE.LinearFilter;
  newTexture.generateMipmaps = reference ? reference.generateMipmaps : false;
  newTexture.anisotropy = reference ? reference.anisotropy
    : (renderer && renderer.capabilities ? renderer.capabilities.getMaxAnisotropy() : 1);
  newTexture.flipY = reference ? reference.flipY : true;
  newTexture.needsUpdate = true;

  const oldMap = surfaceMesh.material.map;
  surfaceMesh.material.map = newTexture;
  surfaceMesh.material.needsUpdate = true;
  // Dispose the previous tuned map (not the shared loadedTextures
  // entry — that one stays alive so Reset can restore from it).
  if (oldMap && oldMap !== loadedTextures[planetKey]) {
    oldMap.dispose();
  }
  return true;
}

// Mobile GPU-memory guard. Equirectangular planet maps ship at up to
// 8192×4096, which decode to ~134 MB of RGBA each (file size on disk is
// irrelevant — it's the decoded surface that lands in GPU memory). The
// eager batch pulls ~28 of them, so the *retained* texture set alone
// exceeds 1 GB, far past iOS Safari's per-tab ceiling. The tab is
// OOM-killed before any JS — including the webglcontextlost handler —
// gets a chance to run, Safari auto-reloads, it crashes again, and the
// user sees "A problem repeatedly occurred." The reload-cap logic can't
// help because the process dies first. The real fix is to never hold
// that much: cap every texture's longest edge on phones so the whole
// eager set fits. 2048 keeps a planet crisp on a phone screen while
// shrinking an 8K map's footprint 16× (134 MB → 8.4 MB).
const MOBILE_TEXTURE_MAX_DIM = 2048;
// Phones load the 2048 copies from public/textures/mobile/ (applyMobileTextures),
// so this shrink is a safety net there. Off the focused system a phone holds
// each world's maps at 1024, a few dozen pixels across in the overview; the
// Sun, the Earth's maps, the rings and the focused system's maps keep 2048
// (sharpenMobileTextures on focus, evictUnfocusedTextures taking a system
// back down once it is left). Every world at 2048 was 130 MB of GPU memory
// (docs/mobile-memory.md).
const MOBILE_OVERVIEW_MAX_DIM = 1024;
let mobileFullTextures = new Set();
function mobileTextureCap(name) {
  const base = String(name).replace(/_(bump|normal|height)$/, '');
  if (name === 'sun' || name === 'saturn_ring' || name.startsWith('earth')
    || mobileFullTextures.has(name) || mobileFullTextures.has(base)) return MOBILE_TEXTURE_MAX_DIM;
  return MOBILE_OVERVIEW_MAX_DIM;
}

function downscaleTextureForMobile(texture, maxDim = MOBILE_TEXTURE_MAX_DIM) {
  if (!RUNNING_ON_MOBILE || !texture) return texture;
  const image = texture.image;
  if (!image || typeof document === 'undefined') return texture;
  const srcW = image.naturalWidth || image.videoWidth || image.width || 0;
  const srcH = image.naturalHeight || image.videoHeight || image.height || 0;
  if (!srcW || !srcH) return texture;
  const longest = Math.max(srcW, srcH);
  if (longest <= maxDim) return texture;

  const scale = maxDim / longest;
  const dstW = Math.max(1, Math.round(srcW * scale));
  const dstH = Math.max(1, Math.round(srcH * scale));
  try {
    const canvas = document.createElement('canvas');
    canvas.width = dstW;
    canvas.height = dstH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return texture;
    ctx.drawImage(image, 0, 0, dstW, dstH);
    // Swapping image → canvas drops the only reference to the full-res
    // decode so it can be GC'd; a canvas source keeps the same flipY /
    // colorSpace semantics as the original <img>, so nothing downstream
    // (seam blend, mipmaps) needs to change.
    texture.image = canvas;
    texture.needsUpdate = true;
    if (typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap && image.close) {
      image.close();
    }
  } catch (err) {
    console.warn('[textures] mobile downscale failed; keeping full-res', err);
  }
  return texture;
}

function configureLoadedTexture(name, texture) {
  // Shrink oversized maps before anything else touches them so the
  // seam blend works on the smaller canvas and only the capped texture
  // is ever uploaded to the GPU. No-op off mobile.
  const mobileCap = RUNNING_ON_MOBILE ? mobileTextureCap(name) : MOBILE_TEXTURE_MAX_DIM;
  const sourceWidth = RUNNING_ON_MOBILE && texture && texture.image
    ? (texture.image.naturalWidth || texture.image.width || 0) : 0;
  const sourceLongest = RUNNING_ON_MOBILE && texture && texture.image
    ? Math.max(sourceWidth, texture.image.naturalHeight || texture.image.height || 0) : 0;
  downscaleTextureForMobile(texture, mobileCap);
  // Stash the source image for every planet so the planet tuner can
  // re-blend a seam later even on bodies that don't ship with a
  // SEAMLESS_TEXTURE_EDGE_FIXES entry. Makes the tuner's seam-width
  // slider work uniformly across the planet picker.
  if (!RUNNING_ON_MOBILE && texture && texture.image && planetOrder.includes(name) && !_planetSourceImages[name]) {
    _planetSourceImages[name] = texture.image;
  }
  // A staged replacement that already tiles at its meridian
  // (NEXT_SEAMLESS, src/data/nextTextures.js) takes no blend, and keeps its
  // mipmaps, which the blend path drops.
  const stagedSeamless = NEXT_SEAMLESS.has(name) && String(textureUrls[name] || '').includes('/textures/next/');
  const edgeFix = stagedSeamless ? null : SEAMLESS_TEXTURE_EDGE_FIXES[name];
  // A map a phone shrank to the overview size keeps the share of its width
  // the blend had at 2048.
  const shrunk = RUNNING_ON_MOBILE && sourceLongest > mobileCap;
  const seamWidth = edgeFix && shrunk && texture.image.width
    ? Math.max(1, Math.round(edgeFix.width * texture.image.width / Math.min(sourceWidth, MOBILE_TEXTURE_MAX_DIM)))
    : edgeFix && edgeFix.width;
  const configuredTexture = edgeFix
    ? makeHorizontallySeamlessTexture(name, texture, seamWidth)
    : texture;
  // The size it holds: a map no bigger than the cap is already at its full.
  if (RUNNING_ON_MOBILE) configuredTexture.userData.mobileCap = shrunk ? mobileCap : MOBILE_TEXTURE_MAX_DIM;

  configuredTexture.colorSpace = linearTextures.has(name)
    ? THREE.NoColorSpace
    : THREE.SRGBColorSpace;
  // Rocketry's far sea reads depth and Kd490 from whichever water mask the
  // imagery stream holds, and only from this one.
  if (name === 'earth_ocean') configuredTexture.userData.earthOcean = true;

  // Equirectangular body maps have to wrap in U. Without this they keep
  // three.js's ClampToEdgeWrapping default, so at the 180 degree meridian
  // the sampler clamps to the edge texel instead of reaching around to
  // the opposite edge, and the last texel column never blends with the
  // first. That is a hard cut exactly on the seam, and anisotropic
  // filtering widens it into a visible streak at grazing angles. Only
  // Earth's maps were getting this, which is why every other body still
  // showed a meridian.
  //
  // wrapT deliberately stays clamped: V runs pole to pole, and wrapping
  // it would blend the north pole into the south.
  //
  // saturn_ring is a radial strip rather than an equirectangular map, so
  // it keeps whatever the ring code sets.
  if (name !== 'saturn_ring') {
    configuredTexture.wrapS = THREE.RepeatWrapping;
    configuredTexture.wrapT = THREE.ClampToEdgeWrapping;
  }

  // No mipmaps on equirectangular body maps. glGenerateMipmap does not
  // wrap: it box-filters each level with the edges clamped, so every
  // coarse level rebuilds a discontinuity at u=0/1 that the base level
  // does not have, and the meridian comes back as you pull away. This is
  // the same reasoning makeHorizontallySeamlessTexture already applies,
  // which is why the four blended planets were seam-free while mercury,
  // venus, mars and pluto were not: those four kept three.js's default
  // mip chain. Distance softness is the accepted tradeoff, and the
  // shader unsharp mask covers close-up crispness.
  //
  // Earth is deliberately exempt below: its maps are powers of two, so each
  // mip texel is the plain mean of the four under it and no level makes an
  // edge at u=0/1 that the base does not have, while RepeatWrapping filters
  // across the meridian at every level.
  if (name !== 'saturn_ring' && !earthDetailTextures.has(name)) {
    configuredTexture.generateMipmaps = false;
    configuredTexture.minFilter = THREE.LinearFilter;
  }

  if (earthDetailTextures.has(name)) {
    configuredTexture.wrapS = THREE.RepeatWrapping;
    configuredTexture.generateMipmaps = true;
    configuredTexture.minFilter = THREE.LinearMipmapLinearFilter;
    configuredTexture.magFilter = THREE.LinearFilter;
  }

  const anisotropyLimit = earthDetailTextures.has(name)
    ? renderer.capabilities.getMaxAnisotropy()
    : graphicsConfig.anisotropy;
  const maxAnisotropy = Math.min(
    renderer.capabilities.getMaxAnisotropy(),
    anisotropyLimit
  );
  if (maxAnisotropy > 1) configuredTexture.anisotropy = maxAnisotropy;

  return configuredTexture;
}

function getTextureTexelSize(texture, fallbackWidth = 2048, fallbackHeight = 1024) {
  const image = texture && texture.image;
  const width = image && (image.naturalWidth || image.videoWidth || image.width);
  const height = image && (image.naturalHeight || image.videoHeight || image.height);
  return new THREE.Vector2(
    1 / Math.max(1, width || fallbackWidth),
    1 / Math.max(1, height || fallbackHeight)
  );
}

function findLoadedTextureByUrl(url) {
  return Object.entries(textureUrls).find(([key, candidateUrl]) => (
    candidateUrl === url && loadedTextures[key]
  ));
}

// bands: a map the deferred stream sends in bands, downloaded once for the
// <img> and the GPU's bitmap alike (loadImage in src/bandupload.js).
function loadTextureByName(name, { onLoaded, onComplete, retry = 2, bands = false } = {}) {
  if (!name || !textureUrls[name]) return Promise.resolve(null);
  if (loadedTextures[name]) return Promise.resolve(loadedTextures[name]);
  if (textureLoadPromises[name]) return textureLoadPromises[name];
  const textureUrl = textureUrls[name];
  const existing = findLoadedTextureByUrl(textureUrl);
  if (existing) {
    loadedTextures[name] = loadedTextures[existing[0]];
    if (onLoaded) onLoaded(name, loadedTextures[name]);
    if (onComplete) onComplete(name, loadedTextures[name]);
    return Promise.resolve(loadedTextures[name]);
  }

  const urlPromise = textureUrlLoadPromises[textureUrl] || new Promise((resolve) => {
    let settled = false;
    let timeoutId = 0;
    const finish = (texture) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      resolve(texture);
    };
    timeoutId = setTimeout(() => {
      console.warn(`[textures] timed out loading ${textureUrl}`);
      finish(null);
    }, LOADING_TEXTURE_TIMEOUT_MS);
    if (bands && BAND_UPLOADS) {
      loadImage(textureUrl).then(async (image) => {
        if (SEAMLESS_TEXTURE_EDGE_FIXES[name]) {
          const decoded = await decodedCopy(image);
          if (decoded) seamDrawCopies.set(image, decoded);
        }
        const texture = new THREE.Texture(image);
        texture.needsUpdate = true;
        const configured = configureLoadedTexture(name, texture);
        // A copy the blend did not draw (a staged map that takes none).
        const unused = seamDrawCopies.get(image);
        if (unused) {
          seamDrawCopies.delete(image);
          unused.close();
        }
        // A seam-blended map is sent from its canvas, not the file.
        if (configured.image !== image) dropImageBytes(image);
        finish(configured);
      }, () => finish(null));
      return;
    }
    textureLoader.load(
      textureUrl,
      (texture) => {
        finish(configureLoadedTexture(name, texture));
      },
      undefined,
      () => {
        finish(null);
      }
    );
  }).finally(() => {
    delete textureUrlLoadPromises[textureUrl];
  });
  textureUrlLoadPromises[textureUrl] = urlPromise;
  textureLoadPromises[name] = urlPromise.then((texture) => {
    if (texture) {
      loadedTextures[name] = texture;
      if (onLoaded) onLoaded(name, texture);
      if (onComplete) onComplete(name, texture);
      return texture;
    }
    // Timed out or errored. Retry once or twice — eager textures
    // (moon, planet surfaces) failing here cause solid-colour
    // fallbacks that the user reads as "broken." Exponential-ish
    // backoff so a flaky CDN doesn't get hammered.
    if (retry > 0) {
      delete textureLoadPromises[name];
      return new Promise(resolve => {
        setTimeout(() => {
          loadTextureByName(name, { onLoaded, onComplete, retry: retry - 1, bands })
            .then(resolve);
        }, 1500);
      });
    }
    if (onComplete) onComplete(name, null);
    return null;
  }).finally(() => {
    // Only clear the in-flight tracker on terminal outcomes — when
    // we recurse for a retry we already cleared it above so the
    // recursive call can start fresh.
    if (loadedTextures[name] || retry === 0) {
      delete textureLoadPromises[name];
    }
  });

  return textureLoadPromises[name];
}

// Re-apply texture material to a moon group once its texture loads.
// Used by createMoon when the eager-load wasn't ready in time, so the
// moon doesn't get stuck at the solid-colour fallback forever.
function queueMoonTextureRetry(moonGroup, moonData) {
  const textureKey = moonData.texture;
  if (!textureKey || !textureUrls[textureKey]) return;
  const textureUrl = textureUrls[textureKey];
  const finalize = (tex) => {
    if (!tex) return;
    if (!moonGroup.parent) return;
    // The textured material takes the parent's shadow as the grey one did
    // (attachMoonSurface).
    applyDynamicSurfaceMaterial(moonGroup, moonData);
  };

  // First try the shared loader (it may already be loading or have
  // succeeded). If that returns null (failed/timed out earlier), fall
  // back to a direct TextureLoader hit on the file's URL so a single
  // flaky timeout in the eager batch doesn't permanently strand the
  // moon at a gray fallback.
  loadTextureByName(textureKey).then(async (tex) => {
    if (tex) {
      // A map that lands once the loading screen has gone goes to the GPU in
      // bands before the moon takes it (as the deferred stream's do); behind
      // the loading screen it is taken at once, so no moon opens grey.
      if (loadingScreenDismissed) await uploadInBands(renderer, tex, textureUrls[textureKey]);
      finalize(tex);
      return;
    }
    console.warn(`[moon] eager+retry load missed ${moonData.name} (${textureUrl}); direct retry`);
    const loader = textureLoader || new THREE.TextureLoader();
    loader.load(
      textureUrl,
      (rawTex) => {
        const configured = configureLoadedTexture(textureKey, rawTex);
        loadedTextures[textureKey] = configured;
        finalize(configured);
      },
      undefined,
      (err) => {
        console.warn(`[moon] direct retry failed for ${moonData.name}:`, err);
      }
    );
  }).catch(err => {
    console.warn(`[moon] texture retry failed for ${moonData.name}:`, err);
  });
}

// ---- Boot texture budget -------------------------------------------
//
// EAGER_TEXTURES above is the "never evict" keep-set that
// getActiveSystemTextureSet builds on. It is not the set the loading
// screen should wait for. Waiting on all of it means roughly 50 MB
// before the viewer is usable, which is over a minute of loading screen
// on a mid-range mobile connection, and mobile is most of the audience.
//
// The startup shot frames exactly one body (see STARTUP_FOCUS_IDS), so
// everything else is off-screen or a distant speck and can arrive while
// the user is already looking at the hero. The rest of the app already
// knows how to take a texture late: applyDynamicSurfaceMaterial rebuilds
// a body's material, and ensureFocusedDetail / scheduleDetailPrefetch
// pull detail in on focus. This just stops the loading screen from
// waiting on work nobody is looking at yet.
//
// Always in the boot set:
//   sun            lights the scene and is often in frame
//   earth          Earth's custom shader is only built when its day map
//                  is present at createPlanets time, and unlike every
//                  other body applyDynamicSurfaceMaterial refuses to
//                  rebuild it, so this one cannot be deferred
//   the smalls     under ~250 KB each; deferring them buys nothing
const BOOT_TEXTURE_ALWAYS = new Set([
  'sun', 'earth', 'earth_specular', 'saturn_ring',
  'uranus', 'neptune', 'europa', 'europa_bump', 'ganymede', 'callisto',
  'io', 'mercury_bump'
]);

// Earth's extra maps are all plain uniforms on its ShaderMaterial, so
// they can be bound after the fact without rebuilding the material or
// touching the shader. They only join the boot set when Earth is the
// hero body, because that is the one case where their absence would be
// visible in the opening frame.
const EARTH_DETAIL_TEXTURE_NAMES = ['earth_night', 'earth_clouds', 'earth_normal', 'earth_bump', 'earth_ocean'];

// earth_clouds.jpg is 8.8 MB, which is about a third of the whole
// boot budget on the Earth hero roll (60% of loads). bindLateEarthTexture
// can bind it afterwards and ramps cloudOpacity from zero over ~900 ms so
// it reads as a reveal rather than a pop.
//
// Left OFF because it changes the opening frame of the most common hero
// shot, and that is a look call rather than a performance one. Flipping
// this to true takes the Earth boot set from ~27 MB to ~17 MB. Worth
// trying on the dev server before deciding.
const DEFER_EARTH_CLOUDS_ON_HERO = false;

// Resolved once, before textures load, so the boot set can be built for
// the body we are actually going to open on. init() reuses the same
// value for frameStartupFocus so the roll cannot disagree with what was
// prioritised.
let bootFocusId = null;
function resolveBootFocusId() {
  if (bootFocusId) return bootFocusId;
  const hash = typeof getHashFocusId === 'function' ? getHashFocusId() : '';
  if (hash && (isStartupFocusId(hash) || celestialData[hash])) bootFocusId = hash;
  else bootFocusId = pickStartupFocusId();
  return bootFocusId;
}

function buildBootTextureSet() {
  // A live wallpaper showing one world waits only for that world and the
  // Sun; the rest of the always-set (the Earth's maps, the ice giants, the
  // Galilean moons) is never on screen there (wallpaperOneWorldTextures).
  const oneWorld = wallpaperOneWorldTextures();
  if (oneWorld) {
    const boot = new Set(['sun']);
    for (const n of oneWorld) {
      if (!textureUrls[n]) continue;
      if (DEFER_EARTH_CLOUDS_ON_HERO && n === 'earth_clouds') continue;
      boot.add(n);
    }
    return boot;
  }
  const focus = resolveBootFocusId();
  const boot = new Set(BOOT_TEXTURE_ALWAYS);
  const data = celestialData[focus];
  if (data) {
    const add = (n) => { if (n && textureUrls[n]) boot.add(n); };
    add(data.texture);
    if (data.moon) add(data.moon.texture);
    if (data.majorMoons) data.majorMoons.forEach((m) => add(m.texture));
    if (focus === 'saturn') add('saturn_ring');
    if (focus === 'mars') add('mars_bump');
    if (focus === 'mercury') add('mercury_bump');
    if (focus === 'earth') {
      EARTH_DETAIL_TEXTURE_NAMES.forEach((n) => {
        if (DEFER_EARTH_CLOUDS_ON_HERO && n === 'earth_clouds') return;
        add(n);
      });
    }
  }
  return boot;
}

// Deferred names, filled by loadTextures and drained once the loading
// screen has lifted.
let deferredTextureNames = [];

// Boot previews: on a desktop the loading screen waits for the phone copies
// of the boot set's big maps (scripts/build-mobile-textures.mjs, 2048 wide)
// and the full maps are swapped in once the viewer is up. On the Earth roll
// that is 5 MB of maps to wait for instead of 33 MB: at 20 Mbps the loading
// screen lifted at 21.6 s and lifts at 4.2 s, with every full map in by
// 14.6 s (23 Oct 2026). The full map goes into the same THREE.Texture, so
// every material and uniform holding it (the Earth's, the Moon's eclipse
// hook) keeps working with nothing rebuilt; only what was measured from
// the image's size is read again (refreshAfterBootPreview).
//
// The plain viewer only: the screensaver, wallpapers, videos and prints
// render from their first frame, and the app shells load from disk. The
// page's preload of the Sun and Earth maps (injectViewerPreloads in
// scripts/build-landing.js) makes the same choice: keep the two in step.
// Bodies with a baked seam blend (SEAMLESS_TEXTURE_EDGE_FIXES) boot full,
// since the blend is made from the image the texture is built on.
const BOOT_PREVIEWS = !RUNNING_ON_MOBILE && APP_MODE === 'default' && !IS_GAME
  && !STREAM_MODE && !IS_APP && !IS_QUEST_APP;
// The interactive viewer (and the game and desktop app) sends the maps that
// arrive while it runs in bands (src/bandupload.js): the boot previews' full
// maps, the deferred stream's, late moons', the imagery gain map and the
// debris models'. Phones keep the whole upload of their smaller maps
// and the extra decoded copy out of their memory; the screensaver, videos
// and prints keep theirs, where a map's arrival is waited on.
const BAND_UPLOADS = !RUNNING_ON_MOBILE && APP_MODE === 'default' && !STREAM_MODE && !IS_QUEST_APP;
// The same viewers load the music at start. A context made before any
// press is suspended (Chrome notes it in the console) and resumes on the
// first click; phones keep it for the press.
const MUSIC_AT_LOAD = BAND_UPLOADS;
const bootPreviewFullUrls = {};
// The names booted on a preview, and { name, at } per swap, for checks
// (window.__debug.bootPreviews).
const bootPreviewNames = [];
const bootPreviewLog = [];

// The swaps wait for a moment without input (at most BOOT_PREVIEW_MAX_WAIT_MS)
// so the upload of an 8K map lands between drags, not in one.
const BOOT_PREVIEW_QUIET_MS = 400;
const BOOT_PREVIEW_MAX_WAIT_MS = 4000;
// The last press, drag, wheel or key (the music start waits on it too).
let lastSceneInput = 0;
if (typeof window !== 'undefined') {
  const note = (e) => { if (e.type !== 'pointermove' || e.buttons) lastSceneInput = performance.now(); };
  for (const ev of ['pointerdown', 'pointermove', 'wheel', 'keydown', 'touchstart']) {
    window.addEventListener(ev, note, { passive: true, capture: true });
  }
}

function useBootPreviews(names) {
  if (!BOOT_PREVIEWS) return;
  for (const name of names) {
    if (SEAMLESS_TEXTURE_EDGE_FIXES[name]) continue;
    const full = textureUrls[name];
    const preview = mobileTextureUrl(full, true);
    if (preview === full) continue;
    bootPreviewFullUrls[name] = full;
    bootPreviewNames.push(name);
    textureUrls[name] = preview;
  }
}

function waitForQuietInput() {
  const started = performance.now();
  return new Promise((resolve) => {
    const check = () => {
      const now = performance.now();
      if (now - lastSceneInput >= BOOT_PREVIEW_QUIET_MS || now - started >= BOOT_PREVIEW_MAX_WAIT_MS) resolve();
      else setTimeout(check, 100);
    };
    check();
  });
}

// Resolves once nothing has been pressed, dragged or scrolled for
// STILL_SCENE_MS, no camera flight is running and the camera has stopped
// gliding round its target (by a thousandth of its distance in a check),
// or STILL_SCENE_MAX_MS after the last input whatever moves.
const STILL_SCENE_MS = 800;
const STILL_SCENE_MAX_MS = 3000;
function waitForStillScene() {
  const offset = new THREE.Vector3();
  const last = new THREE.Vector3(NaN, NaN, NaN);
  return new Promise((resolve) => {
    const check = () => {
      const quiet = performance.now() - lastSceneInput;
      let gliding = false;
      if (camera && controls) {
        offset.subVectors(camera.position, controls.target);
        gliding = !(offset.distanceTo(last) <= offset.length() * 1e-3);
        last.copy(offset);
      }
      if ((quiet >= STILL_SCENE_MS && !isAnimating && !gliding) || quiet >= STILL_SCENE_MAX_MS) resolve();
      else setTimeout(check, 100);
    };
    check();
  });
}

function refreshAfterBootPreview(name) {
  const texture = loadedTextures[name];
  if (planetOrder.includes(name)) _planetSourceImages[name] = texture.image;
  if (name === 'earth_bump') earthBumpSampler = null;
  if (!name.startsWith('earth')) return;
  const group = planets.earth;
  const mesh = group ? getBodySurfaceMesh(group) : null;
  const u = mesh && mesh.material && mesh.material.uniforms;
  if (!u) return;
  if (u.dayTexelSize) u.dayTexelSize.value = getTextureTexelSize(loadedTextures.earth);
  if (u.cloudTexelSize) u.cloudTexelSize.value = getTextureTexelSize(loadedTextures.earth_clouds || loadedTextures.earth);
  if (u.bumpTexelSize) u.bumpTexelSize.value = getTextureTexelSize(loadedTextures.earth_bump || loadedTextures.earth);
}

// The full map for one preview, decoded before it is handed to the texture
// (an undecoded 8K JPEG decodes inside the upload, on the frame that draws
// it). A failed load keeps the preview. It goes to the GPU in bands while the
// preview stays on screen (uploadInBands, asked for as each download lands,
// so the previews keep their place ahead of the deferred stream's maps);
// where that is not possible the swaps run one at a time, each in a quiet
// moment and a frame apart, so no frame uploads two 8K maps. The downloads
// do not wait for them.
let bootPreviewSwaps = Promise.resolve();
async function upgradeBootPreview(name) {
  const url = bootPreviewFullUrls[name];
  const texture = loadedTextures[name];
  delete bootPreviewFullUrls[name];
  if (!url || !texture) return;
  let image = null;
  for (let attempt = 0; attempt < 2 && !image; attempt++) {
    image = BAND_UPLOADS
      ? await loadImage(url).catch(() => null)
      : await textureLoader.loadAsync(url).then((full) => full.image, () => null);
  }
  if (!image) {
    console.warn(`[textures] kept the preview of ${name}: ${url} did not load`);
    return;
  }
  if (typeof image.decode === 'function') await image.decode().catch(() => {});
  const banded = uploadInBands(renderer, texture, url, image);
  bootPreviewSwaps = bootPreviewSwaps.then(async () => {
    const inBands = await banded;
    if (!inBands) {
      await waitForQuietInput();
      texture.dispose();
      texture.image = image;
      texture.needsUpdate = true;
    }
    textureUrls[name] = url;
    refreshAfterBootPreview(name);
    bootPreviewLog.push({ name, at: Math.round(performance.now()), bands: inBands });
    if (!inBands) await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  });
}

// The previews' full maps, before the deferred stream: they are what is on
// screen.
async function upgradeBootPreviews() {
  const names = Object.keys(bootPreviewFullUrls);
  if (!names.length) return;
  const concurrency = 3;
  let cursor = 0;
  const worker = async () => {
    while (cursor < names.length) await upgradeBootPreview(names[cursor++]);
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, names.length) }, worker));
}

// Earth is the one body applyDynamicSurfaceMaterial will not rebuild.
// Its maps are uniforms, so binding them late is a direct assignment.
// cloudOpacity ramps from zero so a late cloud layer reads as a soft
// reveal rather than a pop.
function bindLateEarthTexture(name, texture) {
  const group = planets.earth;
  const mesh = group ? getBodySurfaceMesh(group) : null;
  const u = mesh && mesh.material && mesh.material.uniforms;
  if (!u || !texture) return;
  if (name === 'earth_night' && u.nightTexture) {
    u.nightTexture.value = texture;
  } else if (name === 'earth_clouds' && u.cloudsTexture) {
    texture.wrapS = THREE.RepeatWrapping;
    texture.needsUpdate = true;
    if (u.weatherGuideTexture) u.weatherGuideTexture.value = texture;
    u.cloudsTexture.value = texture;
    if (u.cloudTexelSize) u.cloudTexelSize.value = getTextureTexelSize(texture);
    if (u.cloudOpacity) {
      const target = u.cloudOpacity.value;
      u.cloudOpacity.value = 0;
      const t0 = performance.now();
      const ramp = () => {
        const k = Math.min(1, (performance.now() - t0) / 900);
        u.cloudOpacity.value = target * k;
        if (k < 1) requestAnimationFrame(ramp);
      };
      requestAnimationFrame(ramp);
    }
  } else if (name === 'earth_normal' && u.normalTexture) {
    u.normalTexture.value = texture;
  } else if (name === 'earth_bump' && u.bumpTexture) {
    u.bumpTexture.value = texture;
    if (u.bumpTexelSize) u.bumpTexelSize.value = getTextureTexelSize(texture);
  } else if (name === 'earth_specular' && u.specularTexture) {
    u.specularTexture.value = texture;
  } else if (name === 'earth_ocean' && u.oceanTexture) {
    u.oceanTexture.value = texture;
    u.oceanTextureOn.value = 1;
  }
}

// Rebuild whatever was created before this texture existed. Bodies that
// were built with the solid-colour fallback pick up their surface here.
function upgradeBodiesForTexture(name) {
  if (name.startsWith('earth_')) {
    bindLateEarthTexture(name, loadedTextures[name]);
    return;
  }
  Object.entries(planets).forEach(([planetName, group]) => {
    const data = celestialData[planetName];
    if (!data) return;
    const usesIt = data.texture === name
      || (planetName === 'mars' && name === 'mars_bump')
      || (planetName === 'mercury' && name === 'mercury_bump');
    if (usesIt) applyDynamicSurfaceMaterial(group, data);
  });
  Object.values(moons).forEach((groups) => {
    groups.forEach((moonGroup) => {
      if (!moonGroup.userData || moonGroup.userData.isMinorMoon) return;
      // The Earth's Moon also takes its heightmap when that lands after
      // the moon was built (as the planets above do theirs): without it
      // the relief never attaches and the Moon shades flat.
      const usesIt = moonGroup.userData.texture === name
        || (name === 'moon_bump' && moonGroup.userData.texture === 'moon' && moonGroup.userData.name === 'Moon');
      if (usesIt) {
        applyDynamicSurfaceMaterial(moonGroup, moonGroup.userData);
      }
    });
  });
}

// Drains the deferred set after the loading screen is gone. Sequential
// on mobile for the same decode-peak reason the boot batch is, and
// deliberately unawaited by init: the whole point is that the user is
// already flying around while this finishes.
// ---- Background asset queue ----------------------------------------
//
// The two heaviest things on the page are the ones almost nobody
// navigates to: the ISS model (30.8 MB) and the deep-sky photo set
// (4.4 MB across 33 files). Both used to be fetched as part of every
// session regardless. They are now queued to start only once the
// deferred texture stream has drained, and each attaches to the scene
// the moment it arrives, so nothing needs reloading or re-focusing to
// see it.
//
// Anything the user actually navigates to jumps the queue via
// requestBackgroundAsset, so focusing the ISS fetches it right away
// rather than waiting its turn. Jobs run one at a time on idle so a
// 30 MB download never competes with what is on screen.
const backgroundAssetJobs = new Map();
let backgroundQueueStarted = false;

function registerBackgroundAsset(key, run) {
  if (!backgroundAssetJobs.has(key)) backgroundAssetJobs.set(key, { run, promise: null });
}

// Idempotent: the queue and a user navigation can both ask for the same
// asset, and the second caller joins the first one's promise.
function requestBackgroundAsset(key) {
  const job = backgroundAssetJobs.get(key);
  if (!job) return Promise.resolve();
  if (!job.promise) {
    job.promise = Promise.resolve().then(job.run).catch((err) => {
      console.warn('[assets] background asset failed:', key, err);
    });
  }
  return job.promise;
}

function onIdle(fn) {
  if (typeof requestIdleCallback === 'function') requestIdleCallback(fn, { timeout: 4000 });
  else setTimeout(fn, 400);
}

// A phone fetches these only when someone goes to them: the ISS model is
// 30.8 MB to download and about 40 MB of textures for a station that is
// sub-pixel from everywhere else (docs/mobile-memory.md).
const MOBILE_ON_DEMAND_ASSETS = new Set(['iss']);
// Every device fetches these only when someone goes near them (Hubble's and
// Webb's models, requestCraftModelWhenNear).
const ON_DEMAND_ASSETS = new Set(['hubble', 'jwst']);

// Every geometry, material and map under root, freed (ImageBitmaps closed).
function disposeObjectTree(root) {
  const owned = new Set();
  root.traverse((node) => {
    if (node.geometry) owned.add(node.geometry);
    const materials = !node.material ? [] : Array.isArray(node.material) ? node.material : [node.material];
    for (const material of materials) {
      owned.add(material);
      for (const value of Object.values(material)) if (value && value.isTexture) owned.add(value);
    }
  });
  for (const item of owned) {
    if (item.isTexture && item.image && typeof item.image.close === 'function') item.image.close();
    item.dispose();
  }
}

// A phone lets go of what a feature loaded once it has been left for
// MOBILE_RELEASE_AFTER_MS, and the feature loads it again on return: the ISS
// model and its maps (not focused, not tracked, not in the Cupola; the next
// focus asks for it through requestBackgroundAsset) and the black hole's sky
// cube (lensSkyCube makes it again when the camera comes back near a hole).
// Measured in Mobile Safari, the tracked ISS held about 200 MB of the tab
// for the rest of the session (docs/mobile-memory.md).
const MOBILE_RELEASE_AFTER_MS = 45000;
let mobileIssLeftAt = 0;
let mobileReleaseCheckAt = 0;
let skyCubeUsedAt = 0;
function updateMobileReleases(now) {
  if (now - mobileReleaseCheckAt < 1000) return;
  mobileReleaseCheckAt = now;
  const iss = satelliteObjects.find((s) => s.mesh && s.mesh.userData && s.mesh.userData.name === 'ISS');
  const group = iss && iss.mesh;
  if (group && group.userData.issModel) {
    const inUse = selectedObject === group || (cupolaView && cupolaView.active) || (window.__issTrack && window.__issTrack.selected);
    if (inUse) mobileIssLeftAt = 0;
    else if (!mobileIssLeftAt) mobileIssLeftAt = now;
    else if (now - mobileIssLeftAt > MOBILE_RELEASE_AFTER_MS) {
      const model = group.userData.issModel;
      group.remove(model);
      disposeObjectTree(model);
      group.userData.issModel = null;
      group.userData.assetLoaded = false;
      const job = backgroundAssetJobs.get('iss');
      if (job) job.promise = null;
      mobileIssLeftAt = 0;
    }
  }
  if (skyCube && now - skyCubeUsedAt > MOBILE_RELEASE_AFTER_MS) {
    skyCube.dispose();
    skyCube = null;
    skyCubeState = '';
  }
}

function startBackgroundAssetQueue() {
  if (backgroundQueueStarted) return;
  backgroundQueueStarted = true;
  const keys = [...backgroundAssetJobs.keys()].filter((key) => !ON_DEMAND_ASSETS.has(key) && !(RUNNING_ON_MOBILE && MOBILE_ON_DEMAND_ASSETS.has(key)));
  let i = 0;
  const next = () => {
    if (i >= keys.length) return;
    requestBackgroundAsset(keys[i++]).finally(() => onIdle(next));
  };
  onIdle(next);
}

// The live wallpaper's one-world shows (a planet show, or one curated shot of
// a planet) never show the other worlds, so their maps stay unloaded: each
// display runs its own page, and every map on every page added up to most
// of the 4 GB WebKit's GPU process held on a two-display desktop.
function wallpaperOneWorldTextures() {
  if (!(IS_SCREENSAVER && new URLSearchParams(window.location.search).get('wallpaper') === '1')) return null;
  const o = readScreensaverOptions(window.location.search);
  const id = o.shot ? o.shot.id : o.show === 'planet' ? o.body : null;
  const data = id && celestialData[id];
  if (!data) return null;
  const keep = new Set();
  const add = (n) => { if (n) keep.add(n); };
  add(data.texture);
  if (data.moon) add(data.moon.texture);
  if (data.majorMoons) data.majorMoons.forEach((m) => add(m.texture));
  for (const n of Object.keys(textureUrls)) if (n === id || n.startsWith(`${id}_`)) keep.add(n);
  if (id === 'earth') EARTH_DETAIL_TEXTURE_NAMES.forEach(add);
  return keep;
}

async function streamDeferredTextures() {
  await upgradeBootPreviews();
  if (!deferredTextureNames.length) return;
  const oneWorld = wallpaperOneWorldTextures();
  const names = oneWorld ? deferredTextureNames.filter((n) => oneWorld.has(n)) : deferredTextureNames;
  deferredTextureNames = [];
  const concurrency = RUNNING_ON_MOBILE ? 2 : 4;
  let cursor = 0;
  const worker = async () => {
    while (cursor < names.length) {
      const name = names[cursor++];
      const texture = await loadTextureByName(name, { bands: true });
      if (texture) {
        // On the GPU before a body draws it, a band a frame.
        await uploadInBands(renderer, texture, textureUrls[name]);
        try {
          upgradeBodiesForTexture(name);
        } catch (err) {
          console.warn('[textures] late upgrade failed for', name, err);
        }
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, names.length) }, worker));
  // Textures are what the user is looking at; the heavy optional assets
  // queue up behind them.
  startBackgroundAssetQueue();
}

async function loadTextures() {
  textureLoader = new THREE.TextureLoader();
  // Swap in the WebP encodes where the browser can decode them. This has
  // to settle before the first load starts, and it rewrites the shared
  // textureUrls map in place so every consumer (loadTextureByName,
  // findLoadedTextureByUrl, the moon retry path) agrees on the URL.
  // The probe never rejects; failing it just means JPEG.
  await detectWebpSupport();
  applyTextureFormat(textureUrls);
  applyMobileTextures(textureUrls);
  const eagerNames = Object.keys(textureUrls).filter((name) => EAGER_TEXTURES.has(name));
  const bootSet = buildBootTextureSet();
  // The world the page opens on is focused straight away, so a phone loads
  // its maps at the focused size from the start (mobileTextureCap).
  if (RUNNING_ON_MOBILE) {
    const focusData = celestialData[resolveBootFocusId()];
    if (focusData) {
      mobileFullTextures = new Set([focusData.texture, focusData.moon && focusData.moon.texture,
        ...(focusData.majorMoons || []).map((m) => m.texture)].filter(Boolean));
    }
  }
  const textureNames = eagerNames.filter((name) => bootSet.has(name));
  // Everything else in the eager set is still wanted, just not before
  // the user can see anything. streamDeferredTextures drains this once
  // init() has hidden the loading screen.
  deferredTextureNames = eagerNames.filter((name) => !bootSet.has(name));
  useBootPreviews(textureNames);
  let loaded = 0;
  const reportProgress = (name) => {
    loaded++;
    const progress = (loaded / textureNames.length) * 100;
    loadingBar.style.width = `${progress}%`;
    // The body, not the file: "saturn_ring" reads "Loading Saturn".
    const body = String(name).split(/[_-]/)[0];
    loadingText.textContent = `Loading ${body.charAt(0).toUpperCase()}${body.slice(1)}`;
  };

  if (RUNNING_ON_MOBILE) {
    // Even with the per-texture downscale, the *source* still decodes to
    // full size transiently before we shrink it. A fully parallel batch
    // would stack ~28 of those 134 MB decodes at once and still spike
    // past the iOS ceiling. A small concurrency window keeps the
    // transient peak bounded while the retained set stays capped.
    const MOBILE_DECODE_CONCURRENCY = 3;
    let cursor = 0;
    const worker = async () => {
      while (cursor < textureNames.length) {
        const name = textureNames[cursor++];
        await loadTextureByName(name, { onComplete: () => reportProgress(name) });
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(MOBILE_DECODE_CONCURRENCY, textureNames.length) }, worker)
    );
    return;
  }

  await Promise.all(textureNames.map((name) => loadTextureByName(name, {
    onComplete: () => reportProgress(name)
  })));
}

// The models near the Earth that the viewer's sky dome must not cover: the
// fly ship, the selected satellite and every Earth-orbiting satellite model
// (space probes far out are left to the scaled scene).
const _skyOccluderList = [];
function earthSkyOccluders() {
  _skyOccluderList.length = 0;
  if (flyMode && playerShip) _skyOccluderList.push(playerShip);
  if (selectedObject?.userData?.isSatellite && !_skyOccluderList.includes(selectedObject)) _skyOccluderList.push(selectedObject);
  for (const { mesh, isProbe } of satelliteObjects) {
    if (!isProbe && mesh && !mesh.userData.atL2 && !_skyOccluderList.includes(mesh)) _skyOccluderList.push(mesh);
  }
  return _skyOccluderList;
}

// How sunlit the view is near a body, for the sky's exposure: 1 within one
// radius of the surface with the Sun above the camera's horizon dip (the
// camera in daylight, looking at the sunlit ground), easing to 0 by 2.5
// radii, where the whole-disc view keeps its stars (the formula is rocketry
// sky.js's star suppression). The Earth always, and the focused planet or
// moon: a camera exposed for sunlit ground records few stars (every daylit
// ISS frame and every Apollo surface frame has a black sky).
const _starDimCentre = new THREE.Vector3();
const _starDimSun = new THREE.Vector3();
const _starDimUp = new THREE.Vector3();
function bodySunlitStarDim(body) {
  if (!body || !sun) return 0;
  const radius = getBodyBaseRadius(body);
  if (!(radius > 0)) return 0;
  body.getWorldPosition(_starDimCentre);
  _starDimUp.copy(camera.position).sub(_starDimCentre);
  const r = _starDimUp.length();
  const altitude = r / radius - 1;
  const gate = 1 - THREE.MathUtils.smoothstep(altitude, 1.0, 2.5);
  if (gate <= 0) return 0;
  _starDimUp.divideScalar(r);
  sun.getWorldPosition(_starDimSun).sub(_starDimCentre).normalize();
  const sunElev = _starDimUp.dot(_starDimSun);
  const dip = -Math.sqrt(Math.max(0, 1 - (radius / r) * (radius / r)));
  return gate * THREE.MathUtils.smoothstep(sunElev, dip - 0.05, dip + 0.1);
}
function sunlitStarDim() {
  if (rocketryActive || scaleModeActive || activeSystemId !== 'sol' || window.__starDayDimOff) return 0;
  let dim = bodySunlitStarDim(planets.earth);
  const focus = selectedObject;
  if (focus && focus !== planets.earth
    && (Object.values(planets).includes(focus) || Object.values(moons).some((list) => list.includes(focus)))) {
    dim = Math.max(dim, bodySunlitStarDim(focus));
  }
  return dim;
}

// The sky as drawn in a cube for the black holes' lensing
// (src/sky/skyCube.js): made the first time one is near, and once more when
// the faint stars arrive. The black holes are far out, so the cube takes the
// deep-space band and no zodiacal light.
let skyCube = null;
let skyCubeState = '';
// The supermassive hole's show ('deep') reads its stars two magnitudes
// deeper with the long exposure's band: its lensing shader lifts what reads
// as a star into the arcs and was tuned on the old panorama, whose stars were
// that bright, and its march fills the frame. The stellar hole ('true')
// reads the sky exactly as drawn, so its quad's edge does not show.
const LENS_SKY_STAR_GAIN = 6.3;
function lensSkyCube(near, look = 'true') {
  if (!near || !starfieldMaterial || !starCatalog) return null;
  skyCubeUsedAt = performance.now();
  // A phone's canvas is about a quarter of a desktop's pixel count, and the
  // 1024 cube in half float with its mips is 64 MB; 768 is 36 MB.
  if (!skyCube) skyCube = createSkyCube(renderer, scene, RUNNING_ON_MOBILE ? { size: 768 } : undefined);
  const deep = look === 'deep';
  const state = `${look}:${faintStars ? 2 : 1}:${milkyWayBand ? 1 : 0}`;
  if (state !== skyCubeState) {
    skyCubeState = state;
    const layers = [scene.getObjectByName('skybox'), starCatalog && starCatalog.mesh, faintStars && faintStars.mesh];
    skyCube.render(layers, (texelRad) => {
      const sky = starfieldMaterial.uniforms;
      const starUniforms = [starCatalog, faintStars].filter(Boolean).map((l) => l.mesh.material.uniforms);
      const saved = { alpha: sky.panoramaAlpha.value, exp: sky.skyExposure.value, gain: sky.milkyWayGain.value, sat: sky.milkyWaySaturation.value, dust: sky.dustBoost.value };
      const savedStars = starUniforms.map((u) => [u.opacity.value, u.exposure.value, u.uScintillation.value, u.pixelRatio.value]);
      sky.panoramaAlpha.value = 1; sky.skyExposure.value = SKY_EXPOSURE; sky.dustBoost.value = 0;
      // Far out in deep space: the band as the frame loop draws it there.
      sky.milkyWayGain.value = (deep || milkyWayBand ? SKY_MILKY_WAY_BAND_GAIN : SKY_MILKY_WAY_GAIN) * 2.5;
      sky.milkyWaySaturation.value = deep || milkyWayBand ? 1 : SKY_MILKY_WAY_SATURATION;
      // A star's sprite is CSS pixels: in the cube it should span the same
      // angle as on screen, so its pixel ratio is a screen pixel over a texel.
      const screenPx = THREE.MathUtils.degToRad(camera.fov) / Math.max(1, renderer.domElement.clientHeight || window.innerHeight);
      for (const u of starUniforms) { u.opacity.value = 1; u.exposure.value = SKY_EXPOSURE * (deep ? LENS_SKY_STAR_GAIN : 1); u.uScintillation.value = 0; u.pixelRatio.value = screenPx / texelRad; }
      return () => {
        sky.panoramaAlpha.value = saved.alpha; sky.skyExposure.value = saved.exp; sky.milkyWayGain.value = saved.gain; sky.milkyWaySaturation.value = saved.sat; sky.dustBoost.value = saved.dust;
        starUniforms.forEach((u, i) => { [u.opacity.value, u.exposure.value, u.uScintillation.value, u.pixelRatio.value] = savedStars[i]; });
      };
    });
  }
  return skyCube.texture;
}

// The sky's exposure at rest, as the linear peak of a magnitude 6 star
// (src/stars.js): 0.06 shows it as a small dim point, magnitude 10 at the
// edge of visibility and magnitude 0 clipping into the bloom. The frame loop
// stops it down for the Sun in frame or a sunlit Earth filling the view.
const SKY_EXPOSURE = 0.09;
// The display lift on the Milky Way's true brightness against the stars
// (createStarfield), and the reference pixel the calibration uses.
// 1 is the band's true brightness against the stars, faint beside a sunlit
// planet as a camera records it; the Milky Way switch (#milkyway-toggle)
// lifts it to the long exposure's band and dust.
const SKY_MILKY_WAY_GAIN = 1.0;
const SKY_MILKY_WAY_BAND_GAIN = 4.0;
// The eye sees faint light without colour, so the band at its true
// brightness is nearly grey; the long exposure keeps the colour.
const SKY_MILKY_WAY_SATURATION = 0.35;
let milkyWayBand = false;
let skyMapUpgradeAllowed = false;
let skyMapUpgrade = null;
// The 8K sky map, once, swapped in when it has loaded (createStarfield).
function upgradeSkyMap() {
  if (skyMapUpgrade || !skyMapUpgradeAllowed || !starfieldMaterial) return;
  skyMapUpgrade = loadDataMapTexture('/textures/sky/milkyway_8k.webp').then((texture) => {
    texture.colorSpace = THREE.NoColorSpace;
    texture.generateMipmaps = true;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
    const old = starfieldMaterial.uniforms.skyTexture.value;
    starfieldMaterial.uniforms.skyTexture.value = texture;
    wormholeStarMapTexture = texture;
    updateUfoStarReflectionTexture();
    if (old && old !== texture) old.dispose();
  }).catch((err) => console.warn('8K sky map failed to load:', err));
}
try { milkyWayBand = localStorage.getItem('ss.milkyWay') === '1'; } catch { /* storage blocked */ }
const SKY_REFERENCE_PIXEL_DEG2 = (50 / 900) ** 2;

// Create starfield background with cinematic space panorama
function createStarfield() {
  // Only Low (or an explicit Advanced override) gets the lite sky.
  // Modern mobile devices keep the 8K map so the scene does not look
  // downgraded before the user even touches a planet.
  const useLiteStarfield = graphicsConfig.starfield === 'lite'
    || (graphicsConfig.starfield === undefined && activeGraphicsPreset === 'low');
  // The sky map: NASA SVS Deep Star Maps 2020's Milky Way layer (every star
  // fainter than about 11.5, from Gaia DR2) with the Tycho-2 stars from 10
  // to 11.5 laid in, so the map and the point stars (src/stars.js) are the
  // whole sky once. Log-encoded linear light in ICRS, from
  // scripts/build-sky-background.py; milkyway.json has the decoding and the
  // calibration against the stars' magnitudes.
  // The 4K map (2.4 MB) first, for everyone: at its true brightness the
  // band is faint and the 4K holds it. The 8K (8.8 MB) comes in only where
  // the band is the picture: the Milky Way switch, deep space and the
  // screensaver (upgradeSkyMap).
  const mapUrl = '/textures/sky/milkyway_4k.webp';
  skyMapUpgradeAllowed = !useLiteStarfield;
  const metaPromise = fetch('/textures/sky/milkyway.json').then((r) => (r.ok ? r.json() : null)).catch(() => null);
  const skySegments = useLiteStarfield ? [64, 32] : [128, 64];

  const build = async (texture) => {
    const meta = await metaPromise;
    // Data, not colour: the shader decodes it.
    texture.colorSpace = THREE.NoColorSpace;
    texture.generateMipmaps = true;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    if (!useLiteStarfield) {
      texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
    }
    useSkyMap('icrs');
    wormholeStarMapTexture = texture;

    // The map's linear radiance L as the pixel value a star's light would
    // give: k L is the surface brightness in magnitude-0 fluxes per square
    // degree, a reference CSS pixel (50 degrees over 900) holds
    // SKY_REFERENCE_PIXEL_DEG2 of it, 10^2.4 makes that magnitude-6 fluxes,
    // and STAR_PSF_AREA is how much pixel value one unit of flux spreads over
    // in src/stars.js. So the band and the stars keep their true ratio before
    // the display curve.
    const k = meta && Number.isFinite(meta.radianceToMag0PerDeg2) ? meta.radianceToMag0PerDeg2 : 0;
    const L0 = meta && Number.isFinite(meta.L0) ? meta.L0 : 1e-6;
    const Lmax = meta && Number.isFinite(meta.Lmax) ? meta.Lmax : 1e-3;
    const skyGeometry = new THREE.SphereGeometry(40000000, skySegments[0], skySegments[1]);
    const skyMaterial = new THREE.ShaderMaterial({
      uniforms: {
        skyTexture: { value: texture },
        skyL0: { value: L0 },
        skyLogMax: { value: Math.log2(1 + Lmax / L0) },
        skyRadianceScale: { value: k * SKY_REFERENCE_PIXEL_DEG2 * Math.pow(10, 0.4 * STAR_REFERENCE_MAG) * STAR_PSF_AREA },
        // The frame loop's exposure, shared with the star layers.
        skyExposure: { value: SKY_EXPOSURE },
        // The display lift on the band: the true ratio to the stars reads
        // too faint on a screen, as it does in an unstretched photograph.
        milkyWayGain: { value: SKY_MILKY_WAY_GAIN },
        milkyWaySaturation: { value: SKY_MILKY_WAY_SATURATION },
        dustBoost: { value: 1.0 },
        // Cross-fade target. 1 keeps the inside-the-galaxy sky at full
        // strength; the galaxy module pulls this toward 0 as the camera
        // leaves the solar system so the exterior galaxy view can take over
        // without the two backdrops competing.
        panoramaAlpha: { value: 1.0 },
        // Camera→Sun world-space unit vector. Drives the Henyey-
        // Greenstein zodiacal phase function below so the forward
        // scattering peak actually points toward Sol from the
        // observer's instantaneous position and the gegenschein
        // appears opposite. Updated per-frame from the animate loop.
        sunDirection: { value: new THREE.Vector3(1, 0, 0) },
        uTime: { value: 0 },
        // Which way the map hangs on the sky (src/sky/frame.js).
        ...skyMapUniforms
      },
      vertexShader: `
        varying vec3 vWorldDir;
        void main() {
          vWorldDir = normalize(position);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D skyTexture;
        uniform float skyL0;
        uniform float skyLogMax;
        uniform float skyRadianceScale;
        uniform float skyExposure;
        uniform float milkyWayGain;
        uniform float milkyWaySaturation;
        uniform float dustBoost;
        uniform float panoramaAlpha;
        uniform vec3 sunDirection;
        varying vec3 vWorldDir;
        ${SKY_MAP_GLSL}

        // Two-term Henyey-Greenstein phase function. The single-term
        // HG analytic form is:
        //   P(θ) = (1 - g²) / (1 + g² - 2g·cos(θ))^(3/2)
        // with g > 0 forward-peaked, g < 0 back-peaked. Real
        // interplanetary dust shows a strong forward peak (g ≈ 0.6,
        // small particles preferentially forward-scatter sunlight)
        // PLUS a smaller backward peak (the gegenschein, opposition
        // brightening from coherent backscatter off dust grains).
        float hg(float cosTheta, float g) {
          float g2 = g * g;
          float denom = 1.0 + g2 - 2.0 * g * cosTheta;
          return (1.0 - g2) / pow(max(denom, 1e-4), 1.5);
        }

        void main() {
          // The map is read by direction, so it hangs on the real sky.
          vec3 s = skyMapSample(skyTexture, skyMapUv(vWorldDir)).rgb;
          vec3 L = skyL0 * (exp2(s * skyLogMax) - 1.0);
          // Linear, like the stars: the scene's tone mapping and bloom
          // treat the band and the stars as one picture.
          vec3 color = L * (skyRadianceScale * skyExposure * milkyWayGain);
          color = mix(vec3(dot(color, vec3(0.2126, 0.7152, 0.0722))), color, milkyWaySaturation);

          // Zodiacal light: confined to the ecliptic (interplanetary dust
          // is planar), brightest toward the Sun, dim at 90 degrees, with
          // the gegenschein opposite. g_forward 0.55 and g_back -0.30
          // after Lasue 2009 and Levasseur-Regourd 1996; 78/22 matches the
          // zodiacal-to-gegenschein ratio (about 5:1 at peak).
          float eclipticAlt = abs(vWorldDir.y);
          float eclipticBand = exp(-eclipticAlt * eclipticAlt * 28.0);
          float cosThetaSun = dot(vWorldDir, normalize(sunDirection));
          float phase = (0.78 * hg(cosThetaSun, 0.55) + 0.22 * hg(cosThetaSun, -0.30)) / 4.0;
          color += vec3(1.00, 0.94, 0.76) * eclipticBand * phase * 0.012 * dustBoost * skyExposure / ${SKY_EXPOSURE.toFixed(4)};

          // Cross-fade hand-off to the exterior galaxy view.
          color *= panoramaAlpha;
          gl_FragColor = vec4(color, 1.0);
        }
      `,
      side: THREE.BackSide,
      depthWrite: false
    });

    const skybox = new THREE.Mesh(skyGeometry, skyMaterial);
    skybox.renderOrder = -1000;
    skybox.name = 'skybox';
    scene.add(skybox);
    starfieldMaterial = skyMaterial;
    updateUfoStarReflectionTexture();

    // Real stars on the sky map: HYG to magnitude 8 at once, then Tycho-2
    // from 8 to 10 once the page is up (to 9 on the lite sky). Fainter stars
    // are in the map itself (src/stars.js).
    createStarCatalog(renderer).then((cat) => {
      starCatalog = cat;
      scene.add(cat.mesh);
    }).catch((err) => {
      console.warn('Star catalogue failed to load:', err);
    });
    const loadFaintStars = () => createFaintStarCatalog(renderer, { maxMag: useLiteStarfield ? 9 : 10 }).then((cat) => {
      faintStars = cat;
      scene.add(cat.mesh);
    }).catch((err) => {
      console.warn('Faint star catalogue failed to load:', err);
    });
    if (typeof requestIdleCallback === 'function') requestIdleCallback(loadFaintStars, { timeout: 4000 });
    else setTimeout(loadFaintStars, 2500);
    planetPoints = createPlanetPoints(renderer);
    scene.add(planetPoints.mesh);

    // Named deep-sky objects (Andromeda + bright nebulae + Magellanic
    // Clouds + galaxies + deep fields) at correct celestial
    // coordinates. Real public-domain photos from NASA/ESA/ESO drive
    // each sprite's appearance, cleaned for the sky by
    // scripts/build-dso-photos.mjs (public/textures/dso/clean/).
    //
    // Lazy loading: sprites render with the procedural shader path
    // immediately, and only fetch their photo when the user
    // first focuses the DSO via the objects menu. Saves the user
    // ~4.4MB of texture bandwidth they may never need.
    const DSO_PHOTOS = [
      { name: 'M31 Andromeda',     file: 'm31.webp' },
      { name: 'M42 Orion Nebula',  file: 'm42.webp' },
      { name: 'M45 Pleiades',      file: 'm45.webp' },
      { name: 'M8 Lagoon Nebula',  file: 'm8.webp'  },
      { name: 'M16 Eagle Nebula',  file: 'm16.webp' },
      { name: 'M20 Trifid Nebula', file: 'm20.webp' },
      { name: 'LMC',               file: 'lmc.webp' },
      { name: 'SMC',               file: 'smc.webp' },
      { name: 'M104 Sombrero',     file: 'm104.webp' },
      { name: 'NGC 7293 Helix',    file: 'ngc7293.webp' },
      { name: 'M1 Crab Nebula',    file: 'm1.webp' },
      { name: 'M51 Whirlpool',     file: 'm51.webp' },
      { name: 'M57 Ring Nebula',   file: 'm57.webp' },
      { name: 'Horsehead Nebula',  file: 'horsehead.webp' },
      { name: 'Veil Nebula',       file: 'veil.webp' },
      { name: 'Carina Nebula',     file: 'carina.webp' },
      { name: "NGC 6543 Cat's Eye", file: 'catseye.webp' },
      { name: "M81 Bode's Galaxy", file: 'm81.webp' },
      { name: 'M82 Cigar Galaxy',  file: 'm82.webp' },
      { name: 'M33 Triangulum',    file: 'm33.webp' },
      { name: 'M101 Pinwheel',     file: 'm101.webp' },
      { name: 'M27 Dumbbell',      file: 'm27.webp' },
      { name: 'Centaurus A',       file: 'cena.webp' },
      { name: 'M87',               file: 'm87.webp' },
      { name: 'NGC 6302 Butterfly', file: 'ngc6302.webp' },
      { name: 'Rosette Nebula',    file: 'rosette.webp' },
      { name: 'Hubble Ultra Deep Field', file: 'hudf.webp' },
      { name: 'Hubble Deep Field', file: 'hdf.webp' },
      { name: 'M64 Black Eye',     file: 'm64.webp' },
      { name: 'NGC 4565 Needle',   file: 'ngc4565.webp' },
      { name: 'M83 Southern Pinwheel', file: 'm83.webp' },
      { name: 'Antennae Galaxies', file: 'antennae.webp' },
      { name: 'NGC 1300 Barred Spiral', file: 'ngc1300.webp' }
    ];
    const dsoPhotoFiles = {};
    for (const entry of DSO_PHOTOS) dsoPhotoFiles[entry.name] = entry.file;
    // Shell radius must match the star catalogue (39.5M units).
    // A phone keeps each photo at 256 px in the sky (a degree is about 15 of
    // its pixels) and 1024 once focused: all 33 at their own size were 283 MB
    // of GPU memory there (docs/mobile-memory.md).
    deepSkyObjects = createDeepSkyObjects(39_500_000, {}, {
      photoFiles: dsoPhotoFiles, photoBaseUrl: '/textures/dso/clean/',
      ...(RUNNING_ON_MOBILE ? { photoMaxSize: 256, photoFocusSize: 1024 } : {})
    });
    scene.add(deepSkyObjects.group);

    // Background preload of every DSO photo. The procedural shader
    // fallback renders in the meantime; each photo replaces its
    // sprite as it arrives. Decoupled from focusOnDSO so the
    // celestial sphere fills out automatically without the user
    // having to click each DSO. Preserves the lazy-load
    // infrastructure (loadPhoto is idempotent via the photoLoaded
    // flag) but doesn't gate visual quality on user interaction.
    // 4.4 MB across 33 files. Queued rather than fired on a timer, so
    // it starts after the textures the user is actually looking at.
    // Each sprite swaps from its procedural fallback to the real photo
    // as that photo lands. focusOnDSO still calls loadPhoto directly,
    // and loadPhoto is idempotent, so clicking a DSO before the queue
    // reaches it fetches just that one immediately.
    registerBackgroundAsset('dso-photos', async () => {
      if (!deepSkyObjects) return;
      const sprites = deepSkyObjects.group.children.filter((sprite) => sprite.userData.loadPhoto);
      if (!RUNNING_ON_MOBILE) {
        sprites.forEach((sprite) => sprite.userData.loadPhoto());
        return;
      }
      // One at a time on a phone, so only one full decode exists at once.
      for (const sprite of sprites) await sprite.userData.loadPhoto();
    });

    // Classical constellations overlay. Off by default; toggled
    // from the celestial controls panel.
    constellations = createConstellations(39_500_000);
    scene.add(constellations.group);

    // Pulsar — a real 3D deep-space object positioned at the
    // celestial coordinates of the Crab Pulsar (PSR B0531+21,
    // RA 5h 34.5m, Dec +22°). Sits at a much closer distance
    // than the panorama so its 3D structure (lighthouse beams +
    // equatorial torus) is visible from solar-system viewpoints.

    // Reflections for metallic models: the sky as drawn (decoded, linear,
    // in its real place), rendered once into a cube. The map itself is
    // log-encoded data and would light them far too brightly. Stars are
    // left out (points in a small cube are blobs); the band and the
    // zodiacal light are what a reflection shows.
    // Its shaders are compiled and waited for first (src/gpuwarm.js), and it
    // takes the place of the stand-in set below, at the same size, so the
    // materials compiled against that one need nothing compiled again.
    const envScene = new THREE.Scene();
    envScene.add(new THREE.Mesh(skyGeometry, skyMaterial));
    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    try {
      scene.environment = (await pmremFromSceneAsync(renderer, pmremGenerator, envScene, 0.02, 1, 50_000_000)).texture;
      envStandIn.dispose();
    } catch (err) {
      console.warn('Sky reflections failed:', err);
    } finally {
      pmremGenerator.dispose();
    }
  };
  // Until the sky map is in, the metallic materials reflect a black map of
  // the size the sky's will be, so they compile with their env map before
  // the first frame: when the real one arrived after it, every MeshStandard
  // and MeshPhysical material on screen compiled again inside a frame.
  const envStandIn = pmremStandIn(renderer);
  scene.environment = envStandIn.texture;
  // Decoded off the main thread (src/bitmaptexture.js).
  loadDataMapTexture(mapUrl).then(build, (err) => {
    // The stars, lines and photos still come up on a black sky.
    console.warn('Sky map failed to load:', err);
    const black = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1);
    black.needsUpdate = true;
    build(black);
  });
}

// Create the Sun with outer glow
function createSun() {
  const sunGroup = buildSunBody();

  // Point light from sun
  const sunLight = new THREE.PointLight(0xfff7eb, 3.1, 0, 0);
  sunLight.castShadow = false;
  sunGroup.add(sunLight);

  // Ambient light. Pushed to 0.42 (was originally 0.058) so the dark
  // hemispheres of planets read with visible structure instead of
  // crushing to black under ACES tone mapping. Linear brightness on
  // the dark side now lands around 0.15-0.25 depending on planet
  // colorBoost, which ACES maps to a perceptually dark-gray that
  // still shows surface detail. Real interplanetary probe imagery
  // always shows the dark hemisphere with some reflected/scattered
  // light from the rest of the solar system; this approximates that
  // without needing per-body planet-shine simulation.
  const ambientLight = new THREE.AmbientLight(0x1a1d28, 0.42);
  scene.add(ambientLight);
  // Module-level handle so enter/exitFlyMode can dim and restore it
  // without a scene.traverse on every transition. The overview ambient
  // intensity is held here as the canonical resting value.
  _ambientLightRef = ambientLight;

  scene.add(sunGroup);
  sun = sunGroup;

  // Zodiacal light: faint dust-scattered band along the ecliptic,
  // brightening toward the sun, with the gegenschein backscatter
  // bump anti-solar. Per-frame intensity/visibility is driven from
  // animate() so overview shots and remote systems stay clean.
  zodiacalLightMesh = createZodiacalLight();
  scene.add(zodiacalLightMesh);
}

// The Sun as the viewer draws it: photosphere, corona shell, glow
// billboard and prominences, at the authored radius. createSun adds the
// light and registers it; the To-Scale lineup builds its Sun here too.
function buildSunBody() {
  const data = celestialData.sun;
  const sunGroup = new THREE.Group();
  sunGroup.name = 'sun';
  sunGroup.userData = { ...data, isPlanet: false, isSun: true };

  // Sun sphere with realistic shader
  const sunGeometry = new THREE.SphereGeometry(data.radius, graphicsConfig.sunSegments, graphicsConfig.sunSegments);

  let sunMaterial;
  if (loadedTextures.sun) {
    sunMaterial = new THREE.ShaderMaterial({
      uniforms: {
        sunTexture: { value: loadedTextures.sun },
        time: { value: 0 },
        realisticSun: { value: realisticSunMode ? 1 : 0 },
        octaveLod: { value: 1 },
        uSunDays: sunDaysUniform,
        uSunTint: sunTintUniform,
        uEarthAir: sunEarthAirUniform,
        ...sunLiveUniforms,
        ...sunFarFieldUniforms
      },
      vertexShader: sunVertexShader,
      fragmentShader: sunFragmentShader
    });
  } else {
    sunMaterial = new THREE.ShaderMaterial({
      uniforms: {
        sunTexture: { value: new THREE.DataTexture(new Uint8Array([255, 200, 100, 255]), 1, 1) },
        time: { value: 0 },
        realisticSun: { value: realisticSunMode ? 1 : 0 },
        octaveLod: { value: 1 },
        uSunDays: sunDaysUniform,
        uSunTint: sunTintUniform,
        uEarthAir: sunEarthAirUniform,
        ...sunLiveUniforms,
        ...sunFarFieldUniforms
      },
      vertexShader: sunVertexShader,
      fragmentShader: sunFragmentShader
    });
  }

  const sunMesh = new THREE.Mesh(sunGeometry, sunMaterial);
  applyAnalyticSphereRaycast(sunMesh, data.radius);
  sunGroup.add(sunMesh);

  // Add sun glow sprite for brightness at all distances
  // Keep the corona very close to the photosphere so it reads like a thin
  // solar corona, not a second atmosphere shell.
  const beaconGeometry = new THREE.SphereGeometry(
    data.radius * 1.008,
    Math.max(48, graphicsConfig.sunSegments),
    Math.max(32, Math.floor(graphicsConfig.sunSegments * 0.5))
  );
  const beaconMaterial = new THREE.ShaderMaterial({
    uniforms: {
      glowColor: { value: new THREE.Color(0xffffff) },
      glowStrength: { value: 0.035 },
      glowPower: { value: 6.4 }
    },
    vertexShader: `
      varying vec3 vNormal;
      varying vec3 vViewDir;
      void main() {
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        vNormal = normalize(normalMatrix * normal);
        vViewDir = normalize(-mvPosition.xyz);
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      uniform vec3 glowColor;
      uniform float glowStrength;
      uniform float glowPower;
      varying vec3 vNormal;
      varying vec3 vViewDir;
      void main() {
        float fresnel = pow(1.0 - max(dot(vNormal, vViewDir), 0.0), glowPower);
        float alpha = fresnel * glowStrength;
        gl_FragColor = vec4(glowColor, alpha);
      }
    `,
    transparent: true,
    side: THREE.BackSide,
    blending: THREE.AdditiveBlending,
    depthWrite: false
  });
  const sunBeacon = new THREE.Mesh(beaconGeometry, beaconMaterial);
  sunBeacon.name = 'sunBeacon';
  // Corona shell — the sun picks via sunMesh (analytic sphere test),
  // so the high-segment shell never needs triangle raycasting.
  sunBeacon.raycast = () => {};
  sunGroup.add(sunBeacon);

  // Always-visible sun glow billboard. The sun's mesh shrinks below a
  // pixel at outer-planet distances (especially in realistic mode at
  // ~50 000+ units from Saturn), where it'd otherwise be lost among
  // the ~41 k catalog stars on the skybox. The sprite is sized per-
  // frame so it always reads as a recognisable bright disc — at most
  // a few pixels wide near Earth, growing in scene units as the
  // camera retreats so its apparent radius never drops below the
  // floor we set in the animate loop. Additive blend + warm sun
  // colour so bloom catches it.
  const sunGlowTexture = createGlowTexture();
  const sunGlowMaterial = new THREE.SpriteMaterial({
    map: sunGlowTexture,
    color: 0xffffff,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    // depthTest stays ON so planets correctly occlude the sun glow.
    // The previous `depthTest: false` made the glow sprite render
    // through every body in front of the sun.
    depthTest: true,
    toneMapped: false,
    sizeAttenuation: true
  });
  const sunGlowSprite = new THREE.Sprite(sunGlowMaterial);
  sunGlowSprite.name = 'sunGlowSprite';
  sunGlowSprite.scale.setScalar(data.radius * 4);
  sunGlowSprite.renderOrder = 5;
  sunGroup.add(sunGlowSprite);

  sunGroup.add(createSunProminences(data.radius));
  return sunGroup;
}

// Solar prominences. Each region is a volume of plasma marched by
// sunProminenceFragmentShader inside a box in its own frame: solar radii,
// the Sun's centre at the origin, the base on +Y, the spine along +X and
// across it +Z. The mesh carries that frame (scaled by the Sun's radius,
// so artistic and realistic scale both work) and is the largest box the
// prominence ever fills; the shader marches the tighter current bounds.
// Three shapes from real H-alpha and eclipse imagery (see the shader for
// what each is): a quiescent hedgerow, a post-flare loop arcade and a
// twisted flux-rope arch, one of which erupts once per
// SUN_PROMINENCE_ERUPTION_PERIOD. Heights follow SDO and ground H-alpha
// limb images: the big quiescent curtain stands ~75,000 km (0.11 R),
// arcades and arches 0.05-0.09 R.
// shape per kind (solar radii, twist in radians foot to foot):
//   hedgerow [half length, height, half thickness, feet]
//   arcade   [footpoint half-span, height, loop thickness, half depth]
//   arch     [footpoint half-span, height, rope radius, twist]
// lifeRate/lifePhase drive each region's minutes-long brighten and fade.
const SUN_PROMINENCE_KINDS = { hedgerow: 0, arcade: 1, arch: 2 };
const SUN_PROMINENCE_REGIONS = [
  // The big quiescent curtain on three barbs that rewards close zoom.
  { kind: 'hedgerow', lat: 18, lon: 40, tilt: 1.35, shape: [0.17, 0.12, 0.0075, 3], seed: 0.37, intensity: 1.0, lifeRate: 0.011, lifePhase: 0.7 },
  { kind: 'arcade', lat: -24, lon: 118, tilt: 0.8, shape: [0.045, 0.065, 0.006, 0.035], seed: 0.61, intensity: 0.9, lifeRate: 0.021, lifePhase: 2.1 },
  { kind: 'arch', lat: 8, lon: 205, tilt: 1.15, shape: [0.065, 0.085, 0.02, 9], seed: 0.13, intensity: 0.95, lifeRate: 0.028, lifePhase: 4.0 },
  // Eruptive: lifts off the limb, swells, lets go of its legs and fades.
  { kind: 'arch', lat: -12, lon: 286, tilt: 1.3, shape: [0.075, 0.10, 0.02, 12], seed: 0.82, intensity: 0.92, lifeRate: 0.016, lifePhase: 1.2, eruptive: true },
  { kind: 'hedgerow', lat: 35, lon: 332, tilt: 0.9, shape: [0.06, 0.05, 0.005, 2], seed: 0.55, intensity: 0.8, lifeRate: 0.034, lifePhase: 5.3 }
];
// Seconds between eruptive lift-off events on the eruptive region.
const SUN_PROMINENCE_ERUPTION_PERIOD = 300;
// Optical depth per unit column (solar radii) and the source function's
// brightness: tau ~ 1-2 through a thread bundle, so cores saturate.
const SUN_PROMINENCE_OPACITY = 55;
const SUN_PROMINENCE_EMIT = 3.2;
const SUN_CHROMOSPHERE_EMIT = 0.85;
// sRGB: H-alpha crimson where the plasma is thin, eclipse pink where it
// is thick, pink-white in the densest cores.
const SUN_PROMINENCE_COLORS = { faint: 0xa8102c, mid: 0xff3354, hot: 0xffd0d8 };

function vectorFromSolarLatLon(latDeg, lonDeg) {
  const lat = THREE.MathUtils.degToRad(latDeg);
  const lon = THREE.MathUtils.degToRad(lonDeg);
  const cosLat = Math.cos(lat);
  return new THREE.Vector3(
    Math.cos(lon) * cosLat,
    Math.sin(lat),
    Math.sin(lon) * cosLat
  );
}

function getSolarSurfaceBasis(normal) {
  // Pick a reference axis that isn't nearly parallel to normal so the
  // cross product produces a stable tangent.
  const reference = Math.abs(normal.y) > 0.88
    ? new THREE.Vector3(1, 0, 0)
    : new THREE.Vector3(0, 1, 0);
  const tangent = new THREE.Vector3().crossVectors(reference, normal).normalize();
  const bitangent = new THREE.Vector3().crossVectors(normal, tangent).normalize();
  return { tangent, bitangent };
}

// The prominence's extent in its frame for a shape (Vector4) and lift,
// following the cut-offs in the shader's promDensity so the march never
// clips plasma. The shader works on the unit direction n and the height
// h; a point is n * (1 + h).
function sunProminenceBounds(kind, shape, lift, box) {
  let halfX;
  let halfZ;
  let top;
  if (kind === SUN_PROMINENCE_KINDS.hedgerow) {
    top = shape.y * 1.4;
    halfX = shape.x * 1.15;
    halfZ = 7.5 * shape.z + 0.17 * shape.y;
  } else if (kind === SUN_PROMINENCE_KINDS.arcade) {
    const grow = 1.4;
    top = shape.y * grow + 3.5 * shape.z;
    halfX = shape.x * grow + 3.5 * shape.z;
    halfZ = shape.w * 1.25;
  } else {
    const rope = shape.z * (1 + lift * 2.5);
    top = shape.y * (1 + lift * 3) + 2.8 * rope;
    halfX = shape.x * (1 + lift * 0.4) + 2.4 * rope;
    halfZ = 4 * rope;
  }
  const r = 1 + top;
  const pad = 0.002;
  box.min.set(
    -halfX * r - pad,
    Math.sqrt(Math.max(0, 1 - halfX * halfX - halfZ * halfZ)) - pad,
    -halfZ * r - pad
  );
  box.max.set(halfX * r + pad, r + pad, halfZ * r + pad);
  return box;
}

const _promCamWorld = new THREE.Vector3();
const _promInverse = new THREE.Matrix4();
const _promBufferSize = new THREE.Vector2();
const _promBox = new THREE.Box3();

// Per draw, with the camera actually rendering it: where that camera sits
// in the mesh's frame and how wide a pixel is (radians, so solar radii per
// solar radius of distance), which sets the thread detail the shader keeps.
function updateSunProminenceView(mesh, renderer, camera) {
  const uniforms = mesh.material.uniforms;
  _promCamWorld.setFromMatrixPosition(camera.matrixWorld);
  uniforms.uCamLocal.value.copy(_promCamWorld).applyMatrix4(_promInverse.copy(mesh.matrixWorld).invert());
  const target = renderer.getRenderTarget();
  const heightPx = target ? target.height : renderer.getDrawingBufferSize(_promBufferSize).y;
  const tanHalf = camera.isPerspectiveCamera
    ? Math.tan(THREE.MathUtils.degToRad(camera.fov) * 0.5) / camera.zoom
    : 0.5;
  uniforms.uPixelAngle.value = (2 * tanHalf) / Math.max(1, heightPx);

  const box = mesh.userData.proxyBox;
  if (!box) return;
  // From inside the box its front faces are behind the camera: draw the
  // back faces instead, without the depth test (the march stops at the
  // photosphere on its own).
  const inside = box.containsPoint(uniforms.uCamLocal.value);
  if (inside !== mesh.userData.cameraInside) {
    mesh.userData.cameraInside = inside;
    mesh.material.side = inside ? THREE.BackSide : THREE.FrontSide;
    mesh.material.depthTest = !inside;
    mesh.material.needsUpdate = true;
  }
}

function createSunProminences(radius) {
  const group = new THREE.Group();
  group.name = 'sunProminences';
  // Flag so applyRealisticBodyScales/resetArtisticBodyScales know to
  // scale this group with the sun.
  group.userData.sunScaleAware = true;
  group.visible = false;

  const steps = graphicsConfig.sunSegments >= 128 ? 48 : 32;
  const colors = {
    faint: new THREE.Color(SUN_PROMINENCE_COLORS.faint),
    mid: new THREE.Color(SUN_PROMINENCE_COLORS.mid),
    hot: new THREE.Color(SUN_PROMINENCE_COLORS.hot)
  };

  SUN_PROMINENCE_REGIONS.forEach((region) => {
    const kind = SUN_PROMINENCE_KINDS[region.kind];
    const shape = new THREE.Vector4(...region.shape);
    // The largest the prominence gets: breathing at its tallest, and an
    // eruption at full lift.
    const tallest = shape.clone().setY(shape.y * 1.03);
    const proxyBox = sunProminenceBounds(kind, tallest, region.eruptive ? 1 : 0, new THREE.Box3());
    const size = proxyBox.getSize(new THREE.Vector3());
    const centre = proxyBox.getCenter(new THREE.Vector3());
    const geometry = new THREE.BoxGeometry(size.x, size.y, size.z);
    geometry.translate(centre.x, centre.y, centre.z);
    const bounds = sunProminenceBounds(kind, shape, 0, new THREE.Box3());

    const material = new THREE.ShaderMaterial({
      defines: { PROM_KIND: kind, PROM_STEPS: steps },
      uniforms: {
        uTime: { value: 0 },
        uIntensity: { value: 0 },
        uLift: { value: 0 },
        uSeed: { value: region.seed },
        uOpacity: { value: SUN_PROMINENCE_OPACITY },
        uEmit: { value: SUN_PROMINENCE_EMIT },
        uPixelAngle: { value: 0.001 },
        uShape: { value: shape },
        uBoxMin: { value: bounds.min.clone() },
        uBoxMax: { value: bounds.max.clone() },
        uCamLocal: { value: new THREE.Vector3(0, 0, 10) },
        uFaintColor: { value: colors.faint },
        uMidColor: { value: colors.mid },
        uHotColor: { value: colors.hot }
      },
      vertexShader: sunProminenceVertexShader,
      fragmentShader: sunProminenceFragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      // Premultiplied: emission adds, alpha darkens what is behind (only
      // used in front of the disc).
      blending: THREE.CustomBlending,
      blendEquation: THREE.AddEquation,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneMinusSrcAlphaFactor,
      side: THREE.FrontSide,
      toneMapped: false
    });

    const mesh = new THREE.Mesh(geometry, material);
    const up = vectorFromSolarLatLon(region.lat, region.lon);
    const { tangent, bitangent } = getSolarSurfaceBasis(up);
    const spine = tangent.multiplyScalar(Math.cos(region.tilt))
      .addScaledVector(bitangent, Math.sin(region.tilt))
      .normalize();
    const across = new THREE.Vector3().crossVectors(spine, up);
    mesh.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(spine, up, across));
    mesh.scale.setScalar(radius);
    mesh.renderOrder = 6;
    mesh.raycast = () => {};
    mesh.userData.prominenceRegion = region;
    mesh.userData.prominenceKind = kind;
    mesh.userData.baseShape = shape.clone();
    mesh.userData.proxyBox = proxyBox;
    mesh.userData.cameraInside = false;
    mesh.onBeforeRender = (renderer, scene, camera) => updateSunProminenceView(mesh, renderer, camera);
    group.add(mesh);
  });

  // The chromosphere and its spicules just off the limb, which the
  // prominences' feet stand in.
  const chromosphere = new THREE.Mesh(
    new THREE.SphereGeometry(1.02, 96, 48),
    new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uIntensity: { value: 0 },
        uPixelAngle: { value: 0.001 },
        uCamLocal: { value: new THREE.Vector3(0, 0, 10) },
        uFaintColor: { value: colors.faint },
        uMidColor: { value: colors.mid }
      },
      vertexShader: sunProminenceVertexShader,
      fragmentShader: sunChromosphereFragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
      toneMapped: false
    })
  );
  chromosphere.name = 'sunChromosphere';
  chromosphere.scale.setScalar(radius);
  chromosphere.renderOrder = 6;
  chromosphere.raycast = () => {};
  chromosphere.onBeforeRender = (renderer, scene, camera) => updateSunProminenceView(chromosphere, renderer, camera);
  group.add(chromosphere);
  return group;
}

function updateSunProminences(elapsed, distFromSun) {
  const baseRadius = celestialData.sun.radius || 15;
  const renderedRadius = realisticScaleMode
    ? (getRealisticBodyRadius('sun') || baseRadius)
    : baseRadius;
  updateSunProminenceGroup(sun && sun.getObjectByName('sunProminences'), elapsed, distFromSun, renderedRadius);
}

// Animates one Sun's prominences for a camera `distFromSun` away from a
// photosphere drawn at `renderedRadius` (the viewer's Sun, or the
// To-Scale lineup's).
function updateSunProminenceGroup(group, elapsed, distFromSun, renderedRadius) {
  if (!group) return;

  // Full from 6 sun-radii in, gone by 14: they stand on the limb at the
  // click-to-focus distance (4 radii) and stay as small tufts on the
  // edge a little way out, where they cost a few hundred pixels.
  const closeRange = 1 - THREE.MathUtils.smoothstep(
    distFromSun,
    renderedRadius * 6,
    renderedRadius * 14
  );
  group.visible = closeRange > 0.01;
  if (!group.visible) return;

  group.children.forEach((mesh) => {
    const uniforms = mesh.material && mesh.material.uniforms;
    if (!uniforms) return;
    uniforms.uTime.value = elapsed;

    if (mesh.name === 'sunChromosphere') {
      // Inside the shell a grazing ray no longer means the limb. In white
      // light (the Realistic style) the chromosphere is far fainter than
      // the photosphere and lost in its glare (it shows at totality or
      // through an H-alpha filter), and drawn there it ringed the disc
      // with a pink line.
      mesh.visible = !realisticSunMode && distFromSun > renderedRadius * 1.025;
      uniforms.uIntensity.value = closeRange * SUN_CHROMOSPHERE_EMIT;
      return;
    }

    const region = mesh.userData.prominenceRegion;
    const kind = mesh.userData.prominenceKind;
    const base = mesh.userData.baseShape;
    if (!region) return;

    // Minutes-long life cycle: each region slowly brightens and decays
    // out of phase with its neighbours (lifeRate rad/s, periods 3-10 min).
    const life = 0.55 + 0.45 * Math.sin(elapsed * region.lifeRate + region.lifePhase);
    // A few percent of slow rise and settle.
    const breathe = 1 + 0.03 * Math.sin(elapsed * 0.05 + region.lifePhase * 2.0);
    let lift = 0;
    let fade = 1;

    if (region.eruptive) {
      // Eruption cycle: re-forms over ~30 s, stable, then from 72% of the
      // period the rope accelerates upward, swelling and letting go of
      // its legs, and fades as it leaves.
      const cycle = (elapsed % SUN_PROMINENCE_ERUPTION_PERIOD) / SUN_PROMINENCE_ERUPTION_PERIOD;
      const liftPhase = THREE.MathUtils.smoothstep(cycle, 0.72, 0.97);
      lift = liftPhase * liftPhase;
      fade = THREE.MathUtils.smoothstep(cycle, 0.02, 0.12)
        * (1 - THREE.MathUtils.smoothstep(liftPhase, 0.5, 1.0));
    }

    uniforms.uShape.value.set(base.x, base.y * breathe, base.z, base.w);
    uniforms.uLift.value = lift;
    const bounds = sunProminenceBounds(kind, uniforms.uShape.value, lift, _promBox);
    uniforms.uBoxMin.value.copy(bounds.min);
    uniforms.uBoxMax.value.copy(bounds.max);
    uniforms.uIntensity.value = closeRange * region.intensity * (0.35 + 0.65 * life) * fade;
  });
}

// Create radial gradient texture for sun glow
function createGlowTexture() {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  // A star's look rather than a soft ball: a hard white core and a
  // power-law wing, (1 + (r/a)^2)^-1.5 like the cinematic pass's glare,
  // taken to nothing at the edge.
  const image = ctx.createImageData(size, size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const r = Math.hypot(x + 0.5 - size / 2, y + 0.5 - size / 2) / (size / 2);
      const q = 1 + (r / 0.06) ** 2;
      const edge = 1 - THREE.MathUtils.smoothstep(r, 0.6, 1);
      const i = (y * size + x) * 4;
      image.data[i] = image.data[i + 1] = image.data[i + 2] = 255;
      image.data[i + 3] = Math.round(255 * Math.min(1, 1.6 / (q * Math.sqrt(q))) * edge);
    }
  }
  ctx.putImageData(image, 0, 0);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

function getSurfaceMaterialProfile(bodyData) {
  const key = (bodyData.texture || bodyData.name || '').toLowerCase();
  const type = (bodyData.type || '').toLowerCase();
  const icyBodies = new Set([
    'europa', 'enceladus', 'triton', 'charon', 'ganymede',
    'callisto', 'haumea', 'eris', 'makemake', 'ed138',
    'rhea', 'dione', 'miranda', 'ariel', 'umbriel', 'titania', 'oberon', 'nereid'
  ]);
  const albedoMaps = new Set([
    'rhea', 'dione', 'miranda', 'ariel', 'umbriel', 'titania', 'oberon', 'nereid',
    // Charon's map is fitted to MVIC's normal albedo (docs/charon-texture.md);
    // colors[0] (#a0a0a0) drew it at a third of that.
    'charon',
    // Triton's map is coloured from Voyager's photometry (docs/textures/
    // outer-moons.md); colors[0] (#b0c4de) turned its cream grey blue.
    'triton'
  ]);
  const hazyBodies = new Set(['venus_atmosphere', 'titan']);

  if (key === 'mercury') {
    return {
      materialType: THREE.MeshStandardMaterial,
      roughness: 0.86,
      metalness: 0.0,
      envMapIntensity: 0.32,
      colorBoost: 1.45,
      // MESSENGER-derived bump map drives normal-perturbation shading
      // only — no vertex displacement. Mercury's authored radius is 1.5
      // scene units, so the previous 0.06 displacementScale was ~4% of
      // radius, which combined with the high-frequency bump texture
      // produced visible spikes between adjacent geometry vertices.
      // Real Mercury topography is ~0.3% of radius (peaks vs mean
      // radius). The bump map at scale 0.09 carries the entire visual
      // load: Caloris basin, Rachmaninoff, Beethoven, and the densely-
      // cratered hemisphere all read as shaded relief without the
      // geometry distortion that was making the silhouette spikey.
      bumpScale: 0.09
    };
  }

  if (key === 'jupiter') {
    return {
      materialType: THREE.MeshPhysicalMaterial,
      roughness: 0.74,
      metalness: 0.0,
      // envMapIntensity dropped from 0.95 to 0.18 because the high
      // value was lighting the night-side hemisphere via the
      // starfield env reflections. Jupiter sits far from the sun
      // (5 AU) and its dark side really should be near-black, with
      // only the procedural lightning emissive and a faint
      // reflected-light from the moons reaching it. Saturn was
      // already at 0.4 for the same reason.
      envMapIntensity: 0.18,
      // Was tint-less (fell back to colors[0] = #d4a574) × colorBoost
      // 2.05, which multiplied the whole texture by a saturated warm
      // tan: it crushed the blue channel to ~36%, killed the bright
      // white zones, and over-warmed everything — that's why Jupiter
      // read dark and muddy. Like Mars/Pluto, neutralise the tint so the
      // texture's own (real, photo-derived) colours come through. The
      // tint is a warm CREAM rather than pure white so the bright zones
      // land as creamy bands instead of clipping to flat white ("light
      // bits too light"); colorBoost is low so ACES doesn't blow them.
      tint: 0xeadfca,
      colorBoost: 1.12,
      clearcoat: 0.08,
      clearcoatRoughness: 0.82
    };
  }

  if (key === 'saturn') {
    return {
      // Switched from MeshPhysicalMaterial to MeshStandardMaterial to
      // eliminate clearcoat as a possible source of the persistent
      // vertical streak artifact. Clearcoat's extra reflective pass
      // and its own texture lookups can surface mipmap / anisotropy
      // edge artifacts the standard material doesn't. envMapIntensity
      // also dropped to 0 to remove cubemap reflection contributions
      // from the equation entirely. If the streaks vanish, one of
      // these two passes was the culprit and we can selectively bring
      // back just the parts that don't artifact.
      materialType: THREE.MeshStandardMaterial,
      roughness: 0.85,
      metalness: 0.0,
      envMapIntensity: 0.0,
      colorBoost: 1.16
    };
  }

  if (key === 'mars') {
    return {
      materialType: THREE.MeshStandardMaterial,
      roughness: 0.84,
      metalness: 0.0,
      envMapIntensity: 0.16,
      colorBoost: 1.0,
      // Mars texture is pre-desaturated and channel-balanced in PIL
      // so the mean lands near #ad8f75 — the warm-tan albedo HiRISE
      // and Viking global mosaics show, not the brick-red of the
      // "Red Planet" myth. Shader-side tint stays neutral white;
      // attempting to fix the red in-shader only darkens channels
      // unevenly. Image-space rebalancing is the clean fix.
      tint: 0xffffff,
      // Mars MOLA-derived topography bump. MRO and Viking orbital
      // imagery shows Mars from space as essentially smooth — the
      // 22 km Olympus Mons rise is sub-pixel against the 3389 km
      // radius (0.65%). Earlier 0.10 bumpScale + 0.07 displacement
      // was 5× exaggerated and read as "intense / cartoony peaks"
      // rather than realistic regolith. Now 0.035 bumpScale gives
      // subtle terminator shading only (lit-side stays smooth like
      // the real photos), and displacement is removed — at typical
      // viewing distances the 0.65% real height is sub-pixel anyway,
      // so the geometry stays a clean sphere with bump-map shading
      // providing the topographic hint at low sun angles.
      bumpScale: 0.035
    };
  }

  if (key === 'pluto') {
    return {
      materialType: THREE.MeshStandardMaterial,
      roughness: 0.94,
      metalness: 0.0,
      // Pluto sits at ~40 AU and receives ~1/1600 the sunlight Earth
      // does, but it's actually a fairly bright body (New Horizons shows
      // pale tan/cream nitrogen-ice plains). Rendering it physically dim
      // made it read as "very dark" and unrecognisable, so colorBoost is
      // lifted toward unity to keep it legible — the SUN_IRRADIANCE
      // override already gives it a bright direct multiplier. Texture is
      // the New Horizons natural-colour mosaic with a synthesised
      // winter-shadow south pole.
      envMapIntensity: 0.05,
      colorBoost: 0.92,
      tint: 0xe8e0d8
    };
  }

  if (type.includes('gas giant') || type.includes('ice giant') || ['jupiter', 'saturn', 'uranus', 'neptune'].includes(key)) {
    return {
      materialType: THREE.MeshPhysicalMaterial,
      roughness: 0.9,
      metalness: 0.0,
      envMapIntensity: 0.32,
      colorBoost: 1.12,
      clearcoat: 0.14,
      clearcoatRoughness: 0.72
    };
  }

  if (hazyBodies.has(key)) {
    return {
      materialType: THREE.MeshPhysicalMaterial,
      // Titan's map is the haze's measured colour; without a tint the map
      // was multiplied by its UI colour (goldenrod, blue x 0.014), which
      // turned it brown. titanDeckTint() grades it like Cassini's releases.
      tint: key === 'titan' ? titanDeckTint() : undefined,
      roughness: 0.98,
      metalness: 0.0,
      envMapIntensity: 0.04,
      colorBoost: 1.08,
      clearcoat: 0.02,
      clearcoatRoughness: 0.95
    };
  }

  if (icyBodies.has(key)) {
    return {
      materialType: THREE.MeshStandardMaterial,
      roughness: 0.58,
      metalness: 0.0,
      envMapIntensity: 0.38,
      colorBoost: 1.1,
      // These maps carry the moon's measured albedo and colour
      // (docs/textures/saturn-uranus-moons.md), so they are drawn untinted
      // rather than through colors[0], which stood in for it on moon.jpg.
      tint: albedoMaps.has(key) ? 0xffffff : null
    };
  }

  if (key === 'moon' || type.includes('natural satellite')) {
    // Per-moon bump strength. Real Moon topographic relief is ~0.4%
    // of radius (peaks vs mean), so Apollo and LRO imagery shows the
    // disc as smoothly cratered at orbital distance — not the
    // scalloped silhouette an exaggerated bump produced. Bump now
    // does normal-perturbation shading ONLY (subtle at terminator,
    // smooth on the lit side); vertex displacement is removed
    // because at typical viewing distance the real 0.4% relief is
    // sub-pixel and the displaced sphere just reads as spikey.
    let bumpScale = 0;
    if (key === 'moon') bumpScale = 0.04;
    else if (key === 'europa') bumpScale = 0.05;
    else if (key === 'callisto') bumpScale = 0.06;
    else if (key === 'ganymede') bumpScale = 0.05;
    else if (key === 'io') bumpScale = 0.025;

    // Per-moon color boost. Callisto is the darkest Galilean
    // (albedo around 0.22), without a brightness lift the realistic
    // photometry plus the bump-induced micro-shadowing reads as
    // a near-black blob. Europa is the brightest (around 0.67).
    let colorBoost = 1.06;
    if (key === 'callisto') colorBoost = 1.55;
    else if (key === 'ganymede') colorBoost = 1.20;
    else if (key === 'europa') colorBoost = 1.15;

    return {
      materialType: THREE.MeshStandardMaterial,
      roughness: 0.92,
      metalness: 0.0,
      envMapIntensity: 0.1,
      colorBoost,
      bumpScale
    };
  }

  return {
    materialType: THREE.MeshStandardMaterial,
    roughness: 0.88,
    metalness: 0.0,
    envMapIntensity: 0.12,
    colorBoost: 1.08
  };
}

const SURFACE_VISIBILITY_BOOST = 1.03;

// Moons with baked relief maps (scripts/build-triton-relief.mjs), keyed by
// colour-texture key. nominalReliefM is what the maps were baked for (height
// 1 in the map); reliefM is what the viewer draws, exaggerated so the
// crevices read at viewer distances (Triton's real relief is a few hundred
// metres to about a kilometre).
const MOON_RELIEF = {
  triton: {
    normal: 'triton_normal',
    height: 'triton_height',
    radiusM: 1353400,
    nominalReliefM: 1000,
    reliefM: 2600
  },
  // The dwarf planets' relief is read from their artistic-rendition maps
  // (scripts/textures/build-dwarf-8k.mjs, docs/textures/dwarf-planets.md).
  // reliefM is set by eye at the terminator: the relief is a band-pass of
  // each map normalised to its own contrast, so a busy map (Sedna) or a
  // faint one (Eris' frost, whose fine grain the normalisation lifts) takes
  // less than its radius alone would give it.
  // 2026 ED138 sits on a mid-range 1,050 km disc.
  ed138: {
    normal: 'ed138_normal',
    height: 'ed138_height',
    radiusM: 525000,
    nominalReliefM: 1000,
    reliefM: 1100
  },
  sedna: {
    normal: 'sedna_normal',
    height: 'sedna_height',
    radiusM: 497500,
    nominalReliefM: 1000,
    reliefM: 850
  },
  eris: {
    normal: 'eris_normal',
    height: 'eris_height',
    radiusM: 1163000,
    nominalReliefM: 1000,
    reliefM: 800
  },
  makemake: {
    normal: 'makemake_normal',
    height: 'makemake_height',
    radiusM: 715000,
    nominalReliefM: 1000,
    reliefM: 900
  },
  // Haumea's maps are baked for its volume-equivalent radius; the mesh is
  // its triaxial shape (createDwarfPlanets), whose long ends meet the light
  // at grazing angles, so it takes the least.
  haumea: {
    normal: 'haumea_normal',
    height: 'haumea_height',
    radiusM: 797500,
    nominalReliefM: 1000,
    reliefM: 750
  }
};

function getMoonReliefTextureNames(textureKey) {
  const relief = MOON_RELIEF[textureKey];
  return relief ? [relief.normal, relief.height] : [];
}

// The Earth's Moon reads its colour through the streamed Kit tiles
// (updateMoonCloseView) where they are resident, and moon.jpg elsewhere and
// while a tile fades in. moon.jpg is the same Kit map scaled to the old
// texture's brightness: 1.564 times the tiles in linear light, a pure gain,
// pixel-aligned (measured on the whole map), so the tiles take the same
// scale and the handover has no seam.
const MOON_VIEW_TILE_GAIN = 1.564;
function applyMoonViewImagery(material, bodyData) {
  if (!material || !material.map || !bodyData || bodyData.texture !== 'moon' || bodyData.name !== 'Moon') return;
  const previousOnBeforeCompile = material.onBeforeCompile;
  material.onBeforeCompile = (shader, renderer) => {
    if (previousOnBeforeCompile) previousOnBeforeCompile(shader, renderer);
    Object.assign(shader.uniforms, moonImageryUniforms);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${MOON_IMAGERY_GLSL}`)
      .replace('#include <map_fragment>', `#ifdef USE_MAP
  vec4 sampledDiffuseColor = texture2D( map, vMapUv );
  sampledDiffuseColor.rgb = moonImagerySample(vMapUv, sampledDiffuseColor.rgb / ${MOON_VIEW_TILE_GAIN.toFixed(3)}).rgb * ${MOON_VIEW_TILE_GAIN.toFixed(3)};
  diffuseColor *= sampledDiffuseColor;
#endif`);
  };
  // Hooks added after this one wrap it, so key on the final chain and mark
  // the program as the Moon's own.
  material.customProgramCacheKey = () => `moon-view-imagery|${material.onBeforeCompile.toString()}`;
  material.needsUpdate = true;
}

// Relief for a moon's MeshStandardMaterial: the baked normals tilt the
// shading normal, and the height map is marched toward the Sun so ridges
// and crater walls cast shadows into the crevices beside them. The
// east/north frame is built from the moon's own pole, not from screen
// derivatives, so it is exact on an equirectangular SphereGeometry map
// (u east, v north) and has no seam. Both maps bind late: the material
// compiles once with them off and turns them on when they arrive.
function applyMoonRelief(material, bodyData) {
  const relief = MOON_RELIEF[(bodyData && bodyData.texture) || ''];
  if (!relief || !material || !material.map) return;

  const uniforms = {
    reliefNormalMap: { value: loadedTextures[relief.normal] || null },
    reliefHeightMap: { value: loadedTextures[relief.height] || null },
    reliefOn: { value: 0 },
    reliefSlopeScale: { value: relief.reliefM / relief.nominalReliefM },
    // Height 0..1 in the map spans 2 x reliefM; in radians of arc.
    reliefHeightRad: { value: (2 * relief.reliefM) / relief.radiusM },
    // One height-map texel, in radians of arc at the equator.
    reliefTexelRad: { value: (2 * Math.PI) / 2048 }
  };
  const sync = () => {
    uniforms.reliefNormalMap.value = loadedTextures[relief.normal] || uniforms.reliefNormalMap.value;
    uniforms.reliefHeightMap.value = loadedTextures[relief.height] || uniforms.reliefHeightMap.value;
    const heightImage = uniforms.reliefHeightMap.value && uniforms.reliefHeightMap.value.image;
    if (heightImage && heightImage.width) uniforms.reliefTexelRad.value = (2 * Math.PI) / heightImage.width;
    uniforms.reliefOn.value = uniforms.reliefNormalMap.value && uniforms.reliefHeightMap.value ? 1 : 0;
  };
  sync();
  if (!uniforms.reliefOn.value) {
    Promise.all([relief.normal, relief.height].map((name) => loadTextureByName(name))).then(sync);
  }
  material.userData.reliefUniforms = uniforms;

  const previousOnBeforeCompile = material.onBeforeCompile;
  material.onBeforeCompile = (shader) => {
    if (previousOnBeforeCompile) previousOnBeforeCompile(shader);
    Object.assign(shader.uniforms, uniforms);

    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\nvarying vec3 vReliefPole;`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>\nvReliefPole = normalize(normalMatrix * vec3(0.0, 1.0, 0.0));`);

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
uniform sampler2D reliefNormalMap;
uniform sampler2D reliefHeightMap;
uniform float reliefOn;
uniform float reliefSlopeScale;
uniform float reliefHeightRad;
uniform float reliefTexelRad;
varying vec3 vReliefPole;`
      )
      .replace(
        '#include <normal_fragment_maps>',
        `#include <normal_fragment_maps>
float reliefSun = 1.0;
if (reliefOn > 0.5) {
  // View-space frame on the smooth sphere: east is +u, north is +v.
  vec3 n0 = normalize(vNormal);
  vec3 pole = normalize(vReliefPole);
  vec3 east = cross(pole, n0);
  float cosLat = length(east);
  east = cosLat > 1e-4 ? east / cosLat : vec3(1.0, 0.0, 0.0);
  vec3 north = cross(n0, east);

  vec3 mapN = texture2D(reliefNormalMap, vMapUv).xyz * 2.0 - 1.0;
  normal = normalize(n0 * mapN.z + (east * mapN.x + north * mapN.y) * reliefSlopeScale);

  // Cast shadow: walk the height map toward the Sun (the scene origin) and
  // test the terrain against the ray leaving this point, which rises with
  // the Sun's elevation and with the sphere curving away beneath it.
  vec3 toSun = normalize(viewMatrix[3].xyz + vViewPosition);
  float sinE = dot(toSun, n0);
  vec2 along = vec2(dot(toSun, east), dot(toSun, north));
  float horiz = length(along);
  // Past the terminator the tilted normals would still face the Sun, but
  // only a peak can see it, and none beyond sqrt(2 x relief / radius) of
  // arc (about 3.5 degrees here): the night side stays night.
  if (sinE < -sqrt(reliefHeightRad)) {
    reliefSun = 0.0;
  } else if (sinE < 0.75 && horiz > 1e-4) {
    vec2 dir = along / horiz;
    float tanE = sinE / horiz;
    // UV per radian of arc along the surface.
    vec2 uvPerRad = vec2(0.15915494 / max(cosLat, 0.05), 0.31830989);
    float h0 = texture2D(reliefHeightMap, vMapUv).r;
    // First step about one texel; each step 1.5x the last, to ~70 texels.
    float t = reliefTexelRad * 0.9;
    float blocked = -1.0;
    for (int i = 0; i < 11; i++) {
      float h = texture2D(reliefHeightMap, vMapUv + dir * uvPerRad * t).r;
      float rise = (h - h0) * reliefHeightRad - t * tanE - 0.5 * t * t;
      blocked = max(blocked, rise / (reliefHeightRad * 0.10 + t * 0.012));
      t *= 1.5;
    }
    // Grazing light only: by 45 degrees up the short shadows are lost in
    // the map's own shading.
    float shadow = smoothstep(0.0, 1.0, blocked) * (1.0 - smoothstep(0.45, 0.75, sinE));
    reliefSun = 1.0 - shadow;
  }
}`
      )
      .replace(
        '#include <lights_fragment_end>',
        `#include <lights_fragment_end>
reflectedLight.directDiffuse *= reliefSun;
reflectedLight.directSpecular *= reliefSun;`
      );
  };
  // Every moon's onBeforeCompile chain stringifies the same, so key the
  // program on the relief too or it could be shared with a moon without it.
  material.customProgramCacheKey = () => `moon-relief|${material.onBeforeCompile.toString()}`;
}

function createSurfaceMaterial(bodyData, options = {}) {
  const profile = getSurfaceMaterialProfile(bodyData);
  const {
    texture = null,
    color = profile.tint != null
      ? profile.tint
      : (bodyData.colors ? bodyData.colors[0] : 0xaaaaaa),
    bumpMap = null
  } = options;
  const MaterialType = profile.materialType;

  const params = {
    map: texture,
    color: new THREE.Color(color).multiplyScalar((profile.colorBoost || 1) * SURFACE_VISIBILITY_BOOST),
    roughness: profile.roughness,
    metalness: profile.metalness,
    envMapIntensity: profile.envMapIntensity,
    clearcoat: profile.clearcoat || 0,
    clearcoatRoughness: profile.clearcoatRoughness || 0
  };

  // Optional bump map. Only attached when both the texture and a
  // bumpScale > 0 are present, so bodies without bump data fall back
  // to flat-shaded textures cleanly. The Moon profile sets bumpScale.
  if (bumpMap && profile.bumpScale > 0) {
    params.bumpMap = bumpMap;
    params.bumpScale = profile.bumpScale;
    // Re-use the bump map as a displacement source. With high enough
    // geometry segments the silhouette gets actual physical bumps at
    // close zoom — crater rims pop above the sphere surface and crater
    // floors recess into it. displacementBias compensates for the
    // bump map's average height so the mesh stays centred on its
    // authored radius (otherwise the whole sphere bloats outward).
    if (profile.displacementScale > 0) {
      params.displacementMap = bumpMap;
      params.displacementScale = profile.displacementScale;
      params.displacementBias = profile.displacementBias || 0;
    }
  }

  const material = new MaterialType(params);
  applyMoonRelief(material, bodyData);
  applyMoonViewImagery(material, bodyData);
  return material;
}

const GAS_GIANT_BAND_SETTINGS = {
  jupiter: {
    // Differential rotation off, as on Saturn: shifting each latitude at
    // its own rate sheared the Great Red Spot and the other ovals into
    // slanted smears. The texture now turns with the planet as one piece
    // until the storms get a model of their own.
    rate: 0,
    seamBlendWidth: 0,
    seamBlendStrength: 0,
    // Animated Great Red Spot — gentle ~30s breathing pulse + slow
    // inner swirl. Centre tuned to where the GRS sits in the SSS
    // Jupiter texture. Real GRS is at ~22°S latitude (uv.y ≈ 0.378)
    // and longitude varies; we anchor it in pre-shift UV so it
    // tracks the differential rotation drift.
    redSpotCenterUV: { x: 0.83, y: 0.395 },
    redSpotRadiusUV: { x: 0.06, y: 0.045 },
    redSpotStrength: 0.55,
    redSpotTint: { r: 0.85, g: 0.42, b: 0.32 },
    // Procedural close-up detail. Jupiter benefits most from this
    // because the camera goes closest to it (large body, prominent
    // close-up shots) and the 4K texture pixelates earliest.
    detailStrength: 1.0,
    // Sharpen (unsharp mask) so the bands and GRS rim read crisp — the
    // 4K base goes soft once Jupiter fills the frame. Vibrance is kept
    // deliberately mild: the real lift comes from neutralising the tint
    // (so the texture's true colours and white zones show) rather than
    // pushing saturation, which reads fake. These are the only finishing
    // touches on top of that.
    sharpen: 0.72,
    saturation: 1.06,
    contrast: 0.10,
    // Extra film grain on Jupiter's disc, on top of the global cinematic
    // grain (0.13) — reads a touch grainier, like a higher-ISO frame.
    grain: 0.08
  },
  saturn: {
    // Differential rotation disabled. The latitude-dependent UV
    // shift was creating visible seams wherever its fract() wrap
    // intersected the visible disc. Saturn's bands move slowly
    // enough in real life that a static texture is acceptable;
    // re-enable with a much smaller rate if motion is desired
    // and the seam handling is rebuilt to be invisible.
    rate: 0,
    // Wider seam-blend window (0.06 vs 0.03 default) so the
    // meridian wrap is fully masked even at close zoom — the
    // narrower window was letting a faint vertical band show
    // through when the camera was right up against the disc.
    // Strength stays high so the symmetric mirror sample fully
    // dissolves the boundary.
    seamBlendWidth: 0.06,
    seamBlendStrength: 0.9,
    // Procedural detail still disabled. The high-frequency fbm at
    // V-scale 250 aliases into visible vertical streak patterns.
    detailStrength: 0
  },
  // Ice giants — Uranus and Neptune. Real ice giants have subtle
  // horizontal banding visible in Voyager/Hubble imagery (Neptune
  // even has named features like the Great Dark Spot). The base
  // textures we have are nearly featureless, so we use the same
  // differential-rotation hook to drift any latitude-based detail
  // they DO have. Rates are much smaller than Jupiter/Saturn since
  // these planets rotate ~16-17 hours and have less visible motion.
  uranus: {
    rate: 0.0006,
    seamBlendWidth: 0.02,
    seamBlendStrength: 0.7,
    // Ice giants are mostly featureless — turn detail up so close-up
    // shots show the subtle Voyager-era banding instead of a flat
    // pastel sphere.
    detailStrength: 0.9
  },
  neptune: {
    rate: 0.0010,
    seamBlendWidth: 0.02,
    seamBlendStrength: 0.7,
    detailStrength: 0.9
  }
};

function applyGasGiantBandsForTexture(material, texture, textureName) {
  const settings = GAS_GIANT_BAND_SETTINGS[textureName];
  if (!settings || !texture) return;
  const opts = {
    seamBlendWidth: settings.seamBlendWidth,
    seamBlendStrength: settings.seamBlendStrength
  };
  if (settings.redSpotStrength) {
    opts.redSpotCenterUV = new THREE.Vector2(settings.redSpotCenterUV.x, settings.redSpotCenterUV.y);
    opts.redSpotRadiusUV = new THREE.Vector2(settings.redSpotRadiusUV.x, settings.redSpotRadiusUV.y);
    opts.redSpotStrength = settings.redSpotStrength;
    opts.redSpotTint    = new THREE.Color(settings.redSpotTint.r, settings.redSpotTint.g, settings.redSpotTint.b);
  }
  if (settings.detailStrength) {
    opts.detailStrength = settings.detailStrength;
    // Body radius — used to scale the detail fade-in distance so
    // each gas giant gets the same near/far behaviour proportional
    // to its size. celestialData is the source of truth.
    const bodyKey = textureName;
    const bodyRadius = celestialData[bodyKey] ? celestialData[bodyKey].radius : 8.0;
    opts.bodyRadius = bodyRadius;
  }
  // Image-space sharpen + vibrance + extra grain (opt-in per body).
  if (settings.sharpen)    opts.sharpen    = settings.sharpen;
  if (settings.saturation) opts.saturation = settings.saturation;
  if (settings.contrast)   opts.contrast   = settings.contrast;
  if (settings.grain)      opts.grain      = settings.grain;
  applyGasGiantBands(material, texture, settings.rate, opts);
}

const MAX_MOON_TRANSIT_SHADOWS = 8;

// The shadows' axis runs from the Sun (moonShadowSun, which the origin shift
// moves with everything else) through each moon. At true scale
// (moonShadowSunRadius > 0) the radii are the moons' own and the shadow is
// the real one: black inside the umbra, the penumbra as wide as the Sun's
// disc makes it at that distance (Io's on Jupiter: about 1,660 km of umbra in
// 1,980 km of penumbra), and past the umbra's tip (Phobos and Deimos on Mars,
// whose discs are smaller than the Sun's there) no more than the moon's disc
// hides of the Sun's. The artistic scale keeps its soft grey disc.
function createMoonTransitShadowUniforms() {
  return {
    count: { value: 0 },
    positions: { value: Array.from({ length: MAX_MOON_TRANSIT_SHADOWS }, () => new THREE.Vector3()) },
    radii: { value: Array.from({ length: MAX_MOON_TRANSIT_SHADOWS }, () => 0) },
    sun: { value: new THREE.Vector3() },
    sunRadius: { value: 0 },
  };
}

function registerMoonTransitShadowUniforms(material, planetGroup, shadowUniforms) {
  material.userData.moonShadowUniforms = shadowUniforms;
  planetGroup.userData.moonShadowUniforms = shadowUniforms;
}

function attachMoonTransitShadows(material, planetGroup, bodyData) {
  if (!material || !bodyData || !(bodyData.moon || bodyData.majorMoons)) return;

  const shadowUniforms = createMoonTransitShadowUniforms();

  if (material.isShaderMaterial) {
    material.uniforms.moonShadowCount = shadowUniforms.count;
    material.uniforms.moonShadowPositions = shadowUniforms.positions;
    material.uniforms.moonShadowRadii = shadowUniforms.radii;
    material.uniforms.moonShadowSun = shadowUniforms.sun;
    registerMoonTransitShadowUniforms(material, planetGroup, shadowUniforms);
    return;
  }

  const previousOnBeforeCompile = material.onBeforeCompile;

  material.onBeforeCompile = (shader) => {
    if (previousOnBeforeCompile) previousOnBeforeCompile(shader);

    shader.uniforms.moonShadowCount = shadowUniforms.count;
    shader.uniforms.moonShadowPositions = shadowUniforms.positions;
    shader.uniforms.moonShadowRadii = shadowUniforms.radii;
    shader.uniforms.moonShadowSun = shadowUniforms.sun;
    shader.uniforms.moonShadowSunRadius = shadowUniforms.sunRadius;

    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\nvarying vec3 vMoonShadowWorldPos;`)
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>\nvMoonShadowWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;`
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
uniform int moonShadowCount;
uniform vec3 moonShadowPositions[${MAX_MOON_TRANSIT_SHADOWS}];
uniform float moonShadowRadii[${MAX_MOON_TRANSIT_SHADOWS}];
uniform vec3 moonShadowSun;
uniform float moonShadowSunRadius;
varying vec3 vMoonShadowWorldPos;`
      )
      .replace(
        '#include <opaque_fragment>',
        `
float moonTransitShadow = 1.0;
for (int i = 0; i < ${MAX_MOON_TRANSIT_SHADOWS}; i++) {
  if (i >= moonShadowCount) break;
  vec3 moonPos = moonShadowPositions[i];
  vec3 sunToMoon = moonPos - moonShadowSun;
  vec3 shadowAxis = normalize(sunToMoon);
  vec3 moonToFrag = vMoonShadowWorldPos - moonPos;
  float t = dot(moonToFrag, shadowAxis);
  vec3 perp = moonToFrag - shadowAxis * t;
  float radius = moonShadowRadii[i];
  if (moonShadowSunRadius > 0.0) {
    // The Sun's disc hidden, roughly linear across the penumbra; past the
    // umbra's tip at most (radius / spread)^2, out to spread - radius.
    float spread = max(t, 0.0) * moonShadowSunRadius / length(sunToMoon);
    float deepest = spread > radius ? (radius / spread) * (radius / spread) : 1.0;
    float hidden = deepest * (1.0 - smoothstep(abs(radius - spread), radius + spread + 1e-6, length(perp)));
    moonTransitShadow *= 1.0 - hidden * step(0.0, t);
  } else {
    float umbra = 1.0 - smoothstep(radius * 0.85, radius * 1.2, length(perp));
    moonTransitShadow *= 1.0 - umbra * step(0.0, t) * 0.72;
  }
}
outgoingLight *= moonTransitShadow;
#include <opaque_fragment>`
      );
  };

  registerMoonTransitShadowUniforms(material, planetGroup, shadowUniforms);
}

// Per-frame registry of (material, parentPlanet, uniforms) so the
// animation loop can refresh parentPlanetCenter without each moon
// having to look up its parent itself.
const parentShadowRegistrations = [];

// The light a parent planet throws onto its moons' night sides: its
// geometric albedo (NSSDCA planetary fact sheets) and the colour of its
// light, the mean of the viewer's own map in linear light scaled to
// luminance 1. Saturnshine on Enceladus at full Saturn phase is about 3% of
// the sunlight there (0.499 x (58,232 / 238,000 km)^2).
const PLANETSHINE_PARENTS = {
  mars:    { albedo: 0.170, color: [1.44, 0.913, 0.567] },
  jupiter: { albedo: 0.538, color: [1.154, 0.972, 0.822] },
  saturn:  { albedo: 0.499, color: [1.283, 0.959, 0.567] },
  uranus:  { albedo: 0.488, color: [0.608, 1.098, 1.183] },
  neptune: { albedo: 0.442, color: [0.671, 1.076, 1.218] },
  pluto:   { albedo: 0.52, color: [1.133, 0.978, 0.828] }
};
// Parents whose thick air bends a little reddened sunlight into their
// shadow. Pluto's and Mars' are far too thin: a moon in their shadow is dark.
const REFRACTING_PARENTS = new Set(['jupiter', 'saturn', 'uranus', 'neptune']);
// The display's toe (ACES and the exposure) takes a few percent of daylight
// to black, where a linear camera exposed for the day side still records it
// (3% is about 48 of 255 in sRGB): planetshine is drawn three times its true
// share so Saturnshine on Enceladus reads as it does in Cassini's frames.
// A faint one (Callisto's Jupitershine, 0.08%) stays dark, as it would there.
const PLANETSHINE_DISPLAY_GAIN = 3;

function attachParentPlanetShadow(material, parentPlanet, moonData, moonObject = null) {
  if (!material || !parentPlanet) return;
  // Only worth doing when the parent body is meaningfully bigger
  // than the moon — a tiny captured satellite eclipsing itself is
  // imperceptible. Threshold at parent.radius >= moon.radius * 3.
  const parentData = parentPlanet.userData || {};
  const parentRadius = parentData.radius || 0;
  // The Earth's Moon reads the real eclipse geometry (earthEclipseUniforms),
  // which does not depend on the drawn sizes, so it always takes the shadow
  // (the artistic Moon is drawn too big for the size test below).
  const realEclipse = parentPlanet.name === 'earth' && moonData.name === 'Moon';
  if (!realEclipse && (parentRadius <= 0 || parentRadius < (moonData.radius || 0) * 2)) return;

  const uniforms = {
    parentPlanetCenter: { value: new THREE.Vector3(0, 0, 0) },
    parentPlanetRadius: { value: parentRadius },
    // Relative to the moon's centre, set each frame from the double
    // precision world matrices: at true scale Pluto and Charon are 178,000
    // units out, where a float32 world position is good to about 0.01, half
    // Charon's radius, and the shadow test tore along the terminator.
    uParentOffset: { value: new THREE.Vector3() },
    uParentSunDir: { value: new THREE.Vector3(1, 0, 0) },
    uParentRadius: { value: 0 },
    uSunAngR: { value: 0.004 },
    uParentRefract: { value: REFRACTING_PARENTS.has(parentPlanet.name) ? 1 : 0 },
    uPlanetshine: { value: new THREE.Vector3() }
  };
  material.userData.parentShadowShader = !material.isShaderMaterial;

  if (material.isShaderMaterial) {
    material.uniforms.parentPlanetCenter = uniforms.parentPlanetCenter;
    material.uniforms.parentPlanetRadius = uniforms.parentPlanetRadius;
    parentShadowRegistrations.push({ material, parentPlanet, uniforms, moonObject: null, moonData, realEclipse });
    return;
  }

  // Lambert/Phong path: hook via onBeforeCompile. Sun direction is
  // computed in the fragment shader from the world-space fragment
  // position (sun is at origin in this scene). Shadow ray is
  // analytic sphere-line intersection.
  // Every other moon keeps the drawn-radius shadow cylinder.
  const previousOnBeforeCompile = material.onBeforeCompile;
  material.onBeforeCompile = (shader) => {
    if (previousOnBeforeCompile) previousOnBeforeCompile(shader);
    shader.uniforms.parentPlanetCenter = uniforms.parentPlanetCenter;
    shader.uniforms.parentPlanetRadius = uniforms.parentPlanetRadius;
    shader.uniforms.uEclipseOn = realEclipse ? earthEclipseUniforms.uEclipseOn : { value: 0 };
    shader.uniforms.uEclipseMoonCenter = realEclipse ? earthEclipseUniforms.uEclipseMoonCenter : { value: new THREE.Vector3() };
    shader.uniforms.uEclipseMoonKm = realEclipse ? earthEclipseUniforms.uEclipseMoonKm : { value: new THREE.Vector3() };
    shader.uniforms.uEclipseSunKm = realEclipse ? earthEclipseUniforms.uEclipseSunKm : { value: new THREE.Vector3() };
    shader.uniforms.uEclipseUmbraGain = realEclipse ? earthEclipseUniforms.uEclipseUmbraGain : { value: 1 };
    shader.uniforms.uParentOffset = uniforms.uParentOffset;
    shader.uniforms.uParentSunDir = uniforms.uParentSunDir;
    shader.uniforms.uParentRadius = uniforms.uParentRadius;
    shader.uniforms.uSunAngR = uniforms.uSunAngR;
    shader.uniforms.uParentRefract = uniforms.uParentRefract;
    shader.uniforms.uPlanetshine = uniforms.uPlanetshine;

    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\nvarying vec3 vParentShadowWorldPos;\nvarying vec3 vParentShadowOffset;`)
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>\nvParentShadowWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;\nvParentShadowOffset = mat3(modelMatrix) * transformed;`
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
${OKLAB_MIX_GLSL}
${ECLIPSE_GLSL}
uniform vec3 parentPlanetCenter;
uniform float parentPlanetRadius;
uniform float uEclipseOn;
uniform vec3 uEclipseMoonCenter;
uniform vec3 uEclipseMoonKm;
uniform vec3 uEclipseSunKm;
uniform float uEclipseUmbraGain;
uniform vec3 uParentOffset;
uniform vec3 uParentSunDir;
uniform float uParentRadius;
uniform float uSunAngR;
uniform float uParentRefract;
uniform vec3 uPlanetshine;
varying vec3 vParentShadowWorldPos;
varying vec3 vParentShadowOffset;
// The share of the Sun's disc a parent of angular radius ro hides, d apart.
// When the parent looks many times the Sun's size its limb is a straight
// edge across the Sun: the exact overlap cancels to nothing in float32 there
// (from Charon the Sun is 1/460 of Pluto's width) and sparkled along the
// shadow's edge.
float parentSunCover(float rs, float ro, float d) {
  if (ro > 8.0 * rs) {
    float h = clamp((ro - d) / rs, -1.0, 1.0);
    return (acos(-h) + h * sqrt(1.0 - h * h)) / 3.14159265;
  }
  return clamp(eclDiscCover(rs, ro, d), 0.0, 1.0);
}`
      )
      .replace(
        '#include <opaque_fragment>',
        `
// Parent-planet eclipse shadow. Ray from this fragment toward the
// sun (origin); test if the parent planet sphere is hit before the
// ray escapes. Soft penumbra from the sun's angular size approx.
float parentEclipse = 0.0;
float eclipseDepth = 0.0;
if (uEclipseOn > 0.5) {
  // The Earth's Moon: how much of the Sun the Earth (its shadow widened by
  // the atmosphere) hides from this point on the Moon, and how deep in the
  // umbra the point is (src/eclipse/geometry.js).
  vec3 eclipseP = uEclipseMoonKm + normalize(vParentShadowWorldPos - uEclipseMoonCenter) * ECL_R_MOON;
  vec2 eclipseHD = eclSunHiddenDepth(eclipseP, uEclipseSunKm, vec3(0.0), ECL_R_EARTH_SHADOW);
  parentEclipse = eclipseHD.x;
  eclipseDepth = eclipseHD.y;
} else if (uParentRadius > 0.0) {
  // Every other moon: how much of the Sun's disc the parent's drawn disc
  // covers from this point, in the moon's own frame (uParentOffset is the
  // parent's centre from the moon's, vParentShadowOffset this point's), so
  // the umbra, penumbra and their edge are smooth at any distance from the
  // origin. Only behind the parent: the Sun and the parent on the same side.
  vec3 toParent = uParentOffset - vParentShadowOffset;
  float dParent = length(toParent);
  if (dParent > uParentRadius && dot(toParent, uParentSunDir) > 0.0) {
    vec3 parentDir = toParent / dParent;
    float parentAngR = asin(min(1.0, uParentRadius / dParent));
    float sep = atan(length(cross(uParentSunDir, parentDir)), dot(uParentSunDir, parentDir));
    parentEclipse = parentSunCover(uSunAngR, parentAngR, sep);
  }
}
// Blood-moon Beer-Lambert tint inside the parent's umbra. Real
// lunar eclipses don't go pitch black — the only light reaching
// the moon is sunlight refracted through Earth's atmosphere, with
// Rayleigh stripping blue and leaving the famous deep-red colour.
// Same mechanism gives Jupiter's Galilean moons a faint orange
// glow when in Jupiter's umbra. Modelled by multiplying the
// remaining outgoingLight by a wavelength-dependent transmission
// factor (red survives, blue/green strongly suppressed).
vec3 eclipseRefractTint = mix(vec3(1.0), vec3(1.0, 0.30, 0.10), uParentRefract);
// OKLCh from the shadow-darkened outgoing color to the red-refracted
// eclipse tint. RGB lerp through this transition runs through
// desaturated grey-pink mid-tones at partial penumbra coverage,
// which makes the blood-moon edge read as a flat dim band. OKLCh
// routes through the proper deep-amber to deep-red arc that real
// lunar eclipse photography (Apollo, ground astrophotography)
// captures at the umbra ring.
if (uEclipseOn > 0.5) {
  // Direct sunlight falls with the share of the Sun left. The light bent
  // through the Earth's atmosphere adds to it and is all that reaches the
  // umbra: orange near the umbra's edge, where it passed through thinner
  // air, a deep red at its heart (the tint itself mixed in OKLCh). Far
  // brighter than the real 1/10000, as a photograph exposed for totality
  // shows it, so the eye can follow it.
  vec3 umbraEdgeLight = vec3(1.0, 0.5, 0.22) * 0.7;
  vec3 umbraCoreLight = vec3(0.92, 0.24, 0.1) * 0.34;
  vec3 refracted = outgoingLight * oklchMix(umbraEdgeLight, umbraCoreLight, eclipseDepth) * uEclipseUmbraGain;
  // The penumbra stays grey: the bent light only shows where little direct
  // sunlight is left, at the umbra's rim and inside it. The direct light
  // left takes a gentle curve, as the eye sees it, so the sunlit sliver of a
  // deep partial eclipse still reads brighter than the umbra.
  float refractedShare = smoothstep(0.9, 1.0, parentEclipse);
  outgoingLight = outgoingLight * pow(max(1.0 - parentEclipse, 0.0), 0.45) + refracted * refractedShare;
} else {
  outgoingLight = oklchMix(
    outgoingLight * (1.0 - parentEclipse * 0.96),
    outgoingLight * eclipseRefractTint * 0.045,
    parentEclipse
  );
}
// Planetshine: the parent's sunlit face lights the moon's night side
// (uPlanetshine carries its albedo, its true size from the moon, its phase
// and colour, set each frame). Lambertian, from the parent's centre.
if (uPlanetshine.x + uPlanetshine.y + uPlanetshine.z > 0.0) {
  vec3 psDir = normalize((viewMatrix * vec4(uParentOffset - vParentShadowOffset, 0.0)).xyz);
  outgoingLight += diffuseColor.rgb * uPlanetshine * max(dot(normal, psDir), 0.0);
}
#include <opaque_fragment>`
      );
  };

  parentShadowRegistrations.push({ material, parentPlanet, uniforms, moonObject, moonData, realEclipse });
}

// Each frame: the parent's centre, the Sun's direction, the parent's drawn
// radius and the Sun's angular radius for every moon that takes its
// parent's shadow, and the planetshine on its night side.
const _psMoonPos = new THREE.Vector3();
const _psParentPos = new THREE.Vector3();
const _psSunPos = new THREE.Vector3();
const _psScale = new THREE.Vector3();
function updateParentShadowUniforms() {
  if (sun) sun.getWorldPosition(_psSunPos); else _psSunPos.set(0, 0, 0);
  for (let i = 0; i < parentShadowRegistrations.length; i++) {
    const reg = parentShadowRegistrations[i];
    if (!reg.parentPlanet || !reg.uniforms) continue;
    reg.parentPlanet.getWorldPosition(_psParentPos);
    reg.uniforms.parentPlanetCenter.value.copy(_psParentPos);
    const moonObject = reg.moonObject;
    if (!moonObject || !reg.uniforms.uParentOffset) continue;
    if (reg.material !== moonObject.material) continue; // swapped out
    moonObject.getWorldPosition(_psMoonPos);
    const u = reg.uniforms;
    u.uParentOffset.value.subVectors(_psParentPos, _psMoonPos);
    u.uParentSunDir.value.subVectors(_psSunPos, _psMoonPos).normalize();
    // Looked up again if the parent's surface was rebuilt (focus swaps it).
    if (reg.parentMesh === undefined || (reg.parentMesh && !reg.parentMesh.parent)) {
      reg.parentMesh = getBodySurfaceMesh(reg.parentPlanet) || null;
    }
    const pm = reg.parentMesh;
    if (pm && pm.geometry && pm.geometry.parameters && pm.geometry.parameters.radius) {
      pm.getWorldScale(_psScale);
      u.uParentRadius.value = pm.geometry.parameters.radius * Math.max(_psScale.x, _psScale.y, _psScale.z);
    } else {
      u.uParentRadius.value = getBodyBaseRadius(reg.parentPlanet);
    }
    u.uSunAngR.value = Math.max(1e-5, getSunAngularRadiusAt(moonObject));
    // Planetshine at the moon's true distance from the parent (the drawn
    // artistic orbits are far closer): geometric albedo x (R / d)^2 x the
    // Lambert phase of the parent seen from the moon, times the sunlight's
    // own scale (the 3.1 point light through the Lambert 1/pi).
    const ps = !reg.realEclipse && PLANETSHINE_PARENTS[reg.parentPlanet.name];
    const realR = ps && getRealisticBodyRadius(reg.parentPlanet.name);
    const moonGroup = moonObject.parent;
    const realD = ps && moonGroup && moonGroup.userData && moonGroup.userData.isMoon ? getRealisticMoonDistance(moonGroup) : null;
    if (ps && realR && realD && realD > realR) {
      // The parent's phase seen from the moon: the angle at the parent
      // between the Sun and the moon.
      const sx = _psSunPos.x - _psParentPos.x, sy = _psSunPos.y - _psParentPos.y, sz = _psSunPos.z - _psParentPos.z;
      const off = u.uParentOffset.value; // moon -> parent
      const ls = Math.hypot(sx, sy, sz) || 1;
      const lm = off.length() || 1;
      const cosA = -(sx * off.x + sy * off.y + sz * off.z) / (ls * lm);
      const alpha = Math.acos(Math.max(-1, Math.min(1, cosA)));
      const phase = (Math.sin(alpha) + (Math.PI - alpha) * Math.cos(alpha)) / Math.PI;
      const k = ps.albedo * (realR / realD) * (realR / realD) * phase * (3.1 / Math.PI) * PLANETSHINE_DISPLAY_GAIN;
      u.uPlanetshine.value.set(ps.color[0] * k, ps.color[1] * k, ps.color[2] * k);
    } else {
      u.uPlanetshine.value.set(0, 0, 0);
    }
  }
}

// Saturn's named moons as spheres in the view space of the camera about to
// draw the dusty rings, the four nearest first: centre and drawn radius.
const _dustOccPos = new THREE.Vector3();
const _dustOccScale = new THREE.Vector3();
const _dustOccPool = [];
function updateDustRingOccluders(dust, cam) {
  const u = dust.material && dust.material.uniforms;
  if (!u || !u.uOccluders || !cam) return;
  let n = 0;
  const list = moons.saturn || [];
  for (let i = 0; i < list.length; i++) {
    const moon = list[i];
    if (!moon.visible || (moon.userData && moon.userData.isMinorMoon)) continue;
    const mesh = moon.children[0];
    const params = mesh && mesh.geometry && mesh.geometry.parameters;
    if (!params || !params.radius) continue;
    mesh.getWorldPosition(_dustOccPos).applyMatrix4(cam.matrixWorldInverse);
    mesh.getWorldScale(_dustOccScale);
    if (!_dustOccPool[n]) _dustOccPool[n] = { x: 0, y: 0, z: 0, r: 0, d: 0 };
    const o = _dustOccPool[n++];
    o.x = _dustOccPos.x; o.y = _dustOccPos.y; o.z = _dustOccPos.z;
    o.r = params.radius * Math.max(_dustOccScale.x, _dustOccScale.y, _dustOccScale.z);
    o.d = _dustOccPos.length() - o.r;
  }
  const used = _dustOccPool.slice(0, n).sort((a, b) => a.d - b.d);
  const count = Math.min(4, used.length);
  for (let i = 0; i < count; i++) u.uOccluders.value[i].set(used[i].x, used[i].y, used[i].z, used[i].r);
  u.uOccluderCount.value = count;
}

function getBodySurfaceMesh(group) {
  if (!group) return null;
  if (group.userData && group.userData.isPlanet) {
    const tiltGroup = group.getObjectByName('tiltGroup');
    return tiltGroup && tiltGroup.children[0] ? tiltGroup.children[0] : null;
  }
  if (group.userData && group.userData.isMoon) {
    return group.children[0] || null;
  }
  return null;
}

// Resolve the bump map (heightmap or luminance-derived) for a body
// from its color-texture key. Mirrors the resolution logic in
// createMoon / createPlanet so re-materialised bodies (after a
// system switch) keep their relief shading instead of losing it.
function resolveBumpMapForBody(textureKey) {
  if (!textureKey) return null;
  if (textureKey === 'moon') return loadedTextures.moon_bump || null;
  if (textureKey === 'mars') return loadedTextures.mars_bump || null;
  if (textureKey === 'mercury') return loadedTextures.mercury_bump || null;
  if (textureKey === 'europa') {
    return loadedTextures.europa_bump || loadedTextures.europa || null;
  }
  if (textureKey === 'callisto' || textureKey === 'ganymede' || textureKey === 'io') {
    return loadedTextures[textureKey] || null;
  }
  return null;
}

function applyDynamicSurfaceMaterial(group, bodyData) {
  if (!group || !bodyData || bodyData.texture === 'earth') return;
  const mesh = getBodySurfaceMesh(group);
  if (!mesh) return;
  // Venus draws its own procedural cloud tops (src/shaders/venus.js) and
  // samples no map, so there is nothing to rebuild; rebuilding swapped in
  // the generic textured material, and the orange venus.jpg replaced the
  // shader on the first focus.
  if (mesh.material && mesh.material.isShaderMaterial && mesh.material.fragmentShader === venusFragmentShader) return;

  // Preserve the bump map across system rebuilds. Without this,
  // re-entering Earth's system after focusing elsewhere left the
  // moon (and any other body that had a bump on initial creation)
  // without its heightmap, so the surface looked flat. EAGER
  // textures stay in loadedTextures across evictions, so the
  // resolveBumpMapForBody lookup always returns the right data.
  const bumpMap = resolveBumpMapForBody(bodyData.texture);
  // Every focus change sends every planet and moon through here. A material
  // this function made from the same maps is kept: making it again drew the
  // same picture and compiled its shader again (a 25 to 45 ms frame on
  // every click). The first pass after load still rebuilds, which turns the
  // ice giants' procedural boot surfaces into their maps.
  const relief = MOON_RELIEF[bodyData.texture];
  const inputs = [
    loadedTextures[bodyData.texture] || null,
    bumpMap,
    bodyData.texture === 'saturn' && bodyData.hasRings ? loadedTextures.saturn_ring || null : null,
    relief ? loadedTextures[relief.normal] || null : null,
    relief ? loadedTextures[relief.height] || null : null
  ];
  const built = mesh.material && mesh.material.userData && mesh.material.userData.surfaceInputs;
  if (built && built.every((input, i) => input === inputs[i])) return;
  const pending = mesh.userData.pendingSurface;
  if (pending && pending.userData.surfaceInputs.every((input, i) => input === inputs[i])) return;
  const baseOpts = loadedTextures[bodyData.texture]
    ? { texture: loadedTextures[bodyData.texture], bumpMap }
    : { bumpMap };
  const nextMaterial = createSurfaceMaterial(bodyData, baseOpts);
  nextMaterial.userData.surfaceInputs = inputs;
  const prevMaterial = mesh.material;

  attachMoonTransitShadows(nextMaterial, group, bodyData);
  applyGasGiantBandsForTexture(nextMaterial, loadedTextures[bodyData.texture], bodyData.texture);
  // Saturn's rings shade its clouds, as createPlanet sets up. The rebuild
  // used to leave it out, so Saturn lost its ring shadow once focused.
  if (bodyData.texture === 'saturn' && loadedTextures.saturn_ring && bodyData.hasRings) {
    applyRingShadowProjection(nextMaterial, loadedTextures.saturn_ring, bodyData.ringInnerRadius, bodyData.ringOuterRadius, { strength: 0.78 });
  }

  // Re-apply the same direct-sun dimming the initial createPlanet build
  // does. Without this, re-materialising a body on focus left
  // uSunIntensity at its default 1.0 while the first build had dimmed
  // it, so the disc jumped brighter "after clicking away and coming
  // back". Mirrors the block in createPlanet so both paths agree.
  const sunIrradiance = getSunIrradianceFactor(bodyData.name);
  if (nextMaterial.isShaderMaterial && nextMaterial.uniforms) {
    nextMaterial.uniforms.uSunIntensity = { value: sunIrradiance };
  } else if (nextMaterial.userData && nextMaterial.userData.bandUniforms) {
    nextMaterial.userData.bandUniforms.uSunIntensity.value = sunIrradiance;
  } else if (nextMaterial.color) {
    nextMaterial.color.multiplyScalar(Math.min(sunIrradiance, 1.3));
  }

  if (group.userData && group.userData.isMoon) {
    attachMoonSurface(mesh, bodyData, planets[group.userData.parentPlanet] || group.parent, nextMaterial);
  }
  const swap = () => {
    mesh.material = nextMaterial;
    mesh.userData.baseColor = nextMaterial.color.clone();
    if (prevMaterial && prevMaterial.dispose) prevMaterial.dispose();
  };
  if (!BAND_UPLOADS || !renderer) {
    swap();
    return;
  }
  // In the interactive viewer the new material goes on once its program is
  // ready (src/gpuwarm.js), the old one drawing until then: put on at once,
  // the next frame waited on the driver while it compiled, half a second
  // where shaders compile slowly (Edge on Windows on a first visit, when a
  // map from the deferred stream lands). A newer rebuild meanwhile wins.
  mesh.userData.pendingSurface = nextMaterial;
  compileForSceneAsync(renderer, new THREE.Mesh(mesh.geometry, nextMaterial), camera, scene, SLOW_COMPILE_LIMIT_MS).then(() => {
    if (mesh.userData.pendingSurface !== nextMaterial) {
      nextMaterial.dispose();
      return;
    }
    mesh.userData.pendingSurface = null;
    swap();
  });
}

// What a moon's surface takes beyond createSurfaceMaterial, on the material
// createMoon makes and on every one made after it on focus changes and
// texture loads (applyDynamicSurfaceMaterial): the parent planet's shadow.
// Once per material.
//
// Until 2 Nov 2026 createMoon also gave its first material three things the
// later ones never took, so they showed only until the first click. Kept on
// every material they looked wrong, so they went: a fine relief on the
// Galilean moons (a bump from noise heights in scene units, slopes several
// times steeper than a cliff at either scale, so the disc read as noise and
// the night side lit up), Io's lava (soft glows some 300 km across, on the
// day side as well as the night, and mirrored: Pele's drew at 75 E on a map
// that starts at 0 E) and the Moon's earthshine (the night side lit far
// above what the planetshine rule would draw, PLANETSHINE_DISPLAY_GAIN: at
// full Earth it is about 1/8,000 of the sunlight, which draws black). The
// moon-phase pages keep an earthshine of their own (src/sky/moondisc.js).
function attachMoonSurface(mesh, moonData, parentPlanet, material = mesh.material) {
  if (!material || !material.userData || material.userData.moonSurfaceAttached) return;
  material.userData.moonSurfaceAttached = true;
  // Parent-planet eclipse shadow. Every moon casts a ray from its
  // surface toward the sun and tests whether the parent planet is
  // between them — if yes, the moon is in eclipse, darken the
  // surface. Most visible on Jupiter's Galilean moons, where Io
  // passes into Jupiter's shadow roughly every 1.77 days and the
  // umbra is dramatic from orbit.
  attachParentPlanetShadow(material, parentPlanet, moonData, mesh);
}

function getRelatedTextureNames(object) {
  if (!object || !object.userData) return [];
  const names = new Set();
  const add = (textureName) => {
    if (textureName && textureUrls[textureName]) names.add(textureName);
  };

  if (object.userData.isMoon) {
    add(object.userData.texture);
    const parentData = celestialData[object.userData.parentPlanet];
    if (parentData) {
      add(parentData.texture);
      if (parentData.moon) add(parentData.moon.texture);
      if (parentData.majorMoons) {
        parentData.majorMoons.forEach((moonData) => add(moonData.texture));
      }
      if (object.userData.parentPlanet === 'saturn') add('saturn_ring');
    }
  }

  if (object.userData.isPlanet) {
    add(object.userData.texture);
    if (object.userData.moon) add(object.userData.moon.texture);
    if (object.userData.majorMoons) {
      object.userData.majorMoons.forEach((moonData) => add(moonData.texture));
    }
    if (object.name === 'saturn') add('saturn_ring');
  }

  [...names].forEach((name) => getMoonReliefTextureNames(name).forEach(add));
  return [...names];
}

function getSystemKey(object) {
  if (!object || !object.userData) return null;
  if (object.userData.isMoon) return object.userData.parentPlanet || null;
  if (object.userData.isPlanet) return object.name || null;
  return null;
}

function getActiveSystemTextureSet(object, extraObject = null) {
  const keep = new Set(EAGER_TEXTURES);
  getRelatedTextureNames(object).forEach((name) => keep.add(name));
  getRelatedTextureNames(extraObject).forEach((name) => keep.add(name));
  // A dwarf planet's map is held by its material whatever is focused, so it
  // stays while the deep-space bodies are shown.
  if (deepSpaceBodiesVisible) {
    dwarfPlanets.forEach((dwarf) => {
      if (!dwarf.texture) return;
      keep.add(dwarf.texture);
      getMoonReliefTextureNames(dwarf.texture).forEach((name) => keep.add(name));
    });
  }
  return keep;
}

function refreshSystemMaterials(object) {
  if (!object || !object.userData) return;

  if (object.userData.isMoon) {
    applyDynamicSurfaceMaterial(object, object.userData);
    const parentPlanet = planets[object.userData.parentPlanet];
    if (parentPlanet) {
      applyDynamicSurfaceMaterial(parentPlanet, celestialData[object.userData.parentPlanet]);
      if (moons[object.userData.parentPlanet]) {
        moons[object.userData.parentPlanet].forEach((moonGroup) => {
          if (!moonGroup.userData || moonGroup.userData.isMinorMoon) return;
          applyDynamicSurfaceMaterial(moonGroup, moonGroup.userData);
        });
      }
    }
    return;
  }

  if (object.userData.isPlanet) {
    applyDynamicSurfaceMaterial(object, object.userData);
    if (moons[object.name]) {
      moons[object.name].forEach((moonGroup) => {
        if (!moonGroup.userData || moonGroup.userData.isMinorMoon) return;
        applyDynamicSurfaceMaterial(moonGroup, moonGroup.userData);
      });
    }
  }
}

// A phone's two sizes for one map (mobileTextureCap). Swaps every name that
// holds `from` over to `to`, and keeps `from` for disposal once the bodies
// using it have been given new materials.
let mobileRetiredTextures = [];
function replaceLoadedTexture(from, to) {
  for (const key of Object.keys(loadedTextures)) if (loadedTextures[key] === from) loadedTextures[key] = to;
  if (from !== to) mobileRetiredTextures.push(from);
}

function disposeRetiredMobileTextures() {
  if (!mobileRetiredTextures.length) return;
  const live = new Set(Object.values(loadedTextures));
  for (const texture of mobileRetiredTextures) if (!live.has(texture)) texture.dispose();
  mobileRetiredTextures = [];
}

// The focused system's maps at 2048, read again from the file (in the HTTP
// cache by now); the 1024 copies are retired when ensureFocusedDetail has
// given the bodies new materials.
const mobileSharpenLoads = new Map();
function sharpenMobileTextures(object) {
  mobileFullTextures = new Set(getRelatedTextureNames(object));
  const jobs = [];
  for (const name of mobileFullTextures) {
    const held = loadedTextures[name];
    if (!held || (held.userData.mobileCap || 0) >= mobileTextureCap(name)) continue;
    const url = textureUrls[name];
    if (!mobileSharpenLoads.has(url)) {
      mobileSharpenLoads.set(url, new Promise((resolve) => {
        textureLoader.load(url, (texture) => {
          const current = loadedTextures[name];
          const sharp = configureLoadedTexture(name, texture);
          if (current && (current.userData.mobileCap || 0) < sharp.userData.mobileCap) replaceLoadedTexture(current, sharp);
          else sharp.dispose();
          resolve();
        }, undefined, () => resolve());
      }).finally(() => mobileSharpenLoads.delete(url)));
    }
    jobs.push(mobileSharpenLoads.get(url));
  }
  return jobs.length ? Promise.all(jobs) : null;
}

// The maps of systems no longer looked at back at 1024, shrunk from the
// 2048 copy in place (no download).
function shrinkMobileTextures() {
  // A map two names share (Titan reads Pluto's) takes the larger of their sizes.
  const caps = new Map();
  for (const [name, texture] of Object.entries(loadedTextures)) {
    if (texture) caps.set(texture, Math.max(caps.get(texture) || 0, mobileTextureCap(name)));
  }
  for (const [texture, cap] of caps) {
    const image = texture.image;
    if ((texture.userData.mobileCap || 0) <= cap || !image) continue;
    const w = image.naturalWidth || image.width || 0, h = image.naturalHeight || image.height || 0;
    if (Math.max(w, h) <= cap) continue;
    const scale = cap / Math.max(w, h);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) continue;
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    const small = texture.clone();
    small.source = new THREE.Source(canvas);
    small.userData.mobileCap = cap;
    small.needsUpdate = true;
    replaceLoadedTexture(texture, small);
  }
}

function evictUnfocusedTextures(activeObject, extraObject = null) {
  const keep = getActiveSystemTextureSet(activeObject, extraObject);
  const activeSystemKeys = new Set(
    [getSystemKey(activeObject), getSystemKey(extraObject)].filter(Boolean)
  );
  // A phone takes the maps it is letting go out of loadedTextures before the
  // bodies are given new materials, so those are not made with a map that
  // is then disposed (three would upload it again on the next draw and
  // nothing would ever free it), and shrinks the systems it has left.
  let evicted = null;
  if (RUNNING_ON_MOBILE) {
    mobileFullTextures = new Set([...getRelatedTextureNames(activeObject), ...getRelatedTextureNames(extraObject)]);
    evicted = [];
    for (const name of Object.keys(loadedTextures)) {
      if (keep.has(name)) continue;
      evicted.push(loadedTextures[name]);
      delete loadedTextures[name];
    }
    shrinkMobileTextures();
  }

  Object.entries(planets).forEach(([planetName, planetGroup]) => {
    if (!activeSystemKeys.has(planetName)) {
      applyDynamicSurfaceMaterial(planetGroup, celestialData[planetName]);
    }
  });

  Object.entries(moons).forEach(([planetName, moonGroups]) => {
    if (!activeSystemKeys.has(planetName)) {
      moonGroups.forEach((moonGroup) => {
        if (!moonGroup.userData || moonGroup.userData.isMinorMoon) return;
        applyDynamicSurfaceMaterial(moonGroup, moonGroup.userData);
      });
    }
  });

  if (evicted) {
    const live = new Set(Object.values(loadedTextures));
    for (const texture of evicted) if (texture && texture.dispose && !live.has(texture)) texture.dispose();
    disposeRetiredMobileTextures();
  }
  // Off a phone the maps stay in loadedTextures: the materials just made
  // hold them, so disposing them only had three upload them again on the
  // next draw (up to 400 ms for an 8K map) and a revisit load a second copy
  // that nothing freed (docs/mobile-memory.md, cause 6).
}

function updateMoonTransitShadows(planetName, planetGroup) {
  const mesh = getBodySurfaceMesh(planetGroup);
  const uniforms = mesh && mesh.material && mesh.material.userData.moonShadowUniforms;
  if (!uniforms) return;

  // Drawn radii (realistic scale shrinks planet and moons alike), since
  // the shader tests world-space distances.
  const planetRadius = (realisticScaleMode && getRealisticBodyRadius(planetName))
    || (planetGroup.userData && planetGroup.userData.radius)
    || 1;
  let count = 0;
  (moons[planetName] || []).forEach((moonGroup) => {
    if (!moonGroup.userData || moonGroup.userData.isMinorMoon || count >= MAX_MOON_TRANSIT_SHADOWS) return;
    moonGroup.getWorldPosition(uniforms.positions.value[count]);
    const moonRadius = (moonGroup.userData.radius || 0.1) * moonGroup.scale.x;
    const shadowRadius = planetName === 'earth'
      ? planetRadius * EARTH_MOON_TRANSIT_SHADOW_RADIUS_RATIO
      : moonRadius * (realisticScaleMode ? 1 : MOON_TRANSIT_SHADOW_SCALE);
    // The floor is the drawn scale's: at true scale Phobos and Deimos are
    // under it (0.001 is 30 km) and the shadow is the moon's own.
    uniforms.radii.value[count] = realisticScaleMode && planetName !== 'earth' ? shadowRadius : Math.max(0.001, shadowRadius);
    count++;
  });
  uniforms.count.value = count;
  if (uniforms.sun) {
    if (sun) sun.getWorldPosition(uniforms.sun.value);
    else uniforms.sun.value.set(0, 0, 0);
    uniforms.sunRadius.value = realisticScaleMode && planetName !== 'earth' ? REAL_RADII_KM.sun * REAL_KM_TO_SCENE : 0;
  }
}

function scheduleDetailPrefetch(object) {
  const systemKey = getSystemKey(object);
  if (!systemKey || systemKey === prefetchTargetKey) return;

  if (prefetchTimer) clearTimeout(prefetchTimer);
  prefetchTargetKey = systemKey;
  const serial = ++prefetchSerial;

  prefetchTimer = setTimeout(async () => {
    prefetchTimer = null;
    const names = getRelatedTextureNames(object).filter((name) => !loadedTextures[name]);
    if (names.length) {
      await Promise.all(names.map((name) => loadTextureByName(name)));
    }
    if (serial === prefetchSerial) {
      evictUnfocusedTextures(selectedObject, object);
    } else {
      evictUnfocusedTextures(selectedObject);
    }
  }, 140);
}

function cancelPendingPrefetch(clearTarget = false) {
  if (prefetchTimer) {
    clearTimeout(prefetchTimer);
    prefetchTimer = null;
    prefetchSerial++;
  }
  if (clearTarget) {
    prefetchTargetKey = null;
  }
}

async function ensureFocusedDetail(object) {
  // Heavy optional assets are queued to load in the background; actually
  // navigating to one pulls it to the front.
  if (object && object.userData && object.userData.isSatellite) {
    void requestBackgroundAsset(object.userData.modelAsset || 'iss');
    if (object.userData.atL2) loadJwstTrack();
  }
  prefetchTargetKey = null;
  cancelPendingPrefetch();
  const names = getRelatedTextureNames(object).filter((name) => !loadedTextures[name]);
  const sharpening = RUNNING_ON_MOBILE ? sharpenMobileTextures(object) : null;
  if (names.length || sharpening) {
    await Promise.all(sharpening ? [...names.map((name) => loadTextureByName(name)), sharpening] : names.map((name) => loadTextureByName(name)));
    // A map that arrives while the camera is still flying in would be
    // uploaded on its first draw, mid-flight: the Moon's took 400 ms, a
    // frozen frame halfway into the landing. Swap it in once the camera has
    // landed (or after a few seconds whatever it is doing), and not at all
    // if another body has been picked since; its own focus does the swap.
    const started = performance.now();
    while (isAnimating && performance.now() - started < 4000) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    if (selectedObject !== object) return;
  }
  refreshSystemMaterials(object);
  evictUnfocusedTextures(object);
}

// A planet at its JPL position for simulationJD, as the frame loop places it.
// `a` is the orbit's scene distance (artistic fits each orbit to it; true
// scale is AU_TO_SCENE per AU). False for a body the ephemeris lacks.
function placePlanetOnEphemeris(planetName, planet, a) {
  if (!SUPPORTED_PLANETS.includes(planetName)) return false;
  const eclip = getPlanetPosition(planetName, simulationJD);
  if (eclip) {
    let scale;
    if (realisticScaleMode) {
      scale = AU_TO_SCENE;
    } else {
      const state = getOrbitState(planetName, simulationJD);
      scale = state ? (a / state.a) : 1;
    }
    // Heliocentric ecliptic (x, y, z) → scene (x, z, -y). Our scene's
    // ecliptic plane is xz with y vertical, so the ecliptic-z axis
    // (toward the ecliptic north pole) maps to scene-y. Ecliptic-y
    // must map to scene -z: a plain (x, z, y) swap is a REFLECTION,
    // which mirrored every orbit so planets revolved clockwise seen
    // from scene +Y — against their own (positive rotation.y) spin.
    planet.position.set(eclip.x * scale, eclip.z * scale, -eclip.y * scale);
  }
  return true;
}

// Create all planets
function createPlanets() {
  planetOrder.forEach((planetName) => {
    const data = celestialData[planetName];
    const planetGroup = buildPlanetBody(planetName);

    // Set initial position (-sin matches the per-frame orbit drivers)
    const angle = Math.random() * Math.PI * 2;
    planetGroup.position.x = Math.cos(angle) * data.distance;
    planetGroup.position.z = -Math.sin(angle) * data.distance;
    planetGroup.userData.orbitAngle = angle;
    planetGroup.userData.scaledDistance = data.distance; // Initial scaled distance

    // Store base distance for scale adjustments
    baseDistances[planetName] = data.distance;

    scene.add(planetGroup);
    planets[planetName] = planetGroup;
    markClickablesDirty();

    // Create moons
    if (data.moon) {
      createMoon(planetGroup, data.moon);
    }
    if (data.majorMoons) {
      data.majorMoons.forEach((moonData) => createMoon(planetGroup, moonData));
    }
    // Fill out the remaining known moon count with a procedural swarm of
    // tiny captured bodies — most real minor moons are sub-km irregular rocks.
    createMinorMoonSwarm(planetGroup, data);
  });
}

// One planet as the viewer draws it: the surface in its tilt frame, the
// atmosphere shell and the rings, at the authored radius. The To-Scale
// lineup builds its planets here too, with `lineup` set: that keeps the
// body out of the viewer's registries and globals (Venus's cloud
// material, the procedural surface list, the aurora and belt handles),
// lights it at the same level as every other lineup body, and gives
// Uranus and Neptune the textured surface the viewer swaps in on focus
// (see useViewerIceGiantSurfaces) rather than the boot-time shader.
function buildPlanetBody(planetName, { lineup = false } = {}) {
  const data = celestialData[planetName];
  const planetGroup = new THREE.Group();
  planetGroup.name = planetName;
  planetGroup.userData = { ...data, isPlanet: true, isSun: false };

  // Planet mesh segment count. Earth gets a denser sphere so its
  // limb stays round against the analytic aerial perspective
  // gradient. Mercury and Mars also get a higher segment count
  // because they take vertex displacement from the MESSENGER /
  // MOLA bump maps, and the displacement is only visible on the
  // silhouette when there are enough vertices to deform. Other
  // planets stay on the standard count to keep total mesh load
  // sane.
  const isEarth = planetName === 'earth';
  const isCrateredRocky = planetName === 'mercury' || planetName === 'mars';
  let geometry;
  if (isEarth) {
    geometry = new THREE.SphereGeometry(data.radius, graphicsConfig.earthWidth, graphicsConfig.earthHeight);
  } else if (isCrateredRocky) {
    const crateredSegs = Math.max(256, graphicsConfig.planetSegments * 1.6 | 0);
    geometry = new THREE.SphereGeometry(data.radius, crateredSegs, crateredSegs);
  } else {
    geometry = new THREE.SphereGeometry(data.radius, graphicsConfig.planetSegments, graphicsConfig.planetSegments);
  }
  let material;

  const textureName = data.texture;
  if (loadedTextures[textureName]) {
    // Per-planet bump maps. Mars + Mercury both have dedicated
    // topography sources (MOLA + MESSENGER). Other planets get
    // null and skip the bumpMap branch in createSurfaceMaterial.
    let bumpMap = null;
    if (planetName === 'mars') bumpMap = loadedTextures.mars_bump || null;
    else if (planetName === 'mercury') bumpMap = loadedTextures.mercury_bump || null;
    material = createSurfaceMaterial(data, {
      texture: loadedTextures[textureName],
      bumpMap
    });

    // Earth with custom shader — glint, aerial perspective,
    // terminator warmth, cloud drift, all in one material.
    if (planetName === 'earth') {
      // The cloud texture scrolls in U, so it must wrap not clamp —
      // clamping produces a bright seam that flickers as the UV crosses 1.
      if (loadedTextures.earth_clouds) {
        loadedTextures.earth_clouds.wrapS = THREE.RepeatWrapping;
        loadedTextures.earth_clouds.needsUpdate = true;
      }
      material = new THREE.ShaderMaterial({
        uniforms: {
          dayTexture: { value: loadedTextures[textureName] },
          ...oceanWaveUniforms,
          ...earthImageryUniforms,
          ...cloudImageryUniforms,
          ...nightImageryUniforms,
          nightTexture: { value: loadedTextures.earth_night || loadedTextures[textureName] },
          cloudsTexture: { value: loadedTextures.earth_clouds || null },
          weatherGuideTexture: { value: loadedTextures.earth_clouds || null },
          normalTexture: { value: loadedTextures.earth_normal || null },
          specularTexture: { value: loadedTextures.earth_specular || null },
          // The sea's coverage, depth and Kd490 (src/shaders/earthocean.js),
          // bound late by bindLateEarthTexture when it is not in the boot set.
          oceanTexture: { value: loadedTextures.earth_ocean || null },
          oceanTextureOn: { value: loadedTextures.earth_ocean ? 1 : 0 },
          bumpTexture: { value: loadedTextures.earth_bump || null },
          bumpTexelSize: { value: getTextureTexelSize(loadedTextures.earth_bump || loadedTextures[textureName]) },
          // Physical elevation: sea level is zero, the brightest height
          // texel is approximately 8.8 km above it, in authored mesh units.
          displacementScale: { value: data.radius * EARTH_ELEVATION_METRES / EARTH_METRES },
          dayTexelSize: { value: getTextureTexelSize(loadedTextures[textureName]) },
          cloudTexelSize: { value: getTextureTexelSize(loadedTextures.earth_clouds || loadedTextures[textureName]) },
          surfaceDetailStrength: { value: 0.55 },
          cloudDetailStrength: { value: 0.32 },
          sunPosition: { value: new THREE.Vector3(0, 0, 0) },
          // Cloud opacity 0.85 — heavy, to read as real opaque cloud
          // masses (user wanted them less transparent). Paired with
          // the S-curve cloudSignal in the shader so dense storm
          // cores go near-opaque white while thin cirrus stays
          // translucent, rather than a flat grey film. Surface still
          // shows through the SSS composite's clear-sky gaps.
          cloudOpacity: { value: 0.85 },
          uNightLights: { value: 1 },
          // Cloud tops from orbit (src/shaders/earthcloudtop.js): the noise
          // volume is bound by updateEarthWeather once the camera is near.
          uCloudOrbitVol: { value: null },
          uCloudVolOn: { value: 0 },
          uCloudTopQuality: { value: 1 },
          weatherTime: { value: 0 },
          time: { value: 0 },
          ...earthEclipseUniforms,
          ...(EARTH_RINGS_ON && !lineup ? earthRingUniforms : {})
        },
        ...(EARTH_RINGS_ON && !lineup ? { defines: { EARTH_RINGS: '' } } : {}),
        vertexShader: earthVertexShader,
        fragmentShader: earthFragmentShader
      });
    }

    // Venus procedural cloud-top. Replaces the textured hazy
    // preset + UV-drift super-rotation with the fully procedural
    // shader (OKLCh sulphuric palette, double domain warp,
    // Y-chevron drift, polar collar, super-rotation built into
    // the noise sample point). The original venus.jpg texture is
    // no longer sampled by the surface — the atmosphere shell
    // and the lineup icons still use it. Tuned to Venus in visible
    // light (MESSENGER, Mariner 10 true colour): a pale cream disc with
    // the UV features (the Y, the polar collar) kept as faint hints,
    // Minnaert k = 0.85, a soft twilight and a thin haze rim; see
    // src/shaders/venus.js. The notes below are the earlier UV tuning:
    //   - 4 broad bands (equator bright, mid belts, polar collar)
    //   - turbulence 1.6 (strong swirly festoons)
    //   - drift 0.12 (~10x gas-giant rate; equator visibly laps
    //     the poles at default time speed)
    //   - Y-chevron strength 1.0 (the iconic UV signature)
    //   - polar collar strength 0.85
    //   - heavy limb darken (Venus's photometric function falls
    //     off markedly toward the terminator)
    //   - rim glow 0.55 to land just under the existing
    //     atmosphereStrength so the shell halo still reads
    if (planetName === 'venus') {
      const uniforms = {
        uTime:                { value: 0 },
        uSunDir:              { value: new THREE.Vector3(-1, 0, 0) },
        uPaletteDeep:         { value: new THREE.Color(0.86, 0.72, 0.41) },
        uPaletteBelt:         { value: new THREE.Color(0.95, 0.83, 0.5) },
        uPaletteZone:         { value: new THREE.Color(1.0, 0.9, 0.57) },
        uBandCount:           { value: 4.0 },
        uTurbulence:          { value: 1.3 },
        uDriftRate:           { value: 0.12 },
        uYStrength:           { value: 0.3 },
        uPolarCollarStrength: { value: 0.12 },
        uLimbDarken:          { value: 0.15 },
        uRimGlow:             { value: 0.6 },
        uCloudDetailStrength: { value: 0.8 },
        uCloudDetailFreq:     { value: 7.5 }
      };
      material = new THREE.ShaderMaterial({
        uniforms,
        vertexShader: venusVertexShader,
        fragmentShader: venusFragmentShader,
        side: THREE.FrontSide,
        transparent: false
      });
      if (!lineup) venusShaderMaterial = material;
    }

    // Gas giants shear at different latitudes. Real Jupiter's equator
    // laps its poles roughly once every 20 rotations; Saturn's does
    // the same even faster. Rates tuned so the drift is visible at
    // timeSpeed ~10x and obvious at 50x, without looking silly at 1x.
    applyGasGiantBandsForTexture(material, loadedTextures[textureName], textureName);

    // Saturn ring shadow projection on the planet. Cassini imagery
    // makes this iconic — when the sun is off-equator, the rings
    // project a clear dark band across Saturn's clouds. Body and
    // ring share the same tilt frame (same tiltGroup parent), so
    // the ring plane is exactly Y = 0 in mesh-local space.
    if (planetName === 'saturn' && loadedTextures.saturn_ring && data.hasRings) {
      applyRingShadowProjection(
        material,
        loadedTextures.saturn_ring,
        data.ringInnerRadius,
        data.ringOuterRadius,
        { strength: 0.78 }
      );
    }

    // Atmospheric limb haze for gas giants. Tints fade the rim
    // toward each planet's atmospheric colour:
    //   Jupiter — warm cream (ammonia + ammonium hydrosulfide haze)
    //   Saturn  — paler gold (cooler, smaller scale haze)
    //   Uranus  — pale cyan (CH4 absorbs red, atmosphere reads blue-green)
    //   Neptune — deeper cobalt (same chemistry as Uranus but darker
    //              + more methane = more saturated blue)
    // Strength + exponent are per-planet so ice giants get a more
    // pronounced haze (they ARE basically all atmosphere) than the
    // Jovian gas giants.
    if (planetName === 'jupiter') {
      // Jupiter atmospheric haze. Voyager / Juno imagery shows
      // Jupiter's disc with a clear soft tan veil and a luminous
      // limb where the long chord through the ammonia-cloud deck
      // scatters sunlight. Tuned similar to Saturn: stronger tight
      // limb haze + a wide soft-wash that mildly tints the whole
      // disc so the bands stay visible but blend into a coherent
      // gas-giant atmosphere rather than reading as a flat
      // textured sphere. Slightly warmer / more orange than
      // Saturn's pale gold since Jupiter's ammonia ice is mixed
      // with reddish chromophores from photochemistry.
      // Soft-wash dropped (0.14 → 0.0): the disc-wide veil was
      // dulling the bands toward tan-grey and worked against the
      // vibrance fix. Keep only a tight, slightly cooler limb haze so
      // the rim still reads as a 3D ball of gas without a visible
      // atmosphere line (the shell was removed per the photos).
      applyGasGiantLimb(material, {
        color: new THREE.Color(0xe6cda6),
        strength: 0.42,
        exponent: 2.8,
        softWashStrength: 0.0,
        softWashExponent: 0.7
      });
      // Jupiter lightning was disabled. The procedural night-side
      // flashes were getting picked up by the bloom pass at a high
      // enough HDR value that the bloom mip downsampling rendered
      // them as visible square block artifacts rather than soft
      // points of light. Until the bloom mip aliasing is fixed
      // separately, the lightning emission is off.
      // applyGasGiantLightning(material, { strength: 1.6 });
    } else if (planetName === 'saturn') {
      // Atmosphere / limb-haze removed at the user's request — the
      // soft tan veil was dulling the disc. Saturn keeps its band
      // drift + ring-shadow projection (applied above) and renders
      // crisp with no haze wash.
    } else if ((planetName === 'uranus' || planetName === 'neptune') && !lineup) {
      // Ice giants get a fully procedural surface — no texture, no
      // UV wrap, no seam ever. The textured material that was built
      // a few lines above is replaced with a ShaderMaterial running
      // proceduralGasGiant. The shader handles its own lambert +
      // limb darkening, so we lose Three.js's standard PBR lighting
      // for this one mesh, but the ice giants are nearly featureless
      // in canon anyway and the procedural pattern lands at the
      // same visual register without the seam problem.
      const cfg = PROCEDURAL_PLANET_CONFIGS[planetName];
      material.dispose();
      material = new THREE.ShaderMaterial({
        uniforms: {
          uTime:         { value: 0 },
          uSunDir:       { value: new THREE.Vector3(-1, 0, 0) },
          uPaletteDeep:  { value: new THREE.Color(cfg.paletteDeep) },
          uPaletteBelt:  { value: new THREE.Color(cfg.paletteBelt) },
          uPaletteZone:  { value: new THREE.Color(cfg.paletteZone) },
          uPaletteStorm: { value: new THREE.Color(cfg.paletteStorm) },
          uBandCount:    { value: cfg.bandCount },
          uTurbulence:   { value: cfg.turbulence },
          uStormLatDeg:  { value: cfg.stormLatDeg != null ? cfg.stormLatDeg : 0 },
          uStormLonDeg:  { value: cfg.stormLonDeg != null ? cfg.stormLonDeg : 0 },
          uStormSize:    { value: cfg.stormSize },
          uLimbDarken:   { value: cfg.limbDarken },
          uRimGlow:      { value: cfg.rimGlow },
          uCloudDetailStrength: { value: cfg.cloudDetailStrength != null ? cfg.cloudDetailStrength : 1.0 },
          uCloudDetailFreq:     { value: cfg.cloudDetailFreq != null ? cfg.cloudDetailFreq : 7.0 },
          uBandChaos:           { value: cfg.bandChaos != null ? cfg.bandChaos : 0.0 }
        },
        vertexShader: proceduralGasGiantVertexShader,
        fragmentShader: proceduralGasGiantFragmentShader,
        side: THREE.FrontSide,
        transparent: false
      });
    }
  } else {
    material = createSurfaceMaterial(data);
  }

  // Distance-based sunlight dimming. Custom shaders take it via the
  // uSunIntensity contract (they fall back to 1.0 when unwired, so
  // ordering here is safe); textured gas giants route through their
  // onBeforeCompile band uniforms; plain PBR materials get their
  // diffuse color scaled (clamped so Mercury doesn't clip white).
  // The lineup lights every body alike so sizes compare at one exposure.
  const sunIrradiance = lineup ? 1 : getSunIrradianceFactor(planetName);
  if (material.isShaderMaterial && material.uniforms) {
    material.uniforms.uSunIntensity = { value: sunIrradiance };
  } else if (material.userData && material.userData.bandUniforms) {
    material.userData.bandUniforms.uSunIntensity.value = sunIrradiance;
  } else if (material.color) {
    material.color.multiplyScalar(Math.min(sunIrradiance, 1.3));
  }

  attachMoonTransitShadows(material, planetGroup, data);
  const planetMesh = new THREE.Mesh(geometry, material);
  planetMesh.castShadow = false;  // Disable shadows to prevent artifacts
  planetMesh.receiveShadow = false;
  // Tag the surface mesh so the localhost planet tuner can find it
  // unambiguously without falling into a ring or atmosphere mesh
  // when it walks the planet group looking for the body to retext.
  planetMesh.userData.isPlanetSurface = true;
  planetMesh.userData.planetKey = planetName;
  applyAnalyticSphereRaycast(planetMesh, data.radius);
  // Procedural-surface bodies need their uTime / uSunDir uniforms
  // refreshed each frame. Stash them in the registry the animate()
  // loop reads from. Stash AFTER planetMesh exists so the registry
  // entry includes the live mesh ref (for world-space lookups
  // during the sun-direction update).
  if (!lineup && PROCEDURAL_PLANET_CONFIGS[planetName] && material.isShaderMaterial && material.uniforms.uSunDir) {
    proceduralPlanetSurfaces[planetName] = {
      mesh: planetMesh,
      uniforms: material.uniforms
    };
  }

  // Create a pivot group for the tilted axis rotation
  const tiltGroup = new THREE.Group();
  if (planetName === 'earth') {
    // Earth's axis points at the J2000 celestial pole, tipped toward
    // ecliptic longitude 90 (scene -Z), with +X left on the equinox so the
    // spin angle is the Earth rotation angle (src/orbits/earthframe.js).
    // Other planets keep the display tilt about world Z. ?whatif=notilt
    // stands it up (src/whatiftilt.js; the frame loop keeps it in step).
    tiltGroup.rotation.x = TILT_ON && !lineup ? -THREE.MathUtils.degToRad(tiltState.obliquity) : -EARTH_OBLIQUITY;
  } else {
    tiltGroup.rotation.z = THREE.MathUtils.degToRad(data.tilt);
  }
  tiltGroup.add(planetMesh);
  tiltGroup.name = 'tiltGroup';
  planetGroup.add(tiltGroup);
  if (planetName === 'earth' && planetMesh.material.uniforms) {
    // World-space spin axis for the polar-ice lift in earth.js, which
    // falls back to world +Y (the ecliptic pole) when this is unset. The
    // planet group only translates, so the tilt alone sets it.
    planetMesh.material.uniforms.uSpinAxis = {
      value: new THREE.Vector3(0, 1, 0).applyQuaternion(tiltGroup.quaternion)
    };
  }

  // Atmospheric shell for any body flagged hasAtmosphere. Earth is
  // wrapped by hasAtmosphere too, just with its own tuned colour +
  // shell radius. Venus (thick sulphuric cream), Mars (thin rusty tan)
  // share the same shader with different uniforms. Shell scale comes
  // from body data (real atmospheres vary from ~1% of Earth's radius
  // up to ~23% of Titan's).
  if (data.hasAtmosphere) {
    const shellScale = data.atmosphereScale || 1.025;
    const atmColor   = data.atmosphereColor   || 0x7db8ff;
    const atmStrength = data.atmosphereStrength !== undefined
      ? data.atmosphereStrength
      : 0.75;
    const segments = graphicsConfig.atmosphereSegments;
    const atmosphereGeometry = new THREE.SphereGeometry(
      data.radius * shellScale,
      segments,
      Math.max(32, Math.floor(segments / 2))
    );
    // Per-body scatter tints. Earth keeps its canonical sky-blue
    // Rayleigh. Venus is Mie-dominated sulphuric haze so Rayleigh
    // goes nearly to zero and the tint itself is warm yellow — no
    // blue component leaking in. Mars gets a rusty red tint with
    // low strength (thin atmosphere). Titan gets amber.
    const rayleighTint = new THREE.Color(
      data.rayleighTint !== undefined ? data.rayleighTint : 0x4c99ff
    );
    const rayleighStrengthMul = data.rayleighStrength !== undefined
      ? data.rayleighStrength
      : 1.0;
    const limbTint = new THREE.Color(
      data.limbTint !== undefined ? data.limbTint : 0xb3d9ff
    );
    // Earth gets a polar-aurora green baked into the atmosphere
    // shader. Other bodies pass auroraStrength = 0 and skip the
    // northern-lobe blend entirely. This replaces the previous
    // particle-based aurora — a single shader on the existing
    // atmosphere mesh has no bloom-pile-up risk, no extra draw
    // calls, and the aurora-scattering remains realistic-looking.
    const auroraTint = data.auroraTint !== undefined
      ? new THREE.Color(data.auroraTint)
      : new THREE.Color(0x4cff8c);
    const auroraStrength = data.auroraStrength !== undefined
      ? data.auroraStrength
      : 0;
    // Share the same moon shadow uniforms the surface material
    // registered. Surface mesh is attached above (line 8574), which
    // stores the shadow uniforms on planetGroup.userData. By
    // pointing the atmosphere uniforms at the SAME .value object
    // refs, both shaders update in lockstep when the per-frame
    // updateMoonTransitShadows writes new positions/radii.
    const sharedMoonShadow = planetGroup.userData.moonShadowUniforms;
    const atmoMoonCount = sharedMoonShadow ? sharedMoonShadow.count : { value: 0 };
    const atmoMoonPositions = sharedMoonShadow
      ? sharedMoonShadow.positions
      : { value: Array.from({ length: 8 }, () => new THREE.Vector3()) };
    const atmoMoonRadii = sharedMoonShadow
      ? sharedMoonShadow.radii
      : { value: new Float32Array(8) };

    const atmosphereMaterial = new THREE.ShaderMaterial({
      uniforms: {
        sunPosition: { value: new THREE.Vector3(0, 0, 0) },
        atmosphereColor: { value: new THREE.Color(atmColor) },
        atmosphereStrength: { value: atmStrength },
        // The viewer's low-orbit sky dome takes over close in (earthclose.js).
        uShellCloseFade: { value: 0 },
        earthDensityLimb: { value: planetName === 'earth' ? 1 : 0 },
        rayleighTint: { value: rayleighTint },
        rayleighStrengthMul: { value: rayleighStrengthMul },
        limbTint: { value: limbTint },
        // Terminator twilight colour. Default matches the shader's
        // previous hardcoded Earth amber; Mars overrides with the
        // dust-scatter blue of Curiosity sunset imagery, Venus with
        // sulphuric cream.
        twilightTint: {
          value: new THREE.Color(
            data.twilightTint !== undefined ? data.twilightTint : 0xff804d
          )
        },
        // Backlit forward-scatter ring (Venus "ring of fire" near
        // inferior conjunction). 0 for everything except dense
        // atmospheres that set data.forwardScatterRing.
        forwardRingStrength: { value: data.forwardScatterRing || 0 },
        auroraTint: { value: auroraTint },
        auroraStrength: { value: auroraStrength },
        time: { value: 0 },
        // Ray-traced planet shadow on the atmosphere. planetCenter
        // gets refreshed each frame as the planet orbits.
        // planetRadius is the body radius (NOT atmosphere radius)
        // since we're casting a shadow OF the body ONTO the
        // atmosphere fragments.
        planetCenter: { value: new THREE.Vector3(0, 0, 0) },
        planetRadius: { value: data.radius },
        // Atmosphere top radius for Hillaire chord-length scattering.
        // shellScale is the multiplier the atmosphere SphereGeometry
        // was built with (data.atmosphereScale, default 1.025), so
        // the actual shell outer radius is data.radius * shellScale.
        atmosphereRadius: { value: data.radius * shellScale },
        // Shared with surface mesh for eclipse-on-atmosphere.
        moonShadowCount: atmoMoonCount,
        moonShadowPositions: atmoMoonPositions,
        moonShadowRadii: atmoMoonRadii,
        // The Earth's shell shares the surface's real Moon shadow.
        ...(planetGroup.name === 'earth' ? earthEclipseUniforms : {}),
        ...(EARTH_RINGS_ON && !lineup && planetGroup.name === 'earth' ? earthRingUniforms : {})
      },
      ...(EARTH_RINGS_ON && !lineup && planetGroup.name === 'earth' ? { defines: { EARTH_RINGS: '' } } : {}),
      vertexShader: earthAtmosphereVertexShader,
      fragmentShader: earthAtmosphereFragmentShader,
      transparent: true,
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    const atmosphere = new THREE.Mesh(atmosphereGeometry, atmosphereMaterial);
    atmosphere.name = 'atmosphere';
    // Visual shell only — picking goes through the surface mesh.
    // High-segment BackSide spheres are expensive to triangle-scan
    // in the recursive hover raycast and should never win a pick.
    atmosphere.raycast = () => {};
    // Authored radii stash so applyRealisticBodyScales /
    // resetArtisticBodyScales can rescale the planetRadius /
    // atmosphereRadius uniforms in lockstep with the mesh scale —
    // chord-length limb scattering and the planet shadow cone both
    // compute against these radii in world space.
    atmosphere.userData.baseAtmoRadii = {
      planet: data.radius,
      shell: data.radius * shellScale
    };
    // Render order = 10 so the atmosphere shell draws AFTER the
    // sun (sunMesh: 6, sunGlowSprite: 5). With AdditiveBlending,
    // any path through the atmosphere shell that reaches the sun
    // ends up with the atmosphere's blue scatter ADDED over the
    // solar disc / corona. From ISS-close perspectives this reads
    // as "sun seen through hazy atmosphere" — exactly what real
    // sub-orbital imagery shows — instead of the sun punching
    // through the atmospheric halo as a sharp bright disc.
    atmosphere.renderOrder = 10;
    planetGroup.add(atmosphere);
  }

  if (planetName === 'earth' && !lineup) {
    // The aurora as emission in its shell, night side only (the old cone
    // curtains and the atmosphere's polar tint drew green on the disc).
    createEarthAuroras(planetMesh);
  }

  if (planetName === 'jupiter' && !lineup) {
    // Jupiter has the strongest aurorae in the solar system —
    // ~10× brighter than Earth's and visible from Hubble UV
    // imagery as persistent oval rings around both magnetic poles.
    // Built using the same cone-curtain machinery as Earth.
    createJupiterAuroras(tiltGroup, data);
  }

  // Saturn/Uranus rings (add to tilt group so they rotate with planet)
  if (data.hasRings) {
    const ringGeometry = new THREE.RingGeometry(
      data.ringInnerRadius,
      data.ringOuterRadius,
      128
    );

    // Fix UV mapping for radial texture
    const pos = ringGeometry.attributes.position;
    const uv = ringGeometry.attributes.uv;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      const dist = Math.sqrt(x * x + y * y);
      const normalizedDist = (dist - data.ringInnerRadius) / (data.ringOuterRadius - data.ringInnerRadius);
      uv.setXY(i, normalizedDist, 0.5);
    }

    let ringMaterial;
    if (loadedTextures.saturn_ring && planetName === 'saturn') {
      // Saturn rings use a custom shader so the planet can cast an
      // analytic cylindrical shadow across them.
      ringMaterial = new THREE.ShaderMaterial({
        uniforms: {
          ringTexture: { value: loadedTextures.saturn_ring },
          sunPosition: { value: new THREE.Vector3(0, 0, 0) },
          planetCenter: { value: new THREE.Vector3() },
          planetRadius: { value: data.radius },
          opacity: { value: 0.92 },
          // Spoke uniforms — driven from animate() each frame.
          // Strength baked into the material; only Saturn enables
          // spokes (Uranus uses a different shader entirely).
          spokeTime:     { value: 0 },
          spokeStrength: { value: 1.0 },
          // Rings share the planet's distance-based sunlight level;
          // the angular radius drives the physical penumbra width of
          // the planet's shadow and is refreshed per frame.
          uSunIntensity: { value: sunIrradiance },
          uSunAngularRadius: { value: 0 },
          // The strip is Cassini's: the real gaps and optical depth are in its
          // alpha, so the shader's procedural divisions stand aside
          // (src/shaders/rings.js; 0 restores the old painted strip's path).
          realStrip: { value: 1 }
        },
        // Unlit face by single scattering, the background dimmed along the
        // slant path (src/shaders/rings.js).
        defines: { RING_PHOTOMETRY: 1 },
        vertexShader: saturnRingVertexShader,
        fragmentShader: saturnRingFragmentShader,
        transparent: true,
        side: THREE.DoubleSide,
        depthWrite: false
      });
    } else {
      // Procedural ring for Uranus
      ringMaterial = new THREE.ShaderMaterial({
        uniforms: {
          innerRadius: { value: data.ringInnerRadius },
          outerRadius: { value: data.ringOuterRadius },
          ringColor1: { value: new THREE.Color(planetName === 'saturn' ? 0xc9a227 : 0x88aacc) },
          ringColor2: { value: new THREE.Color(planetName === 'saturn' ? 0x8b7355 : 0x6688aa) },
          sunPosition: { value: new THREE.Vector3(0, 0, 0) },
          planetCenter: { value: new THREE.Vector3() },
          planetRadius: { value: data.radius },
          shadowStrength: { value: planetName === 'saturn' ? 0.78 : 0.55 },
          opacity: { value: planetName === 'saturn' ? 0.9 : 0.4 },
          uSunIntensity: { value: sunIrradiance },
          uSunAngularRadius: { value: 0 }
        },
        vertexShader: simpleRingVertexShader,
        fragmentShader: proceduralRingFragmentShader,
        transparent: true,
        side: THREE.DoubleSide,
        depthWrite: false
      });
    }

    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.rotation.x = Math.PI / 2;
    ring.name = 'ring';
    // The ring shaders test a world-space shadow cylinder of radius
    // planetRadius against world-space fragment positions, so that uniform
    // has to track the body scale (see applyRingShadowScale). Stash the
    // authored value the same way the atmosphere stashes baseAtmoRadii.
    ring.userData.baseRingPlanetRadius = data.radius;
    // Add ring to tilt group so it rotates correctly with planet's axis
    tiltGroup.add(ring);
    if (planetName === 'saturn' && ring.material.uniforms.spokeTime) {
      // The dusty G and E rings (src/shaders/rings.js), which only show
      // with the Sun behind them. Same frame as the main ring.
      const dust = new THREE.Mesh(
        new THREE.RingGeometry(data.radius * 2.6, data.radius * 8.6, 192, 1),
        new THREE.ShaderMaterial({
          uniforms: {
            sunPosition: { value: new THREE.Vector3(0, 0, 0) },
            planetCenter: { value: new THREE.Vector3() },
            planetRadius: { value: data.radius },
            uBodyRadius: { value: data.radius },
            uSunIntensity: { value: sunIrradiance },
            uGain: { value: 0.012 },
            // 2,250 km, half the E ring's 4,000 to 5,000 km FWHM at
            // Enceladus's orbit (Hedman et al. 2012), in Saturn radii.
            uSlabHalf: { value: 2250 / 58232 },
            uOccluders: { value: [new THREE.Vector4(), new THREE.Vector4(), new THREE.Vector4(), new THREE.Vector4()] },
            uOccluderCount: { value: 0 },
          },
          vertexShader: saturnDustRingVertexShader,
          fragmentShader: saturnDustRingFragmentShader,
          transparent: true,
          side: THREE.DoubleSide,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        })
      );
      dust.rotation.x = Math.PI / 2;
      dust.name = 'dust-rings';
      dust.renderOrder = 1;
      dust.userData.baseRingPlanetRadius = data.radius;
      // The named moons nearest the camera, in view space for the camera
      // drawing it, so the dust in front of them is only what lies before
      // them (saturnDustRingFragmentShader).
      dust.onBeforeRender = (_renderer, _scene, cam) => updateDustRingOccluders(dust, cam);
      ring.userData.dust = dust;
      tiltGroup.add(dust);
    }
    // The scale may already be applied when a session boots straight into
    // realistic mode, in which case the uniform would start stale.
    applyRingShadowScale(tiltGroup, tiltGroup.scale.x || 1);
  }

  // Saturn's rings round the Earth (?whatif=rings, src/earthrings.js): the
  // per-frame planet loop finds them as 'ring' like any other.
  if (planetName === 'earth' && !lineup && EARTH_RINGS_ON && loadedTextures.saturn_ring) {
    tiltGroup.add(createEarthRingMesh({ radius: data.radius, texture: loadedTextures.saturn_ring, sunIntensity: sunIrradiance }));
    setEarthRingTau(ringTauTable(loadedTextures.saturn_ring.image));
    applyRingShadowScale(tiltGroup, tiltGroup.scale.x || 1);
  }

  return planetGroup;
}

const MINOR_MOON_SWARM_CAP = 140;

// Populate the gap between this planet's documented moon count and the moons
// we have detailed data for, using small procedurally generated bodies.
function createMinorMoonSwarm(planetGroup, data) {
  const named = (data.majorMoons ? data.majorMoons.length : 0) + (data.moon ? 1 : 0);
  // Capped: Saturn's count grew from 146 to 285 in two years of survey
  // announcements, and each swarm moon is its own mesh. The swarm hints at
  // the many small moons; it is not a census.
  const remaining = Math.min(Math.max(0, (data.moons || 0) - named), MINOR_MOON_SWARM_CAP);
  if (remaining === 0) return;

  const majorMax = data.majorMoons && data.majorMoons.length
    ? Math.max(...data.majorMoons.map((m) => m.distance))
    : (data.moon ? data.moon.distance : data.radius * 4);
  const innerDist = majorMax * 1.15;
  const outerDist = Math.max(innerDist * 2.2, data.radius * 12);

  for (let i = 0; i < remaining; i++) {
    // Stratified spacing + jitter so they don't stack into a perfect ring
    const frac = (i + Math.random() * 0.75) / remaining;
    const distance = innerDist + (outerDist - innerDist) * frac;
    const radius = 0.04 + Math.random() * 0.1;
    const inclination = (Math.random() - 0.5) * 28;
    const angle = Math.random() * Math.PI * 2;
    const direction = Math.random() > 0.92 ? -1 : 1;
    const orbitalSpeed = direction * (0.004 + Math.random() * 0.018);
    const grey = 90 + Math.floor(Math.random() * 90);

    const geometry = new THREE.SphereGeometry(radius, 12, 12);
    const material = createSurfaceMaterial({
      texture: 'minor_moon',
      type: 'Natural Satellite',
      colors: [`rgb(${grey}, ${Math.max(0, grey - 8)}, ${Math.max(0, grey - 18)})`]
    });
    const mesh = new THREE.Mesh(geometry, material);
    const moonGroup = new THREE.Group();
    moonGroup.add(mesh);
    // -sin on z matches the per-frame moon driver's orbit sense
    moonGroup.position.x = Math.cos(angle) * distance;
    moonGroup.position.z = -Math.sin(angle) * distance;
    moonGroup.position.y = Math.sin(angle) * distance * Math.sin(THREE.MathUtils.degToRad(inclination)) * 0.3;

    moonGroup.userData = {
      name: `${planetGroup.name.charAt(0).toUpperCase() + planetGroup.name.slice(1)} satellite`,
      isMinorMoon: true,
      parentPlanet: planetGroup.name,
      distance,
      baseDistance: distance,
      radius,
      orbitalSpeed,
      inclination,
      orbitAngle: angle
    };

    // Same tilted moon frame as the named moons (Earth excepted).
    getMoonOrbitFrame(planetGroup).add(moonGroup);

    if (!moons[planetGroup.name]) {
      moons[planetGroup.name] = [];
    }
    moons[planetGroup.name].push(moonGroup);
  }
  markClickablesDirty();
}

// Parent frame for a planet's moons and moon orbit lines. Real moons
// orbit close to their planet's EQUATORIAL plane, not the ecliptic —
// most visibly at Uranus, whose moons share its ~98° tilted ring
// plane. The moonFrame copies the tiltGroup's axial tilt but NOT its
// scale: realistic mode shrinks the tiltGroup to true body size, and
// that scale must not collapse moon orbital distances (those come from
// getRealisticMoonDistance). Earth is the exception —
// the real Moon orbits near the ecliptic (5.1° inclination), so its
// moons stay directly under the planet group.
function getMoonOrbitFrame(parentPlanet) {
  if (parentPlanet.name === 'earth') return parentPlanet;
  let frame = parentPlanet.getObjectByName('moonFrame');
  if (!frame) {
    frame = new THREE.Group();
    frame.name = 'moonFrame';
    const tiltGroup = parentPlanet.getObjectByName('tiltGroup');
    if (tiltGroup) frame.rotation.copy(tiltGroup.rotation);
    parentPlanet.add(frame);
  }
  return frame;
}

// Create a moon for a planet
// Moons drawn in their real shape, by celestialData's `shape` key.
const MOON_SHAPES = { phobos: PHOBOS_SHAPE, deimos: DEIMOS_SHAPE };
// How far a body reaches past its mean radius (Phobos's long axis 1.26), so
// the zoom floor keeps the camera outside it; 1 for a sphere.
function bodyShapeReach(data) {
  const shape = data && MOON_SHAPES[data.shape];
  // Small bodies with shape models carry their reach (src/smallbodies/).
  return shape ? shape.max : (data && data.shapeReach) || 1;
}

// Titan's haze (src/shaders/titanHaze.js). Altitudes are measured from the
// drawn sphere, which is Titan's solid radius, 2,575 km. The detached
// layer's height is Cassini's 2005 value (500 km, PIA06090; Seignovert et
// al. 2017, arXiv:1804.10842). The main haze's scale height and optical
// depth, the layer's width and depth, and the light scale were fitted to
// Cassini's natural-colour frames: PIA14602 (the limb profile and its blue
// fringe), PIA06230, PIA21625 and PIA17180 (backlit).
const TITAN_RADIUS_KM = 2574.7;
// The deck's grade over the map's measured colour (linear): the golden disc
// of Cassini's natural-colour releases (PIA06230, PIA14602, PIA21890: sRGB
// R/G 1.2, B/G 0.4 to 0.6), and their exposure, which shows Titan about as
// bright as the icy moons beside it where its albedo (0.23) is a fifth of
// theirs. The haze above lets about a third of it through at the disc's
// centre, on the way in and out. A function, so the material profile can
// call it before this line has run.
function titanDeckTint() {
  return new THREE.Color().setRGB(7.8, 6.2, 2.6, THREE.LinearSRGBColorSpace);
}
const TITAN_HAZE = {
  topKm: 640,
  scaleHeightKm: 70,
  tangentTauOneKm: 150,
  detachedKm: 500,
  detachedSigmaKm: 18,
  detachedTau: 0.04,
  mainAlbedo: [0.98, 0.66, 0.15],
  thinAlbedo: [0.90, 0.85, 0.72],
  thinFromKm: 380,
  thinToKm: 560,
  forwardG: 0.55,
  light: 3.6
};
const _titanHazeSun = new THREE.Vector3();
const _titanHazeCentre = new THREE.Vector3();
const _titanHazeScale = new THREE.Vector3();
// Share of the Sun's disc (angular radius rs) hidden by a disc of angular
// radius ro whose centre is d away; the JS twin of the moon shader's cover.
function sunDiscCover(rs, ro, d) {
  if (d >= rs + ro) return 0;
  if (ro > 8 * rs) {
    const h = THREE.MathUtils.clamp((ro - d) / rs, -1, 1);
    return (Math.acos(-h) + h * Math.sqrt(1 - h * h)) / Math.PI;
  }
  if (d <= Math.abs(ro - rs)) return ro >= rs ? 1 : (ro * ro) / (rs * rs);
  const a = THREE.MathUtils.clamp((d * d + rs * rs - ro * ro) / (2 * d * rs), -1, 1);
  const b = THREE.MathUtils.clamp((d * d + ro * ro - rs * rs) / (2 * d * ro), -1, 1);
  const k = Math.max(0, (-d + rs + ro) * (d + rs - ro) * (d - rs + ro) * (d + rs + ro));
  return (rs * rs * Math.acos(a) + ro * ro * Math.acos(b) - 0.5 * Math.sqrt(k)) / (Math.PI * rs * rs);
}
function createTitanHaze(moonData, parentPlanet) {
  const R = TITAN_RADIUS_KM;
  const H = TITAN_HAZE.scaleHeightKm / R;
  const hTau = TITAN_HAZE.tangentTauOneKm / R;
  // Main haze extinction at the deck (per body radius) that puts a tangent
  // optical depth of one at tangentTauOneKm: tau = b0 exp(-h/H) sqrt(2 pi r H).
  const b0 = 1 / (Math.exp(-hTau / H) * Math.sqrt(2 * Math.PI * (1 + hTau) * H));
  // The detached layer is a Gaussian shell; seen edge on at its own height
  // its optical depth is b * 2 Gamma(5/4) (8 r^2 sigma^2)^(1/4).
  const sigma = TITAN_HAZE.detachedSigmaKm / R;
  const rDet = 1 + TITAN_HAZE.detachedKm / R;
  const detB = TITAN_HAZE.detachedTau / (1.8128 * Math.pow(8 * rDet * rDet * sigma * sigma, 0.25));
  const topR = 1 + TITAN_HAZE.topKm / R;
  // The sunlight the deck's material receives (the Sun light's intensity
  // and the outer planets' exposure factor), so haze and deck agree.
  const light = 3.1 * Math.min(getSunIrradianceFactor(parentPlanet && parentPlanet.name), 1.3);
  const uniforms = {
    uBodyLocalR: { value: moonData.radius },
    uSunDirView: { value: new THREE.Vector3(0, 0, 1) },
    uSunE: { value: light * TITAN_HAZE.light },
    uTopR: { value: topR },
    uH: { value: H },
    uB0: { value: b0 },
    uDetH: { value: rDet - 1 },
    uDetW: { value: sigma },
    uDetB: { value: detB },
    uOpacity: { value: 1 },
    uMainAlb: { value: new THREE.Vector3().fromArray(TITAN_HAZE.mainAlbedo) },
    uThinAlb: { value: new THREE.Vector3().fromArray(TITAN_HAZE.thinAlbedo) },
    uThinRange: { value: new THREE.Vector2(TITAN_HAZE.thinFromKm / R, TITAN_HAZE.thinToKm / R) },
    uG: { value: TITAN_HAZE.forwardG }
  };
  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: titanHazeVertexShader,
    fragmentShader: titanHazeFragmentShader,
    transparent: true,
    depthWrite: false,
    // Premultiplied: the haze's light plus what lies behind it times its
    // transmittance.
    blending: THREE.CustomBlending,
    blendEquation: THREE.AddEquation,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.SrcAlphaFactor,
    side: THREE.FrontSide
  });
  const geometry = new THREE.SphereGeometry(moonData.radius * topR, 96, 64);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'moonHaze';
  mesh.raycast = () => {};
  let shadowReg = null;
  mesh.onBeforeRender = (_renderer, _scene, cam) => {
    mesh.getWorldPosition(_titanHazeCentre);
    if (sun) sun.getWorldPosition(_titanHazeSun); else _titanHazeSun.set(0, 0, 0);
    _titanHazeSun.sub(_titanHazeCentre).normalize().transformDirection(cam.matrixWorldInverse);
    uniforms.uSunDirView.value.copy(_titanHazeSun);
    // In Saturn's shadow the haze goes dark with the deck: the deck's own
    // shadow uniforms (attachParentPlanetShadow), taken at Titan's centre.
    const deck = mesh.parent && mesh.parent.children[0];
    if (!shadowReg || shadowReg.moonObject !== deck || shadowReg.material !== deck.material) {
      shadowReg = parentShadowRegistrations.find((r) => r.moonObject === deck && r.material === deck.material) || null;
    }
    let lit = 1;
    const su = shadowReg && shadowReg.uniforms;
    if (su && su.uParentOffset && su.uParentRadius.value > 0) {
      const off = su.uParentOffset.value;
      const d = off.length();
      const cosSep = off.dot(su.uParentSunDir.value) / Math.max(d, 1e-12);
      if (d > su.uParentRadius.value && cosSep > 0) {
        const ro = Math.asin(Math.min(1, su.uParentRadius.value / d));
        lit = 1 - sunDiscCover(su.uSunAngR.value, ro, Math.acos(Math.min(1, cosSep)));
      }
    }
    uniforms.uSunE.value = light * TITAN_HAZE.light * lit;
    // Outside the shell its front faces cover the disc, and the depth test
    // keeps whatever is in front of Titan clear of haze. Inside, the back
    // faces draw it and the shader stops each ray at the deck.
    mesh.getWorldScale(_titanHazeScale);
    const inside = cam.position.distanceTo(_titanHazeCentre) < moonData.radius * topR * _titanHazeScale.x * 1.002;
    const side = inside ? THREE.BackSide : THREE.FrontSide;
    if (material.side !== side) {
      material.side = side;
      material.depthTest = !inside;
      material.needsUpdate = true;
    }
  };
  return mesh;
}

function createMoon(parentPlanet, moonData) {
  const moonGroup = new THREE.Group();
  moonGroup.name = moonData.name;

  // Earth's actual Moon gets a denser geometry so the displacement
  // map can deform vertices into visible crater relief at close zoom.
  // 384 segments → ~150k vertices, enough resolution that the 4K
  // bump map's mid-frequency crater-rim detail shows up as real
  // bumpy silhouette rather than smooth-sphere-with-shading. Other
  // moons stay on the cheap default to keep total mesh load sane
  // (every gas giant has a handful of named moons).
  const isEarthMoon = moonData.texture === 'moon' && moonData.name === 'Moon';
  // A moon with a shape model takes its real shape (src/bodies/shapeModel.js),
  // which needs enough vertices at every preset to show it.
  const shape = MOON_SHAPES[moonData.shape] || null;
  const segs = isEarthMoon
    ? Math.max(384, graphicsConfig.moonSegments * 6)
    : shape ? Math.max(64, graphicsConfig.moonSegments) : graphicsConfig.moonSegments;
  const geometry = new THREE.SphereGeometry(moonData.radius, segs, segs);
  if (shape) applyShapeModel(geometry, shape);
  let material;

  if (loadedTextures[moonData.texture]) {
    // Bump map sourcing. Earth's Moon has a dedicated heightmap;
    // every other named moon uses its colour texture as a
    // luminance-derived bump (cracks/craters correlate with
    // brightness on icy bodies). Smaller / unrelief moons get no
    // bump at all (their bumpScale is 0 in the surface profile so
    // the bumpMap would be a no-op anyway).
    let bumpMap = null;
    const tex = moonData.texture;
    if (isEarthMoon) {
      bumpMap = loadedTextures.moon_bump || null;
    } else if (tex === 'europa') {
      bumpMap = loadedTextures.europa_bump || loadedTextures.europa || null;
    } else if (tex === 'callisto' || tex === 'ganymede' || tex === 'io') {
      bumpMap = loadedTextures[tex] || null;
    }
    material = createSurfaceMaterial(moonData, {
      texture: loadedTextures[moonData.texture],
      bumpMap
    });
  } else {
    material = createSurfaceMaterial(moonData, { color: 0xaaaaaa });
    // Texture not ready at creation time (eager load timed out, or
    // the user landed on a fast path where createMoon ran before
    // loadTextures finished its work for this slot). Kick off a
    // resilient retry that swaps the material in once the bitmap
    // arrives — without this the moon stays pale grey forever.
    queueMoonTextureRetry(moonGroup, moonData);
  }

  const moonMesh = new THREE.Mesh(geometry, material);
  moonMesh.castShadow = false;
  moonMesh.receiveShadow = false;
  applyAnalyticSphereRaycast(moonMesh, moonData.radius);
  // Distance-based sunlight dimming, same compressed falloff as the
  // parent planet. Applied BEFORE the baseColor cache below so the
  // per-frame umbra darkening treats the dimmed tint as its
  // reference and the two effects compose instead of fighting.
  if (material.color) {
    material.color.multiplyScalar(
      Math.min(getSunIrradianceFactor(parentPlanet.name), 1.3)
    );
  }
  // Cache the base tint so the per-frame umbra darkening can multiply
  // it down and back up without drift. Both the textured and solid-
  // colour paths store .color on MeshLambertMaterial, so one cache
  // covers both branches.
  moonMesh.userData.baseColor = material.color.clone();

  // The parent planet's shadow (attachMoonSurface). Without it every moon
  // stays fully lit regardless of where the parent body is, which is the
  // "videogame moon" look the user wanted to escape.
  attachMoonSurface(moonMesh, moonData, parentPlanet);

  moonGroup.add(moonMesh);

  // Titan's haze is a raymarched volume over the deck the surface mesh
  // draws (createTitanHaze). Any other moon with air would take the
  // planet atmosphere shell below.
  if (moonData.hazeVolume) {
    moonGroup.add(createTitanHaze(moonData, parentPlanet));
  } else if (moonData.hasAtmosphere) {
    const shellScale = moonData.atmosphereScale || 1.1;
    const atmColor   = moonData.atmosphereColor || 0xe8a058;
    const atmStrength = moonData.atmosphereStrength !== undefined
      ? moonData.atmosphereStrength
      : 0.6;
    const moonAtmGeometry = new THREE.SphereGeometry(
      moonData.radius * shellScale,
      graphicsConfig.moonSegments,
      graphicsConfig.moonSegments
    );
    // Titan's haze is Mie-dominated, amber all the way through. Mirror
    // the planet path: every atmosphere shell gets per-body Rayleigh
    // and limb tints so Earth's sky-blue doesn't leak onto moons.
    const moonRayleighTint = new THREE.Color(
      moonData.rayleighTint !== undefined ? moonData.rayleighTint : 0xc49058
    );
    const moonRayleighStrengthMul = moonData.rayleighStrength !== undefined
      ? moonData.rayleighStrength
      : 0.15;
    const moonLimbTint = new THREE.Color(
      moonData.limbTint !== undefined ? moonData.limbTint : 0xe8a058
    );
    const moonAtmMaterial = new THREE.ShaderMaterial({
      uniforms: {
        sunPosition: { value: new THREE.Vector3(0, 0, 0) },
        atmosphereColor: { value: new THREE.Color(atmColor) },
        atmosphereStrength: { value: atmStrength },
        // The viewer's low-orbit sky dome takes over close in (earthclose.js).
        uShellCloseFade: { value: 0 },
        rayleighTint: { value: moonRayleighTint },
        rayleighStrengthMul: { value: moonRayleighStrengthMul },
        limbTint: { value: moonLimbTint },
        // Same per-body twilight/forward-ring contract as planets.
        // Titan's thick haze gets a deep-orange terminator and a
        // mild backlit ring (Cassini saw its full halo regularly).
        twilightTint: {
          value: new THREE.Color(
            moonData.twilightTint !== undefined ? moonData.twilightTint : 0xff804d
          )
        },
        forwardRingStrength: { value: moonData.forwardScatterRing || 0 }
      },
      vertexShader: earthAtmosphereVertexShader,
      fragmentShader: earthAtmosphereFragmentShader,
      transparent: true,
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    const moonAtm = new THREE.Mesh(moonAtmGeometry, moonAtmMaterial);
    moonAtm.name = 'moonAtmosphere';
    moonAtm.raycast = () => {};
    moonGroup.add(moonAtm);
  }

  // Set initial position (-sin matches the per-frame moon driver)
  const angle = Math.random() * Math.PI * 2;
  moonGroup.position.x = Math.cos(angle) * moonData.distance;
  moonGroup.position.z = -Math.sin(angle) * moonData.distance;

  registerRealMoonRadius(moonData);

  // Store full moon data for info card, mark as clickable moon
  moonGroup.userData = {
    ...moonData,
    baseDistance: moonData.distance,
    orbitAngle: angle,
    isMoon: true,
    parentPlanet: parentPlanet.name
  };

  // Moons live inside the axial-tilt moonFrame (Earth excepted) so
  // they orbit in the planet's equatorial plane, not the ecliptic.
  getMoonOrbitFrame(parentPlanet).add(moonGroup);

  // Create orbit line for this moon
  createMoonOrbitLine(parentPlanet, moonData);
  const moonOrbitLine = moonOrbitLines[parentPlanet.name] && moonOrbitLines[parentPlanet.name][moonOrbitLines[parentPlanet.name].length - 1];
  if (moonOrbitLine) {
    moonOrbitLine.userData.moonRef = moonGroup;
  }

  if (!moons[parentPlanet.name]) {
    moons[parentPlanet.name] = [];
  }
  moons[parentPlanet.name].push(moonGroup);
  markClickablesDirty();
}

// Create realistic asteroid geometry with varied shapes
function createAsteroidGeometry(baseRadius, detail, seed = 0) {
  // Use different base geometries for variety
  let geometry;
  const shapeType = seed % 3;

  if (shapeType === 0) {
    geometry = new THREE.IcosahedronGeometry(baseRadius, detail);
  } else if (shapeType === 1) {
    geometry = new THREE.DodecahedronGeometry(baseRadius, detail);
  } else {
    geometry = new THREE.OctahedronGeometry(baseRadius, detail);
  }

  const positions = geometry.attributes.position;

  // Multi-octave noise for realistic rocky surface
  for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i);
    const y = positions.getY(i);
    const z = positions.getZ(i);

    const dist = Math.sqrt(x * x + y * y + z * z);

    // Multiple noise frequencies for realistic detail
    const noise1 = Math.sin(x * 3.0 + seed) * Math.cos(y * 3.0) * Math.sin(z * 3.0); // Large features
    const noise2 = Math.sin(x * 7.0 + seed * 2) * Math.cos(y * 7.0) * Math.sin(z * 7.0) * 0.4; // Medium
    const noise3 = Math.sin(x * 15.0 + seed * 3) * Math.cos(y * 15.0) * Math.sin(z * 15.0) * 0.15; // Small craters

    // Combine noise with crater-like depressions
    const craterNoise = Math.max(0, Math.sin(x * 10.0 + seed) * Math.sin(z * 10.0)) * 0.2;

    const offset = 1.0 + noise1 * 0.35 + noise2 + noise3 - craterNoise;
    const scale = (dist * offset) / dist;

    positions.setXYZ(i, x * scale, y * scale, z * scale);
  }

  geometry.computeVertexNormals();
  return geometry;
}

// The asteroid belt: JPL's numbered asteroids on their own orbits as points of
// light (src/sky/asteroids.js), loaded once the page is idle. They are drawn
// at their real brightness, nearly invisible as in every photograph from
// inside the belt, unless the Asteroids switch lights them (a display
// brightness by size, ASTEROID_LIT_OFFSET magnitudes above H).
let asteroidLayer = null;
const ASTEROID_LIT_OFFSET = 8;
// Artistic distances of the belt's edges (2.1 and 3.3 AU) in base units;
// Mars and Jupiter's own come from their data.
const ASTEROID_ARTISTIC_BELT = [240, 290];
const ASTEROID_ARTISTIC_AU = [1.524, 2.1, 3.3, 5.203];
function asteroidArtisticAnchors() {
  return [baseDistances.mars || 200, ASTEROID_ARTISTIC_BELT[0], ASTEROID_ARTISTIC_BELT[1], baseDistances.jupiter || 320];
}
// Same piecewise-linear map as the shader's artisticDistance.
function asteroidArtisticDistance(a) {
  const au = ASTEROID_ARTISTIC_AU, sc = asteroidArtisticAnchors();
  const k = a < au[1] ? 0 : a < au[2] ? 1 : 2;
  return sc[k] + (sc[k + 1] - sc[k]) * (a - au[k]) / (au[k + 1] - au[k]);
}
// The Asteroids switch remembers a choice per scale, off by default in both
// (Andre, 13 Oct 2026: lit read as too much against pictures of the belt).
let asteroidsLitReal = false;
let asteroidsLitArtistic = false;
try {
  asteroidsLitReal = localStorage.getItem('ss.asteroids.real') === '1';
  asteroidsLitArtistic = localStorage.getItem('ss.asteroids.artistic') === '1';
} catch { /* storage blocked */ }
function asteroidsLit() { return realisticScaleMode ? asteroidsLitReal : asteroidsLitArtistic; }
let showAsteroidsToggle = () => {};

function createAsteroidBelt() {
  const load = () => createAsteroids(renderer, {
    maxCount: skyMapUpgradeAllowed ? Infinity : 60000,
    named: asteroidBeltBodies.map((b) => b.elements).filter(Boolean),
  }).then((layer) => {
    asteroidLayer = layer;
    scene.add(layer.mesh);
  }).catch((err) => {
    console.warn('Asteroids failed to load:', err);
  });
  if (typeof requestIdleCallback === 'function') requestIdleCallback(load, { timeout: 5000 });
  else setTimeout(load, 3000);
}

// A belt body's place from its elements at the sim clock (J2000 ecliptic to
// the scene's (x, z, -y)).
const _beltEcl = [0, 0, 0];
function placeBeltBody(body) {
  const el = body.userData.elements;
  if (!el) return;
  const [x, y, z] = elementsPosition(el, simulationJD, ASTEROID_ELEMENTS_EPOCH_JD, _beltEcl);
  const scale = realisticScaleMode ? AU_TO_SCENE : (asteroidArtisticDistance(el.a) * scaleMultiplier) / el.a;
  body.position.set(x * scale, z * scale, -y * scale);
  const real = realisticScaleMode ? getRealisticBodyRadius((body.userData.name || '').toLowerCase()) : null;
  body.scale.setScalar(real && body.userData.radius ? real / body.userData.radius : 1);
}

// Ceres's Dawn map, with the map's own brightness as a bump (crater rims and
// pits; bumpScale 0.075) and 16x anisotropy so it stays sharp at a glance.
// The map holds Ceres's true reflectance (linear mean 0.065 for an albedo of
// 0.096, docs/textures/outer-moons.md), which drew it as a near-black ball;
// it is shown by the small bodies' display rule, 0.17 * (A / 0.12)^0.6
// (docs/textures/small-bodies.md, "Albedos"), which puts it between Hygiea
// and Pallas in brightness as in albedo: 0.149, a gain of 2.28 on the map.
const CERES_DISPLAY_GAIN = 0.17 * Math.pow(0.096 / 0.12, 0.6) / 0.0653;
function ceresSurfaceMaterial(texture) {
  if (texture.anisotropy !== 16) {
    texture.anisotropy = 16;
    texture.needsUpdate = true;
  }
  return new THREE.MeshStandardMaterial({
    map: texture,
    color: new THREE.Color(1, 1, 1).multiplyScalar(CERES_DISPLAY_GAIN),
    bumpMap: texture,
    bumpScale: 0.075,
    roughness: 0.95,
    metalness: 0.02
  });
}

function createAsteroidBeltBodies() {
  initSmallBodyModels();
  asteroidBeltBodies.forEach((body, index) => {
    const group = new THREE.Group();
    group.name = body.name;

    // Ceres is the only round body in the belt (hydrostatic equilibrium), a
    // sphere wearing the Dawn map (docs/textures/outer-moons.md).
    const isCeres = body.name === 'Ceres';
    // Vesta, Pallas and Hygiea: a smooth stand-in with their proportions
    // until the shape model arrives (src/smallbodies/).
    const model = isCeres ? null : smallBodyModel(body.name);
    const geometry = isCeres
      ? new THREE.SphereGeometry(body.radius, 64, 48)
      : model
        ? smallBodyPlaceholderGeometry(model, body.radius)
        : createAsteroidGeometry(body.radius, 1, index + 32);
    let material;
    if (model) {
      material = smallBodyPlaceholderMaterial(model);
    } else if (isCeres && loadedTextures.ceres) {
      material = ceresSurfaceMaterial(loadedTextures.ceres);
    } else {
      material = new THREE.MeshStandardMaterial({
        color: body.color,
        roughness: 0.98,
        metalness: 0.0,
        flatShading: !isCeres,
      });
    }
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = body.name;
    group.add(mesh);
    // The Dawn map is not in the boot set, so it is usually still loading
    // here, and the deferred stream's upgradeBodiesForTexture only knows the
    // planets and moons: put it on when it lands, or Ceres stays the plain
    // grey stand-in for the whole visit.
    if (isCeres && !loadedTextures.ceres) {
      loadTextureByName('ceres').then((texture) => {
        if (!texture || mesh.material.map) return;
        const plain = mesh.material;
        mesh.material = ceresSurfaceMaterial(texture);
        plain.dispose();
      });
    }

    group.userData = {
      name: body.name,
      type: body.type,
      description: body.description,
      realDiameter: body.realDiameter,
      radius: body.radius,
      distance: body.distance,
      orbitalSpeed: body.orbitalSpeed,
      inclination: body.inclination,
      elements: body.elements || null,
      orbitAngle: Math.random() * Math.PI * 2,
      isBeltBody: true,
      isPlanet: true
    };
    // True size at true scale (getBodyBaseRadius, placeBeltBody).
    registerRealMoonRadius(body);
    if (model) registerSmallBody(mesh, { radius: body.radius, owner: group });

    const angle = group.userData.orbitAngle;
    const incl = THREE.MathUtils.degToRad(body.inclination || 0);
    // -sin on z matches the per-frame belt-body driver
    group.position.x = Math.cos(angle) * body.distance;
    group.position.z = -Math.sin(angle) * body.distance;
    group.position.y = Math.sin(angle) * body.distance * Math.sin(incl) * 0.12;

    scene.add(group);
    beltBodyObjects.push(group);
  });
  markClickablesDirty();
}

// Build a vertical truncated-cone curtain around the magnetic pole.
// The cone sits radially outward from the polar circle and extends
// upward (along the local "up" direction at high latitudes). The
// shader paints longitudinally-folded vertical ribbons within it,
// feathered at the top — the visible aurora curtain shape.
//
// hemisphere = +1 builds the northern cone (positive Y); -1 flips
// the cone to the southern hemisphere by negating Y.
function createAuroraConeCurtain(earthRadius, latDeg, altLow, altHigh, hemisphere = 1, options = {}) {
  // Polar-circle anchor at the chosen latitude. cos(lat) gives the
  // ring's distance from the rotational axis; sin(lat) gives its
  // vertical offset from the equator.
  const latRad = THREE.MathUtils.degToRad(Math.abs(latDeg));
  const ringRadiusAxis = Math.cos(latRad);
  const ringHeightAxis = Math.sin(latRad);

  const bottomRadius = ringRadiusAxis * earthRadius * (1 + altLow);
  const topRadius    = ringRadiusAxis * earthRadius * (1 + altHigh);
  const bottomY      = hemisphere * ringHeightAxis * earthRadius * (1 + altLow);
  const topY         = hemisphere * ringHeightAxis * earthRadius * (1 + altHigh);
  const height       = Math.abs(topY - bottomY);
  const centerY      = (topY + bottomY) / 2;

  const geometry = new THREE.CylinderGeometry(
    hemisphere > 0 ? topRadius : bottomRadius,
    hemisphere > 0 ? bottomRadius : topRadius,
    height,
    192, 1, true
  );
  geometry.translate(0, centerY, 0);

  // Per-layer colour palette + fold parameters. Each cone instance
  // gets its own colour set and fold phase so the main oval and
  // diffuse shoulder layer don't lock visually.
  //
  // asymmetry — strength of the night-side equatorward stretch
  // (vertex displacement that broadens the oval on the magnetotail
  // side, recreating the real teardrop oval shape). 0 = perfectly
  // round oval; 0.35 = typical Earth main oval; 0.55 = wider
  // shoulder behaviour.
  //
  // diffuse — flag (0/1) that switches the fragment shader to a
  // smooth uniform-density mode for the diffuse shoulder layer.
  // Disables the high-contrast fold gaps that turn the main oval
  // into discrete ribbons, leaving a soft continuous glow.
  const {
    colBase  = new THREE.Color(0x55ff8a),
    colMid   = new THREE.Color(0x33ff66),
    colTop   = new THREE.Color(0xff5d8a),
    foldFreq = 1.0,
    foldPhase = 0.0,
    intensity = hemisphere > 0 ? 1.0 : 0.7,
    asymmetry = 0.0,
    diffuse = 0.0,
    name = hemisphere > 0 ? 'northernLightsCurtain' : 'southernLightsCurtain'
  } = options;

  const material = new THREE.ShaderMaterial({
    uniforms: {
      time: { value: 0 },
      intensity: { value: intensity },
      sunPosition: { value: new THREE.Vector3(0, 0, 0) },
      // Sun direction in MAGNETIC FRAME local space — used by the
      // vertex shader to bias the oval geometry equatorward on the
      // night side. Updated per frame from the host code.
      sunDirLocal: { value: new THREE.Vector3(1, 0, 0) },
      colBase: { value: colBase },
      colMid: { value: colMid },
      colTop: { value: colTop },
      foldFreq: { value: foldFreq },
      foldPhase: { value: foldPhase },
      asymmetry: { value: asymmetry },
      diffuse: { value: diffuse }
    },
    vertexShader: northernLightsVertexShader,
    fragmentShader: northernLightsFragmentShader,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    side: THREE.DoubleSide,
    blending: THREE.NormalBlending
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.renderOrder = 12;
  mesh.frustumCulled = false;
  // Purely visual shell — never a pick target. Without this the
  // recursive hover raycast triangle-scans every 192-segment cone.
  mesh.raycast = () => {};
  return mesh;
}

// Earth's aurora (earthAuroraFragmentShader in shaders/aurora.js): emission
// in a shell 85 to 330 km up, marched per pixel inside one sphere at the
// shell's top. The sphere is a child of the Earth mesh, so its frame is the
// mesh's own (it turns with the ground and scales with either display
// scale) and the ovals are placed round the geomagnetic dipole poles
// (IGRF-14 2025.0: 80.85 N, 72.76 W and its antipode, BGS), fixed to the Sun while the
// Earth turns under them. Only the night side shows them.
// updateEarthAuroras drives the Sun's direction, a substorm-like activity
// cycle and the distance fade; updateEarthAuroraView the camera.
const EARTH_AURORA_SHELL_KM = 330;
const EARTH_GEOMAGNETIC_POLE = { lat: 80.85, lon: -72.76 };
// Brightness of the emission on screen, calibrated on ISS crew photographs
// of the night side (scratchpad refs, NASA images iss030e119777 and kin):
// a ribbon seen from above sits at about the city lights' level and the
// edge-on band along the limb a little above them; AURORA_PEAK is the
// linear luminance the brightest edge-on folds approach and never pass,
// well under the lit Earth's.
const EARTH_AURORA_GAIN = 0.04;
const EARTH_AURORA_PEAK = 0.45;
const _auroraCamWorld = new THREE.Vector3();
const _auroraInverse = new THREE.Matrix4();
const _auroraBufferSize = new THREE.Vector2();
const _auroraSunWorld = new THREE.Vector3();
// The Sun's angular radius from the Earth, radians.
const SUN_ANGULAR_RADIUS = R_SUN_KM / 149597870.7;

// A geographic direction in the Earth mesh's frame (earthSurfaceUV's).
function earthMeshDirection(latDeg, lonDeg) {
  const lat = THREE.MathUtils.degToRad(latDeg);
  const lon = THREE.MathUtils.degToRad(lonDeg);
  return new THREE.Vector3(Math.cos(lat) * Math.cos(lon), Math.sin(lat), -Math.cos(lat) * Math.sin(lon));
}

function createEarthAuroras(earthMesh) {
  if (!earthMesh || earthAuroras) return earthAuroras;
  const R = earthMesh.geometry.parameters.radius;
  const axis = earthMeshDirection(EARTH_GEOMAGNETIC_POLE.lat, EARTH_GEOMAGNETIC_POLE.lon);
  const e1 = new THREE.Vector3(0, 1, 0).cross(axis).normalize();
  const e2 = new THREE.Vector3().crossVectors(axis, e1);
  // The march's most steps: a limb view through the whole oval takes the most.
  // and the shortest step (km) the folds allow it.
  const steps = activeGraphicsPreset === 'low' ? 48 : activeGraphicsPreset === 'medium' ? 80 : 128;
  const nearKm = activeGraphicsPreset === 'low' ? 45 : activeGraphicsPreset === 'medium' ? 36 : 30;
  const material = new THREE.ShaderMaterial({
    defines: { AURORA_STEPS: steps, AURORA_NEAR_KM: nearKm.toFixed(1), AURORA_PEAK: EARTH_AURORA_PEAK.toFixed(3) },
    uniforms: {
      uCamLocal: { value: new THREE.Vector3(0, 0, R * 10) },
      uR: { value: R },
      uSunLocal: { value: new THREE.Vector3(1, 0, 0) },
      uMagAxis: { value: axis },
      uMagE1: { value: e1 },
      uMagE2: { value: e2 },
      uSunMagCS: { value: new THREE.Vector2(1, 0) },
      uTime: { value: 0 },
      uPixelAngle: { value: 0.001 },
      uIntensity: { value: 0 },
      uActivity: { value: 0.4 },
      uSunVisible: { value: 0 },
      // The oval's shape, from live data near now (src/spaceweather/viewer.js).
      ...auroraLiveUniforms()
    },
    vertexShader: earthAuroraVertexShader,
    fragmentShader: earthAuroraFragmentShader,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
    side: THREE.FrontSide,
    toneMapped: false
  });
  const shell = new THREE.Mesh(
    new THREE.SphereGeometry(R * (1 + EARTH_AURORA_SHELL_KM / 6371), 96, 64),
    material
  );
  shell.name = 'earthAuroras';
  shell.renderOrder = 12;
  // Purely visual: never a pick target.
  shell.raycast = () => {};
  shell.userData.cameraInside = false;
  shell.onBeforeRender = (renderer, scene, cam) => updateEarthAuroraView(shell, renderer, cam);
  earthMesh.add(shell);
  earthAuroras = { root: shell, mesh: shell, e1, e2 };
  return earthAuroras;
}

// Per draw, with the camera actually rendering it: where it sits in the
// Earth's frame and how wide a pixel is. Inside the shell (below 330 km)
// the sphere's front faces are behind the camera, so the back faces draw,
// without the depth test (the march stops at the ground itself).
function updateEarthAuroraView(shell, renderer, cam) {
  const u = shell.material.uniforms;
  _auroraCamWorld.setFromMatrixPosition(cam.matrixWorld);
  u.uCamLocal.value.copy(_auroraCamWorld).applyMatrix4(_auroraInverse.copy(shell.matrixWorld).invert());
  const target = renderer.getRenderTarget();
  const heightPx = target ? target.height : renderer.getDrawingBufferSize(_auroraBufferSize).y;
  const tanHalf = cam.isPerspectiveCamera ? Math.tan(THREE.MathUtils.degToRad(cam.fov) * 0.5) / cam.zoom : 0.5;
  u.uPixelAngle.value = (2 * tanHalf) / Math.max(1, heightPx);
  // Whether the Sun shows over the Earth's limb from here: the shader then
  // clears the aurora along sky directions near the Sun, since a camera
  // exposed for any sliver of it, however reddened, records none. The air
  // bends the first sliver into view while the Sun's centre is still
  // AIR_GRAZING_BEND plus its own radius below the limb (as an angle from
  // here); by the time its top would reach the limb unbent, a squashed
  // disc stands on it. Nothing here depends on where the camera points, so
  // panning never switches it (an earlier exposure stand-in on the view
  // direction made it pop in and out).
  const c = u.uCamLocal.value;
  const sDir = u.uSunLocal.value;
  const R = u.uR.value;
  const along = c.dot(sDir);
  let sunVisible = 1;
  if (along < 0) {
    const above = (Math.sqrt(Math.max(c.lengthSq() - along * along, 0)) - R) / Math.sqrt(Math.max(c.lengthSq() - R * R, 1e-12));
    sunVisible = THREE.MathUtils.smoothstep(above, -AIR_GRAZING_BEND - SUN_ANGULAR_RADIUS, -SUN_ANGULAR_RADIUS);
  }
  u.uSunVisible.value = sunVisible;
  u.uIntensity.value = shell.userData.baseIntensity || 0;
  const inside = u.uCamLocal.value.length() < shell.geometry.parameters.radius * 1.0005;
  if (inside !== shell.userData.cameraInside) {
    shell.userData.cameraInside = inside;
    shell.material.side = inside ? THREE.BackSide : THREE.FrontSide;
    shell.material.depthTest = !inside;
    shell.material.needsUpdate = true;
  }
}

// Geomagnetic activity: a quiet base with a substorm-like brightening
// every twelve minutes, an onset of a couple of minutes and a slow
// recovery; it moves the ovals equatorward (slowly: the arcs drift well
// under a km/s), brightens the night side and pushes the arcs' lower
// edges down (the pink hem).
function earthAuroraActivity(elapsed) {
  const phase = (elapsed % 720) / 720;
  const pulse = THREE.MathUtils.smoothstep(phase, 0, 0.2) * (1 - THREE.MathUtils.smoothstep(phase, 0.2, 0.9));
  return 0.35 + 0.55 * pulse;
}

function updateEarthAuroras(earthMesh, elapsed) {
  if (!earthAuroras || !earthAuroras.mesh) return;
  const shell = earthAuroras.mesh;
  const u = shell.material.uniforms;
  const R = earthMesh.geometry.parameters.radius;
  _auroraInverse.copy(earthMesh.matrixWorld).invert();
  const camRadii = _auroraCamWorld.copy(camera.position).applyMatrix4(_auroraInverse).length() / R;
  // A long exposure's subject: full from low orbit out to the event
  // views, fading from 8 radii and gone by 30.
  const fade = 1 - THREE.MathUtils.smoothstep(camRadii, 8, 30);
  shell.visible = fade > 0.001;
  if (!shell.visible) return;
  if (sun) sun.getWorldPosition(_auroraSunWorld); else _auroraSunWorld.set(0, 0, 0);
  const s = u.uSunLocal.value.copy(_auroraSunWorld).applyMatrix4(_auroraInverse).normalize();
  // The Sun's magnetic longitude as (cos, sin).
  u.uSunMagCS.value.set(s.dot(earthAuroras.e1), s.dot(earthAuroras.e2)).normalize();
  // window.__auroraTimeOverride (seconds) holds the display at one moment
  // for captures.
  const t = typeof window !== 'undefined' && Number.isFinite(window.__auroraTimeOverride) ? window.__auroraTimeOverride : elapsed;
  u.uTime.value = t;
  u.uActivity.value = earthAuroraActivity(t);
  shell.userData.baseIntensity = EARTH_AURORA_GAIN * fade;
  // Near now, NOAA's Kp and OVATION nowcast drive it (src/spaceweather/viewer.js).
  updateLiveAurora(u, shell.userData, simulationJD);
}

let jupiterAuroras = null;

function createJupiterAuroras(jupiterTiltGroup, jupiterData) {
  if (!jupiterTiltGroup || jupiterAuroras) return jupiterAuroras;

  const auroraRoot = new THREE.Group();
  auroraRoot.name = 'jupiterAuroras';
  auroraRoot.renderOrder = 12;
  auroraRoot.visible = true;

  // Jupiter's magnetic dipole is tilted ~9.6° from its rotation
  // axis. Tilt the magnetic frame so the auroral oval sits offset
  // the way Hubble UV imagery shows.
  const magneticFrame = new THREE.Group();
  magneticFrame.name = 'jupiterMagneticAuroraFrame';
  magneticFrame.rotation.z = THREE.MathUtils.degToRad(9.6);
  magneticFrame.rotation.x = THREE.MathUtils.degToRad(-3);

  const radius = jupiterData.radius; // ~8 for Jupiter
  // Jupiter's atmosphere depth is much larger relative to body
  // radius than Earth's, but the visible auroral curtains sit at
  // similar fractional altitudes when normalised — keep alt
  // fractions in line with Earth's setup but scaled.
  const altLow  = 0.008;
  const altHigh = 0.075;

  // Jovian aurorae are dominated by H₂ Lyman/Werner band UV
  // emission. In Hubble UV-mapped-to-RGB they read as cyan-violet
  // bright ovals. In direct optical they'd be very faint. We
  // exaggerate visibility for a recognisable rendering.
  // Primary cyan-blue oval (the brightest H₃⁺ ring).
  const cyanOpts = {
    colBase: new THREE.Color(0x6cb8ff),
    colMid:  new THREE.Color(0xa6e2ff),
    colTop:  new THREE.Color(0xe0f4ff),
    foldFreq: 1.0,
    foldPhase: 0.0,
    intensity: 1.0
  };
  // Jupiter's main auroral oval sits at ~75° magnetic latitude
  // (closer to the pole than Earth's because Jupiter's field is
  // mostly dipolar at high latitudes).
  magneticFrame.add(createAuroraConeCurtain(radius, 75, altLow, altHigh,  1, cyanOpts));
  magneticFrame.add(createAuroraConeCurtain(radius, 75, altLow, altHigh, -1, {
    ...cyanOpts, intensity: 0.85
  }));

  // Secondary violet layer — represents the polar emission cap
  // inside the main oval that Hubble UV imagery resolves.
  const violetOpts = {
    colBase: new THREE.Color(0x6048ff),
    colMid:  new THREE.Color(0xa074ff),
    colTop:  new THREE.Color(0xd0a8ff),
    foldFreq: 1.45,
    foldPhase: 21.6,
    intensity: 0.55,
    name: 'jupiterAuroraVioletNorth'
  };
  magneticFrame.add(createAuroraConeCurtain(radius, 78, altLow + 0.006, altHigh + 0.014,  1, violetOpts));
  magneticFrame.add(createAuroraConeCurtain(radius, 78, altLow + 0.006, altHigh + 0.014, -1, {
    ...violetOpts, intensity: 0.42, name: 'jupiterAuroraVioletSouth'
  }));

  auroraRoot.add(magneticFrame);
  jupiterTiltGroup.add(auroraRoot);
  jupiterAuroras = { root: auroraRoot, frame: magneticFrame };
  return jupiterAuroras;
}

// Science mode's Van Allen belts: the magnetosphere layer's belts alone
// (src/magnetosphere/), loaded on first use.
function setVanAllenBeltsVisible(visible) {
  vanAllenBeltsOn = Boolean(visible);
  if (vanAllenBeltsOn) loadMagnetosphere().then((m) => m && m.setScience(vanAllenBeltsOn)).catch(() => {});
  else if (magnetosphereMode) magnetosphereMode.setScience(false);
}

// Create the 2001 Space Odyssey Monolith easter egg
function createMonolith() {
  // Monolith proportions: 1:4:9 (squares of 1, 2, 3)
  // Thin slab standing upright
  const thickness = 0.3;  // Very thin
  const width = 1.2;      // 4x thickness
  const height = 2.7;     // 9x thickness

  const geometry = new THREE.BoxGeometry(thickness, height, width);

  // Perfect black, slightly reflective material
  const material = new THREE.MeshStandardMaterial({
    color: 0x000000,
    metalness: 0.9,
    roughness: 0.1,
    envMapIntensity: 0.5
  });

  monolith = new THREE.Mesh(geometry, material);
  monolith.name = 'monolith';
  monolith.userData = {
    name: 'TMA-1',
    type: 'Alien Artifact',
    description: 'A mysterious black monolith of unknown origin. Its proportions are 1:4:9, the squares of the first three integers.',
    realDiameter: '1 x 4 x 9 meters',
    realDistance: 'Jupiter orbit',
    orbitalPeriod: 'Unknown',
    moons: 0,
    colors: ['#000000', '#111111'],
    glowColor: '#000000',
    isPlanet: false,
    isSun: false,
    isMonolith: true, // marker so the raycast + findCelestialParent treat it as clickable
    radius: 1.6, // synthetic radius for camera focus distance (focusOnObject reads data.radius)
    orbitAngle: 0,
    orbitRadius: 16, // Near Io's orbit around Jupiter
    orbitalSpeed: 0.03
  };

  // Position near Jupiter (will be updated in animate loop)
  if (planets.jupiter) {
    planets.jupiter.add(monolith);
  }
  // Nobody can click it in the screensaver, where it reads as a render glitch.
  if (IS_SCREENSAVER) monolith.visible = false;
  markClickablesDirty();
}

function createSatelliteOrbitLine(radius) {
  const points = [];
  const segments = 160;
  for (let i = 0; i <= segments; i++) {
    const theta = (i / segments) * Math.PI * 2;
    points.push(new THREE.Vector3(
      Math.cos(theta) * radius,
      0,
      Math.sin(theta) * radius
    ));
  }

  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const material = new THREE.LineBasicMaterial({
    color: 0x9ed8ff,
    transparent: true,
    opacity: 0.18,
    depthTest: true,
    depthWrite: false,
    toneMapped: false
  });
  const line = new THREE.Line(geometry, material);
  line.name = 'ISS orbit';
  line.renderOrder = -2;
  line.visible = orbitsVisible;
  return line;
}

const EARTH_MEAN_RADIUS_KM = 6371;
const ISS_REAL_LENGTH_KM = 0.109;
const ISS_MIN_DISPLAY_LENGTH = 0.0075;
const ISS_MIN_FOCUS_RADIUS = 0.03;
const ISS_CLOSE_CAMERA_NEAR = 0.00002;
const ISS_CLOSE_MIN_DISTANCE = 0.0001;
const ISS_ALTITUDE_KM = 420;
const SCENE_CLICK_DRAG_TOLERANCE_PX = 6;

function publicAssetUrl(path) {
  const cleanPath = String(path).replace(/^\/+/, '');
  if (typeof window !== 'undefined' && window.location.protocol === 'file:') {
    const isDistFile = window.location.pathname.includes('/dist/');
    return isDistFile ? `./${cleanPath}` : `./public/${cleanPath}`;
  }
  return `/${cleanPath}`;
}

const ISS_MODEL_SOURCES = [
  {
    url: publicAssetUrl('models/iss-stationary-nasa.glb'),
    credit: 'NASA 3D Resources: ISS_stationary'
  },
  {
    url: publicAssetUrl('models/iss-nasa.glb'),
    credit: 'NASA 3D Resources: International Space Station (ISS) (B)'
  }
];
// A phone loads a copy of the stationary model made for it (3 MB against
// 30.8): duplicate maps merged, maps at 512 and in JPEG, the geometry in
// Draco, the same nodes and frame (docs/mobile-memory.md). Its maps need no
// shrinking at load, which is where the full one cost a phone's tab most.
const ISS_MODEL_SOURCES_FOR_DEVICE = RUNNING_ON_MOBILE
  ? [{ url: publicAssetUrl('models/iss-stationary-nasa-mobile.glb'), credit: ISS_MODEL_SOURCES[0].credit }, ...ISS_MODEL_SOURCES]
  : ISS_MODEL_SOURCES;
const DRACO_DECODER_URL = publicAssetUrl('vendor/draco/gltf/');

function sceneUnitsFromEarthKm(earthRadius, km) {
  return earthRadius * (km / EARTH_MEAN_RADIUS_KM);
}

function getGltfLoader() {
  if (gltfLoader) return Promise.resolve(gltfLoader);
  if (!gltfLoaderPromise) {
    gltfLoaderPromise = Promise.all([
      import('three/addons/loaders/GLTFLoader.js'),
      import('three/addons/loaders/DRACOLoader.js')
    ]).then(([{ GLTFLoader }, { DRACOLoader }]) => {
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath(DRACO_DECODER_URL);
      dracoLoader.setDecoderConfig({ type: 'wasm' });

      gltfLoader = new GLTFLoader();
      gltfLoader.setDRACOLoader(dracoLoader);
      return gltfLoader;
    });
  }
  return gltfLoaderPromise;
}

function loadGltfFromSources(loader, sources, index = 0) {
  const source = sources[index];
  return new Promise((resolve, reject) => {
    loader.load(
      source.url,
      (gltf) => resolve({ gltf, source }),
      undefined,
      (err) => {
        const nextIndex = index + 1;
        if (nextIndex >= sources.length) {
          reject(err);
          return;
        }
        console.warn(`[assets] unable to load ISS model ${source.url}; trying fallback`, err);
        loadGltfFromSources(loader, sources, nextIndex).then(resolve, reject);
      }
    );
  });
}

function attachScaledModel(root, model, targetLength) {
  model.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(model);
  const size = new THREE.Vector3();
  const center = new THREE.Vector3();
  box.getSize(size);
  box.getCenter(center);
  const maxDimension = Math.max(size.x, size.y, size.z);
  if (!Number.isFinite(maxDimension) || maxDimension <= 0) return false;

  const scale = targetLength / maxDimension;
  model.scale.setScalar(scale);
  model.position.copy(center).multiplyScalar(-scale);
  model.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    child.castShadow = false;
    child.receiveShadow = false;
    child.frustumCulled = false;
    if (child.material) {
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      materials.forEach((material) => {
        material.needsUpdate = true;
      });
    }
  });
  root.add(model);
  return true;
}

function tuneIssModelMaterials(model) {
  const subtleGlow = new THREE.Color(0xffffff);
  model.traverse((child) => {
    if (!(child instanceof THREE.Mesh) || !child.material) return;
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.forEach((material) => {
      if (material.map) {
        if (material.color) material.color.set(0xffffff);
        if (material.emissive) {
          material.emissive.set(0x000000);
          material.emissiveIntensity = 0;
        }
      } else {
        if (material.color) material.color.lerp(subtleGlow, 0.06);
        if (material.emissive) {
          material.emissive.lerp(subtleGlow, 0.04);
          material.emissiveIntensity = Math.max(material.emissiveIntensity || 0, 0.04);
        }
      }
      material.needsUpdate = true;
    });
  });
}

function createISSModel(sat, earthRadius) {
  const realLengthSceneUnits = sceneUnitsFromEarthKm(earthRadius, ISS_REAL_LENGTH_KM);
  const displayLengthSceneUnits = Math.max(realLengthSceneUnits, ISS_MIN_DISPLAY_LENGTH);
  const group = new THREE.Group();
  group.userData.visualRadius = displayLengthSceneUnits * 0.5;
  group.userData.realLengthSceneUnits = realLengthSceneUnits;
  group.userData.displayLengthSceneUnits = displayLengthSceneUnits;

  // 30.8 MB for an object that is sub-pixel unless you deliberately fly
  // to it. The group, its hit target and its focus radius are all built
  // synchronously above, so the ISS stays selectable and focusable the
  // whole time; only the mesh arrives late, and it attaches itself when
  // it does. requestBackgroundAsset('iss') from ensureFocusedDetail
  // pulls it forward the moment anyone actually navigates there.
  registerBackgroundAsset('iss', () => getGltfLoader()
    .then((loader) => loadGltfFromSources(loader, ISS_MODEL_SOURCES_FOR_DEVICE))
    .then(({ gltf, source }) => {
      // A phone keeps its fifteen 1024 maps at 512 (80 MB to 20 MB).
      if (RUNNING_ON_MOBILE) shrinkModelTextures(gltf.scene, 512);
      tuneIssModelMaterials(gltf.scene);
      // One draw call per material instead of one per part (src/issmodel.js).
      const model = mergeByMaterial(gltf.scene);
      model.userData.drawnLength = displayLengthSceneUnits;
      const loaded = attachScaledModel(group, model, displayLengthSceneUnits);
      group.userData.issModel = model;
      // It is hidden while out of view (updateIssModelVisibility), so its
      // shaders are built now, and the warm-up (src/gpuwarm.js) uploads its
      // maps while the page is quiet, instead of in the frame that first
      // shows it, which can be in the middle of a fly-in. It stays hidden
      // until they are ready: shown sooner, the frame waited on the driver
      // for each (half a second apiece where shaders compile slowly).
      model.userData.gpuWarm = true;
      model.userData.compiling = true;
      compileForSceneAsync(renderer, model, camera, scene, SLOW_COMPILE_LIMIT_MS)
        .then(() => { model.userData.compiling = false; });
      group.userData.assetLoaded = loaded;
      group.userData.assetSource = source.credit;
      group.userData.assetUrl = source.url;
    })
    .catch((err) => {
      console.warn('[assets] unable to load NASA ISS model', err);
      group.userData.assetLoaded = false;
    }));

  return group;
}

// The orbit, guide and model share Earth's display scale. Scaling only
// Earth's tilt group left the ISS tens of thousands of kilometres out in
// realistic mode, beyond the detailed imagery range.
function updateEarthSatelliteScales(scale) {
  for (const { mesh, orbitGroup, isProbe } of satelliteObjects) {
    if (isProbe) continue;
    if (!orbitGroup) {
      if (mesh.userData.atL2) setDrawnSizeScale(mesh, scale);
      continue;
    }
    orbitGroup.scale.setScalar(scale);
    const authored = mesh.userData.authoredInspection;
    if (authored) for (const key of ['radius', 'physicalRadius', 'selectionRadius']) {
      mesh.userData[key] = authored[key] * scale;
    }
  }
  for (const { mesh } of interstellarEntries) setDrawnSizeScale(mesh, scale);
}

// Webb and the interstellar visitors are drawn at the Earth's size factor
// (shrunk with it at true scale, as the satellites in their orbit groups
// are), with their focus and pick radii to match. Set when they are made,
// by the scale switch (before it frames the focused body again) and each
// frame they are placed.
function drawnSizeFactor() {
  return realisticScaleMode ? getRealisticBodyRadius('earth') / celestialData.earth.radius : 1;
}
function setDrawnSizeScale(mesh, size) {
  if (mesh.userData.sizeScale === size) return;
  const authored = mesh.userData.authoredInspection || mesh.userData.authoredSize;
  mesh.userData.sizeScale = size;
  mesh.scale.setScalar(size);
  for (const key of ['radius', 'physicalRadius', 'selectionRadius']) {
    if (authored && authored[key] != null) mesh.userData[key] = authored[key] * size;
  }
}

// The ISS model flies the real station's orbit: SGP4 on its latest element
// set (our /api/satellites, else the bundled snapshot), at the sim clock, so
// it is over the right place and laps the Earth every 92 minutes in real
// time. It is placed the way src/satellites/index.js places every satellite
// (TEME to Earth-fixed by GMST, into the Earth mesh's axes, then the mesh's
// own spin), scaled to the Earth as drawn, so both scale modes are right.
// Until the elements arrive, or if they never do, it keeps its authored orbit.
// A page left open (the Cupola stream runs for days) asks for a fresh element
// set every few hours: SGP4 drifts kilometres a day and the station reboosts.
// A refresh that fails keeps the set it has, and never falls back to the
// bundled snapshot, which is older.
let issSatrec = null;
const issOrbitLine = { lineAt: -1 };
let issElementsRefresh = null;
const ISS_ELEMENTS_REFRESH_MS = 3 * 60 * 60 * 1000;
function loadIssElements({ refresh = false } = {}) {
  if (issSatrec && !refresh) return;
  if (!issElementsRefresh) issElementsRefresh = setInterval(() => loadIssElements({ refresh: true }), ISS_ELEMENTS_REFRESH_MS);
  const take = (body) => {
    const row = body && Array.isArray(body.sats) && body.sats.find((r) => r[0] === ISS_NORAD);
    if (!row) return false;
    let rec = null;
    try { rec = satrecFrom(row); } catch { rec = null; }
    if (!rec) return false;
    issSatrec = rec;
    issOrbitLine.lineAt = -1;
    return true;
  };
  const latest = fetch(`/api/satellites?norad=${ISS_NORAD}`)
    .then((r) => (r.ok ? r.json() : null))
    .then((body) => { if (!take(body)) throw new Error('no ISS'); });
  (refresh ? latest : latest.catch(() => fetch('/data/satellites.json').then((r) => r.json()).then(take)))
    .catch(() => {});
}

const _issPos = new THREE.Vector3();
const _issVel = new THREE.Vector3();
const _issNormal = new THREE.Vector3();
const _issBack = new THREE.Vector3();
const _issDown = new THREE.Vector3();
const _issBasis = new THREE.Matrix4();
function placeIssFromOrbit(mesh, orbitGroup, orbitLine) {
  if (!placeOnSgp4Orbit(mesh, orbitGroup, orbitLine, issSatrec, issOrbitLine)) return false;
  // The model's nose is its local -X and its top the orbit normal (+Y), with
  // the Earth below along -Z, as its authored orbit held it.
  _issNormal.copy(_issPos).cross(_issVel).normalize();
  _issBack.copy(_issVel).negate();
  _issDown.copy(_issPos).normalize().negate();
  mesh.quaternion.setFromRotationMatrix(_issBasis.makeBasis(_issBack, _issNormal, _issDown));
  return true;
}

// Any satellite on SGP4 from its elements (satrec) at the sim clock, placed
// in the tilt group's axes (its position also in _issPos, its direction of
// travel in _issVel); lineState.lineAt keeps when its orbit line was drawn.
function placeOnSgp4Orbit(mesh, orbitGroup, orbitLine, satrec, lineState) {
  const tilt = planets.earth && planets.earth.getObjectByName('tiltGroup');
  const earthMesh = tilt && tilt.children.find((c) => c.isMesh);
  if (!earthMesh) return false;
  const ms = (simulationJD - 2440587.5) * 86400000;
  const state = issTeme(satrec, new Date(ms));
  if (!state) return false;
  // The orbit group stays beside the tilt group (updateEarthSatelliteScales
  // sizes it for the scale mode) and takes the tilt group's frame, so the
  // coordinates below, worked out in the tilt group's axes, land as they are.
  orbitGroup.position.copy(tilt.position);
  orbitGroup.quaternion.copy(tilt.quaternion);
  orbitGroup.scale.copy(tilt.scale);
  if (!earthMesh.geometry.boundingSphere) earthMesh.geometry.computeBoundingSphere();
  const kmToScene = (earthMesh.geometry.boundingSphere.radius * earthMesh.scale.x) / 6378.137;
  const c = Math.cos(state.gmst);
  const s = Math.sin(state.gmst);
  const toTilt = (t, out) => out.set(c * t.x + s * t.y, t.z, s * t.x - c * t.y).applyQuaternion(earthMesh.quaternion);
  toTilt(state.r, _issPos).multiplyScalar(kmToScene);
  toTilt(state.v, _issVel).normalize();
  mesh.position.copy(_issPos);
  // The orbit line: one lap of the real orbit, redrawn every ten sim minutes
  // (the plane precesses and the orbit decays) and when the scale changes.
  const stamp = Math.round(ms / 600000) * 1000 + Math.round(kmToScene * 1e6) % 1000;
  if (orbitLine && stamp !== lineState.lineAt) {
    lineState.lineAt = stamp;
    const pos = orbitLine.geometry.attributes.position;
    const n = pos.count;
    const periodMs = (2 * Math.PI / satrec.no) * 60000;
    const p = new THREE.Vector3();
    for (let i = 0; i < n; i++) {
      const at = issTeme(satrec, new Date(ms + (i / (n - 1)) * periodMs));
      if (!at) continue;
      toTilt(at.r, p).multiplyScalar(kmToScene);
      pos.setXYZ(i, p.x, p.y, p.z);
    }
    pos.needsUpdate = true;
    orbitLine.geometry.computeBoundingSphere();
  }
  return true;
}

// Create satellites orbiting Earth
function createSatellites() {
  if (!planets.earth) return;
  const earthRadius = planets.earth.userData.radius || celestialData.earth.radius;
  const issOrbitRadius = earthRadius + sceneUnitsFromEarthKm(earthRadius, ISS_ALTITUDE_KM);

  satellites
    .filter((sat) => sat.name === 'ISS')
    .forEach(sat => {
	      const satellite = createISSModel(sat, earthRadius);
	      const visualRadius = satellite.userData.visualRadius || sat.size * 4;
	      // focusRadius drives the initial focus distance via
	      // focusBodyRadius * zoomMultiplier. visualRadius * 8 was
	      // dramatically overinflating it (8x the bounding radius
	      // landed the camera dozens of body-widths away). 1.5x sits
	      // close to the actual silhouette so the satellite reads as
	      // a clear subject when first selected.
	      const focusRadius = Math.max(ISS_MIN_FOCUS_RADIUS, visualRadius * 1.5);
	      const selectionRadius = Math.max(0.35, visualRadius * 5);

      const hitTarget = new THREE.Mesh(
        new THREE.SphereGeometry(selectionRadius, 12, 8),
        new THREE.MeshBasicMaterial({
          color: 0x000000,
          transparent: true,
          opacity: 0,
          depthWrite: false
        })
      );
      hitTarget.name = `${sat.name} selection volume`;
      satellite.add(hitTarget);

      // Create orbit group
      const orbitGroup = new THREE.Group();
      orbitGroup.name = `${sat.name} orbit group`;
      orbitGroup.rotation.x = THREE.MathUtils.degToRad(sat.inclination);
      const orbitLine = createSatelliteOrbitLine(issOrbitRadius);
      orbitGroup.add(orbitLine);
      orbitGroup.add(satellite);
      satelliteOrbitLines.push(orbitLine);

      satellite.position.x = issOrbitRadius;
      satellite.name = sat.name;
      satellite.userData = {
        name: sat.name,
        type: sat.type,
        description: sat.description,
        radius: focusRadius,
        physicalRadius: visualRadius,
        selectionRadius,
        authoredInspection: { radius: focusRadius, physicalRadius: visualRadius, selectionRadius },
        realSize: sat.realSize,
        realDistance: sat.realAltitude,
        orbitalSpeed: sat.orbitalSpeed,
        isSatellite: true,
        orbitAngle: Math.random() * Math.PI * 2
      };

      planets.earth.add(orbitGroup);
      loadIssElements();
      satelliteObjects.push({
        mesh: satellite,
        data: { ...sat, orbitRadius: issOrbitRadius },
        orbitGroup,
        orbitLine,
        isProbe: false
      });
    });
  createHubble(earthRadius);
  createJwst();
  markClickablesDirty();
}

// Hubble and Webb are drawn the way the ISS is: as many times bigger than
// they are as the station is drawn (its 109 m at ISS_MIN_DISPLAY_LENGTH,
// about 175 times, so the three compare truly side by side), scaled with the
// Earth as drawn, with NASA's own models (public/models/README.md). A model
// is fetched only once its craft is focused or the camera comes within
// CRAFT_MODEL_NEAR of its drawn lengths (they are left out of the background
// queue, ON_DEMAND_ASSETS), and drawn only while some of it is in view
// (updateCraftModelVisibility). Each craft is placed, and drawn, only while
// it is chosen or the camera is near enough to the Earth for it to show; from
// further out it is far under a pixel and costs nothing.
// Lengths: Hubble 13.2 m (NASA, JPL Horizons' -48 record); Webb's sunshield
// 21.197 m (Horizons' -170 record). docs/new-objects-verification.md.
const HUBBLE_NORAD = 20580;
const HUBBLE_LENGTH_KM = 0.0132;
const JWST_LENGTH_KM = 0.021197;
const craftDisplayLength = (km) => ISS_MIN_DISPLAY_LENGTH * km / ISS_REAL_LENGTH_KM;
// A model is fetched once the camera is within this many of its drawn
// lengths (it is under half a pixel across further out).
const CRAFT_MODEL_NEAR = 2000;
// Hubble is placed while the camera is within this many Earth radii (drawn)
// of the Earth, Webb within this many times its own distance from it.
const HUBBLE_PLACE_NEAR = 60;
const JWST_PLACE_NEAR = 8;
const HUBBLE_MODEL_SOURCES = [{ url: publicAssetUrl('models/hubble-nasa.glb'), credit: 'NASA 3D Resources: Hubble Space Telescope (A)' }];
const JWST_MODEL_SOURCES = [{ url: publicAssetUrl('models/jwst-nasa.glb'), credit: 'NASA 3D Resources: James Webb Space Telescope (B)' }];
const craftModelGroups = [];

// A craft's model, loaded on demand into group and scaled to length.
function registerCraftModel(group, key, sources, length) {
  craftModelGroups.push(group);
  registerBackgroundAsset(key, () => getGltfLoader()
    .then((loader) => loadGltfFromSources(loader, sources))
    .then(({ gltf, source }) => {
      tuneIssModelMaterials(gltf.scene);
      const model = mergeByMaterial(gltf.scene);
      model.userData.drawnLength = length;
      group.userData.assetLoaded = attachScaledModel(group, model, length);
      group.userData.craftModel = model;
      // Hidden while out of view, so its shaders are built now (as the ISS's),
      // and until they are ready: shown sooner, the frame waited on the
      // driver for each (half a second apiece where shaders compile slowly).
      model.userData.gpuWarm = true;
      model.userData.compiling = true;
      model.visible = false;
      compileForSceneAsync(renderer, model, camera, scene, SLOW_COMPILE_LIMIT_MS)
        .then(() => { model.userData.compiling = false; });
      group.userData.assetSource = source.credit;
      group.userData.assetUrl = source.url;
    })
    .catch((err) => {
      console.warn(`[assets] unable to load the ${key} model`, err);
      group.userData.assetLoaded = false;
    }));
}

const _craftPos = new THREE.Vector3();
const _craftScale = new THREE.Vector3();
const _craftEarth = new THREE.Vector3();
// Fetches a craft's model the first time it is chosen or the camera comes
// near it.
function requestCraftModelWhenNear(group) {
  if (group.userData.modelRequested || !camera) return;
  group.getWorldPosition(_craftPos);
  const length = group.userData.drawnLength * group.getWorldScale(_craftScale).x;
  if (selectedObject !== group && camera.position.distanceTo(_craftPos) > CRAFT_MODEL_NEAR * length) return;
  group.userData.modelRequested = true;
  void requestBackgroundAsset(group.userData.modelAsset);
}

// As updateIssModelVisibility: a loaded model is drawn only while some of it
// can be in view.
function updateCraftModelVisibility() {
  if (!camera) return;
  let updated = false;
  for (const group of craftModelGroups) {
    const model = group.userData.craftModel;
    if (!model || !group.visible) continue;
    if (model.userData.compiling) { model.visible = false; continue; }
    if (renderer.xr.isPresenting || window.__issGateOff) {
      model.visible = true;
      continue;
    }
    if (!updated) { camera.updateMatrixWorld(); updated = true; }
    group.getWorldPosition(_craftPos);
    const length = model.userData.drawnLength * group.getWorldScale(_craftScale).x;
    model.visible = modelInView({ center: _craftPos, length, camera });
  }
}

// Shows or hides a craft (and its orbit line), for the clicks and the orbit
// guides as well.
function setCraftShown(mesh, shown, orbitLine = null) {
  if (mesh.visible === shown) return;
  mesh.visible = shown;
  if (orbitLine) {
    orbitLine.userData.craftHidden = !shown;
    updateLocalOrbitGuideVisibility();
  }
  markClickablesDirty();
}

// A craft's group: an unseen sphere round it to click, and the data the info
// panel, the focus and the follow read (as the ISS's, createSatellites). The
// focus radius frames it about two and a half of its lengths away.
function createCraftGroup(sat, km, selectionRadius, extra) {
  const length = craftDisplayLength(km);
  const group = new THREE.Group();
  group.name = sat.name;
  const hit = new THREE.Mesh(
    new THREE.SphereGeometry(selectionRadius, 12, 8),
    new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0, depthWrite: false })
  );
  hit.name = `${sat.name} selection volume`;
  group.add(hit);
  const focusRadius = length * 1.25;
  const physicalRadius = length * 0.5;
  group.userData = {
    name: sat.name,
    type: sat.type,
    description: sat.description,
    radius: focusRadius,
    physicalRadius,
    selectionRadius,
    authoredInspection: { radius: focusRadius, physicalRadius, selectionRadius },
    realSize: sat.realSize,
    realDistance: sat.realAltitude,
    isSatellite: true,
    drawnLength: length,
    ...extra
  };
  return group;
}

// Hubble's elements (NORAD 20580) from our /api/satellites, else the bundled
// snapshot, as the ISS's are (loadIssElements), asked for the first time the
// camera comes near the Earth and every few hours after. SGP4 on them puts
// Hubble over the right place at the sim clock. It is drawn from its
// deployment (25 April 1990, from Discovery on STS-31) and not before.
const HUBBLE_DEPLOY_JD = 2448006.5;
const hubbleOrbit = { satrec: null, lineAt: -1, asked: false };
function loadHubbleElements({ refresh = false } = {}) {
  if (!refresh) {
    if (hubbleOrbit.asked) return;
    hubbleOrbit.asked = true;
    setInterval(() => loadHubbleElements({ refresh: true }), ISS_ELEMENTS_REFRESH_MS);
  }
  const take = (body) => {
    const row = body && Array.isArray(body.sats) && body.sats.find((r) => r[0] === HUBBLE_NORAD);
    if (!row) return false;
    let rec = null;
    try { rec = satrecFrom(row); } catch { rec = null; }
    if (!rec) return false;
    hubbleOrbit.satrec = rec;
    hubbleOrbit.lineAt = -1;
    return true;
  };
  const latest = fetch(`/api/satellites?norad=${HUBBLE_NORAD}`)
    .then((r) => (r.ok ? r.json() : null))
    .then((body) => { if (!take(body)) throw new Error('no Hubble'); });
  (refresh ? latest : latest.catch(() => fetch('/data/satellites.json').then((r) => r.json()).then(take)))
    .catch(() => {});
}

const _hubbleAway = new THREE.Vector3();
const _hubbleNorth = new THREE.Vector3();
const _hubbleSide = new THREE.Vector3();
const _hubbleAcross = new THREE.Vector3();
const _hubbleQuat = new THREE.Quaternion();
const _hubbleBasis = new THREE.Matrix4();
// Hubble on its orbit, turned to the Sun. NASA's model has its arrays fixed
// flat across the tube (facing along it, +Y, the aperture's end), so the Sun
// is set HUBBLE_SUN_TILT round from the tube's aft end, about the arrays'
// masts (+Z): the tube's side takes the light, as in the servicing missions'
// photographs (straight down the tube, the side is lit edge on and reads
// black), and the arrays still catch most of it. The aperture faces away from
// the Sun, and the masts lie level with the ecliptic.
const HUBBLE_SUN_TILT = Math.PI / 4;
function placeHubble(entry) {
  const { mesh, orbitGroup, orbitLine } = entry;
  const onPath = simulationJD >= HUBBLE_DEPLOY_JD;
  if (mesh.userData.onPath !== onPath) {
    mesh.userData.onPath = onPath;
    markClickablesDirty();
  }
  const earth = planets.earth;
  const near = selectedObject === mesh || Boolean(camera && earth
    && camera.position.distanceTo(earth.getWorldPosition(_craftEarth)) < getBodyBaseRadius(earth) * HUBBLE_PLACE_NEAR);
  if (near) loadHubbleElements();
  const shown = onPath && near && Boolean(hubbleOrbit.satrec);
  setCraftShown(mesh, shown, orbitLine);
  if (!shown || !placeOnSgp4Orbit(mesh, orbitGroup, orbitLine, hubbleOrbit.satrec, hubbleOrbit)) return;
  if (sun) {
    const parent = mesh.parent;
    parent.updateMatrixWorld();
    parent.worldToLocal(sun.getWorldPosition(_hubbleAway)).sub(mesh.position).normalize().negate();
    _hubbleNorth.set(0, 1, 0).applyQuaternion(parent.getWorldQuaternion(_hubbleQuat).invert());
    _hubbleSide.crossVectors(_hubbleNorth, _hubbleAway);
    if (_hubbleSide.lengthSq() < 1e-8) _hubbleSide.set(1, 0, 0).cross(_hubbleAway);
    _hubbleSide.normalize();
    _hubbleAcross.crossVectors(_hubbleAway, _hubbleSide);
    // Turned about the masts: the Sun at the tilt from the aft end (-Y)
    // towards +X.
    const c = Math.cos(HUBBLE_SUN_TILT);
    const t = Math.sin(HUBBLE_SUN_TILT);
    _hubbleNorth.copy(_hubbleAway).multiplyScalar(c).addScaledVector(_hubbleAcross, t);
    _hubbleAcross.multiplyScalar(c).addScaledVector(_hubbleAway, -t);
    mesh.quaternion.setFromRotationMatrix(_hubbleBasis.makeBasis(_hubbleAcross, _hubbleNorth, _hubbleSide));
  }
  requestCraftModelWhenNear(mesh);
}

function createHubble(earthRadius) {
  const sat = satellites.find((s) => s.name === 'Hubble');
  if (!sat) return;
  const group = createCraftGroup(sat, HUBBLE_LENGTH_KM, 0.25, { modelAsset: 'hubble' });
  registerCraftModel(group, 'hubble', HUBBLE_MODEL_SOURCES, group.userData.drawnLength);
  const orbitRadius = earthRadius + sceneUnitsFromEarthKm(earthRadius, 470);
  const orbitGroup = new THREE.Group();
  orbitGroup.name = `${sat.name} orbit group`;
  const orbitLine = createSatelliteOrbitLine(orbitRadius);
  orbitLine.name = 'Hubble orbit';
  orbitLine.material.color.set(0xffd27a);
  orbitGroup.add(orbitLine);
  orbitGroup.add(group);
  satelliteOrbitLines.push(orbitLine);
  group.visible = false;
  orbitLine.userData.craftHidden = true;
  orbitLine.visible = false;
  planets.earth.add(orbitGroup);
  satelliteObjects.push({ mesh: group, data: { ...sat, orbitRadius }, orbitGroup, orbitLine, isProbe: false, place: placeHubble });
}

// Webb flies JPL Horizons' path (public/data/jwst-path.json, src/orbits/
// visitors.js) round the Sun-Earth L2 point, laid out from the Earth as
// drawn: at true scale at its true distance, in artistic scale in its true
// direction at jwstArtisticRange (the Moon's distance as drawn sets the
// scale, so Webb sits about twice as far out as the Moon). Hidden before its
// launch and after the end of its prediction. It holds its sunshield to the
// Sun: NASA's model has the telescope on +Y over the shield and the shield's
// long side along Z, kept level with the ecliptic.
const JWST_PATH_URL = '/data/jwst-path.json';
let jwstTrack = null;
let jwstTrackRequested = false;
function loadJwstTrack() {
  if (jwstTrackRequested) return;
  jwstTrackRequested = true;
  fetch(JWST_PATH_URL)
    .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`Webb path ${res.status}`))))
    .then((data) => { jwstTrack = data; })
    .catch((err) => {
      console.warn('Webb path failed to load:', err);
      setTimeout(() => { jwstTrackRequested = false; loadJwstTrack(); }, 15000);
    });
}

const _jwstGeo = [0, 0, 0];
const _jwstOffset = new THREE.Vector3();
const _jwstSun = new THREE.Vector3();
const _jwstAway = new THREE.Vector3();
const _jwstSide = new THREE.Vector3();
const _jwstUp = new THREE.Vector3();
const _jwstBasis = new THREE.Matrix4();
const _jwstCarry = new THREE.Vector3();
// The Moon's distance from the Earth as drawn now (the artistic spread
// moves it), for jwstArtisticRange.
function earthMoonDrawnRange() {
  const moon = (moons.earth || []).find((m) => m.userData && m.userData.name === 'Moon');
  return (moon && moon.userData.distance) || celestialData.earth.moon.distance * scaleMultiplier;
}
// Webb's place from the Earth in scene units at jd into out (a Vector3), or
// null outside its path.
function jwstSceneOffset(jd, out) {
  const g = jwstTrack && jwstGeocentric(jwstTrack, jd, _jwstGeo);
  if (!g) return null;
  const km = Math.hypot(g[0], g[1], g[2]);
  const k = realisticScaleMode
    ? REAL_KM_TO_SCENE
    : jwstArtisticRange(km, getBodyBaseRadius(planets.earth), earthMoonDrawnRange()) / km;
  return out.set(g[0] * k, g[2] * k, -g[1] * k);
}
function placeJwst(entry) {
  const { mesh } = entry;
  const earth = planets.earth;
  if (!earth) return;
  earth.getWorldPosition(_craftEarth);
  const offset = jwstTrack ? jwstSceneOffset(simulationJD, _jwstOffset) : null;
  const onPath = !jwstTrack || Boolean(offset);
  if (mesh.userData.onPath !== onPath) {
    mesh.userData.onPath = onPath;
    markClickablesDirty();
  }
  setDrawnSizeScale(mesh, drawnSizeFactor());
  const range = offset ? offset.length() : 0;
  const near = selectedObject === mesh || !camera
    || camera.position.distanceTo(_craftEarth) < Math.max(range, getBodyBaseRadius(earth)) * JWST_PLACE_NEAR;
  setCraftShown(mesh, Boolean(offset) && near);
  if (!offset) {
    // Until its path loads it waits hidden at the Earth (a link to it opened
    // first follows it out, as the probes' do).
    mesh.position.copy(_craftEarth);
    mesh.userData.waiting = true;
    updateJwstPath();
    return;
  }
  if (!near) {
    updateJwstPath();
    return;
  }
  _jwstOffset.add(_craftEarth);
  if (mesh.userData.waiting && selectedObject === mesh && !isAnimating && camera && controls) {
    _jwstCarry.copy(_jwstOffset).sub(mesh.position);
    camera.position.add(_jwstCarry);
    controls.target.add(_jwstCarry);
    if (_focusPrevObj === mesh) _focusPrevObjPos.add(_jwstCarry);
  }
  mesh.userData.waiting = false;
  mesh.position.copy(_jwstOffset);
  if (sun) {
    _jwstAway.copy(mesh.position).sub(sun.getWorldPosition(_jwstSun)).normalize();
    _jwstSide.set(0, 1, 0).cross(_jwstAway);
    if (_jwstSide.lengthSq() < 1e-8) _jwstSide.set(1, 0, 0);
    _jwstSide.normalize();
    _jwstUp.crossVectors(_jwstAway, _jwstSide);
    mesh.quaternion.setFromRotationMatrix(_jwstBasis.makeBasis(_jwstUp, _jwstAway, _jwstSide));
  }
  requestCraftModelWhenNear(mesh);
  updateJwstPath();
}

function createJwst() {
  const sat = satellites.find((s) => s.name === 'JWST');
  if (!sat) return;
  // atL2: not in Earth orbit, so the Earth-close views (cloud shadows, the
  // headset's focus) leave it alone.
  const group = createCraftGroup(sat, JWST_LENGTH_KM, 0.6, { modelAsset: 'jwst', atL2: true });
  registerCraftModel(group, 'jwst', JWST_MODEL_SOURCES, group.userData.drawnLength);
  setDrawnSizeScale(group, drawnSizeFactor());
  group.visible = false;
  scene.add(group);
  onIdle(loadJwstTrack);
  satelliteObjects.push({ mesh: group, data: sat, isProbe: false, place: placeJwst });
  createJwstPath(group);
}

// Webb's halo orbit, drawn while the orbits are shown: half a year either
// side of now, about a lap each way, in the frame that keeps the Sun-Earth
// line fixed (each day's place turned about the ecliptic pole by how far the
// Earth has gone round the Sun since, getPlanetPosition), which is how the
// halo round L2 is drawn in NASA's diagrams; in the fixed frame the path
// winds round the Sun with the Earth. Laid out from the Earth as drawn and
// worked out again every two sim days, or when the scale changes.
const JWST_PATH_DAYS = 182;
const JWST_PATH_REBUILD_DAYS = 2;
const jwstPath = { line: null, mesh: null, builtJd: NaN, builtScale: null };
function createJwstPath(mesh) {
  const material = new LineMaterial({
    color: new THREE.Color(0xffd27a),
    linewidth: 1.4,
    transparent: true,
    opacity: 0.35,
    resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
    worldUnits: false,
    depthWrite: false
  });
  const line = new Line2(new LineGeometry(), material);
  line.name = 'JWST halo orbit';
  line.renderOrder = -2;
  line.frustumCulled = false;
  line.visible = false;
  scene.add(line);
  jwstPath.line = line;
  jwstPath.mesh = mesh;
}

const _jwstPathPoint = new THREE.Vector3();
function updateJwstPath() {
  const { line, mesh } = jwstPath;
  if (!line) return;
  const show = orbitsVisible && mesh.visible;
  if (line.visible !== show) line.visible = show;
  if (!show) return;
  line.position.copy(_craftEarth);
  const scale = realisticScaleMode ? -1 : scaleMultiplier;
  if (jwstPath.builtScale === scale && Math.abs(simulationJD - jwstPath.builtJd) < JWST_PATH_REBUILD_DAYS) return;
  jwstPath.builtScale = scale;
  jwstPath.builtJd = simulationJD;
  const now = getPlanetPosition('earth', simulationJD);
  const angleNow = Math.atan2(now.y, now.x);
  const positions = [];
  for (let d = -JWST_PATH_DAYS; d <= JWST_PATH_DAYS; d++) {
    const t = simulationJD + d;
    if (!jwstSceneOffset(t, _jwstPathPoint)) continue;
    const earth = getPlanetPosition('earth', t);
    // A turn by a about the ecliptic pole (scene +Y) takes ecliptic (x, y),
    // which is scene (x, -z), to (x cos a - y sin a, x sin a + y cos a).
    const a = angleNow - Math.atan2(earth.y, earth.x);
    const c = Math.cos(a), s = Math.sin(a);
    const x = _jwstPathPoint.x, y = -_jwstPathPoint.z;
    positions.push(x * c - y * s, _jwstPathPoint.y, -(x * s + y * c));
  }
  if (positions.length < 6) return;
  line.geometry.dispose();
  const geometry = new LineGeometry();
  geometry.setPositions(positions);
  line.geometry = geometry;
}

const WORMHOLE_DATA = {
  name: 'Wormhole',
  type: 'Speculative Spacetime Feature',
  description: 'A fictional Einstein-Rosen bridge near Saturn, drawn as Interstellar drew it: a spherical gravitational lens showing another galaxy through the throat, with our own stars bent into an Einstein ring around it.',
  radius: 0.32,
  realDiameter: 'City-scale fictional mouth',
  realDistance: 'Outer Solar System, near Saturn orbit',
  orbitalPeriod: 'Stationary scene feature',
  colors: ['#bfe7ff', '#1b335f'],
  glowColor: '#8fd7ff'
};

function getWormholeStarMapFallbackTexture() {
  if (!wormholeStarMapFallbackTexture) {
    const data = new Uint8Array([0, 0, 0, 255]);
    wormholeStarMapFallbackTexture = new THREE.DataTexture(data, 1, 1, THREE.RGBAFormat);
    wormholeStarMapFallbackTexture.colorSpace = THREE.SRGBColorSpace;
    wormholeStarMapFallbackTexture.needsUpdate = true;
  }
  return wormholeStarMapFallbackTexture;
}

function createWormhole() {
  if (wormholeObject) return wormholeObject;

  const radius = WORMHOLE_DATA.radius;
  const group = new THREE.Group();
  group.name = 'wormhole';
  group.position.set(-76, 42, -96);
  group.userData = {
    ...WORMHOLE_DATA,
    isWormhole: true,
    isDeepSpaceBody: true
  };

  // The mouth has no surface: what shows is the lens's bending of the sky
  // behind it, and the other galaxy through the throat.
  wormholeLens = createWormholeLens({
    renderer,
    scene,
    cubeSize: activeGraphicsPreset === 'low' || activeGraphicsPreset === 'medium' ? 512 : 1024
  });
  group.add(wormholeLens.mesh);

  const hitTarget = new THREE.Mesh(
    new THREE.SphereGeometry(radius * 2.0, 32, 16),
    new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0,
      depthWrite: false
    })
  );
  hitTarget.name = 'wormhole selection volume';
  group.add(hitTarget);

  group.visible = deepSpaceBodiesVisible;
  const saturn = planets.saturn;
  if (saturn) {
    saturn.add(group);
  } else {
    scene.add(group);
  }

  wormholeObject = group;
  markClickablesDirty();
  return wormholeObject;
}

const _wormholeCenter = new THREE.Vector3();
const _wormholeScale = new THREE.Vector3();

function updateWormholeVisuals() {
  if (!wormholeObject || !wormholeLens || !renderer) return;
  wormholeObject.updateWorldMatrix(true, false);
  wormholeObject.getWorldPosition(_wormholeCenter);
  wormholeObject.getWorldScale(_wormholeScale);
  let shown = wormholeObject.visible;
  for (let p = wormholeObject.parent; shown && p; p = p.parent) shown = p.visible;
  wormholeLens.update({
    camera,
    center: _wormholeCenter,
    rho: WORMHOLE_DATA.radius * _wormholeScale.x,
    viewportHeight: renderer.domElement.height,
    visible: shown
  });
}

function createProbeMaterials(accentColor = 0x00ff88) {
  return {
    dish: new THREE.MeshStandardMaterial({
      color: 0xe7e2d4,
      metalness: 0.55,
      roughness: 0.34,
      side: THREE.DoubleSide
    }),
    white: new THREE.MeshStandardMaterial({
      color: 0xdedbd0,
      metalness: 0.4,
      roughness: 0.42
    }),
    dark: new THREE.MeshStandardMaterial({
      color: 0x151617,
      metalness: 0.45,
      roughness: 0.7
    }),
    graphite: new THREE.MeshStandardMaterial({
      color: 0x32383c,
      metalness: 0.65,
      roughness: 0.48
    }),
    foil: new THREE.MeshStandardMaterial({
      color: 0xc6923a,
      metalness: 0.85,
      roughness: 0.38
    }),
    copper: new THREE.MeshStandardMaterial({
      color: 0xb56c36,
      metalness: 0.75,
      roughness: 0.45
    }),
    boom: new THREE.MeshStandardMaterial({
      color: 0x8c9290,
      metalness: 0.85,
      roughness: 0.32
    }),
    glass: new THREE.MeshStandardMaterial({
      color: 0x142232,
      metalness: 0.1,
      roughness: 0.18
    }),
    accent: new THREE.MeshBasicMaterial({
      color: accentColor,
      transparent: true,
      opacity: 0.85,
      toneMapped: false
    })
  };
}

function createProbeBox(size, position, material, rotation = null) {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(size.x, size.y, size.z), material);
  mesh.position.copy(position);
  if (rotation) mesh.rotation.set(rotation.x, rotation.y, rotation.z);
  return mesh;
}

function createCylinderBetween(start, end, radius, material, segments = 8) {
  const direction = end.clone().sub(start);
  const length = direction.length();
  const geometry = new THREE.CylinderGeometry(radius, radius, length, segments);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.copy(start).add(end).multiplyScalar(0.5);
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
  return mesh;
}

function createHighGainDish(radius, depth, materials, options = {}) {
  const group = new THREE.Group();
  const dish = new THREE.Mesh(
    new THREE.ConeGeometry(radius, depth, options.segments || 48, 1, true),
    materials.dish
  );
  dish.rotation.x = Math.PI / 2;
  group.add(dish);

  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(radius, Math.max(radius * 0.022, 0.01), 8, options.segments || 48),
    materials.white
  );
  rim.position.z = -depth * 0.5;
  group.add(rim);

  const feed = new THREE.Mesh(
    new THREE.CylinderGeometry(radius * 0.06, radius * 0.045, depth * 0.55, 12),
    materials.graphite
  );
  feed.rotation.x = Math.PI / 2;
  feed.position.z = -depth * 0.22;
  group.add(feed);

  const feedTip = new THREE.Mesh(
    new THREE.SphereGeometry(radius * 0.08, 12, 8),
    materials.copper
  );
  feedTip.position.z = -depth * 0.48;
  group.add(feedTip);

  const strutAnchors = [
    new THREE.Vector3(radius * 0.68, radius * 0.45, -depth * 0.5),
    new THREE.Vector3(-radius * 0.68, radius * 0.45, -depth * 0.5),
    new THREE.Vector3(0, -radius * 0.78, -depth * 0.5)
  ];
  strutAnchors.forEach((anchor) => {
    group.add(createCylinderBetween(
      anchor,
      new THREE.Vector3(0, 0, -depth * 0.18),
      radius * 0.012,
      materials.boom,
      6
    ));
  });

  return group;
}

function addVoyagerBooms(group, materials, size, mirror = 1) {
  // Voyager's recognizable silhouette is mostly its offset booms.
  const magnetometerStart = new THREE.Vector3(-size * 0.62, 0, size * 0.4);
  const magnetometerEnd = new THREE.Vector3(-size * 9.5, size * 0.5 * mirror, size * 0.85);
  group.add(createCylinderBetween(magnetometerStart, magnetometerEnd, size * 0.025, materials.boom, 6));
  const magnetometerSensor = new THREE.Mesh(
    new THREE.SphereGeometry(size * 0.12, 10, 8),
    materials.graphite
  );
  magnetometerSensor.position.copy(magnetometerEnd);
  group.add(magnetometerSensor);

  const rtgStart = new THREE.Vector3(size * 0.55, -size * 0.12, size * 0.35);
  const rtgEnd = new THREE.Vector3(size * 4.3, -size * 1.05 * mirror, size * 1.15);
  group.add(createCylinderBetween(rtgStart, rtgEnd, size * 0.05, materials.boom, 8));

  const rtg = new THREE.Mesh(
    new THREE.CylinderGeometry(size * 0.22, size * 0.22, size * 1.5, 18),
    materials.graphite
  );
  rtg.rotation.z = Math.PI / 2.6;
  rtg.rotation.y = Math.PI / 2;
  rtg.position.copy(rtgEnd);
  group.add(rtg);

  for (let i = -2; i <= 2; i++) {
    const fin = createProbeBox(
      new THREE.Vector3(size * 0.05, size * 0.5, size * 0.22),
      new THREE.Vector3(size * (4.05 + i * 0.12), -size * 1.05 * mirror, size * 1.15),
      materials.copper,
      new THREE.Vector3(0, 0, Math.PI / 2.6)
    );
    group.add(fin);
  }

  const scienceStart = new THREE.Vector3(size * 0.12, size * 0.48, size * 0.55);
  const scienceEnd = new THREE.Vector3(size * 1.9, size * 2.1 * mirror, size * 1.0);
  group.add(createCylinderBetween(scienceStart, scienceEnd, size * 0.035, materials.boom, 6));
  group.add(createProbeBox(
    new THREE.Vector3(size * 0.55, size * 0.28, size * 0.36),
    scienceEnd,
    materials.dark,
    new THREE.Vector3(0.2, 0.1, 0.6 * mirror)
  ));
}

const VOYAGER_MODEL_SOURCES = [
  {
    url: publicAssetUrl('models/voyager-nasa-b.glb'),
    credit: 'NASA 3D Resources: Voyager Probe (B) — high detail'
  },
  {
    url: publicAssetUrl('models/voyager-nasa-a.glb'),
    credit: 'NASA 3D Resources: Voyager Probe (A) — fallback'
  }
];

function buildProceduralVoyager(probe, group) {
  const size = probe.size;
  const materials = createProbeMaterials(probe.color);

  const dish = createHighGainDish(size * 1.35, size * 0.52, materials, { segments: 64 });
  dish.position.z = -size * 0.18;
  group.add(dish);

  const bus = createProbeBox(
    new THREE.Vector3(size * 1.1, size * 0.72, size * 0.82),
    new THREE.Vector3(0, 0, size * 0.72),
    materials.foil,
    new THREE.Vector3(0.08, 0.18, 0.12)
  );
  group.add(bus);

  const electronics = createProbeBox(
    new THREE.Vector3(size * 0.74, size * 0.4, size * 0.36),
    new THREE.Vector3(-size * 0.12, -size * 0.05, size * 1.22),
    materials.dark
  );
  group.add(electronics);

  const record = new THREE.Mesh(
    new THREE.CylinderGeometry(size * 0.24, size * 0.24, size * 0.025, 32),
    materials.foil
  );
  record.rotation.x = Math.PI / 2;
  record.position.set(size * 0.36, -size * 0.38, size * 0.9);
  group.add(record);

  addVoyagerBooms(group, materials, size, probe.name.endsWith('2') ? -1 : 1);

  const beacon = new THREE.Mesh(new THREE.SphereGeometry(size * 0.07, 8, 8), materials.accent);
  beacon.position.set(-size * 0.32, size * 0.2, size * 1.36);
  group.add(beacon);
}

function createVoyagerModel(probe) {
  const size = probe.size;
  const group = new THREE.Group();
  group.userData.visualRadius = size * 9.8;
  // Target overall length so the NASA glTF lands at a comparable
  // scene scale to the procedural model (which was about size * 2.5
  // bounding length).
  const targetLength = size * 2.6;

  // The NASA model is the source of truth; the procedural primitives
  // act as a placeholder until the glTF resolves, then get cleared
  // so we don't render both stacked.
  const placeholder = new THREE.Group();
  buildProceduralVoyager(probe, placeholder);
  group.add(placeholder);

  getGltfLoader()
    .then((loader) => loadGltfFromSources(loader, VOYAGER_MODEL_SOURCES))
    .then(({ gltf, source }) => {
      if (RUNNING_ON_MOBILE) shrinkModelTextures(gltf.scene, 512);
      group.remove(placeholder);
      placeholder.traverse((child) => {
        if (child.geometry && child.geometry.dispose) child.geometry.dispose();
        if (child.material && child.material.dispose) child.material.dispose();
      });
      const loaded = attachScaledModel(group, gltf.scene, targetLength);
      group.userData.assetLoaded = loaded;
      group.userData.assetSource = source.credit;
      group.userData.assetUrl = source.url;
    })
    .catch((err) => {
      console.warn('[assets] unable to load NASA Voyager model — using procedural fallback', err);
      group.userData.assetLoaded = false;
    });

  return group;
}

function createPioneerModel(probe) {
  const size = probe.size;
  const materials = createProbeMaterials(probe.color);
  const group = new THREE.Group();

  const dish = createHighGainDish(size * 1.65, size * 0.62, materials, { segments: 56 });
  dish.position.z = -size * 0.16;
  group.add(dish);

  group.add(createProbeBox(
    new THREE.Vector3(size * 0.9, size * 0.72, size * 0.44),
    new THREE.Vector3(0, 0, size * 0.72),
    materials.foil
  ));

  const antennaMast = createCylinderBetween(
    new THREE.Vector3(0, 0, size * 0.48),
    new THREE.Vector3(0, 0, size * 1.75),
    size * 0.045,
    materials.boom,
    10
  );
  group.add(antennaMast);

  const crossBooms = [
    [new THREE.Vector3(size * 0.42, 0, size * 0.82), new THREE.Vector3(size * 4.2, size * 0.35, size * 1.2)],
    [new THREE.Vector3(-size * 0.42, 0, size * 0.82), new THREE.Vector3(-size * 4.2, -size * 0.35, size * 1.2)],
    [new THREE.Vector3(0, size * 0.36, size * 0.9), new THREE.Vector3(size * 0.2, size * 3.8, size * 1.3)]
  ];
  crossBooms.forEach(([start, end], index) => {
    group.add(createCylinderBetween(start, end, size * 0.035, materials.boom, 6));
    const pod = new THREE.Mesh(
      new THREE.CylinderGeometry(size * 0.16, size * 0.16, size * 0.72, 14),
      index === 2 ? materials.dark : materials.graphite
    );
    pod.rotation.z = Math.PI / 2;
    pod.position.copy(end);
    group.add(pod);
  });

  const plaque = new THREE.Mesh(
    new THREE.PlaneGeometry(size * 0.42, size * 0.28),
    materials.foil
  );
  plaque.position.set(size * 0.18, -size * 0.38, size * 0.48);
  plaque.rotation.x = -0.15;
  group.add(plaque);

  group.userData.visualRadius = size * 5.8;
  return group;
}

function createNewHorizonsModel(probe) {
  const size = probe.size;
  const materials = createProbeMaterials(probe.color);
  const group = new THREE.Group();

  const dish = createHighGainDish(size * 1.05, size * 0.42, materials, { segments: 48 });
  dish.position.z = -size * 0.2;
  group.add(dish);

  group.add(createProbeBox(
    new THREE.Vector3(size * 1.15, size * 0.82, size * 0.72),
    new THREE.Vector3(0, 0, size * 0.55),
    materials.foil,
    new THREE.Vector3(0.0, 0.0, Math.PI / 4)
  ));

  group.add(createProbeBox(
    new THREE.Vector3(size * 0.9, size * 0.18, size * 0.5),
    new THREE.Vector3(0, size * 0.52, size * 0.72),
    materials.graphite
  ));

  const rtgStart = new THREE.Vector3(size * 0.45, -size * 0.15, size * 0.72);
  const rtgEnd = new THREE.Vector3(size * 3.1, -size * 0.72, size * 0.96);
  group.add(createCylinderBetween(rtgStart, rtgEnd, size * 0.04, materials.boom, 8));

  const rtg = new THREE.Mesh(
    new THREE.CylinderGeometry(size * 0.2, size * 0.2, size * 1.25, 18),
    materials.graphite
  );
  rtg.rotation.z = Math.PI / 2.45;
  rtg.rotation.y = Math.PI / 2;
  rtg.position.copy(rtgEnd);
  group.add(rtg);

  for (let i = -2; i <= 2; i++) {
    group.add(createProbeBox(
      new THREE.Vector3(size * 0.04, size * 0.38, size * 0.18),
      new THREE.Vector3(size * (2.88 + i * 0.1), -size * 0.72, size * 0.96),
      materials.copper,
      new THREE.Vector3(0, 0, Math.PI / 2.45)
    ));
  }

  const thrusterPositions = [
    [size * 0.45, size * 0.45, size * 1.02],
    [-size * 0.45, size * 0.45, size * 1.02],
    [size * 0.45, -size * 0.45, size * 1.02],
    [-size * 0.45, -size * 0.45, size * 1.02]
  ];
  thrusterPositions.forEach(([x, y, z]) => {
    const thruster = new THREE.Mesh(
      new THREE.ConeGeometry(size * 0.09, size * 0.22, 12),
      materials.dark
    );
    thruster.rotation.x = -Math.PI / 2;
    thruster.position.set(x, y, z);
    group.add(thruster);
  });

  const beacon = new THREE.Mesh(new THREE.SphereGeometry(size * 0.055, 8, 8), materials.accent);
  beacon.position.set(-size * 0.18, size * 0.16, size * 1.08);
  group.add(beacon);

  group.userData.visualRadius = size * 4.8;
  return group;
}

function createGenericProbeModel(probe) {
  const size = probe.size;
  const materials = createProbeMaterials(probe.color);
  const group = new THREE.Group();
  group.add(createHighGainDish(size * 1.15, size * 0.45, materials));
  group.add(createProbeBox(
    new THREE.Vector3(size * 0.8, size * 0.6, size * 0.6),
    new THREE.Vector3(0, 0, size * 0.62),
    materials.foil
  ));
  group.add(createCylinderBetween(
    new THREE.Vector3(size * 0.3, 0, size * 0.7),
    new THREE.Vector3(size * 2.6, size * 0.65, size * 0.95),
    size * 0.04,
    materials.boom,
    8
  ));
  group.userData.visualRadius = size * 4;
  return group;
}

function createProbeModel(probe) {
  if (probe.name.startsWith('Voyager')) return createVoyagerModel(probe);
  if (probe.name.startsWith('Pioneer')) return createPioneerModel(probe);
  if (probe.name.startsWith('New Horizons')) return createNewHorizonsModel(probe);
  return createGenericProbeModel(probe);
}

// The probes leaving the solar system (Voyager 1 and 2, New Horizons and
// Pioneer 10) fly their real paths on the sim clock: JPL Horizons' states
// (public/data/probe-trajectories.json, scripts/build-probe-trajectories.mjs)
// read by src/probes/trajectory.js. At true scale they sit at their true
// distances (Voyager 1 172 AU out in October 2026); in artistic scale at
// artisticDistanceForAu of it, past every dwarf planet. Each passes the
// planets it flew by as they are drawn, on the side and at the distance it
// really did. A probe is hidden before its launch, and its line is the path
// flown so far.
const PROBE_PATHS_URL = '/data/probe-trajectories.json';
let probeTracks = null;
let probeTracksRequested = false;
function loadProbeTracks() {
  if (probeTracksRequested) return;
  probeTracksRequested = true;
  fetch(PROBE_PATHS_URL)
    .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`probe paths ${res.status}`))))
    // A probe chosen before its path arrived moves to it next frame, and
    // placeSpaceProbes takes the camera with it.
    .then((data) => { probeTracks = data.probes || null; })
    .catch((err) => {
      console.warn('Probe paths failed to load:', err);
      setTimeout(() => { probeTracksRequested = false; loadProbeTracks(); }, 15000);
    });
}

// Where artistic scale puts a distance from the Sun, in base units before the
// spread: through the planets' semi-major axes at their authored distances,
// with the asteroid belt's edges as rungs (ASTEROID_ARTISTIC_BELT), on a
// monotone cubic in log-log (slopes the harmonic mean of the neighbouring
// steps, Fritsch and Butland 1984, so a path crossing a rung does not kink),
// in proportion inside Mercury's orbit, and past Pluto's as the square root
// of the distance, so Eris at 95 AU (drawn on it by placeDwarfPlanet) comes
// out about one and a half times Pluto's mean distance and Voyager 1, at
// 172 AU, about twice.
const ARTISTIC_DEEP_POWER = 0.5;
let artisticLadderRungs = null;
// The ladder's rungs: AU, base distance, their logs (x, y) and the slope m.
function artisticLadder() {
  if (!artisticLadderRungs) {
    const planet = (name) => [getOrbitState(name, 2451545.0).a, baseDistances[name] || celestialData[name].distance];
    const rungs = [
      planet('mercury'), planet('venus'), planet('earth'), planet('mars'),
      [ASTEROID_ARTISTIC_AU[1], ASTEROID_ARTISTIC_BELT[0]], [ASTEROID_ARTISTIC_AU[2], ASTEROID_ARTISTIC_BELT[1]],
      planet('jupiter'), planet('saturn'), planet('uranus'), planet('neptune'), planet('pluto'),
    ].map(([au, d]) => ({ au, d, x: Math.log(au), y: Math.log(d), m: 0 }));
    const last = rungs.length - 1;
    rungs[0].m = 1;
    rungs[last].m = ARTISTIC_DEEP_POWER;
    for (let i = 1; i < last; i++) {
      const a = (rungs[i].y - rungs[i - 1].y) / (rungs[i].x - rungs[i - 1].x);
      const b = (rungs[i + 1].y - rungs[i].y) / (rungs[i + 1].x - rungs[i].x);
      rungs[i].m = a * b > 0 ? 2 * a * b / (a + b) : 0;
    }
    artisticLadderRungs = rungs;
  }
  return artisticLadderRungs;
}
function artisticDistanceForAu(au) {
  const rungs = artisticLadder();
  const first = rungs[0];
  const last = rungs[rungs.length - 1];
  if (au <= first.au) return first.d * au / first.au;
  if (au >= last.au) return last.d * Math.pow(au / last.au, ARTISTIC_DEEP_POWER);
  const x = Math.log(au);
  let i = 1;
  while (rungs[i].x < x) i++;
  const r0 = rungs[i - 1];
  const r1 = rungs[i];
  const h = r1.x - r0.x;
  const t = (x - r0.x) / h;
  const t2 = t * t;
  const t3 = t2 * t;
  return Math.exp((2 * t3 - 3 * t2 + 1) * r0.y + (t3 - 2 * t2 + t) * h * r0.m
    + (3 * t2 - 2 * t3) * r1.y + (t3 - t2) * h * r1.m);
}

// Artistic scale draws each planet at its own scale (its authored distance
// over its semi-major axis), which is not the ladder's at the planet's
// distance that day: Pluto, 32.9 AU out when New Horizons passed it, is drawn
// 5% inside where the ladder puts 32.9 AU, and the hold below would have
// pulled the probe back towards the Sun to meet it. So a probe's artistic
// distance is put right for each flyby by a share that changes slowly from
// one flyby to the next and fades over PROBE_FRAME_DECAY days after the
// last, leaving the hold the planet's small offsets to take up.
const PROBE_FRAME_DECAY = 8 * 365.25;
const probeFrameShares = new Map();
function probeFrameShare(track, jd) {
  let shares = probeFrameShares.get(track);
  if (!shares) {
    shares = [];
    const h = [0, 0, 0];
    for (const enc of track.encounters || []) {
      const planet = getPlanetPosition(enc.planet, enc.jd);
      if (!planet || !probeHelio(track, enc.jd, h)) continue;
      const kp = (baseDistances[enc.planet] || celestialData[enc.planet].distance) / getOrbitState(enc.planet, enc.jd).a;
      const drawn = kp * Math.hypot(planet.x, planet.y, planet.z);
      shares.push([enc.jd, drawn / artisticDistanceForAu(Math.hypot(h[0], h[1], h[2])) - 1]);
    }
    shares.sort((a, b) => a[0] - b[0]);
    probeFrameShares.set(track, shares);
  }
  if (!shares.length) return 0;
  let i = 0;
  while (i < shares.length && shares[i][0] <= jd) i++;
  if (i === 0) return shares[0][1];
  const [t0, c0] = shares[i - 1];
  if (i === shares.length) return c0 * (1 - THREE.MathUtils.smoothstep(jd, t0, t0 + PROBE_FRAME_DECAY));
  const [t1, c1] = shares[i];
  return c0 + (c1 - c0) * THREE.MathUtils.smoothstep(jd, t0, t1);
}

const _probePlace = {};
// Flybys that turned the path less than PROBE_BEND_STRAIGHT are moved aside
// to clear the drawn planet, those that turned it more than
// PROBE_BEND_TURNED are pushed out from its centre, and those between take
// some of each (radians).
const PROBE_BEND_STRAIGHT = 5 * Math.PI / 180;
const PROBE_BEND_TURNED = 25 * Math.PI / 180;
// A probe's scene position at jd into out, or null before its launch.
function probeScenePosition(track, jd, out) {
  const p = probePlace(track, jd, getPlanetPosition, _probePlace);
  if (!p) return null;
  if (realisticScaleMode) return out.set(p.x * AU_TO_SCENE, p.z * AU_TO_SCENE, -p.y * AU_TO_SCENE);
  const r = Math.hypot(p.x, p.y, p.z);
  const k = artisticDistanceForAu(r) * (1 + probeFrameShare(track, jd)) * scaleMultiplier / r;
  out.set(p.x * k, p.z * k, -p.y * k);
  if (p.weight > 0) {
    // Artistic scale draws a planet about a hundred times too big for its
    // distance, so through a flyby the probe is kept a clearance outside the
    // drawn planet and its rings, on the side it really passed: pushed out
    // from the planet's centre where the planet turned it (Voyager 2 at
    // Neptune wraps round it), moved aside where it passed all but straight
    // (New Horizons at Pluto, which a push from the centre would send round
    // the planet in a loop). The clearance goes as the flyby ends.
    const data = celestialData[p.planet];
    const at = p.at;
    const kp = (baseDistances[p.planet] || data.distance) * scaleMultiplier / getOrbitState(p.planet, jd).a;
    const rel = p.rel;
    const d = Math.hypot(rel[0], rel[1], rel[2]);
    const w = p.weight;
    const clear = Math.max(2.2 * data.radius, 1.15 * (data.ringOuterRadius || 0)) * w;
    const s = d > 0 ? Math.sqrt((d * kp) * (d * kp) + clear * clear) / d : 0;
    const aside = 1 - THREE.MathUtils.smoothstep(p.bend, PROBE_BEND_STRAIGHT, PROBE_BEND_TURNED);
    const nx = at[0] * kp + rel[0] * (s + (kp - s) * aside) + p.side[0] * clear * aside;
    const ny = at[1] * kp + rel[1] * (s + (kp - s) * aside) + p.side[1] * clear * aside;
    const nz = at[2] * kp + rel[2] * (s + (kp - s) * aside) + p.side[2] * clear * aside;
    out.x += (nx - out.x) * w;
    out.y += (nz - out.y) * w;
    out.z += (-ny - out.z) * w;
  }
  return out;
}

function formatProbeDistance(au) {
  const km = au * KM_PER_AU;
  return km >= 1e9
    ? `${(km / 1e9).toFixed(2)} billion km (${au.toFixed(1)} AU)`
    : `${Math.round(km / 1e6).toLocaleString('en-GB')} million km (${au.toFixed(2)} AU)`;
}

const _probeScene = new THREE.Vector3();
const _probeCarry = new THREE.Vector3();
// Each frame: every probe to where it is on the sim clock. At true scale a
// probe is drawn as much bigger than it is as the Earth's satellites are
// (scaled with the Earth as drawn, updateEarthSatelliteScales). Until the
// paths load, and before a probe's launch, it waits hidden at the Earth.
function placeSpaceProbes() {
  const size = realisticScaleMode ? getRealisticBodyRadius('earth') / celestialData.earth.radius : 1;
  for (const entry of satelliteObjects) {
    if (!entry.isProbe) continue;
    const { mesh, data } = entry;
    const track = probeTracks && probeTracks[data.name.toLowerCase()];
    const pos = track ? probeScenePosition(track, simulationJD, _probeScene) : null;
    const onPath = Boolean(pos);
    if (mesh.userData.onPath !== onPath) {
      mesh.userData.onPath = onPath;
      mesh.visible = deepSpaceBodiesVisible && onPath;
      markClickablesDirty();
    }
    if (mesh.userData.sizeScale !== size) {
      const authored = mesh.userData.authoredSize;
      mesh.userData.sizeScale = size;
      mesh.scale.setScalar(size);
      mesh.userData.radius = authored.radius * size;
      mesh.userData.selectionRadius = authored.selectionRadius * size;
    }
    if (!pos) {
      mesh.userData.waiting = true;
      if (planets.earth) planets.earth.getWorldPosition(mesh.position);
      continue;
    }
    if (mesh.userData.waiting && selectedObject === mesh && !isAnimating && camera && controls) {
      // Chosen while it waited (a link to it opened before its path loaded):
      // the camera goes with it and keeps its framing. A focus tween in
      // flight follows it on its own.
      _probeCarry.copy(pos).sub(mesh.position);
      camera.position.add(_probeCarry);
      controls.target.add(_probeCarry);
      if (_focusPrevObj === mesh) _focusPrevObjPos.add(_probeCarry);
    }
    mesh.userData.waiting = false;
    mesh.position.copy(pos);
    // The high-gain dish faces the Earth, which from out here is beside the Sun.
    mesh.lookAt(0, 0, 0);
    const au = Math.hypot(_probePlace.helio[0], _probePlace.helio[1], _probePlace.helio[2]);
    const centi = Math.round(au * 100);
    if (mesh.userData.distanceCenti !== centi) {
      mesh.userData.distanceCenti = centi;
      mesh.userData.realDistance = formatProbeDistance(au);
    }
  }
  updateProbePaths();
}

// The line of each probe's path flown so far: PROBE_PATH_STEPS points to
// each knot of its path (the knots crowd round the flybys) and steps
// doubling from a minute either side of each closest approach (New Horizons
// has one knot within 144 days of Pluto, its path there being all but
// straight, while the artistic hold takes it past the planet as drawn in
// half an hour), each halved where the line would bend or the probe swing
// round the planet it is held to, up to the last point before now, then the
// probe itself, which the last point follows every frame. The points are
// worked out once per scale (path.samples) and laid out relative to the
// probe, for float precision at 172 AU, where a float steps in sixteenths
// of a unit; the line is laid out again when the clock passes the next
// point.
const PROBE_PATH_STEPS = 6;
// A step is halved, up to PROBE_PATH_SPLITS times, where it strays from a
// straight line by more than PROBE_PATH_BEND of its length (a bend of about
// 1.5 degrees on an arc) or the probe's bearing from the planet it is held
// to turns by more than PROBE_PATH_SWEEP.
const PROBE_PATH_BEND = 0.003;
const PROBE_PATH_SWEEP = 4 * Math.PI / 180;
const PROBE_PATH_SPLITS = 9;
const PROBE_PATH_COLORS = { 'voyager 1': 0x66ffb0, 'voyager 2': 0x6ec3ff, 'new horizons': 0xffa6c9, 'pioneer 10': 0xffc477 };
const probePaths = [];
// While the spread slider moves, a line keeps its points stretched with the
// spread (right away from the flybys, where the planets' sizes do not
// stretch), and they are worked out again once it has rested for
// PROBE_PATH_SETTLE_MS, one probe a frame (an artistic path takes up to
// 10 ms).
const PROBE_PATH_SETTLE_MS = 150;
let probePathScale = null;
let probePathScaleAt = 0;

function createProbePaths() {
  for (const entry of satelliteObjects) {
    if (!entry.isProbe) continue;
    const key = entry.data.name.toLowerCase();
    const material = new LineMaterial({
      color: new THREE.Color(PROBE_PATH_COLORS[key] || 0x9fd7ff),
      linewidth: 1.6,
      transparent: true,
      opacity: 0.35,
      resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
      worldUnits: false,
      depthWrite: false
    });
    const line = new Line2(new LineGeometry(), material);
    line.name = `${entry.data.name} trajectory`;
    line.renderOrder = -2;
    line.frustumCulled = false;
    line.visible = false;
    scene.add(line);
    probePaths.push({ key, mesh: entry.mesh, line, launched: false, samples: null, prevJd: Infinity, nextJd: -Infinity, count: 0 });
  }
}

function probePathVisible(path) {
  return orbitsVisible && deepSpaceBodiesVisible && path.launched;
}

// Works out the path's points in the scene for the current scale: times and
// positions.
function buildProbePathSamples(path, track) {
  const scale = realisticScaleMode ? -1 : scaleMultiplier;
  const knots = track.knots;
  const times = [];
  const points = [];
  const sweep = Math.cos(PROBE_PATH_SWEEP);
  // The probe at t, with the planet it is held to and its bearing from it.
  const at = (t) => {
    probeScenePosition(track, t, _probeScene);
    const held = _probePlace.weight > 0;
    return {
      x: _probeScene.x,
      y: _probeScene.y,
      z: _probeScene.z,
      planet: held ? _probePlace.planet : null,
      rel: held ? _probePlace.rel.slice() : null
    };
  };
  const swings = (a, b) => {
    if (!a.planet || a.planet !== b.planet) return false;
    const [ax, ay, az] = a.rel;
    const [bx, by, bz] = b.rel;
    return ax * bx + ay * by + az * bz < sweep * Math.hypot(ax, ay, az) * Math.hypot(bx, by, bz);
  };
  const offLine = (a, m, b) => {
    const bx = b.x - a.x, by = b.y - a.y, bz = b.z - a.z;
    const mx = m.x - a.x, my = m.y - a.y, mz = m.z - a.z;
    const len2 = bx * bx + by * by + bz * bz;
    const along = len2 > 0 ? (mx * bx + my * by + mz * bz) / len2 : 0;
    const ox = mx - bx * along, oy = my - by * along, oz = mz - bz * along;
    return ox * ox + oy * oy + oz * oz > PROBE_PATH_BEND * PROBE_PATH_BEND * len2;
  };
  // The points after a (at ta) up to b (at tb), halving the step while it
  // swings round a planet or its middle or either quarter (an S bend can
  // cross the line at the middle) is off the straight line; m is the middle.
  const add = (ta, a, tb, b, m, splits) => {
    const tm = (ta + tb) / 2;
    if (splits > 0) {
      const q1 = at((ta + tm) / 2);
      const q3 = at((tm + tb) / 2);
      if (swings(a, b) || offLine(a, m, b) || offLine(a, q1, b) || offLine(a, q3, b)) {
        add(ta, a, tm, m, q1, splits - 1);
        add(tm, m, tb, b, q3, splits - 1);
        return;
      }
    }
    times.push(tb);
    points.push(b.x, b.y, b.z);
  };
  const first = knots[0][0];
  const last = knots[knots.length - 1][0];
  const base = [last];
  for (let k = 0; k < knots.length - 1; k++) {
    for (let i = 0; i < PROBE_PATH_STEPS; i++) base.push(knots[k][0] + (knots[k + 1][0] - knots[k][0]) * i / PROBE_PATH_STEPS);
  }
  for (const enc of track.encounters || []) {
    base.push(enc.jd);
    for (let minutes = 1; minutes < FLYBY_WINDOW * 1440; minutes *= 2) base.push(enc.jd - minutes / 1440, enc.jd + minutes / 1440);
  }
  base.sort((a, b) => a - b);
  let t0 = first;
  let p0 = at(t0);
  times.push(t0);
  points.push(p0.x, p0.y, p0.z);
  for (const t1 of base) {
    if (t1 <= t0 || t1 > last) continue;
    const p1 = at(t1);
    add(t0, p0, t1, p1, at((t0 + t1) / 2), PROBE_PATH_SPLITS);
    t0 = t1;
    p0 = p1;
  }
  path.samples = { scale, times: Float64Array.from(times), points: Float64Array.from(points) };
}

function layOutProbePath(path) {
  const jd = simulationJD;
  const { times, points, scale } = path.samples;
  const stretch = scale > 0 && !realisticScaleMode ? scaleMultiplier / scale : 1;
  // The points flown before now.
  let count = 0;
  let hi = times.length;
  while (count < hi) {
    const mid = (count + hi) >> 1;
    if (times[mid] < jd) count = mid + 1; else hi = mid;
  }
  path.prevJd = count > 0 ? times[count - 1] : -Infinity;
  path.nextJd = count < times.length ? times[count] : Infinity;
  const launched = count > 0;
  if (launched !== path.launched) {
    path.launched = launched;
    path.line.visible = probePathVisible(path);
  }
  path.count = launched ? count + 1 : 0;
  if (!launched) return;
  const origin = path.mesh.position;
  path.line.position.copy(origin);
  const positions = new Float32Array((count + 1) * 3);
  for (let i = 0; i < count * 3; i += 3) {
    positions[i] = points[i] * stretch - origin.x;
    positions[i + 1] = points[i + 1] * stretch - origin.y;
    positions[i + 2] = points[i + 2] * stretch - origin.z;
  }
  path.line.geometry.dispose();
  const geometry = new LineGeometry();
  geometry.setPositions(positions);
  path.line.geometry = geometry;
}

function updateProbePaths() {
  // Nothing to do while the lines are hidden (deep space starts off): they
  // are worked out when they are next shown.
  if (!probeTracks || !orbitsVisible || !deepSpaceBodiesVisible) return;
  const scale = realisticScaleMode ? -1 : scaleMultiplier;
  const now = performance.now();
  if (scale !== probePathScale) {
    probePathScale = scale;
    probePathScaleAt = now;
  }
  let build = now - probePathScaleAt >= PROBE_PATH_SETTLE_MS;
  for (const path of probePaths) {
    const track = probeTracks[path.key];
    if (!track) continue;
    const stale = !path.samples || path.samples.scale !== scale;
    if (stale) {
      // Into or out of true scale the points are worked out at once.
      const stretchable = path.samples && path.samples.scale > 0 && scale > 0;
      if (!stretchable || build) {
        if (path.samples || build) {
          buildProbePathSamples(path, track);
          build = false;
        } else {
          continue;
        }
      }
    }
    if (stale || simulationJD >= path.nextJd || simulationJD < path.prevJd) layOutProbePath(path);
    if (path.count < 2) continue;
    // The last point rides on the probe.
    const buffer = path.line.geometry.attributes.instanceStart.data;
    const at = (path.count - 2) * buffer.stride + 3;
    buffer.array[at] = path.mesh.position.x - path.line.position.x;
    buffer.array[at + 1] = path.mesh.position.y - path.line.position.y;
    buffer.array[at + 2] = path.mesh.position.z - path.line.position.z;
    buffer.addUpdateRange(at, 3);
    buffer.needsUpdate = true;
  }
}

function createSpaceProbes() {
  loadProbeTracks();
  spaceProbes.forEach(probe => {
    const group = new THREE.Group();
    const model = createProbeModel(probe);
    group.add(model);

    const visualRadius = model.userData.visualRadius || probe.size * 4;
    const focusRadius = Math.max(probe.size * 1.8, visualRadius * 0.28);
    const hitGeometry = new THREE.SphereGeometry(visualRadius, 12, 8);
    const hitMaterial = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0,
      depthWrite: false
    });
    const hitTarget = new THREE.Mesh(hitGeometry, hitMaterial);
    hitTarget.name = `${probe.name} selection volume`;
    group.add(hitTarget);

    // Placed on its path every frame (placeSpaceProbes).
    group.name = probe.name;

    group.userData = {
      name: probe.name,
      type: probe.type,
      description: probe.description,
      radius: focusRadius,
      selectionRadius: visualRadius,
      authoredSize: { radius: focusRadius, selectionRadius: visualRadius },
      realSize: probe.realSize,
      realDistance: probe.realDistance,
      launched: probe.launched,
      isSpaceProbe: true,
      isPlanet: true, // So it can be clicked/hovered
      isDeepSpaceBody: true
    };

    scene.add(group);
    satelliteObjects.push({ mesh: group, data: probe, isProbe: true });
    group.visible = false;
  });
  createProbePaths();
  markClickablesDirty();
}

// The analytic comet volume (src/comets/cometVolume.js): coma, jets, ion
// and dust tails integrated along the view ray in one draw. false brings
// back the older particle and ribbon comets below.
const COMET_VOLUME_LOOK = false;

function createCometOrbitLine(comet, group) {
  const samples = 256;
  const a = (comet.perihelion + comet.aphelion) / 2;
  const e = (comet.aphelion - comet.perihelion) / (comet.aphelion + comet.perihelion);
  const inclination = THREE.MathUtils.degToRad(comet.inclination || 0);
  const sinI = Math.sin(inclination);
  const cosI = Math.cos(inclination);
  const positions = [];

  for (let i = 0; i <= samples; i++) {
    const theta = (i / samples) * Math.PI * 2;
    const r = a * (1 - e * e) / (1 + e * Math.cos(theta));
    // -sin on z: matches computeCometOrbitPosition so the line stays
    // under the comet.
    positions.push(
      Math.cos(theta) * r * scaleMultiplier,
      Math.sin(theta) * r * sinI * scaleMultiplier * 0.3,
      -Math.sin(theta) * r * cosI * scaleMultiplier
    );
  }

  const orbitColor = new THREE.Color(comet.ionColor || comet.tailColor || 0x8ab5ff).multiplyScalar(0.55);
  const lineGeometry = new LineGeometry();
  lineGeometry.setPositions(positions);
  const lineMaterial = new LineMaterial({
    color: orbitColor,
    linewidth: 2,
    transparent: true,
    opacity: 0.35,
    resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
    worldUnits: false
  });

  const orbitLine = new Line2(lineGeometry, lineMaterial);
  orbitLine.computeLineDistances();
  orbitLine.visible = orbitsVisible && deepSpaceBodiesVisible;
  orbitLine.renderOrder = -1;
  scene.add(orbitLine);
  cometOrbitLines.push(orbitLine);
  group.userData.cometOrbitLine = orbitLine;
}

function getCometPalette(comet) {
  return {
    nucleus: comet.coreColor || 0x7a746c,
    coma: comet.comaColor || 0xd9e4ef,
    ion: comet.ionColor || comet.tailColor || 0x8ab5ff,
    dust: comet.dustColor || 0xe8dcc2
  };
}

// Diatomic carbon (C2) fluoresces green in the coma when solar
// heating drives gas production — the signature color of every
// modern comet astrophoto (NEOWISE, ZTF, Tsuchinshan-ATLAS). The
// coma lerps toward this with activity; far from the sun it stays
// the dusty pale reflection color.
const _COMET_C2_GREEN = new THREE.Color(0x7fe6a4);
const _cometComaScratch = new THREE.Color();
function getCometComaColor(palette, active) {
  return _cometComaScratch.setHex(palette.coma).lerp(_COMET_C2_GREEN, active * 0.55);
}

function getCometActivity(data, sourceDistance) {
  const solarHeating = Math.pow(
    Math.max(data.perihelion, 1) / Math.max(sourceDistance, data.perihelion),
    2.2
  );
  return THREE.MathUtils.clamp((solarHeating - 0.12) / 0.88, 0, 1);
}

function createCometParticleMaterial(color, pointScale = 120, minPointSize = 0.5) {
  return new THREE.ShaderMaterial({
    uniforms: {
      color: { value: new THREE.Color(color) },
      opacity: { value: 1 },
      pointScale: { value: pointScale },
      minPointSize: { value: minPointSize }
    },
    vertexShader: `
      attribute float size;
      attribute float alpha;
      uniform float pointScale;
      uniform float minPointSize;
      varying float vAlpha;
      void main() {
        vAlpha = alpha;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = max(
          size * (pointScale / max(1.0, -mvPosition.z)),
          minPointSize
        );
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      uniform vec3 color;
      uniform float opacity;
      varying float vAlpha;
      void main() {
        vec2 center = gl_PointCoord - vec2(0.5);
        float dist = length(center);
        // Pinpoint sprite: a tight bright core with a short falloff
        // instead of a wide soft halo, so each particle reads as a fine
        // grain of dust/ice rather than a big fuzzy glob.
        float a = 1.0 - smoothstep(0.0, 0.42, dist);
        a = pow(a, 2.2);
        gl_FragColor = vec4(color, a * vAlpha * opacity);
      }
    `,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false
  });
}

function createCometTailRibbonMaterial(color) {
  return new THREE.ShaderMaterial({
    uniforms: {
      color: { value: new THREE.Color(color) },
      opacity: { value: 0 },
      time: { value: 0 }
    },
    vertexShader: `
      attribute float tailT;
      attribute float tailSide;
      varying float vTailT;
      varying float vTailSide;

      void main() {
        vTailT = tailT;
        vTailSide = abs(tailSide);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 color;
      uniform float opacity;
      uniform float time;
      varying float vTailT;
      varying float vTailSide;

      void main() {
        float headFade = smoothstep(0.0, 0.05, vTailT);
        float tailFade = pow(1.0 - vTailT, 1.5);
        float edgeFade = smoothstep(1.0, 0.05, vTailSide);
        // Two-frequency filament so the ion tail reads with both broad
        // streamers and finer striations layered together — the visible
        // texture in real ion-tail photos comes from this kind of
        // multi-scale density structure, not a single sine band.
        float fastBand = sin(vTailT * 42.0 - time * 1.15 + vTailSide * 4.5);
        float slowBand = sin(vTailT * 11.0 - time * 0.42 + vTailSide * 1.8);
        float filament = 0.70 + fastBand * 0.18 + slowBand * 0.22;
        float alpha = opacity * headFade * tailFade * edgeFade * filament;
        if (alpha < 0.006) discard;
        gl_FragColor = vec4(color, alpha);
      }
    `,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    depthTest: true,
    side: THREE.DoubleSide,
    toneMapped: false
  });
}

function createCometTailRibbon(name, color, length, width) {
  const columns = 9;
  const rows = 72;
  const positions = [];
  const tailTs = [];
  const tailSides = [];
  const indices = [];

  for (let row = 0; row <= rows; row++) {
    const t = row / rows;
    const widthAtT = width * (0.08 + Math.pow(t, 0.68) * 0.92);
    const z = t * length;
    for (let col = 0; col <= columns; col++) {
      const side = (col / columns) * 2 - 1;
      positions.push(side * widthAtT, 0, z);
      tailTs.push(t);
      tailSides.push(side);
    }
  }

  const rowStride = columns + 1;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < columns; col++) {
      const a = row * rowStride + col;
      const b = a + 1;
      const c = a + rowStride;
      const d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('tailT', new THREE.Float32BufferAttribute(tailTs, 1));
  geometry.setAttribute('tailSide', new THREE.Float32BufferAttribute(tailSides, 1));
  geometry.setIndex(indices);
  geometry.computeBoundingSphere();

  const ribbon = new THREE.Mesh(geometry, createCometTailRibbonMaterial(color));
  ribbon.name = name;
  ribbon.frustumCulled = false;
  ribbon.renderOrder = 10;
  return ribbon;
}

function computeCometOrbitPosition(comet, angle) {
  const a = (comet.perihelion + comet.aphelion) / 2;
  const e = (comet.aphelion - comet.perihelion) / (comet.aphelion + comet.perihelion);
  const r = a * (1 - e * e) / (1 + e * Math.cos(angle));
  const incl = THREE.MathUtils.degToRad(comet.inclination || 0);
  // -sin on z: counterclockwise from +Y, same sense as the planets.
  return {
    r,
    position: new THREE.Vector3(
      Math.cos(angle) * r * scaleMultiplier,
      Math.sin(angle) * r * Math.sin(incl) * scaleMultiplier * 0.3,
      -Math.sin(angle) * r * Math.cos(incl) * scaleMultiplier
    )
  };
}

function computeCometTangent(comet, angle) {
  const a = (comet.perihelion + comet.aphelion) / 2;
  const e = (comet.aphelion - comet.perihelion) / (comet.aphelion + comet.perihelion);
  const p = a * (1 - e * e);
  const denom = 1 + e * Math.cos(angle);
  const r = p / denom;
  const dr = p * e * Math.sin(angle) / (denom * denom);
  const incl = THREE.MathUtils.degToRad(comet.inclination || 0);
  const sinI = Math.sin(incl);
  const cosI = Math.cos(incl);
  // z component negated to match the mirrored orbit parameterization.
  const tangent = new THREE.Vector3(
    dr * Math.cos(angle) - r * Math.sin(angle),
    (dr * Math.sin(angle) + r * Math.cos(angle)) * sinI * 0.3,
    -(dr * Math.sin(angle) + r * Math.cos(angle)) * cosI
  );
  if (tangent.lengthSq() < 1e-8) {
    tangent.set(0, 1, 0);
  }
  return tangent.normalize();
}

function buildCometBasis(position, tangent) {
  const tailAxis = position.clone().normalize();
  let tangentAxis = tangent.clone().sub(tailAxis.clone().multiplyScalar(tangent.dot(tailAxis)));
  if (tangentAxis.lengthSq() < 1e-8) {
    tangentAxis = new THREE.Vector3(0, 1, 0).cross(tailAxis);
  }
  tangentAxis.normalize();
  const binormal = new THREE.Vector3().crossVectors(tailAxis, tangentAxis).normalize();
  const basis = new THREE.Matrix4().makeBasis(tangentAxis, binormal, tailAxis);
  const quaternion = new THREE.Quaternion().setFromRotationMatrix(basis);
  return { tailAxis, tangentAxis, binormal, quaternion };
}

// state, when given, is where the comet is and where it is going ({ r, the
// distance from the Sun in the units of data.perihelion; position; tangent,
// a unit vector}), for a comet on a real path (3I/ATLAS,
// placeInterstellarObjects); without it, the authored orbit at orbitAngle.
function updateCometVisuals(mesh, data, state = null) {
  const visuals = mesh.userData && mesh.userData.cometVisuals;
  if (!visuals) return;

  const angle = mesh.userData.orbitAngle || 0;
  const { r, position } = state || computeCometOrbitPosition(data, angle);
  const tangent = state ? state.tangent : computeCometTangent(data, angle);
  const basis = buildCometBasis(position, tangent);

  mesh.quaternion.copy(basis.quaternion);

  const active = getCometActivity(data, r);
  // Read shape/intensity parameters from the admin override object.
  // When the localhost admin panel isn't active these match the
  // historical hardcoded values, so behaviour is unchanged.
  const cao = cometAdminOverrides;
  const ionTailLengthScale = cao.ionLengthBase + active * cao.ionLengthGain;
  const dustTailLengthScale = cao.dustLengthBase + active * cao.dustLengthGain;
  const ionWidthScale = cao.ionWidthBase + active * cao.ionWidthGain;
  const dustWidthScale = cao.dustWidthBase + active * cao.dustWidthGain;

  const palette = getCometPalette(data);
  const elapsed = clock && typeof clock.elapsedTime === 'number'
    ? clock.elapsedTime
    : (typeof performance !== 'undefined' ? performance.now() : Date.now()) * 0.001;

  // Realism boost: real comet photos are dominated by a bright fuzzy
  // coma (gas envelope), with the tails fading off into space. Earlier
  // values had the coma too faint to read as a glowing ball and the
  // tails so transparent they looked like pencil lines. Upping the
  // baseline visibility so even at low activity the comet reads as a
  // proper comet, not a star.
  const baseGlow = 0.45 + active * 0.55; // 0.45 minimum so it's always visible

  // Scale + tumble apply to ANY nucleus — the procedural placeholder
  // mesh OR the real GLB wrapper group it gets swapped for. Rotate
  // around the cached random axis so each comet tumbles distinctively
  // (speed per-comet, set in createComets; elapsed drives the angle).
  visuals.core.scale.setScalar(0.58 + active * 0.08);
  const coreTumble = visuals.core.userData && visuals.core.userData.tumble;
  if (coreTumble) {
    visuals.core.setRotationFromAxisAngle(coreTumble.axis, elapsed * coreTumble.speed);
  }
  // Emissive only on the procedural material core (the GLB ships its
  // own materials, so this guard skips the wrapper group).
  if (visuals.core.material) {
    visuals.core.material.emissive.setHex(palette.nucleus);
    visuals.core.material.emissiveIntensity = visuals.volume ? 0 : 0.02 + active * 0.04;
  }

  if (visuals.volume) {
    updateCometVolume(visuals.volume, data, {
      active,
      r: position.length(),
      time: elapsed,
      nucleusZ: visuals.core.position.z
    });
    return;
  }

  const comaColor = getCometComaColor(palette, active);
  if (visuals.comaMaterial && visuals.comaMaterial.uniforms) {
    visuals.comaMaterial.uniforms.color.value.copy(comaColor);
    visuals.comaMaterial.uniforms.opacity.value = cao.comaCloudOpacityBase + active * cao.comaCloudOpacityGain;
  }
  if (visuals.comaEnvelope && visuals.comaEnvelope.material && visuals.comaEnvelope.material.uniforms) {
    const u = visuals.comaEnvelope.material.uniforms;
    u.color.value.copy(comaColor);
    u.opacity.value = cao.comaOpacityBase + active * cao.comaOpacityGain;
    if (u.tailTaper) u.tailTaper.value = cao.tailTaperBase - active * cao.tailTaperReduce;
    if (u.fanLow)         u.fanLow.value         = cao.fanLow;
    if (u.fanHigh)        u.fanHigh.value        = cao.fanHigh;
    if (u.headRadius)     u.headRadius.value     = cao.headRadius;
    if (u.backFadeStart)  u.backFadeStart.value  = cao.backFadeStart;
    if (u.tailDecayPow)   u.tailDecayPow.value   = cao.tailDecayPow;
    if (u.time)           u.time.value           = elapsed;
    if (u.sunPosition)    u.sunPosition.value.set(0, 0, 0);
    const baseRadius = data.size * 5.8;
    const haloPerp = cao.haloPerpBase + active * cao.haloPerpGain;
    const haloLong = cao.haloLongBase + active * cao.haloLongGain;
    const haloOffset = haloLong * baseRadius;
    visuals.comaEnvelope.scale.set(haloPerp, haloPerp, haloLong);
    visuals.comaEnvelope.position.set(0, 0, haloOffset);
  }
  if (visuals.jetMaterial && visuals.jetMaterial.uniforms) {
    // Jets are fresh gas — they pick up the C2 green strongest.
    visuals.jetMaterial.uniforms.color.value.copy(comaColor);
    visuals.jetMaterial.uniforms.opacity.value = cao.jetOpacityBase + active * cao.jetOpacityGain;
  }

  if (visuals.ionMaterial && visuals.ionMaterial.uniforms) {
    visuals.ionMaterial.uniforms.color.value.setHex(palette.ion);
  }
  if (visuals.dustMaterial && visuals.dustMaterial.uniforms) {
    visuals.dustMaterial.uniforms.color.value.setHex(palette.dust);
  }
  if (visuals.ionRibbon && visuals.ionRibbon.material && visuals.ionRibbon.material.uniforms) {
    visuals.ionRibbon.material.uniforms.color.value.setHex(palette.ion);
    visuals.ionRibbon.material.uniforms.opacity.value = cao.ionOpacityBase + active * cao.ionOpacityGain;
    visuals.ionRibbon.material.uniforms.time.value = elapsed;
    visuals.ionRibbon.scale.set(ionWidthScale * 1.6, 1, ionTailLengthScale * 2.1);
  }
  if (visuals.dustRibbon && visuals.dustRibbon.material && visuals.dustRibbon.material.uniforms) {
    visuals.dustRibbon.material.uniforms.color.value.setHex(palette.dust);
    visuals.dustRibbon.material.uniforms.opacity.value = cao.dustOpacityBase + active * cao.dustOpacityGain;
    visuals.dustRibbon.material.uniforms.time.value = elapsed * 0.78;
    visuals.dustRibbon.scale.set(dustWidthScale * 1.85, 1, dustTailLengthScale * 1.7);
    // Type II dust tail lag. Heavy dust grains keep more of the
    // comet's orbital velocity than ions do, so the dust tail bows
    // AWAY from the anti-sun line, trailing back along the orbit —
    // the classic V-split between the straight blue ion tail and
    // the curved cream dust tail in every comet photograph. Local
    // +X is the motion tangent, so the lag tilts the ribbon toward
    // -X, stronger when the comet is active (longer tail = more
    // visible curvature).
    visuals.dustRibbon.rotation.y = -(0.12 + active * 0.16);
  }

  const comaPositions = visuals.comaPositions;
  const comaAlphas = visuals.comaAlphas;
  const comaSizes = visuals.comaSizes;
  const baseComaPositions = visuals.baseComaPositions;
  const baseComaAlphas = visuals.baseComaAlphas;
  const baseComaSizes = visuals.baseComaSizes;
  if (comaPositions && baseComaPositions) {
    const comaRadiusScale = data.size * (2.55 + active * 4.55);
    const comaSideScale = 0.62 + active * 0.12;
    const comaTailStretch = 1.28 + active * 1.35;
    // Continuous outward stream. Each particle has a life cycle:
    // born near the nucleus → drifts anti-sun → fades out → recycles.
    // Per-particle phase from index means thousands of particles
    // are at different points of their life cycle simultaneously,
    // so the cloud is always fully populated and never appears to
    // pulse. End result: visible OUTWARD FLOW — particles look
    // like they're being blown away by solar pressure, not
    // orbiting the nucleus.
    const streamRate = 0.08;          // cycle rate (slow flow)
    // Nucleus position in the comet group's local frame. The core
    // mesh sits at (0, 0, coreBaseRadius * 1.0) — particles must
    // emit from this same position so they appear to come FROM
    // the comet body, not from the abstract group origin.
    const nucleusZ = data.size * 0.52;
    for (let i = 0; i < comaPositions.length; i += 3) {
      const bx = baseComaPositions[i];
      const by = baseComaPositions[i + 1];
      const bz = baseComaPositions[i + 2];
      const tailBias = Math.max(0, bz);
      const sunwardCut = bz < 0 ? Math.max(0, 1 + bz * 20) : 1;
      const phase = i * 0.00071;       // unique seed per particle
      const lifeT = (elapsed * streamRate + phase) % 1.0;
      const radial = 0.05 + lifeT * 0.95;
      const fadeIn  = Math.min(1, lifeT * 12);
      const fadeOut = 1 - Math.min(1, Math.max(0, (lifeT - 0.85) / 0.15));
      const lifeAlpha = fadeIn * fadeOut;

      // Position blends from the nucleus (at birth, radial≈0.05)
      // to the particle's full base position (at end, radial=1.0).
      // x/y converge to 0 at birth, full at end. z anchors at
      // nucleusZ at birth and reaches the full streaming position
      // at end. Result: every particle visibly emerges from the
      // nucleus and streams outward.
      comaPositions[i]     = bx * comaRadiusScale * comaSideScale * radial;
      comaPositions[i + 1] = by * comaRadiusScale * comaSideScale * 0.82 * radial;
      comaPositions[i + 2] = nucleusZ * (1.0 - lifeT) +
                             (bz * comaRadiusScale * comaTailStretch +
                              tailBias * data.size * (0.9 + active * 1.8)) * radial;
      comaAlphas[i / 3] = baseComaAlphas[i / 3] * sunwardCut *
                         (0.36 + active * 0.46) * lifeAlpha;
      // Particles grow slightly as they age — dust spreads as it
      // moves anti-sun, individual grains in the cloud appear
      // larger from camera as their column thickness changes.
      comaSizes[i / 3] = baseComaSizes[i / 3] * (0.95 + active * 0.28) *
                        (0.7 + lifeT * 0.4);
    }
    visuals.comaGeometry.attributes.position.needsUpdate = true;
    visuals.comaGeometry.attributes.alpha.needsUpdate = true;
    visuals.comaGeometry.attributes.size.needsUpdate = true;
  }

  const jetPositions = visuals.jetPositions;
  const jetAlphas = visuals.jetAlphas;
  const jetParams = visuals.jetParams;
  const jetSources = visuals.jetSources || [];
  if (jetPositions && jetParams && jetSources.length) {
    const jetLength = data.tailLength * (0.08 + active * 0.25);
    const jetSpread = data.size * (0.08 + active * 0.42);
    // Stream speed — full source→tip cycle takes ~8 seconds at the
    // base rate, faster when activity is high (near perihelion).
    // Slow enough that the outgassing reads as a steady plume
    // rather than a frantic spray, but visibly animated.
    const streamSpeed = 0.03 + active * 0.025;  // 75% slower
    for (let i = 0; i < jetPositions.length; i += 3) {
      const p = (i / 3) * 5;
      const baseT = jetParams[p];
      const sideA = jetParams[p + 1];
      const sideB = jetParams[p + 2];
      const phase = jetParams[p + 3];
      const sourceIndex = Math.floor(jetParams[p + 4]) % jetSources.length;
      const source = jetSources[sourceIndex];
      // Animated t — each particle continuously streams from source
      // (t=0) to tip (t=1), then wraps back. Per-particle baseT
      // offset spreads particles out along the jet so the plume
      // is always populated rather than pulsing.
      const t = (baseT + elapsed * streamSpeed) % 1.0;
      const shimmer = Math.sin(elapsed * 0.21 + phase) * 0.12;  // 75% slower
      const fan = jetSpread * (0.2 + t * 1.35);
      jetPositions[i] = source.x * data.size * (0.48 + t * 0.25) + sideA * fan + shimmer * fan;
      jetPositions[i + 1] = source.y * data.size * (0.48 + t * 0.25) + sideB * fan * 0.62;
      jetPositions[i + 2] = source.z * data.size * 0.5 + t * data.size * 1.5 + t * t * jetLength;
      // Alpha now drives a fade-in at source (t≈0) AND fade-out
      // at tip (t≈1) so streaming particles don't pop in/out as
      // they wrap. Bell curve peaks mid-stream.
      const fadeIn  = THREE.MathUtils.smoothstep(t, 0.0, 0.08);
      const fadeOut = 1.0 - THREE.MathUtils.smoothstep(t, 0.85, 1.0);
      jetAlphas[i / 3] = fadeIn * fadeOut * Math.pow(1 - t, 1.0) * (0.05 + active * 0.95);
    }
    visuals.jetGeometry.attributes.position.needsUpdate = true;
    visuals.jetGeometry.attributes.alpha.needsUpdate = true;
  }

  const ionPositions = visuals.ionPositions;
  const ionAlphas = visuals.ionAlphas;
  const ionBase = visuals.baseIonPositions;
  const ionMaxZ = visuals.ionMaxZ || 1;
  // Ion tail particles stream anti-sun continuously. Each particle's
  // axial position advances along the tail, wrapping back to source
  // when it reaches the tip. Each gets a unique phase so the tail
  // is always populated rather than pulsing.
  const ionStreamSpeed = 0.02 + active * 0.025;  // 75% slower
  for (let i = 0; i < ionPositions.length; i += 3) {
    const bx = ionBase[i];
    const by = ionBase[i + 1];
    const bz = ionBase[i + 2];
    // Animated t — slow streaming + per-particle phase from index.
    const tBase = THREE.MathUtils.clamp(bz / ionMaxZ, 0, 1);
    const tAnim = (tBase + elapsed * ionStreamSpeed) % 1.0;
    const phase = i * 0.013;
    // Subtle lateral wiggle to give the tail filament structure.
    const wiggleAmp = data.size * 0.04 * (0.2 + tAnim);
    const wiggleX = Math.sin(elapsed * 0.375 + phase) * wiggleAmp;  // 75% slower
    const wiggleY = Math.cos(elapsed * 0.425 + phase * 1.3) * wiggleAmp * 0.5;
    const width = (0.08 + tAnim * 0.42 + tAnim * tAnim * 0.2) * ionWidthScale * data.size * 1.8;
    ionPositions[i]     = bx * width + wiggleX;
    ionPositions[i + 1] = by * width * 0.42 + wiggleY;
    ionPositions[i + 2] = tAnim * data.tailLength * 1.5 * ionTailLengthScale;
    // Fade-in / fade-out at the loop boundaries so wrap-around
    // isn't visible.
    const fadeIn  = THREE.MathUtils.smoothstep(tAnim, 0.0, 0.06);
    const fadeOut = 1.0 - THREE.MathUtils.smoothstep(tAnim, 0.88, 1.0);
    ionAlphas[i / 3] = fadeIn * fadeOut * (1 - tAnim * 0.7) * (0.08 + active * 0.9);
  }
  visuals.ionGeometry.attributes.position.needsUpdate = true;
  visuals.ionGeometry.attributes.alpha.needsUpdate = true;

  const dustPositions = visuals.dustPositions;
  const dustAlphas = visuals.dustAlphas;
  const dustBase = visuals.baseDustPositions;
  const dustMaxZ = visuals.dustMaxZ || 1;
  // Dust tail particles stream anti-sun too, slower than ions (real
  // dust is heavier and accelerates less aggressively under solar
  // radiation pressure). Lateral wiggle gives the broad dust fan
  // visible turbulent structure.
  const dustStreamSpeed = 0.0125 + active * 0.018;  // 75% slower
  for (let i = 0; i < dustPositions.length; i += 3) {
    const bx = dustBase[i];
    const by = dustBase[i + 1];
    const bz = dustBase[i + 2];
    const tBase = THREE.MathUtils.clamp(bz / dustMaxZ, 0, 1);
    const tAnim = (tBase + elapsed * dustStreamSpeed) % 1.0;
    const phase = i * 0.019;
    // Dust wiggle bumped from 0.06 → 0.18 amplitude so the
    // particulate flow is visibly turbulent rather than appearing
    // to drift on rails. Each particle's phase from its index makes
    // the motion uncoordinated. Plus a slow radial outward push
    // (real dust accelerates anti-sun via solar pressure) so the
    // particles feel like they're flowing outward, not just
    // wiggling in place.
    const wiggleAmp = data.size * 0.18 * (0.3 + tAnim);
    const wiggleX = Math.sin(elapsed * 0.275 + phase) * wiggleAmp;
    const wiggleY = Math.cos(elapsed * 0.225 + phase * 1.7) * wiggleAmp * 0.6;
    const radialPush = Math.sin(elapsed * 0.15 + phase * 2.3) * data.size * 0.04 * tAnim;
    const width = (0.18 + tAnim * 0.95) * dustWidthScale * data.size * 2.0;
    // Syndyne curvature: older dust (farther down the tail) lags
    // progressively behind the head's orbital motion, so lateral
    // displacement grows quadratically with tail fraction. Local +X
    // is the motion tangent; the curve bows toward -X, matching the
    // ribbon's lag tilt above.
    const tailLen = data.tailLength * dustTailLengthScale;
    const syndyne = -(0.10 + active * 0.14) * tAnim * tAnim * tailLen;
    dustPositions[i]     = bx * width + wiggleX + bx * radialPush + syndyne;
    dustPositions[i + 1] = by * width * 0.38 + wiggleY + by * radialPush;
    dustPositions[i + 2] = tAnim * tailLen;
    const fadeIn  = THREE.MathUtils.smoothstep(tAnim, 0.0, 0.05);
    const fadeOut = 1.0 - THREE.MathUtils.smoothstep(tAnim, 0.90, 1.0);
    dustAlphas[i / 3] = fadeIn * fadeOut * (1 - tAnim * 0.5) * (0.08 + active * 0.66);
  }
  visuals.dustGeometry.attributes.position.needsUpdate = true;
  visuals.dustGeometry.attributes.alpha.needsUpdate = true;
}

// Real rocky nucleus model for comets. "Asteroid 101955 Bennu" by
// Geo_Monkey (CC BY 4.0, credited on /credits) is a dark rubble-pile
// near-Earth body — the closest real-scan match to a comet nucleus. We
// load it once, normalise it (centred, longest dim = 1, dark cometary
// albedo baked into the materials), and clone it per comet. The
// procedural nucleus stays as the instant placeholder until this loads.
let cometNucleusSource = null;
let cometNucleusPromise = null;
function loadCometNucleusModel() {
  if (cometNucleusSource) return Promise.resolve(cometNucleusSource);
  if (cometNucleusPromise) return cometNucleusPromise;
  cometNucleusPromise = (async () => {
    const [{ GLTFLoader }, meshoptModule] = await Promise.all([
      import('three/addons/loaders/GLTFLoader.js'),
      import('three/addons/libs/meshopt_decoder.module.js')
    ]);
    const MeshoptDecoder = meshoptModule.MeshoptDecoder || meshoptModule.default || meshoptModule;
    if (MeshoptDecoder && MeshoptDecoder.ready) {
      try { await MeshoptDecoder.ready; } catch (err) { console.warn('[comet] meshopt ready failed', err); }
    }
    const loader = new GLTFLoader();
    loader.setMeshoptDecoder(MeshoptDecoder);
    const gltf = await loader.loadAsync('/models/asteroid-bennu.glb');
    const scene = gltf.scene;
    const box = new THREE.Box3().setFromObject(scene);
    const size = new THREE.Vector3(); box.getSize(size);
    const center = new THREE.Vector3(); box.getCenter(center);
    const fit = 1 / (Math.max(size.x, size.y, size.z) || 1);
    scene.traverse((n) => {
      if (!n.isMesh) return;
      n.castShadow = false;
      n.receiveShadow = false;
      if (n.material) {
        // Comet nuclei are among the darkest objects in the solar
        // system (~4% albedo, charcoal). The Bennu model ships much
        // lighter, so darken hard and kill any emissive/specular so the
        // rock reads as a near-black cometary body under the coma.
        const m = n.material;
        if (m.color) m.color.multiplyScalar(0.14);
        if (typeof m.roughness === 'number') m.roughness = 1.0;
        if (typeof m.metalness === 'number') m.metalness = 0.0;
        if (m.emissive) m.emissive.setScalar(0.0);
      }
    });
    cometNucleusSource = { scene, fit, center };
    return cometNucleusSource;
  })();
  return cometNucleusPromise;
}

// Swap a comet's procedural placeholder nucleus for the real GLB rock
// once it loads. Wrapped in a Group so the per-frame scale/tumble in
// updateCometVisuals drives the wrapper while the GLB keeps its own
// normalisation scale.
function swapCometNucleusToModel(group, placeholderCore, coreBaseRadius) {
  loadCometNucleusModel().then((src) => {
    if (!group || !placeholderCore || !group.parent && !placeholderCore.parent) return;
    const glb = src.scene.clone(true);
    const s = src.fit * coreBaseRadius * 2.0; // longest dim → nucleus diameter
    glb.scale.setScalar(s);
    glb.position.copy(src.center).multiplyScalar(-s);
    const wrapper = new THREE.Group();
    wrapper.name = 'cometCoreModel';
    wrapper.add(glb);
    wrapper.position.copy(placeholderCore.position);
    wrapper.userData.tumble = placeholderCore.userData.tumble;
    const parent = placeholderCore.parent || group;
    parent.add(wrapper);
    parent.remove(placeholderCore);
    if (placeholderCore.geometry) placeholderCore.geometry.dispose();
    if (placeholderCore.material && placeholderCore.material.dispose) placeholderCore.material.dispose();
    const v = group.userData && group.userData.cometVisuals;
    if (v) v.core = wrapper;
  }).catch((err) => console.warn('[comet] nucleus model load failed; keeping procedural core', err));
}

// Create comets with tails
// list and finish default to the authored comets and finishComet; the
// interstellar comet 3I/ATLAS is built by the same code with its own
// (createInterstellarObjects).
function createComets(list = comets, finish = finishComet) {
  list.forEach(comet => {
    const palette = getCometPalette(comet);
    const group = new THREE.Group();
    group.name = comet.name;

    // Comet nucleus — irregular rocky body. Real cometary nuclei
    // (Rosetta's 67P, Borrelly, Halley as imaged by Giotto) are
    // strongly non-spherical, lumpy, with surface relief from
    // sublimation pits and outgassing fissures. We build a higher-
    // resolution sphere then displace each vertex by multi-octave
    // value noise so the silhouette reads as a real rock, not a
    // smoothly-perturbed ball. Tumbling rotation is applied in
    // updateCometVisuals.
    const coreBaseRadius = comet.size * 0.52;
    const coreGeometry = new THREE.SphereGeometry(coreBaseRadius, 64, 48);
    const corePositions = coreGeometry.attributes.position;
    // Cheap 3D value noise — hash + lerp.
    const cometCoreHash = (x, y, z) => {
      const s = Math.sin(x * 12.9898 + y * 78.233 + z * 37.719) * 43758.5453;
      return s - Math.floor(s);
    };
    const cometCoreNoise3 = (x, y, z) => {
      const ix = Math.floor(x), iy = Math.floor(y), iz = Math.floor(z);
      const fx = x - ix, fy = y - iy, fz = z - iz;
      const sx = fx * fx * (3 - 2 * fx);
      const sy = fy * fy * (3 - 2 * fy);
      const sz = fz * fz * (3 - 2 * fz);
      const n000 = cometCoreHash(ix,     iy,     iz);
      const n100 = cometCoreHash(ix + 1, iy,     iz);
      const n010 = cometCoreHash(ix,     iy + 1, iz);
      const n110 = cometCoreHash(ix + 1, iy + 1, iz);
      const n001 = cometCoreHash(ix,     iy,     iz + 1);
      const n101 = cometCoreHash(ix + 1, iy,     iz + 1);
      const n011 = cometCoreHash(ix,     iy + 1, iz + 1);
      const n111 = cometCoreHash(ix + 1, iy + 1, iz + 1);
      const lx0 = n000 * (1 - sx) + n100 * sx;
      const lx1 = n010 * (1 - sx) + n110 * sx;
      const lx2 = n001 * (1 - sx) + n101 * sx;
      const lx3 = n011 * (1 - sx) + n111 * sx;
      const ly0 = lx0 * (1 - sy) + lx1 * sy;
      const ly1 = lx2 * (1 - sy) + lx3 * sy;
      return ly0 * (1 - sz) + ly1 * sz;
    };
    for (let i = 0; i < corePositions.count; i++) {
      const x = corePositions.getX(i);
      const y = corePositions.getY(i);
      const z = corePositions.getZ(i);
      const r = Math.sqrt(x * x + y * y + z * z) || 1;
      const nx = x / r, ny = y / r, nz = z / r;
      // 3-octave fBm — coarse lumps, mid pits, fine grain
      const f1 = cometCoreNoise3(nx * 1.4,  ny * 1.4,  nz * 1.4);
      const f2 = cometCoreNoise3(nx * 4.2,  ny * 4.2,  nz * 4.2);
      const f3 = cometCoreNoise3(nx * 11.0, ny * 11.0, nz * 11.0);
      const fbm = f1 * 0.55 + f2 * 0.28 + f3 * 0.17;
      // Bias toward [-0.5, +0.5] then scale to a meaningful relief.
      const displace = 1.0 + (fbm - 0.5) * 0.55;
      positions: void 0;
      corePositions.setXYZ(
        i,
        nx * coreBaseRadius * displace,
        ny * coreBaseRadius * displace,
        nz * coreBaseRadius * displace
      );
    }
    coreGeometry.computeVertexNormals();

    const coreMaterial = new THREE.MeshStandardMaterial({
      color: new THREE.Color(palette.nucleus).multiplyScalar(0.55),
      roughness: 1.0,
      metalness: 0.0,
      emissive: new THREE.Color(palette.nucleus).multiplyScalar(0.20),
      emissiveIntensity: 0.002,
      flatShading: false
    });
    // Procedural surface detail. Real comet nuclei (Rosetta's 67P)
    // are very dark (~4% albedo), pitted, with bright fresh-ice
    // patches in deep depressions, dark dust mantles on raised
    // terrain, and crack-fissure shadows. Multi-octave 3D noise of
    // the vertex's local position drives:
    //   - albedo (rock / dust / ice mix)
    //   - roughness (rock rough, ice glassier)
    //   - emissive cracks (deep fissures glow faintly from below)
    coreMaterial.onBeforeCompile = (shader) => {
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', `#include <common>\nvarying vec3 vCorePosLocal;`)
        .replace('#include <begin_vertex>', `#include <begin_vertex>\nvCorePosLocal = position;`);

      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>
${OKLAB_MIX_GLSL}
varying vec3 vCorePosLocal;

float coreHash3(vec3 p) {
  return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453);
}
float coreNoise3(vec3 p) {
  vec3 i = floor(p);
  vec3 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(mix(coreHash3(i + vec3(0,0,0)), coreHash3(i + vec3(1,0,0)), f.x),
        mix(coreHash3(i + vec3(0,1,0)), coreHash3(i + vec3(1,1,0)), f.x), f.y),
    mix(mix(coreHash3(i + vec3(0,0,1)), coreHash3(i + vec3(1,0,1)), f.x),
        mix(coreHash3(i + vec3(0,1,1)), coreHash3(i + vec3(1,1,1)), f.x), f.y),
    f.z
  );
}
float coreFbm3(vec3 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * coreNoise3(p);
    p *= 2.07;
    a *= 0.5;
  }
  return v;
}`)
        .replace('#include <color_fragment>', `
// Procedural nucleus surface — multi-octave 3D noise sampled
// from object-local position.
vec3 nucPos = vCorePosLocal * 1.3;
float nucLarge = coreFbm3(nucPos);
float nucMid   = coreFbm3(nucPos * 3.5 + vec3(13.1, 7.4, 5.9));
float nucFine  = coreFbm3(nucPos * 11.0 + vec3(57.3, 31.2, 19.7));
float nucSurface = nucLarge * 0.55 + nucMid * 0.30 + nucFine * 0.15;

// Terrain types (real comet nuclei have these): dark rocky base,
// lighter dust mantles on raised terrain, bright fresh-ice patches
// where outgassing has exposed subsurface ices.
vec3 darkRock   = vec3(0.06, 0.05, 0.045);
vec3 dustMantle = vec3(0.20, 0.16, 0.12);
vec3 icePatch   = vec3(0.55, 0.55, 0.62);

// OKLCh between the dark rocky base and the dust mantle so the
// terrain transitions through proper warm browns rather than a
// muddy grey-brown linear-RGB midpoint.
vec3 surfColor = oklchMix(darkRock, dustMantle, smoothstep(0.30, 0.65, nucSurface));
// Ice patches are localised, only show where the large-scale
// noise puts us in a depression AND the fine-scale noise has a
// fresh-exposed look. Multiplier limits ice coverage to ~5-8%
// of surface as in real Rosetta imagery. OKLCh from the warm
// dusty rock to the cool blue-white ice keeps both endpoints
// saturated at the patch edge.
float iceFactor = smoothstep(0.70, 0.85, nucMid) * smoothstep(0.55, 0.30, nucLarge);
surfColor = oklchMix(surfColor, icePatch, iceFactor * 0.65);

// Crack / fissure darkening — deepest pockets of the noise field
// represent vent fractures, much darker than the surrounding
// surface (the floor of the crack is in deep shadow).
float crackFactor = smoothstep(0.30, 0.18, nucLarge) * smoothstep(0.45, 0.20, nucMid);
surfColor *= mix(1.0, 0.30, crackFactor);

// Tint by the material's color uniform so each comet keeps its
// distinct palette character (Halley darker, NEOWISE slightly
// warmer, etc) on top of the procedural variation.
diffuseColor.rgb = surfColor * (0.7 + diffuse.rgb * 0.3);

#include <color_fragment>`)
        .replace('#include <roughnessmap_fragment>', `
#include <roughnessmap_fragment>
// Roughness varies with terrain: rough rocky terrain (1.0), softer
// dust (0.85), glassy ice patches (0.35). Cracks pitch up rough
// again because their floors are dust-filled.
float rough = mix(1.0, 0.85, smoothstep(0.30, 0.65, nucSurface));
rough = mix(rough, 0.35, iceFactor * 0.6);
roughnessFactor *= rough;`)
        .replace('#include <emissivemap_fragment>', `
#include <emissivemap_fragment>
// Subtle warm emissive in the deep fissures — represents glowing
// sublimating material at the crack floors, what gives real
// active comets their faint inner radiance under deep shadow.
// Strength tied to cracks so it only appears in fissures, not the
// whole surface. Drives bloom enough to give the nucleus a sense
// of "being alive" under the coma.
vec3 crackGlow = vec3(1.0, 0.45, 0.25) * 0.18;
totalEmissiveRadiance += crackGlow * crackFactor;`);
    };
    const core = new THREE.Mesh(coreGeometry, coreMaterial);
    core.name = 'cometCore';
    // Slight forward offset so the nucleus sits embedded inside
    // the coma's leading tip rather than at its apex. ~1× the
    // nucleus base radius is enough to bury it inside the visible
    // hemispherical cap of the coma without occluding the head.
    core.position.set(0, 0, coreBaseRadius * 1.0);
    // Random tumble axis + speed per comet so each one rotates
    // distinctively. Real comet rotation periods range 5h-50h+;
    // we just need plausible motion.
    core.userData.tumble = {
      axis: new THREE.Vector3(
        Math.random() * 2 - 1,
        Math.random() * 2 - 1,
        Math.random() * 2 - 1
      ).normalize(),
      speed: 0.12 + Math.random() * 0.20
    };
    group.add(core);
    // Upgrade the procedural placeholder to the real GLB rock once it
    // loads (async; repoints group.userData.cometVisuals.core).
    swapCometNucleusToModel(group, core, coreBaseRadius);

    if (COMET_VOLUME_LOOK) {
      const volume = createCometVolume(comet, palette);
      group.add(volume);
      // An unseen sphere round the head keeps the comet easy to click
      // (the raycast list takes the group's sphere meshes).
      const hitSphere = new THREE.Mesh(
        new THREE.SphereGeometry(comet.size * 4, 16, 12),
        new THREE.MeshBasicMaterial({ visible: false })
      );
      hitSphere.name = 'cometHitSphere';
      group.add(hitSphere);
      finish(comet, palette, group, { core, volume });
      return;
    }

    // Coma envelope. Real comet photos (NEOWISE, Hale-Bopp, McNaught)
    // show a bright fuzzy head that bleeds asymmetrically into the
    // dust tail along the anti-sun direction. We render that as a
    // sphere with the local +Z axis aligned with the tail (the
    // comet's basis quaternion handles that), then taper alpha in
    // the fragment shader: full fresnel on the sun-facing side,
    // exponentially fading toward zero down the tail axis. The
    // geometry stays spherical so the falloff is the only thing
    // creating the teardrop.
    const comaEnvelopeRadius = comet.size * 5.8;
    // Sphere geometry rotated so its poles align with the tail
    // axis (Z). The vertex shader fully OVERRIDES the radial
    // position based on a teardrop profile function — pole vertices
    // use the UV.x as a synthetic azimuth so they fan out into a
    // proper hemispherical leading cap instead of staying collapsed
    // at the pole. Result: actual rounded leading edge, not a flat
    // disc.
    // Higher tessellation (48,32 → 96,64) so any per-vertex
    // displacement or alpha gradient doesn't reveal individual
    // latitude rings as the user just saw with the differential
    // rotation. With ~6000 vertices the smooth-shaded surface
    // doesn't expose mesh structure.
    const comaEnvelopeGeometry = new THREE.SphereGeometry(comaEnvelopeRadius, 96, 64);
    comaEnvelopeGeometry.rotateX(Math.PI / 2);
    const comaEnvelopeMaterial = new THREE.ShaderMaterial({
      uniforms: {
        color: { value: new THREE.Color(palette.coma) },
        opacity: { value: 0.16 },
        comaRadius: { value: comaEnvelopeRadius },
        // Drives how aggressively the back fades. Higher = tighter
        // coma, less wispy trail. Lower = longer atmospheric trail
        // bleeding into the dust tail.
        tailTaper: { value: 1.4 },
        // Teardrop profile parameters. headRadius is the lateral
        // scale at the leading edge (set this > 0 to avoid a
        // pin-prick front). fanLow/fanHigh control the front-rise
        // smoothstep. backFadeStart + tailDecayPow control the tail
        // taper. All live-tweakable from the comet admin panel.
        fanLow: { value: 0.0 },
        fanHigh: { value: 0.35 },
        headRadius: { value: 0.40 },
        backFadeStart: { value: 0.55 },
        tailDecayPow: { value: 1.4 },
        time: { value: 0 },
        // Sun position — used by the Henyey-Greenstein forward-
        // scattering term so the coma brightens dramatically when
        // viewed against the sun (real-photo behaviour). Updated
        // each frame in updateCometVisuals.
        sunPosition: { value: new THREE.Vector3(0, 0, 0) }
      },
      vertexShader: `
        uniform float comaRadius;
        uniform float fanLow;
        uniform float fanHigh;
        uniform float headRadius;
        uniform float backFadeStart;
        uniform float tailDecayPow;
        uniform float time;
        varying vec3 vNormal;
        varying vec3 vViewDir;
        varying vec3 vLocalPos;
        varying vec3 vBasePos;
        varying vec3 vWorldPos;
        varying vec3 vTailAxisW;
        void main() {
          // Three-zone teardrop with rounded leading cap.
          //   Front cap (t ∈ [0, fanHigh]): hemisphere bulge,
          //     radius rises from 0 at the apex via sqrt curve
          //     up to headRadius * comaRadius at the cap edge.
          //   Body (t ∈ [fanHigh, backFadeStart]): smooth ramp
          //     from headRadius up to 1.0 (max width).
          //   Back fade (t > backFadeStart): tapers to 0 via
          //     pow(..., tailDecayPow) for the tail point.
          float zRatio = position.z / comaRadius;
          float t = clamp((zRatio + 1.0) * 0.5, 0.0, 1.0);

          // Pole-vertex azimuth from UV.x. Sphere collapses to a
          // point at its poles (xy=0) so we need a synthetic
          // azimuthal direction to fan them into a hemisphere cap.
          // Non-pole vertices use their original xy direction.
          float origR = length(position.xy);
          float poleAz = uv.x * 6.2832;
          vec2 dir = origR > 0.001 ? position.xy / origR : vec2(cos(poleAz), sin(poleAz));

          // UNIFORM (rigid) rotation around the tail axis. Every
          // vertex rotates by the same angle so no shearing or
          // ring-wireframe artifact like the previous differential
          // rotation. Slow rate — visible drift, not a spin.
          float rotAngle = time * 0.08;
          float cR = cos(rotAngle), sR = sin(rotAngle);
          dir = vec2(dir.x * cR - dir.y * sR, dir.x * sR + dir.y * cR);

          // Three-zone profile.
          //   Cap: r = headRadius * capR_norm (hemispherical curve
          //        from collapsed apex to headRadius at cap exit).
          //   Body: ramp from headRadius up to 1.0 by backFadeStart.
          //   Back: 1.0 → small floor (0.015 to avoid back-pole
          //        triangle-fan artifact).
          float profileR;
          if (t < fanHigh) {
            float capT = fanHigh > 0.001 ? t / fanHigh : 0.0;
            float capR_norm = sqrt(max(1.0 - (1.0 - capT) * (1.0 - capT), 0.0));
            profileR = headRadius * capR_norm;
          } else if (t < backFadeStart) {
            float bodyT = (t - fanHigh) / max(backFadeStart - fanHigh, 0.001);
            profileR = mix(headRadius, 1.0, smoothstep(0.0, 1.0, bodyT));
          } else {
            float backT = smoothstep(backFadeStart, 1.0, t);
            profileR = max(pow(1.0 - backT, tailDecayPow), 0.015);
          }

          // Multi-frequency breathing — overlapping sines give the
          // envelope a visibly undulating motion. Amplitude pushed
          // to ±15% so the motion is obvious at typical viewing
          // distances. Three frequencies overlap so the motion
          // never reads as periodic.
          float az = atan(dir.y, dir.x);
          float breathe1 = sin(time * 0.22 + az * 2.0 + position.z * 0.5);
          float breathe2 = sin(time * 0.14 - az * 3.0 + position.z * 0.3) * 0.7;
          float breathe3 = sin(time * 0.31 + az * 1.0 - position.z * 0.7) * 0.5;
          float breathe = 1.0 + 0.15 * ((breathe1 + breathe2 + breathe3) * 0.45);

          // Anti-sun flow wave — a traveling ridge/trough pattern
          // that visibly moves down the tail axis over time. Real
          // comets show "shock waves" or density variations pulsing
          // anti-sun under solar wind variability; this captures
          // that without exposing mesh structure. Wavelength tied
          // to position.z, frequency tied to time.
          float flowWave = sin(position.z * 0.6 - time * 0.45);
          // Amplitude grows toward the tail (more wave activity in
          // the lower-density outer regions).
          float flowAmp = 0.06 * smoothstep(0.0, 0.6, t);
          breathe *= 1.0 + flowAmp * flowWave;

          float newR = profileR * comaRadius * breathe;
          vec3 conePos = vec3(dir.x * newR, dir.y * newR, position.z);

          vBasePos = position;
          vLocalPos = conePos;
          // World-space tail axis (local +Z). modelMatrix = R * S with
          // axis-aligned scale, so the third column stays parallel to
          // the tail direction; normalize strips the haloLong scale.
          vTailAxisW = normalize(mat3(modelMatrix) * vec3(0.0, 0.0, 1.0));
          vNormal = normalize(normalMatrix * normal);
          vec4 mvPosition = modelViewMatrix * vec4(conePos, 1.0);
          vViewDir = normalize(-mvPosition.xyz);
          vWorldPos = (modelMatrix * vec4(conePos, 1.0)).xyz;
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform vec3 color;
        uniform float opacity;
        uniform float comaRadius;
        uniform float tailTaper;
        uniform float time;
        uniform vec3 sunPosition;
        varying vec3 vNormal;
        varying vec3 vViewDir;
        varying vec3 vLocalPos;
        varying vec3 vBasePos;
        varying vec3 vWorldPos;
        varying vec3 vTailAxisW;

        ${OKLAB_MIX_GLSL}

        float hash21(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
        }
        float noise21(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          float a = hash21(i);
          float b = hash21(i + vec2(1.0, 0.0));
          float c = hash21(i + vec2(0.0, 1.0));
          float d = hash21(i + vec2(1.0, 1.0));
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
        }
        float fbm21(vec2 p) {
          float v = 0.0;
          float a = 0.5;
          for (int i = 0; i < 4; i++) {
            v += a * noise21(p);
            p *= 2.13;
            a *= 0.5;
          }
          return v;
        }

        void main() {
          // Tail-axis position. zRatio: -1 = apex (at nucleus),
          // +1 = far back base.
          float zRatio = vBasePos.z / comaRadius;
          if (zRatio < -1.0) discard;
          float t = (zRatio + 1.0) * 0.5;

          // Axial fade — exponential falloff along the tail, but
          // with a near-transparent zone at the apex so the coma
          // does NOT obscure the nucleus underneath. The nucleus
          // sits embedded slightly into the tip; with the head-fade
          // the surrounding gas reads as a faint envelope rather
          // than a solid veil covering the body.
          float axialFade = exp(-t * tailTaper);
          // headFade: 0 at the apex (t=0), ramps to full opacity by
          // t = ~headFadeEnd. This is the "see-through" zone where
          // the nucleus shows through.
          float headFade = smoothstep(0.0, 0.06, t);
          axialFade *= headFade;

          // Even wider haze fade. The smoothstep range is pushed
          // to (0, 0.75) so 75% of the visible surface is in the
          // fade zone — almost no defined silhouette anywhere.
          // Fresnel near-uniform (0.40 → 1.0) so the surface reads
          // as a uniform soft cloud rather than a fresnel-defined
          // sphere with a peripheral rim.
          float facing = abs(dot(normalize(vNormal), normalize(vViewDir)));
          float edgeSoft = smoothstep(0.0, 0.75, facing);
          float fresnel = mix(0.40, 1.0, edgeSoft);

          // Multi-octave dust turbulence — applied across the
          // whole body now (not just toward the back) so the
          // entire envelope reads as variegated dust cloud, not
          // a smooth shell. This breaks up the silhouette and
          // helps the diffuse look.
          // Time-scrolled dust noise. THREE overlapping octaves at
          // different scales scroll at different speeds and
          // directions — gives the surface visible flowing texture
          // at multiple scales (fine grain → mid-scale eddies →
          // large coherent structures sweeping through).
          //   Micro octave: very fine grain (×22 scale), fast drift.
          //   Mid octave:   sub-cloud features, lateral scroll.
          //   Macro octave: large coherent waves, slow rotation.
          // FIVE overlapping noise layers at different scales +
          // a streak layer anisotropic along the tail axis. Gives
          // the coma visible structure at every scale (sub-pixel
          // grain → particulate clumps → eddies → coherent waves
          // → tail filaments). Each octave animates independently.
          vec2 dustUvGrain  = vec2(vBasePos.x * 48.0 + zRatio * 60.0 + time * 0.45,
                                   vBasePos.y * 48.0 - time * 0.30);
          vec2 dustUvMicro  = vec2(vBasePos.x * 22.0 + zRatio * 30.0 + time * 0.34,
                                   vBasePos.y * 22.0 + cos(time * 0.20) * 1.2);
          vec2 dustUvMid    = vec2(vBasePos.x * 9.0 + zRatio * 14.0 + time * 0.18,
                                   vBasePos.y * 9.0 + sin(time * 0.13) * 0.6);
          vec2 dustUvMacro  = vec2(vBasePos.x * 3.2 + zRatio * 5.0 + time * 0.06,
                                   vBasePos.y * 3.2 + cos(time * 0.08) * 0.8 + time * 0.04);
          // Streaks stretched 3× along the tail axis → filaments
          // visibly flowing anti-sun, not blobs.
          vec2 dustUvStreak = vec2(vBasePos.x * 14.0 + sin(time * 0.10) * 0.4,
                                   zRatio * 4.2 + time * 0.22);
          float dustGrain  = fbm21(dustUvGrain);
          float dustMicro  = fbm21(dustUvMicro);
          float dustMid    = fbm21(dustUvMid);
          float dustMacro  = fbm21(dustUvMacro);
          float dustStreak = fbm21(dustUvStreak);
          float dust = mix(0.25, 1.50, dustMid) *
                       mix(0.55, 1.30, dustMacro) *
                       mix(0.80, 1.18, dustMicro) *
                       mix(0.92, 1.08, dustGrain) *
                       mix(0.78, 1.28, dustStreak);
          // Stronger mask weight (0.90 instead of 0.85) so the
          // noise variation has more visual punch.
          float dustMask = mix(0.78, dust, 0.92);

          // === SOFTENED FORWARD SCATTER ===
          // Same HG phase function as before but with three
          // diffusing changes so the brightness gradient reads
          // organic rather than mathematical:
          //   g 0.60 → 0.42 — softer peak, less sharp focus
          //   multiplier 0.35 → 0.20 — gentler backlit boost
          //   noise modulation — the brightness across the surface
          //     varies with fbm noise so the gradient isn't a
          //     clean radial falloff
          vec3 worldView = normalize(cameraPosition - vWorldPos);
          vec3 sunDir = normalize(sunPosition - vWorldPos);
          float cosTheta = dot(worldView, sunDir);
          float g = 0.42;
          float g2 = g * g;
          float denom = pow(1.0 + g2 - 2.0 * g * cosTheta, 1.5);
          float hgPhase = (1.0 - g2) / max(denom, 0.001);
          // Multiply the HG term by a slow noise field so the
          // forward-scatter brightness has organic spatial
          // variation — patches of "denser dust" forward-scatter
          // more, breaking up the clean gradient.
          float scatterNoise = mix(0.75, 1.25, dustMacro);
          float forwardScatter = 1.0 + hgPhase * 0.20 * scatterNoise;

          // Base alpha lifted from 0.75 to 0.85, but combined with
          // the softer HG term the result feels diffuse rather
          // than spotlit.
          float alpha = fresnel * axialFade * dustMask * opacity * 0.85 * edgeSoft * forwardScatter;

          // === END-ON VEIL SUPPRESSION ===
          // Looking down the tail axis (camera sunward of the comet,
          // or anti-sun looking back past it) the teardrop shell
          // projects to a HUGE near-constant-alpha disc: an ultra-dim
          // grey-green veil with a defined rim and radial pole-fan
          // banding. Additive light that dim doesn't read as a glow —
          // it reads as a dark semi-transparent OCCLUDER swallowing
          // the starfield (the dark D-shaped disc bug). When the view
          // direction aligns with the tail axis, reshape the envelope
          // into a centered fuzzball: a radial gaussian falloff from
          // the tail axis plus a fade-out of the sub-glow veil so only
          // the genuinely bright core survives. Side-on views (where
          // the teardrop silhouette and soft fringes look right) are
          // untouched.
          float axisAlign = abs(dot(worldView, normalize(vTailAxisW)));
          float endOn = smoothstep(0.55, 0.9, axisAlign);
          float rho = length(vLocalPos.xy) / comaRadius;
          float ball = exp(-rho * rho * 3.0);
          float veil = smoothstep(0.02, 0.12, alpha);
          alpha = mix(alpha, alpha * ball * veil, endOn);

          // Soften the discard boundary so the silhouette never shows
          // a hard rim at any view angle — alpha glides to zero
          // instead of popping at the cutoff.
          alpha *= smoothstep(0.0015, 0.012, alpha);
          if (alpha < 0.0015) discard;
          // Warm tint dialed back — full warm shift at strong
          // forward scatter only, and milder (1.10/0.92 instead of
          // 1.15/0.85). The dust still feels golden when backlit
          // but not artificially saturated.
          float tintMix = smoothstep(0.4, 1.0, cosTheta);
          // OKLCh routes the cool-to-warm forward-scatter shift along
          // the perceptual hue arc so the dust tint reads as proper
          // amber/gold when backlit instead of a slightly desaturated
          // off-white at intermediate angles.
          vec3 forwardTint = oklchMix(color, color * vec3(1.10, 1.00, 0.92), tintMix);
          gl_FragColor = vec4(forwardTint, alpha);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      // depthTest stays ON so closer geometry (Earth, Sun, planets)
      // properly occludes the coma when the comet is behind them.
      // Earlier this was false to prevent the coma vanishing when
      // looking through the sun's bloom toward the comet, but that
      // made the coma render on top of EVERYTHING regardless of Z
      // order, which is wrong when the comet is hidden behind Earth.
      depthTest: true,
      side: THREE.DoubleSide
    });
    const comaEnvelope = new THREE.Mesh(comaEnvelopeGeometry, comaEnvelopeMaterial);
    comaEnvelope.name = 'comaEnvelope';
    // High renderOrder keeps the coma drawing after most opaque
    // scene geometry, so additive blending composites correctly
    // against the previously-rendered frame buffer. With depthTest
    // ON, occluders still hide the coma — renderOrder just controls
    // the ORDER of draws, not whether they get z-tested.
    comaEnvelope.renderOrder = 12;
    group.add(comaEnvelope);

    // Optically thin coma - a particle volume avoids the shell/atmosphere
    // look and lets brightness fall away like column-integrated dust.
    // Higher particle count for finer-grained dust. 1800 was
    // sparse — individual particles were visible as discrete dots.
    // 6500 gives a dense visual texture where particles read as a
    // continuous granular cloud rather than countable specks.
    const comaParticleCount = 6500;
    const comaGeometry = new THREE.BufferGeometry();
    const comaPositions = new Float32Array(comaParticleCount * 3);
    const comaSizes = new Float32Array(comaParticleCount);
    const comaAlphas = new Float32Array(comaParticleCount);
    const baseComaPositions = new Float32Array(comaParticleCount * 3);
    const baseComaSizes = new Float32Array(comaParticleCount);
    const baseComaAlphas = new Float32Array(comaParticleCount);

    for (let i = 0; i < comaParticleCount; i++) {
      const u = Math.random() * 2 - 1;
      const theta = Math.random() * Math.PI * 2;
      const shellRadius = Math.pow(Math.random(), 1.45);
      const sideRadius = Math.sqrt(Math.max(0, 1 - u * u));
      const tailBias = Math.max(0, u) * Math.pow(shellRadius, 1.6) * 0.45;
      const x = Math.cos(theta) * sideRadius * shellRadius;
      const y = Math.sin(theta) * sideRadius * shellRadius * 0.82;
      const z = u * shellRadius + tailBias;
      const density = 1 / Math.pow(0.28 + shellRadius * 1.15, 1.35);

      baseComaPositions[i * 3] = x;
      baseComaPositions[i * 3 + 1] = y;
      baseComaPositions[i * 3 + 2] = z;
      // Particle size sweet-spot — small enough to read as fine
      // grain with 6500 of them, big enough to actually be visible
      // at typical viewing distances. 0.35× was too tiny; 0.65×
      // gives proper density without losing the granular look.
      baseComaSizes[i] = (0.35 + Math.random() * 0.58) * comet.size * (1.25 - shellRadius * 0.42) * 0.38;
      // Brighter baseline alpha so the higher particle count
      // reads with proper density — previous ceiling of 0.24
      // made the cloud look washed-out / barely-there.
      baseComaAlphas[i] = THREE.MathUtils.clamp(density * (0.18 + Math.random() * 0.14), 0.04, 0.45);
    }

    comaGeometry.setAttribute('position', new THREE.BufferAttribute(comaPositions, 3));
    comaGeometry.setAttribute('size', new THREE.BufferAttribute(comaSizes, 1));
    comaGeometry.setAttribute('alpha', new THREE.BufferAttribute(comaAlphas, 1));
    const comaMaterial = createCometParticleMaterial(palette.coma, 100, 0.6);
    const comaCloud = new THREE.Points(comaGeometry, comaMaterial);
    comaCloud.name = 'comaCloud';
    comaCloud.frustumCulled = false;
    group.add(comaCloud);

    // Localised jets from active regions, inspired by Rosetta images.
    const jetParticleCount = 520;
    const jetGeometry = new THREE.BufferGeometry();
    const jetPositions = new Float32Array(jetParticleCount * 3);
    const jetSizes = new Float32Array(jetParticleCount);
    const jetAlphas = new Float32Array(jetParticleCount);
    const jetParams = new Float32Array(jetParticleCount * 5);
    const jetSources = [
      new THREE.Vector3(-0.48, 0.18, -0.86).normalize(),
      new THREE.Vector3(0.26, -0.22, -0.94).normalize(),
      new THREE.Vector3(0.12, 0.52, -0.84).normalize(),
      new THREE.Vector3(0.56, 0.08, -0.82).normalize()
    ];

    for (let i = 0; i < jetParticleCount; i++) {
      const t = Math.pow(Math.random(), 0.72);
      jetParams[i * 5] = t;
      jetParams[i * 5 + 1] = (Math.random() - 0.5) * 2;
      jetParams[i * 5 + 2] = (Math.random() - 0.5) * 2;
      jetParams[i * 5 + 3] = Math.random() * Math.PI * 2;
      jetParams[i * 5 + 4] = Math.floor(Math.random() * jetSources.length);
      jetSizes[i] = (1 - t * 0.55) * comet.size * (0.55 + Math.random() * 0.5);
      jetAlphas[i] = 0;
    }

    jetGeometry.setAttribute('position', new THREE.BufferAttribute(jetPositions, 3));
    jetGeometry.setAttribute('size', new THREE.BufferAttribute(jetSizes, 1));
    jetGeometry.setAttribute('alpha', new THREE.BufferAttribute(jetAlphas, 1));
    const jetMaterial = createCometParticleMaterial(palette.coma, 90);
    const jetPlumes = new THREE.Points(jetGeometry, jetMaterial);
    jetPlumes.name = 'comaJets';
    jetPlumes.frustumCulled = false;
    group.add(jetPlumes);

    // Ion tail particle system. Real ion tails are thin, mostly
    // parallel filaments (striations driven by solar wind / magnetic
    // field structure), not a uniform cone of dust. We distribute
    // particles across N discrete filaments, each with its own
    // sinusoidal random-walk along the tail length, then add
    // per-particle thickness and density clumping. The result reads
    // as a layered bundle of streamers rather than a smooth gradient,
    // closer to how ion tails appear on Hale-Bopp / NEOWISE plates.
    const ionParticleCount = 1600;
    const ionGeometry = new THREE.BufferGeometry();
    const ionPositions = new Float32Array(ionParticleCount * 3);
    const ionSizes = new Float32Array(ionParticleCount);
    const ionAlphas = new Float32Array(ionParticleCount);

    const ionFilamentCount = 7;
    for (let i = 0; i < ionParticleCount; i++) {
      const t = Math.random();
      // Pick a filament index; each filament has a fixed lateral
      // offset and a unique phase for its wiggle.
      const fIdx = Math.floor(Math.random() * ionFilamentCount);
      const fPhase = fIdx * 1.787;
      // Filament base offset (lateral position of this streamer).
      // Asymmetric weighting via sin so filaments cluster off-centre
      // rather than evenly distributing around zero, which mimics
      // the bias real ion tails take from the local magnetic field.
      const fBaseX = Math.sin(fPhase) * comet.size * 0.45;
      const fBaseY = Math.cos(fPhase * 1.3) * comet.size * 0.28;
      // Wiggle along the filament length. Two frequencies so the
      // streamer reads as turbulent rather than a clean sine.
      const wiggleX = Math.sin(t * 9.0 + fPhase) * comet.size * 0.18
                    + Math.sin(t * 27.0 + fPhase * 2.1) * comet.size * 0.06;
      const wiggleY = Math.cos(t * 8.3 + fPhase * 1.5) * comet.size * 0.12
                    + Math.cos(t * 24.0 + fPhase * 0.7) * comet.size * 0.04;
      // Thin per-particle scatter inside each filament so the
      // streamer has body, not just a 1D line of points.
      const thickX = (Math.random() - 0.5) * comet.size * 0.10;
      const thickY = (Math.random() - 0.5) * comet.size * 0.08;
      // Density clumping along length. Multi-freq sin product gates
      // particle alpha so the tail has "knots" of brighter material
      // separated by gaps, instead of a uniform fade.
      const clumpA = Math.sin(t * 23.0 + fPhase) * 0.5 + 0.5;
      const clumpB = Math.sin(t * 6.0 + 1.3) * 0.5 + 0.5;
      const clump = clumpA * 0.6 + clumpB * 0.4;

      ionPositions[i * 3]     = fBaseX + wiggleX + thickX;
      ionPositions[i * 3 + 1] = fBaseY + wiggleY + thickY;
      ionPositions[i * 3 + 2] = t * comet.tailLength * 1.5;
      ionSizes[i] = (1 - t * 0.7) * comet.size * (0.4 + Math.random() * 0.6);
      ionAlphas[i] = (1 - t) * 0.85 * (0.25 + clump * 0.75);
    }

    ionGeometry.setAttribute('position', new THREE.BufferAttribute(ionPositions, 3));
    ionGeometry.setAttribute('size', new THREE.BufferAttribute(ionSizes, 1));
    ionGeometry.setAttribute('alpha', new THREE.BufferAttribute(ionAlphas, 1));

    const ionMaterial = new THREE.ShaderMaterial({
      uniforms: {
        color: { value: new THREE.Color(palette.ion) },
        pointTexture: { value: null }
      },
      vertexShader: `
        attribute float size;
        attribute float alpha;
        varying float vAlpha;
        void main() {
          vAlpha = alpha;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * (200.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform vec3 color;
        varying float vAlpha;
        void main() {
          vec2 center = gl_PointCoord - vec2(0.5);
          float dist = length(center);
          float alpha = smoothstep(0.5, 0.0, dist) * vAlpha;
          gl_FragColor = vec4(color, alpha);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });

    const ionTail = new THREE.Points(ionGeometry, ionMaterial);
    ionTail.name = 'ionTail';
    ionTail.frustumCulled = false;
    group.add(ionTail);

    const ionRibbon = createCometTailRibbon(
      'ionTailRibbon',
      palette.ion,
      comet.tailLength * 1.85,
      comet.size * 0.95
    );
    group.add(ionRibbon);

    // Dust tail - particle system (yellowish, straight, wider)
    const dustParticleCount = 1200;
    const dustGeometry = new THREE.BufferGeometry();
    const dustPositions = new Float32Array(dustParticleCount * 3);
    const dustSizes = new Float32Array(dustParticleCount);
    const dustAlphas = new Float32Array(dustParticleCount);

    // Dust tail distribution. Real dust tails are NOT straight, they
    // curve along the orbital path. Lighter dust grains experience
    // proportionally more radiation-pressure deceleration than
    // heavier ones, so dust emitted earlier in the orbit drifts to
    // one side of the anti-sun axis, producing the characteristic
    // sweeping arc Hale-Bopp / NEOWISE / McNaught all show. We curve
    // the tail in +X (orbital tangent direction in local frame) with
    // a power-law that grows with t. Also adds density puffs along
    // the length and a wider fan spread than the ion tail to match
    // dust's broader scattering character.
    for (let i = 0; i < dustParticleCount; i++) {
      const t = Math.random();
      // Curvature: heavier curve toward the end, lighter near the
      // nucleus. pow(t, 0.55) gives a sweeping arc that starts gentle
      // and accelerates outward, matching observed dust-tail geometry.
      const orbitalCurve = Math.pow(t, 0.55) * comet.size * 2.4;
      // Fan spread widens with distance, the tail flares as dust
      // disperses. Asymmetric so the +X side (curve-side) gets more
      // spread than the -X side, which exaggerates the curved arc.
      const fanWidth = (0.25 + t * 1.6) * comet.size;
      const fanX = (Math.random() - 0.35) * fanWidth * 2.4;
      const fanY = (Math.random() - 0.5) * fanWidth * 1.1;
      // Density puffs along the tail length. Multi-freq sin product
      // creates discrete "knots" of denser material, not a smooth
      // gradient. Real dust tails show this clearly because gravity
      // tides + outgassing fluctuations cluster the material.
      const puffA = Math.sin(t * 4.5) * 0.5 + 0.5;
      const puffB = Math.sin(t * 17.0 + 1.7) * 0.4 + 0.6;
      const puff = puffA * 0.6 + puffB * 0.4;
      // Random off-axis drift for some particles, simulating the
      // "ragged edge" effect where outlying grains escape the main
      // tail body and drift independently.
      const drift = (Math.random() < 0.12 ? 1.0 : 0.0) * (Math.random() - 0.5) * comet.size * 1.5;

      dustPositions[i * 3]     = orbitalCurve + fanX + drift;
      dustPositions[i * 3 + 1] = fanY + drift * 0.4;
      dustPositions[i * 3 + 2] = t * comet.tailLength;
      dustSizes[i] = (1 - t * 0.5) * comet.size * (0.7 + Math.random() * 0.7);
      dustAlphas[i] = (1 - t) * 0.55 * (0.3 + puff * 0.7);
    }

    dustGeometry.setAttribute('position', new THREE.BufferAttribute(dustPositions, 3));
    dustGeometry.setAttribute('size', new THREE.BufferAttribute(dustSizes, 1));
    dustGeometry.setAttribute('alpha', new THREE.BufferAttribute(dustAlphas, 1));

    const dustMaterial = new THREE.ShaderMaterial({
      uniforms: {
        color: { value: new THREE.Color(palette.dust) }
      },
      vertexShader: `
        attribute float size;
        attribute float alpha;
        varying float vAlpha;
        void main() {
          vAlpha = alpha;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * (200.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform vec3 color;
        varying float vAlpha;
        void main() {
          vec2 center = gl_PointCoord - vec2(0.5);
          float dist = length(center);
          float alpha = smoothstep(0.5, 0.0, dist) * vAlpha;
          gl_FragColor = vec4(color, alpha);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });

    const dustTail = new THREE.Points(dustGeometry, dustMaterial);
    dustTail.name = 'dustTail';
    dustTail.frustumCulled = false;
    group.add(dustTail);

    const dustRibbon = createCometTailRibbon(
      'dustTailRibbon',
      palette.dust,
      comet.tailLength * 1.22,
      comet.size * 2.9
    );
    group.add(dustRibbon);

    finish(comet, palette, group, {
      core,
      comaEnvelope,
      comaCloud,
      jetPlumes,
      comaGeometry,
      jetGeometry,
      comaMaterial,
      jetMaterial,
      comaPositions,
      comaSizes,
      comaAlphas,
      baseComaPositions,
      baseComaSizes,
      baseComaAlphas,
      jetPositions,
      jetAlphas,
      jetParams,
      jetSources,
      ionTail,
      dustTail,
      ionRibbon,
      dustRibbon,
      ionGeometry,
      dustGeometry,
      ionMaterial,
      dustMaterial,
      ionPositions,
      dustPositions,
      ionAlphas,
      dustAlphas,
      baseIonPositions: ionPositions.slice(),
      baseDustPositions: dustPositions.slice(),
      ionMaxZ: comet.tailLength * 1.5,
      dustMaxZ: comet.tailLength
    });
  });
  markClickablesDirty();
}

// Data for raycasting and the info panel, the starting position, and the
// comet's place in the scene. Shared by both looks.
function finishComet(comet, palette, group, cometVisuals) {
  group.userData = {
    name: comet.name,
    type: comet.type,
    description: comet.description,
    radius: comet.size,
    realSize: comet.realSize,
    realDiameter: comet.realSize,
    realDistance: 'Variable',
    orbitalPeriod: comet.orbitalPeriod || 'Variable',
    moons: 0,
    colors: [palette.nucleus, palette.coma],
    glowColor: palette.ion,
    isComet: true,
    isPlanet: true, // So it can be clicked and hovered
    perihelion: comet.perihelion,
    aphelion: comet.aphelion,
    orbitalSpeed: comet.orbitalSpeed,
    inclination: comet.inclination,
    orbitAngle: comet.startAngle || Math.random() * Math.PI * 2,
    cometVisuals,
    isDeepSpaceBody: true
  };

  const orbitState = computeCometOrbitPosition(comet, group.userData.orbitAngle);
  group.position.copy(orbitState.position);

  scene.add(group);
  cometObjects.push({ mesh: group, data: comet });
  createCometOrbitLine(comet, group);
  updateCometVisuals(group, comet);
  group.visible = deepSpaceBodiesVisible;
}

// The interstellar visitors, 1I/ʻOumuamua and 3I/ATLAS, on JPL Horizons'
// paths (public/data/interstellar-paths.json, src/orbits/visitors.js) from
// 1800 to 2200: at true scale at their true distances, in artistic scale at
// artisticDistanceForAu of them, as the dwarf planets are. Like the probes,
// at true scale each is drawn as many times bigger than it is as the Earth's
// satellites are (scaled with the Earth as drawn). ʻOumuamua's shape is an
// artistic rendition of NASA's description (up to 400 m long, about ten times
// as long as it is wide, reddish); 3I/ATLAS is a comet of the particle look
// (createComets), its activity set by its true distance from the Sun against
// its perihelion and its coma and tails worked out only while some of them
// are in view and a few pixels across (cometWorthUpdating). Their paths are
// drawn while the orbits and deep space are shown, forty years either side
// of perihelion.
const INTERSTELLAR_PATHS_URL = '/data/interstellar-paths.json';
let interstellarTracks = null;
let interstellarTracksRequested = false;
const interstellarEntries = [];
function loadInterstellarTracks() {
  if (interstellarTracksRequested) return;
  interstellarTracksRequested = true;
  fetch(INTERSTELLAR_PATHS_URL)
    .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`interstellar paths ${res.status}`))))
    .then((data) => { interstellarTracks = data.objects || null; })
    .catch((err) => {
      console.warn('Interstellar paths failed to load:', err);
      setTimeout(() => { interstellarTracksRequested = false; loadInterstellarTracks(); }, 15000);
    });
}

function interstellarUserData(data, extra = {}) {
  return {
    name: data.name,
    bodyId: data.bodyId,
    type: data.type,
    description: data.description,
    designation: data.designation,
    radius: data.size,
    realSize: data.realSize,
    realDiameter: data.realSize,
    realDistance: 'Variable',
    authoredSize: { radius: data.size, selectionRadius: data.size * 4 },
    selectionRadius: data.size * 4,
    isInterstellar: true,
    isPlanet: true, // So it can be clicked and hovered
    isDeepSpaceBody: true,
    ...extra
  };
}

// 3I/ATLAS comes out of createComets with its coma and tails; this takes the
// place of finishComet for it.
function finishInterstellarComet(comet, palette, group, cometVisuals) {
  group.userData = interstellarUserData(comet, {
    isComet: true,
    colors: [palette.nucleus, palette.coma],
    glowColor: palette.ion,
    perihelion: comet.perihelion,
    cometVisuals
  });
  setDrawnSizeScale(group, drawnSizeFactor());
  group.visible = false;
  scene.add(group);
  interstellarEntries.push({ mesh: group, data: comet, comet: true });
}

// ʻOumuamua as NASA describes it: a lumpy body about ten times as long as it
// is wide (createAsteroidGeometry drawn out along X), reddish and dark.
function createOumuamua(data) {
  const group = new THREE.Group();
  group.name = data.name;
  const geometry = createAsteroidGeometry(data.size, 3, 11);
  geometry.scale(1, 1 / data.elongation, 1 / data.elongation);
  geometry.computeVertexNormals();
  const body = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({ color: data.color, roughness: 1, metalness: 0 }));
  body.name = `${data.name} body`;
  group.add(body);
  const hit = new THREE.Mesh(
    new THREE.SphereGeometry(data.size * 4, 12, 8),
    new THREE.MeshBasicMaterial({ visible: false })
  );
  hit.name = `${data.name} selection volume`;
  group.add(hit);
  group.userData = interstellarUserData(data, { rotationHours: data.rotationHours, spinBody: body });
  setDrawnSizeScale(group, drawnSizeFactor());
  group.visible = false;
  scene.add(group);
  interstellarEntries.push({ mesh: group, data, comet: false });
}

function createInterstellarObjects() {
  loadInterstellarTracks();
  for (const data of interstellarObjects) {
    if (data.isComet) createComets([data], finishInterstellarComet);
    else createOumuamua(data);
  }
  createInterstellarPaths();
  markClickablesDirty();
}

const _visitorHelio = [0, 0, 0];
// An interstellar object's scene position at jd into out, or null outside its
// path; its distance from the Sun in AU is left in _visitorHelio's length.
function visitorScenePosition(track, jd, out) {
  const h = visitorHelio(track, jd, _visitorHelio);
  if (!h) return null;
  const r = Math.hypot(h[0], h[1], h[2]);
  const k = realisticScaleMode ? AU_TO_SCENE : artisticDistanceForAu(r) * scaleMultiplier / r;
  return out.set(h[0] * k, h[2] * k, -h[1] * k);
}

// Whether a comet's coma and tails are worth working out this frame: some of
// them in view and at least a couple of pixels across (they are about 11,000
// points moved on the CPU).
const _cometSphere = new THREE.Sphere();
const _cometFrustum = new THREE.Frustum();
const _cometViewProjection = new THREE.Matrix4();
function cometWorthUpdating(group, reach) {
  if (!camera) return false;
  group.getWorldPosition(_cometSphere.center);
  _cometSphere.radius = reach;
  const d = Math.max(camera.position.distanceTo(_cometSphere.center), 1e-9);
  const px = (reach / d) * (window.innerHeight / 2) / Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
  if (px < 2) return false;
  camera.updateMatrixWorld();
  _cometViewProjection.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
  _cometFrustum.setFromProjectionMatrix(_cometViewProjection);
  return _cometFrustum.intersectsSphere(_cometSphere);
}

// 3I/ATLAS's coma and tails follow the sunlight it gets. They are drawn in
// full from INTERSTELLAR_COMA_FULL_AU in, its distance from the Sun on 21
// July 2025 (Horizons), when Hubble imaged the dust coming off it (NASA), and
// fade out by twice that distance, where the sunlight is a quarter as strong
// (the fade is our choice; the particle look's own base glow, kept so the
// periodic comets always read, would draw a tail on it at 13 AU that no
// telescope saw).
const INTERSTELLAR_COMA_FULL_AU = 3.85;
function interstellarComaShare(au) {
  return THREE.MathUtils.clamp(((INTERSTELLAR_COMA_FULL_AU / au) ** 2 - 0.25) / 0.75, 0, 1);
}
const _comaHelio = [0, 0, 0];
// Whether a comet is drawn with its coma and tails, which a focus frames
// whole: always for the periodic comets; for 3I/ATLAS by its distance from
// the Sun on the sim clock (as last placed until its path has loaded), and
// with none it is framed on its nucleus.
function cometComaShown(object) {
  const data = object.userData;
  if (!data.isInterstellar) return true;
  const track = interstellarTracks && interstellarTracks[data.bodyId];
  const h = track && visitorHelio(track, simulationJD, _comaHelio);
  return h ? interstellarComaShare(Math.hypot(h[0], h[1], h[2])) > 0 : data.comaShown !== false;
}
const COMET_TAIL_PARTS = ['comaEnvelope', 'comaCloud', 'jetPlumes', 'ionTail', 'dustTail', 'ionRibbon', 'dustRibbon'];
function setCometTailsShown(visuals, shown) {
  for (const key of COMET_TAIL_PARTS) if (visuals[key]) visuals[key].visible = shown;
}
// Scales what updateCometVisuals has just written (it writes every alpha
// and opacity afresh, so nothing compounds).
function fadeCometVisuals(visuals, share) {
  for (const key of ['comaAlphas', 'jetAlphas', 'ionAlphas', 'dustAlphas']) {
    const alphas = visuals[key];
    if (alphas) for (let i = 0; i < alphas.length; i++) alphas[i] *= share;
  }
  const materials = [visuals.comaMaterial, visuals.jetMaterial, visuals.comaEnvelope && visuals.comaEnvelope.material,
    visuals.ionRibbon && visuals.ionRibbon.material, visuals.dustRibbon && visuals.dustRibbon.material];
  for (const material of materials) {
    if (material && material.uniforms && material.uniforms.opacity) material.uniforms.opacity.value *= share;
  }
}

const _visitorPos = new THREE.Vector3();
const _visitorNext = new THREE.Vector3();
const _visitorPrev = new THREE.Vector3();
const _visitorTangent = new THREE.Vector3();
const _visitorCarry = new THREE.Vector3();
const _visitorCometState = { r: 0, position: null, tangent: null };
// Each frame, once deep space has been built: each visitor to where it is on
// the sim clock. Until the paths load it waits hidden at the Earth, as the
// probes do.
function placeInterstellarObjects(scaledDelta, simStepJD) {
  if (!interstellarEntries.length) return;
  const size = drawnSizeFactor();
  for (const entry of interstellarEntries) {
    const { mesh, data } = entry;
    const track = interstellarTracks && interstellarTracks[data.bodyId];
    const pos = track ? visitorScenePosition(track, simulationJD, _visitorPos) : null;
    const onPath = Boolean(pos);
    if (mesh.userData.onPath !== onPath) {
      mesh.userData.onPath = onPath;
      mesh.visible = deepSpaceBodiesVisible && onPath;
      markClickablesDirty();
    }
    setDrawnSizeScale(mesh, size);
    if (!pos) {
      mesh.userData.waiting = true;
      if (planets.earth) planets.earth.getWorldPosition(mesh.position);
      continue;
    }
    const au = Math.hypot(_visitorHelio[0], _visitorHelio[1], _visitorHelio[2]);
    if (mesh.userData.waiting && selectedObject === mesh && !isAnimating && camera && controls) {
      _visitorCarry.copy(pos).sub(mesh.position);
      camera.position.add(_visitorCarry);
      controls.target.add(_visitorCarry);
      if (_focusPrevObj === mesh) _focusPrevObjPos.add(_visitorCarry);
    }
    mesh.userData.waiting = false;
    mesh.position.copy(pos);
    // A focus framed before the paths had loaded is framed again once it has
    // landed, for what the visitor is now (3I/ATLAS far out has no tail to
    // take in).
    if (mesh.userData.framedBlind && !isAnimating) {
      mesh.userData.framedBlind = false;
      if (selectedObject === mesh && camera && controls) {
        camera.position.sub(controls.target).setLength(getFocusDistance(mesh)).add(controls.target);
      }
    }
    const centi = Math.round(au * 100);
    if (mesh.userData.distanceCenti !== centi) {
      mesh.userData.distanceCenti = centi;
      mesh.userData.realDistance = formatProbeDistance(au);
    }
    if (!mesh.visible) continue;
    if (entry.comet) {
      const share = interstellarComaShare(au);
      const shown = share > 0;
      if (mesh.userData.comaShown !== shown) {
        mesh.userData.comaShown = shown;
        setCometTailsShown(mesh.userData.cometVisuals, shown);
      }
      // The tails stream away from the Sun and lag along the motion, taken
      // from where it is half a day either side.
      if (!shown || !cometWorthUpdating(mesh, data.tailLength * 2 * size)) continue;
      visitorScenePosition(track, simulationJD + 0.5, _visitorNext);
      visitorScenePosition(track, simulationJD - 0.5, _visitorPrev);
      _visitorTangent.copy(_visitorNext).sub(_visitorPrev);
      if (_visitorTangent.lengthSq() < 1e-20) _visitorTangent.set(0, 1, 0);
      _visitorCometState.r = au;
      _visitorCometState.position = mesh.position;
      _visitorCometState.tangent = _visitorTangent.normalize();
      updateCometVisuals(mesh, data, _visitorCometState);
      if (share < 1) fadeCometVisuals(mesh.userData.cometVisuals, share);
    } else {
      // Tumbling (NASA's 7.3 hours; display rates in artistic scale, the
      // true period at true scale, bodySpinStep).
      const body = mesh.userData.spinBody;
      body.rotation.z = wrapAngle(body.rotation.z + bodySpinStep(0.01, scaledDelta, simStepJD, data.rotationHours));
      body.rotation.x = wrapAngle(body.rotation.x + bodySpinStep(0.003, scaledDelta, simStepJD, data.rotationHours * 3.1));
    }
  }
  updateInterstellarPaths();
}

// Each visitor's path, forty years either side of perihelion, its samples
// crowded round perihelion (the time from it goes as the cube of an even
// step). It is laid out relative to where the object was when it was worked
// out, for float precision at hundreds of AU, and worked out again when the
// scale changes or the object has gone a year along it.
const INTERSTELLAR_PATH_YEARS = 40;
const INTERSTELLAR_PATH_POINTS = 480;
const INTERSTELLAR_PATH_REBUILD_DAYS = 365;
const interstellarPaths = [];
function createInterstellarPaths() {
  for (const entry of interstellarEntries) {
    const material = new LineMaterial({
      color: new THREE.Color(entry.comet ? 0x9fd0ff : 0xe0a080),
      linewidth: 1.4,
      transparent: true,
      opacity: 0.35,
      resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
      worldUnits: false,
      depthWrite: false
    });
    const line = new Line2(new LineGeometry(), material);
    line.name = `${entry.data.name} path`;
    line.renderOrder = -2;
    line.frustumCulled = false;
    line.visible = false;
    scene.add(line);
    interstellarPaths.push({ entry, line, builtScale: null, builtJd: NaN });
  }
}

const _visitorPathPoint = new THREE.Vector3();
function updateInterstellarPaths() {
  if (!interstellarTracks) return;
  const scale = realisticScaleMode ? -1 : scaleMultiplier;
  for (const path of interstellarPaths) {
    const { entry, line } = path;
    const track = interstellarTracks[entry.data.bodyId];
    const show = Boolean(track && track.perihelion) && orbitsVisible && entry.mesh.visible;
    if (line.visible !== show) line.visible = show;
    if (!show) continue;
    if (path.builtScale === scale && Math.abs(simulationJD - path.builtJd) < INTERSTELLAR_PATH_REBUILD_DAYS) continue;
    path.builtScale = scale;
    path.builtJd = simulationJD;
    const anchor = line.position.copy(entry.mesh.position);
    const peri = track.perihelion.jd;
    const span = INTERSTELLAR_PATH_YEARS * 365.25;
    const half = INTERSTELLAR_PATH_POINTS / 2;
    const positions = [];
    for (let i = -half; i <= half; i++) {
      const u = i / half;
      if (!visitorScenePosition(track, peri + span * u * u * u, _visitorPathPoint)) continue;
      _visitorPathPoint.sub(anchor);
      positions.push(_visitorPathPoint.x, _visitorPathPoint.y, _visitorPathPoint.z);
    }
    line.geometry.dispose();
    const geometry = new LineGeometry();
    geometry.setPositions(positions);
    line.geometry = geometry;
  }
}

// Create near-Earth asteroids
function createNEOs() {
  initSmallBodyModels();
  nearEarthObjects.forEach(neo => {
    // A smooth stand-in with the body's proportions until its shape model
    // arrives (src/smallbodies/); a jittered lump for any NEO without one.
    const model = smallBodyModel(neo.name);
    let geometry;
    let material;
    if (model) {
      geometry = smallBodyPlaceholderGeometry(model, neo.size);
      material = smallBodyPlaceholderMaterial(model);
    } else {
      geometry = createAsteroidGeometry(neo.size, 2, neo.name.length);
      material = new THREE.MeshStandardMaterial({ color: neo.color, roughness: 1, metalness: 0 });
    }

    const asteroid = new THREE.Mesh(geometry, material);
    asteroid.name = neo.name;

    asteroid.userData = {
      name: neo.name,
      type: neo.type,
      description: neo.description,
      realSize: neo.realSize,
      radius: neo.size,
      isNEO: true,
      isPlanet: true, // So it can be clicked
      orbitRadius: neo.orbitRadius,
      eccentricity: neo.eccentricity || 0,
      orbitalSpeed: neo.orbitalSpeed,
      inclination: neo.inclination || 0,
      orbitAngle: Math.random() * Math.PI * 2,
      isDeepSpaceBody: true
    };

    scene.add(asteroid);
    neoObjects.push({ mesh: asteroid, data: neo });
    asteroid.visible = deepSpaceBodiesVisible;
    if (model) registerSmallBody(asteroid, { radius: neo.size });
  });
  markClickablesDirty();
}

// The small bodies' models swap into these meshes once they are close
// enough to see (src/smallbodies/index.js); the click cache is rebuilt then.
function initSmallBodyModels() {
  initSmallBodies({
    mobile: RUNNING_ON_MOBILE,
    renderer,
    sunPosition: (v) => (sun ? sun.getWorldPosition(v) : v.set(0, 0, 0)),
    onSwap: () => markClickablesDirty(),
  });
}

// A small body on its real pole, turning at its real period (bodySpinStep:
// display rates in artistic mode, the true period at true scale); Apophis
// tumbles, precessing and rotating at its two periods.
function turnSmallBody(object, model, scaledDelta, simStepJD) {
  const ud = object.userData;
  if (!ud.smallBodySpin) ud.smallBodySpin = [Math.random() * FULL_TURN, Math.random() * FULL_TURN];
  const spin = ud.smallBodySpin;
  if (model.tumble) {
    const { precessionHours, rotationHours } = model.tumble;
    spin[0] = wrapAngle(spin[0] + bodySpinStep(14.4 / precessionHours, scaledDelta, simStepJD, precessionHours));
    spin[1] = wrapAngle(spin[1] + bodySpinStep(14.4 / rotationHours, scaledDelta, simStepJD, rotationHours));
  } else {
    spin[0] = wrapAngle(spin[0] + bodySpinStep(14.4 / model.periodHours, scaledDelta, simStepJD, model.periodHours));
  }
  orientSmallBody(object, model, spin);
}

// Create additional dwarf planets
// A dwarf planet's true radius in km: Haumea's volume-equivalent one from
// its axes, otherwise half its diameter (for one only known within a range,
// the range's geometric middle).
function dwarfPlanetRadiusKm(dwarf) {
  if (dwarf.axesKm) return Math.cbrt(dwarf.axesKm[0] * dwarf.axesKm[1] * dwarf.axesKm[2]);
  const sizes = (dwarf.realDiameter || '').replace(/,/g, '').match(/\d+(\.\d+)?/g);
  if (!sizes) return null;
  const lo = Number(sizes[0]);
  const hi = Number(sizes[1] || sizes[0]);
  return Math.sqrt(lo * hi) / 2;
}

// A dwarf planet's place from its elements at the sim clock: at true scale
// its true distance and size, at artistic scale its distance on the ladder
// the probes are drawn by (artisticDistanceForAu), so each keeps its true
// order among Pluto and the probes (Eris, near aphelion at 95 AU in 2026,
// beyond Pluto and short of the Voyagers).
const _dwarfEcl = [0, 0, 0];
function placeDwarfPlanet(group) {
  const el = group.userData.elements;
  if (!el) return;
  const [x, y, z] = elementsPosition(el, simulationJD, ASTEROID_ELEMENTS_EPOCH_JD, _dwarfEcl);
  const r = Math.hypot(x, y, z);
  const k = realisticScaleMode ? AU_TO_SCENE : artisticDistanceForAu(r) * scaleMultiplier / r;
  group.position.set(x * k, z * k, -y * k);
  const real = realisticScaleMode ? getRealisticBodyRadius(group.userData.name.toLowerCase()) : null;
  group.scale.setScalar(real ? real / group.userData.radius : 1);
}

function createDwarfPlanets() {
  dwarfPlanets.forEach(dwarf => {
    if (dwarf.name === 'Ceres') return;
    const group = new THREE.Group();
    group.name = dwarf.name;
    const key = dwarf.name.toLowerCase();
    const km = dwarfPlanetRadiusKm(dwarf);
    if (km && REAL_RADII_KM[key] == null) REAL_RADII_KM[key] = km;

    // Dwarf planet body
    const geometry = new THREE.SphereGeometry(dwarf.radius, graphicsConfig.dwarfSegments, graphicsConfig.dwarfSegments);
    if (dwarf.axesKm) {
      // A triaxial body (Haumea): dwarf.radius is its volume-equivalent
      // radius, the long axis lies on x (the map's u = 0 and 0.5), the
      // middle one on z and the shortest, its spin axis, on y.
      const [a, b, c] = dwarf.axesKm;
      const mean = Math.cbrt(a * b * c);
      geometry.scale(a / mean, c / mean, b / mean);
    }

    let material;
    if (dwarf.texture) {
      // A map of its own (the dwarf planets' are artistic renditions), lit
      // like Charon's; the plain colour stands in until it loads.
      material = createSurfaceMaterial(dwarf);
      loadTextureByName(dwarf.texture).then((texture) => {
        if (!texture) return;
        const plain = mesh.material;
        mesh.material = createSurfaceMaterial(dwarf, { texture, color: 0xffffff });
        plain.dispose();
      });
    } else {
      material = new THREE.MeshStandardMaterial({
        color: dwarf.colors[0],
        roughness: 0.8,
        metalness: 0.1
      });
    }

    const mesh = new THREE.Mesh(geometry, material);
    group.add(mesh);

    // Add subtle glow, except round a mapped surface, where the shell read as
    // a tinted ring round the limb.
    if (!dwarf.texture) {
      const glowGeometry = new THREE.SphereGeometry(dwarf.radius * 1.1, 16, 16);
      const glowMaterial = new THREE.MeshBasicMaterial({
        color: dwarf.colors[1],
        transparent: true,
        opacity: 0.15
      });
      const glow = new THREE.Mesh(glowGeometry, glowMaterial);
      group.add(glow);
    }

    group.userData = {
      name: dwarf.name,
      type: dwarf.type,
      description: dwarf.description,
      radius: dwarf.radius,
      realDiameter: dwarf.realDiameter,
      orbitalPeriod: dwarf.orbitalPeriod || 'Unknown',
      isDwarfPlanet: true,
      isPlanet: true, // So it can be clicked
      elements: dwarf.elements,
      rotationSpeed: dwarf.rotationSpeed,
      isDeepSpaceBody: true
    };
    placeDwarfPlanet(group);

    scene.add(group);
    // Store in planets object for consistency
    planets[dwarf.name.toLowerCase()] = group;
    group.visible = deepSpaceBodiesVisible;
  });
  markClickablesDirty();
}

// 3D UFO ship for fly mode
let playerShip = null;
let shipCubeCamera = null;
let shipCubeRenderTarget = null;
// One face of the ship's reflection cube, as CubeCamera.update draws each of
// its six (three r160): the face's camera, XR off, the cube's mips rebuilt
// after the face so the envMap's chain stays whole.
function renderShipCubeFace(face) {
  const cube = shipCubeCamera;
  if (cube.parent === null) cube.updateMatrixWorld();
  if (cube.coordinateSystem !== renderer.coordinateSystem) {
    cube.coordinateSystem = renderer.coordinateSystem;
    cube.updateCoordinateSystem();
  }
  const target = renderer.getRenderTarget();
  const activeFace = renderer.getActiveCubeFace();
  const activeMip = renderer.getActiveMipmapLevel();
  const xrEnabled = renderer.xr.enabled;
  renderer.xr.enabled = false;
  renderer.setRenderTarget(cube.renderTarget, face, cube.activeMipmapLevel);
  renderer.render(scene, cube.children[face]);
  renderer.setRenderTarget(target, activeFace, activeMip);
  renderer.xr.enabled = xrEnabled;
}
// The reflection looks every way from the ship, so its first draw on
// entering fly mode drew every world in the system and uploaded all their
// maps in one frame (about 4 s of 8K uploads, the page frozen). The hull's
// curve shrinks the reflection three or four cube texels to a screen pixel,
// so a world under two texels in radius is under a pixel there (the sky's
// planet points still show it): it sits the reflection out, and its moons,
// in its group, with it.
const _shipCubeHidden = [];
const _shipCubeProbe = new THREE.Vector3();
function hideSubTexelBodiesFromShipCube() {
  // The deep-sky photos are faint patches a few degrees across, lost on the
  // steel, and drawn all round they were thirty more uploads.
  const dso = deepSkyObjects && deepSkyObjects.group;
  if (dso && dso.visible) {
    dso.visible = false;
    _shipCubeHidden.push(dso);
  }
  const twoTexels = Math.PI / shipCubeRenderTarget.width;
  forEachFlightBody((body) => {
    if (!body || body === sun || !body.visible) return;
    const dist = body.getWorldPosition(_shipCubeProbe).distanceTo(shipCubeCamera.position);
    if (getBodyBaseRadius(body) < dist * twoTexels) {
      body.visible = false;
      _shipCubeHidden.push(body);
    }
  });
}
function restoreShipCubeBodies() {
  for (const body of _shipCubeHidden) body.visible = true;
  _shipCubeHidden.length = 0;
}
let craftFillLight = null;
let craftFillLightTarget = null;

// Layer used to isolate the player-craft fill light to the player
// craft alone. DirectionalLight without a layer scope leaks across
// the whole scene; that's how a fill light meant only for the ship
// was ending up softly illuminating the dark hemisphere of nearby
// planets during fly-mode autopilot approaches. Pinning the fill
// light to this layer and enabling the same layer on the craft (and
// its children) restricts the bleed to the ship — see
// enableShipFillLayer below.
const SHIP_FILL_LAYER = 1;

/**
 * Mark a craft root (and every descendant) as eligible for the
 * SHIP_FILL_LAYER, so the directional fill light only affects this
 * ship and not the planets around it. Idempotent — calling twice is
 * fine, layers.enable just sets a bit.
 */
function enableShipFillLayer(root) {
  if (!root) return;
  root.traverse((node) => {
    if (node && node.layers && typeof node.layers.enable === 'function') {
      node.layers.enable(SHIP_FILL_LAYER);
    }
  });
}

function createCraftFillLight() {
  craftFillLight = new THREE.DirectionalLight(0xd8e6ff, DEFAULT_CRAFT_FLIGHT_PROFILE.fillLightIntensity || 1);
  craftFillLight.visible = false;
  // Scope the fill to the ship-only layer so it can't bleed onto
  // nearby planet dark sides. The user reported "sometimes the dark
  // side isn't dark" when autopiloting up to planets in fly mode —
  // that was this light, unrestricted, lifting the night hemisphere
  // of the body the craft was approaching. layers.set replaces the
  // mask entirely so the light no longer matches any layer-0 object.
  craftFillLight.layers.set(SHIP_FILL_LAYER);
  craftFillLightTarget = new THREE.Object3D();
  craftFillLightTarget.visible = false;
  craftFillLight.target = craftFillLightTarget;
  scene.add(craftFillLight);
  scene.add(craftFillLightTarget);
}

// Scratch vectors reused per frame so the shadow-factor computation
// doesn't allocate. Kept here next to the function that consumes them.
const _shadowFactorSunPos = new THREE.Vector3();
const _shadowFactorBodyPos = new THREE.Vector3();
const _shadowFactorToShip = new THREE.Vector3();
const _shadowFactorToSun = new THREE.Vector3();

// Returns 0 in full sunlight, ramping to 1 when the ship is on the
// anti-sun hemisphere of (and close to) a planet. Drives the
// directional fill-light dim so the ship's non-emissive hull goes dark
// on the night side of a body — matching real probe imagery where a
// shadowed spacecraft only reads via its own running lights / windows.
function computeShipShadowFactor() {
  if (!sun || !playerShip) return 0;
  sun.getWorldPosition(_shadowFactorSunPos);
  const shipPos = playerShip.position;

  let nearestBody = null;
  let nearestDist = Infinity;
  for (const name in planets) {
    const p = planets[name];
    if (!p) continue;
    p.getWorldPosition(_shadowFactorBodyPos);
    const d = shipPos.distanceTo(_shadowFactorBodyPos);
    if (d < nearestDist) {
      nearestDist = d;
      nearestBody = p;
    }
  }
  if (!nearestBody) return 0;

  const radius = (nearestBody.userData && nearestBody.userData.radius) || 1;
  // Proximity falloff: full effect within 2 radii, gone by 8 radii.
  // Beyond that, the ship reads as "in interplanetary space" and the
  // fill light stays at its hero value regardless of sun direction.
  const proximityFalloff = 1 - THREE.MathUtils.smoothstep(nearestDist, radius * 2, radius * 8);
  if (proximityFalloff <= 0) return 0;

  nearestBody.getWorldPosition(_shadowFactorBodyPos);
  _shadowFactorToShip.copy(shipPos).sub(_shadowFactorBodyPos).normalize();
  _shadowFactorToSun.copy(_shadowFactorSunPos).sub(_shadowFactorBodyPos).normalize();
  // dot = 1 on the sun-facing pole, -1 at the anti-sun pole.
  const dot = _shadowFactorToShip.dot(_shadowFactorToSun);
  // Soft hemisphere ramp: 0 on the terminator, 1 deep into night.
  const nightHemisphere = THREE.MathUtils.smoothstep(-dot, -0.2, 0.6);
  return nightHemisphere * proximityFalloff;
}

// Apparent-size lock for crafts flagged with useApparentSizeLock in
// userData. Lets the saucer (and any future craft) live at TRUE world
// scale (e.g. 737-sized at 1.55e-5 scene units) while still rendering
// at a readable pixel size on screen. Each frame:
//   1. Compute the on-screen projected size of the ship at its base
//      scale, given camera distance + FOV + viewport height.
//   2. If smaller than minApparentPixels, scale up so the projected
//      size equals minApparentPixels.
//   3. Otherwise leave the scale at base (so when the user pulls in
//      very close, the ship can grow naturally past the minimum).
// The planet doesn't get this treatment, so when flying close to a
// planet the saucer reads as a tiny dot against a huge body — which
// is exactly the "feel the scale" experience the user wants.
function enforceShipMinApparentSize() {
  if (!playerShip || !camera || !flyMode) return;
  if (!playerShip.userData || !playerShip.userData.useApparentSizeLock) return;
  const baseScale = (playerShip.userData.baseScale || 1) * atmoCraftScaleLive;
  const baseDiameter = playerShip.userData.baseDiameter || 1;
  const minPixels = playerShip.userData.minApparentPixels || 20;

  const dist = camera.position.distanceTo(playerShip.position);
  if (dist < 1e-6) {
    playerShip.scale.setScalar(baseScale);
    return;
  }
  const viewportHeight = (renderer && renderer.domElement) ? renderer.domElement.clientHeight : 1080;
  const fovRad = THREE.MathUtils.degToRad(camera.fov || 50);
  // Apparent diameter in pixels at base scale.
  const worldDiameter = baseDiameter * baseScale;
  const apparentPixels = (worldDiameter / dist) * (viewportHeight / (2 * Math.tan(fovRad / 2)));

  let renderScale = baseScale;
  if (apparentPixels < minPixels) {
    renderScale = baseScale * (minPixels / Math.max(apparentPixels, 1e-9));
  }
  playerShip.scale.setScalar(renderScale);
}

// Scratch vectors for the per-frame fill-light positioning so the
// hot path doesn't allocate.
const _fillSunPos = new THREE.Vector3();
const _fillShipPos = new THREE.Vector3();
const _fillSunDir = new THREE.Vector3();

function updateCraftFillLight() {
  if (!craftFillLight || !craftFillLightTarget || !camera || !playerShip) return;
  const profile = getActiveCraftProfile();
  const enabled = !!(flyMode && profile.fillLight);
  craftFillLight.visible = enabled;
  craftFillLightTarget.visible = enabled;
  if (!enabled) return;

  // Dim the directional fill in planet shadow. 0.15 floor keeps the
  // ship readable as a silhouette (so it doesn't completely disappear
  // when the user has eased onto the night side), but the bulk of the
  // hull goes dark as expected for a craft in the body's shadow cone.
  // The ship's emissive windows / warp coils / running lights are
  // unaffected since they don't read this directional light at all.
  const shadow = computeShipShadowFactor();
  const baseIntensity = profile.fillLightIntensity || 1;
  craftFillLight.intensity = baseIntensity * (1 - shadow * 0.85);

  // Anchor the fill light direction to the SUN, not the camera.
  // Previously this set position = camera.position which made the
  // ship's lit hemisphere rotate to track whichever angle the
  // user was panning from — the dark side never stayed dark when
  // the camera orbited the craft. Photographic-style fix: derive a
  // direction that's 60° off the sun direction in the world XZ plane
  // with a slight upward lift, and place the light far along that
  // direction from the ship. The lit side of the hull now stays
  // consistent regardless of camera pan, matching the sun-relative
  // shading model the planets already use.
  playerShip.getWorldPosition(_fillShipPos);
  if (sun) {
    sun.getWorldPosition(_fillSunPos);
    _fillSunDir.subVectors(_fillSunPos, _fillShipPos);
    if (_fillSunDir.lengthSq() < 1e-6) {
      _fillSunDir.set(1, 0.3, 0); // degenerate-case fallback
    }
    _fillSunDir.normalize();
  } else {
    _fillSunDir.set(1, 0.3, 0);
  }
  // Rotate the sun direction 60° around world Y for the off-axis
  // fill angle. Photography convention: key (sun) at 0°, fill at
  // ~45-60° off-key, dialled so the shadow side gets a soft lift
  // without flattening the terminator.
  const offAxis = Math.PI / 3;
  const dc = Math.cos(offAxis);
  const ds = Math.sin(offAxis);
  const fillDirX = _fillSunDir.x * dc - _fillSunDir.z * ds;
  const fillDirZ = _fillSunDir.x * ds + _fillSunDir.z * dc;
  // Lift the fill above the orbital plane by mixing in world up.
  // Pure-equatorial fill produces a strong shadow under the ship
  // that reads as "fluorescent-tube lit from the side"; a 30%
  // up-tilt softens that into something closer to ambient bounce.
  const fillDirY = _fillSunDir.y * 0.7 + 0.3;
  const fillLen = Math.sqrt(fillDirX * fillDirX + fillDirY * fillDirY + fillDirZ * fillDirZ);
  // Place the light far in the fill direction so DirectionalLight's
  // direction = (target - position) reads as nearly-parallel rays
  // toward the ship.
  const fillDistance = 100;
  craftFillLight.position.set(
    _fillShipPos.x + (fillDirX / fillLen) * fillDistance,
    _fillShipPos.y + (fillDirY / fillLen) * fillDistance,
    _fillShipPos.z + (fillDirZ / fillLen) * fillDistance
  );
  craftFillLightTarget.position.copy(_fillShipPos);
}

function updateUfoStarReflectionTexture() {
  if (!ufoStarReflectionMaterial || !ufoStarReflectionMaterial.uniforms) return;
  const u = ufoStarReflectionMaterial.uniforms;
  if (u.starMap) {
    u.starMap.value = wormholeStarMapTexture || getWormholeStarMapFallbackTexture();
  }
  const sky = starfieldMaterial && starfieldMaterial.uniforms;
  if (u.skyEncoded && sky && wormholeStarMapTexture) {
    u.skyEncoded.value = 1;
    u.skyL0.value = sky.skyL0.value;
    u.skyLogMax.value = sky.skyLogMax.value;
    u.skyDisplay.value = sky.skyRadianceScale.value * SKY_EXPOSURE * SKY_MILKY_WAY_GAIN;
  }
}

function getUfoReflectColorForTarget(target) {
  const data = target && target.data ? target.data : {};
  const colorValue = data.glowColor || (data.colors && data.colors[0]) || 0x9ed8ff;
  try {
    ufoPlanetReflectColor.set(colorValue);
  } catch (err) {
    ufoPlanetReflectColor.set(0x9ed8ff);
  }
  const type = String(data.type || '').toLowerCase();
  const key = String(target && target.key || data.name || '').toLowerCase();
  if (type.includes('gas') || type.includes('ice') || key === 'jupiter' || key === 'saturn') {
    ufoPlanetReflectColor.multiplyScalar(1.22);
  } else if (key === 'earth') {
    ufoPlanetReflectColor.lerp(new THREE.Color(0x6ad8ff), 0.38).multiplyScalar(1.12);
  } else {
    ufoPlanetReflectColor.multiplyScalar(1.08);
  }
  return ufoPlanetReflectColor;
}

function updateUfoPlanetReflection(delta) {
  if (!ufoStarReflectionMaterial || !ufoStarReflectionMaterial.uniforms) return;
  const uniforms = ufoStarReflectionMaterial.uniforms;
  const target = flyMode ? alienScanCurrentTarget : null;
  let targetStrength = 0;
  let targetSize = 0.04;

  if (target && target.object) {
    target.object.getWorldPosition(ufoPlanetReflectBodyPos);
    if (playerShip) {
      playerShip.getWorldPosition(ufoPlanetReflectShipPos);
    } else {
      ufoPlanetReflectShipPos.copy(camera.position);
    }

    const distance = ufoPlanetReflectShipPos.distanceTo(ufoPlanetReflectBodyPos);
    const radius = getScanRadiusForBody(target.data);
    ufoPlanetReflectDir.copy(ufoPlanetReflectBodyPos).sub(ufoPlanetReflectShipPos).normalize();
    targetSize = THREE.MathUtils.clamp((radius / Math.max(distance, 0.001)) * 2.65, 0.035, 0.42);
    targetStrength = THREE.MathUtils.clamp(target.strength * (0.34 + targetSize * 2.2), 0, 1);

    if (uniforms.planetReflectDir) uniforms.planetReflectDir.value.copy(ufoPlanetReflectDir);
    if (uniforms.planetReflectColor) uniforms.planetReflectColor.value.copy(getUfoReflectColorForTarget(target));
    if (uniforms.planetReflectSize) uniforms.planetReflectSize.value = targetSize;
  }

  const alpha = dampingFactor(delta, target ? 10 : 14);
  ufoPlanetReflectStrength += (targetStrength - ufoPlanetReflectStrength) * alpha;
  if (ufoPlanetReflectStrength < 0.002) ufoPlanetReflectStrength = 0;
  if (uniforms.planetReflectStrength) uniforms.planetReflectStrength.value = ufoPlanetReflectStrength;
}

function createUfoStarReflectionMaterial() {
  const material = new THREE.ShaderMaterial({
    uniforms: {
      starMap: { value: wormholeStarMapTexture || getWormholeStarMapFallbackTexture() },
      intensity: { value: graphicsConfig.cinematic ? 0.78 : 0.62 },
      planetReflectDir: { value: new THREE.Vector3(0, 0, -1) },
      planetReflectColor: { value: new THREE.Color(0x9ed8ff) },
      planetReflectStrength: { value: 0 },
      planetReflectSize: { value: 0.04 },
      time: { value: 0 },
      // The sky map's decoding (createStarfield), once it has loaded: the map
      // is log-encoded linear light, the fallback plain colour.
      skyEncoded: { value: 0 },
      skyL0: { value: 0 },
      skyLogMax: { value: 1 },
      skyDisplay: { value: 1 },
      ...skyMapUniforms
    },
    vertexShader: `
      varying vec3 vWorldPosition;
      varying vec3 vWorldNormal;
      varying vec3 vObjectNormal;

      void main() {
        vObjectNormal = normalize(position);
        vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
        vWorldNormal = normalize(mat3(modelMatrix) * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D starMap;
      uniform float intensity;
      uniform vec3 planetReflectDir;
      uniform vec3 planetReflectColor;
      uniform float planetReflectStrength;
      uniform float planetReflectSize;
      uniform float time;

      varying vec3 vWorldPosition;
      varying vec3 vWorldNormal;
      varying vec3 vObjectNormal;

      ${OKLAB_MIX_GLSL}

      const float PI = 3.141592653589793;

      ${SKY_MAP_GLSL}
      // The sky map as the skybox hangs it (src/sky/frame.js).
      vec2 equirectUv(vec3 direction) {
        return skyMapUv(direction);
      }

      uniform float skyEncoded;
      uniform float skyL0;
      uniform float skyLogMax;
      uniform float skyDisplay;
      vec3 skyRead(vec2 uv) {
        vec3 s = texture2D(starMap, uv).rgb;
        return skyEncoded > 0.5 ? skyL0 * (exp2(s * skyLogMax) - 1.0) * skyDisplay : s;
      }

      vec3 starSample(vec3 direction, float spread) {
        vec2 uv = equirectUv(direction);
        vec3 s0 = skyRead(uv);
        vec3 s1 = skyRead(uv + vec2(spread, 0.0));
        vec3 s2 = skyRead(uv - vec2(spread * 0.73, spread * 0.41));
        vec3 s3 = skyRead(uv + vec2(-spread * 0.31, spread * 0.86));
        return max(max(s0, s1), max(s2, s3));
      }

      void main() {
        vec3 normal = normalize(vWorldNormal);
        vec3 viewRay = normalize(vWorldPosition - cameraPosition);
        vec3 cameraRay = normalize(cameraPosition - vWorldPosition);
        vec3 objectNormal = normalize(vObjectNormal);

        vec3 tangent = normalize(cross(abs(normal.y) > 0.92 ? vec3(1.0, 0.0, 0.0) : vec3(0.0, 1.0, 0.0), normal));
        vec3 bitangent = normalize(cross(normal, tangent));
        float brush = sin(objectNormal.y * 58.0 + objectNormal.x * 11.0 + time * 0.018) * 0.012;
        vec3 brushedNormal = normalize(normal + tangent * brush + bitangent * brush * 0.45);
        vec3 reflected = reflect(viewRay, brushedNormal);

        float facing = clamp(dot(normal, cameraRay), 0.0, 1.0);
        float fresnel = pow(1.0 - facing, 1.7);
        float surfaceMask = smoothstep(0.07, 0.62, fresnel);
        vec3 stars = starSample(reflected, mix(0.0012, 0.0045, fresnel));
        float lum = dot(stars, vec3(0.2126, 0.7152, 0.0722));
        float pointMask = smoothstep(0.08, 0.55, lum);
        float polarFade = 1.0 - smoothstep(0.86, 1.0, abs(objectNormal.y));
        float edgeRibbon = 1.0 - smoothstep(0.018, 0.16, abs(objectNormal.y));
        float brightPoints = smoothstep(0.42, 0.92, lum);
        float starGlints = brightPoints * pow(fresnel, 2.7) * (0.55 + edgeRibbon * 0.85);
        float sweep = pow(max(dot(reflect(normalize(vec3(-0.32, 0.84, 0.43)), normal), cameraRay), 0.0), 56.0);
        float planetAlign = dot(normalize(reflected), normalize(planetReflectDir));
        float planetCore = smoothstep(1.0 - planetReflectSize, 1.0, planetAlign);
        float planetHalo = smoothstep(1.0 - planetReflectSize * 3.4, 1.0, planetAlign) * 0.46;
        float planetEdgeSheen = pow(max(planetAlign, 0.0), 12.0) * pow(fresnel, 1.4);
        float planetReflection = (planetCore * 0.95 + planetHalo + planetEdgeSheen * 0.28) *
          planetReflectStrength * surfaceMask * polarFade;

        // OKLCh extrapolation (t = 1.36) past the star color pushes
        // saturation along the perceptual hue arc rather than the
        // RGB straight line, giving star reflections a cleaner
        // spectral tint instead of an over-bright washed result.
        vec3 color = oklchMix(vec3(lum), stars, 1.36) * pointMask * surfaceMask * polarFade * intensity;
        color += vec3(0.74, 0.88, 1.0) * starGlints * surfaceMask * polarFade * intensity;
        color += vec3(0.95, 0.98, 1.0) * sweep * edgeRibbon * polarFade * intensity * 0.44;
        color += planetReflectColor * planetReflection * (0.72 + fresnel * 0.42);
        float alpha = (pointMask * (0.035 + fresnel * 0.33) + starGlints * 0.18 + sweep * 0.16) *
          surfaceMask * polarFade * intensity;
        alpha += planetReflection * 0.2;
        if (alpha < 0.004) discard;

        gl_FragColor = vec4(color, alpha);
      }
    `,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
    side: THREE.FrontSide,
    toneMapped: false
  });

  return material;
}

function createPlayerShip() {
  // Create UFO saucer profile using LatheGeometry
  // Profile: classic flat disc shape
  const points = [];

  // Create smooth saucer profile - flat disc, no dome
  // Bottom center
  points.push(new THREE.Vector2(0, -0.03));
  // Bottom flat
  points.push(new THREE.Vector2(0.35, -0.03));
  // Bottom curve to edge
  points.push(new THREE.Vector2(0.45, -0.02));
  points.push(new THREE.Vector2(0.5, 0));
  // Top curve from edge
  points.push(new THREE.Vector2(0.45, 0.02));
  points.push(new THREE.Vector2(0.35, 0.03));
  // Top flat to center
  points.push(new THREE.Vector2(0, 0.03));

  const geometry = new THREE.LatheGeometry(points, 128); // More segments for smooth circle

  // Create CubeCamera for dynamic reflections of nearby planets and
  // starfield. Higher quality presets get sharper steel reflections;
  // weak devices keep the cheaper cube target.
  const reflectionSize = (graphicsConfig.anisotropy || 0) >= 8 ? 512 : 256;
  shipCubeRenderTarget = new THREE.WebGLCubeRenderTarget(reflectionSize, {
    format: THREE.RGBAFormat,
    generateMipmaps: true,
    minFilter: THREE.LinearMipmapLinearFilter
  });
  // The sky sphere is deliberately huge, so the reflection camera needs
  // a matching far plane or the polished hull reflects black space
  // instead of the actual star panorama.
  shipCubeCamera = new THREE.CubeCamera(0.1, 50000000, shipCubeRenderTarget);

  // Smooth polished stainless steel: bright, highly metallic and
  // reflective, but not a perfect mirror. The brushed-steel anisotropy +
  // roughness map were removed — on the saucer's radial lathe topology
  // the anisotropic highlight streaked outward from the centre, reading
  // as the faceted "vinyl record" sunburst lines. A uniform low roughness
  // + clearcoat gives a clean smooth-metal finish instead.
  const material = new THREE.MeshPhysicalMaterial({
    color: 0xe2e1dc,
    metalness: 1.0,
    roughness: 0.14,
    envMap: shipCubeRenderTarget.texture,
    envMapIntensity: 1.85,
    clearcoat: 1.0,
    clearcoatRoughness: 0.045,
    reflectivity: 0.95,
    specularIntensity: 0.94,
    specularColor: new THREE.Color(0xffffff),
    sheen: 0.1,
    sheenRoughness: 0.18,
    sheenColor: new THREE.Color(0xc6d0d8)
  });
  material.toneMapped = true;

  playerShip = new THREE.Mesh(geometry, material);
  // True 737-scale saucer. Earth is 5 scene units across = 12,742 km,
  // so 1 scene unit = ~2548 km. A Boeing 737 is 39.5 m = 1.55e-5
  // scene units. At that geometric scale the saucer is sub-pixel at
  // any normal chase distance, so we use an "apparent-size lock"
  // (enforceShipMinApparentSize, called every frame) that keeps the
  // ship at a readable minimum on-screen pixel size. The geometry
  // stays 737-accurate; the render-scale gets boosted per frame so
  // it never drops below MIN_APPARENT_PIXELS. Planets render at
  // their true scale, so the saucer reads as a tiny dot against the
  // huge planet exactly as the user requested.
  const SAUCER_BASE_SCALE = 1.55e-5; // 737-length scale
  playerShip.scale.set(SAUCER_BASE_SCALE, SAUCER_BASE_SCALE, SAUCER_BASE_SCALE);
  playerShip.rotation.x = Math.PI * 0.15; // Slight tilt forward
  playerShip.userData = playerShip.userData || {};
  playerShip.userData.baseScale = SAUCER_BASE_SCALE;
  playerShip.userData.baseDiameter = 1.0; // LatheGeometry max radius 0.5, diameter 1.0
  // ~220 px wide on a 1080-tall viewport = ~20% of screen height,
  // which reads as a proper "chase view, ship is the hero of the
  // shot" framing. Planets behind it still render at true scale, so
  // a close-up of Earth still fills the rest of the frame around it.
  playerShip.userData.minApparentPixels = 220;
  playerShip.userData.useApparentSizeLock = true;
  playerShip.visible = false;

  // A polished rolled edge gives the saucer something crisp to catch
  // glancing star/sun reflections, which reads more like stainless
  // steel than a flat grey disc.
  const rimGeometry = new THREE.TorusGeometry(0.49, 0.012, 18, 192);
  rimGeometry.rotateX(Math.PI / 2);
  const rim = new THREE.Mesh(rimGeometry, material);
  rim.name = 'ufoPolishedRim';
  playerShip.add(rim);

  ufoStarReflectionMaterial = createUfoStarReflectionMaterial();
  const starReflectionOverlay = new THREE.Mesh(geometry, ufoStarReflectionMaterial);
  starReflectionOverlay.name = 'ufoStarReflectionOverlay';
  starReflectionOverlay.scale.setScalar(1.006);
  starReflectionOverlay.renderOrder = 3;
  playerShip.add(starReflectionOverlay);

  // Add directly to scene (not camera) so we can orbit around it
  scene.add(playerShip);
  scene.add(shipCubeCamera);
  createCraftFillLight();
  // Enable the ship-only fill light layer on the saucer + its rim /
  // reflection overlay children. Pairs with craftFillLight.layers.set
  // inside createCraftFillLight so the fill only hits the ship, not
  // any planet that happens to share a hemisphere with it.
  enableShipFillLayer(playerShip);

  // Track the saucer reference separately so the craft switcher can
  // restore it without re-running its expensive cube-camera + brushed-
  // steel material setup.
  saucerShipRef = playerShip;
  loadedCraft.saucer = {
    key: 'saucer',
    root: playerShip,
    tick: () => {},
    dispose: () => {}
  };
  activeCraftKey = 'saucer';
  document.body.dataset.craft = 'saucer';
  // Seed the propulsion dropdown with the saucer's options at boot so
  // the user opens fly mode on Anti-gravity rather than the stale
  // generic Ion default.
  refreshPropulsionSelectForCraft('saucer');
}

// Craft switcher: lets fly-mode pilots pick between the default
// stainless saucer, an "abnormal sphere" raymarched-style displaced
// mesh, and the Enterprise NCC-1701-D GLB. Heavy assets (sphere
// shader module, Enterprise GLB) only fetch on first pick.
let saucerShipRef = null;
let activeCraftKey = 'saucer';
const loadedCraft = {};
let craftSwitchInFlight = false;

async function setActiveCraft(key) {
  if (craftSwitchInFlight) return;
  if (key === activeCraftKey) return;
  if (!playerShip) return; // Saucer not initialised yet.
  craftSwitchInFlight = true;
  try {
    let craft = loadedCraft[key];
    if (!craft) {
      if (key === 'sphere') {
        const m = await import('./ships/sphere.js');
        craft = m.createSphereCraft();
      } else if (key === 'enterprise') {
        try {
          const m = await import('./ships/enterprise.js');
          craft = await m.createEnterpriseCraft();
        } catch (err) {
          console.error('[craft] Enterprise load failed; falling back to saucer:', err);
          // Bail cleanly so the picker doesn't stay locked — user can
          // pick a different craft or retry.
          return;
        }
      } else if (key === 'ranger') {
        try {
          const m = await import('./ships/ranger.js');
          craft = await m.createRangerCraft();
        } catch (err) {
          console.error('[craft] Ranger load failed; falling back to saucer:', err);
          return;
        }
      } else if (key === 'enterprise-refit') {
        try {
          const m = await import('./ships/enterprise-refit.js');
          craft = await m.createEnterpriseRefitCraft();
        } catch (err) {
          console.error('[craft] Enterprise-refit load failed; falling back to saucer:', err);
          return;
        }
      } else if (key === 'hatak') {
        try {
          const m = await import('./ships/hatak.js');
          craft = await m.createHatakCraft();
        } catch (err) {
          console.error('[craft] Ha\'tak load failed; falling back to saucer:', err);
          return;
        }
      } else if (key === 'bc303') {
        try {
          const m = await import('./ships/bc303.js');
          craft = await m.createBc303Craft();
        } catch (err) {
          console.error('[craft] BC-303 load failed; falling back to saucer:', err);
          return;
        }
      } else if (key === 'bc304') {
        try {
          const m = await import('./ships/bc304.js');
          craft = await m.createBc304Craft();
        } catch (err) {
          console.error('[craft] BC-304 load failed; falling back to saucer:', err);
          return;
        }
      } else if (key === 'shuttle') {
        try {
          const m = await import('./ships/spaceShuttle.js');
          craft = await m.createSpaceShuttleCraft();
        } catch (err) {
          console.error('[craft] Space Shuttle load failed; falling back to saucer:', err);
          return;
        }
      } else if (key === 'warbird') {
        try {
          const m = await import('./ships/warbird.js');
          craft = await m.createWarbirdCraft();
        } catch (err) {
          console.error('[craft] D\'deridex Warbird load failed; falling back to saucer:', err);
          return;
        }
      } else if (key === 'enterprise-b') {
        try {
          const m = await import('./ships/enterprise-b.js');
          craft = await m.createEnterpriseBCraft();
        } catch (err) {
          console.error('[craft] Enterprise-B load failed; falling back to saucer:', err);
          return;
        }
      } else if (key === 'skennenrahawi') {
        try {
          const m = await import('./ships/skennenrahawi.js');
          craft = await m.createSkennenrahawiCraft();
        } catch (err) {
          console.error('[craft] Skennenrahawi load failed; falling back to saucer:', err);
          return;
        }
      } else if (key === 'alien-saucer') {
        try {
          const m = await import('./ships/alienSaucer.js');
          craft = await m.createAlienSaucerCraft();
        } catch (err) {
          console.error('[craft] Alien Saucer load failed; falling back to saucer:', err);
          return;
        }
      } else if (key === 'mando-n1') {
        try {
          const m = await import('./ships/mandoN1.js');
          craft = await m.createMandoN1Craft();
        } catch (err) {
          console.error('[craft] Mando N-1 load failed; falling back to saucer:', err);
          return;
        }
      } else if (key === 'naboo') {
        try {
          const m = await import('./ships/naboo.js');
          craft = await m.createNabooCraft();
        } catch (err) {
          console.error('[craft] Naboo Starfighter load failed; falling back to saucer:', err);
          return;
        }
      } else if (key === 'xwing') {
        try {
          const m = await import('./ships/xwing.js');
          craft = await m.createXwingCraft();
        } catch (err) {
          console.error('[craft] X-wing load failed; falling back to saucer:', err);
          return;
        }
      } else if (key === 'alkesh') {
        try {
          const m = await import('./ships/alkesh.js');
          craft = await m.createAlkeshCraft();
        } catch (err) {
          console.error('[craft] Alkesh load failed; falling back to saucer:', err);
          return;
        }
      } else if (key === 'death-glider') {
        try {
          const m = await import('./ships/deathGlider.js');
          craft = await m.createDeathGliderCraft();
        } catch (err) {
          console.error('[craft] Death Glider load failed; falling back to saucer:', err);
          return;
        }
      } else if (key === 'saucer') {
        craft = loadedCraft.saucer;
      } else {
        return;
      }
      loadedCraft[key] = craft;
      // Re-apply any persisted vessel-configurator tweaks so the user's
      // saved orientation / scale / camera-offset survives reloads.
      // Without this the dial-in would reset to the ship module's
      // initial values every page load, which made orienting new
      // glTFs a paste-and-reload chore.
      applyPersistedCraftTuner(key, craft);
      if (key !== 'saucer') {
        scene.add(craft.root);
        // Match the saucer: tag this craft (and every descendant) for
        // SHIP_FILL_LAYER so the directional fill light only affects
        // the ship and stops bleeding onto nearby planet dark sides.
        enableShipFillLayer(craft.root);
        // Pre-compile shaders for the new craft before it goes
        // visible. Without this, the first frame the GPU sees a new
        // PBR variant it hitches for tens-to-hundreds of ms compiling
        // — which on a heavy ship like the Enterprise looks like the
        // whole page froze. compileAsync uses
        // KHR_parallel_shader_compile when available so it doesn't
        // block the main thread.
        if (renderer) {
          try {
            await compileForSceneAsync(renderer, craft.root, camera, scene);
          } catch (err) {
            console.warn('[craft] precompile failed:', err);
          }
        }
      }
    }

    // Hide the outgoing craft, swap the global reference, show the new
    // one. Keeping all crafts in the scene (just toggling .visible) is
    // cheaper than add/remove on every switch and avoids GC churn.
    if (loadedCraft[activeCraftKey]) {
      loadedCraft[activeCraftKey].root.visible = false;
    }
    activeCraftKey = key;
    playerShip = craft.root;
    playerShip.visible = !!flyMode;
    resetCraftOrbitCamera();
    syncPlayerShipToCamera();
    // Body data-attribute is the CSS hook for per-craft HUD theming
    // (e.g. body[data-craft="enterprise"] swaps the alien glyph
    // overlay for an LCARS-style Starfleet readout).
    document.body.dataset.craft = key;
    // Re-populate the propulsion dropdown with this craft's in-character
    // options. Enterprise gets Impulse + Warp; Saucer gets Anti-grav +
    // Warp; Ranger gets Chemical + Fusion + Impulse; etc.
    refreshPropulsionSelectForCraft(key);
  } finally {
    craftSwitchInFlight = false;
  }
}

// === Craft admin tuner =====================================================
// Localhost-only floating panel that lets the dev dial in orientation,
// scale, follow offset and chase aliveness for any craft and copy the
// resulting values back into source. Hidden for everyone else.

// Comet manipulation overrides — localhost admin panel mutates these
// in real time; updateCometVisuals reads them per frame. Each field
// defaults to the current hardcoded value so behaviour is unchanged
// when the admin panel is closed / overrides.enabled is false.
const cometAdminOverrides = {
  enabled: false,
  // Defaults baked in from the admin-panel tuning session. The
  // hemisphere cap occupies most of the mesh (fanHigh=0.95) so the
  // coma reads as a long, rounded teardrop with a quick tip taper.
  fanLow: 0.2,
  fanHigh: 0.95,
  headRadius: 0.4,
  backFadeStart: 0.95,
  tailDecayPow: 4,
  // Envelope size — bumped substantially per request. perpBase
  // 1.6 → 2.5 (wider envelope), longBase 8 → 14 (much longer).
  haloPerpBase: 2.5,
  haloPerpGain: 1.0,
  haloLongBase: 14,
  haloLongGain: 7,
  tailTaperBase: 5,
  tailTaperReduce: 3,
  comaOpacityBase: 0.42,
  comaOpacityGain: 0.30,
  comaCloudOpacityBase: 0.82,
  comaCloudOpacityGain: 0.28,
  jetOpacityBase: 0.55,
  jetOpacityGain: 0.65,
  ionLengthBase: 0.85,
  ionLengthGain: 0.95,
  ionWidthBase: 1.53,
  ionWidthGain: 1.5,
  ionOpacityBase: 1.0,
  ionOpacityGain: 0.75,
  dustLengthBase: 2.7,
  dustLengthGain: 3,
  dustWidthBase: 2.49,
  dustWidthGain: 2,
  dustOpacityBase: 0,
  dustOpacityGain: 0.24
};

const CRAFT_ADMIN_DEFAULTS = {
  saucer:              { rxDeg: 0, ryDeg: 0,   rzDeg: 0, scale: 1, ox: 0, oy: -0.012, oz: -0.08,  chaseLag: 999,  bobAmplitude: 0,      swayAmplitude: 0     },
  sphere:              { rxDeg: 0, ryDeg: 0,   rzDeg: 0, scale: 1, ox: 0, oy: -0.24,  oz: -2.4,   chaseLag: 11,   bobAmplitude: 0.012,  swayAmplitude: 0.04  },
  ranger:              { rxDeg: 0, ryDeg: 180, rzDeg: 0, scale: 1, ox: 0, oy: -0.024, oz: -0.13,  chaseLag: 15,   bobAmplitude: 0.0015, swayAmplitude: 0.012 },
  'enterprise-refit':  { rxDeg: 0, ryDeg: 180, rzDeg: 0, scale: 1, ox: 0, oy: -0.04,  oz: -0.18,  chaseLag: 16,   bobAmplitude: 0.0012, swayAmplitude: 0.011 },
  enterprise:          { rxDeg: 0, ryDeg: 180, rzDeg: 0, scale: 1, ox: 0, oy: -0.07,  oz: -0.26,  chaseLag: 17.3, bobAmplitude: 0.001,  swayAmplitude: 0.01  },
  hatak:               { rxDeg: 0, ryDeg: 180, rzDeg: 0, scale: 1, ox: 0, oy: -0.06,  oz: -0.28,  chaseLag: 16,   bobAmplitude: 0.0012, swayAmplitude: 0.011 },
  bc303:               { rxDeg: 0, ryDeg: 180, rzDeg: 0, scale: 1, ox: 0, oy: -0.04,  oz: -0.20,  chaseLag: 15.5, bobAmplitude: 0.0014, swayAmplitude: 0.012 },
  bc304:               { rxDeg: 0, ryDeg: 90,  rzDeg: 0, scale: 1, ox: 0, oy: -0.05,  oz: -0.23,  chaseLag: 15.8, bobAmplitude: 0.0013, swayAmplitude: 0.012 },
  shuttle:             { rxDeg: 0, ryDeg: 270, rzDeg: 0, scale: 1, ox: 0, oy: -0.022, oz: -0.11,  chaseLag: 14,   bobAmplitude: 0.0018, swayAmplitude: 0.014 },
  warbird:             { rxDeg: 0, ryDeg: 180, rzDeg: 0, scale: 1, ox: 0, oy: -0.07,  oz: -0.32,  chaseLag: 17,   bobAmplitude: 0.0011, swayAmplitude: 0.010 },
  'enterprise-b':      { rxDeg: 0, ryDeg: 180, rzDeg: 0, scale: 1, ox: 0, oy: -0.05,  oz: -0.22,  chaseLag: 16.5, bobAmplitude: 0.0013, swayAmplitude: 0.011 },
  skennenrahawi:       { rxDeg: 0, ryDeg: 180, rzDeg: 0, scale: 1, ox: 0, oy: -0.058, oz: -0.25,  chaseLag: 16.8, bobAmplitude: 0.0012, swayAmplitude: 0.011 },
  'alien-saucer':      { rxDeg: 0, ryDeg: 180, rzDeg: 0, scale: 1, ox: 0, oy: -0.025, oz: -0.13,  chaseLag: 14.5, bobAmplitude: 0.0020, swayAmplitude: 0.016 },
  'mando-n1':          { rxDeg: 0, ryDeg: 180, rzDeg: 0, scale: 1, ox: 0, oy: -0.018, oz: -0.10,  chaseLag: 13,   bobAmplitude: 0.0014, swayAmplitude: 0.012 },
  naboo:               { rxDeg: 0, ryDeg: 180, rzDeg: 0, scale: 1, ox: 0, oy: -0.018, oz: -0.10,  chaseLag: 13,   bobAmplitude: 0.0014, swayAmplitude: 0.012 },
  xwing:               { rxDeg: 0, ryDeg: 180, rzDeg: 0, scale: 1, ox: 0, oy: -0.020, oz: -0.11,  chaseLag: 13.5, bobAmplitude: 0.0016, swayAmplitude: 0.013 },
  alkesh:              { rxDeg: 0, ryDeg: 180, rzDeg: 0, scale: 1, ox: 0, oy: -0.032, oz: -0.17,  chaseLag: 15,   bobAmplitude: 0.0014, swayAmplitude: 0.012 },
  'death-glider':      { rxDeg: 0, ryDeg: 180, rzDeg: 0, scale: 1, ox: 0, oy: -0.020, oz: -0.11,  chaseLag: 13.5, bobAmplitude: 0.0016, swayAmplitude: 0.013 }
};

// Persisted-state helpers. The vessel configurator saves every
// slider change per-craft to localStorage so dial-ins survive
// reloads — that's what makes the configurator practical for
// orienting new ship glTFs without paste-and-reload churn. Once
// the values feel right, "Copy values" emits paste-ready code so
// they can be locked into the ship module source.
const CRAFT_TUNER_STORAGE_PREFIX = 'craftTuner.';
function loadPersistedCraftTuner(key) {
  try {
    const raw = localStorage.getItem(CRAFT_TUNER_STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
}
function persistCraftTuner(key, state) {
  try {
    localStorage.setItem(CRAFT_TUNER_STORAGE_PREFIX + key, JSON.stringify(state));
  } catch (e) {
    // localStorage can throw in private-mode safari etc. Non-fatal.
  }
}

/**
 * Apply any persisted vessel-configurator state to a freshly-loaded
 * craft. Called from setActiveCraft so dial-ins survive reloads.
 * No-op when no persisted state exists or when the craft module
 * doesn't expose applyOrientation/applyScale (saucer).
 */
function applyPersistedCraftTuner(key, craft) {
  if (!craft) return;
  const persisted = loadPersistedCraftTuner(key);
  if (!persisted) return;
  if (craft.applyOrientation && (persisted.rxDeg != null || persisted.ryDeg != null || persisted.rzDeg != null)) {
    craft.applyOrientation({
      rxDeg: persisted.rxDeg || 0,
      ryDeg: persisted.ryDeg || 0,
      rzDeg: persisted.rzDeg || 0
    });
  }
  if (craft.applyScale && persisted.scale != null) {
    craft.applyScale(persisted.scale);
  }
  if (craft.applyShipOffset && (persisted.px != null || persisted.py != null || persisted.pz != null)) {
    craft.applyShipOffset({
      x: persisted.px || 0,
      y: persisted.py || 0,
      z: persisted.pz || 0
    });
  }
  // Camera offset + chase profile values also persist — write back
  // into CRAFT_FLIGHT_PROFILES so they take effect the moment the
  // craft becomes active, not just when the tuner UI fires next.
  const profile = CRAFT_FLIGHT_PROFILES[key];
  if (profile) {
    if (persisted.oy != null)              profile.followY = persisted.oy;
    if (persisted.oz != null)              profile.followDistance = -persisted.oz;
    if (persisted.chaseLag != null)        profile.chaseLag = persisted.chaseLag;
    if (persisted.bobAmplitude != null)    profile.bobAmplitude = persisted.bobAmplitude;
    if (persisted.swayAmplitude != null)   profile.swayAmplitude = persisted.swayAmplitude;
  }
}

function isLocalhostHost() {
  const h = window.location.hostname;
  if (h === 'localhost' || h === '127.0.0.1' || h === '::1') return true;
  // Treat LAN-private IPs and vite's --host network URLs as dev too,
  // otherwise the configurator vanishes the moment you switch from
  // localhost:5173 to your machine's network URL for cross-device
  // testing. Private ranges: 10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16.
  if (/^10\./.test(h)) return true;
  if (/^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(h)) return true;
  if (/^192\.168\./.test(h)) return true;
  // Explicit dev override via URL param. `?tuner=1` forces the
  // configurator on any host so the user can pop it open while
  // testing through a tunnel or remote URL.
  if (/[?&]tuner=1\b/.test(window.location.search)) return true;
  return false;
}

// === Tuner dock — shared controller for the consolidated dev drawer ===
// Owns visibility and tab switching for the bottom-docked multi-pane
// tuner drawer. Each per-tuner setup function registers show/hide
// hooks here (e.g. resync sliders when its pane becomes visible) and
// repoints its controls at the matching pane. Function declarations
// are hoisted so the setups can call these regardless of order.
const TUNER_DOCK_TABS = ['craft', 'planet', 'comet', 'cinematic', 'sun', 'galaxy', 'director'];
const tunerDockHooks = { craft: {}, planet: {}, comet: {}, cinematic: {} };
let tunerDockEl = null;
let tunerDockActiveTab = 'craft';

function tunerDockRegister(tab, hooks) {
  tunerDockHooks[tab] = hooks || {};
}
function tunerDockFireShow(tab) {
  const h = tunerDockHooks[tab];
  if (h && h.onShow) { try { h.onShow(); } catch (e) {} }
  // The show hook may have re-synced slider values; repaint their fills.
  if (tunerDockEl) repaintTunerFills(tunerDockEl.querySelector('.tuner-pane.active'));
}
function tunerDockFireHide(tab) {
  const h = tunerDockHooks[tab];
  if (h && h.onHide) { try { h.onHide(); } catch (e) {} }
}
function tunerDockSyncSidebarButtons() {
  const expanded = !!tunerDockEl && !tunerDockEl.classList.contains('minimized');
  const map = { comet: 'admin-manipulate-comet', cinematic: 'admin-cinematic-tuner', craft: 'vessel-tuner-btn' };
  Object.keys(map).forEach((tab) => {
    const btn = document.getElementById(map[tab]);
    if (btn) btn.classList.toggle('playing', expanded && tunerDockActiveTab === tab);
  });
}
function tunerDockSetMinimized(min) {
  if (!tunerDockEl) return;
  const wasMin = tunerDockEl.classList.contains('minimized');
  if (min === wasMin) { tunerDockSyncSidebarButtons(); return; }
  tunerDockEl.classList.toggle('minimized', min);
  tunerDockEl.setAttribute('aria-hidden', String(min));
  if (min) tunerDockFireHide(tunerDockActiveTab);
  else tunerDockFireShow(tunerDockActiveTab);
  tunerDockSyncSidebarButtons();
}
function tunerDockReflectActive(tab) {
  if (!tunerDockEl) return;
  tunerDockEl.querySelectorAll('.tuner-tab').forEach((b) => {
    b.classList.toggle('active', b.dataset.tab === tab);
  });
  tunerDockEl.querySelectorAll('.tuner-pane').forEach((p) => {
    p.classList.toggle('active', p.dataset.pane === tab);
  });
}
function tunerDockSelectTab(tab, expand) {
  if (!tunerDockEl || !TUNER_DOCK_TABS.includes(tab)) return;
  const wasMin = tunerDockEl.classList.contains('minimized');
  const changing = tab !== tunerDockActiveTab;
  // Leaving a currently-visible pane fires its hide hook so per-pane
  // side effects (e.g. comet overrides) switch off cleanly.
  if (!wasMin && changing) tunerDockFireHide(tunerDockActiveTab);
  tunerDockActiveTab = tab;
  tunerDockReflectActive(tab);
  if (expand) {
    // If this un-minimizes, setMinimized fires onShow for us. If the
    // dock was already open and we just changed tab, fire it here.
    if (wasMin) tunerDockSetMinimized(false);
    else { if (changing) tunerDockFireShow(tab); tunerDockSyncSidebarButtons(); }
  } else {
    tunerDockSyncSidebarButtons();
  }
}
// Sidebar buttons / hotkeys call this: open the dock on `tab`, or
// minimize it if that tab is already the visible one.
function tunerDockToggleTab(tab) {
  if (!tunerDockEl) return;
  const min = tunerDockEl.classList.contains('minimized');
  if (!min && tunerDockActiveTab === tab) tunerDockSetMinimized(true);
  else tunerDockSelectTab(tab, true);
}

// Help text for the non-obvious controls, keyed by input id or by the
// comet sliders' data-key. setupTunerHelp injects a "?" badge (native
// tooltip) next to each labelled control that has an entry here.
const TUNER_HELP = {
  // Craft
  'craft-admin-rx': 'Rotate the model around its X axis (pitch), in degrees.',
  'craft-admin-ry': 'Rotate the model around its Y axis (yaw), in degrees.',
  'craft-admin-rz': 'Rotate the model around its Z axis (roll), in degrees.',
  'craft-admin-scale': 'Uniform size multiplier for the model.',
  'craft-admin-ox': 'Chase-camera offset left/right of the ship.',
  'craft-admin-oy': 'Chase-camera offset above/below the ship.',
  'craft-admin-oz': 'Chase-camera distance behind the ship (more negative = further back).',
  'craft-admin-px': 'Nudge the mesh on its own pivot along X, without moving the camera.',
  'craft-admin-py': 'Nudge the mesh on its own pivot along Y, without moving the camera.',
  'craft-admin-pz': 'Nudge the mesh on its own pivot along Z, without moving the camera.',
  'craft-admin-lag': 'How loosely the chase camera trails the ship. Higher = floatier lag.',
  'craft-admin-bob': 'Amplitude of the idle up/down bobbing motion.',
  'craft-admin-sway': 'Amplitude of the gentle side-to-side banking sway.',
  // Planet
  'planet-admin-atmScale': 'Radius of the atmosphere shell vs the planet (1.00 = surface). Higher = thicker halo.',
  'planet-admin-atmStrength': 'Overall brightness / opacity of the atmospheric glow.',
  'planet-admin-atmColor': 'Base colour of the atmospheric glow.',
  'planet-admin-rayStrength': 'Strength of Rayleigh scattering — the blue day-side wash and sunset reddening.',
  'planet-admin-rayTint': 'Tint colour for Rayleigh scattering.',
  'planet-admin-limbTint': "Colour of the bright rim where the atmosphere catches the sun at the planet's edge.",
  'planet-admin-seam': "Pixels blended across the texture's wrap seam to hide the vertical join. 0 = off.",
  // Comet (data-key)
  'headRadius': 'Radius of the bright coma head around the nucleus.',
  'fanLow': 'Lower bound of the fan angle shaping how the coma spreads.',
  'fanHigh': 'Upper bound of the fan angle shaping how wide the coma spreads.',
  'backFadeStart': 'Where the coma begins fading on the side facing away from the sun.',
  'tailDecayPow': 'Falloff power of tail brightness with distance — higher fades faster.',
  'haloPerpBase': 'Base width of the coma halo perpendicular to the tail.',
  'haloPerpGain': 'Extra halo width scaled by the comet activity level.',
  'haloLongBase': 'Base length of the coma halo along the tail direction.',
  'haloLongGain': 'Extra halo length scaled by the comet activity level.',
  'tailTaperBase': 'Base taper of the tail.',
  'tailTaperReduce': 'How much the tail narrows along its length.',
  'comaOpacityBase': 'Base opacity of the coma envelope.',
  'comaOpacityGain': 'Extra coma opacity scaled by activity.',
  'comaCloudOpacityBase': 'Base opacity of the cloudy coma detail.',
  'comaCloudOpacityGain': 'Extra cloudy-coma opacity scaled by activity.',
  'jetOpacityBase': 'Base opacity of the sunward jets.',
  'jetOpacityGain': 'Extra jet opacity scaled by activity.',
  'ionLengthBase': 'Base length of the straight blue ion tail.',
  'ionLengthGain': 'Extra ion-tail length scaled by activity.',
  'ionWidthBase': 'Base width of the ion tail.',
  'ionWidthGain': 'Extra ion-tail width scaled by activity.',
  'ionOpacityBase': 'Base opacity of the ion tail.',
  'ionOpacityGain': 'Extra ion-tail opacity scaled by activity.',
  'dustLengthBase': 'Base length of the curved yellow dust tail.',
  'dustLengthGain': 'Extra dust-tail length scaled by activity.',
  'dustWidthBase': 'Base width of the dust tail.',
  'dustWidthGain': 'Extra dust-tail width scaled by activity.',
  'dustOpacityBase': 'Base opacity of the dust tail.',
  'dustOpacityGain': 'Extra dust-tail opacity scaled by activity.',
  // Cinematic
  'tuner-distance': 'Camera distance from the body, measured in body radii.',
  'tuner-side': 'Sideways camera offset (left/right) as a fraction of distance.',
  'tuner-up': 'Vertical camera offset (up/down) as a fraction of distance.',
  'tuner-bias': 'Shifts the look-at point toward the sun (positive) or away (negative).',
  'tuner-fov': 'Field of view in degrees — lower is more zoomed / telephoto.',
  'tuner-exposure': 'Post-process exposure — overall image brightness.',
  'tuner-contrast': 'Tonal contrast of the final image.',
  'tuner-saturation': 'Colour saturation of the final image.',
  'tuner-vignette': 'Strength of the dark edge vignette.',
  'tuner-grain': 'Amount of film-grain noise.',
  'tuner-ambient': 'Ambient fill-light intensity — brightens shadowed sides.',
  'tuner-sunlight': 'Sun (key light) intensity.'
};

function makeTunerHelpBadge(text) {
  const b = document.createElement('span');
  b.className = 'tuner-help';
  b.textContent = '?';
  b.title = text;
  b.tabIndex = 0;
  b.setAttribute('aria-label', text);
  return b;
}
function setupTunerHelp(dock) {
  // Grid rows (craft / planet / comet): badge goes inside the label span.
  dock.querySelectorAll('.craft-admin-row').forEach((row) => {
    const ctrl = row.querySelector('input, select');
    if (!ctrl) return;
    const text = TUNER_HELP[ctrl.id] || TUNER_HELP[ctrl.dataset.key];
    if (!text) return;
    const label = row.querySelector('span');
    if (label) label.appendChild(makeTunerHelpBadge(text));
  });
  // Cinematic stacked fields: badge goes right after the label text,
  // before the value readout span.
  dock.querySelectorAll('.camera-tuner-field').forEach((field) => {
    const ctrl = field.querySelector('input, select');
    if (!ctrl) return;
    const text = TUNER_HELP[ctrl.id];
    if (!text) return;
    const label = field.querySelector('label');
    if (!label) return;
    const valSpan = label.querySelector('span');
    const badge = makeTunerHelpBadge(text);
    if (valSpan) label.insertBefore(badge, valSpan);
    else label.appendChild(badge);
  });
}

// Paint a teal fill up to the current value so the sliders read like
// proper filled tracks (Chrome — Firefox uses ::-moz-range-progress).
function paintTunerRangeFill(inp) {
  const min = parseFloat(inp.min);
  const max = parseFloat(inp.max);
  const val = parseFloat(inp.value);
  if (!isFinite(min) || !isFinite(max) || max <= min || !isFinite(val)) return;
  const pct = Math.max(0, Math.min(100, ((val - min) / (max - min)) * 100));
  inp.style.background =
    'linear-gradient(to right, #7dd3a8 0%, #7dd3a8 ' + pct +
    '%, #232a36 ' + pct + '%, #232a36 100%)';
}
function repaintTunerFills(scope) {
  if (!scope) return;
  scope.querySelectorAll('input[type="range"]').forEach(paintTunerRangeFill);
}

function setupTunerDock() {
  const dock = document.getElementById('tuner-dock');
  if (!dock) return;
  if (!isLocalhostHost()) { dock.remove(); return; }
  tunerDockEl = dock;
  // Marks the page as carrying the dock so other bottom-anchored HUD
  // (the controls-hint legend) can lift clear of the dock bar.
  document.body.classList.add('has-tuner-dock');

  // Inject "?" help badges and paint the slider fills.
  setupTunerHelp(dock);
  repaintTunerFills(dock);
  dock.querySelectorAll('.tuner-pane').forEach((pane) => {
    pane.addEventListener('input', (e) => {
      if (e.target && e.target.matches('input[type="range"]')) paintTunerRangeFill(e.target);
    });
    // Selects (craft / body pickers) re-sync sliders programmatically
    // on change; repaint next frame once those handlers have run.
    pane.addEventListener('change', () => requestAnimationFrame(() => repaintTunerFills(pane)));
  });

  dock.querySelectorAll('.tuner-tab').forEach((btn) => {
    btn.addEventListener('click', () => tunerDockToggleTab(btn.dataset.tab));
  });
  const toggleBtn = document.getElementById('tuner-dock-toggle');
  if (toggleBtn) {
    toggleBtn.addEventListener('click', () => {
      tunerDockSetMinimized(!dock.classList.contains('minimized'));
    });
  }
  // Clicking the empty bar area (brand / host label) expands too.
  const bar = dock.querySelector('.tuner-dock-bar');
  if (bar) {
    bar.addEventListener('click', (e) => {
      if (!dock.classList.contains('minimized')) return;
      if (e.target === bar || e.target.classList.contains('tuner-dock-brand')
          || e.target.classList.contains('tuner-dock-host')) {
        tunerDockSelectTab(tunerDockActiveTab, true);
      }
    });
  }
  // Hotkeys: Shift+V → Craft, Shift+P → Planet, Shift+T → Cinematic.
  window.addEventListener('keydown', (e) => {
    if (!e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
    const tag = (e.target && e.target.tagName) || '';
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    let tab = null;
    if (e.code === 'KeyV') tab = 'craft';
    else if (e.code === 'KeyP') tab = 'planet';
    else if (e.code === 'KeyT') tab = 'cinematic';
    if (!tab) return;
    tunerDockToggleTab(tab);
    e.preventDefault();
  });

  // Default tab = craft, but the drawer starts minimized (markup ships
  // with .minimized) so only the bar shows until the user opens it.
  tunerDockActiveTab = 'craft';
  tunerDockReflectActive('craft');
  tunerDockSyncSidebarButtons();
}

function setupCraftAdminTuner() {
  // The tuner dock owns visibility now; bail on non-localhost (the
  // dock element is already removed there). This just wires up the
  // controls that live inside the Craft pane. Shift+V / the VESSEL
  // HUD button open the dock on this tab via the dock controller.
  if (!isLocalhostHost()) return;
  const panel = document.getElementById('tuner-pane-craft');
  if (!panel) return;

  const targetSel = document.getElementById('craft-admin-target');
  const $ = (id) => document.getElementById(id);
  const inputs = {
    rxDeg:         $('craft-admin-rx'),
    ryDeg:         $('craft-admin-ry'),
    rzDeg:         $('craft-admin-rz'),
    scale:         $('craft-admin-scale'),
    ox:            $('craft-admin-ox'),
    oy:            $('craft-admin-oy'),
    oz:            $('craft-admin-oz'),
    px:            $('craft-admin-px'),
    py:            $('craft-admin-py'),
    pz:            $('craft-admin-pz'),
    chaseLag:      $('craft-admin-lag'),
    bobAmplitude:  $('craft-admin-bob'),
    swayAmplitude: $('craft-admin-sway')
  };
  const outputs = {
    rxDeg:         $('craft-admin-rx-out'),
    ryDeg:         $('craft-admin-ry-out'),
    rzDeg:         $('craft-admin-rz-out'),
    scale:         $('craft-admin-scale-out'),
    ox:            $('craft-admin-ox-out'),
    oy:            $('craft-admin-oy-out'),
    oz:            $('craft-admin-oz-out'),
    px:            $('craft-admin-px-out'),
    py:            $('craft-admin-py-out'),
    pz:            $('craft-admin-pz-out'),
    chaseLag:      $('craft-admin-lag-out'),
    bobAmplitude:  $('craft-admin-bob-out'),
    swayAmplitude: $('craft-admin-sway-out')
  };

  function fmt(key, v) {
    if (key === 'rxDeg' || key === 'ryDeg' || key === 'rzDeg') return `${Math.round(v)}°`;
    if (key === 'scale')                                       return v.toFixed(2);
    if (key === 'chaseLag')                                    return v.toFixed(1);
    if (key === 'bobAmplitude' || key === 'swayAmplitude')     return v.toFixed(3);
    if (key === 'px' || key === 'py' || key === 'pz')          return v.toFixed(3);
    return v.toFixed(2);
  }

  function readState(key) {
    // Priority order: live in-memory state (set during this session) →
    // persisted localStorage → ship-module initialEulerDeg fallback.
    // The localStorage layer is what makes the configurator survive
    // page reloads without paste-and-reload while iterating on a
    // ship's orientation.
    const profile = CRAFT_FLIGHT_PROFILES[key] || {};
    const craft = loadedCraft[key];
    const init = (craft && craft.initialEulerDeg) || { rx: 0, ry: 0, rz: 0 };
    const persisted = loadPersistedCraftTuner(key);
    const base = {
      rxDeg:         init.rx,
      ryDeg:         init.ry,
      rzDeg:         init.rz,
      scale:         1,
      ox:            0,
      oy:            profile.followY != null ? profile.followY : 0,
      oz:            profile.followDistance != null ? -profile.followDistance : 0,
      px:            0,
      py:            0,
      pz:            0,
      chaseLag:      profile.chaseLag != null ? profile.chaseLag : 999,
      bobAmplitude:  profile.bobAmplitude || 0,
      swayAmplitude: profile.swayAmplitude || 0
    };
    if (persisted) {
      // Merge persisted over base so any keys we add later just fall
      // through to defaults without breaking old saved blobs.
      return { ...base, ...persisted };
    }
    return base;
  }

  function writeState(key, state) {
    const profile = CRAFT_FLIGHT_PROFILES[key];
    if (profile) {
      profile.followY = state.oy;
      // followDistance is positive in profile, negative on Z in slider.
      profile.followDistance = -state.oz;
      profile.chaseLag = state.chaseLag;
      profile.bobAmplitude = state.bobAmplitude;
      profile.swayAmplitude = state.swayAmplitude;
    }
    const craft = loadedCraft[key];
    if (craft) {
      if (craft.applyOrientation) {
        craft.applyOrientation({ rxDeg: state.rxDeg, ryDeg: state.ryDeg, rzDeg: state.rzDeg });
      }
      if (craft.applyScale) {
        craft.applyScale(state.scale);
      }
      if (craft.applyShipOffset) {
        craft.applyShipOffset({ x: state.px || 0, y: state.py || 0, z: state.pz || 0 });
      }
    }
    persistCraftTuner(key, state);
  }

  function syncToTarget() {
    const key = targetSel.value;
    const state = readState(key);
    Object.keys(inputs).forEach((k) => {
      if (!inputs[k]) return;
      inputs[k].value = state[k];
      outputs[k].textContent = fmt(k, state[k]);
    });
    panel.dataset.activeKey = key;
  }

  function onSliderChange(key) {
    const value = parseFloat(inputs[key].value);
    outputs[key].textContent = fmt(key, value);
    const target = targetSel.value;
    const cur = readState(target);
    cur[key] = value;
    writeState(target, cur);
  }

  Object.keys(inputs).forEach((k) => {
    if (!inputs[k]) return;
    inputs[k].addEventListener('input', () => onSliderChange(k));
  });

  targetSel.addEventListener('change', syncToTarget);

  // The dock owns show/hide. Register the Craft pane's "became
  // visible" hook so sliders resync to the active craft, and surface
  // the in-HUD VESSEL button as a dock launcher for this tab.
  tunerDockRegister('craft', { onShow: () => syncToTarget() });
  const hudBtn = document.getElementById('vessel-tuner-btn');
  if (hudBtn) {
    hudBtn.style.display = '';
    hudBtn.addEventListener('click', () => tunerDockToggleTab('craft'));
  }

  $('craft-admin-reset').addEventListener('click', () => {
    const target = targetSel.value;
    const def = CRAFT_ADMIN_DEFAULTS[target];
    if (!def) return;
    writeState(target, { ...def });
    syncToTarget();
  });

  $('craft-admin-copy').addEventListener('click', async () => {
    const target = targetSel.value;
    const cur = readState(target);
    // Paste-ready snippet. Split into two blocks: the orient rotation
    // goes in the ship module (e.g. src/ships/bc304.js, replace the
    // existing `orient.rotation.y = …` block), and the chase/follow
    // profile goes in CRAFT_FLIGHT_PROFILES in src/main.js.
    const snippet =
`// ─── paste into src/ships/${target}.js (orient setup) ───
orient.rotation.set(
  THREE.MathUtils.degToRad(${cur.rxDeg}),
  THREE.MathUtils.degToRad(${cur.ryDeg}),
  THREE.MathUtils.degToRad(${cur.rzDeg})
);
// also update the returned initialEulerDeg so the tuner picks it up:
// initialEulerDeg: { rx: ${cur.rxDeg}, ry: ${cur.ryDeg}, rz: ${cur.rzDeg} },

// optional ship-pivot offset (skip if zero):
// root.position.set(
//   baseRootPosition.x + (${(cur.px || 0).toFixed(3)}),
//   baseRootPosition.y + (${(cur.py || 0).toFixed(3)}),
//   baseRootPosition.z + (${(cur.pz || 0).toFixed(3)})
// );

// ─── paste into CRAFT_FLIGHT_PROFILES['${target}'] in src/main.js ───
followY: ${cur.oy.toFixed(3)},
followDistance: ${(-cur.oz).toFixed(3)},
chaseLag: ${cur.chaseLag.toFixed(2)},
bobAmplitude: ${cur.bobAmplitude.toFixed(4)},
swayAmplitude: ${cur.swayAmplitude.toFixed(4)},`;
    try {
      await navigator.clipboard.writeText(snippet);
      const btn = $('craft-admin-copy');
      const orig = btn.textContent;
      btn.textContent = 'Copied';
      setTimeout(() => { btn.textContent = orig; }, 1200);
    } catch (err) {
      console.warn('clipboard write failed:', err);
    }
  });

  // Reset wipes both the live state AND the persisted localStorage
  // entry so the tuner is back to fresh defaults on next reload. Did
  // not do this before — the persistence layer is new this turn.
  $('craft-admin-reset').addEventListener('click', () => {
    const target = targetSel.value;
    try { localStorage.removeItem(CRAFT_TUNER_STORAGE_PREFIX + target); } catch (e) {}
  }, { capture: true });

  // Initial sync once. The MutationObserver-on-data-attribute approach
  // I tried earlier was self-feeding (syncToTarget itself wrote the
  // attribute back), so just sync at boot and re-sync explicitly when
  // the user picks a different craft.
  syncToTarget();
  const craftSelect = document.getElementById('craft-type');
  if (craftSelect) {
    craftSelect.addEventListener('change', () => {
      // Wait a microtask for setActiveCraft to populate loadedCraft.
      // Defer with rAF rather than setTimeout so we yield to whatever
      // async work is happening but still re-sync next frame at the
      // latest if loadedCraft fills earlier.
      requestAnimationFrame(() => {
        targetSel.value = craftSelect.value;
        syncToTarget();
      });
    });
  }
}

// === Planet tuner panel — localhost only ===
// Live-tunes the atmospheric shader uniforms (shell scale, strength,
// rayleigh tint + strength, limb tint, atmosphere colour) and the
// seam-blend width for every planet that has either atmosphere
// params in celestialData or seam-fix params in
// SEAMLESS_TEXTURE_EDGE_FIXES. Copy button emits paste-ready code
// for both files so dial-ins can be locked in source. Shift+P
// toggles. localStorage persists per body so reloads keep the dial.
const PLANET_TUNER_STORAGE_PREFIX = 'planetTuner.';

function loadPersistedPlanetTuner(key) {
  try {
    const raw = localStorage.getItem(PLANET_TUNER_STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
}
function persistPlanetTuner(key, state) {
  try {
    localStorage.setItem(PLANET_TUNER_STORAGE_PREFIX + key, JSON.stringify(state));
  } catch (e) {}
}

function findPlanetAtmosphereMesh(planetKey) {
  const planet = planets[planetKey];
  if (!planet) return null;
  let result = null;
  planet.traverse((child) => {
    if (child.name === 'atmosphere' && child.isMesh) result = child;
  });
  return result;
}

/**
 * Push tuner state into the live atmosphere shader uniforms +
 * (optionally) rebuild the shell geometry on scale change. Falls
 * through gracefully if the body has no atmosphere (mesh missing).
 */
function applyPlanetTunerState(key, state) {
  const data = celestialData[key];
  if (!data) return;
  const mesh = findPlanetAtmosphereMesh(key);
  if (mesh) {
    const u = mesh.material.uniforms;
    if (state.atmStrength != null && u.atmosphereStrength) {
      u.atmosphereStrength.value = state.atmStrength;
    }
    if (state.atmColor != null && u.atmosphereColor) {
      u.atmosphereColor.value.set(state.atmColor);
    }
    if (state.rayStrength != null && u.rayleighStrengthMul) {
      u.rayleighStrengthMul.value = state.rayStrength;
    }
    if (state.rayTint != null && u.rayleighTint) {
      u.rayleighTint.value.set(state.rayTint);
    }
    if (state.limbTint != null && u.limbTint) {
      u.limbTint.value.set(state.limbTint);
    }
    if (state.atmScale != null) {
      // Rebuild the shell geometry at the new scale. The shader's
      // atmosphereRadius uniform updates in lockstep so Hillaire
      // chord-length scattering stays consistent with the new shell.
      const segments = graphicsConfig.atmosphereSegments;
      const newGeom = new THREE.SphereGeometry(
        data.radius * state.atmScale,
        segments,
        Math.max(32, Math.floor(segments / 2))
      );
      mesh.geometry.dispose();
      mesh.geometry = newGeom;
      if (u.atmosphereRadius) {
        u.atmosphereRadius.value = data.radius * state.atmScale;
      }
      // Keep the authored-radii stash current so the realistic-scale
      // toggle rescales from the tuned shell, not the original one.
      if (mesh.userData.baseAtmoRadii) {
        mesh.userData.baseAtmoRadii.shell = data.radius * state.atmScale;
      }
    }
  }
  // Seam re-blend works whether or not there's atmosphere — it's the
  // SURFACE texture, not the atmosphere shell.
  if (state.seamWidth != null && _planetSourceImages[key]) {
    reblendPlanetSeam(key, state.seamWidth);
  }
}

function setupPlanetAdminTuner() {
  // The tuner dock owns visibility; bail on non-localhost (the dock
  // is already removed there). Shift+P opens the dock on this tab.
  if (!isLocalhostHost()) return;
  const panel = document.getElementById('tuner-pane-planet');
  if (!panel) return;

  const $ = (id) => document.getElementById(id);
  const targetSel = $('planet-admin-target');
  const inputs = {
    atmScale:     $('planet-admin-atmScale'),
    atmStrength:  $('planet-admin-atmStrength'),
    atmColor:     $('planet-admin-atmColor'),
    rayStrength:  $('planet-admin-rayStrength'),
    rayTint:      $('planet-admin-rayTint'),
    limbTint:     $('planet-admin-limbTint'),
    seamWidth:    $('planet-admin-seam')
  };
  const outputs = {
    atmScale:     $('planet-admin-atmScale-out'),
    atmStrength:  $('planet-admin-atmStrength-out'),
    rayStrength:  $('planet-admin-rayStrength-out'),
    seamWidth:    $('planet-admin-seam-out')
  };

  function hexFromNumber(n) {
    if (typeof n !== 'number') return '#ffffff';
    return '#' + n.toString(16).padStart(6, '0');
  }
  function fmt(key, v) {
    if (key === 'atmScale') return Number(v).toFixed(3);
    if (key === 'atmStrength' || key === 'rayStrength') return Number(v).toFixed(2);
    if (key === 'seamWidth') return String(Math.round(Number(v)));
    return String(v);
  }

  function readState(key) {
    const data = celestialData[key] || {};
    const persisted = loadPersistedPlanetTuner(key);
    const seamFix = SEAMLESS_TEXTURE_EDGE_FIXES[key];
    const base = {
      atmScale:    data.atmosphereScale != null ? data.atmosphereScale : 1.012,
      atmStrength: data.atmosphereStrength != null ? data.atmosphereStrength : 0.5,
      atmColor:    hexFromNumber(data.atmosphereColor != null ? data.atmosphereColor : 0xb4dde8),
      rayStrength: data.rayleighStrength != null ? data.rayleighStrength : 0.16,
      rayTint:     hexFromNumber(data.rayleighTint != null ? data.rayleighTint : 0x82c6dc),
      limbTint:    hexFromNumber(data.limbTint != null ? data.limbTint : 0xc6e8f4),
      seamWidth:   seamFix ? seamFix.width : 0
    };
    return persisted ? { ...base, ...persisted } : base;
  }

  function writeState(key, state) {
    applyPlanetTunerState(key, state);
    persistPlanetTuner(key, state);
  }

  function syncToTarget() {
    const key = targetSel.value;
    const state = readState(key);
    Object.keys(inputs).forEach((k) => {
      if (!inputs[k]) return;
      inputs[k].value = state[k];
      if (outputs[k]) outputs[k].textContent = fmt(k, state[k]);
    });
    // Apply on switch so the body the user just picked reflects the
    // persisted dial without waiting for a slider event.
    applyPlanetTunerState(key, state);
  }

  function onChange(k) {
    const raw = inputs[k].value;
    const value = (k === 'atmColor' || k === 'rayTint' || k === 'limbTint') ? raw : parseFloat(raw);
    if (outputs[k]) outputs[k].textContent = fmt(k, value);
    const target = targetSel.value;
    const cur = readState(target);
    cur[k] = value;
    writeState(target, cur);
  }

  // Seam reblend is canvas-heavy on 4K planet textures, so dragging
  // the slider with `input` would queue dozens of pixel-walk ops and
  // tank responsiveness. Coalesce: latest value wins, processed on
  // the next animation frame, and only one in-flight at a time.
  let pendingSeamFrame = null;
  let pendingSeamKey = null;
  function scheduleSeamReblend() {
    if (pendingSeamFrame) return;
    pendingSeamFrame = requestAnimationFrame(() => {
      pendingSeamFrame = null;
      if (pendingSeamKey == null) return;
      const target = targetSel.value;
      const value = parseFloat(inputs.seamWidth.value);
      if (outputs.seamWidth) outputs.seamWidth.textContent = fmt('seamWidth', value);
      const cur = readState(target);
      cur.seamWidth = value;
      writeState(target, cur);
      pendingSeamKey = null;
    });
  }

  Object.keys(inputs).forEach((k) => {
    if (!inputs[k]) return;
    if (k === 'seamWidth') {
      inputs[k].addEventListener('input', () => {
        pendingSeamKey = targetSel.value;
        scheduleSeamReblend();
      });
    } else {
      inputs[k].addEventListener('input', () => onChange(k));
    }
  });
  targetSel.addEventListener('change', syncToTarget);

  // When the Planet pane opens, point the body selector at whatever
  // planet is currently focused so tuning affects what the user is
  // actually looking at (the dropdown otherwise keeps its last value,
  // which made the sliders appear to do nothing on a different body).
  function syncPlanetBodyToView() {
    try {
      if (!selectedObject || !planets) return;
      for (const key of Object.keys(planets)) {
        if (planets[key] === selectedObject) {
          const known = Array.from(targetSel.options).some((o) => o.value === key);
          if (known && targetSel.value !== key) targetSel.value = key;
          return;
        }
      }
    } catch (e) {}
  }
  tunerDockRegister('planet', { onShow: () => { syncPlanetBodyToView(); syncToTarget(); } });

  $('planet-admin-reset').addEventListener('click', () => {
    const key = targetSel.value;
    try { localStorage.removeItem(PLANET_TUNER_STORAGE_PREFIX + key); } catch (e) {}
    // Re-derive from celestialData / SEAMLESS_TEXTURE_EDGE_FIXES and
    // re-apply, sync sliders.
    syncToTarget();
  });

  $('planet-admin-copy').addEventListener('click', async () => {
    const key = targetSel.value;
    const cur = readState(key);
    const atmColorHex = cur.atmColor.replace('#', '0x');
    const rayTintHex = cur.rayTint.replace('#', '0x');
    const limbTintHex = cur.limbTint.replace('#', '0x');
    const snippet =
`// ─── paste into src/data/celestialData.js (${key} entry) ───
hasAtmosphere: true,
atmosphereScale: ${cur.atmScale.toFixed(3)},
atmosphereColor: ${atmColorHex},
atmosphereStrength: ${cur.atmStrength.toFixed(2)},
rayleighTint: ${rayTintHex},
rayleighStrength: ${cur.rayStrength.toFixed(2)},
limbTint: ${limbTintHex},

// ─── paste into SEAMLESS_TEXTURE_EDGE_FIXES in src/main.js ───
${key}: { width: ${Math.round(cur.seamWidth)} }`;
    try {
      await navigator.clipboard.writeText(snippet);
      const btn = $('planet-admin-copy');
      const orig = btn.textContent;
      btn.textContent = 'Copied';
      setTimeout(() => { btn.textContent = orig; }, 1200);
    } catch (err) {
      console.warn('clipboard write failed:', err);
    }
  });

  // Apply any persisted dial-in at boot so the look survives reloads
  // even before the user opens the panel.
  Object.keys(celestialData).forEach((key) => {
    const persisted = loadPersistedPlanetTuner(key);
    if (persisted) applyPlanetTunerState(key, persisted);
  });

  // Initial sync sets sliders + outputs for the default-selected body.
  syncToTarget();
}

// === Comet manipulation panel — localhost only ===
// Floating panel with sliders for every visual parameter we've been
// hand-tuning. Live-updates the cometAdminOverrides object so the
// next animate() pass picks up new values. Copy button serializes
// the active values as JSON to clipboard.
function setupCometAdminTuner() {
  if (!isLocalhostHost()) return;
  const panel = document.getElementById('tuner-pane-comet');
  if (!panel) return;

  // Defaults snapshotted before user starts tweaking, so RESET can
  // restore them and so we know whether each field has diverged.
  const defaults = { ...cometAdminOverrides };

  // The dock drives visibility. Comet overrides only take effect while
  // the Comet pane is the visible one, so tie cometAdminOverrides.enabled
  // to the dock's show/hide hooks. The sidebar button opens the tab.
  tunerDockRegister('comet', {
    onShow: () => { cometAdminOverrides.enabled = true; syncInputs(); },
    onHide: () => { cometAdminOverrides.enabled = false; }
  });
  const sidebarToggleBtn = document.getElementById('admin-manipulate-comet');
  if (sidebarToggleBtn) sidebarToggleBtn.addEventListener('click', () => tunerDockToggleTab('comet'));

  const inputs = Array.from(panel.querySelectorAll('input[type=range][data-key]'));
  function syncInputs() {
    inputs.forEach((inp) => {
      const k = inp.dataset.key;
      const v = cometAdminOverrides[k];
      if (typeof v === 'number') {
        inp.value = String(v);
        const out = inp.parentElement.querySelector('output');
        if (out) out.value = v.toFixed(2);
      }
    });
  }
  inputs.forEach((inp) => {
    const k = inp.dataset.key;
    const out = inp.parentElement.querySelector('output');
    inp.addEventListener('input', () => {
      const v = parseFloat(inp.value);
      cometAdminOverrides[k] = v;
      cometAdminOverrides.enabled = true;
      if (out) out.value = v.toFixed(2);
    });
  });

  document.getElementById('comet-admin-reset').addEventListener('click', () => {
    Object.assign(cometAdminOverrides, defaults);
    syncInputs();
  });
  document.getElementById('comet-admin-copy').addEventListener('click', async () => {
    const snapshot = {};
    Object.keys(defaults).forEach((k) => {
      if (k === 'enabled') return;
      snapshot[k] = cometAdminOverrides[k];
    });
    const json = JSON.stringify(snapshot, null, 2);
    try {
      await navigator.clipboard.writeText(json);
      console.info('[comet-admin] config copied to clipboard:\n' + json);
      const btn = document.getElementById('comet-admin-copy');
      const orig = btn.textContent;
      btn.textContent = 'COPIED';
      setTimeout(() => { btn.textContent = orig; }, 1200);
    } catch (err) {
      console.error('[comet-admin] clipboard write failed; values logged below', err);
      console.info(json);
    }
  });

  syncInputs();
}

// 7 hovering UFOs at mysterious / conspiracy locations on Earth.
// One over Area 51 (the user's request) plus six other discoverable
// spots — Roswell, the Bermuda Triangle, Skinwalker Ranch, Easter
// Island, Stonehenge, and the Tunguska event site. Tiny (so they
// only show up when zoomed close to Earth) and parented to Earth's
// rotating mesh so they stay over their assigned coordinates as
// the planet spins.
const SECRET_UFO_LOCATIONS = [
  { name: 'Area 51',          lat:  37.2353, lon: -115.8111 },
  { name: 'Roswell',          lat:  33.3943, lon: -104.5230 },
  { name: 'Bermuda Triangle', lat:  25.0000, lon:  -71.0000 },
  { name: 'Skinwalker Ranch', lat:  40.2622, lon: -109.8848 },
  { name: 'Easter Island',    lat: -27.1127, lon: -109.3497 },
  { name: 'Stonehenge',       lat:  51.1789, lon:   -1.8262 },
  { name: 'Tunguska',         lat:  60.8861, lon:  101.8941 },
];

let secretUfos = [];

function createSecretUfos() {
  if (!planets.earth) return;
  // Earth structure: planets.earth → tiltGroup (axial tilt) → mesh.
  // We parent the UFOs to the inner mesh so they rotate with Earth's
  // daily spin AND inherit the axial tilt — they stay over their
  // surface coordinates regardless of orbit / time speed.
  const earthGroup = planets.earth;
  const tiltGroup = earthGroup.children.find((c) => c.userData && c.userData.isTiltGroup)
    || earthGroup.children[0];
  if (!tiltGroup) return;
  const earthMesh = tiltGroup.children[0];
  if (!earthMesh) return;

  const earthRadius = celestialData.earth.radius;
  // Scale tuned so each UFO is ~1 km across at Earth's true scale —
  // "large UFO" rather than island. Real Earth radius is 6371 km, so
  // ufoScale = 0.00016 × earthRadius ≈ 1 km equivalent. Small enough
  // to feel discoverable, big enough to spot from ISS-altitude zoom.
  const ufoScale = earthRadius * 0.00016;
  // Hover just half a UFO-width above the surface so the saucer's
  // bottom (lathe profile y=-0.03) sits ~0.5 km above ground —
  // believable hovering altitude, bottom clearly aligned with the
  // planet rather than floating off into space.
  const hoverHeight = ufoScale * 0.55;

  // Reuse a single shared geometry + a simpler material for all
  // seven secret UFOs. We don't need per-instance reflections
  // (those are reserved for the player's UFO) so the secret ones
  // get a cheaper static material.
  const points = [
    new THREE.Vector2(0, -0.03),
    new THREE.Vector2(0.35, -0.03),
    new THREE.Vector2(0.45, -0.02),
    new THREE.Vector2(0.5, 0),
    new THREE.Vector2(0.45, 0.02),
    new THREE.Vector2(0.35, 0.03),
    new THREE.Vector2(0, 0.03),
  ];
  const ufoGeometry = new THREE.LatheGeometry(points, 64);
  const ufoMaterial = new THREE.MeshStandardMaterial({
    color: 0xc8c9c5,
    metalness: 0.95,
    roughness: 0.22,
    emissive: 0x223344,
    emissiveIntensity: 0.15,
  });
  // A faint emissive glow so they're spottable against the dark
  // night side of Earth.
  const glowMaterial = new THREE.MeshBasicMaterial({
    color: 0x88ddff,
    transparent: true,
    opacity: 0.55,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });

  SECRET_UFO_LOCATIONS.forEach((loc, i) => {
    // lat/lon → 3D position on Earth's local sphere. The convention
    // here matches Three.js SphereGeometry's default UV mapping with
    // an equirectangular Earth texture: longitude maps via theta
    // around the +Y axis and the +X meridian sits at theta = 0.
    const phi = (90 - loc.lat) * Math.PI / 180;
    const theta = (loc.lon + 180) * Math.PI / 180;
    const r = earthRadius + hoverHeight;
    const px = -r * Math.sin(phi) * Math.cos(theta);
    const py =  r * Math.cos(phi);
    const pz =  r * Math.sin(phi) * Math.sin(theta);

    const ufo = new THREE.Mesh(ufoGeometry, ufoMaterial);
    ufo.scale.setScalar(ufoScale);
    ufo.position.set(px, py, pz);

    // Orient the saucer so its disc plane is tangent to Earth — its
    // local +Y axis points outward along the radial direction.
    const up = new THREE.Vector3(px, py, pz).normalize();
    const defaultUp = new THREE.Vector3(0, 1, 0);
    const orient = new THREE.Quaternion().setFromUnitVectors(defaultUp, up);
    ufo.quaternion.copy(orient);

    // Faint blue glow ring underneath the saucer for visibility.
    const glowRim = new THREE.Mesh(
      new THREE.TorusGeometry(0.49, 0.02, 12, 64),
      glowMaterial,
    );
    glowRim.rotation.x = Math.PI / 2;
    glowRim.position.y = -0.01;
    ufo.add(glowRim);

    ufo.userData = {
      isSecretUfo: true,
      name: `UFO over ${loc.name}`,
      type: 'Unidentified Aerial Phenomenon',
      description: `Allegedly hovering over ${loc.name}. Whether it is real, a weather balloon, or "swamp gas" depends on who you ask.`,
      basePosition: new THREE.Vector3(px, py, pz),
      surfaceUp: up.clone(),
      bobPhase: i * 0.83 + Math.random() * 1.5,
      // Bob amplitude scales with UFO size so the bob looks like a
      // gentle hover, not a teleport across the sky.
      bobAmplitude: ufoScale * 0.4,
      spinRate: 0.08 + Math.random() * 0.10,
      location: loc.name,
      isHidden: true, // not in nav by default — discoverable
    };

    earthMesh.add(ufo);
    secretUfos.push(ufo);
  });
}

// TNG-style warp factor mapping. flyVelocity below 5 reads as sub-
// warp; from 5 up to 300 maps to warp 1.0 through 9.0 on a smooth
// log curve; above 300 the factor approaches 10 asymptotically so
// flyVelocity 1000 ≈ warp 9.5, 3000 ≈ warp 9.9, 10000 ≈ warp 9.9999.
// Used by both the body classList toggle and the warp-factor HUD
// readout so the user sees a precise Star Trek factor instead of
// just an on/off "warp engaged" indicator.
function speedToWarpFactor(speed) {
  if (speed < 5) return 0;
  // Sub-warp-9 region: log curve from 1.0 at speed=5 to 9.0 at speed=300.
  const t = Math.log10(speed / 5) / Math.log10(60); // 60 = 300/5
  if (t <= 1) return 1 + 8 * t;
  // Beyond warp 9 use an asymptotic curve toward 9.9999.
  // overflow t∈[1,..]; each integer step adds another "9" to the warp
  // factor (speed 3000 → t≈1.56 → asymptote ~0.72 → warp ~9.72; speed
  // 10000 → t≈2.31 → asymptote ~0.995 → warp ~9.9995, very close to
  // the canonical maximum).
  const overflow = t - 1;
  const asymptotic = 1 - Math.pow(0.1, overflow);
  return 9 + 0.9999 * asymptotic;
}

function formatWarpFactor(w) {
  if (w <= 0) return '';
  if (w < 9) return `Warp ${w.toFixed(1)}`;
  // Show more precision near the asymptote so the user sees 9.99,
  // 9.999, 9.9999 increments rather than rounding to 10.0.
  if (w < 9.9) return `Warp ${w.toFixed(2)}`;
  if (w < 9.99) return `Warp ${w.toFixed(3)}`;
  return `Warp ${w.toFixed(4)}`;
}

// Update warp effect based on propulsion and speed
function updateWarpEffect(delta = 1 / 60) {
  // Watchdog: the dropdown is the source of truth for the user's
  // current propulsion choice. If something elsewhere has mutated
  // `currentPropulsion` away from what the dropdown shows (we've
  // seen reports of "switching doesn't stick" that we couldn't trace
  // statically), force it back to the UI value here. This makes the
  // visible dropdown the authority and prevents any stale-state path
  // from re-engaging warp after the user picked Impulse.
  if (propulsionSelect && propulsionSelect.value &&
      propulsionSelect.value !== currentPropulsion &&
      propulsionTypes[propulsionSelect.value]) {
    currentPropulsion = propulsionSelect.value;
  }
  const currentSpeed = flyVelocity.length();
  const isWarp = currentPropulsion === 'warp' && currentSpeed > 5;
  const warpFactor = isWarp ? speedToWarpFactor(currentSpeed) : 0;

  const warpNorm = isWarp
    ? THREE.MathUtils.clamp((warpFactor - 1) / 8.9999, 0, 1)
    : 0;
  if (isWarp && !wormholeTunnelInFlight && !(hyperspace && hyperspace.isActive())) {
    document.body.classList.add('warp-active');
    // CSS variable drives the subtle tunnel tint + Doppler vignette.
    document.documentElement.style.setProperty('--warp-factor', warpNorm.toFixed(4));
  } else {
    document.body.classList.remove('warp-active');
    document.documentElement.style.setProperty('--warp-factor', '0');
  }
  // Drive the 3D warp field (instanced streaks) from the same factor.
  // Bloom in the post-process pipeline picks up the bright spheres and
  // turns them into the iconic TNG light-streaks. Wormhole rides and
  // hyperspace jumps own the whole frame, so the overlay steps aside.
  const effectiveWarpNorm = (wormholeTunnelInFlight || (hyperspace && hyperspace.isActive()))
    ? 0
    : warpNorm;
  if (warpEffect && camera) {
    warpEffect.setWarpFactor(camera, effectiveWarpNorm, delta);
  }

  const warpReadout = document.getElementById('warp-factor-readout');
  if (warpReadout) {
    if (isWarp) {
      warpReadout.textContent = formatWarpFactor(warpFactor);
      warpReadout.classList.add('is-active');
    } else {
      warpReadout.textContent = '';
      warpReadout.classList.remove('is-active');
    }
  }

  // Update UFO hum intensity based on speed
  if (flyMode) {
    const prop = propulsionTypes[currentPropulsion];
    const speedRatio = Math.min(1, currentSpeed / prop.maxSpeed);
    setUfoHumIntensity(0.3 + speedRatio * 0.7);
  }

  // Update ship visibility and position
  if (playerShip) {
    const profile = getActiveCraftProfile();
    playerShip.visible = flyMode;
    applyCraftFlightScale();

    if (flyMode) {
      const time = Date.now() * 0.001;
      if (ufoStarReflectionMaterial && ufoStarReflectionMaterial.uniforms) {
        ufoStarReflectionMaterial.uniforms.time.value = time;
      }

      if (isMiddleMouseDown) {
        // Drive the ship directly from flyVelocity so the user can
        // keep flying while orbiting around it. Without this, the
        // ship freezes at the position it had when middle-click was
        // pressed and the camera "loses tracking" of any forward
        // momentum the user still has on the stick.
        playerShip.position.addScaledVector(flyVelocity, delta * 60);
        // Anchor the orbit on the ship's live world position rather
        // than the snapshot captured at click-start. Orbit camera now
        // follows the ship as it moves.
        ufoWorldPos.copy(playerShip.position);

        // Smooth orbit interpolation. Time-corrected so the orbit
        // damping feels the same at any frame rate (k = 5 reproduces
        // the old 0.08-per-frame feel at 60 Hz).
        const smoothing = 1 - Math.exp(-5.0 * delta);
        cameraOrbitAngle += (targetOrbitAngle - cameraOrbitAngle) * smoothing;
        cameraOrbitPitch += (targetOrbitPitch - cameraOrbitPitch) * smoothing;

        // Spherical coordinates for smooth orbit
        const x = ufoWorldPos.x + Math.sin(cameraOrbitAngle) * Math.cos(cameraOrbitPitch) * cameraOrbitDistance;
        const z = ufoWorldPos.z + Math.cos(cameraOrbitAngle) * Math.cos(cameraOrbitPitch) * cameraOrbitDistance;
        const y = ufoWorldPos.y + Math.sin(cameraOrbitPitch) * cameraOrbitDistance;

        camera.position.set(x, y, z);
        camera.lookAt(ufoWorldPos);

        // Pan offset (right-click + middle-click drag) — translate
        // both the camera and the look-at target along the camera's
        // local right/up axes so the ship slides off-centre in the
        // frame for an offset composition.
        if (cameraPanOffsetX !== 0 || cameraPanOffsetY !== 0) {
          FLY_SCRATCH_PAN_RIGHT.set(1, 0, 0).applyQuaternion(camera.quaternion);
          FLY_SCRATCH_PAN_UP.set(0, 1, 0).applyQuaternion(camera.quaternion);
          camera.position.addScaledVector(FLY_SCRATCH_PAN_RIGHT, cameraPanOffsetX);
          camera.position.addScaledVector(FLY_SCRATCH_PAN_UP, cameraPanOffsetY);
          // Re-aim at the original look-at after sliding so the ship
          // anchors on whatever offset position the user composed.
          // (We do NOT re-lookAt the ship — that would defeat the pan.)
        }

        // Update flyEuler to match new camera orientation
        flyEuler.setFromQuaternion(camera.quaternion);
      } else if (orbitParked) {
        // Parked: do nothing. Camera and ship stay exactly where they
        // were when the user released middle-click. Mouse-look still
        // works on the camera quaternion via the regular fly handlers,
        // so the user can pan the framing for a screenshot. Any thrust
        // input clears `orbitParked` (see updateFlyMovement) and we
        // fall through to the chase branch on the next frame.
      } else {
        // Position the active craft in a third-person chase slot. The
        // Enterprise needs a much longer leash than the tiny saucer so
        // it reads as a ship instead of a clipped cockpit overlay.
        const offset = getCraftFollowOffset(FLY_SCRATCH_CRAFT_OFFSET, profile);
        offset.applyQuaternion(camera.quaternion);

        // Target chase position. With chaseLag finite, the ship's
        // actual position lerps toward this every frame — gives the
        // chase cam real "weight" so it feels like we're trailing the
        // ship rather than welding it to the camera. Saucer keeps
        // chaseLag=999 which is effectively rigid (preserves the
        // existing crisp UFO feel).
        const targetX = camera.position.x + offset.x;
        const targetY = camera.position.y + offset.y;
        const targetZ = camera.position.z + offset.z;

        // Rigid attachment in camera-local space — the ship's lateral
        // and vertical positions track the camera exactly so turning
        // can never drift the ship out of frame. Forward-direction
        // "lag" is layered on top as a separate scalar that responds
        // only to forward velocity (chase-cam feel without the
        // off-screen drift the world-space lerp had).
        playerShip.position.set(targetX, targetY, targetZ);

        // Smooth resume from parked-inspection: lerp from the ship's
        // last frozen position toward the chase slot over a short
        // window. position.lerp(FROM, 1-smoothT) keeps it on TARGET
        // when smoothT=1 (transition done) and on FROM when smoothT=0.
        if (chaseResumeProgress < 1) {
          chaseResumeProgress = Math.min(1, chaseResumeProgress + delta / CHASE_RESUME_DURATION);
          const t = chaseResumeProgress;
          const smoothT = t * t * (3 - 2 * t);
          playerShip.position.lerp(FLY_SCRATCH_RESUME_FROM, 1 - smoothT);
        }

        const lag = profile.chaseLag != null ? profile.chaseLag : 999;
        if (lag < 999) {
          FLY_SCRATCH_CAM_FWD.set(0, 0, -1).applyQuaternion(camera.quaternion);

          // Forward speed = projection of the ship's velocity onto the
          // camera's forward axis. Positive means thrusting forward.
          const forwardSpeed = flyVelocity.dot(FLY_SCRATCH_CAM_FWD);
          const sensitivity = (profile.chaseLagSensitivity != null)
            ? profile.chaseLagSensitivity
            : 0.18;
          // Lag cap derives from the SCALED follow distance (via
          // getCraftFollowDistance) — with the raw profile value, a
          // 6%-scale atmospheric rig got dragged backward by several
          // rig-lengths and the camera ended up at the ship's nose.
          const maxLag = getCraftFollowDistance(profile) * 0.45;
          // Build the target backward distance proportional to forward
          // speed, but cap so we never overtake the ship.
          const target = THREE.MathUtils.clamp(forwardSpeed * sensitivity, -maxLag * 0.4, maxLag);
          const alpha = 1 - Math.exp(-lag * delta);
          chaseLagForwardDistance += (target - chaseLagForwardDistance) * alpha;
          // Re-clamp every frame to the CURRENT maxLag. Without this, a
          // wheel scroll that shrinks followDistance (and thus maxLag)
          // faster than the lag lerp can catch up leaves a stale lag
          // value that's larger than the new followDistance forward
          // offset — the addScaledVector below then drives the ship
          // backward past the camera and out of view.
          chaseLagForwardDistance = THREE.MathUtils.clamp(
            chaseLagForwardDistance,
            -maxLag * 0.4,
            maxLag
          );
          // Pull the ship backward (against camera-forward) by the
          // smoothed lag distance.
          playerShip.position.addScaledVector(FLY_SCRATCH_CAM_FWD, -chaseLagForwardDistance);
        } else {
          chaseLagForwardDistance = 0;
        }

        // Bob (vertical) + sway (horizontal) — subtle sinusoidal
        // motion in the camera's local frame so it always reads
        // perpendicular to view, not pinned to world axes.
        if (profile.bobAmplitude || profile.swayAmplitude) {
          // Amplitudes scale with the atmospheric rig so the idle
          // motion stays subtle when the craft is at 6% size.
          const bob = (profile.bobAmplitude || 0) * Math.sin(time * 0.7) * atmoCraftScaleLive;
          const sway = (profile.swayAmplitude || 0) * Math.sin(time * 0.42) * atmoCraftScaleLive;
          FLY_SCRATCH_BOB.set(sway, bob, 0).applyQuaternion(camera.quaternion);
          playerShip.position.add(FLY_SCRATCH_BOB);
        }

        // Ground-proximity guard: the slot must never bury into the
        // displaced terrain at low atmospheric altitude.
        clampShipAboveTerrain();

        // Craft faces same direction as camera.
        playerShip.quaternion.copy(camera.quaternion);
        if (profile.basePitch) playerShip.rotateX(profile.basePitch);

        // Calculate target attitude from local velocity direction, not
        // raw speed. Reversing through zero should read as a controlled
        // lean, never a coin-flip.
        FLY_SCRATCH_CAMERA_INV_QUAT.copy(camera.quaternion).invert();
        const localVel = FLY_SCRATCH_LOCAL_VEL.copy(flyVelocity).applyQuaternion(FLY_SCRATCH_CAMERA_INV_QUAT);
        const prop = propulsionTypes[currentPropulsion] || propulsionTypes.ion;
        const speedNorm = THREE.MathUtils.clamp(localVel.length() / Math.max(prop.maxSpeed, 0.001), 0, 1);
        const attitude = FLY_SCRATCH_ATTITUDE.copy(localVel);
        if (speedNorm > 0.002) attitude.normalize();
        else attitude.set(0, 0, 0);
        const maxPitchTilt = profile.maxPitchTilt;
        const maxBankTilt = profile.maxBankTilt;
        // Velocity-direction attitude: ship tilts in the direction it's
        // actually drifting (strafe right → bank right, reverse → nose up).
        let velTiltX = THREE.MathUtils.clamp(attitude.z * maxPitchTilt * speedNorm, -maxPitchTilt, maxPitchTilt);
        let velTiltZ = THREE.MathUtils.clamp(-attitude.x * maxBankTilt * speedNorm, -maxBankTilt, maxBankTilt);

        // Steering-input bank. When the user is yawing (Q/E or
        // rotateLeft/Right), the ship banks INTO the turn like an
        // aircraft in a coordinated turn. When pitching (ArrowUp/Down),
        // a small nose-pitch reinforces the visual that the user is
        // actively steering. Layered ADDITIVELY on top of the velocity
        // attitude so a strafing + yawing combo banks harder.
        let steerYaw = 0;
        if (flyKeys.rotateLeft)  steerYaw -= 1;
        if (flyKeys.rotateRight) steerYaw += 1;
        let steerPitch = 0;
        if (flyKeys.rotateUp)   steerPitch += 1;
        if (flyKeys.rotateDown) steerPitch -= 1;
        // Bank magnitude proportional to maxBankTilt so it scales with
        // the craft profile (saucer gets a more aggressive bank than
        // the Enterprise). 0.85× max because pure steering shouldn't
        // saturate to the absolute limit, leaving headroom for the
        // additive velocity contribution.
        const steerBank = -steerYaw * maxBankTilt * 0.85;
        const steerNose = -steerPitch * maxPitchTilt * 0.5;
        const targetTiltX = THREE.MathUtils.clamp(velTiltX + steerNose, -maxPitchTilt * 1.6, maxPitchTilt * 1.6);
        const targetTiltZ = THREE.MathUtils.clamp(velTiltZ + steerBank, -maxBankTilt * 1.6, maxBankTilt * 1.6);

        // Critically-damped attitude interpolation avoids overshoot when
        // switching rapidly between forward and reverse thrust.
        const tiltAlpha = dampingFactor(delta, profile.attitudeDamping || 9);
        ufoTiltX += (targetTiltX - ufoTiltX) * tiltAlpha;
        ufoTiltZ += (targetTiltZ - ufoTiltZ) * tiltAlpha;
        ufoTiltVelX *= 0.72;
        ufoTiltVelZ *= 0.72;
        const tiltClampX = maxPitchTilt * 1.6;
        const tiltClampZ = maxBankTilt * 1.6;
        ufoTiltX = THREE.MathUtils.clamp(ufoTiltX + ufoTiltVelX, -tiltClampX, tiltClampX);
        ufoTiltZ = THREE.MathUtils.clamp(ufoTiltZ + ufoTiltVelZ, -tiltClampZ, tiltClampZ);

        // Apply smooth tilt
        playerShip.rotateX(ufoTiltX);
        playerShip.rotateZ(ufoTiltZ);

        if (profile.wobble) {
          // UFO wobble - slight instability while hovering.
          playerShip.rotateX(Math.sin(time * 1.2) * 0.015);
          playerShip.rotateZ(Math.sin(time * 0.9) * 0.01);
        }

        if (profile.hover) {
          // Subtle hover for the saucer only. A starship should hold a
          // clean chase line, especially during keyboard navigation.
          playerShip.position.y += Math.sin(time * 0.5) * 0.005 * atmoCraftScaleLive;
        }

        // Save position for orbit reference
        ufoWorldPos.copy(playerShip.position);

        // Reset orbit angles to current view
        targetOrbitAngle = 0;
        targetOrbitPitch = 0.3;
        cameraOrbitAngle = 0;
        cameraOrbitPitch = 0.3;
      }
    }
  }
  updateCraftFillLight();
  // Apparent-size lock for crafts that live at true world-scale (the
  // saucer at 737-size). Runs every frame after ship position is set
  // so the projected pixel-size stays at or above the configured
  // minimum regardless of camera distance.
  enforceShipMinApparentSize();
}

// Toggle orbit stabilization
function toggleOrbitStabilization() {
  const btn = document.getElementById('stabilize-orbit');

  if (orbitStabilized) {
    // Turn off stabilization
    clearOrbitStabilization();
  } else {
    // Find nearest celestial body to orbit
    const nearestBody = findNearestBody();
    if (nearestBody) {
      orbitStabilized = true;
      orbitTarget = nearestBody.object;
      orbitRadius = nearestBody.distance;

      // Calculate initial orbit angle
      const targetPos = new THREE.Vector3();
      orbitTarget.getWorldPosition(targetPos);
      const toCamera = camera.position.clone().sub(targetPos);
      orbitAngle = Math.atan2(toCamera.x, toCamera.z);

      if (btn) {
        btn.classList.add('active');
        btn.textContent = `Orbiting ${nearestBody.name}`;
      }
    }
  }
}

// Find nearest celestial body
function findNearestBody() {
  let nearest = null;
  let nearestDist = Infinity;
  const consider = (object, label, minimumDistance = 3) => {
    if (!object || object.visible === false) return;
    const objectPos = new THREE.Vector3();
    object.getWorldPosition(objectPos);
    const dist = camera.position.distanceTo(objectPos);
    const radius = object.userData && (object.userData.radius || object.userData.size || 1);
    const minDistance = Math.max(minimumDistance, radius * 6);
    if (dist < nearestDist && dist > minDistance) {
      nearestDist = dist;
      nearest = { object, distance: dist, name: label };
    }
  };

  // Check sun
  if (sun) {
    const sunPos = new THREE.Vector3();
    sun.getWorldPosition(sunPos);
    const dist = camera.position.distanceTo(sunPos);
    if (dist < nearestDist && dist > 50) { // Not too close
      nearestDist = dist;
      nearest = { object: sun, distance: dist, name: 'Sun' };
    }
  }

  // Check planets
  Object.entries(planets).forEach(([name, planet]) => {
    consider(planet, name, 10);
  });

  cometObjects.forEach(({ mesh }) => {
    consider(mesh, mesh.userData && mesh.userData.name ? mesh.userData.name : 'Comet', 4);
  });

  neoObjects.forEach(({ mesh }) => {
    consider(mesh, mesh.userData && mesh.userData.name ? mesh.userData.name : 'NEO', 4);
  });

  beltBodyObjects.forEach((group) => {
    consider(group, group.userData && group.userData.name ? group.userData.name : 'Belt body', 4);
  });

  satelliteObjects.forEach(({ mesh, isProbe }) => {
    consider(mesh, mesh.userData && mesh.userData.name ? mesh.userData.name : (isProbe ? 'Probe' : 'Satellite'), isProbe ? 4 : 3);
  });

  return nearest;
}

// Update orbit stabilization. Drives the camera around `orbitTarget`
// in a circular path on the XZ plane while keeping flyVelocity aligned
// with the tangent so the chase code (which positions the ship at
// camera + offset, with forward-velocity-based aliveness) reads the
// orbit as a smooth forward thrust. Net effect: the ship orbits the
// body, camera stays in chase view, both move tangentially.
const _orbitTargetPos = new THREE.Vector3();
function updateOrbitStabilization(delta) {
  if (!orbitStabilized || !orbitTarget) return;
  // While the user is hand-orbiting (middle-click) or parked, defer to
  // their input — autopilot would just fight them.
  if (isMiddleMouseDown || orbitParked) return;

  orbitTarget.getWorldPosition(_orbitTargetPos);

  // Keplerian-ish: closer orbits go faster.
  const orbitSpeed = 0.3 / Math.sqrt(Math.max(orbitRadius / 100, 0.1));
  orbitAngle += orbitSpeed * delta;

  // Desired camera position on the orbit ring.
  const desiredX = _orbitTargetPos.x + Math.sin(orbitAngle) * orbitRadius;
  const desiredZ = _orbitTargetPos.z + Math.cos(orbitAngle) * orbitRadius;
  const desiredY = _orbitTargetPos.y;

  // Strong, time-corrected damping so the autopilot wins decisively
  // over any leftover flyVelocity from the user's previous thrust
  // input. The previous 0.02-per-frame smoothing was so weak that the
  // camera barely moved toward the orbit slot — that's the "weird
  // thing" from the report.
  const posAlpha = 1 - Math.exp(-5 * delta);
  camera.position.x += (desiredX - camera.position.x) * posAlpha;
  camera.position.y += (desiredY - camera.position.y) * posAlpha;
  camera.position.z += (desiredZ - camera.position.z) * posAlpha;

  // Tangent direction of motion (CCW orbit) and the resulting yaw.
  const tangentX = Math.cos(orbitAngle);
  const tangentZ = -Math.sin(orbitAngle);
  const targetYaw = Math.atan2(tangentX, tangentZ);

  // Hand the chase camera a meaningful forward velocity so its
  // velocity-driven aliveness (lag, banking) reads correctly. flyVel
  // is interpreted as "units per (1/60) second" by updateFlyMovement,
  // so divide by 60 to convert from units-per-second.
  const orbitalSpeedUnitsPerSec = orbitSpeed * orbitRadius;
  flyVelocity.set(
    tangentX * orbitalSpeedUnitsPerSec / 60,
    0,
    tangentZ * orbitalSpeedUnitsPerSec / 60
  );

  // Yaw the camera toward the tangent direction along the shortest
  // angular path. flyEuler is read by mouse-look as well; level out
  // pitch and roll so the autopilot leaves a clean horizon.
  let yawDelta = targetYaw - flyEuler.y;
  while (yawDelta > Math.PI) yawDelta -= 2 * Math.PI;
  while (yawDelta < -Math.PI) yawDelta += 2 * Math.PI;
  const yawAlpha = 1 - Math.exp(-4 * delta);
  flyEuler.y += yawDelta * yawAlpha;
  // Time-corrected pitch decay (≈ ×0.92/frame at 60 Hz).
  flyEuler.x *= Math.exp(-5.0 * delta);
  flyEuler.z = 0;
  camera.quaternion.setFromEuler(flyEuler);
}

// Collision detection for fly mode
let isRespawning = false;

// Scratch — checkCollisions runs every frame for every body.
const FLY_SCRATCH_COLLIDE_POS = new THREE.Vector3();

function checkCollisions() {
  if (!flyMode || isRespawning) return;
  // The cinematic director flies a deliberate camera through the system
  // (close passes, sun reveals). It must be exempt from the crash/
  // incineration death + respawn, otherwise it dies and respawns every
  // pass — the "pulsing back into the sun / crashing into everything"
  // loop. It does its own sun-distance clamp instead.
  if (director.active) return;

  const camPos = camera.position;

  // "You crashed" is the LAST line of defence — enforceCollisionAvoidance
  // already keeps the ship outside each body's safety bubble in normal
  // flight, so this only fires when the camera somehow ended up inside
  // a body's actual mesh (teleport / autopilot bypass / scale change
  // mid-flight). Previously the planet trigger fired ~1.25 units above
  // Earth's surface in artistic mode (radius × scaleMultiplier × 0.5),
  // which made atmospheric flight impossible — the ship died before it
  // could ever reach the cloud tops. Now triggers only inside the body
  // itself (radius × 0.92), giving a small margin so the safe-bubble
  // glide doesn't double-fire the death overlay at the edge.
  if (sun) {
    const sunPos = FLY_SCRATCH_COLLIDE_POS;
    sun.getWorldPosition(sunPos);
    const distToSun = camPos.distanceTo(sunPos);
    // getBodyBaseRadius follows the realistic-scale mesh size, so the
    // death trigger hugs the visible surface in both scale modes.
    const sunRadius = getBodyBaseRadius(sun) * 0.95;
    if (distToSun < sunRadius) {
      triggerDeath('sun');
      return;
    }
  }

  Object.entries(planets).forEach(([name, planet]) => {
    if (!planet || planet.visible === false) return;
    const data = celestialData[name] || planet.userData;
    if (!data) return;

    const planetPos = FLY_SCRATCH_COLLIDE_POS;
    planet.getWorldPosition(planetPos);
    const distToPlanet = camPos.distanceTo(planetPos);
    // Realistic-scale aware (see getBodyBaseRadius) so the trigger
    // sits inside the rendered body, not the 12×-larger artistic one.
    const deathRadius = getBodyBaseRadius(planet) * 0.92;

    if (distToPlanet < deathRadius) {
      triggerDeath('planet', data.name || name);
    }
  });
}

function triggerDeath(type, name = '') {
  isRespawning = true;

  // Show death overlay
  const deathOverlay = document.getElementById('death-overlay');
  const deathMessage = document.getElementById('death-message');

  if (type === 'sun') {
    deathMessage.textContent = 'Incinerated by the Sun';
  } else {
    deathMessage.textContent = `Crashed into ${name}`;
  }

  deathOverlay.classList.add('active');

  // Reset after delay
  setTimeout(() => {
    respawnShip();
    deathOverlay.classList.remove('active');
    isRespawning = false;
  }, 2000);
}

function respawnShip() {
  // Reset to safe position (Earth orbit area)
  camera.position.set(200 * scaleMultiplier, 50 * scaleMultiplier, 200 * scaleMultiplier);
  camera.lookAt(0, 0, 0);
  flyEuler.setFromQuaternion(camera.quaternion);
  flyVelocity.set(0, 0, 0);
  flyAngularVelocity.set(0, 0);
}

// Create orbit lines with fat line rendering - NASA-style colored orbits
function createOrbitLines() {
  orbitLines = []; // Clear existing

  // Unique colors for each planet orbit (inspired by NASA Eyes)
  const orbitColors = {
    mercury: 0xb0b0b0,  // Silver gray
    venus: 0xffd700,    // Golden yellow
    earth: 0x4169e1,    // Royal blue
    mars: 0xff6347,     // Tomato red
    jupiter: 0xdaa520,  // Goldenrod
    saturn: 0xf4a460,   // Sandy brown
    uranus: 0x40e0d0,   // Turquoise
    neptune: 0x4169e1,  // Royal blue
    pluto: 0xdda0dd     // Plum
  };

  planetOrder.forEach((planetName) => {
    const data = celestialData[planetName];

    const SAMPLES = 512;
    const lineGeometry = new LineGeometry();
    const positions = [];

    if (SUPPORTED_PLANETS.includes(planetName)) {
      // Ephemeris path. Sample in true anomaly around the orbit and
      // rotate from the orbital plane into ecliptic via the standard
      // Rz(Ω)·Rx(i)·Rz(ω) sequence so the line includes longitude of
      // ascending node and longitude of perihelion, not just inclination.
      // That way each planet's orbit ring sits in its actual plane in
      // space and matches where the planet is drawn from ephemeris.
      const state = getOrbitState(planetName, simulationJD);
      const scale = realisticScaleMode ? AU_TO_SCENE : (data.distance / state.a);
      const cosO = Math.cos(state.longNode);
      const sinO = Math.sin(state.longNode);
      const cosI = Math.cos(state.i);
      const sinI = Math.sin(state.i);
      const cosW = Math.cos(state.argPeri);
      const sinW = Math.sin(state.argPeri);
      for (let k = 0; k <= SAMPLES; k++) {
        const theta = (k / SAMPLES) * 2 * Math.PI;
        const r = state.a * (1 - state.e * state.e) / (1 + state.e * Math.cos(theta));
        const xp = r * Math.cos(theta);
        const yp = r * Math.sin(theta);
        const xEcl =
          xp * (cosW * cosO - sinW * sinO * cosI) +
          yp * (-sinW * cosO - cosW * sinO * cosI);
        const yEcl =
          xp * (cosW * sinO + sinW * cosO * cosI) +
          yp * (-sinW * sinO + cosW * cosO * cosI);
        const zEcl = xp * (sinW * sinI) + yp * (cosW * sinI);
        // Ecliptic (x, y, z) → scene (x, z, -y) — must match the planet
        // position remap exactly or the body drifts off its line.
        positions.push(xEcl * scale, zEcl * scale, -yEcl * scale);
      }
    } else {
      // Legacy path for bodies not in the ephemeris table.
      const a = data.distance;
      const e = data.eccentricity || 0;
      const incl = data.orbitalInclination || 0;
      const cosI = Math.cos(incl);
      const sinI = Math.sin(incl);
      for (let k = 0; k <= SAMPLES; k++) {
        const theta = (k / SAMPLES) * 2 * Math.PI;
        const r = a * (1 - e * e) / (1 + e * Math.cos(theta));
        const x = Math.cos(theta) * r;
        const y = Math.sin(theta) * r * sinI;
        // -sin keeps the line under the body: orbits run counter-
        // clockwise seen from +Y, same sense as the ephemeris planets.
        const z = -Math.sin(theta) * r * cosI;
        positions.push(x, y, z);
      }
    }

    lineGeometry.setPositions(positions);

    const lineMaterial = new LineMaterial({
      color: orbitColors[planetName] || 0x6699cc,
      linewidth: 4, // Thicker lines
      transparent: true,
      opacity: 0.6,
      resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
      worldUnits: false,
      dashed: false,
      depthTest: true,
      depthWrite: false,
      toneMapped: false
    });

    const orbitLine = new Line2(lineGeometry, lineMaterial);
    orbitLine.computeLineDistances();
    orbitLine.visible = orbitsVisible; // Hidden by default
    orbitLine.renderOrder = -1; // Render behind planets
    orbitLine.userData.planet = planetName;
    scene.add(orbitLine);
    orbitLines.push(orbitLine); // Store reference for scaling
  });
}

// Tear down existing planet orbit lines and rebuild them. Called when
// the realistic/artistic toggle flips so the ring geometry matches the
// new radial scaling.
function rebuildOrbitLines() {
  orbitLines.forEach((line) => {
    scene.remove(line);
    if (line.geometry && line.geometry.dispose) line.geometry.dispose();
    if (line.material && line.material.dispose) line.material.dispose();
  });
  orbitLines = [];
  createOrbitLines();
}

// Create moon orbit line around a planet
function createMoonOrbitLine(parentPlanet, moonData) {
  const curve = new THREE.EllipseCurve(
    0, 0,
    moonData.distance, moonData.distance,
    0, 2 * Math.PI,
    false, 0
  );

  const points = curve.getPoints(128);

  // Create fat line for moon orbits too
  const lineGeometry = new LineGeometry();
  const positions = [];
  points.forEach(p => {
    positions.push(p.x, 0, p.y);
  });
  positions.push(points[0].x, 0, points[0].y);
  lineGeometry.setPositions(positions);

  // Moon orbit colors - lighter, more subtle
  const moonOrbitColor = moonData.colors ? new THREE.Color(moonData.colors[0]) : new THREE.Color(0x8888aa);

  const lineMaterial = new LineMaterial({
    color: moonOrbitColor,
    linewidth: 2,
    transparent: true,
    opacity: 0.4,
    resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
    worldUnits: false,
    depthTest: true,
    depthWrite: false,
    toneMapped: false
  });

  const orbitLine = new Line2(lineGeometry, lineMaterial);
  orbitLine.computeLineDistances();
  orbitLine.renderOrder = -2;
  orbitLine.visible = orbitsVisible; // Hidden by default
  orbitLine.userData.baseDistance = moonData.distance;
  orbitLine.userData.circlePositions = positions;
  orbitLine.userData.moonRef = null;
  // Same tilted frame as the moon itself so the guide ring sits in
  // the orbit plane (equatorial for everything except Earth).
  getMoonOrbitFrame(parentPlanet).add(orbitLine);

  if (!moonOrbitLines[parentPlanet.name]) {
    moonOrbitLines[parentPlanet.name] = [];
  }
  moonOrbitLines[parentPlanet.name].push(orbitLine);
}

function parseDistanceKm(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return null;

  const cleaned = value.replace(/,/g, '').trim();
  const match = cleaned.match(/^([\d.]+)\s*([MB])?/i);
  if (!match) return null;

  let amount = parseFloat(match[1]);
  if (!Number.isFinite(amount)) return null;

  const suffix = (match[2] || '').toUpperCase();
  if (suffix === 'M') amount *= 1e6;
  if (suffix === 'B') amount *= 1e9;
  return amount;
}

// Handle window resize
// Note on resize + pixelRatio: renderer.setSize takes CSS dimensions
// and multiplies internally by pixelRatio. composer.setSize also
// takes CSS dimensions and multiplies for its internal render
// targets. Passes that hold their own state (SMAA, FXAA, Bloom) get
// resized via composer.setSize too, but their uniforms/resolutions
// need to reflect the actual pixel-resolution, not CSS. We update
// FXAA's resolution uniform manually since it doesn't auto-scale.
function onWindowResize() {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  // The lineup's framing depends on the aspect; land on the stop again.
  if (scaleModeActive && scaleGroup) scaleGoTo(scaleIndex, 0);
  renderer.setSize(window.innerWidth, window.innerHeight);
  // Composer's internal render targets are sized in *pixels*, not CSS
  // units, so multiply by the renderer's pixel ratio — same as at init.
  // Also include the super-sampling multiplier so SSAA survives resize.
  const pr = renderer.getPixelRatio();
  const rs = Math.max(1, graphicsConfig.renderScale || 1);
  // composerW/H = physical pixels (for the texel-size uniforms below).
  // composer.setSize() re-multiplies by pixelRatio internally, so it gets
  // CSS dimensions — see the init site for why passing physical pixels
  // here double-applied the ratio and black-screened Chrome.
  const composerW = window.innerWidth * pr * rs;
  const composerH = window.innerHeight * pr * rs;
  composer.setSize(window.innerWidth * rs, window.innerHeight * rs);

  // FXAA's resolution uniform is the reciprocal pixel size. Walk the
  // composer passes and update any that need it — SMAAPass and
  // UnrealBloomPass have their own setSize called by composer.setSize
  // above, FXAA's ShaderPass doesn't, so patch it here.
  composer.passes.forEach((pass) => {
    if (pass.material && pass.material.uniforms && pass.material.uniforms.texelSize) {
      pass.material.uniforms.texelSize.value.set(1 / composerW, 1 / composerH);
    }
    if (pass.material && pass.material.uniforms && pass.material.uniforms.resolution
        && pass.material.fragmentShader && pass.material.fragmentShader.indexOf('FXAA') !== -1) {
      pass.material.uniforms.resolution.value.set(1 / composerW, 1 / composerH);
    }
  });

  // Update orbit line material resolution
  orbitLines.forEach((line) => {
    if (line.material && line.material.resolution) {
      line.material.resolution.set(window.innerWidth, window.innerHeight);
    }
  });

  // Update moon orbit line material resolution
  Object.values(moonOrbitLines).forEach((moonLines) => {
    moonLines.forEach((line) => {
      if (line.material && line.material.resolution) {
        line.material.resolution.set(window.innerWidth, window.innerHeight);
      }
    });
  });

  cometOrbitLines.forEach((line) => {
    if (line.material && line.material.resolution) {
      line.material.resolution.set(window.innerWidth, window.innerHeight);
    }
  });

  probePaths.forEach(({ line }) => line.material.resolution.set(window.innerWidth, window.innerHeight));
  if (jwstPath.line) jwstPath.line.material.resolution.set(window.innerWidth, window.innerHeight);
  interstellarPaths.forEach(({ line }) => line.material.resolution.set(window.innerWidth, window.innerHeight));
}

// Mouse move handler
// Rich-hover HUD state. The instant .hover-label still shows the
// hovered body's name with zero delay; this richer panel appears
// only after the cursor has dwelt on the SAME object without moving
// for HOVER_DWELL_MS — that's what makes the HUD intentional rather
// than a thing that flashes whenever the cursor crosses a planet.
const HOVER_DWELL_MS = 600;
let hoverDwellTimer = null;
let hoverDwellTarget = null;
let richHoverEl = null;

function ensureRichHoverEl() {
  if (richHoverEl) return richHoverEl;
  richHoverEl = document.createElement('div');
  richHoverEl.id = 'rich-hover-hud';
  richHoverEl.className = 'rich-hover-hud';
  document.body.appendChild(richHoverEl);
  return richHoverEl;
}

function dismissRichHover() {
  if (richHoverEl) richHoverEl.classList.remove('visible');
}

function escapeHovHtml(s) {
  if (typeof s !== 'string') return '';
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;'
  })[c]);
}

/**
 * Walk up the parent chain from an intersected mesh to identify which
 * celestial body it belongs to. Returns a normalised id like 'sun',
 * 'jupiter', 'moon-io', 'comet-halleys-comet', or null if nothing
 * recognised.
 */
function identifyHoveredBody(obj) {
  let cur = obj;
  while (cur) {
    if (cur === sun) return { kind: 'sun', id: 'sun' };
    for (const key in planets) {
      if (planets[key] === cur) return { kind: 'planet', id: key };
    }
    for (const planetName in moons) {
      const arr = moons[planetName];
      if (!arr) continue;
      for (const m of arr) {
        if (m === cur && m.userData && m.userData.name) {
          return { kind: 'moon', id: m.userData.name.toLowerCase(), parentPlanet: planetName, raw: m };
        }
      }
    }
    if (nibiruHandle && cur === nibiruHandle.group) return { kind: 'nibiru', id: 'nibiru' };
    if (cur.userData && cur.userData.bodyId === 'nibiru') return { kind: 'nibiru', id: 'nibiru' };
    cur = cur.parent;
  }
  // Direct-tag fallbacks for sprites, satellites, comets, NEOs — they
  // tend to be the intersected object itself rather than nested.
  if (obj && obj.userData) {
    if (obj.userData.isComet) return { kind: 'comet', id: (obj.userData.name || '').toLowerCase(), raw: obj };
    if (obj.userData.isNEO) return { kind: 'neo', id: (obj.userData.name || '').toLowerCase(), raw: obj };
    if (obj.userData.isSatellite || obj.userData.isProbe) {
      return { kind: 'satellite', id: (obj.userData.name || '').toLowerCase(), raw: obj };
    }
    if (obj.userData.isConstellation) {
      return { kind: 'constellation', id: (obj.userData.name || '').toLowerCase(), raw: obj };
    }
  }
  if (obj instanceof THREE.Sprite && obj.name) {
    return { kind: 'dso', id: obj.name.toLowerCase(), raw: obj };
  }
  return null;
}

function buildRichHoverHtml(target) {
  if (!target) return '';
  const stat = (label, value) =>
    `<span class="hud-stat-label">${escapeHovHtml(label)}</span><span class="hud-stat-value">${escapeHovHtml(value)}</span>`;

  if (target.kind === 'sun' || target.kind === 'planet') {
    const data = celestialData[target.id];
    if (!data) return '';
    const stats = [
      data.realDiameter && stat('Diameter', data.realDiameter),
      data.realDistance && stat('From Sun', data.realDistance),
      data.orbitalPeriod && stat('Orbit', data.orbitalPeriod),
      data.moons != null && stat('Moons', String(data.moons))
    ].filter(Boolean).join('');
    return `
      <p class="hud-title">${escapeHovHtml(data.name)}</p>
      <p class="hud-subtitle">${escapeHovHtml(data.type || '')}</p>
      ${data.description ? `<p class="hud-desc">${escapeHovHtml(data.description)}</p>` : ''}
      ${stats ? `<div class="hud-stats">${stats}</div>` : ''}
    `;
  }

  if (target.kind === 'moon' && target.parentPlanet) {
    const parent = celestialData[target.parentPlanet];
    if (!parent) return '';
    // Find the matching moon data — single .moon or majorMoons[].
    let moonData = null;
    if (parent.moon && parent.moon.name && parent.moon.name.toLowerCase() === target.id) {
      moonData = parent.moon;
    } else if (parent.majorMoons) {
      moonData = parent.majorMoons.find(m => m.name && m.name.toLowerCase() === target.id) || null;
    }
    if (!moonData) return '';
    const stats = [
      moonData.realDiameter && stat('Diameter', moonData.realDiameter),
      moonData.realDistance && stat('From ' + parent.name, moonData.realDistance),
      moonData.orbitalPeriod && stat('Orbit', moonData.orbitalPeriod)
    ].filter(Boolean).join('');
    return `
      <p class="hud-title">${escapeHovHtml(moonData.name)}</p>
      <p class="hud-subtitle">Moon · ${escapeHovHtml(parent.name)}</p>
      ${moonData.description ? `<p class="hud-desc">${escapeHovHtml(moonData.description)}</p>` : ''}
      ${stats ? `<div class="hud-stats">${stats}</div>` : ''}
    `;
  }

  if (target.kind === 'nibiru') {
    return `
      <p class="hud-title">Anomaly</p>
      <p class="hud-subtitle">Wanderer · trans-Neptunian</p>
      <p class="hud-desc">A wandering twelfth world, procedurally rendered with no texture. Period not catalogued.</p>
    `;
  }

  if (target.kind === 'constellation' && target.raw) {
    const ud = target.raw.userData || {};
    return `
      <p class="hud-title">${escapeHovHtml(ud.name || 'Constellation')}</p>
      <p class="hud-subtitle">Constellation</p>
      ${ud.description ? `<p class="hud-desc">${escapeHovHtml(ud.description)}</p>` : ''}
    `;
  }

  if (target.kind === 'dso' && target.raw) {
    const name = target.raw.name || (target.raw.userData && target.raw.userData.name);
    const desc = target.raw.userData && target.raw.userData.description;
    return `
      <p class="hud-title">${escapeHovHtml(name || 'Deep-sky object')}</p>
      <p class="hud-subtitle">Deep sky</p>
      ${desc ? `<p class="hud-desc">${escapeHovHtml(desc)}</p>` : ''}
    `;
  }

  // Comet / NEO / satellite — use the userData entries scoped on the
  // intersected mesh.
  if ((target.kind === 'comet' || target.kind === 'neo' || target.kind === 'satellite') && target.raw) {
    const ud = target.raw.userData || {};
    const subtitle = target.kind === 'comet' ? 'Comet'
      : target.kind === 'neo' ? 'Near-Earth Object'
      : (ud.isProbe ? 'Space probe' : 'Satellite');
    return `
      <p class="hud-title">${escapeHovHtml(ud.name || target.id)}</p>
      <p class="hud-subtitle">${subtitle}</p>
      ${ud.description ? `<p class="hud-desc">${escapeHovHtml(ud.description)}</p>` : ''}
    `;
  }

  return '';
}

function showRichHover() {
  if (!hoveredObject) return;
  const target = identifyHoveredBody(hoveredObject);
  if (!target) return;
  const html = buildRichHoverHtml(target);
  if (!html) return;
  const el = ensureRichHoverEl();
  el.innerHTML = html;
  // Position to the bottom-right of the cursor. Clamp against the
  // viewport edges so the panel doesn't disappear off-screen near
  // the right / bottom of the window.
  const offsetX = 18;
  const offsetY = 18;
  const W = window.innerWidth;
  const H = window.innerHeight;
  const PANEL_W = 290;
  const PANEL_H = 220;
  const x = Math.min(mouseScreenX + offsetX, W - PANEL_W - 8);
  const y = Math.min(mouseScreenY + offsetY, H - PANEL_H - 8);
  el.style.left = x + 'px';
  el.style.top = y + 'px';
  el.classList.add('visible');
}

// Body kinds the rich hover HUD is intentionally suppressed for.
// Primary bodies (sun, planets, moons) already have plenty of UI
// affordance (planet nav, click-to-focus, full info panel) and the
// hover card was reading as redundant noise when scanning the
// scene. The HUD only triggers for ambient / non-primary stuff:
// stars, constellations, deep-sky objects, comets, NEOs,
// satellites, and the secret Nibiru body — those have no other
// quick "what is that?" surface in the UI.
const RICH_HOVER_SUPPRESSED_KINDS = new Set(['sun', 'planet', 'moon']);

function updateHoverDwell() {
  // Same object as last frame: keep timer running, keep HUD if it's
  // already showing (also gently re-anchor its position to the new
  // cursor position so the panel follows the cursor a bit).
  if (hoveredObject === hoverDwellTarget) {
    if (richHoverEl && richHoverEl.classList.contains('visible')) {
      const offsetX = 18;
      const offsetY = 18;
      const W = window.innerWidth;
      const H = window.innerHeight;
      richHoverEl.style.left = Math.min(mouseScreenX + offsetX, W - 298) + 'px';
      richHoverEl.style.top = Math.min(mouseScreenY + offsetY, H - 228) + 'px';
    }
    return;
  }
  // Target changed (or none). Reset timer + hide rich HUD.
  if (hoverDwellTimer) {
    clearTimeout(hoverDwellTimer);
    hoverDwellTimer = null;
  }
  dismissRichHover();
  hoverDwellTarget = hoveredObject;
  if (!hoveredObject) return;
  // Identify what's under the cursor. Suppress the HUD entirely for
  // primary bodies — only schedule the dwell timer for ambient /
  // non-primary objects.
  const target = identifyHoveredBody(hoveredObject);
  if (!target || RICH_HOVER_SUPPRESSED_KINDS.has(target.kind)) return;
  hoverDwellTimer = setTimeout(() => {
    hoverDwellTimer = null;
    if (hoverDwellTarget === hoveredObject) {
      showRichHover();
    }
  }, HOVER_DWELL_MS);
}

function onMouseMove(event) {
  mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
  mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
  // Store screen coordinates for hover label positioning
  mouseScreenX = event.clientX;
  mouseScreenY = event.clientY;

  if (flyMode) {
    hoveredObject = null;
    document.body.style.cursor = '';
    cancelPendingPrefetch(true);
    if (hoverLabel) hoverLabel.classList.remove('visible');
    dismissRichHover();
    if (hoverDwellTimer) { clearTimeout(hoverDwellTimer); hoverDwellTimer = null; }
    hoverDwellTarget = null;
    return;
  }

  // GAME MODE: no website hover labels/raycasts — the intro/hangar overlay
  // the scene, and fly mode (the only time the scene is visible) returns
  // above. Mouse coords still update for the systems that need them.
  if (IS_GAME) return;

  // Defer the actual hover raycast to the animate loop. High-polling
  // mice deliver several mousemove events per rendered frame, and
  // each checkHover() is a scene raycast — running it per event
  // multiplied the cost for zero visual benefit. The flag collapses
  // any number of moves into one raycast per frame.
  hoverCheckPending = true;
}

// One-per-frame hover raycast flag — set by onMouseMove, consumed by
// the animate loop.
let hoverCheckPending = false;

// Shared clickable-object list for checkHover() and onMouseClick().
// Rebuilding via whole-scene traverse() on every mousemove/click is
// expensive and the two former copies had drifted apart; the list
// only changes when bodies are added/removed or the active system
// switches, so it's cached here and invalidated via
// markClickablesDirty() at those mutation points.
let clickableObjectsDirty = true;
const clickableObjectsCache = [];
const clickableObjectsScratch = [];

function markClickablesDirty() {
  clickableObjectsDirty = true;
}

function rebuildClickableObjectsCache() {
  const out = clickableObjectsCache;
  out.length = 0;

  // Add sun and planets
  [sun, ...Object.values(planets)].forEach(group => {
    if (!group) return;
    group.traverse((child) => {
      if (child instanceof THREE.Mesh &&
          (child.geometry instanceof THREE.SphereGeometry ||
           child.geometry instanceof THREE.IcosahedronGeometry)) {
        out.push(child);
      }
    });
  });

  // Add moons (skip tiny procedural minor moons so they don't intercept clicks)
  Object.values(moons).forEach(moonArray => {
    moonArray.forEach(moonGroup => {
      if (moonGroup.userData && moonGroup.userData.isMinorMoon) return;
      moonGroup.traverse((child) => {
        if (child instanceof THREE.Mesh && child.geometry instanceof THREE.SphereGeometry) {
          out.push(child);
        }
      });
    });
  });

  // Add comets (check the core mesh)
  cometObjects.forEach(({ mesh }) => {
    mesh.traverse((child) => {
      if (child instanceof THREE.Mesh &&
          (child.geometry instanceof THREE.SphereGeometry ||
           child.geometry instanceof THREE.IcosahedronGeometry)) {
        out.push(child);
      }
    });
  });

  // Add NEOs
  neoObjects.forEach(({ mesh }) => {
    if (mesh instanceof THREE.Mesh) {
      out.push(mesh);
    }
  });

  // Add named belt bodies — any mesh; the most permissive of the two
  // former copies (the click handler took all meshes, hover had a
  // geometry whitelist).
  beltBodyObjects.forEach((group) => {
    group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        out.push(child);
      }
    });
  });

  // Add satellites and probes (a probe hidden before its launch, or with
  // Deep space off, waits at the Earth and must not catch its clicks)
  satelliteObjects.forEach(({ mesh, isProbe }) => {
    if ((isProbe || mesh.userData.drawnLength) && !mesh.visible) return;
    mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        out.push(child);
      }
    });
  });

  // ʻOumuamua and 3I/ATLAS, while they are shown.
  interstellarEntries.forEach(({ mesh }) => {
    if (!mesh.visible) return;
    mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) out.push(child);
    });
  });

  if (wormholeObject) {
    wormholeObject.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        out.push(child);
      }
    });
  }

  // Remote-system bodies (only the active system; hidden systems
  // would still respond to raycasts because three.js doesn't honour
  // group.visible for descendants by default — explicit gate.)
  addRemoteSystemClickables(out);

  // Monolith — BoxGeometry, lives as a child of Jupiter so its world
  // matrix tracks Jupiter's orbit. The sphere/icosahedron filter above
  // skips it; add explicitly.
  if (monolith) out.push(monolith);
}

// Returns the cached body list plus the currently visible deep-sky
// sprites. The same scratch array is reused per call — consume it
// immediately, don't hold a reference across frames.
function getClickableObjects() {
  if (clickableObjectsDirty) {
    rebuildClickableObjectsCache();
    clickableObjectsDirty = false;
  }
  clickableObjectsScratch.length = 0;
  for (let i = 0; i < clickableObjectsCache.length; i++) {
    clickableObjectsScratch.push(clickableObjectsCache[i]);
  }
  // Deep-sky sprites (named nebulae, galaxies, clusters). These are
  // THREE.Sprite instances which the sphere-geometry filter above
  // skips; appended per call (not cached) because sprite visibility
  // can change without any body add/remove.
  if (deepSkyObjects && deepSkyObjects.group) {
    for (const sprite of deepSkyObjects.group.children) {
      if (sprite instanceof THREE.Sprite && sprite.visible) {
        clickableObjectsScratch.push(sprite);
      }
    }
  }
  return clickableObjectsScratch;
}

// Check for hover on celestial objects
function checkHover() {
  raycaster.setFromCamera(mouse, camera);

  // Get all clickable objects (sun, planets, moons, comets, NEOs,
  // satellites, remote systems, DSO sprites) — cached list.
  const clickableObjects = getClickableObjects();

  const celestialObject = pickCelestialAtMouse(clickableObjects);
  if (celestialObject) {
    hoveredObject = celestialObject;
    document.body.style.cursor = 'pointer';
    scheduleDetailPrefetch(celestialObject);

    // Show hover label. DSO sprites use sprite.name (the catalog
    // designation, e.g. "M31 Andromeda") since they don't carry
    // userData.name like the planetary bodies do.
    const labelText =
      (celestialObject.userData && celestialObject.userData.name)
      || celestialObject.name
      || null;
    if (hoverLabel && labelText) {
      hoverLabel.textContent = labelText;
      hoverLabel.classList.remove('hover-label--rich');
      hoverLabel.style.left = (mouseScreenX + 15) + 'px';
      hoverLabel.style.top = (mouseScreenY + 15) + 'px';
      hoverLabel.classList.add('visible');
    }
    return;
  }

  // Constellation hover. Only checked when the overlay is visible
  // (otherwise these invisible hit-spheres shouldn't intercept the
  // user's pointer). Runs after celestial-body raycast so a hovered
  // planet always wins; constellations are background-only.
  if (constellations && constellations.isVisible() && constellations.hitTargets.length) {
    const cIntersects = raycaster.intersectObjects(constellations.hitTargets, false);
    if (cIntersects.length > 0) {
      const hit = cIntersects[0].object;
      const ud = hit.userData;
      if (ud && ud.isConstellation) {
        hoveredObject = hit;
        document.body.style.cursor = 'pointer';
        if (hoverLabel) {
          hoverLabel.innerHTML =
            `<span class="hover-label__title">${ud.name}</span>` +
            `<span class="hover-label__desc">${ud.description}</span>`;
          hoverLabel.classList.add('hover-label--rich');
          hoverLabel.style.left = (mouseScreenX + 15) + 'px';
          hoverLabel.style.top = (mouseScreenY + 15) + 'px';
          hoverLabel.classList.add('visible');
        }
        return;
      }
    }
  }

  if (hoveredObject) {
    hoveredObject = null;
    document.body.style.cursor = 'default';
  }
  cancelPendingPrefetch(true);
  if (hoverLabel) {
    hoverLabel.classList.remove('visible');
    hoverLabel.classList.remove('hover-label--rich');
  }
}

// Find the planet/sun/moon/comet/NEO/satellite group from a clicked mesh
function findCelestialParent(object) {
  let current = object;
  while (current) {
    if (current.userData && (
      current.userData.isPlanet ||
      current.userData.isSun ||
      current.userData.isStar ||
      current.userData.isBlackHole ||
      current.userData.isMoon ||
      current.userData.isComet ||
      current.userData.isNEO ||
      current.userData.isBeltBody ||
      current.userData.isSatellite ||
      current.userData.isSpaceProbe ||
      current.userData.isDwarfPlanet ||
      current.userData.isWormhole ||
      current.userData.isMonolith
    )) {
      return current;
    }
    current = current.parent;
  }
  return null;
}

// Screen-space pick for the active remote system. Generated planets are
// often a pixel or less across from a viewing distance, so the ray misses
// them and lands on the star behind. Any body whose projected disc is
// within REMOTE_PICK_PX of the cursor, and in front of whatever the ray
// hit, is a candidate; the closest to the cursor wins.
const REMOTE_PICK_PX = 16;
const _remotePickPos = new THREE.Vector3();
const _remotePickNdc = new THREE.Vector3();
function pickRemoteBodyNearCursor(ndcX, ndcY, rayHitDistance = Infinity) {
  const handle = activeSystemId !== 'sol' ? remoteSystems[activeSystemId] : null;
  if (!handle || !camera) return null;
  const h = window.innerHeight;
  const halfW = window.innerWidth / 2, halfH = h / 2;
  const pxPerRad = halfH / Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
  let best = null;
  let bestMiss = REMOTE_PICK_PX;
  for (const id in handle.bodies) {
    const body = handle.bodies[id];
    if (!body || !body.visible) continue;
    body.getWorldPosition(_remotePickPos);
    const dist = _remotePickPos.distanceTo(camera.position);
    const radius = body.userData.radius || 1;
    if (dist - radius > rayHitDistance) continue; // behind what the ray hit
    _remotePickNdc.copy(_remotePickPos).project(camera);
    if (_remotePickNdc.z > 1) continue;
    const px = Math.hypot((_remotePickNdc.x - ndcX) * halfW, (_remotePickNdc.y - ndcY) * halfH);
    const miss = px - (radius / Math.max(dist, 1e-6)) * pxPerRad;
    if (miss < bestMiss) { bestMiss = miss; best = body; }
  }
  return best;
}

// The ray's own body wins unless it is a star and a smaller body sits
// under the cursor in front of it (the sub-pixel planet case).
function preferNearCursorBody(rayBody, nearBody) {
  if (!nearBody || rayBody === nearBody) return rayBody || nearBody;
  if (!rayBody) return nearBody;
  if (rayBody.userData && rayBody.userData.isStar && !(nearBody.userData && nearBody.userData.isStar)) return nearBody;
  return rayBody;
}

// Raycast pick shared by hover, orbit-mode click and fly-mode click.
function pickCelestialAtMouse(clickableObjects) {
  const intersects = raycaster.intersectObjects(clickableObjects);
  const nearBody = pickRemoteBodyNearCursor(mouse.x, mouse.y, intersects.length ? intersects[0].distance : Infinity);
  return preferNearCursorBody(intersects.length ? chooseCelestialIntersection(intersects) : null, nearBody);
}

function chooseCelestialIntersection(intersects) {
  if (!intersects.length) return null;

  const first = intersects[0];
  const firstObject = findCelestialParent(first.object);
  const satelliteCandidate = intersects
    .map((hit) => ({ hit, object: findCelestialParent(hit.object) }))
    .find(({ hit, object }) => {
      if (!object || !object.userData || !object.userData.isSatellite) return false;
      const pickSlack = Math.max(object.userData.selectionRadius || 0, object.userData.radius || 0, 0.75);
      return hit.distance <= first.distance + pickSlack;
    });

  if (satelliteCandidate) return satelliteCandidate.object;
  if (firstObject) return firstObject;

  // Deep-sky sprite — has no celestial-parent userData flags but is
  // still a legitimate hover/click target. Return it directly so the
  // hover label and click dispatch can act on the sprite itself.
  if (first.object instanceof THREE.Sprite
      && deepSkyObjects
      && deepSkyObjects.group
      && first.object.parent === deepSkyObjects.group) {
    return first.object;
  }
  return null;
}

// Mouse click handler
function onMouseClick(event) {
  // GAME MODE: the shell's intro/hangar overlays own the pointer and fly
  // mode handles its own input — never raycast-pick the website scene.
  // Without this, a click on the hangar canvas (not a button, so it passes
  // isScenePickEvent) hits the paused, hidden solar system underneath and
  // can focus a planet / rewrite the URL hash behind the game.
  if (IS_GAME) return;
  if (flyMode) return;
  // The raycast ignores visibility, so a click in To-Scale picked the
  // hidden Sun or Mercury behind the lineup and flew the camera off.
  if (scaleModeActive) return;
  if (isAnimating) return;
  if (!isScenePickEvent(event)) return;
  if (sceneClickMoved(event)) return;

  mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
  mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
  raycaster.setFromCamera(mouse, camera);

  // Same cached clickable list as checkHover() — see
  // getClickableObjects() for what's included.
  const clickableObjects = getClickableObjects();

  const celestialObject = pickCelestialAtMouse(clickableObjects);
  if (celestialObject) {
    // First-click TMA-1 cinematic — Kubrick-style fade-to-black
    // discovery message the first time the user opens the monolith.
    // Fires before the focus animation so the caption reads while
    // the camera glides toward the slab.
    if (celestialObject.userData && celestialObject.userData.isMonolith && !monolithDiscovered) {
      monolithDiscovered = true;
      showMonolithDiscovery();
    }
    // DSO sprites get the dedicated telescope focus path; everything
    // else uses the standard body-focus pipeline.
    if (celestialObject instanceof THREE.Sprite
        && deepSkyObjects
        && deepSkyObjects.group
        && celestialObject.parent === deepSkyObjects.group) {
      focusOnDSO(celestialObject, { interrupt: true, via: 'click' });
    } else {
      focusOnObject(celestialObject, { via: 'click' });
    }
  }
}

function getSceneObjectId(object) {
  if (!object) return '';
  // Prefer the catalog body id (set by builder.js for remote-system
  // bodies) so URL hashes + matryoshka lookups stay slugged ('alpha-ab'
  // instead of 'alpha centauri ab'). Fall back to object.name for
  // legacy Sol bodies where the id is already the lowercase name.
  if (object.userData?.bodyId) return String(object.userData.bodyId).toLowerCase();
  return String(object.name || object.userData?.name || '').toLowerCase();
}

function getHashFocusId() {
  const raw = (window.location.hash || '').replace(/^#\/?/, '');
  const [root] = raw.split('/').filter(Boolean);
  if (!root) return '';
  try {
    return decodeURIComponent(root).toLowerCase();
  } catch (err) {
    return root.toLowerCase();
  }
}

// The breadcrumb src/reloadwatch.js keeps for the viewer_reload event: where
// the page is, its preset and pixel ratio, and its own tally of texture
// memory (the maps it holds plus the Earth's imagery atlas while in use).
function reloadWatchState() {
  const mode = rocketryActive ? 'rocketry' : flyMode ? 'fly' : (cupolaView && cupolaView.active) ? 'cupola'
    : scaleModeActive ? 'toscale' : famousPhotoMode ? 'photo' : activeSystemId !== 'sol' ? activeSystemId
    : realisticScaleMode ? 'true' : 'art';
  let bytes = 0;
  const seen = new Set();
  for (const texture of Object.values(loadedTextures)) {
    const image = texture && texture.image;
    if (!image || seen.has(texture)) continue;
    seen.add(texture);
    const w = image.naturalWidth || image.width || 0, h = image.naturalHeight || image.height || 0;
    bytes += w * h * 4 * (texture.generateMipmaps ? 4 / 3 : 1);
  }
  const imagery = window.__earthImagery && window.__earthImagery.stats;
  if (imagery && imagery.resident > 0) bytes += imagery.atlasBytes || 0;
  const canvas = renderer && renderer.domElement;
  return {
    step: `${selectedObject ? getSceneObjectId(selectedObject) || 'body' : 'overview'}:${mode}`,
    preset: activeGraphicsPreset,
    dpr: +(window.devicePixelRatio || 1).toFixed(2),
    pr: renderer ? +renderer.getPixelRatio().toFixed(2) : 0,
    cw: canvas ? canvas.width : 0,
    ch: canvas ? canvas.height : 0,
    textures: renderer ? renderer.info.memory.textures : 0,
    texMB: Math.round(bytes / 1048576),
    heapMB: performance.memory ? Math.round(performance.memory.usedJSHeapSize / 1048576) : 0,
  };
}

// The view a link can carry (src/ui/viewlink.js): the focused body (the Sun
// with nothing focused), the moment, the clock's speed, the scale, the
// overlays on and the camera from the body. Only in our own solar system.
function getViewState() {
  if (activeSystemId && activeSystemId !== 'sol') return null;
  const body = selectedObject || sun;
  if (!body || !camera) return null;
  const centre = body.getWorldPosition(new THREE.Vector3());
  const cam = cameraToSpherical(camera.position.clone().sub(centre), getBodyBaseRadius(body));
  return {
    body: selectedObject ? getSceneObjectId(selectedObject) : 'sun',
    ms: (simulationJD - 2440587.5) * 86400000,
    speed: timePreset === 'real' ? 'real' : timeSpeed,
    realistic: realisticScaleMode,
    spread: realisticScaleMode ? null : scaleMultiplier,
    cam,
    overlays: readViewOverlays(),
  };
}

// The overlays switched on, as view link keys (sat, orb, con, sci).
function readViewOverlays() {
  return Object.keys(VIEW_OVERLAYS).filter((key) => {
    const btn = document.getElementById(VIEW_OVERLAYS[key]);
    return !!(btn && btn.classList.contains('playing'));
  });
}

// The parts of a view shared by view links (applyViewLink) and lesson scenes
// (applyLessonScene): the scale and spread, the clock, the overlays and the
// body a view is of.
function applyViewScale(realistic, spread) {
  if (realistic != null && realistic !== realisticScaleMode && window.__setRealisticScaleMode) {
    window.__setRealisticScaleMode(realistic, { instant: true });
  }
  if (!realisticScaleMode && spread && Math.abs(spread - scaleMultiplier) > 1e-3) {
    scaleMultiplier = Math.min(SCALE_MAX, Math.max(SCALE_MIN, spread));
    lastScalePos = scaleToSlider(scaleMultiplier);
    scaleSlider.value = lastScalePos;
    scaleValue.textContent = formatScale(scaleMultiplier);
    updateScale();
  }
}

// speed: 'real' (real pace from jd) or a timeSpeed multiplier.
function applyViewClock(jd, speed) {
  simulationJD = jd;
  realClockLockedJD = null;
  if (speed === 'real') {
    timeReverse = false;
    timePreset = 'real';
    timeSpeed = timePresetSpeed('real');
  } else {
    timeReverse = speed < 0;
    timePreset = null;
    timeSpeed = speed;
  }
  syncTimeControls();
}

function applyViewOverlays(keys) {
  for (const [key, id] of Object.entries(VIEW_OVERLAYS)) {
    const btn = document.getElementById(id);
    if (btn && btn.classList.contains('playing') !== keys.includes(key)) btn.click();
  }
}

// The scene object for a view's body id, building deep space when the body
// lives there (the wormhole, comets, probes, dwarf planets), or null.
function resolveViewBody(id) {
  const key = String(id || '').toLowerCase();
  if (!key) return null;
  let target = key === 'sun' ? sun : planets[key] || findLoadedSceneObjectByName(key);
  if (!target && !deepSpaceBodiesLoaded && getBody(key)) {
    ensureDeepSpaceBodies();
    target = findLoadedSceneObjectByName(key);
  }
  return target || null;
}

// Runs fn once the frame loop itself has run twice (it may not have started
// yet while the page loads): a camera placed before the planet pass has moved
// the bodies to a new moment and scale is left behind when they jump.
function afterBodiesPlaced(fn) {
  const startFrame = frameCount;
  const place = () => {
    if (frameCount < startFrame + 2) { requestAnimationFrame(place); return; }
    fn();
  };
  requestAnimationFrame(place);
}

// Opens a view link: the scale first (it moves every body), then the clock
// and the overlays, then the camera, two frames on, once the planet pass has
// put the bodies where they are at that moment and scale.
function applyViewLink(view) {
  if (!view) return;
  if (eclipseModeActive()) eclipseMode.exit();
  applyViewScale(view.realistic, view.spread);
  // Real pace from the linked moment.
  applyViewClock(dateToJD(view.ms), view.speed);
  applyViewOverlays(view.overlays);
  const target = resolveViewBody(view.body) || sun;
  afterBodiesPlaced(() => {
    const offset = view.cam ? new THREE.Vector3().copy(sphericalToCamera(view.cam, getBodyBaseRadius(target))) : undefined;
    focusOnObject(target, { interrupt: true, force: true, duration: 0.05, offset });
  });
}

// === Lessons (teachers mode) ===
// The viewer side of src/lessons (docs/lessons.md): a teacher presents a
// lesson on a projector, or a pupil works through it on their own device.
// The lesson modules load on first use (lessons()) and reach the scene only
// through the driver below (createLessonViewer), so all they need of the
// viewer is in this block. A scene leaves the other modes first and refuses
// while flying, in rocketry, in XR or in another star system.

let lessonChrome = null;            // 'present' | 'pupil' while a lesson runs
let lessonsLoader = null;
let lessonViewer = null;
let lessonAxis = null;
// A scene's named camera frame, kept as the bodies move (the Moon round the
// Earth with the clock running) until the camera is dragged. The follow code
// would otherwise carry the camera along with the body and lose the frame.
let lessonHold = null;
let lessonSerial = 0;               // bumps on every applyScene
// While a scene shows an Events panel scene, the moons stay where it staged
// them.
let lessonHoldsMoons = false;
let lessonApplying = false;
let lessonFrameHook = null;
const lessonFrameFns = new Set();
// The part of the window the lesson's own panels leave free (the
// presenter's caption, the pupil's coach), as insets in CSS pixels, set by
// the lesson UI through setSafeArea.
let lessonSafeArea = { top: 0, right: 0, bottom: 0, left: 0 };
// The polar limits while a lesson runs: the viewer's usual 27 degree cap at
// each pole would hold 'above-north' well off the ecliptic pole.
const LESSON_POLAR_MIN = 0.02;
// The named frames as [azimuth, elevation] in degrees: the azimuth turns from
// the Sun's direction the way that leaves the Sun on the left of the picture,
// the elevation rises above the ecliptic.
const LESSON_FRAMES = {
  'from-sun': [0, 8],
  'night-side': [165, 10],
  // Down on the northern night side, the Sun hidden behind the body and out
  // of the frame: the aurora's oval in the dark.
  'night-north': [180, 50],
  // Square on to the Sun line, so the terminator is edge-on down the middle.
  'sun-side': [90, 15],
  'side-on': [90, 2],
  'above-north': [90, 84],
};
const _lessonSunPos = new THREE.Vector3();
const _lessonEarthPos = new THREE.Vector3();
const _lessonS = new THREE.Vector3();
const _lessonH = new THREE.Vector3();
const _lessonW = new THREE.Vector3();
const _lessonN = new THREE.Vector3(0, 1, 0);
const _lessonAnchor = new THREE.Vector3();
const _lessonDir = new THREE.Vector3();
const _lessonNdc = new THREE.Vector3();
const _lessonTmp = new THREE.Vector3();
const _lessonTmp2 = new THREE.Vector3();

function lessonActive() {
  return lessonChrome !== null;
}

function lessons() {
  if (!lessonsLoader) {
    lessonsLoader = import('./lessons/index.js')
      .then((m) => m.createLessons(createLessonViewer()))
      .catch((err) => { lessonsLoader = null; throw err; });
  }
  return lessonsLoader;
}

// ?lesson=<id>[&present=1][&step=n][&class=1] opens a lesson once the loading
// screen has gone. Its state lives in the query string, since focusing a body
// rewrites the hash.
function startLessonFromUrl() {
  if (IS_SCREENSAVER || IS_GAME) return;
  let id = null;
  try { id = new URLSearchParams(window.location.search).get('lesson'); } catch { /* malformed */ }
  if (!id) return;
  lessons().then((l) => l.startFromUrl(window.location.search)).catch((err) => console.error('[lessons] could not start', err));
}

// The unit direction a named frame puts the camera in from anchorPos, from
// the Sun's direction and the ecliptic (+Y north), so it means the same in
// either scale. For the Sun itself (and the overview) the Earth's direction
// stands in for the Sun's. 'from-earth' looks from the Earth's side.
function lessonFrameDirection(frame, anchorPos, out = new THREE.Vector3()) {
  const sunPos = sun ? sun.getWorldPosition(_lessonSunPos) : _lessonSunPos.set(0, 0, 0);
  const earthPos = planets.earth ? planets.earth.getWorldPosition(_lessonEarthPos) : null;
  if (frame === 'from-earth' && earthPos) {
    out.copy(earthPos).sub(anchorPos);
    if (out.lengthSq() > 1e-12) return out.normalize();
  }
  const s = _lessonS.copy(sunPos).sub(anchorPos);
  if (s.lengthSq() < 1e-10 && earthPos) s.copy(earthPos).sub(anchorPos);
  const h = _lessonH.copy(s).setY(0);
  if (h.lengthSq() < 1e-12) h.set(1, 0, 0);
  h.normalize();
  // A quarter turn from the Sun line in the ecliptic, the side that puts the
  // Sun on the left of the picture.
  const w = _lessonW.crossVectors(_lessonN, h).normalize();
  const [az, el] = LESSON_FRAMES[frame] || LESSON_FRAMES['from-sun'];
  const a = THREE.MathUtils.degToRad(az);
  const e = THREE.MathUtils.degToRad(el);
  return out.copy(h).multiplyScalar(Math.cos(a) * Math.cos(e))
    .addScaledVector(w, Math.sin(a) * Math.cos(e))
    .addScaledVector(_lessonN, Math.sin(e))
    .normalize();
}

// The free area as the camera sees it: the tangents of its half-angles, and
// where its middle is from the window's middle (fractions of the window, +x
// right, +y up).
function lessonSafeView() {
  const W = window.innerWidth;
  const H = window.innerHeight;
  const a = lessonSafeArea;
  const w = Math.max(W - a.left - a.right, W * 0.3);
  const h = Math.max(H - a.top - a.bottom, H * 0.3);
  const tanV = Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
  return {
    tanFull: tanV,
    tanV: tanV * (h / H),
    tanH: tanV * camera.aspect * (w / W),
    x: (Math.min(a.left, W * 0.7) + w / 2 - W / 2) / W,
    y: (H / 2 - (Math.min(a.top, H * 0.7) + h / 2)) / H,
  };
}

// How far out a circle of this radius fills the free area with a margin.
function lessonFitDistance(radius) {
  const view = lessonSafeView();
  return radius * 1.2 / Math.min(view.tanV, view.tanH);
}

// A distance in body radii is written for a wide window. A narrow one (a
// phone held upright, its lower half under the coach) moves the camera back
// until the body fits the free area as it would three quarters of a full
// height.
function lessonDistanceScale() {
  const view = lessonSafeView();
  return Math.max(1, (0.75 * view.tanFull) / Math.min(view.tanV, view.tanH));
}

// Resolves once the camera has stopped moving (or the scene was overtaken by
// another, or a flight never ends): true when it landed for this scene.
function lessonWaitLanded(serial, timeoutMs = 15000) {
  return new Promise((resolve) => {
    const t0 = performance.now();
    let still = 0;
    const tick = () => {
      if (serial !== lessonSerial) { resolve(false); return; }
      const moving = isAnimating || !!scaleTween || (scaleModeActive && !scaleGroup);
      still = moving ? 0 : still + 1;
      if (still >= 2 || performance.now() - t0 > timeoutMs) { resolve(serial === lessonSerial); return; }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

function lessonFrames(n) {
  return new Promise((resolve) => {
    let left = n;
    const tick = () => { if (--left <= 0) resolve(); else requestAnimationFrame(tick); };
    requestAnimationFrame(tick);
  });
}

function relaxLessonPolarLimits() {
  controls.minPolarAngle = LESSON_POLAR_MIN;
  controls.maxPolarAngle = Math.PI - LESSON_POLAR_MIN;
}

// Out of everything a scene is not: the documentary, photo mode, the
// showreel, a deep-sky telescope view, To Scale and eclipse mode unless the
// scene asks for them, the open panels. The ISS's tracking ends with its
// focus.
function leaveModesForLesson(scene) {
  if (documentaryActive) exitDocumentary({ returnHome: false });
  if (photoModeActive) exitPhotoMode();
  if (coolWorldsShowreelActive) clearCoolWorldsShowreel();
  if (dsoFocusActive) disableDsoZoomMode();
  const mode = scene.mode || {};
  if (scaleModeActive && mode.scale == null) exitScaleMode();
  if (eclipseModeActive() && (!mode.eclipse || !eclipseMode.event || eclipseMode.event.id !== mode.eclipse)) eclipseMode.exit();
  closeTransientPanels();
  controls.autoRotate = false;
}

function setLessonRate(rate) {
  if (rate === 'real') {
    // Real pace from the moment on the clock.
    applyViewClock(simulationJD, 'real');
    return;
  }
  const r = Number(rate) || 0;
  applyViewClock(simulationJD, r / (realisticScaleMode ? 1 : ARTISTIC_TIME_RATE));
}

function setLessonJD(jd) {
  if (!Number.isFinite(jd)) return;
  simulationJD = jd;
  realClockLockedJD = null;
  // A spin held back by a fast clock is paid back over seconds; a lesson's
  // new moment shows the right meridian at once.
  earthSpinLag = 0;
}

// The Earth's spin axis through the poles, a child of its tiltGroup so it
// tilts with it and scales with it in either scale. Built once.
function setLessonEarthAxis(on) {
  const tilt = planets.earth && planets.earth.getObjectByName('tiltGroup');
  if (!tilt) return false;
  if (on && !lessonAxis) {
    const r = celestialData.earth.radius;
    const material = new THREE.MeshBasicMaterial({ color: 0xf0c27a });
    // Long enough to clear the poles, short enough to stay in a picture
    // framed four radii out.
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.014, r * 0.014, r * 2.6, 12, 1), material);
    // The north end carries a point, so the class can tell which pole leans
    // toward the Sun.
    const tip = new THREE.Mesh(new THREE.ConeGeometry(r * 0.05, r * 0.16, 18), material);
    tip.position.y = r * 1.3 + r * 0.08;
    lessonAxis = new THREE.Group();
    lessonAxis.name = 'lessonEarthAxis';
    for (const part of [shaft, tip]) {
      // Not a pick target: a click on the axis is a click on the scene.
      part.raycast = () => {};
      lessonAxis.add(part);
    }
    tilt.add(lessonAxis);
  }
  if (lessonAxis) lessonAxis.visible = Boolean(on);
  return true;
}

// A scene's magnetosphere: on with the lesson's own framing (no Frame it and
// no card, which the lesson's panels would sit over), the solar wind held
// when the scene gives one, off for a scene without it. When the lesson
// ends it goes off again if the lesson turned it on.
let lessonMagnetosphere = null;
let lessonMagnetosphereOwned = false;
function setLessonMagnetosphere(spec) {
  lessonMagnetosphere = spec || null;
  if (!spec) {
    if (magnetosphereMode) {
      magnetosphereMode.hold(null);
      if (magnetosphereMode.enabled) magnetosphereMode.setEnabled(false);
    }
    lessonMagnetosphereOwned = false;
    return;
  }
  if (!(magnetosphereMode && magnetosphereMode.enabled)) lessonMagnetosphereOwned = true;
  loadMagnetosphere().then((m) => {
    if (!m || lessonMagnetosphere !== spec) return;
    m.hold(spec.hold || null);
    m.setEnabled(true, { frame: false, card: false });
  });
}

// Body classes for the lesson chrome (index.html hides the viewer's own
// panels under them). null ends the lesson's hold on the viewer: the axis,
// the held frame and the relaxed pole limits go, To Scale and eclipse mode
// close and the clock goes back to the live sky.
function setLessonChrome(mode) {
  const next = mode === 'present' || mode === 'pupil' ? mode : null;
  const was = lessonChrome;
  lessonChrome = next;
  document.body.classList.toggle('lesson-mode', Boolean(next));
  document.body.classList.toggle('lesson-present', next === 'present');
  document.body.classList.toggle('lesson-pupil', next === 'pupil');
  if (next) {
    closeTransientPanels();
    return;
  }
  if (!was) return;
  lessonSerial++;
  lessonHold = null;
  lessonHoldsMoons = false;
  lessonSafeArea = { top: 0, right: 0, bottom: 0, left: 0 };
  setLessonEarthAxis(false);
  if (magnetosphereMode) magnetosphereMode.hold(null);
  if (lessonMagnetosphereOwned && magnetosphereMode && magnetosphereMode.enabled) magnetosphereMode.setEnabled(false);
  lessonMagnetosphere = null;
  lessonMagnetosphereOwned = false;
  if (scaleModeActive) exitScaleMode();
  if (eclipseModeActive()) eclipseMode.exit();
  if (!isIssObject(selectedObject)) {
    controls.minPolarAngle = Math.PI * 0.15;
    controls.maxPolarAngle = Math.PI * 0.85;
  }
  setTimePreset('real');
}

// A lesson's body id (docs/lessons.md, "Levels": the focusable ids) as the
// scene object: what a view link finds, except for the two the lessons name
// as the Objects browser shows them. 'milky-way' is the galaxy itself (the
// name lookup finds the invisible anchor the autopilot flies to, whose
// framing is millions of units off the disc), and 'black hole' the stellar
// black hole. The ISS ('iss') and every named moon resolve as they are.
function resolveLessonBody(id) {
  const key = String(id || '').toLowerCase();
  if (key === 'milky-way') return (galaxy && galaxy.object) || resolveViewBody(key);
  if (key === 'black hole') return (blackHole && blackHole.object) || null;
  return resolveViewBody(key);
}

// The other way: the id a lesson knows a scene object by, so a task's focus
// check sees the galaxy and the black hole under the names it was given
// (getSceneObjectId has them as their meshes' names).
function lessonBodyId(object) {
  if (!object) return null;
  if ((galaxy && object === galaxy.object) || (milkyWayVista && object === milkyWayVista)) return 'milky-way';
  if (blackHole && object === blackHole.object) return 'black hole';
  return getSceneObjectId(object);
}

function lessonSnapshot() {
  return {
    jd: simulationJD,
    realistic: realisticScaleMode,
    spread: realisticScaleMode ? null : scaleMultiplier,
    focusId: lessonBodyId(selectedObject),
    // The focused body's own name ('Io', 'ISS', 'Milky Way'), for the coach.
    focusName: selectedObject ? String((selectedObject.userData && selectedObject.userData.name) || selectedObject.name || '') || null : null,
    paused: timeSpeed === 0,
    // Sim seconds per real second, in either scale.
    rate: timeSpeed * (realisticScaleMode ? 1 : ARTISTIC_TIME_RATE),
    real: timePreset === 'real',
    overlays: readViewOverlays(),
    magnetosphere: Boolean(magnetosphereMode && magnetosphereMode.enabled),
    scaleIndex: scaleModeActive ? scaleIndex : null,
    eclipseId: eclipseModeActive() && eclipseMode.event ? eclipseMode.event.id : null,
    busy: lessonApplying || isAnimating || !!scaleTween,
    blocked: lessonBlockedReason(),
  };
}

function lessonBlockedReason() {
  if (flyMode) return 'fly';
  if (rocketryActive || rocketryLoading) return 'rocketry';
  if (renderer && renderer.xr && renderer.xr.isPresenting) return 'xr';
  if (activeSystemId && activeSystemId !== 'sol') return 'system';
  if (wormholeTunnelInFlight || starMapJumpInFlight) return 'jump';
  return null;
}

// The world point a label stands on: { point, normal (outward, for points on
// a surface), body, radius (of the body's disc, for a body label) }.
function lessonTargetPoint(target) {
  if (!target) return null;
  if (target.subsolar || target.antisolar) {
    const named = target.body ? resolveLessonBody(target.body) : null;
    const body = named || (selectedObject && selectedObject !== sun && selectedObject.userData && (selectedObject.userData.isPlanet || selectedObject.userData.isMoon) ? selectedObject : planets.earth);
    if (!body || !sun) return null;
    const centre = body.getWorldPosition(new THREE.Vector3());
    const normal = sun.getWorldPosition(new THREE.Vector3()).sub(centre).normalize();
    if (target.antisolar) normal.negate();
    return { point: centre.addScaledVector(normal, getBodyBaseRadius(body)), normal, body, radius: 0 };
  }
  if (target.body) {
    const body = resolveLessonBody(target.body);
    if (!body) return null;
    return { point: body.getWorldPosition(new THREE.Vector3()), normal: null, body, radius: getBodyBaseRadius(body) };
  }
  if (Number.isFinite(target.lat) && Number.isFinite(target.lon)) {
    const mesh = getEarthMeshForTerrain();
    if (!mesh || !planets.earth) return null;
    mesh.updateWorldMatrix(true, false);
    const local = earthSurfaceDirection(target.lat, target.lon, new THREE.Vector3()).multiplyScalar(mesh.geometry.parameters.radius);
    const point = mesh.localToWorld(local);
    const normal = point.clone().sub(planets.earth.getWorldPosition(_lessonTmp)).normalize();
    return { point, normal, body: planets.earth, radius: 0 };
  }
  return null;
}

// Whether the line from the camera to point passes through the Sun, the
// Earth, the Moon or the focused body (other than the one it stands on).
function lessonPointOccluded(point, own) {
  const moon = findMoon('earth', 'Moon');
  const span = _lessonTmp.copy(point).sub(camera.position);
  const len2 = span.lengthSq();
  if (len2 < 1e-18) return false;
  for (const body of [sun, planets.earth, moon, selectedObject]) {
    if (!body || body === own || !body.visible) continue;
    const centre = body.getWorldPosition(_lessonTmp2);
    const t = THREE.MathUtils.clamp(centre.clone().sub(camera.position).dot(span) / len2, 0, 1);
    const r = getBodyBaseRadius(body);
    if (t > 0 && t < 1 && camera.position.clone().addScaledVector(span, t).distanceTo(centre) < r * 0.98) return true;
  }
  return false;
}

// Where a label's target is on screen, in CSS pixels: { x, y, visible, r },
// r the body's disc radius there (0 for a point on a surface). Same maths as
// the fly HUD's target bracket (updateFlyTargetHud).
function projectLessonTarget(target) {
  const hidden = { x: 0, y: 0, visible: false, r: 0 };
  if (!camera || scaleModeActive || flyMode || rocketryActive) return hidden;
  const p = lessonTargetPoint(target);
  if (!p) return hidden;
  camera.updateMatrixWorld();
  const ndc = _lessonNdc.copy(p.point).project(camera);
  const w = window.innerWidth;
  const h = window.innerHeight;
  const x = (ndc.x * 0.5 + 0.5) * w;
  const y = (1 - (ndc.y * 0.5 + 0.5)) * h;
  let visible = ndc.z < 1 && Math.abs(ndc.x) <= 1 && Math.abs(ndc.y) <= 1;
  // A point on the far side of its body is behind it.
  if (visible && p.normal) visible = _lessonTmp2.copy(camera.position).sub(p.point).dot(p.normal) > 0;
  if (visible) visible = !lessonPointOccluded(p.point, p.body);
  let r = 0;
  if (p.radius > 0) {
    const d = Math.max(camera.position.distanceTo(p.point), 1e-9);
    r = (Math.atan2(p.radius, d) / (THREE.MathUtils.degToRad(camera.fov) * 0.5)) * (h * 0.5);
  }
  return { x, y, visible, r };
}

// Each frame after the bodies and the camera have moved: the held frame,
// then the labels.
function updateLessonFrame(delta) {
  const hold = lessonHold;
  if (hold && !isAnimating && !flyMode && !scaleModeActive && !rocketryActive) {
    const anchorPos = hold.anchor ? hold.anchor.getWorldPosition(_lessonAnchor) : _lessonAnchor.set(0, 0, 0);
    const dist = camera.position.distanceTo(controls.target);
    if (dist > 0) {
      lessonFrameDirection(hold.frame, anchorPos, _lessonDir);
      // The body sits in the middle of the area the lesson's panels leave
      // free: a pan, eased in after the landing and after any change in the
      // panels, so the view never jumps.
      const view = lessonSafeView();
      const ease = 1 - Math.exp(-Math.min(delta, 0.1) * 4);
      hold.shiftX += (view.x - hold.shiftX) * ease;
      hold.shiftY += (view.y - hold.shiftY) * ease;
      const halfH = dist * view.tanFull;
      const look = _lessonTmp.copy(_lessonDir).negate();
      const up = _lessonTmp2.copy(camera.up).addScaledVector(look, -camera.up.dot(look)).normalize();
      const right = look.cross(up);
      const pan = right.multiplyScalar(-hold.shiftX * 2 * halfH * camera.aspect).addScaledVector(up, -hold.shiftY * 2 * halfH);
      controls.target.copy(anchorPos).add(pan);
      camera.position.copy(controls.target).addScaledVector(_lessonDir, dist);
      camera.lookAt(controls.target);
    }
  }
  for (const fn of lessonFrameFns) {
    try { fn(delta); } catch (err) { console.error('[lessons] frame hook failed', err); }
  }
}

// The overview: the Sun in the middle, the camera at `position`.
function flyLessonOverview(position) {
  interruptCameraMotion();
  releaseIssCameraUp();
  selectedObject = null;
  _focusPrevObj = null;
  currentNavIndex = 0;
  if (navLabel) navLabel.textContent = 'Sun';
  matryoshka.close();
  setCameraNearPlane(getOverviewCameraNear());
  const startPos = camera.position.clone();
  const startTarget = controls.target.clone();
  const end = new THREE.Vector3(0, 0, 0);
  isAnimating = true;
  controls.enabled = false;
  controls.minDistance = 0;
  controls.maxDistance = Infinity;
  const state = { t: 0 };
  leaseCameraToTween();
  activeCameraTween = gsap.to(state, {
    t: 1,
    duration: 2.2,
    ease: 'power2.inOut',
    onUpdate: () => {
      const k = state.t * state.t * (3 - 2 * state.t);
      lerpRoundSun(camera.position, startPos, position, k);
      controls.target.lerpVectors(startTarget, end, k);
      camera.lookAt(controls.target);
    },
    onComplete: () => {
      controls.target.copy(end);
      controls.enabled = true;
      controls.minDistance = getSunZoomFloor(getBodyBaseRadius(sun));
      controls.maxDistance = Math.max(3500, position.length() * 6);
      relaxLessonPolarLimits();
      isAnimating = false;
      activeCameraTween = null;
    },
  });
}

// The camera for a scene without a mode: the focus body in its frame, or the
// overview.
async function placeLessonCamera(scene, serial) {
  const cam = scene.camera || {};
  const focus = scene.focus ? resolveLessonBody(scene.focus) : null;
  if (scene.focus && !focus) console.warn(`[lessons] no body "${scene.focus}" in the scene`);
  if (!focus) {
    // Distances here are in Earth-Sun distances as drawn, which reads the
    // same in either scale.
    const au = planets.earth ? planets.earth.getWorldPosition(_lessonTmp).length() : 1;
    let position;
    if (cam.fit === 'earth-orbit' || cam.fit === 'moon-orbit') {
      position = lessonFrameDirection(cam.frame || 'above-north', _lessonAnchor.set(0, 0, 0)).multiplyScalar(lessonFitDistance(au));
    } else if (cam.frame) {
      position = lessonFrameDirection(cam.frame, _lessonAnchor.set(0, 0, 0)).multiplyScalar((cam.distance || 2.4) * au * lessonDistanceScale());
    } else if (cam.az != null) {
      position = new THREE.Vector3().copy(sphericalToCamera({ az: cam.az, el: cam.el, dist: cam.distance || 2.4 }, au));
    } else {
      // The viewer's own overview in each scale (returnToOverview, the
      // realistic switch).
      position = realisticScaleMode ? new THREE.Vector3(0.45, 0.32, 0.85).normalize().multiplyScalar(28_000) : new THREE.Vector3(350, 220, 550);
    }
    flyLessonOverview(position);
    if (cam.frame || cam.fit) lessonHold = { frame: cam.frame || 'above-north', anchor: null, shiftX: 0, shiftY: 0 };
    return lessonWaitLanded(serial);
  }

  const radius = getBodyBaseRadius(focus);
  const defaultRadii = getFocusDistance(focus) / Math.max(radius, 1e-9);
  const earth = planets.earth;
  let anchor = focus;
  let offset;
  let frame = cam.frame || null;
  if (cam.fit === 'moon-orbit' && earth) {
    // The Moon's whole orbit in the picture, round the Earth.
    const moon = findMoon('earth', 'Moon');
    const orbit = moon ? moon.getWorldPosition(_lessonTmp).distanceTo(earth.getWorldPosition(_lessonTmp2)) : getBodyBaseRadius(earth) * 60;
    frame = frame || 'above-north';
    anchor = earth;
    offset = lessonFrameDirection(frame, earth.getWorldPosition(_lessonAnchor)).multiplyScalar(lessonFitDistance(orbit));
  } else if (cam.fit === 'earth-orbit') {
    frame = frame || 'above-north';
    offset = lessonFrameDirection(frame, focus.getWorldPosition(_lessonAnchor)).multiplyScalar(lessonFitDistance(earth ? earth.getWorldPosition(_lessonTmp).length() : radius * 100));
  } else if (frame) {
    let dist = (cam.distance || defaultRadii) * radius * lessonDistanceScale();
    if (frame === 'from-earth' && earth && focus !== earth) {
      // On the Earth's side of the body, never as far back as the Earth.
      const gap = focus.getWorldPosition(_lessonTmp).distanceTo(earth.getWorldPosition(_lessonTmp2)) - getBodyBaseRadius(earth) * 1.4;
      if (gap > radius * 1.5) dist = Math.min(dist, gap);
    }
    offset = lessonFrameDirection(frame, focus.getWorldPosition(_lessonAnchor)).multiplyScalar(dist);
  } else if (cam.az != null) {
    offset = new THREE.Vector3().copy(sphericalToCamera({ az: cam.az, el: cam.el, dist: cam.distance || defaultRadii }, radius));
  } else if (cam.distance) {
    offset = getFocusCameraOffset(focus, 1).clone().normalize().multiplyScalar(cam.distance * radius);
  }

  if (anchor !== focus) {
    // The fit is round the Earth while another body (the Moon) is the focus.
    const lookFrom = offset.clone();
    animateEventCamera({
      focusObject: focus,
      navText: focus.userData && focus.userData.name,
      minDistance: getBodyBaseRadius(anchor) * 1.2,
      maxDistance: lookFrom.length() * 50,
      getLookAt: () => getWorldPosition(anchor),
      getCameraPosition: (lookAt) => lookAt.clone().add(lookFrom),
    });
  } else {
    focusOnObject(focus, { interrupt: true, force: true, preservePanel: true, offset, onComplete: relaxLessonPolarLimits });
  }
  if (frame) lessonHold = { frame, anchor, shiftX: 0, shiftY: 0 };
  const landed = await lessonWaitLanded(serial);
  if (landed) relaxLessonPolarLimits();
  return landed;
}

// To Scale at a stop.
async function placeLessonToScale(index, serial) {
  if (!scaleModeActive) enterScaleMode();
  const t0 = performance.now();
  while (scaleModeActive && !scaleGroup && performance.now() - t0 < 30000) {
    await lessonFrames(1);
    if (serial !== lessonSerial) return false;
  }
  if (!scaleGroup) return false;
  if (scaleIndex !== index || scaleTween) scaleGoTo(index);
  return lessonWaitLanded(serial);
}

// Eclipse mode at an eclipse from the calendar, at greatest eclipse plus the
// offset (resolved by src/lessons/scene.js).
async function placeLessonEclipse(mode, jd, serial) {
  const { skyEventById } = await import('./sky/events.js');
  const event = skyEventById(mode.eclipse);
  if (!event) {
    console.warn(`[lessons] no eclipse "${mode.eclipse}" in the calendar`);
    return false;
  }
  const shown = eclipseModeActive() && eclipseMode.event && eclipseMode.event.id === event.id;
  if (!shown) startEclipseMode(event);
  // The framing follows the shadow frame by frame on its way in, so the
  // moment can change under it; eclipse mode's own playing clock waits for
  // the landing.
  setLessonJD(jd);
  setLessonRate(0);
  if (shown) frameEclipse(event);
  return lessonWaitLanded(serial);
}

async function placeLessonEvent(id, serial) {
  runSpaceEvent(id);
  // Some scenes load what they need first (deep space for the wormhole and
  // the comets), so their camera may start a few frames on.
  await lessonFrames(8);
  return lessonWaitLanded(serial, 20000);
}

// A lesson step's scene: the scale, the moment and the overlays first (they
// move the bodies), then two frames on the camera, then the clock at the
// step's pace once it has landed (paused while it flies, so the step opens
// on its moment). Resolves { ok, reason }.
async function applyLessonScene(rawScene) {
  const serial = ++lessonSerial;
  const blocked = lessonBlockedReason();
  if (blocked) return { ok: false, reason: blocked };
  const { normaliseScene, resolveSceneJD, isLiveSky } = await import('./lessons/scene.js');
  if (serial !== lessonSerial) return { ok: false, reason: 'superseded' };
  const scene = normaliseScene(rawScene);
  const mode = scene.mode || {};
  lessonApplying = true;
  try {
    leaveModesForLesson(scene);
    lessonHold = null;
    lessonHoldsMoons = Boolean(mode.event);
    killExitFlyTweens();
    interruptCameraMotion();
    relaxLessonPolarLimits();
    // Eclipse mode takes the true scale itself, and To Scale owns the camera
    // a scale switch would move.
    if (!mode.eclipse && !(mode.scale != null && scaleModeActive)) applyViewScale(scene.scale === 'true', scene.spread);
    const jd = resolveSceneJD(scene, { nowJD: dateToJD(new Date()) });
    setLessonJD(Number.isFinite(jd) ? jd : simulationJD);
    setLessonRate(0);
    applyViewOverlays(scene.show);
    setLessonEarthAxis(scene.axis);
    setLessonMagnetosphere(scene.magnetosphere);
    await lessonFrames(2);
    if (serial !== lessonSerial) return { ok: false, reason: 'superseded' };
    let landed;
    if (mode.scale != null) landed = await placeLessonToScale(mode.scale, serial);
    else if (mode.eclipse) landed = await placeLessonEclipse(mode, Number.isFinite(jd) ? jd : simulationJD, serial);
    else if (mode.event) landed = await placeLessonEvent(mode.event, serial);
    else landed = await placeLessonCamera(scene, serial);
    if (serial !== lessonSerial) return { ok: false, reason: 'superseded' };
    if (isLiveSky(scene)) setTimePreset('real');
    else setLessonRate(scene.rate);
    return { ok: landed !== false, reason: landed === false ? 'not-landed' : null };
  } finally {
    if (serial === lessonSerial) lessonApplying = false;
  }
}

// The driver the lesson modules use (docs/lessons.md, "The viewer driver").
function createLessonViewer() {
  if (lessonViewer) return lessonViewer;
  // A drag takes the camera back from a held frame (a wheel zoom keeps it,
  // since the hold keeps the distance the camera is at).
  renderer.domElement.addEventListener('pointerdown', () => { lessonHold = null; }, { capture: true });
  lessonFrameHook = updateLessonFrame;
  lessonViewer = {
    snapshot: lessonSnapshot,
    applyScene: applyLessonScene,
    setRate: setLessonRate,
    setJD: (jd) => { setLessonJD(jd); syncTimeControls(); },
    project: projectLessonTarget,
    setEarthAxis: setLessonEarthAxis,
    setChrome: setLessonChrome,
    // The insets the lesson's panels cover, so a held frame centres its body
    // in what is left.
    setSafeArea(insets = {}) {
      lessonSafeArea = {
        top: Math.max(0, Number(insets.top) || 0),
        right: Math.max(0, Number(insets.right) || 0),
        bottom: Math.max(0, Number(insets.bottom) || 0),
        left: Math.max(0, Number(insets.left) || 0),
      };
    },
    // Called every frame once the camera is placed (the labels).
    onFrame(fn) { lessonFrameFns.add(fn); return () => lessonFrameFns.delete(fn); },
    // For the pupil coach's inline controls.
    setOverlay(key, on) {
      const btn = VIEW_OVERLAYS[key] && document.getElementById(VIEW_OVERLAYS[key]);
      if (btn && btn.classList.contains('playing') !== Boolean(on)) btn.click();
    },
    scaleStep: (direction) => scaleStep(direction),
    focus(id) {
      const body = resolveLessonBody(id);
      if (body) focusOnObject(body, { interrupt: true, preservePanel: true });
      return Boolean(body);
    },
    navigate: (direction) => navigatePlanet(direction),
  };
  return lessonViewer;
}

// Spacetime mode (src/spacetime/): every body in its gravity well on one
// sheet, to scale. Loaded the first time it opens, from the Explore group or
// a #spacetime link.
function loadSpacetimeMode() {
  if (!spacetimeLoad) {
    spacetimeLoad = import('./spacetime/index.js').then(({ createSpacetimeMode }) => {
      spacetimeMode = createSpacetimeMode({
        scene, camera, controls, renderer,
        getSun: () => sun,
        getPlanets: () => planets,
        getMoons: () => moons,
        radiusOf: (body) => getBodyBaseRadius(body),
        isRealistic: () => realisticScaleMode,
        setRealistic: (on) => window.__setRealisticScaleMode && window.__setRealisticScaleMode(on),
        getSelected: () => selectedObject,
        getOrbitLines: () => orbitLines,
        getMoonOrbitLines: () => moonOrbitLines,
        orbitsShown: () => orbitsVisible,
        isFlying: () => isAnimating,
        showOrbits: (on) => { if (orbitsVisible !== on && orbitsToggle) orbitsToggle.click(); },
        canRun: () => !flyMode && !scaleModeActive && !rocketryActive && !eclipseModeActive() && !famousPhotoMode
          && !astroModeActive() && (!activeSystemId || activeSystemId === 'sol'),
        frameOverview: frameSpacetimeOverview,
        focus: (body) => focusOnObject(body, { interrupt: true }),
        resolveBody: (id) => resolveScreensaverBody(id),
        track: (event, params) => track(event, params),
        onActive: (on, depth) => {
          const btn = document.getElementById('spacetime-toggle');
          if (btn) btn.classList.toggle('playing', on);
          if (on) modeEnter('spacetime', { depth }); else modeExit('spacetime');
        },
      });
      window.__spacetime = spacetimeMode;
      return spacetimeMode;
    });
  }
  return spacetimeLoad;
}

// #spacetime, #spacetime/<body>, #spacetime[/<body>]/x<depth>.
function openSpacetimeFromHash(hash) {
  const m = /^#\/?spacetime(?:\/(?!x\d)([a-z]+))?(?:\/x(\d+))?/i.exec(hash || '');
  const body = m && m[1] ? m[1].toLowerCase() : null;
  const depth = m && m[2] ? Number(m[2]) : undefined;
  loadSpacetimeMode()
    .then((mode) => mode.enter({ body, depth, overview: !body }))
    .catch((err) => console.error('[spacetime] unavailable', err));
}

function initSpacetimeButton() {
  const btn = document.getElementById('spacetime-toggle');
  if (!btn) return;
  btn.addEventListener('click', () => {
    loadSpacetimeMode()
      .then((mode) => mode.toggle())
      .catch((err) => console.error('[spacetime] unavailable', err));
  });
}

// The magnetosphere (src/magnetosphere/): MAGNETOSPHERE in the Explore
// group, the space weather card's button and #magnetosphere turn on the
// layer and frame it; science mode shows its Van Allen belts alone.
function loadMagnetosphere() {
  if (IS_SCREENSAVER) return Promise.resolve(null);
  if (magnetosphereMode) return Promise.resolve(magnetosphereMode);
  if (!magnetosphereLoading) {
    magnetosphereLoading = import('./magnetosphere/index.js').then((m) => {
      magnetosphereMode = m.createMagnetosphereMode({
        scene,
        getEarthMesh: getEarthMeshForTerrain,
        poleLocal: earthMeshDirection(EARTH_GEOMAGNETIC_POLE.lat, EARTH_GEOMAGNETIC_POLE.lon),
        getSunWorld: (out) => (sun ? sun.getWorldPosition(out) : out.set(0, 0, 0)),
        getCamera: () => camera,
        getSimMs: () => (simulationJD - 2440587.5) * 86400000,
        isSuppressed: () => rocketryActive || rocketryLoading || scaleModeActive || astroModeActive()
          || Boolean(famousPhotoStarsHidden) || Boolean(cupolaView && cupolaView.active)
          || Boolean(activeSystemId && activeSystemId !== 'sol'),
        frame: (offset) => {
          if (!planets.earth || flyMode || scaleModeActive || rocketryActive) return;
          focusOnObject(planets.earth, { interrupt: true, force: true, offset, via: 'magnetosphere' });
        },
        lowQuality: activeGraphicsPreset === 'low',
        onChange: (on) => {
          const btn = document.getElementById('magnetosphere-toggle');
          const label = document.getElementById('magnetosphere-label');
          if (btn) btn.classList.toggle('playing', on);
          if (label) label.textContent = vt(on ? 'controls.magnetosphere.on' : 'controls.magnetosphere.off');
        },
      });
      if (vanAllenBeltsOn) magnetosphereMode.setScience(true);
      // For checks (scripts/test-magnetosphere.mjs).
      window.__magnetosphere = magnetosphereMode;
      return magnetosphereMode;
    }).catch((err) => {
      console.error('[magnetosphere] unavailable', err);
      magnetosphereLoading = null;
      return null;
    });
  }
  return magnetosphereLoading;
}

function setMagnetosphereOn(on) {
  return loadMagnetosphere().then((m) => { if (m) m.setEnabled(on); return m; });
}

function initMagnetosphereButton() {
  const btn = document.getElementById('magnetosphere-toggle');
  if (!btn) return;
  btn.addEventListener('click', () => setMagnetosphereOn(!(magnetosphereMode && magnetosphereMode.enabled)));
}

// The inner planets from above the plane, looking across the Sun's well.
function frameSpacetimeOverview() {
  if (!camera || !controls) return;
  activeCameraTween?.kill();
  activeCameraTween = null;
  selectedObject = null;
  _focusPrevObj = null;
  currentNavIndex = 0;
  if (navLabel) navLabel.textContent = 'Sun';
  setCameraNearPlane(getOverviewCameraNear());
  controls.minDistance = 0.05;
  controls.maxDistance = 50_000_000;
  const dist = realisticScaleMode ? AU_TO_SCENE * 3.4 : 700;
  const elev = THREE.MathUtils.degToRad(24);
  const az = Math.atan2(camera.position.z, camera.position.x);
  // Level with the sheet where Earth is, however deep the wells are drawn.
  const lift = spacetimeMode ? spacetimeMode.overviewHeight() : 0;
  const end = new THREE.Vector3(Math.cos(az) * Math.cos(elev) * dist, Math.sin(elev) * dist + lift, Math.sin(az) * Math.cos(elev) * dist);
  const start = camera.position.clone();
  const startTarget = controls.target.clone();
  const endTarget = new THREE.Vector3(0, lift, 0);
  isAnimating = true;
  controls.enabled = false;
  leaseCameraToTween();
  const state = { p: 0 };
  activeCameraTween = gsap.to(state, {
    p: 1,
    duration: 2.4,
    ease: 'power3.inOut',
    onUpdate: () => {
      lerpRoundSun(camera.position, start, end, state.p);
      controls.target.lerpVectors(startTarget, endTarget, state.p);
      camera.lookAt(controls.target);
      leaseCameraToTween();
    },
    onComplete: () => {
      controls.target.copy(endTarget);
      controls.enabled = true;
      isAnimating = false;
      activeCameraTween = null;
    },
  });
}

function syncFocusToHash(options = {}) {
  const id = getHashFocusId();
  if (!id) return;

  if (id === 'wow') {
    setTimeout(runCoolWorldsShowreel, 900);
    return;
  }

  if (id === 'cool-worlds' || id === 'coolworlds') {
    if (isLocalEnvironment()) setTimeout(runCoolWorldsShowreel, 900);
    return;
  }

  // #launch/<slug>: watch a real launch in 3D (src/launches/watch.js).
  if (id === 'launch') {
    if (!IS_SCREENSAVER) setTimeout(() => launchWatch().then((w) => w.openFromHash(window.location.hash)), 400);
    return;
  }

  // #sky/<lat>,<lon>[/<UTC>][?az=&alt=&fov=]: Astronomy mode (src/astro/link.js).
  if (id === 'sky' || id.startsWith('sky?')) {
    if (!IS_SCREENSAVER) openAstronomyMode({ hash: window.location.hash }).catch((err) => console.error('[astro] unavailable', err));
    return;
  }
  if (astroModeActive()) astroMode.exit();

  // #to-scale[/<body>]: the planets and the Sun side by side at true size,
  // at that body's stop (all for the whole line). The videos' /v/ links
  // land here.
  if (id === 'to-scale') {
    const m = /^#\/?to-scale(?:\/([a-z]+))?/.exec(window.location.hash);
    const stop = m && m[1] ? scaleStopIndexOf(m[1]) : 0;
    if (scaleModeActive) scaleGoTo(stop);
    else if (!flyMode) enterScaleMode(stop);
    return;
  }

  if (scaleModeActive || flyMode) return;

  // #spacetime[/<body>][/x<depth>]: the gravity wells, to scale
  // (src/spacetime/).
  if (id === 'spacetime') {
    if (!IS_SCREENSAVER) openSpacetimeFromHash(window.location.hash);
    return;
  }

  // #aurora, #space-weather, #sun-today: the live aurora, the card, the Sun
  // today (src/spaceweather/ui.js).
  if (spaceWeatherUI && spaceWeatherUI.fromHash(id)) return;

  // #magnetosphere: the field, its boundaries and the belts round the Earth
  // (src/magnetosphere/).
  if (id === 'magnetosphere') {
    if (!IS_SCREENSAVER) afterBodiesPlaced(() => setMagnetosphereOn(true));
    return;
  }

  // #view/<body>/<moment>?...: an exact view (src/ui/viewlink.js).
  if (id === 'view') {
    applyViewLink(decodeView(window.location.hash));
    return;
  }

  // #photo/pale-blue-dot: a famous photo as a live view (src/photos/mode.js).
  if (id === 'photo') {
    if (!IS_SCREENSAVER) {
      const m = /^#\/?photo\/([a-z0-9-]+)/.exec(window.location.hash);
      startFamousPhoto(m ? m[1] : null);
    }
    return;
  }

  // #climate[/<layer>[/<year>]]: climate data on the Earth (src/climate/).
  if (id === 'climate') {
    if (climateMode) climateMode.fromHash(window.location.hash);
    return;
  }

  // #eclipse/2027-08-02: the viewer at that eclipse (src/eclipse/mode.js).
  if (id === 'eclipse') {
    const event = eclipseFromHash(window.location.hash);
    if (event) startEclipseMode(event);
    return;
  }

  let target = id === 'sun' ? sun : planets[id] || findLoadedSceneObjectByName(id);
  // The wormhole, comets, probes and dwarf planets are built the first time
  // deep space is used, so a link to one of them builds them (as the
  // Events panel's scenes do) instead of opening the panel on an empty sky.
  if (!target && !deepSpaceBodiesLoaded && getBody(id)) {
    ensureDeepSpaceBodies();
    target = findLoadedSceneObjectByName(id);
  }
  if (!target) return;

  focusOnObject(target, options);
}

// Scratch vectors for the focus-tween path — the tween's onUpdate and
// getFocusCameraOffset run every frame while a camera tween is live.
// Same convention as FLY_SCRATCH_*.
const FOCUS_SCRATCH_QUAT = new THREE.Quaternion();
const FOCUS_SCRATCH_AXIS_A = new THREE.Vector3();
const FOCUS_SCRATCH_AXIS_B = new THREE.Vector3();
const FOCUS_SCRATCH_AXIS_C = new THREE.Vector3();
const FOCUS_SCRATCH_OFFSET = new THREE.Vector3();
const FOCUS_SCRATCH_OBJ_POS = new THREE.Vector3();
const FOCUS_SCRATCH_END_DIR = new THREE.Vector3();
const FOCUS_SCRATCH_DIR_NOW = new THREE.Vector3();
const FOCUS_SCRATCH_CAM_TARGET = new THREE.Vector3();
const FOCUS_SCRATCH_LOOK_AT = new THREE.Vector3();

// Returns FOCUS_SCRATCH_OFFSET (shared scratch) — consume immediately,
// don't hold the returned vector across frames.
function getFocusCameraOffset(object, distance) {
  const data = object.userData || {};

  if ((data.isGalaxy || data.isMilkyWayVista) && galaxy && galaxy.object) {
    // The galaxy and its vista land on GALAXY_VIEW (src/galaxy.js), the
    // framing chosen with the tuner dock's Galaxy tab.
    return galaxyViewOffset(GALAXY_VIEW.elevation, GALAXY_VIEW.azimuth, distance, FOCUS_SCRATCH_OFFSET);
  }

  if (data.isBlackHole) {
    // Frame the BH from a 3/4 angle so the accretion disc reads as
    // an ellipse with the event horizon silhouette in front. Same
    // axis convention as the galaxy (the BH inherits the galaxy's
    // tilt) but a slightly steeper view to emphasise the disc's
    // Doppler asymmetry.
    const q = object.getWorldQuaternion(FOCUS_SCRATCH_QUAT);
    const normal = FOCUS_SCRATCH_AXIS_A.set(0, 0, 1).applyQuaternion(q).normalize();
    const tangent = FOCUS_SCRATCH_AXIS_B.set(1, 0, 0).applyQuaternion(q).normalize();
    const minor = FOCUS_SCRATCH_AXIS_C.set(0, 1, 0).applyQuaternion(q).normalize();
    return FOCUS_SCRATCH_OFFSET.copy(normal).multiplyScalar(0.50)
      .addScaledVector(tangent, 0.70)
      .addScaledVector(minor, 0.18)
      .normalize()
      .multiplyScalar(distance);
  }

  // The ISS as its own external cameras see it: from behind and above along
  // its orbit (its nose is local -X, the Earth below along local +Z), about
  // 32 degrees down, so the station sits over the Earth with the curved
  // horizon, 21 degrees below level from 420 km, across the top of the
  // frame. The co-orbital follow keeps that composition round the lap.
  if (data.isSatellite && data.name === 'ISS' && planets.earth) {
    const q = object.getWorldQuaternion(FOCUS_SCRATCH_QUAT);
    const back = FOCUS_SCRATCH_AXIS_A.set(1, 0, 0).applyQuaternion(q);
    const up = FOCUS_SCRATCH_AXIS_B.set(0, 0, -1).applyQuaternion(q);
    const side = FOCUS_SCRATCH_AXIS_C.set(0, 1, 0).applyQuaternion(q);
    return FOCUS_SCRATCH_OFFSET.copy(back).multiplyScalar(0.8)
      .addScaledVector(up, 0.5)
      .addScaledVector(side, 0.22)
      .normalize()
      .multiplyScalar(distance);
  }

  // Hubble from the Sun's side (placeHubble sets the Sun between its aft end,
  // -Y, and its side, +X), a little round towards a mast (+Z): the tube and
  // the arrays both in the light.
  if (data.isSatellite && data.name === 'Hubble') {
    const q = object.getWorldQuaternion(FOCUS_SCRATCH_QUAT);
    return FOCUS_SCRATCH_OFFSET.set(0.7, -0.7, 0.3)
      .applyQuaternion(q)
      .normalize()
      .multiplyScalar(distance);
  }

  // Webb from off the end of its mirror (NASA's model looks along +Z, the
  // sunshield's long side, with the telescope on +Y above the shield and the
  // Sun below it), a little below the shield and to one side: the sunlit
  // shield and the bus in front, the mirror over the shield's edge. From
  // above, the cold side, it is in its own shadow and reads black.
  if (data.atL2) {
    const q = object.getWorldQuaternion(FOCUS_SCRATCH_QUAT);
    const ahead = FOCUS_SCRATCH_AXIS_A.set(0, 0, 1).applyQuaternion(q);
    const up = FOCUS_SCRATCH_AXIS_B.set(0, 1, 0).applyQuaternion(q);
    const side = FOCUS_SCRATCH_AXIS_C.set(1, 0, 0).applyQuaternion(q);
    return FOCUS_SCRATCH_OFFSET.copy(ahead).multiplyScalar(0.8)
      .addScaledVector(up, -0.32)
      .addScaledVector(side, 0.45)
      .normalize()
      .multiplyScalar(distance);
  }

  if (data.isSatellite && planets.earth) {
    const objectPosition = object.getWorldPosition(FOCUS_SCRATCH_AXIS_A);
    const earthPosition = planets.earth.getWorldPosition(FOCUS_SCRATCH_AXIS_B);

    const radial = objectPosition.sub(earthPosition);
    if (radial.lengthSq() > 0.0001) {
      radial.normalize();
      const tangent = FOCUS_SCRATCH_AXIS_C.set(0, 1, 0).cross(radial);
      if (tangent.lengthSq() < 0.0001) {
        tangent.set(1, 0, 0).cross(radial);
      }
      tangent.normalize();

      const offset = FOCUS_SCRATCH_OFFSET.copy(radial).multiplyScalar(0.72)
        .addScaledVector(tangent, 0.5);
      offset.y += 0.28;
      return offset.normalize().multiplyScalar(distance);
    }
  }

  if (data.hasRings) {
    const ring = object.getObjectByName('ring');
    if (ring) {
      const ringNormal = FOCUS_SCRATCH_AXIS_A.set(0, 0, 1)
        .applyQuaternion(ring.getWorldQuaternion(FOCUS_SCRATCH_QUAT))
        .normalize();

      const inPlane = FOCUS_SCRATCH_AXIS_B.set(1, 0.25, 1).projectOnPlane(ringNormal);
      if (inPlane.lengthSq() < 0.001) {
        inPlane.set(0, 1, 0).projectOnPlane(ringNormal);
      }
      inPlane.normalize();

      // Frame ringed planets from above the ring plane: face-on enough to
      // read as a broad disk, shallow enough to keep the iconic ellipse.
      return FOCUS_SCRATCH_OFFSET.copy(inPlane).multiplyScalar(0.8)
        .addScaledVector(ringNormal, 0.6)
        .normalize()
        .multiplyScalar(distance);
    }
  }

  // A small body in its real shape (src/smallbodies/) is framed from its lit
  // side, 50 degrees from the Sun, at the fixed direction's distance (1.5x):
  // from that direction Eros often arrived as a black silhouette.
  if (data.shapeReach) {
    const toSun = sun ? sun.getWorldPosition(FOCUS_SCRATCH_AXIS_A) : FOCUS_SCRATCH_AXIS_A.set(0, 0, 0);
    toSun.sub(object.getWorldPosition(FOCUS_SCRATCH_AXIS_B));
    if (toSun.lengthSq() > 1e-12) {
      toSun.normalize();
      const across = FOCUS_SCRATCH_AXIS_C.set(1, 0.5, 1).projectOnPlane(toSun);
      if (across.lengthSq() < 1e-6) across.set(0, 1, 0).projectOnPlane(toSun);
      across.normalize();
      return FOCUS_SCRATCH_OFFSET.copy(toSun).multiplyScalar(Math.cos(0.87))
        .addScaledVector(across, Math.sin(0.87))
        .multiplyScalar(distance * 1.5);
    }
  }

  return FOCUS_SCRATCH_OFFSET.set(distance, distance * 0.5, distance);
}

function setCameraNearPlane(value) {
  if (!camera) return;
  camera.near = value;
  camera.updateProjectionMatrix();
}

// Focus camera on an object
// Telescope-view focus for deep-sky objects. DSOs sit on the celestial
// shell ~40M units from the origin — flying the camera to one would
// require crossing the entire scene and would break the spatial
// metaphor (the sprite is a billboard, not a real destination, and
// the panorama skybox lives at the same shell radius). What the user
// actually wants is "turn my head toward Andromeda and zoom in" —
// same camera position, rotated to look along the DSO's celestial
// direction, with FOV pulled in for the telescope feel and the
// scroll wheel rebound to refine the FOV-zoom further.
let dsoFocusActive = false;
let dsoBaseFov = 45;
let dsoWheelHandler = null;
// Target FOV for DSO scroll-wheel zoom — the actual camera.fov eases
// toward this value each frame so a single wheel tick reads as a
// smooth telescope dolly rather than a discrete FOV step.
let dsoTargetFov = 45;

function focusOnDSO(sprite, options = {}) {
  if (!sprite) return;

  // Lazy-load the photo on first focus. Sprites render with their
  // procedural shader path until then; the camera tweens immediately
  // (no waiting on the network) and the photo swaps in once decoded.
  if (sprite.userData.loadPhoto) {
    sprite.userData.loadPhoto({ focus: true });
  }

  if (isAnimating) {
    if (!options.interrupt) return;
    if (activeCameraTween) activeCameraTween.kill();
    activeCameraTween = null;
    controls.enabled = true;
    isAnimating = false;
  }
  if (options.via) {
    const id = getSceneObjectId(sprite);
    if (id) explore('select_content', id, { content_type: 'dso', item_id: id, via: options.via });
  }

  // Direction from camera to DSO.
  const dsoWorld = sprite.getWorldPosition(new THREE.Vector3());
  const dir = dsoWorld.clone().sub(camera.position).normalize();

  // Preserve the camera's current orbit-radius so OrbitControls keeps
  // behaving naturally; place the new target along the DSO direction
  // at that same radius. Camera physically stays put.
  const radius = camera.position.distanceTo(controls.target);
  const newTarget = camera.position.clone().add(dir.multiplyScalar(radius));

  // Per-DSO target FOV. Drive framing off the sprite's actual angular
  // size on the shell so M31 (3°), M42 (1°), and the LMC (5.5°) each
  // sit at roughly the same fraction of the frame after the tween.
  const SHELL_RADIUS = 39_500_000;
  const spriteAngularDeg =
    Math.max(sprite.scale.x, sprite.scale.y) / SHELL_RADIUS * 180 / Math.PI;
  // Want sprite to fill ~1/3 of the frame: FOV ≈ 3 × angular size.
  // Floor at 4° so the smallest DSOs (M16, M20) don't get an absurdly
  // narrow window; ceiling at 22° so the LMC doesn't lose the
  // telescope feel.
  const targetFov = Math.max(4, Math.min(22, spriteAngularDeg * 3));

  // Capture the FOV from BEFORE we entered DSO mode. If we're already
  // in DSO mode and switching DSOs, dsoBaseFov already holds the
  // pre-DSO value — don't overwrite it.
  if (!dsoFocusActive) dsoBaseFov = camera.fov;

  const startFov = camera.fov;
  const startTarget = controls.target.clone();
  const tweenState = { t: 0 };

  isAnimating = true;
  selectedObject = sprite;
  controls.enabled = false;

  if (typeof navLabel !== 'undefined' && navLabel) {
    navLabel.textContent = sprite.name;
  }

  // The info panel, as focusOnObject opens it (src/data/bodies/deepsky.js).
  if (!options.preservePanel) {
    const dsoId = getSceneObjectId(sprite);
    if (getBody(dsoId)) {
      closeTransientPanels('matryoshka');
      matryoshka.open(dsoId);
    } else {
      matryoshka.close();
    }
  }

  activeCameraTween = gsap.to(tweenState, {
    t: 1,
    duration: 1.6,
    ease: 'power2.inOut',
    onUpdate: () => {
      controls.target.lerpVectors(startTarget, newTarget, tweenState.t);
      camera.fov = startFov + (targetFov - startFov) * tweenState.t;
      camera.updateProjectionMatrix();
      controls.update();
    },
    onComplete: () => {
      controls.enabled = true;
      isAnimating = false;
      activeCameraTween = null;
      enableDsoZoomMode();
    }
  });
}

// While in DSO focus, rebind the scroll wheel to FOV adjustment so
// the user can zoom further into the DSO without dollying the camera
// (which would push it through the panorama skybox). Standard
// OrbitControls dolly is restored when the user clicks any non-DSO
// body via focusOnObject(). We also briefly unlock the polar-angle
// limits so the user can pan to the southern sky DSOs (LMC, SMC at
// dec ≈ -70°) — the default min/maxPolarAngle constraints reserve
// the polar caps for the solar-system view but they prevent reaching
// any object near the celestial poles.
let dsoSavedPolarLimits = null;

function enableDsoZoomMode() {
  if (dsoFocusActive) return;
  dsoFocusActive = true;
  controls.enableZoom = false;

  dsoSavedPolarLimits = {
    min: controls.minPolarAngle,
    max: controls.maxPolarAngle
  };
  controls.minPolarAngle = 0;
  controls.maxPolarAngle = Math.PI;

  dsoTargetFov = camera.fov;
  dsoWheelHandler = (e) => {
    e.preventDefault();
    const factor = Math.exp(e.deltaY * 0.0015);
    const next = dsoTargetFov * factor;
    // Floor at 0.3° so the user can zoom deep enough into HUDF / HDF
    // to actually see individual galaxies in the photo. Sub-1° FOV
    // is genuine telescope territory; the high-res 2048px HUDF
    // texture supports it without obvious pixelation. The wheel
    // writes to the TARGET; the animation loop's smoothing pass
    // eases camera.fov toward it.
    dsoTargetFov = Math.min(dsoBaseFov, Math.max(0.3, next));
  };
  renderer.domElement.addEventListener('wheel', dsoWheelHandler, { passive: false });
}

// Per-frame smoothing for DSO telescope zoom. Called from animate().
function updateDsoZoomSmoothing(delta) {
  if (!dsoFocusActive) return;
  const ease = 1.0 - Math.exp(-delta * 6.0);
  const diff = dsoTargetFov - camera.fov;
  if (Math.abs(diff) < 0.001) return;
  camera.fov += diff * ease;
  camera.updateProjectionMatrix();
}

function disableDsoZoomMode() {
  if (!dsoFocusActive) return;
  dsoFocusActive = false;
  controls.enableZoom = true;
  if (dsoWheelHandler) {
    renderer.domElement.removeEventListener('wheel', dsoWheelHandler);
    dsoWheelHandler = null;
  }
  if (dsoSavedPolarLimits) {
    controls.minPolarAngle = dsoSavedPolarLimits.min;
    controls.maxPolarAngle = dsoSavedPolarLimits.max;
    dsoSavedPolarLimits = null;
  }
  camera.fov = dsoBaseFov;
  camera.updateProjectionMatrix();
}

// How far out focusOnObject frames a body, in scene units of the current
// scale (lesson scenes measure their own distances against it too).
function getFocusDistance(object) {
  const data = object.userData || {};
  if ((data.isGalaxy || data.isMilkyWayVista) && galaxy && galaxy.object) {
    return GALAXY_VIEW.distance * galaxy.object.userData.galaxyRadius;
  }
  // Moons need larger zoom multiplier since they're smaller
  if (data.isInterstellar && !interstellarTracks) data.framedBlind = true;
  const zoomMultiplier = data.isMoon
    ? 8
    : data.isSun
      ? 4
      : data.isComet
        ? (cometComaShown(object) ? 46 : 8)
        : data.isNEO
          ? 16
          : data.isSpaceProbe
            ? 3.5
            : data.isWormhole
              ? 7
              : data.isBeltBody
              ? 14
              : data.isDwarfPlanet
              ? 7
              : data.isSatellite
              ? 2
              : data.isGalaxy
              ? 4.6
              : data.isBlackHole
              ? 5.0
              : 5;
  // In realistic mode the body's actual mesh has been scaled to its
  // true proportional size: Earth ~0.213 in a scene where it lives
  // at 5000 from the sun. focus distances must use that effective
  // radius, not the authored artistic radius, otherwise the camera
  // ends up dozens of body-radii out and the planet renders as a
  // tiny dot on the chase HUD.
  let focusBodyRadius = data.radius;
  let focusRingOuter = data.ringOuterRadius || 0;
  if (realisticScaleMode) {
    const realRadius = getRealisticBodyRadius((data.name || '').toLowerCase());
    if (realRadius) {
      const scaleFactor = realRadius / data.radius;
      focusBodyRadius = realRadius;
      focusRingOuter = focusRingOuter * scaleFactor;
    }
  }
  return data.hasRings
    ? Math.max(focusBodyRadius * zoomMultiplier, focusRingOuter * 3.6)
    : focusBodyRadius * zoomMultiplier;
}

function focusOnObject(object, options = {}) {
  // exitFlyMode's return-to-overview tweens run outside isAnimating —
  // kill them so they can't fight the focus animation for the camera
  // or null the new selection from their delayed onComplete.
  killExitFlyTweens();
  if (isAnimating) {
    if (!options.interrupt) return;
    if (activeCameraTween) activeCameraTween.kill();
    activeCameraTween = null;
    controls.enabled = true;
    isAnimating = false;
  }
  const objectId = getSceneObjectId(object);
  // Only a person's choice carries `via` (src/analytics.js explore()).
  if (options.via && objectId) explore('select_content', objectId, { content_type: 'body', item_id: objectId, via: options.via });

  if (!isIssObject(object)) releaseIssCameraUp();
  // The ISS is shown at true scale: its 408 km height and 92-minute laps only
  // read against the real-size Earth.
  // The switch keeps the current framing at once, then the fly-in starts two
  // frames later: the switch costs a long frame, and a long frame inside a
  // moving camera reads as a lurch where one before it moves reads as nothing.
  if (isIssObject(object) && !realisticScaleMode && window.__setRealisticScaleMode) {
    // With nothing selected the switch would go to the overview: keep the
    // camera where it is relative to the Earth instead.
    const unframed = !selectedObject && planets.earth;
    const earthR0 = unframed ? getBodyBaseRadius(planets.earth) : 0;
    if (unframed) _scaleSwitchFallback.copy(camera.position).sub(planets.earth.getWorldPosition(_scaleSwitchBodyPos));
    window.__setRealisticScaleMode(true, { instant: true });
    if (unframed) {
      setScaleAnchor(planets.earth, _scaleSwitchFallback.multiplyScalar(getBodyBaseRadius(planets.earth) / earthR0));
    }
    isAnimating = true;
    controls.enabled = false;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      isAnimating = false;
      controls.enabled = true;
      focusOnObject(object, { ...options, interrupt: true });
    }));
    return;
  }

  // Persist current focus in the URL hash so the view is shareable
  // and refreshable. e.g. /#halleys-comet — on reload, the existing
  // syncFocusToHash() init step reads the hash and refocuses the
  // same body. replaceState (not pushState) keeps browser back-
  // button history clean — selecting bodies isn't navigation. An eclipse
  // keeps its own address (#eclipse/<date>) while it is shown.
  if (objectId && !eclipseModeActive() && !famousPhotoModeActive() && typeof window !== 'undefined' && window.history && window.history.replaceState) {
    try {
      const url = new URL(window.location.href);
      url.hash = '#' + objectId;
      window.history.replaceState({}, '', url.toString());
    } catch (_) { /* malformed URL — ignore */ }
  }

  // If we're already focused on this body, just re-open the matryoshka
  // panel (user dismissed it earlier and clicked the body again to bring
  // it back). No camera animation needed.
  if (selectedObject === object && !options.force) {
    if (!options.preservePanel && getBody(objectId)) {
      matryoshka.open(objectId);
    } else if (!options.preservePanel) {
      matryoshka.close();
    }
    return;
  }

	  isAnimating = true;
	  // Snapshot the outgoing selection BEFORE overwriting it — the
	  // galaxy-transition detection below needs to know where we came
	  // from, and reading selectedObject after this assignment would
	  // always see the new target.
	  const prevSelected = selectedObject;
	  selectedObject = object;
	  _focusPrevObj = null;
	  void ensureFocusedDetail(object);
	  const data = object.userData;
	  // In realistic mode the body's actual radius is sub-unit, so
	  // the artistic near (scaleMultiplier * 0.1 = 10) clips the
	  // planet during the focus animation. Skip the override and
	  // let the onComplete branch set a body-specific tiny near.
	  setCameraNearPlane(data.isSatellite ? ISS_CLOSE_CAMERA_NEAR : getOverviewCameraNear());

  // Update navigation index and label
  const objectName = objectId;
  syncNavToBody(data.name, objectName);
  navLabel.textContent = data.name || objectName.charAt(0).toUpperCase() + objectName.slice(1);

  const distance = getFocusDistance(object);

  // Store starting positions
  const startLookAt = controls.target.clone();
  const startCameraPos = camera.position.clone();

  // Disable controls during animation
  controls.enabled = false;

  // Animate with dynamic target tracking. Duration and easing are
  // overridable so documentary mode can use a longer, more cinematic
  // travel between bodies.
  //
  // Galaxy ↔ planet transitions are special: the start and end
  // distances differ by 5–6 orders of magnitude (40M units vs ~100
  // units), so a linear position lerp spends most of the duration
  // looking nearly identical, then collapses everything in the last
  // ~200 ms. Detect that case and use exponential distance
  // interpolation (constant relative-scale change per second) plus
  // a FOV pulse so the dive feels cinematic.
  const sourceWasGalaxy = !!(prevSelected && prevSelected.userData?.isGalaxy
    && prevSelected !== object);
  const targetIsGalaxy = !!data.isGalaxy;
  const isGalacticTransition = sourceWasGalaxy || targetIsGalaxy;

  const animationState = { progress: 0 };
  // Galactic transition: 3 s feels snappier than 4.6 s while still
  // reading as cinematic at this scale gap. Sine-like easing avoids
  // the slight overshoot feel of power3 — clean acceleration in,
  // graceful settle.
  // The ISS fly-in: from wherever the camera is to the station's framing, a
  // range of a thousand to ten million, so it gets its own path (onUpdate) at
  // linear progress and a duration that grows with the log of that range.
  const issApproach = isIssObject(object) && !!planets.earth && !options.offset && !isGalacticTransition;
  let issApproachDuration = 0;
  if (issApproach) {
    const endLen = Math.max(getFocusCameraOffset(object, distance).length(), 1e-6);
    const startLen = Math.max(camera.position.distanceTo(object.getWorldPosition(FOCUS_SCRATCH_OBJ_POS)), endLen);
    issApproachDuration = THREE.MathUtils.clamp(2.2 + 0.4 * Math.log10(startLen / endLen), 2.8, 5.0);
    planets.earth.getWorldPosition(_issFlyEarth);
    _issFlyRel0.copy(camera.position).sub(_issFlyEarth);
    _issFlyUp0.copy(camera.up);
    // The camera's turn round the Earth, to the point far above the station.
    object.getWorldPosition(FOCUS_SCRATCH_OBJ_POS);
    _issFlyRel.copy(FOCUS_SCRATCH_OBJ_POS).addScaledVector(getFocusCameraOffset(object, distance), ISS_FLY_FAR).sub(_issFlyEarth);
    _issFlyArc.start(_issFlyRel0, _issFlyRel, camera.up);
    // The view's turn, from where it looks now to the station.
    _issLookTo.copy(controls.target).sub(camera.position);
    if (_issLookTo.lengthSq() < 1e-30) camera.getWorldDirection(_issLookTo);
    _issLookTurn.start(_issLookTo, FOCUS_SCRATCH_OBJ_POS.sub(camera.position), camera.up);
  }
  // A planet or moon the straight line would pass through: the tween goes
  // round it (startBodyRoute), taking longer the further round it goes, so a
  // half turn round the Earth is not crammed into the usual 1.8 s.
  let routeBody = null;
  if (!isGalacticTransition && !issApproach) {
    const objectNow = object.getWorldPosition(FOCUS_SCRATCH_OBJ_POS);
    const end = FOCUS_SCRATCH_CAM_TARGET.copy(objectNow)
      .add(options.offset ? FOCUS_SCRATCH_OFFSET.copy(options.offset) : getFocusCameraOffset(object, distance));
    routeBody = startBodyRoute(camera.position, end, [prevSelected, object]);
    _bodyRouteSeg.copy(controls.target).sub(camera.position);
    if (_bodyRouteSeg.lengthSq() < 1e-30) camera.getWorldDirection(_bodyRouteSeg);
    _bodyLookTurn.start(_bodyRouteSeg, FOCUS_SCRATCH_DIR_NOW.copy(objectNow).sub(camera.position), camera.up);
  }
  _lastFocusRoute = routeBody ? (routeBody.userData.name || routeBody.name || 'body') : null;
  const routeTime = routeBody ? 0.9 * Math.min(1, _bodyRouteTurn.size / Math.PI) : 0;
  const duration = options.duration ?? (isGalacticTransition ? 3.0 : issApproach ? issApproachDuration : 1.8 + routeTime);
  const ease = options.ease ?? (isGalacticTransition ? 'sine.inOut' : issApproach ? 'none' : 'power2.inOut');

  // Pre-compute starting distance from the (initial) object position
  // for the log-distance interpolation. We use the object's CURRENT
  // position at start as an anchor; the target moves during the
  // animation but the start anchor doesn't.
  const startObjectPos = new THREE.Vector3();
  object.getWorldPosition(startObjectPos);
  const startDistFromTarget = Math.max(1.0, startCameraPos.distanceTo(startObjectPos));
  const startDirFromTarget = startCameraPos.clone().sub(startObjectPos).normalize();
  const baseFov = camera.fov;

  // Open the OrbitControls distance clamps for the galactic flight.
  // animate() keeps calling controls.update() every frame while the
  // tween owns the camera, and update() clamps the camera-to-target
  // radius to the PREVIOUS focus's min/maxDistance. A planetary
  // maxDistance (~2k) against the galaxy's ~46M end distance pinned
  // the rendered camera right behind the lerping look-at target —
  // riding it into the galaxy core, then snapping 46M out on the
  // frame onComplete finally raised the clamp. (The reverse trip hit
  // the same wall: the galaxy's ~4M minDistance shoved the camera 4M
  // off a planet-bound target.) onComplete still narrows both bounds
  // to the destination's own limits, so the open clamps only live for
  // the duration of the tween.
  // The ISS fly-in crosses the previous focus's zoom and polar limits too
  // (the Earth's held the camera 0.05 units off the station, then let it
  // snap in at the end); onComplete sets the station's own.
  if (isGalacticTransition || issApproach) {
    controls.minDistance = 0;
    controls.maxDistance = Infinity;
  }
  if (issApproach) {
    controls.minPolarAngle = 0;
    controls.maxPolarAngle = Math.PI;
    // The close Earth's cloud volumes are baked the first time the camera
    // comes near (a 60 to 90 ms frame with a GPU readback), which for the
    // station was halfway down the fly-in at full speed. Bake them now,
    // before the camera moves; the tween's clock starts after this.
    if (renderer) updateEarthWeather(renderer, getEarthMeshForTerrain(), performance.now(), true);
  }

  leaseCameraToTween();
  activeCameraTween = gsap.to(animationState, {
    progress: 1,
    duration,
    ease,
    onUpdate: () => {
      const t = animationState.progress;
      const smoothT = t * t * (3 - 2 * t);

      // Get current object position (it moves during animation)
      const currentObjectPos = object.getWorldPosition(FOCUS_SCRATCH_OBJ_POS);

      // Calculate camera offset from object (shared scratch vector).
      // options.offset lands the camera at a given body-relative spot
      // instead (the scale toggle keeps the user's framing).
      const offset = options.offset
        ? FOCUS_SCRATCH_OFFSET.copy(options.offset)
        : getFocusCameraOffset(object, distance);

      if (isGalacticTransition) {
        // Logarithmic distance interpolation. distance-from-target
        // halves repeatedly per equal time-slice instead of linearly,
        // which keeps the apparent motion steady across the huge
        // scale gap between galactic and planetary distances.
        const endDist = Math.max(1.0, offset.length());
        const logStart = Math.log(startDistFromTarget);
        const logEnd = Math.log(endDist);
        const distFromTargetNow = Math.exp(
          logStart + (logEnd - logStart) * smoothT
        );

        // Direction smoothly interpolates from where we started
        // (relative to the original object position) to the cinematic
        // offset direction.
        const endDirFromTarget = FOCUS_SCRATCH_END_DIR.copy(offset).normalize();
        const dirNow = FOCUS_SCRATCH_DIR_NOW.copy(startDirFromTarget)
          .lerp(endDirFromTarget, smoothT)
          .normalize();

        camera.position.copy(currentObjectPos)
          .addScaledVector(dirNow, distFromTargetNow);

        // FOV pulse pulled way back. The previous 14° peak made the
        // transition feel jarring; 5° gives a subtle dolly hint
        // without shifting the framing aggressively.
        const pulse = Math.sin(smoothT * Math.PI) * 5.0;
        camera.fov = baseFov + pulse;
        camera.updateProjectionMatrix();
      } else if (issApproach) {
        // Two legs, blended where they meet. A great circle round the
        // Earth with the distance from its centre changing in log steps,
        // so the path never cuts through the planet, to a point far above
        // the station along its framing; then a dolly straight down that
        // line, again in log steps, so the station grows at an even rate
        // instead of filling the frame in the last few frames.
        const earthPos = planets.earth.getWorldPosition(_issFlyEarth);
        const earthR = getBodyBaseRadius(planets.earth);
        _issFlyFar.copy(currentObjectPos).addScaledVector(offset, ISS_FLY_FAR);
        _issFlyRel.copy(_issFlyFar).sub(earthPos);
        const u1 = THREE.MathUtils.smootherstep(t, 0, 0.62);
        const u2 = THREE.MathUtils.smootherstep(t, 0.38, 1);
        const r0 = Math.max(_issFlyRel0.length(), 1e-6);
        const r1 = Math.max(_issFlyRel.length(), 1e-6);
        // The way round (this once slerped a quaternion into itself, which
        // stays at the identity: the camera dived along its starting ray,
        // then cut through the Earth to reach the far side).
        _issFlyArc.at(_issFlyDir, _issFlyRel, u1);
        // Log steps from the start radius to the end one, lifted by a bump
        // that peaks halfway round (zero when the straight log path is
        // already high enough for the angle to cover).
        const logR0 = Math.log(r0);
        const logR1 = Math.log(r1);
        const lift = Math.max(0, Math.log(earthR * (1 + ISS_FLY_LIFT * _issFlyArc.size)) - (logR0 + logR1) / 2);
        const r = Math.max(Math.exp(logR0 + (logR1 - logR0) * u1 + lift * Math.sin(Math.PI * u1)), earthR * 1.02);
        _issFlyMacro.copy(_issFlyDir).multiplyScalar(r).add(earthPos);
        const scale = Math.exp(Math.log(ISS_FLY_FAR) * (1 - u2));
        _issFlyDolly.copy(currentObjectPos).addScaledVector(offset, scale);
        // The legs blend about the Earth's centre (direction, then distance in
        // log steps), so the handover never takes the chord between them,
        // which dips under the ground when they are far apart round it.
        const w = THREE.MathUtils.smootherstep(t, 0.38, 0.62);
        _issFlyMacro.sub(earthPos);
        _issFlyDolly.sub(earthPos);
        const ra = Math.max(_issFlyMacro.length(), 1e-9);
        const rb = Math.max(_issFlyDolly.length(), 1e-9);
        camera.position.copy(_issFlyMacro).divideScalar(ra)
          .lerp(_issFlyDolly.divideScalar(rb), w).normalize()
          .multiplyScalar(Math.exp(Math.log(ra) + (Math.log(rb) - Math.log(ra)) * w))
          .add(earthPos);
        // Ease the roll toward the station's horizon on the way down, which
        // the follow code then keeps.
        _issFlyRadial.copy(currentObjectPos).sub(earthPos).normalize();
        camera.up.copy(_issFlyUp0).lerp(_issFlyRadial, THREE.MathUtils.smootherstep(t, 0.2, 1)).normalize();
        _issUpActive = true;
      } else {
        // Standard linear position lerp for body-to-body within the
        // solar system — distances are similar enough that linear
        // works fine. Round the Sun where the line would cut it, and round
        // a planet or moon the line would pass through.
        const targetCameraPos = FOCUS_SCRATCH_CAM_TARGET.copy(currentObjectPos).add(offset);
        if (routeBody) arcRoundBody(camera.position, routeBody, targetCameraPos, smoothT);
        else lerpRoundSun(camera.position, startCameraPos, targetCameraPos, smoothT);
      }

      // Where the camera looks (the galactic trip slides its look point to
      // the target; everything else turns the view itself).
      if (issApproach) {
        // The ISS fly-in turns the view itself rather than sliding the look
        // point from the old target to the station: the camera passes close
        // to that sliding point on a long trip (from the Moon), and the view
        // whipped round at 400 degrees a second as it went by. The turn is
        // about the axis fixed at the start (the angle to the station round
        // it kept continuous), then the small rest of the way out of that
        // plane, both eased in over the first half while the station is far.
        const k = THREE.MathUtils.smootherstep(t, 0, 0.55);
        const issDist = Math.max(_issLookTo.copy(currentObjectPos).sub(camera.position).length(), 1e-12);
        _issLookTurn.at(FOCUS_SCRATCH_LOOK_AT, _issLookTo, k);
        controls.target.copy(camera.position).addScaledVector(FOCUS_SCRATCH_LOOK_AT, issDist);
        camera.lookAt(controls.target);
      } else if (!isGalacticTransition) {
        // The view turns from where it looked to the body, as the ISS fly-in
        // does. Sliding the look point from the old target to the new one
        // whipped the view round wherever the camera passed close to that
        // point (970 degrees a second from the Earth to the Moon), and going
        // round a body it would slide through the body.
        const lookDist = Math.max(FOCUS_SCRATCH_DIR_NOW.copy(currentObjectPos).sub(camera.position).length(), 1e-12);
        _bodyLookTurn.at(FOCUS_SCRATCH_LOOK_AT, FOCUS_SCRATCH_DIR_NOW, smoothT);
        controls.target.copy(camera.position).addScaledVector(FOCUS_SCRATCH_LOOK_AT, lookDist);
      } else {
        controls.target.copy(FOCUS_SCRATCH_LOOK_AT.lerpVectors(startLookAt, currentObjectPos, smoothT));
      }
    },
    onComplete: () => {
      controls.enabled = true;
      _focusLandedOn = object;
      // Restore the original FOV in case the galactic transition's
      // pulse left it slightly off (gsap easing should land at exact
      // midpoint=0 contribution but defensive snap costs nothing).
      if (isGalacticTransition) {
        camera.fov = baseFov;
        camera.updateProjectionMatrix();
      }
      // Minimum zoom distance: never closer than 1.2× body radius, AND
      // never closer than 2× the camera near plane — otherwise a tiny
      // moon's "get in closer" limit lets you push the camera inside
      // the sphere and the near plane carves a black hole through it.
      if (data.isGalaxy) {
        // The galaxy is a particle field, not a sphere — let the user
        // both push in close enough to be inside the disc and pull all
        // the way back to a halo view. Cap maxDistance below the far
        // plane so the disc never clips.
        controls.minDistance = Math.max(data.radius * 0.4, camera.near * 4);
        controls.maxDistance = Math.min(camera.far * 0.85, data.radius * 14);
      } else if (data.isBlackHole) {
        // Let the user push in close enough to see the event horizon
        // shadow + photon ring detail, and pull back enough to see the
        // disc against the surrounding bulge starfield.
        controls.minDistance = Math.max(data.radius * 0.18, camera.near * 4);
        controls.maxDistance = Math.min(camera.far * 0.70, data.radius * 25);
      } else {
        // In realistic mode, the planet's mesh has been shrunk to its
        // true scene size (Earth ~0.213 unit radius). Use that
        // effective radius for min/max distance so the user can zoom
        // in close to a tiny body without hitting an artistic-radius
        // clamp.
        let effectiveRadius = data.radius;
        if (realisticScaleMode) {
          const realRadius = getRealisticBodyRadius((data.name || '').toLowerCase());
          if (realRadius) effectiveRadius = realRadius;
        }
        // Camera near plane needs to be CLOSER to the camera than the
        // planet's near surface from minDistance, otherwise the
        // surface gets clipped before the user can see it. With a
        // 1.05× radius minDistance the closest surface point from
        // camera is 0.05× radius — set near to 0.5× of that
        // (= 0.025× radius) for safe headroom.
        if (realisticScaleMode) {
          setCameraNearPlane(Math.max(0.0001, effectiveRadius * 0.005));
        } else if (data.shapeReach) {
          // A small body with a shape model (src/smallbodies/) is a fraction
          // of the artistic near plane across; its own size sets it.
          setCameraNearPlane(Math.min(camera.near, effectiveRadius * data.shapeReach * 0.01));
        }
        // Tight close zoom — satellites render through a floating
        // local camera, so the user can inspect inside the ISS
        // structure instead of being held outside the physical
        // bounding radius.
        if (data.isSatellite) {
          setCameraNearPlane(ISS_CLOSE_CAMERA_NEAR);
          controls.minDistance = Math.max(ISS_CLOSE_MIN_DISTANCE, camera.near * 2);
          // Full 360 around the ISS: polar nearly 0 to PI (to within
          // three degrees of straight over and under the station), no
          // azimuth limits (default already 360). At the pole itself the
          // view's roll was set by the up easing onto the station's
          // vertical and spun from frame to frame.
          controls.minPolarAngle = 0.05;
          controls.maxPolarAngle = Math.PI - 0.05;
          controls.minAzimuthAngle = -Infinity;
          controls.maxAzimuthAngle = Infinity;
          controls.enablePan = true;
          controls.enableRotate = true;
          controls.enableZoom = true;
          // Keep scroll-wheel-click drag as pan here too, so small
          // craft can be reframed the same way as planets. Wheel
          // scroll still handles zoom; left-click drag rotates.
          controls.mouseButtons = {
            LEFT: THREE.MOUSE.ROTATE,
            MIDDLE: THREE.MOUSE.PAN,
            RIGHT: THREE.MOUSE.PAN
          };
          // Touch parity: one-finger drag rotates (default), two-
          // finger pinch zooms + pans. Already the OrbitControls
          // default but set explicitly so the satellite mode is
          // self-contained.
          controls.touches = {
            ONE: THREE.TOUCH.ROTATE,
            TWO: THREE.TOUCH.DOLLY_PAN
          };
          // Responsiveness tuned for inspecting a small craft: faster
          // rotate so you can sweep all the way around with a normal
          // drag, lighter damping so the camera settles quickly
          // instead of feeling laggy, and the pan/zoom speeds match
          // the rotate speed for a coherent feel.
          controls.dampingFactor = 0.10;
          controls.panSpeed = 0.9;
          controls.zoomSpeed = 1.3;
          controls.rotateSpeed = 1.4;
          controls.screenSpacePanning = true;
        } else {
          // In realistic mode the bodies are physically scaled and
          // therefore quite small in scene units. Users expect to be
          // able to fly right down to the surface (Earth at radius
          // 0.213, you should be able to see it filling the frame).
          // 1.005x lets the camera get to ~half a percent above the
          // surface. Artistic mode keeps 1.03 since the bodies are
          // already ergonomically sized and 3% above gives a clean
          // hero-shot framing.
          // The Sun has its own floor in both scales, and no near-plane
          // term: updateSunCloseCamera shrinks the near plane on the way in.
          const minDistMul = realisticScaleMode ? 1.005 : 1.03;
          controls.minDistance = data.isSun
            ? getSunZoomFloor(effectiveRadius)
            : Math.max(effectiveRadius * minDistMul * bodyShapeReach(data), camera.near * 1.2);
          // Restore the default polar-angle constraints + control
          // speeds when focused on a non-satellite body.
          controls.minPolarAngle = Math.PI * 0.15;
          controls.maxPolarAngle = Math.PI * 0.85;
          controls.dampingFactor = 0.08;
          controls.panSpeed = 0.8;
          controls.zoomSpeed = 1.2;
          controls.rotateSpeed = 0.8;
          // Restore the app's viewer bindings. Wheel scroll zooms;
          // scroll-wheel-click drag pans so users can reframe the
          // selected body without changing the orbit angle.
          controls.mouseButtons = {
            LEFT: THREE.MOUSE.ROTATE,
            MIDDLE: THREE.MOUSE.PAN,
            RIGHT: THREE.MOUSE.PAN
          };
          controls.screenSpacePanning = true;
        }
        controls.maxDistance = Math.max(effectiveRadius * 800, data.isSun ? effectiveRadius * 800 : 1200);
      }
      isAnimating = false;
      activeCameraTween = null;
      // A caller that needs the landing (a lesson scene) hears it here, after
      // the limits for the body are set and before the next frame uses them.
      if (options.onComplete) options.onComplete();
    }
  });

  // Open the matryoshka info panel for this body
  if (options.preservePanel) {
    closeTransientPanels('matryoshka');
  } else if (getBody(objectId)) {
    closeTransientPanels('matryoshka');
    matryoshka.open(objectId);
  } else {
    matryoshka.close();
  }
}

// Return to overview
function returnToOverview() {
  if (isAnimating) return;
  releaseIssCameraUp();

  isAnimating = true;
  selectedObject = null;
  _focusPrevObj = null;
  currentNavIndex = 0;
  navLabel.textContent = 'Sun';

  const startCameraPos = camera.position.clone();
	  const targetPosition = new THREE.Vector3(350, 220, 550);
	  const startLookAt = controls.target.clone();
	  const targetLookAt = new THREE.Vector3(0, 0, 0);
	  // The overview's own near plane: the artistic one alone was 10 at true
	  // scale, which clipped the whole Sun from 1.4 radii out. Leaving the
	  // Sun's floor, updateSunCloseCamera keeps it lowered until it is clear.
	  setCameraNearPlane(getOverviewCameraNear());

  controls.enabled = false;

  const animationState = { progress: 0 };

  leaseCameraToTween();
  gsap.to(animationState, {
    progress: 1,
    duration: 2.5,
    ease: 'power3.inOut',
    onUpdate: () => {
      const t = animationState.progress;
      const smoothT = t * t * (3 - 2 * t);

      // Interpolate camera position, round the Sun from its far side.
      lerpRoundSun(camera.position, startCameraPos, targetPosition, smoothT);

      // Interpolate lookAt target
      const currentLookAt = new THREE.Vector3();
      currentLookAt.lerpVectors(startLookAt, targetLookAt, smoothT);
      camera.lookAt(currentLookAt);
      controls.target.copy(currentLookAt);
    },
    onComplete: () => {
      controls.target.copy(targetLookAt);
      controls.enabled = true;
      controls.minDistance = 20;
      controls.maxDistance = 3500;
      controls.minPolarAngle = Math.PI * 0.15;
      controls.maxPolarAngle = Math.PI * 0.85;
      controls.dampingFactor = 0.08;
      controls.panSpeed = 0.8;
      controls.zoomSpeed = 1.2;
      controls.rotateSpeed = 0.8;
      isAnimating = false;
    }
  });

  matryoshka.close();
  closeObjectBrowser();
  closeShareSheet();
}

// === Documentary mode ===
// Narrative tour of the solar system. Hides all normal UI chrome, shows a
// subtitle panel + chapter timeline. Auto-advances through chapters unless
// paused. Narration audio is pre-generated at build time by
// scripts/generate-narration.js (one ElevenLabs call per chapter, only
// when the chapter text changes — never at runtime). Audio files live
// under public/narration/{id}.mp3. Chapters import is at the top of
// the file alongside other data imports.

let documentaryActive = false;
let documentaryIndex = 0;
let documentaryPaused = false;
let documentaryTimer = null;
let documentaryGapTimer = null;

// Cinematic pacing for documentary mode.
// - GAP: silent beat between chapters so the narration doesn't feel
//   edited tight. The camera travel in the next chapter begins at the
//   end of this gap, so the user gets to sit with the previous body
//   briefly before moving on.
// - TRAVEL: long slow camera move to the next body. Longer than the
//   click-to-focus transition (1.8s) because we want the journey itself
//   to feel like a scene.
const DOCUMENTARY_GAP_MS = 2200;
const DOCUMENTARY_TRAVEL_S = 4.2;

function enterDocumentary() {
  if (documentaryActive) return;
  documentaryActive = true;
  closeTransientPanels();
  document.body.classList.add('documentary-mode');
  buildDocumentaryTimeline();
  documentaryPaused = false;
  updatePlayPauseLabel();
  // Cinematic slow orbit around whichever body is being narrated. The
  // built-in OrbitControls autoRotate runs only when controls.enabled is
  // true, so it pauses automatically during focus transitions and stays
  // dormant while we're moving between bodies — exactly what we want.
  controls.autoRotate = true;
  controls.autoRotateSpeed = 0.55;
  playDocumentaryChapter(0);
}

// returnHome: false leaves the camera where it is, for a caller that flies
// it somewhere itself (a lesson scene).
function exitDocumentary({ returnHome = true } = {}) {
  if (!documentaryActive) return;
  documentaryActive = false;
  clearTimeout(documentaryTimer);
  documentaryTimer = null;
  clearTimeout(documentaryGapTimer);
  documentaryGapTimer = null;
  clearTimeout(chapterAudioStartTimer);
  chapterAudioStartTimer = null;
  chapterAudioReadyToStart = false;
  controls.autoRotate = false;
  stopChapterAudio();
  document.body.classList.remove('documentary-mode');
  const subtitle = document.getElementById('doc-subtitle-text');
  const chapterLabel = document.getElementById('doc-chapter-label');
  if (subtitle) subtitle.classList.remove('visible');
  if (chapterLabel) chapterLabel.classList.remove('visible');
  // A chapter's cinematic travel (focusOnObject) may still be in flight,
  // leaving isAnimating=true and an active tween. returnToOverview() bails
  // immediately while isAnimating is set, so the camera would never pull
  // back and exit would appear to do nothing. Kill the travel tween and
  // clear the animating flag first — same interrupt pattern focusOnObject
  // uses — so the overview return always runs.
  if (activeCameraTween) activeCameraTween.kill();
  activeCameraTween = null;
  controls.enabled = true;
  isAnimating = false;
  if (returnHome) returnToOverview();
}

function toggleDocumentaryPause() {
  if (!documentaryActive) return;
  documentaryPaused = !documentaryPaused;
  updatePlayPauseLabel();
  controls.autoRotate = !documentaryPaused;
  if (documentaryPaused) {
    clearTimeout(documentaryTimer);
    documentaryTimer = null;
    clearTimeout(documentaryGapTimer);
    documentaryGapTimer = null;
    if (chapterAudio) chapterAudio.pause();
  } else if (chapterAudio && chapterAudio.currentTime > 0 && !chapterAudio.ended) {
    // Resume the in-flight narration audio if we have one mid-stream
    chapterAudio.play().catch(() => {});
  } else if (chapterAudioReadyToStart && chapterAudio) {
    // Travel finished while paused — now we can start the voice
    maybeStartChapterAudio();
  } else {
    // No audio in flight (TTS failed or finished) — restart the
    // fixed-duration fallback timer
    scheduleNextChapter();
  }
}

function updatePlayPauseLabel() {
  const btn = document.getElementById('doc-play-pause');
  if (btn) btn.textContent = documentaryPaused ? 'Play' : 'Pause';
}

function buildDocumentaryTimeline() {
  const container = document.getElementById('doc-chapters');
  if (!container || container.children.length) return;
  DOCUMENTARY_CHAPTERS.forEach((chapter, i) => {
    const dot = document.createElement('button');
    dot.className = 'doc-chapter-dot';
    dot.setAttribute('aria-label', `Jump to ${chapter.label}`);
    dot.addEventListener('click', () => {
      if (!documentaryActive) return;
      playDocumentaryChapter(i);
    });
    const tip = document.createElement('span');
    tip.className = 'doc-chapter-tooltip';
    tip.textContent = chapter.label;
    dot.appendChild(tip);
    container.appendChild(dot);
  });
}

function highlightDocumentaryDot(index) {
  const container = document.getElementById('doc-chapters');
  if (!container) return;
  Array.from(container.children).forEach((dot, i) => {
    dot.classList.toggle('active', i === index);
  });
}

// Audio state for the current chapter. Null when TTS isn't playing.
let chapterAudio = null;
let chapterAudioToken = 0; // increments each chapter so stale fetches no-op
let chapterAudioReadyToStart = false; // loaded + travel complete + not yet played
let chapterAudioStartTimer = null;    // timer that triggers audio after travel

function playDocumentaryChapter(index) {
  if (!documentaryActive) return;
  documentaryIndex = Math.max(0, Math.min(DOCUMENTARY_CHAPTERS.length - 1, index));
  const chapter = DOCUMENTARY_CHAPTERS[documentaryIndex];
  highlightDocumentaryDot(documentaryIndex);
  focusOnChapterBody(chapter.id);
  showDocumentaryNarration(chapter);

  // Cancel any in-flight chapter audio + invalidate pending fetches
  stopChapterAudio();
  const myToken = ++chapterAudioToken;
  chapterAudioReadyToStart = false;

  // Wait out the camera travel before the voice kicks in. The subtitle
  // still fades in early so the viewer has something to anchor to while
  // the camera moves — but the narrator only starts once we've arrived.
  clearTimeout(chapterAudioStartTimer);
  chapterAudioStartTimer = setTimeout(() => {
    chapterAudioStartTimer = null;
    if (myToken !== chapterAudioToken) return;
    chapterAudioReadyToStart = true;
    maybeStartChapterAudio();
  }, DOCUMENTARY_TRAVEL_S * 1000);

  loadChapterAudio(chapter.id).then((audio) => {
    if (myToken !== chapterAudioToken) {
      audio?.pause();
      return;
    }
    if (!audio) {
      // No pre-generated MP3. Fall back to the fixed-duration timer,
      // delayed by the travel time so advancement still feels paced.
      setTimeout(() => {
        if (myToken === chapterAudioToken) scheduleNextChapter();
      }, DOCUMENTARY_TRAVEL_S * 1000);
      return;
    }
    chapterAudio = audio;
    audio.addEventListener('ended', () => {
      if (myToken !== chapterAudioToken) return;
      scheduleAdvanceAfterGap();
    });
    maybeStartChapterAudio();
  });
}

// Play the loaded chapter audio only once BOTH conditions hold:
//   (a) the camera has finished its cinematic travel to the body, and
//   (b) we have an audio element ready.
// Called whenever either condition flips; self-cancels if paused.
function maybeStartChapterAudio() {
  if (!documentaryActive) return;
  if (documentaryPaused) return;
  if (!chapterAudioReadyToStart) return;
  if (!chapterAudio) return;
  if (!chapterAudio.paused) return; // already playing
  if (chapterAudio.currentTime > 0) return; // already started, user paused/resumed path handles it
  chapterAudio.play().catch(() => scheduleNextChapter());
}

function advanceChapter() {
  if (!documentaryActive) return;
  if (documentaryIndex < DOCUMENTARY_CHAPTERS.length - 1) {
    playDocumentaryChapter(documentaryIndex + 1);
  } else {
    exitDocumentary();
  }
}

// Sit for DOCUMENTARY_GAP_MS after the narration ends before moving on.
// Subtitle fades out immediately so the silent beat feels intentional
// rather than like the screen forgot to do anything.
function scheduleAdvanceAfterGap() {
  clearTimeout(documentaryGapTimer);
  documentaryGapTimer = null;
  if (documentaryPaused) return;
  fadeOutDocumentarySubtitle();
  documentaryGapTimer = setTimeout(() => {
    documentaryGapTimer = null;
    advanceChapter();
  }, DOCUMENTARY_GAP_MS);
}

// Fixed-duration fallback used when TTS is unavailable.
function scheduleNextChapter() {
  clearTimeout(documentaryTimer);
  documentaryTimer = null;
  if (documentaryPaused) return;
  const chapter = DOCUMENTARY_CHAPTERS[documentaryIndex];
  documentaryTimer = setTimeout(advanceChapter, chapter.duration);
}

function stopChapterAudio() {
  if (chapterAudio) {
    chapterAudio.pause();
    chapterAudio.src = '';
    chapterAudio = null;
  }
}

// Load the pre-generated narration audio for a chapter. Returns an
// HTMLAudioElement on success, null if the file isn't present (e.g.,
// scripts/generate-narration.js wasn't run, or the build didn't include
// it). The audio is generated ONCE at build time and served as a static
// asset — no runtime ElevenLabs calls.
function loadChapterAudio(chapterId) {
  return new Promise((resolve) => {
    const audio = new Audio(`/narration/${chapterId}.mp3`);
    let settled = false;
    const settle = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    audio.addEventListener('canplaythrough', () => settle(audio), { once: true });
    audio.addEventListener('error', () => settle(null), { once: true });
    // Safety timeout — if neither event fires within 4s, give up.
    setTimeout(() => settle(null), 4000);
  });
}

function focusOnChapterBody(id) {
  // Outro has no associated body — pull the camera back to the system
  // overview so the final lines play over the full scene.
  if (id === 'outro') {
    returnToOverview();
    return;
  }
  const target = id === 'sun' ? sun : planets[id];
  if (target) focusOnObject(target, { duration: DOCUMENTARY_TRAVEL_S, ease: 'power3.inOut' });
}

function fadeOutDocumentarySubtitle() {
  const subtitle = document.getElementById('doc-subtitle-text');
  const chapterLabel = document.getElementById('doc-chapter-label');
  if (subtitle) subtitle.classList.remove('visible');
  if (chapterLabel) chapterLabel.classList.remove('visible');
}

function showDocumentaryNarration(chapter) {
  const subtitle = document.getElementById('doc-subtitle-text');
  const chapterLabel = document.getElementById('doc-chapter-label');
  if (!subtitle || !chapterLabel) return;
  subtitle.classList.remove('visible');
  chapterLabel.classList.remove('visible');
  setTimeout(() => {
    chapterLabel.textContent = chapter.label;
    subtitle.textContent = chapter.narration;
    chapterLabel.classList.add('visible');
    subtitle.classList.add('visible');
  }, 350);
}

// === To-Scale mode ===
// The planets and the Sun side by side at their true relative sizes,
// smallest to largest, stepped through with Left and Right (arrow keys or
// A/D, the edge buttons, the strip of stops under the caption, or a
// swipe). Every body is built by the viewer's own code (buildPlanetBody,
// buildSunBody) and sized with the realistic-mode helpers, so the lineup
// shows exactly what the viewer draws: Earth's shader, the textured ice
// giants the viewer shows on focus, the Sun with its corona and
// prominences. The lineup is built on entry, once any texture the viewer
// had evicted is back, and disposed on exit; the main scene is only
// hidden, so it comes back untouched.

let scaleModeActive = false;
let scaleGroup = null;
let scaleTween = null;
let scaleSavedCamera = null;
let scaleSavedVisibility = null;
let scaleIndex = 0;
// Bumped on every entry and exit, so a lineup whose textures arrive after
// the user has already left is never built.
let scaleEntrySerial = 0;
let scaleCaptionTimer = 0;
// The sky the lineup keeps behind it: the Milky Way map, the catalogue
// stars and the deep-sky photos.
const SCALE_SKY_LAYERS = new Set(['skybox', 'star-catalog', 'star-catalog-faint', 'deep-sky-objects']);

// Hides anything the viewer shows behind the lineup after entry, keeping
// its visibility for exit. A #to-scale link opens the lineup while the page
// is still adding layers (the zodiacal light, the planets as points, the
// belt), and some layers set their own visibility every frame, so this runs
// just before each frame is drawn.
function hideSceneForScale() {
  if (!scaleSavedVisibility) return;
  for (const child of scene.children) {
    if (!child.visible || child === scaleGroup || SCALE_SKY_LAYERS.has(child.name)) continue;
    if (!scaleSavedVisibility.some(([saved]) => saved === child)) scaleSavedVisibility.push([child, true]);
    child.visible = false;
  }
}

// Real equatorial diameters in km, smallest to largest. The Sun closes
// the line so the jump from Jupiter to the star lands last.
const SCALE_BODIES = [
  { name: 'pluto',   label: 'Pluto',   diameter: 2376 },
  { name: 'mercury', label: 'Mercury', diameter: 4879 },
  { name: 'mars',    label: 'Mars',    diameter: 6779 },
  { name: 'venus',   label: 'Venus',   diameter: 12104 },
  { name: 'earth',   label: 'Earth',   diameter: 12742 },
  { name: 'neptune', label: 'Neptune', diameter: 49244 },
  { name: 'uranus',  label: 'Uranus',  diameter: 50724 },
  { name: 'saturn',  label: 'Saturn',  diameter: 116460 },
  { name: 'jupiter', label: 'Jupiter', diameter: 139820 },
  { name: 'sun',     label: 'Sun',     diameter: 1391000, isStar: true },
];
// The stop after the Sun frames the whole line.
const SCALE_OVERVIEW_INDEX = SCALE_BODIES.length;
const SCALE_EARTH_DIAMETER = 12742;

// Lineup units per km. Earth comes out at its authored radius, so the
// viewer's most involved shader runs at exactly the size it was tuned at
// and every other body is scaled from there.
const SCALE_UNITS_PER_KM = celestialData.earth.radius / (SCALE_EARTH_DIAMETER / 2);

// The lineup's sunlight comes from in front of the line and a little above
// it, so each body keeps a thin terminator on its lower limb while both
// neighbours in a frame show their lit faces (light from one side put the
// near face of the next body in night, lost against space). Shaders that
// take a sun position get a point this far along it from their own centre.
// A video can light the line from elsewhere with ?lineuplight=x,y,z: the
// planets video lights it from the Sun's end, so each body shows a
// terminator and Saturn's shadow lies across its rings.
const SCALE_SUN_DIR = scaleLightFromUrl() || new THREE.Vector3(0.2, 0.3, 1).normalize();
const SCALE_SUN_DISTANCE = 1e6;

function scaleLightFromUrl() {
  if (typeof window === 'undefined') return null;
  const m = /[?&]lineuplight=(-?[\d.]+),(-?[\d.]+),(-?[\d.]+)/.exec(window.location.search);
  if (!m) return null;
  const dir = new THREE.Vector3(Number(m[1]), Number(m[2]), Number(m[3]));
  return dir.lengthSq() > 1e-6 ? dir.normalize() : null;
}

// How each body faces the camera (radians about X, then Y): a little
// above its equator, Saturn high enough over the rings to open them into
// the familiar ellipse, Uranus turned until its rings read as the near
// bull's-eye it shows today, Earth turned so its axis leans toward the Sun.
const SCALE_BODY_VIEW = {
  earth: { x: 0.12, y: -Math.PI / 2 },
  saturn: { x: 0.42, y: 0 },
  uranus: { x: 0.2, y: 1.1 },
};
const SCALE_DEFAULT_VIEW = { x: 0.18, y: 0 };

// A framed body's diameter takes this share of the frame's short side.
const SCALE_FRAME_FILL = 0.42;
// Gap between neighbours, in units of the smaller one's half-width: tight
// enough that from any stop the next body's limb is in shot (the Sun's
// fills the right of the Jupiter frame), so every frame is a comparison.
const SCALE_GAP = 1.2;
// Turning rate of every lineup body, rad/s.
const SCALE_SPIN = 0.06;
const SCALE_STEP_SECONDS = 1.3;
// Field of view in the lineup, fixed so every entry frames alike (the
// viewer's hero framings leave their own behind).
const SCALE_FOV = 40;
// How far above the frame centre the line sits, in half-heights, so the
// caption under it never lands on a body. The overview's Sun is the
// biggest thing on screen and needs the most room.
const SCALE_LIFT = 0.08;
const SCALE_OVERVIEW_LIFT = 0.14;
let scaleLift = SCALE_LIFT;

// Textures the lineup draws, loaded before it is built: the viewer
// evicts the maps of bodies far from the focus, and building without
// them would give those bodies a flat colour.
function scaleTextureNames() {
  const names = new Set(['sun', 'saturn_ring', 'mars_bump', 'mercury_bump']);
  SCALE_BODIES.forEach((entry) => {
    const texture = celestialData[entry.name] && celestialData[entry.name].texture;
    if (texture) names.add(texture);
  });
  return [...names];
}

function buildScaleGroup() {
  const group = new THREE.Group();
  group.name = 'scaleLineup';

  const bodies = [];
  let cursorX = 0;
  let prevExtent = 0;
  SCALE_BODIES.forEach((entry, i) => {
    const data = celestialData[entry.name];
    const radius = (entry.diameter / 2) * SCALE_UNITS_PER_KM;
    const scale = radius / data.radius;
    const object = entry.isStar ? buildSunBody() : buildPlanetBody(entry.name, { lineup: true });
    if (entry.isStar) setSunBodyScale(object, scale);
    else setPlanetBodyScale(object, scale);

    // Half-width of what is drawn: rings count, so neighbours never
    // overlap Saturn's and the framing keeps them in shot.
    const extent = data.hasRings ? Math.max(radius, data.ringOuterRadius * scale) : radius;
    if (i > 0) cursorX += Math.min(prevExtent, extent) * SCALE_GAP;
    const x = cursorX + extent;
    object.position.set(x, 0, 0);
    const view = SCALE_BODY_VIEW[entry.name] || SCALE_DEFAULT_VIEW;
    if (!entry.isStar) object.rotation.set(view.x, view.y, 0);
    group.add(object);
    cursorX = x + extent;
    prevExtent = extent;

    const centre = new THREE.Vector3(x, 0, 0);
    const sunPosition = centre.clone().addScaledVector(SCALE_SUN_DIR, SCALE_SUN_DISTANCE);
    const tiltGroup = object.getObjectByName('tiltGroup');
    bodies.push({
      ...entry,
      object,
      x,
      radius,
      extent,
      centre,
      sunPosition,
      surface: entry.isStar ? object.children[0] : tiltGroup && tiltGroup.children[0],
      atmosphere: object.getObjectByName('atmosphere'),
      ring: object.getObjectByName('ring'),
      prominences: object.getObjectByName('sunProminences'),
      bandPhase: 0
    });
  });

  group.updateMatrixWorld(true);
  bodies.forEach(aimScaleBodyLight);

  // Lights for the bodies on standard materials, matching the viewer's
  // sunlight and ambient (createSun); the viewer's own are hidden with the
  // rest of the main scene.
  const sunLight = new THREE.DirectionalLight(0xfff7eb, 3.1);
  sunLight.position.copy(SCALE_SUN_DIR).multiplyScalar(1000);
  group.add(sunLight);
  group.add(new THREE.AmbientLight(0x1a1d28, 0.42));

  group.userData.bodies = bodies;
  group.userData.span = cursorX;
  return group;
}

// Points every sun-facing uniform of a lineup body at the lineup's light.
// The bodies stand still, so this runs once; updateScaleLineup only moves
// what follows the spin and the clock.
function aimScaleBodyLight(body) {
  const surfaceUniforms = body.surface && body.surface.material && body.surface.material.uniforms;
  if (surfaceUniforms) {
    if (surfaceUniforms.sunPosition) surfaceUniforms.sunPosition.value.copy(body.sunPosition);
    if (surfaceUniforms.uSunDir) surfaceUniforms.uSunDir.value.copy(SCALE_SUN_DIR);
    // Earth's polar ice follows the spin axis, which the view turn moved.
    if (surfaceUniforms.uSpinAxis) {
      surfaceUniforms.uSpinAxis.value.set(0, 1, 0)
        .applyQuaternion(body.surface.parent.getWorldQuaternion(new THREE.Quaternion()));
    }
  }
  [body.atmosphere, body.ring].forEach((mesh) => {
    const u = mesh && mesh.material && mesh.material.uniforms;
    if (!u) return;
    if (u.sunPosition) u.sunPosition.value.copy(body.sunPosition);
    if (u.planetCenter) u.planetCenter.value.copy(body.centre);
  });
  // The corona billboard only helps a Sun too small to see; here it is
  // always large on screen, as at the viewer's own Sun close-up.
  const glow = body.object.getObjectByName('sunGlowSprite');
  if (glow) glow.visible = false;
}

const _scaleSunLocal = new THREE.Vector3();
const _scaleInverse = new THREE.Matrix4();

// Per-frame drive of the lineup, the counterpart of what animate() does
// for the viewer's bodies: spin, shader clocks, gas-giant band drift and
// the Sun's prominences.
function updateScaleLineup(delta, elapsed) {
  if (!scaleModeActive || !scaleGroup) return;
  for (const body of scaleGroup.userData.bodies) {
    if (body.isStar) {
      body.object.rotation.y = wrapAngle(body.object.rotation.y + SCALE_SPIN * 0.5 * delta);
      body.object.children.forEach((child) => {
        const u = child.material && child.material.uniforms;
        if (!u) return;
        if (u.octaveLod) u.octaveLod.value = 1;
        if (u.time) u.time.value = elapsed;
      });
      updateSunProminenceGroup(body.prominences, elapsed, camera.position.distanceTo(body.centre), body.radius);
      continue;
    }

    const surface = body.surface;
    if (!surface) continue;
    surface.rotation.y = wrapAngle(surface.rotation.y + SCALE_SPIN * delta);
    const material = surface.material;
    const u = material.uniforms;
    if (u) {
      if (u.time) u.time.value = elapsed;
      if (u.uTime) u.uTime.value = elapsed;
    }
    const bands = material.userData && material.userData.bandUniforms;
    if (bands) {
      const wrapPeriod = 1 / Math.max(bands.bandRate.value, 1e-5);
      body.bandPhase = (body.bandPhase + delta * 0.02) % wrapPeriod;
      bands.bandTime.value = body.bandPhase;
      if (bands.gasGrainTime) bands.gasGrainTime.value = (performance.now() % 100000) * 0.001;
    }
    // Saturn's ring shadow is traced in the spinning mesh's own frame.
    const ringShadow = material.userData && material.userData.ringShadowUniforms;
    if (ringShadow) {
      surface.updateWorldMatrix(true, false);
      _scaleInverse.copy(surface.matrixWorld).invert();
      ringShadow.sunPosLocal.value.copy(_scaleSunLocal.copy(body.sunPosition).applyMatrix4(_scaleInverse));
    }
    const atmosphereUniforms = body.atmosphere && body.atmosphere.material.uniforms;
    if (atmosphereUniforms && atmosphereUniforms.time) atmosphereUniforms.time.value = elapsed;
    const ringUniforms = body.ring && body.ring.material.uniforms;
    if (ringUniforms && ringUniforms.spokeTime) ringUniforms.spokeTime.value = elapsed;
  }
}

function disposeScaleGroup(group) {
  group.traverse((o) => {
    if (o.geometry) o.geometry.dispose();
    if (!o.material) return;
    // The glow billboard's canvas texture is made per Sun; every other
    // texture is the viewer's own and stays.
    if (o.isSprite && o.material.map) o.material.map.dispose();
    o.material.dispose();
  });
}

// Camera distance that frames a body of `radius` at SCALE_FRAME_FILL of
// the frame's short side, with its rings (`extent`) inside the width.
function scaleFrameDistance(radius, extent) {
  const tanV = Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5));
  const tanH = tanV * camera.aspect;
  return Math.max(
    radius / (SCALE_FRAME_FILL * Math.min(tanV, tanH)),
    extent / (0.8 * tanH)
  );
}

// Camera x, distance and lift for a stop: a body centred in frame, or
// the whole line for the overview.
function scaleStop(index) {
  const bodies = scaleGroup.userData.bodies;
  if (index >= bodies.length) {
    const span = scaleGroup.userData.span;
    const star = bodies[bodies.length - 1];
    const tanV = Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5));
    const tanH = tanV * camera.aspect;
    // A margin each side keeps Pluto clear of the edge button and the
    // Sun's near limb, which perspective widens, inside the frame.
    return {
      x: span / 2,
      z: Math.max(span / 2 / (0.78 * tanH), star.radius / (0.58 * tanV)),
      lift: SCALE_OVERVIEW_LIFT
    };
  }
  const body = bodies[index];
  return { x: body.x, z: scaleFrameDistance(body.radius, body.extent), lift: SCALE_LIFT };
}

// Fly to a stop. The camera slides along the line and its distance eases
// in log space, so a step from Jupiter to the Sun (ten times the distance)
// moves as smoothly as Pluto to Mercury. A step pressed mid-flight starts
// from wherever the camera is, so repeated presses chain without a jolt.
function scaleGoTo(index, duration = SCALE_STEP_SECONDS) {
  if (!scaleGroup) return;
  scaleIndex = THREE.MathUtils.clamp(index, 0, SCALE_OVERVIEW_INDEX);
  updateScaleUi();
  const stop = scaleStop(scaleIndex);
  if (scaleTween) {
    scaleTween.kill();
    scaleTween = null;
  }
  const fromX = camera.position.x;
  const fromLogZ = Math.log(Math.max(camera.position.z, 1e-3));
  const toLogZ = Math.log(stop.z);
  const fromLift = scaleLift;
  const tanV = Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5));
  // The camera drops below the line and looks straight ahead, which
  // lifts the line in frame without tilting it.
  const place = (t) => {
    const z = Math.exp(THREE.MathUtils.lerp(fromLogZ, toLogZ, t));
    scaleLift = THREE.MathUtils.lerp(fromLift, stop.lift, t);
    camera.position.set(THREE.MathUtils.lerp(fromX, stop.x, t), -scaleLift * z * tanV, z);
    camera.lookAt(camera.position.x, camera.position.y, 0);
  };
  if (duration <= 0) {
    place(1);
    return;
  }
  const state = { t: 0 };
  scaleTween = gsap.to(state, {
    t: 1,
    duration,
    ease: 'power2.inOut',
    onUpdate: () => place(state.t),
    onComplete: () => { scaleTween = null; }
  });
}

function scaleStep(direction) {
  if (!scaleModeActive || !scaleGroup) return;
  const next = scaleIndex + direction;
  if (next < 0 || next > SCALE_OVERVIEW_INDEX) return;
  scaleGoTo(next);
}

function scaleTimesEarth(diameter) {
  const ratio = diameter / SCALE_EARTH_DIAMETER;
  if (ratio < 1) return ratio.toFixed(2);
  if (ratio < 20) return ratio.toFixed(1);
  return Math.round(ratio).toString();
}

function setScaleCaption(title, detail) {
  const label = document.getElementById('scale-body-label');
  const diameter = document.getElementById('scale-body-diameter');
  if (!label || !diameter) return;
  label.classList.remove('visible');
  diameter.classList.remove('visible');
  clearTimeout(scaleCaptionTimer);
  scaleCaptionTimer = setTimeout(() => {
    label.textContent = title;
    diameter.textContent = detail;
    label.classList.add('visible');
    diameter.classList.add('visible');
  }, 220);
}

function scaleStopName(index) {
  return index >= SCALE_OVERVIEW_INDEX ? 'All' : SCALE_BODIES[index].label;
}

// The stop for a body's name ('all' for the whole line), the first stop
// for a name the lineup does not have.
function scaleStopIndexOf(name) {
  const key = String(name || '').toLowerCase();
  if (key === 'all') return SCALE_OVERVIEW_INDEX;
  return Math.max(0, SCALE_BODIES.findIndex((entry) => entry.name === key));
}

// The lineup as plain numbers for the video renderer's lineup camera
// (src/video/shotcam.js): every body's centre on the x axis, its radius
// and drawn half-width (rings count), and the line's length. Null until
// the lineup is built.
function scaleLineupLayout() {
  if (!scaleModeActive || !scaleGroup) return null;
  return {
    bodies: scaleGroup.userData.bodies.map((body) => ({
      name: body.name, label: body.label, diameter: body.diameter, x: body.x, radius: body.radius, extent: body.extent
    })),
    span: scaleGroup.userData.span
  };
}

// Caption, edge buttons and strip for the current stop.
function updateScaleUi() {
  if (scaleIndex >= SCALE_OVERVIEW_INDEX) {
    setScaleCaption('Side by side', 'Pluto to the Sun at true scale');
  } else {
    const body = SCALE_BODIES[scaleIndex];
    const km = `${body.diameter.toLocaleString()} km`;
    setScaleCaption(
      body.label,
      body.name === 'earth' ? `${km} · the yardstick` : `${km} · ${scaleTimesEarth(body.diameter)} × Earth`
    );
  }

  const prev = document.getElementById('scale-prev');
  const next = document.getElementById('scale-next');
  const atStart = scaleIndex <= 0;
  const atEnd = scaleIndex >= SCALE_OVERVIEW_INDEX;
  if (prev) {
    prev.disabled = atStart;
    const name = document.getElementById('scale-prev-name');
    if (name) name.textContent = atStart ? '' : scaleStopName(scaleIndex - 1);
  }
  if (next) {
    next.disabled = atEnd;
    const name = document.getElementById('scale-next-name');
    if (name) name.textContent = atEnd ? '' : scaleStopName(scaleIndex + 1);
  }
  document.querySelectorAll('#scale-strip .scale-stop').forEach((button) => {
    const active = Number(button.dataset.index) === scaleIndex;
    button.classList.toggle('active', active);
    button.setAttribute('aria-current', active ? 'true' : 'false');
  });
}

// The strip of stops under the caption, one per body plus the overview.
function buildScaleStrip() {
  const strip = document.getElementById('scale-strip');
  if (!strip || strip.childElementCount) return;
  for (let i = 0; i <= SCALE_OVERVIEW_INDEX; i++) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'scale-stop';
    button.dataset.index = String(i);
    button.setAttribute('aria-label', scaleStopName(i));
    button.innerHTML = `<span class="scale-stop-dot"></span><span class="scale-stop-name">${scaleStopName(i)}</span>`;
    button.addEventListener('click', () => scaleGoTo(i));
    strip.appendChild(button);
  }
}

async function enterScaleMode(startIndex = 0) {
  if (scaleModeActive) return;
  if (documentaryActive) return;
  if (flyMode) return;
  scaleModeActive = true;
  const serial = ++scaleEntrySerial;
  modeEnter('scale');
  closeTransientPanels();

  // A focus flight still in the air would keep writing the camera.
  if (activeCameraTween) {
    activeCameraTween.kill();
    activeCameraTween = null;
    isAnimating = false;
  }

  // Save camera state so exit can restore exactly what the user was
  // looking at before they triggered the mode.
  scaleSavedCamera = {
    position: camera.position.clone(),
    target: controls.target.clone(),
    near: camera.near,
    fov: camera.fov
  };
  controls.enabled = false;
  controls.autoRotate = false;

  // Save-and-hide every existing scene child except the sky, so the
  // starfield keeps its role as backdrop. Scale group is added after the
  // save pass so it never appears in the saved list.
  scaleSavedVisibility = [];
  scene.children.forEach((child) => {
    if (SCALE_SKY_LAYERS.has(child.name)) return;
    scaleSavedVisibility.push([child, child.visible]);
    child.visible = false;
  });

  buildScaleStrip();
  scaleIndex = THREE.MathUtils.clamp(startIndex, 0, SCALE_OVERVIEW_INDEX);
  updateScaleUi();
  setScaleCaption('To scale', 'Loading');
  document.body.classList.add('scale-mode');

  await Promise.all(scaleTextureNames().map((name) => loadTextureByName(name)));
  if (serial !== scaleEntrySerial) return;

  try {
    scaleGroup = buildScaleGroup();
  } catch (err) {
    // Hand the viewer back rather than leave an empty starfield.
    console.error('[to-scale] could not build the lineup', err);
    exitScaleMode();
    return;
  }
  scene.add(scaleGroup);
  camera.fov = SCALE_FOV;
  setCameraNearPlane(0.02);
  scaleLift = SCALE_LIFT;
  scaleGoTo(scaleIndex, 0);
}

function exitScaleMode() {
  if (!scaleModeActive) return;
  scaleModeActive = false;
  scaleEntrySerial++;
  modeExit('scale');

  if (scaleTween) {
    scaleTween.kill();
    scaleTween = null;
  }
  if (scaleGroup) {
    scene.remove(scaleGroup);
    disposeScaleGroup(scaleGroup);
    scaleGroup = null;
  }

  // Restore every child's previous visibility — includes sun, planets,
  // asteroid belt, orbit lines, comets, satellites, NEOs, space probes,
  // dwarf planets, player ship, lights. No enumeration required.
  if (scaleSavedVisibility) {
    scaleSavedVisibility.forEach(([child, wasVisible]) => {
      child.visible = wasVisible;
    });
    scaleSavedVisibility = null;
  }

  if (scaleSavedCamera) {
    camera.position.copy(scaleSavedCamera.position);
    controls.target.copy(scaleSavedCamera.target);
    camera.lookAt(scaleSavedCamera.target);
    camera.fov = scaleSavedCamera.fov;
    setCameraNearPlane(scaleSavedCamera.near);
    scaleSavedCamera = null;
  }
  controls.enabled = true;

  clearTimeout(scaleCaptionTimer);
  const label = document.getElementById('scale-body-label');
  const diameter = document.getElementById('scale-body-diameter');
  if (label) label.classList.remove('visible');
  if (diameter) diameter.classList.remove('visible');

  document.body.classList.remove('scale-mode');
  // Leaving a lineup opened from a #to-scale link drops the link, so a
  // reload comes back to the viewer.
  if (getHashFocusId() === 'to-scale') clearFocusHash();
}

// Hide loading screen. We enforce a minimum display time so the intro
// actually reads; assets often load in under a second and the user
// doesn't see the title. After the opacity fade completes we apply
// .finished (display:none) so the screen is pulled out of the render
// tree entirely — otherwise its background image keeps composting
// every frame and costs GPU.
// The website holds its title card long enough to read; the screensaver has
// nothing to read and fades into the scene as soon as it is ready.
const LOADING_MIN_DISPLAY_MS = IS_SCREENSAVER ? 0 : 3000;
// Per-texture load timeout. Generous enough that the moon's 2.9 MB
// JPEG queued behind earth_clouds (11 MB) at the back of the browser's
// 6-connection pool doesn't fall through to the gray-sphere fallback.
// The eager batch (~50 MB total) can serialize end-to-end past the
// old 12 s budget on a cold cache, even on localhost.
const LOADING_TEXTURE_TIMEOUT_MS = 45000;
const LOADING_FAILSAFE_MS = 20000;
// How long the loading screen waits for the first frame's shaders to compile
// (compileShownNow in src/gpuwarm.js).
const BOOT_COMPILE_LIMIT_MS = 40000;
const loadingStartMs = performance.now();
let loadingScreenDismissed = false;
let loadingScreenFailsafe = setTimeout(() => {
  if (!loadingScreenDismissed) {
    console.warn('[loading] startup watchdog fired, hiding loader');
    hideLoadingScreen();
  }
}, LOADING_FAILSAFE_MS);

function hideLoadingScreen() {
  if (!loadingScreen || loadingScreenDismissed) return;
  loadingScreenDismissed = true;
  clearTimeout(loadingScreenFailsafe);
  // The funnel's viewer_open (docs/analytics.md): the viewer is ready.
  viewerOpened();
  loadingBar.style.width = '100%';
  loadingText.textContent = 'Ready';

  const elapsed = performance.now() - loadingStartMs;
  const wait = Math.max(0, LOADING_MIN_DISPLAY_MS - elapsed);

  setTimeout(() => {
    loadingScreen.classList.add('hidden');
    setTimeout(() => {
      loadingScreen.classList.add('finished');
    }, 900);
    startLessonFromUrl();
  }, wait);
}

function releaseLoadingEarth() {
  if (typeof window !== 'undefined' && typeof window.__stopLoadingEarth === 'function') {
    try {
      window.__stopLoadingEarth();
    } catch (err) {
      console.warn('[loading] unable to stop loading earth', err);
    }
  }
}

// Multi-star-system runtime — registry hookup, scene building for
// non-Sol systems, and the warp-jump teleport flow. Sol stays on the
// legacy createSun/createPlanets pipeline above; this section handles
// everything else.
// Build one non-Sol system into the scene, hidden until the player
// jumps in. Used for the authored catalogue at boot and for systems the
// navigation layer generates on arrival.
function mountRemoteSystem(data) {
  try {
    const handle = buildSystem(data, {
      renderer,
      // Lazy: Gargantua's lensing shader picks the sky up the first frame
      // its system is the one in view (lensSkyCube).
      getSkyboxTexture: () => lensSkyCube(activeSystemId === data.id, 'deep'),
    });
    scene.add(handle.root);
    handle.root.visible = false;
    remoteSystems[data.id] = handle;
    markClickablesDirty();
    return handle;
  } catch (err) {
    console.error(`[systems] failed to build ${data.id}:`, err);
    return null;
  }
}

function initRemoteSystems() {
  if (!scene) {
    console.warn('[systems] initRemoteSystems called before scene exists');
    return;
  }
  for (const data of listSystems()) {
    if (data.legacy) continue;
    mountRemoteSystem(data);
  }
  // Always boot into Sol. We used to resume the persisted remote system
  // on a fresh page load, but that teleport path (animate:false) only
  // moves the camera — it skips the warp/wormhole setup that the in-app
  // jump runs to frame and render a system. For Gargantua that dropped
  // the user straight into the heaviest raymarched system, parked
  // ~119,000 AU out where the accretion disk doesn't render: a black
  // viewport plus a slow boot. Remote systems stay fully explorable via
  // in-app travel within a session; they just don't survive a reload.
  // Resetting to Sol here also auto-heals any localStorage already stuck
  // on a remote system from before this fix.
  restoreCurrentSystem();
  if (getCurrentSystemId() !== 'sol') {
    setCurrentSystem('sol');
  }
  activeSystemId = getCurrentSystemId();
  markClickablesDirty();

  // Park the Gargantua return mouth in its system root. Has to wait
  // until after buildSystem() so the system handle exists in the
  // registry.
  ensureGargantuaReturnMouth();
}

/**
 * Resume the persisted system at the end of init(), after the Sol
 * focus logic has already run. If the player left from AC last
 * session, this is what actually puts them back at AC instead of
 * staring at Sol's startup focus.
 */
function resumeActiveSystemPostFocus() {
  if (activeSystemId === 'sol') return;
  if (!remoteSystems[activeSystemId]) {
    activeSystemId = 'sol';
    setCurrentSystem('sol');
    markClickablesDirty();
    return;
  }
  showSystem(activeSystemId, { teleport: true, animate: false });
}

/**
 * Push every interactive mesh from the currently-active remote system
 * into the given clickableObjects array. Used by checkHover() and the
 * click handler so the user can hover/click stars and planets in
 * non-Sol systems. Skips minor decorative meshes (the BH disc
 * raymarcher's huge sphere, the wormhole halo) so the raycast lands
 * on the intended body rather than its shading volume.
 */
function addRemoteSystemClickables(out) {
  if (activeSystemId === 'sol') return;
  const handle = remoteSystems[activeSystemId];
  if (!handle) return;
  for (const id in handle.bodies) {
    const group = handle.bodies[id];
    if (!group) continue;
    group.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      // Skip the Gargantua raymarcher sphere — it's the full scene
      // dome, not the BH itself; clicking should still target it but
      // via its enclosing group, not the giant outer mesh.
      if (child.name === 'gargantuaRaymarcher') {
        out.push(child);
        return;
      }
      // Skip the return-mouth halo (large faint outer sphere). The
      // inner mouth mesh still gets added so the wormhole stays
      // clickable.
      if (child.geometry instanceof THREE.SphereGeometry ||
          child.geometry instanceof THREE.IcosahedronGeometry) {
        out.push(child);
      }
    });
  }
}

function tickRemoteSystems(delta, elapsed) {
  for (const id in remoteSystems) {
    const handle = remoteSystems[id];
    if (handle && handle.root.visible) {
      handle.tick(delta, elapsed);
    }
  }
}

/**
 * Reveal a remote system and optionally teleport the camera near its
 * primary star. Hides every other remote system to keep the scene
 * lean. Used by both the warp-jump flow (animate=true) and the boot
 * resume path (animate=false).
 */
function showSystem(id, { teleport = false, animate: doAnimate = true } = {}) {
  for (const otherId in remoteSystems) {
    remoteSystems[otherId].root.visible = (otherId === id);
  }
  activeSystemId = id;
  setCurrentSystem(id);
  // The clickable cache bakes in the active system's bodies — rebuild
  // on switch so hover/click target the right system.
  markClickablesDirty();

  // Reset the bottom nav label + index to the primary star of the
  // new system so the prev/next arrows cycle the right catalog.
  // Without this, switching from Sol → Alpha Centauri left the
  // arrows walking through Sol's planets behind the scenes.
  const entries = getActiveNavBodies();
  currentNavIndex = 0;
  if (navLabel && entries.length) {
    navLabel.textContent = entries[0].displayName;
  }

  // Kill any in-flight camera tween + clear the focus selection
  // before teleporting. Without this, the focus tween (from
  // focusOnObject) keeps overwriting camera.position every frame in
  // its onUpdate callback, so the teleport "succeeds" but the camera
  // immediately snaps back to whichever Sol body was focused — that's
  // why visiting Alpha Centauri was reading as "still on Earth."
  if (teleport) {
    if (typeof interruptCameraMotion === 'function') interruptCameraMotion();
    if (typeof isAnimating !== 'undefined') isAnimating = false;
    if (typeof selectedObject !== 'undefined') selectedObject = null;
    if (typeof autopilot !== 'undefined' && autopilot && autopilot.active) {
      // Don't try to autopilot a Sol body while we're warping to AC.
      if (typeof cancelAutopilot === 'function') cancelAutopilot();
    }
    // Flight-mode hooks fight the teleport. orbitStabilized lerps the
    // camera back toward orbitTarget every frame (updateOrbitStabilization),
    // flyParkedBody keeps the ship locked to the parked body's world
    // frame, and leftover flyVelocity coasts the camera off after we
    // teleport. Tear them all down so the new system's standoff
    // sticks — same teardown pattern startAutopilotCruise uses.
    if (typeof clearOrbitStabilization === 'function') clearOrbitStabilization();
    if (typeof clearFlyParkedBody === 'function') clearFlyParkedBody({ inheritVelocity: false });
    if (typeof resetFlyInputState === 'function') resetFlyInputState();
    if (typeof orbitParked !== 'undefined') orbitParked = false;
    // OrbitControls only belongs to orbit mode. Enabling it in fly mode
    // lets its own pointer handlers turn a left-drag into a swing
    // around controls.target (the new star) instead of mouse-look.
    if (controls) {
      controls.enabled = !flyMode;
    }
  }

  if (id === 'sol') {
    activeSystemTarget = null;
    if (teleport) {
      // Drop the camera back near Sol at a respectful distance so the
      // sun reads as the centerpiece, not a wall of bloom.
      camera.position.set(0, 60, 220);
      camera.lookAt(0, 0, 0);
      if (controls) {
        controls.target.set(0, 0, 0);
        controls.update();
      }
      if (typeof flyEuler !== 'undefined' && camera.quaternion) {
        flyEuler.setFromQuaternion(camera.quaternion);
        if (typeof flyTargetEuler !== 'undefined') flyTargetEuler.copy(flyEuler);
      }
      if (typeof syncPlayerShipToCamera === 'function') syncPlayerShipToCamera();
    }
    return;
  }

  const handle = remoteSystems[id];
  if (!handle) return;
  const data = handle.data;
  const primary = data.stars.find(s => s.isPrimary) || data.stars[0];
  if (!primary) return;
  activeSystemTarget = handle;

  if (!teleport) return;

  // Where the primary star sits in world space. The system root is
  // at scenePosition; the primary's local position is usually (0,0,0).
  const worldStarPos = new THREE.Vector3(
    (data.scenePosition?.x || 0) + (primary.position?.x || 0),
    (data.scenePosition?.y || 0) + (primary.position?.y || 0),
    (data.scenePosition?.z || 0) + (primary.position?.z || 0)
  );

  // Black-hole primaries need a hero-shot framing that mirrors the
  // wormhole arrival path (doWormholeMidpointSwap). The generic
  // standoff = radius * 14 was anchoring 1.5 M units out — pushing
  // the disk to a 2-degree dot. STANDOFF = 600 k frames the disk at
  // ~7° with the lensed arcs still visible above and below the
  // silhouette. Direction matches the Interstellar canonical shot so
  // the disk reads as the scene's hero subject from arrival.
  let camPos;
  if (primary.type === 'BlackHole') {
    const STANDOFF = 600_000;
    const heroOffset = new THREE.Vector3(0.62, 0.38, -0.68)
      .normalize()
      .multiplyScalar(STANDOFF);
    camPos = worldStarPos.clone().add(heroOffset);
  } else {
    // Camera offset from the star, scaled to the primary's size so big
    // stars get a wider standoff. Y lift gives a slight overhead read.
    const standoff = Math.max(150, primary.radius * 14);
    camPos = worldStarPos.clone().add(new THREE.Vector3(standoff, standoff * 0.35, standoff));
  }
  camera.position.copy(camPos);
  camera.lookAt(worldStarPos);
  if (controls) {
    controls.target.copy(worldStarPos);
    controls.update();
  }
  // Re-derive the fly-mode euler from the new camera orientation so
  // the next frame's mouse-look picks up the post-jump heading rather
  // than rotating back to the pre-jump pose. Same reason syncs the
  // ship — without it, the chase camera still has the ship parked
  // back in Sol space.
  if (typeof flyEuler !== 'undefined' && camera.quaternion) {
    flyEuler.setFromQuaternion(camera.quaternion);
    if (typeof flyTargetEuler !== 'undefined') flyTargetEuler.copy(flyEuler);
  }
  if (typeof syncPlayerShipToCamera === 'function') syncPlayerShipToCamera();
}

// Light-years between two systems: the nav catalogue's HYG positions
// when it is loaded (they agree with the route planner), otherwise
// the authored galaxyPosition fields.
function systemDistanceLy(fromId, toId) {
  const posOf = (id) => {
    const star = navigation && navigation.catalog && navigation.catalog.starForSystem(id);
    if (star) return star;
    const sys = getSystem(id);
    return sys && sys.galaxyPosition;
  };
  const a = posOf(fromId);
  const b = posOf(toId);
  if (!a || !b) return 8;
  return Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
}

// Tunnel tint from a system's primary star temperature.
function systemStarTint(id) {
  const sys = getSystem(id);
  const primary = sys && sys.stars && (sys.stars.find((st) => st.isPrimary) || sys.stars[0]);
  const kelvin = (primary && primary.temperature) || 5778;
  const [r, g, b] = kelvinToRgb(kelvin);
  return new THREE.Color(r, g, b);
}

// Per-frame drive of the hyperspace layer. One group unit is sized so
// the tunnel sits just beyond the player's ship (the ship stays in
// front of it via the depth test) or a few near planes out when there
// is no ship, so it covers the rest of the scene.
const _hyperShipPos = new THREE.Vector3();
function updateHyperspace(delta) {
  if (!hyperspace || !hyperspace.isActive() || !camera) return;
  let unit = camera.near * 4;
  let occlude = false;
  if (flyMode && playerShip && playerShip.visible) {
    playerShip.getWorldPosition(_hyperShipPos);
    const shipDist = _hyperShipPos.distanceTo(camera.position);
    if (shipDist > 0) {
      unit = Math.max(unit, shipDist * 0.75);
      occlude = true;
    }
  }
  hyperspace.update(camera, delta, {
    unit,
    occludeWithDepth: occlude,
    width: window.innerWidth,
    height: window.innerHeight
  });
}

/**
 * Hyperspace jump to the named system (hyperspace.js draws it).
 *
 *   spool 2.4 s  stars stretch into streaks, drive shake builds
 *   punch 0.55 s the tunnel closes in from the edges, FOV punches wide
 *   cruise       3.5 to 13 s, longer for longer jumps, with a seeded
 *                +/-18% so no two jumps run the same; the system swap
 *                happens 45% in, under full tunnel cover
 *   exit  2.1 s  the tunnel opens from the centre onto the new star
 *
 * The destination's shaders are compiled during the spool so the swap
 * frame does not hitch. Re-entrant guard via starMapJumpInFlight.
 */
function jumpToSystem(id) {
  if (starMapJumpInFlight) return;
  if (id === activeSystemId) return;
  const target = getSystem(id);
  if (!target) {
    console.warn(`[systems] jumpToSystem: unknown ${id}`);
    return;
  }
  if (!camera || !scene) {
    console.warn('[systems] jumpToSystem called before scene ready');
    return;
  }

  // Sol ↔ Gargantua is reached through the Saturn-orbit wormhole, not
  // by warping across normal space. Route those two jumps through the
  // tunnel flythrough instead of the hyperspace jump.
  const isWormholePair =
    (activeSystemId === 'sol' && id === 'gargantua') ||
    (activeSystemId === 'gargantua' && id === 'sol');
  if (isWormholePair && triggerWormholeFlight(id, { fromStarMap: true })) {
    return;
  }

  starMapJumpInFlight = true;
  const fromId = activeSystemId;
  const distanceLy = systemDistanceLy(fromId, id);
  const seed = (Date.now() % 100000) + distanceLy * 131;
  const jitter = 0.82 + ((Math.sin(seed) * 43758.5453) % 1 + 1) % 1 * 0.36;
  const cruiseSeconds = THREE.MathUtils.clamp(2.6 + distanceLy * 0.42, 3.5, 13) * jitter;

  // Compile the destination's materials while the drive spools.
  const handle = remoteSystems[id];
  if (handle && renderer) {
    const wasVisible = handle.root.visible;
    handle.root.visible = true;
    // compile() walks the visible tree synchronously, so the root can
    // be hidden again straight away; only program linking is async.
    compileForSceneAsync(renderer, handle.root, camera, scene);
    handle.root.visible = wasVisible;
  }

  if (flyMode && flyVelocity) flyVelocity.set(0, 0, 0);

  const launch = () => {
    loadSpaceAmbientModule()
      .then((mod) => mod.playHyperspaceJump && mod.playHyperspaceJump({ ...HYPERSPACE_TIMING, cruise: cruiseSeconds }))
      .catch(() => {});

    hyperspace.start({
      cruiseSeconds,
      destinationName: target.name,
      distanceLy,
      fromColor: systemStarTint(fromId),
      toColor: systemStarTint(id),
      seed,
      onSwap: () => {
        jumpDeparture = null;
        showSystem(id, { teleport: true, animate: true });
      },
      onComplete: () => {
        starMapJumpInFlight = false;
        showStarMapToast(`Arrived at ${target.name}`);
      }
    });
  };

  // In fly mode the ship flies out of the jump: it turns onto a clear
  // heading if a body is in the way, then accelerates through the spool.
  if (flyMode && playerShip) beginJumpDeparture(launch);
  else launch();
}

// ---- Jump departure: clear heading + real motion into the tunnel ----------
//
// Before the tunnel closes the ship must visibly move, and never through
// a planet. planJumpDeparture measures how far the ship will travel
// during the spool (half the distance to the nearest surface, so the
// nearest body slides past rather than nothing moving), checks the
// forward path against every body with a margin for atmospheres and
// rings, and if it is blocked picks the clear direction closest to the
// current heading. updateJumpDeparture then turns the ship onto it and
// flies it along the heading until the swap.
let jumpDeparture = null;
const _jdPos = new THREE.Vector3();
const _jdTo = new THREE.Vector3();
const _jdMat = new THREE.Matrix4();

function forEachJumpObstacle(fn) {
  forEachFlightBody(fn);
  if (activeSystemId === 'sol') {
    for (const key in moons) {
      for (const moon of moons[key]) {
        if (moon && !(moon.userData && moon.userData.isMinorMoon)) fn(moon);
      }
    }
  }
}

function jumpObstacleClearance(body) {
  const r = getBodyBaseRadius(body);
  const kind = getBodyKind(body);
  if (kind === 'star') return r * 1.8 + 2;
  if (kind === 'giant') return r * 2.6 + 1; // rings
  return r * 1.5 + 0.5;
}

// First body the path origin + dir * [0, length] passes too close to.
function jumpPathBlocker(origin, dir, length) {
  let blocker = null;
  let blockerT = Infinity;
  forEachJumpObstacle((body) => {
    body.getWorldPosition(_jdPos);
    _jdTo.subVectors(_jdPos, origin);
    const t = THREE.MathUtils.clamp(_jdTo.dot(dir), 0, length);
    const miss = _jdTo.addScaledVector(dir, -t).length();
    if (miss < jumpObstacleClearance(body) && t < blockerT) {
      blocker = body;
      blockerT = t;
    }
  });
  return blocker;
}

function planJumpDeparture() {
  const origin = camera.position.clone();
  const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion).normalize();
  let nearest = Infinity;
  forEachJumpObstacle((body) => {
    body.getWorldPosition(_jdPos);
    nearest = Math.min(nearest, _jdPos.distanceTo(origin) - getBodyBaseRadius(body));
  });
  const spoolDistance = THREE.MathUtils.clamp((Number.isFinite(nearest) ? nearest : 200) * 0.5, 4, 4000);
  // The path checked covers the spool and the punch (the tunnel only
  // fully covers the view once the punch is done).
  const checkLength = spoolDistance * 4;
  const blocker = jumpPathBlocker(origin, forward, checkLength);
  let heading = forward;
  if (blocker) {
    // Fibonacci sphere of candidate headings; keep the clear one that
    // needs the smallest turn.
    const N = 400;
    const cand = new THREE.Vector3();
    let bestDot = -Infinity;
    for (let i = 0; i < N; i++) {
      const y = 1 - (i + 0.5) / N * 2;
      const rr = Math.sqrt(1 - y * y);
      const phi = i * Math.PI * (3 - Math.sqrt(5));
      cand.set(Math.cos(phi) * rr, y, Math.sin(phi) * rr);
      const d = cand.dot(forward);
      if (d <= bestDot) continue;
      if (jumpPathBlocker(origin, cand, checkLength)) continue;
      bestDot = d;
      heading = cand.clone();
    }
    // Boxed in (skimming a surface): climb straight away from it.
    if (bestDot === -Infinity) {
      blocker.getWorldPosition(_jdPos);
      heading = origin.clone().sub(_jdPos).normalize();
    }
  }
  const angle = forward.angleTo(heading);
  _jdMat.lookAt(origin, origin.clone().add(heading), camera.up);
  return {
    blocker,
    heading,
    spoolDistance,
    startQuat: camera.quaternion.clone(),
    endQuat: new THREE.Quaternion().setFromRotationMatrix(_jdMat),
    alignSeconds: angle > 0.02 ? 0.9 + (angle / Math.PI) * 2.4 : 0
  };
}

function beginJumpDeparture(launch) {
  const plan = planJumpDeparture();
  jumpDeparture = {
    ...plan,
    t: 0,
    launched: false,
    launchT: 0,
    launch,
    origin: camera.position.clone()
  };
  if (plan.blocker) {
    showStarMapToast(`Plotting a clear exit past ${plan.blocker.userData.name || 'the obstacle'}`);
  }
  if (plan.alignSeconds === 0) {
    jumpDeparture.launched = true;
    launch();
  }
}

function updateJumpDeparture(delta) {
  const jd = jumpDeparture;
  if (!jd || !camera) return;
  if (!flyMode) { jumpDeparture = null; if (!jd.launched) jd.launch(); return; }
  jd.t += Math.min(delta, 0.1);
  const { spoolDistance: D, heading } = jd;
  let dist;
  if (!jd.launched) {
    // Align: turn onto the heading while easing forward along it.
    const k = Math.min(1, jd.t / jd.alignSeconds);
    const e = k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2;
    camera.quaternion.slerpQuaternions(jd.startQuat, jd.endQuat, e);
    dist = D * 0.06 * k * k;
    if (k >= 1) {
      jd.launched = true;
      jd.launchT = jd.t;
      jd.alignDist = dist;
      jd.launch();
    }
  } else {
    // Spool then punch: accelerate along the heading; the punch runs
    // the distance up hard just as the tunnel closes over it.
    const s = jd.t - jd.launchT;
    const spool = HYPERSPACE_TIMING.spool;
    const base = jd.alignDist || 0;
    if (s <= spool) {
      dist = base + D * Math.pow(s / spool, 2.2);
    } else {
      const p = (s - spool) / HYPERSPACE_TIMING.punch;
      dist = base + D + D * 3 * p * p;
    }
    camera.quaternion.copy(jd.endQuat);
  }
  camera.position.copy(jd.origin).addScaledVector(heading, dist);
  camera.updateMatrixWorld(true);
  // Keep the flight model in step so it neither fights the drive nor
  // snaps back once the jump releases it.
  if (flyVelocity) flyVelocity.set(0, 0, 0);
  if (typeof flyAngularVelocity !== 'undefined') flyAngularVelocity.set(0, 0);
  flyEuler.setFromQuaternion(camera.quaternion);
  flyTargetEuler.copy(flyEuler);
  syncPlayerShipToCamera();
}

// Sol ↔ Gargantua wormhole flythrough. Replaces the standard warp-
// streak jump for this specific pair so the player flies through the
// tunnel rather than smash-cutting on warp factor. The midpoint
// handler swaps systems + teleports the camera to a position right
// outside the destination mouth, so the second half of the flight
// reads as continuous motion emerging from the bridge.
function triggerWormholeFlight(destinationId, { fromStarMap = false } = {}) {
  if (wormholeTunnelInFlight) return false;
  if (!wormholeTunnel) return false;
  if (!camera) return false;
  wormholeTunnelInFlight = true;
  if (fromStarMap) {
    starMapJumpInFlight = true;
  }

  // Orbit-mode focus state fights the tunnel ride. The flight is
  // driven from FLY MODE by the fast-travel menu, but the OBJECTS
  // browser can dispatch it from orbit mode while a body is focused —
  // and updateFocusedObjectFollow() then lerps camera + controls.target
  // back toward selectedObject every frame, dragging the midpoint
  // camera park straight back to the focused Sol body. Tear the focus
  // selection down before the ride starts, same as the teleport branch
  // of showSystem() does for the warp-streak jump.
  if (!flyMode) {
    interruptCameraMotion();
    selectedObject = null;
    _focusPrevObj = null;
    // Focus also tightened the OrbitControls distance clamps around the
    // focused body (e.g. Earth radius * 14). Orbit mode keeps calling
    // controls.update() every frame, which clamps the camera back to
    // maxDistance from the target — collapsing the 600k BH hero
    // standoff to ~2k, inside the raymarcher: black screen. Restore the
    // boot defaults from setupControls() so the arrival framing sticks.
    if (controls) {
      controls.minDistance = 20;
      controls.maxDistance = 50000000;
    }
  }

  const tint = destinationId === 'gargantua' ? 'sol-to-gargantua' : 'gargantua-to-sol';
  activeWormholeFlight = {
    sourceId: activeSystemId,
    destinationId,
    fromStarMap
  };
  lastWormholeTriggerMs = performance.now();

  wormholeTunnel.play({ tint }).catch(err => {
    console.error('[wormhole] flight failed', err);
    wormholeTunnelInFlight = false;
    if (fromStarMap) starMapJumpInFlight = false;
    activeWormholeFlight = null;
  });
  return true;
}

function doWormholeMidpointSwap() {
  const id = activeWormholeFlight && activeWormholeFlight.destinationId;
  if (!id) return;

  // Standard system swap (hides others, sets current, persists). The
  // showSystem helper handles autopilot cancellation + control state.
  showSystem(id, { teleport: false, animate: false });
  showStarMapToast(id === 'gargantua' ? 'Crossing the bridge into Gargantua space' : 'Returning to Sol');

  if (id === 'gargantua') {
    // Park the camera at a hero-shot distance from the BH itself,
    // not from the return mouth. The mouth is offset ~720k from the
    // BH; anchoring the standoff there put the camera 1M+ from the
    // disk and OUTSIDE the raymarcher's sphere — black screen.
    //
    // Frame: 600k off the BH on a 3/4 oblique view. Disk outer is
    // 72k, so angular size at 600k = atan(72k/600k) ≈ 6.85° — big
    // enough to read as the scene's hero subject, small enough that
    // the lensing arcs are still visible around the silhouette
    // rather than filling the entire frame.
    ensureGargantuaReturnMouth();
    const handle = remoteSystems['gargantua'];
    const bh = handle && handle.bodies && handle.bodies['gargantua'];
    if (!bh) return;
    const bhWorld = new THREE.Vector3();
    bh.getWorldPosition(bhWorld);
    const STANDOFF = 600_000;
    // Direction picked to mirror the Interstellar canonical shot —
    // disk tilted, silhouette dead-centre, the lensed arcs visible
    // above + below the horizon ring.
    const heroOffset = new THREE.Vector3(0.62, 0.38, -0.68)
      .normalize()
      .multiplyScalar(STANDOFF);
    camera.position.copy(bhWorld).add(heroOffset);
    camera.lookAt(bhWorld);
    if (controls) {
      controls.target.copy(bhWorld);
      controls.update();
    }
  } else {
    // Return to Sol: emerge by the Saturn-orbit mouth, looking back
    // toward the Sun so the inner system frames the arrival. The mouth
    // is a lazy deep-space body — the fast-travel menu force-loads it
    // before listing, but the OBJECTS browser dispatch doesn't, so
    // build it here or the camera park silently no-ops and the player
    // arrives in Sol still parked at Gargantua's coordinates.
    ensureDeepSpaceBodies();
    if (!wormholeObject) return;
    const mouthWorld = new THREE.Vector3();
    wormholeObject.getWorldPosition(mouthWorld);
    const sunWorld = new THREE.Vector3(0, 0, 0);
    const awayFromSun = mouthWorld.clone().sub(sunWorld).normalize();
    camera.position.copy(mouthWorld).add(awayFromSun.multiplyScalar(24));
    camera.lookAt(sunWorld);
    if (controls) {
      controls.target.copy(sunWorld);
      controls.update();
    }
  }
}

// Return mouth in the Gargantua system. Sibling to the Saturn-orbit
// wormhole on the Sol side but parked off-axis from the BH so the
// disk doesn't sit directly behind it. Created lazily after the
// gargantua system is built, since it parents into that system's root.
function ensureGargantuaReturnMouth() {
  if (gargantuaReturnMouth) return gargantuaReturnMouth;
  const handle = remoteSystems['gargantua'];
  if (!handle) return null;

  const group = new THREE.Group();
  group.name = 'gargantuaReturnWormhole';
  // System root is at (0, -8M, 80M); place the mouth a few hundred
  // thousand units off the BH axis so it's visible against the disk
  // from arrival distance but isn't centred on the silhouette.
  group.position.set(420_000, 50_000, 720_000);

  // Simple emissive mirror-style sphere matching the Sol-side mouth
  // aesthetics. We don't need the cube-camera reflection here because
  // the player is mostly looking at the BH, not the mouth.
  const radius = 4_500;
  const mesh = new THREE.Mesh(
    new THREE.SphereGeometry(radius, 64, 32),
    new THREE.MeshBasicMaterial({
      color: 0x88c4ff,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
    })
  );
  mesh.renderOrder = 18;
  group.add(mesh);

  // Outer halo so the mouth reads as a wormhole at distance, not as a
  // small blue sphere drifting near the disk.
  const halo = new THREE.Mesh(
    new THREE.SphereGeometry(radius * 1.6, 32, 16),
    new THREE.MeshBasicMaterial({
      color: 0xbfe7ff,
      transparent: true,
      opacity: 0.18,
      depthWrite: false,
      side: THREE.BackSide,
    })
  );
  group.add(halo);

  group.userData = {
    isWormhole: true,
    isReturnMouth: true,
    name: 'Bridge to Sol',
    radius: radius * 2,
  };

  handle.root.add(group);
  gargantuaReturnMouth = group;
  return group;
}

// Proximity-based flythrough trigger. Called each frame from animate().
// If the camera gets within trigger distance of the system's wormhole
// mouth, kick off the flight. Re-trigger lockout prevents the return
// flight from firing instantly on emergence.
const _wormholeScratch = new THREE.Vector3();
function updateWormholeProximity() {
  if (!camera) return;
  if (wormholeTunnelInFlight) return;
  // Only auto-transport when actively FLYING into the mouth. In orbit /
  // view mode, focusing on the wormhole (its tiny radius pulls the camera
  // inside the trigger envelope) must NOT transport — it opens the panel,
  // and the "Enter wormhole" button there is the explicit way through.
  if (!flyMode) return;
  const now = performance.now();
  if (now - lastWormholeTriggerMs < 4000) return;

  if (activeSystemId === 'sol' && wormholeObject && wormholeObject.visible) {
    wormholeObject.getWorldPosition(_wormholeScratch);
    const d = _wormholeScratch.distanceTo(camera.position);
    // Sol-side mouth is small (radius 0.32); use a 25× envelope so a
    // player flying toward Saturn at warp can't tunnel through the
    // mouth in a single frame without tripping the transition.
    const radius = (wormholeObject.userData && wormholeObject.userData.radius) || 0.32;
    if (d < radius * 25) {
      triggerWormholeFlight('gargantua');
    }
  } else if (activeSystemId === 'gargantua' && gargantuaReturnMouth) {
    gargantuaReturnMouth.getWorldPosition(_wormholeScratch);
    const d = _wormholeScratch.distanceTo(camera.position);
    const radius = gargantuaReturnMouth.userData.radius || 9_000;
    if (d < radius * 6) {
      triggerWormholeFlight('sol');
    }
  }
}

function showStarMapToast(message) {
  let toast = document.getElementById('starmap-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'starmap-toast';
    toast.style.cssText = `
      position: fixed; top: 32px; left: 50%; transform: translateX(-50%);
      padding: 10px 22px; z-index: 410;
      background: rgba(10, 14, 22, 0.94); border: 1px solid rgba(255,255,255,0.1);
      border-radius: 3px; color: rgba(233, 228, 214, 0.92);
      font-family: 'Orbitron', 'Inter', sans-serif; font-size: 12px;
      letter-spacing: 0.18em; text-transform: uppercase;
      opacity: 0; transition: opacity 350ms ease;
    `;
    document.body.appendChild(toast);
  }
  toast.textContent = message;
  // Below the navigation plate and chips in fly mode.
  toast.style.top = document.body.classList.contains('fly-mode') ? '122px' : '32px';
  toast.style.opacity = '1';
  clearTimeout(toast._hideTimer);
  toast._hideTimer = setTimeout(() => {
    toast.style.opacity = '0';
  }, 2600);
}

/**
 * Teleport the camera to view Nibiru. Used by the URL hash handler
 * and exposed on window.__nibiru.goto so users who heard the rumour
 * can find it without having to fly past Pluto. No-op if the body
 * didn't build at init.
 */
function gotoNibiru() {
  if (!nibiruHandle || !camera) return false;
  // Place the camera 35 scene units off the body along +X relative to
  // its position, lifted slightly so the great vortex sits in frame
  // rather than the pole. Nibiru's radius is 7.5, so 35 reads as a
  // medium-close orbital view (planet fills ~30% of screen height).
  const target = nibiruHandle.group.position.clone();
  camera.position.set(target.x + 28, target.y + 9, target.z + 22);
  camera.lookAt(target);
  if (controls) {
    controls.target.copy(target);
    controls.update();
  }
  return true;
}

// Listen for URL-hash discovery. `#nibiru` either on initial load or
// later (paste into address bar, follow a shared link) jumps the
// camera to the secret planet. Other hashes are left to the existing
// focus-by-hash plumbing.
if (typeof window !== 'undefined') {
  const checkNibiruHash = () => {
    // Normalise the hash the same way getHashFocusId does: strip the
    // leading `#` and any optional `/`, lowercase, accept either
    // 'nibiru' or 'niburu'. That makes shared links like
    //   /#nibiru
    //   /#/nibiru
    //   /#/niburu       (user's typo also works)
    //   /#Nibiru
    // all land at the secret body without spec-strictness biting.
    const raw = (window.location.hash || '').replace(/^#\/?/, '');
    const key = raw.split('/')[0].toLowerCase();
    if (key === 'nibiru' || key === 'niburu') {
      // Defer one frame so init() has finished building the body if
      // we're catching the hash from a cold load.
      requestAnimationFrame(() => { gotoNibiru(); });
    }
  };
  window.addEventListener('hashchange', checkNibiruHash);
  // Also fire once at module load; init() runs synchronously after,
  // so the rAF inside the handler waits past it.
  checkNibiruHash();
  window.__nibiru = {
    goto: gotoNibiru,
    /** Returns the current Nibiru position as a plain object (debug). */
    position: () => {
      if (!nibiruHandle) return null;
      const p = nibiruHandle.group.position;
      return { x: p.x, y: p.y, z: p.z };
    },
    /** Returns the body handle for live shader uniform tweaking. */
    handle: () => nibiruHandle
  };
}

// Expose the jump function on window so the inline STAR MAP modal
// script in index.html can call into main.js without bundling its own
// module. The modal markup ships uncompiled so it works while the
// main bundle streams.
if (typeof window !== 'undefined') {
  window.__starMap = {
    listSystems: () => listSystems().map(s => ({
      id: s.id,
      name: s.name,
      description: s.description,
      distanceLY: s.distanceLY,
      spectralClasses: s.spectralClasses,
      summary: s.summary || null,
      isCurrent: s.id === activeSystemId
    })),
    getCurrentId: () => activeSystemId,
    jumpTo: (id) => jumpToSystem(id)
  };
}

// Animation loop - optimized for smooth 60fps+
let frameCount = 0;
// Frame cap for screensaver mode (?fps=), so the GPU idles between frames:
// the live wallpaper runs all day and passes 30, or 15 on battery through
// window.__wallpaper.setFps. A skipped display refresh draws nothing, and the
// canvas keeps showing the last frame.
const IS_WALLPAPER_PAGE = IS_SCREENSAVER && new URLSearchParams(window.location.search).get('wallpaper') === '1';
let frameCapMs = IS_SCREENSAVER && Number(new URLSearchParams(window.location.search).get('fps')) > 0
  ? 1000 / Number(new URLSearchParams(window.location.search).get('fps'))
  : 0;
let lastCappedFrame = -Infinity;
// The distance readout's last text, so the DOM is written only on a change.
let lastDistanceText = '';
function animate() {
  // setAnimationLoop drives this; the renderer handles XR / non-XR
  // ticking so we don't call requestAnimationFrame here.
  const capMs = frameCapMs;
  if (capMs && !renderer.xr.isPresenting) {
    const now = performance.now();
    if (now - lastCappedFrame < capMs - 2) return;
    lastCappedFrame = Math.max(lastCappedFrame + capMs, now - capMs);
  }
  frameCount++;
  // Spacetime mode lowers the bodies onto its sheet once they are placed;
  // they go back up here, before this frame places them.
  if (spacetimeMode) spacetimeMode.restore();

  const delta = clock.getDelta();
  const elapsed = clock.getElapsedTime();
  const scaledDelta = timeSpeed * delta;
  // Advance the ephemeris JD. Realistic mode runs at wall-clock pace
  // (one real second = one sim second), artistic accelerates so the
  // orbits are visible without cranking the time slider.
  const ephemerisRate = realisticScaleMode ? 1 : ARTISTIC_TIME_RATE;
  const simStepJD = (delta * timeSpeed * ephemerisRate) / 86400;
  // Real time follows the wall clock exactly (rocketry keeps its own warp
  // clock, which steps with its physics). A scale switch changes what Real's
  // speed is before initTimePresets' tick resyncs it (up to 250 ms); stepping
  // the old speed at the new rate in between moved the clock about an hour
  // on every switch back to artistic, so that gap still counts as real time.
  const realScaleUnsynced = realisticScaleMode !== syncedRealisticMode && timeSpeed === syncedTimeSpeed;
  if (timePreset === 'real' && (timeSpeed === timePresetSpeed('real') || realScaleUnsynced) && !rocketryActive) {
    const wallJD = dateToJD(new Date());
    if (realClockLockedJD === null || Math.abs(simulationJD - realClockLockedJD) > 1e-9) realClockOffsetJD = simulationJD - wallJD;
    simulationJD = wallJD + realClockOffsetJD;
    realClockLockedJD = simulationJD;
  } else {
    simulationJD += simStepJD;
    realClockLockedJD = null;
  }
  // Refresh the sim-date readout a few times per second so users notice
  // it's the actual sky right now (or the simulated future / past after
  // they've cranked time).
  if (simDateEl && elapsed - lastSimDateUpdate > 0.25) {
    simDateEl.textContent = jdToDateString(simulationJD);
    lastSimDateUpdate = elapsed;
  }
  // Astronomy mode draws its own scene on the same clock; the solar system
  // waits where it was.
  if (astroMode && astroMode.active && !renderer.xr.isPresenting) {
    astroMode.frame();
    return;
  }
  // Auto-tour drives the screensaver presentation. No-op in any other mode.
  tickAutoTour(elapsed);

  // Non-Sol systems tick their own orbits + binary motion. Only the
  // currently visible system actually does work; the rest are hidden
  // groups and skipped.
  tickRemoteSystems(delta, elapsed);
  updateWormholeProximity();

  // Atmospheric entry state — drives the craft scale illusion and the
  // daylit-sky star fade while flying inside an atmosphere envelope.
  updateAtmosphericEntry(delta);

  // Secret procedural planet. Advance shader time, refresh sun
  // direction so the lit hemisphere tracks the real Sol, spin the
  // body. No-op if Nibiru failed to build at init.
  // Placed at 235 AU in whichever scale the planets are drawn at.
  if (nibiruHandle) {
    nibiruHandle.tick(delta, elapsed, sun, realisticScaleMode
      ? { auToScene: AU_TO_SCENE }
      : { plutoDistance: (baseDistances.pluto || 0) * scaleMultiplier });
  }

  // Ice giants on the procedural surface shader. Push elapsed time
  // (drives the band shear + storm swirl) and sun direction (drives
  // the lit hemisphere terminator) into each registered surface.
  if (sun) {
    const _procSunPos = _procSunPosScratch;
    const _procBodyPos = _procBodyPosScratch;
    sun.getWorldPosition(_procSunPos);
    for (const key in proceduralPlanetSurfaces) {
      const entry = proceduralPlanetSurfaces[key];
      if (!entry || !entry.uniforms) continue;
      entry.uniforms.uTime.value = elapsed;
      entry.mesh.getWorldPosition(_procBodyPos);
      _procSunDirScratch.subVectors(_procSunPos, _procBodyPos);
      if (_procSunDirScratch.lengthSq() > 1e-6) {
        _procSunDirScratch.normalize();
        entry.uniforms.uSunDir.value.copy(_procSunDirScratch);
      }
    }
  }

  // Venus procedural cloud advance. uDriftRate inside the shader
  // already provides the equator-fastest super-rotation, so all we
  // do here is advance uTime (scaled by simulation timeSpeed so the
  // clouds visibly accelerate when the user cranks the slider) and
  // refresh uSunDir so the lit hemisphere tracks the real Sun. The
  // legacy UV-offset trick is gone — drift is now noise-space.
  if (venusShaderMaterial && planets.venus) {
    const u = venusShaderMaterial.uniforms;
    u.uTime.value += delta * timeSpeed;
    // Wrap like bandPhase so the accumulator never outgrows float32
    // precision. 36000 s is lattice-aligned for every FBM time rate
    // the shader uses (0.02…0.05 → integer noise offsets); the
    // residual super-rotation phase jump once per period hides in
    // the cloud noise.
    const VENUS_TIME_PERIOD = 36000;
    while (u.uTime.value >= VENUS_TIME_PERIOD) u.uTime.value -= VENUS_TIME_PERIOD;
    const venusWorld = _venusBodyWorld.setFromMatrixPosition(planets.venus.matrixWorld);
    const sunWorld = sun ? _venusSunWorld.setFromMatrixPosition(sun.matrixWorld) : _venusSunWorld.set(0, 0, 0);
    u.uSunDir.value.copy(sunWorld).sub(venusWorld).normalize();
  }

  if (fpsLabel && graphicsConfig.showFps) {
    fpsFrames += 1;
    fpsAccum += delta;
    if (fpsAccum >= 0.4) {
      const fps = Math.round(fpsFrames / fpsAccum);
      fpsLabel.textContent = `${fps} FPS`;
      fpsFrames = 0;
      fpsAccum = 0;
    }
  }

  // To-Scale lineup: spin, shader clocks, band drift, prominences.
  updateScaleLineup(delta, elapsed);

  // Update fly mode movement
  updateFlyMovement(delta);

  // Adjust camera.near for close-approach views (atmospheres, surfaces).
  // Runs after fly movement so it sees the new camera position this
  // frame and updates the projection before rendering.
  updateFlyCameraNear();

  // Smooth DSO telescope zoom (fly-mode-independent).
  updateDsoZoomSmoothing(delta);

  // Update orbit stabilization
  updateOrbitStabilization(delta);

  // Update warp effect and ship
  updateWarpEffect(delta);

  // Check for collisions
  checkCollisions();

  // Update controls (only when not in fly mode or scale mode). Scale
  // mode drives camera.position directly via GSAP — calling
  // controls.update() would overwrite those tweens with the internal
  // spherical state every frame, which is why bodies refused to
  // centre cleanly.
  // The screensaver tour aims the camera itself; OrbitControls would turn it
  // back to controls.target (the Sun) and clamp it out to minDistance. An
  // eclipse embedded in the screensaver page (the sky event pages) holds the
  // tour and frames through the controls like the viewer.
  // The ISS shows frame through the viewer's own ISS tracking, so they keep
  // the controls as the viewer does.
  // In XR the headset owns the camera (renderer.xr writes its pose each frame).
  if (!flyMode && !scaleModeActive && !rocketryActive && !xrHoldsCamera() && (!IS_SCREENSAVER || eclipseModeActive() || screensaverIssShow)) {
    controls.update();
  }

  // Rotate sun
  if (sun) {
    sun.rotation.y = wrapAngle(
      sun.rotation.y + bodySpinStep(celestialData.sun.rotationSpeed, scaledDelta, simStepJD)
    );

    // Distance-based surface-animation LOD. Use the sun's true world
    // position (not camera.position.length()) so the throttle stays
    // correct under the satellite-inspection origin shift. Express the
    // distance in sun radii so the same thresholds work in artistic
    // and realistic scale modes.
    sun.getWorldPosition(_sunLodSunPos);
    const _sunDist = camera.position.distanceTo(_sunLodSunPos);
    const _sunRadiusUnits = realisticScaleMode
      ? (getRealisticBodyRadius('sun') || celestialData.sun.radius)
      : celestialData.sun.radius;
    const _sunRadii = _sunDist / Math.max(_sunRadiusUnits, 0.0001);
    if (sunSwitch) sunSwitch.update(_sunRadii);
    // 1 at/inside NEAR, 0 at/beyond FAR.
    const _sunLodT = 1 - THREE.MathUtils.clamp(
      (_sunRadii - SUN_LOD_NEAR) / (SUN_LOD_FAR - SUN_LOD_NEAR), 0, 1
    );
    // octaveLod: full (1.0) when close, down to 0.5 when far.
    const _sunOctaveLod = 0.5 + 0.5 * _sunLodT;
    sunDaysUniform.value = simulationJD - 2451545.0;
    // Near now, NOAA's active regions and flares (src/spaceweather/viewer.js).
    updateLiveSun(sun, simulationJD);

    // Update sun shader time + octave LOD
    sun.children.forEach((child) => {
      const u = child.material && child.material.uniforms;
      if (!u) return;
      if (u.octaveLod) u.octaveLod.value = _sunOctaveLod;
      if (u.time) u.time.value = elapsed;
    });

    updateSunProminences(elapsed, _sunDist);

    // The far field's bake (sunfarfield.js), while the Sun is more than a
    // few pixels across and its far field shows: in the Detailed style, or
    // in either style inside the close range, and in the To-Scale lineup.
    // Its cubes are made once that holds or the disc passes
    // SUN_FAR_FIELD_WANT_PX, and never while the Sun is a dot.
    if (sun.userData._sunSurfaceMesh === undefined) {
      sun.userData._sunSurfaceMesh = sun.children.find((c) => c.isMesh && c.material && c.material.uniforms && c.material.uniforms.realisticSun) || null;
    }
    const _sunSurface = sun.userData._sunSurfaceMesh;
    if (_sunSurface) {
      const _sunFarActive = scaleModeActive || (_sunRadii < 250 && (!realisticSunMode || _sunRadii < 5.5));
      const _sunDiscPx = 2 * Math.asin(Math.min(1, 1 / Math.max(_sunRadii, 1)))
        * renderer.domElement.height / (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov || 45) / 2));
      updateSunFarField(renderer, elapsed, {
        sunTexture: _sunSurface.material.uniforms.sunTexture.value,
        size: graphicsConfig.sunSegments >= 144 ? 768 : graphicsConfig.sunSegments >= 96 ? 512 : 256,
        active: _sunFarActive,
        wanted: _sunFarActive || _sunDiscPx > SUN_FAR_FIELD_WANT_PX,
      });
    }

    // Scale sun glow based on camera distance - natural perspective shrinking
    // (child refs cached on first lookup — getObjectByName traverses)
    if (sun.userData._sunBeacon === undefined) {
      sun.userData._sunBeacon = sun.getObjectByName('sunBeacon') || null;
    }
    const sunBeacon = sun.userData._sunBeacon;
    if (sunBeacon) {
      const distFromSun = camera.position.length();
      const baseDistance = 700; // Reference distance for subtle size breathing
      const scaleFactor = baseDistance / Math.max(distFromSun, 100);
      const shellScale = 1.004 + Math.min(0.006, 0.002 * Math.pow(scaleFactor, 0.2));
      sunBeacon.scale.set(shellScale, shellScale, shellScale);

      // Keep the corona faint; the photosphere and bloom should do most
      // of the visual work so the halo never reads as a fat atmosphere.
      const opacityFactor = Math.min(1, Math.max(0.25, 1 - (distFromSun / (70000 * scaleMultiplier))));
      if (sunBeacon.material && sunBeacon.material.uniforms && sunBeacon.material.uniforms.glowStrength) {
        sunBeacon.material.uniforms.glowStrength.value = 0.035 * opacityFactor;
      }
      // A glow for the Sun seen from afar. Within a couple of radii of the
      // surface it adds nothing, and its back faces masked the stars in a
      // dark band just beyond the limb, so it fades out there.
      const nearSurface = THREE.MathUtils.smoothstep(_sunRadii, 1.6, 2.4);
      sunBeacon.visible = nearSurface > 0.01;
      if (sunBeacon.material && sunBeacon.material.uniforms && sunBeacon.material.uniforms.glowStrength) {
        sunBeacon.material.uniforms.glowStrength.value *= nearSurface;
      }
    }

    // Always-visible sun glow sprite.
    //   Realistic mode: sprite size is fixed in world units (= ~5×
    //     the sun's true scene radius) so perspective shrinks it
    //     correctly with camera distance. From Pluto the sun reads
    //     as a bright pinpoint, not an oversized disc — matches what
    //     you'd actually see through a window at 39 AU.
    //   Artistic mode: sprite scales with camera distance so the sun
    //     stays visible regardless of zoom. This is "visualisation
    //     friendly" rather than physically correct.
    if (sun.userData._sunGlowSprite === undefined) {
      sun.userData._sunGlowSprite = sun.getObjectByName('sunGlowSprite') || null;
    }
    const sunGlowSprite = sun.userData._sunGlowSprite;
    if (sunGlowSprite) {
      const distFromSun = camera.position.length();
      const sunData = celestialData.sun;
      let glowSize;
      if (realisticScaleMode) {
        // Halo scales between a tight crown (hidden behind sun mesh
        // at close range so the photosphere isn't washed out) and a
        // larger billboard at distance (so the sun stays visible
        // from outer-planet AND overview distances). realSun * 40
        // with a 250k ramp gives:
        //   Earth distance (5k):    ~47 units, 0.54° angular (real)
        //   Saturn distance (48k):  ~169 units, 0.20° angular (small)
        //   Pluto distance (197k):  ~750 units, 0.22° angular (small)
        //   Default overview (28k): ~119 units, 0.24° angular (small)
        // This is a deliberate dial-back from realSun * 60: at Saturn
        // distance the previous setting produced haze around the
        // sun visible across the frame; 40 keeps the sun present
        // without saturating the bloom pass.
        const realSun = getRealisticBodyRadius('sun') || sunData.radius;
        const tightSize = realSun * 1.05;
        const farSize = realSun * 40;
        const farRamp = THREE.MathUtils.clamp(distFromSun / 250_000, 0, 1);
        glowSize = THREE.MathUtils.lerp(tightSize, farSize, farRamp);
      } else {
        // Glow stays just slightly larger than the sun mesh at close-
        // range views (Earth, inner planets) so it reads as a halo,
        // not as a giant white block crowding nearby bodies. At outer-
        // system distances the linear distance term takes over and
        // grows the glow back so the sun stays visible past Pluto.
        const minWorldSize = sunData.radius * 1.6;
        const distScale = distFromSun * 0.018;
        glowSize = THREE.MathUtils.clamp(distScale, minWorldSize, sunData.radius * 1200);
      }
      sunGlowSprite.scale.setScalar(glowSize);
      // Fade the sprite based on how big the actual sun mesh reads on
      // screen. When you're close to a planet (Earth, Mars) the sun
      // mesh is already 6° wide and easy to see — keeping the sprite
      // fully visible at that point produces a giant billboard halo
      // behind the planet, and the bright sprite center then bleeds
      // into bloom mip-aliasing artifacts (a hard white square on
      // darker bodies in frame). Fading it out also drops the post-
      // process workload at those camera positions.
      //
      // The sprite still kicks in fully at outer-planet / Pluto
      // distances where the sun mesh becomes a sub-pixel speck and
      // would otherwise vanish into the starfield.
      const sunRefRadius = realisticScaleMode
        ? (getRealisticBodyRadius('sun') || sunData.radius)
        : sunData.radius;
      const sunApparent = sunRefRadius / Math.max(distFromSun, 1);
      const visibilityNeed = 1 - THREE.MathUtils.smoothstep(sunApparent, 0.003, 0.020);
      // The glare is the lens's (the cinematic pass, which also lays it
      // over a planet's limb and draws the far Sun as a star); the sprite
      // only keeps the disc visible where that pass is off (the Low
      // preset), neutral white, since there is no air to warm it. Under
      // the glare it went through the bloom and swelled the far Sun.
      sunGlowSprite.material.opacity = graphicsConfig.lensFlare ? 0 : visibilityNeed * 0.9;
      sunGlowSprite.visible = !graphicsConfig.lensFlare && visibilityNeed > 0.02;
    }
  }

  if (starfieldMaterial && starfieldMaterial.uniforms) {
    // In XR camera.position is the headset's, in metres, near the origin
    // (a zero vector at the start of a 'local' session made sunDirection
    // NaN and the sky white); the sky reads the eye in the scene's frame.
    const skyEye = camera.position; // in XR too: the head rides the 2D camera
    const distFromSun = skyEye.length();
    // Scale-aware deep-space factor with quadratic onset so the wisp
    // and zodiacal contributions stay near zero through the inner
    // solar system and only emerge as the camera leaves planet
    // territory. Linear ramp had the wisp contributing meaningfully
    // anywhere past Mars; quadratic delays the onset until the
    // outer planets / Kuiper belt distances.
    const deepSpaceStart = 1500 * scaleMultiplier;  // ~just past Mars orbit
    const deepSpaceEnd = 8000 * scaleMultiplier;    // beyond Pluto orbit
    const linearFactor = THREE.MathUtils.clamp(
      (distFromSun - deepSpaceStart) / (deepSpaceEnd - deepSpaceStart),
      0, 1
    );
    const deepSpaceFactor = linearFactor * linearFactor;
    // The band is faintly present from the inner planets and rises toward
    // deep space, where nothing nearby competes with it (the eye adapts).
    if (starfieldMaterial.uniforms.uTime) starfieldMaterial.uniforms.uTime.value = elapsed;
    starfieldMaterial.uniforms.milkyWayGain.value = (milkyWayBand ? SKY_MILKY_WAY_BAND_GAIN : SKY_MILKY_WAY_GAIN) * (1 + deepSpaceFactor * 1.5);
    starfieldMaterial.uniforms.milkyWaySaturation.value = milkyWayBand ? 1 : SKY_MILKY_WAY_SATURATION;
    // The live wallpaper keeps the 4K map: it runs a page per display, and on
    // a 6K screen at full resolution WebKit's GPU process ran out of room and
    // dropped texture uploads (the Earth's clouds went missing).
    if (!skyMapUpgrade && !IS_WALLPAPER_PAGE && (milkyWayBand || deepSpaceFactor > 0.3 || IS_SCREENSAVER)) upgradeSkyMap();
    starfieldMaterial.uniforms.dustBoost.value = 0.30 + deepSpaceFactor * 1.20;
    // Camera→Sun direction for the Henyey-Greenstein zodiacal phase
    // function. Sun is at scene origin; the unit vector toward it is
    // -normalize(cameraPosition). When the camera looks toward Sol
    // the zodiacal cone brightens; looking opposite gives the
    // smaller gegenschein peak; perpendicular gives the dim minimum.
    if (starfieldMaterial.uniforms.sunDirection && skyEye.lengthSq() > 1e-12) {
      starfieldMaterial.uniforms.sunDirection.value
        .copy(skyEye).multiplyScalar(-1).normalize();
    }
  }

  // Galaxy cross-fade + GPU rotation + deep-field tracking. Far from
  // the sun, the inside-the-galaxy panorama is no longer the right
  // metaphor — fade it out and bring the exterior galaxy in. Time
  // accumulates scaled by the timeSpeed slider so boosting time
  // visibly rotates the galaxy.
  if (galaxy) {
    galaxyClockTime += scaledDelta;
    // Same wrap idea as the gas-giant bandPhase below — keep the
    // accumulator bounded so float32 precision holds after hours at
    // high time speed. See GALAXY_CLOCK_PERIOD for why 8250 s is the
    // seamless period for the arm/bar pattern rotation.
    while (galaxyClockTime >= GALAXY_CLOCK_PERIOD) galaxyClockTime -= GALAXY_CLOCK_PERIOD;
    const distFromSun = camera.position.length();
    // In artistic mode the spread pushes the planets out, so the hand-off
    // scales with it. At true scale scaleMultiplier is a fixed 100 that
    // says nothing about distance: scaled by it the fade ended at 150M
    // units, past the galaxy (centre 12.3M out) and the black hole (56M),
    // so the screensaver's Milky Way scene showed the Earth's sky panorama
    // and its black hole scene the edge of the 40M sky sphere. True scale
    // fades over 15M to 38M: inside that the camera is still in the
    // galaxy's disc (the Sun is 12.3M from its centre), where the panorama
    // is the right view and the galaxy's points, all close by, washed the
    // frame white; the fade ends before the sky sphere's 40M edge.
    const fadeStart = realisticScaleMode ? 8_000_000 : 60_000 * Math.max(1, scaleMultiplier);
    const fadeEnd = realisticScaleMode ? 20_000_000 : 1_500_000 * Math.max(1, scaleMultiplier);
    const { panoramaOpacity, galaxyOpacity } = galaxy.tick(
      camera,
      galaxyClockTime,
      distFromSun,
      { fadeStart, fadeEnd }
    );
    galaxyViewShare = galaxyOpacity;
    // Daylit-atmosphere star wash: inside a sunlit atmosphere the sky
    // brightness hides the stars (night-side entries keep them). The
    // occlusion factor comes from the atmospheric entry state and
    // multiplies the same crossfade every star layer already shares.
    // A camera exposed for the sunlit Earth records few stars (every daylit
    // ISS frame has a black sky), so close to the planet with the Sun up over
    // the camera's own horizon the star layers dim; at night, in the Earth's
    // shadow or from the usual whole-Earth view they are untouched.
    const starDayDim = sunlitStarDim();
    const panoramaOpacityEff = panoramaOpacity * (1 - atmoSkyOcclusionLive);
    // One exposure for the sky map and every star layer, in magnitudes
    // stopped down: the Sun bright in frame (adaptiveGlare) or the sunlit
    // Earth filling the view (starDayDim) lose the faint stars first and the
    // brightest last, as a camera's exposure does.
    const starGlareWash = Math.min(Math.max(adaptiveGlare, 0), 1);
    // About a magnitude for the Sun in frame (more read as the stars
    // switching off), four and a half over sunlit ground.
    // In an eclipse the exposure opens up for the darkened sky
    // (eclipseExposure) and the stars are stopped down by a little more
    // than that, as the eye sees them against totality's sky: the planets
    // and the brightest stars come out.
    const skyStopDown = 1.1 * starGlareWash + 4.5 * starDayDim + 3.1 * Math.log10(eclipseExposure);
    const skyExposure = SKY_EXPOSURE * Math.pow(10, -0.4 * skyStopDown);
    if (starfieldMaterial && starfieldMaterial.uniforms.panoramaAlpha) {
      starfieldMaterial.uniforms.panoramaAlpha.value = panoramaOpacityEff;
      if (starfieldMaterial.uniforms.skyExposure) starfieldMaterial.uniforms.skyExposure.value = skyExposure;
      else starfieldMaterial.uniforms.panoramaAlpha.value *= 1 - 0.92 * starDayDim;
    }
    // Stars twinkle only through air: in a daylit or night atmosphere
    // (rocketry's own sky handles the ground there).
    const starScintillation = atmoEntry && atmoEntry.immersion > 0 ? Math.min(1, atmoEntry.immersion) : 0;
    const starTwinkleClock = (performance.now() % 100000) * 0.001;
    for (const layer of [starCatalog, faintStars]) {
      if (!layer) continue;
      layer.setOpacity(panoramaOpacityEff);
      layer.setExposure(skyExposure);
      layer.setScintillation(starScintillation);
      layer.setTime(starTwinkleClock);
    }
    // Artistic distances are not to scale, so there a planet's brightness is
    // taken in the artistic layout as drawn, with the Earth's orbit as 1 AU:
    // from any planet the others come out between about magnitude -3 and +4
    // (Mars from Neptune the faintest), so each reads as a bright star, the
    // order kept, and the spread slider changes only the discs.
    if (planetPoints) {
      planetPoints.update({
        active: activeSystemId === 'sol' && !scaleModeActive,
        artistic: !realisticScaleMode,
        camera, bodies: planets, radiusOf: getBodyBaseRadius,
        unitsPerAu: realisticScaleMode ? AU_TO_SCENE : (baseDistances.earth || celestialData.earth.distance) * scaleMultiplier,
        reachOf: realisticScaleMode ? undefined : planetMoonReach,
        cssHeight: renderer.domElement.clientHeight || window.innerHeight,
        exposure: skyExposure * (1 - atmoSkyOcclusionLive),
        drawnShift: spacetimeMode ? spacetimeMode.shiftOf : undefined,
      });
    }
    if (asteroidLayer) {
      asteroidLayer.update({
        active: activeSystemId === 'sol' && !scaleModeActive,
        camera, jd: simulationJD, artistic: !realisticScaleMode, unitsPerAu: AU_TO_SCENE,
        spread: scaleMultiplier, mapScene: asteroidArtisticAnchors(),
        exposure: skyExposure * (1 - atmoSkyOcclusionLive), lit: asteroidsLit(), litOffset: ASTEROID_LIT_OFFSET,
      });
    }

    // Deep-sky objects: zoom-aware brightness. Capped at 1.0 so the
    // additive composite never blows out when the user zooms in, and
    // stopped down with the stars (the same exposure), or a photo stays
    // lit while the sky round it dims for the Sun or sunlit ground. Each
    // photo's fade into the sky is baked into it
    // (scripts/build-dso-photos.mjs), so nothing else is driven here.
    if (deepSkyObjects && deepSkyObjects.group) {
      const v = Math.max(0.4, Math.min(1.0, (45 / camera.fov) * 0.55));
      const dsoBrightness = v * panoramaOpacityEff * (skyExposure / SKY_EXPOSURE);
      deepSkyObjects.group.children.forEach((sprite) => {
        const u = sprite.material && sprite.material.uniforms;
        if (u && u.zoomVisibility) u.zoomVisibility.value = dsoBrightness;
      });
    }
    // The black hole shares the deep-space-only visibility regime —
    // it's a far destination, not part of the solar system.
    if (blackHole) {
      blackHole.setOpacity(galaxyOpacity);
      blackHole.tick(galaxyClockTime);
    }
  }


  // Update planets (skip dwarf planets - they have their own animation loop)
  Object.keys(planets).forEach((planetName) => {
    const planet = planets[planetName];
    const data = celestialData[planetName];
    // Skip dwarf planets and objects without celestialData
    if (!data || planet.userData.isDwarfPlanet) return;
    const a = planet.userData.scaledDistance || data.distance;

    // Position from JPL ephemeris. Both realistic and artistic modes
    // use the same orbital physics; only the radial scaling differs:
    //   - realistic: AU_TO_SCENE per AU (true proportions)
    //   - artistic:  scene-distance / semi-major-axis (each orbit fits
    //                its data.distance value, eccentricity preserved)
    // Phase, inclination, longitude of ascending node, and longitude
    // of perihelion all come from the JPL elements so every planet
    // sits at its real angular position relative to the others for
    // the current epoch.
    if (!placePlanetOnEphemeris(planetName, planet, a)) {
      // Bodies not in the ephemeris table fall back to the simulated
      // Keplerian orbit. orbitAngle accumulates the MEAN anomaly at a
      // constant rate (same period as before); Kepler's equation then
      // gives the true anomaly per frame so eccentric bodies obey the
      // equal-area law instead of moving at a uniform angular rate.
      // Polar form r = a(1 - e²)/(1 + e·cosθ) reduces exactly to
      // r = a when e = 0.
      planet.userData.orbitAngle = wrapAngle(
        planet.userData.orbitAngle + boundedAngularStep(data.orbitalSpeed, scaledDelta, MAX_PLANET_ORBIT_STEP)
      );
      const e = data.eccentricity || 0;
      const incl = data.orbitalInclination || 0;
      const theta = trueAnomalyFromMean(planet.userData.orbitAngle, e);
      const r = a * (1 - e * e) / (1 + e * Math.cos(theta));
      const cosI = Math.cos(incl);
      const sinI = Math.sin(incl);
      // -sin on z: counterclockwise from +Y, same sense as the
      // ephemeris planets after the reflection fix.
      planet.position.x = Math.cos(theta) * r;
      planet.position.y = Math.sin(theta) * r * sinI;
      planet.position.z = -Math.sin(theta) * r * cosI;
    }

    // Planet rotation (now in tilt group). Ref cached on first lookup
    // — getObjectByName traverses the subtree every call.
    if (planet.userData._tiltGroup === undefined) {
      planet.userData._tiltGroup = planet.getObjectByName('tiltGroup') || null;
    }
    const tiltGroup = planet.userData._tiltGroup;
    if (tiltGroup && tiltGroup.children[0]) {
      // Rotate the planet mesh inside the tilt group. Earth's angle comes
      // from the clock (earthSpinAngle); the others turn at their display rate.
      tiltGroup.children[0].rotation.y = planetName === 'earth'
        ? earthSpinAngle(simStepJD, delta)
        : wrapAngle(
          tiltGroup.children[0].rotation.y + bodySpinStep(data.rotationSpeed, scaledDelta, simStepJD)
        );
      if (planetName === 'earth' && TILT_ON) applyTiltWhatIf(planet, tiltGroup);

      // Update Earth shader uniforms — sun position is world origin;
      // time drives cloud drift.
      if (planetName === 'earth') {
        const earthMesh = tiltGroup.children[0];
        const uniforms = earthMesh.material && earthMesh.material.uniforms;
        if (uniforms) {
          if (uniforms.sunPosition) uniforms.sunPosition.value.set(0, 0, 0);
          if (uniforms.time) uniforms.time.value = elapsed;
        }

        updateEarthAuroras(earthMesh, elapsed);
      }

      // Jupiter aurora drive — same pattern as Earth, with a wider
      // distance gate (Jupiter is much bigger than Earth so the
      // aurora resolves from further away). Magnetic pole follows
      // Jupiter's spin like Earth's does, with a slow longitudinal
      // breathing to make the ribbons feel alive.
      if (planetName === 'jupiter' && jupiterAuroras && jupiterAuroras.root) {
        const jupRadius = planet.userData.radius || 8;
        const jupWorld = _auroraWorldPos;
        planet.getWorldPosition(jupWorld);
        const distToJup = camera.position.distanceTo(jupWorld);
        jupiterAuroras.root.visible = distToJup < jupRadius * 80;
        if (jupiterAuroras.root.visible) {
          const jupMesh = tiltGroup.children[0];
          jupiterAuroras.root.rotation.y = jupMesh.rotation.y + Math.sin(elapsed * 0.035) * 0.05;
          jupiterAuroras.root.traverse((child) => {
            if (!child.material || !child.material.uniforms) return;
            if (child.material.uniforms.sunPosition) child.material.uniforms.sunPosition.value.set(0, 0, 0);
            if (child.material.uniforms.time) child.material.uniforms.time.value = elapsed;
          });
        }
      }

      // Gas giant differential rotation. Cap phase movement per frame
      // so high time scales do not smear the latitude-shifted texture.
      // Modulo the accumulated phase to its wrap period (1 / bandRate)
      // so the equator's effective shift stays within [0, 1] and the
      // texture wraps at most once per latitude. Without this, after
      // running for a few minutes the phase grew large enough that
      // fract(uvX + phase * cos^2lat) crossed integers multiple times
      // between pole and equator, surfacing as the visible vertical
      // seam streaks Saturn was showing. One wrap is hidden by the
      // existing seamBlendWidth; multiple wraps fight that blend.
      if (planetName === 'jupiter' || planetName === 'saturn' ||
          planetName === 'uranus'  || planetName === 'neptune') {
        const gasMesh = tiltGroup.children[0];
        const bandUniforms = gasMesh.material && gasMesh.material.userData.bandUniforms;
        if (bandUniforms) {
          const wrapPeriod = 1.0 / Math.max(bandUniforms.bandRate.value, 1e-5);
          let nextPhase = (planet.userData.bandPhase || 0) +
            boundedAngularStep(1, scaledDelta, MAX_SHADER_PHASE_STEP);
          // Wrap to [0, wrapPeriod). The texture tiles at integer
          // multiples of bandRate * phase, so a wrap-around at this
          // exact period is invisible for a perfectly tileable map
          // and falls inside the seamBlend zone for our slightly
          // non-tileable Saturn texture.
          while (nextPhase >= wrapPeriod) nextPhase -= wrapPeriod;
          planet.userData.bandPhase = nextPhase;
          bandUniforms.bandTime.value = planet.userData.bandPhase;
          // Wall-clock seed for the per-body film grain so it shimmers
          // every frame regardless of sim time-speed (bandTime above
          // freezes when paused). Matches the global grain's phase form.
          if (bandUniforms.gasGrainTime) {
            bandUniforms.gasGrainTime.value = (performance.now() % 100000) * 0.001;
          }
        }

        // Saturn ring-shadow projection AND Jupiter night-side
        // lightning both need the sun's position in mesh-local frame
        // (ring-shadow ray-traces it through the ring plane;
        // lightning uses it for the night-side mask). Compute the
        // inverse model matrix once per frame and reuse for any of
        // the gas-giant uniforms that need a local-space sun.
        const ringShadowUniforms = gasMesh.material && gasMesh.material.userData.ringShadowUniforms;
        const lightningUniforms  = gasMesh.material && gasMesh.material.userData.lightningUniforms;
        if (ringShadowUniforms || lightningUniforms) {
          gasMesh.updateWorldMatrix(true, false);
          const inv = _gasGiantInverseMatrix.copy(gasMesh.matrixWorld).invert();
          if (ringShadowUniforms && planetName === 'saturn') {
            ringShadowUniforms.sunPosLocal.value.set(0, 0, 0).applyMatrix4(inv);
            if (ringShadowUniforms.cameraPosLocal) ringShadowUniforms.cameraPosLocal.value.copy(camera.position).applyMatrix4(inv);
            if (ringShadowUniforms.uSunAngularRadius) {
              ringShadowUniforms.uSunAngularRadius.value = getSunAngularRadiusAt(planet);
            }
          }
          if (lightningUniforms) {
            lightningUniforms.lightningSunPosLocal.value.set(0, 0, 0).applyMatrix4(inv);
          }
        }
      }
    }

    // Update atmosphere sun position + time (time drives the polar
    // aurora shimmer on Earth; other bodies have auroraStrength = 0
    // so the time uniform is harmless but unused there).
    if (planet.userData._atmosphere === undefined) {
      planet.userData._atmosphere = planet.getObjectByName('atmosphere') || null;
    }
    const atmosphere = planet.userData._atmosphere;
    if (atmosphere && atmosphere.material.uniforms) {
      atmosphere.material.uniforms.sunPosition.value.set(0, 0, 0);
      if (atmosphere.material.uniforms.time) {
        atmosphere.material.uniforms.time.value = elapsed;
      }
      // Refresh the ray-traced shadow uniforms each frame as the
      // planet orbits — the shadow cone has to follow the body's
      // world-space position, not just the local atmosphere mesh.
      if (atmosphere.material.uniforms.planetCenter) {
        planet.getWorldPosition(atmosphere.material.uniforms.planetCenter.value);
      }
    }


    // Ring shadows need the planet center in world space. Saturn uses a
    // textured shader; Uranus uses the procedural shader but the geometry
    // of the shadow is the same.
    if (planet.userData._ring === undefined) {
      planet.userData._ring = planet.getObjectByName('ring') || null;
    }
    const ring = planet.userData._ring;
    if (ring && ring.material.uniforms && ring.material.uniforms.planetCenter) {
      planet.getWorldPosition(ring.material.uniforms.planetCenter.value);
      if (ring.material.uniforms.uSunAngularRadius) {
        ring.material.uniforms.uSunAngularRadius.value = getSunAngularRadiusAt(planet);
      }
      // Drive the Saturn ring spoke phase. Uses elapsed seconds so
      // spoke lifecycle / drift speed is independent of timeSpeed
      // (Saturn rotation co-evolves separately, so spokes don't need
      // to be locked to it). Other ring materials don't have this
      // uniform — guard so we don't crash on Uranus.
      if (ring.material.uniforms.spokeTime) {
        ring.material.uniforms.spokeTime.value = elapsed;
      }
      if (ring.userData.dust) ring.userData.dust.material.uniforms.planetCenter.value.copy(ring.material.uniforms.planetCenter.value);
      if (planetName === 'earth') {
        updateEarthRingUniforms({ center: ring.material.uniforms.planetCenter.value, tiltGroup: ring.parent, ring, sunPosition: ring.material.uniforms.sunPosition.value });
      }
    }

    // Update moons
    if (moons[planetName]) {
      // Planet world position + shadow axis for the umbra test. Sun is
      // at world origin so the axis is just the planet's world position
      // normalised (planetGroup carries only translation). Moon offsets
      // are taken in world space below since moons now live inside the
      // axial-tilt moonFrame, not directly under planetGroup.
      const planetWorldPos = planet.position;
      const shadowAxisX = planetWorldPos.x;
      const shadowAxisY = planetWorldPos.y;
      const shadowAxisZ = planetWorldPos.z;
      const planetDist = Math.sqrt(
        shadowAxisX * shadowAxisX + shadowAxisY * shadowAxisY + shadowAxisZ * shadowAxisZ
      );
      const axisNx = planetDist > 0 ? shadowAxisX / planetDist : 1;
      const axisNy = planetDist > 0 ? shadowAxisY / planetDist : 0;
      const axisNz = planetDist > 0 ? shadowAxisZ / planetDist : 0;
      // The planet's drawn radius: its shadow is what eclipses the moons.
      const planetRadius = (realisticScaleMode && getRealisticBodyRadius(planetName)) || data.radius;

      moons[planetName].forEach((moon) => {
        // First-touch: parse the moon's real orbital period from its
        // data string ("1.77 days") and lock in a starting phase. Once
        // cached, we drive the angle directly from simulationJD so all
        // moons orbit at their correct relative periods (Io fastest of
        // the Galileans, Callisto slowest, etc.) instead of the
        // hand-tuned per-moon orbitalSpeed values which weren't in real
        // proportion to each other.
        if (moon.userData.orbitalPeriodDays === undefined) {
          const m = String(moon.userData.orbitalPeriod || '').match(/([\d.]+)\s*day/);
          moon.userData.orbitalPeriodDays = m ? parseFloat(m[1]) : null;
          moon.userData.orbitInitialPhase = moon.userData.orbitAngle || 0;
        }

        // A lesson shows the Moon's real phase for the date in the artistic
        // scale too, as eclipse mode does. A lesson showing an Events panel
        // scene keeps every moon where the scene put it (the eclipse line-up
        // sets the Moon between the Earth and the Sun).
        if (lessonHoldsMoons) {
          // Held.
        } else if (realisticScaleMode && isEarthsMoon(moon)) {
          placeEarthMoonFromEphemeris(moon, simulationJD);
        } else if (realisticScaleMode && galileanIndex(moon) >= 0 && placeGalileanFromEphemeris(moon, galileanIndex(moon), simulationJD)) {
          // Placed.
        } else if ((eclipseModeActive() || lessonActive()) && isEarthsMoon(moon)) {
          placeEarthMoonFromEphemeris(moon, simulationJD, moon.userData.distance);
        } else if (moon.userData.orbitalPeriodDays && moon.userData.orbitalPeriodDays > 0) {
          // Parsed period strings are always positive — carry the sign
          // of the authored orbitalSpeed through so retrograde moons
          // (Triton's orbitalSpeed: -0.018) actually run backwards.
          const orbitSign = (moon.userData.orbitalSpeed || 0) < 0 ? -1 : 1;
          const omega = orbitSign * (2 * Math.PI) / moon.userData.orbitalPeriodDays;
          moon.userData.orbitAngle = wrapAngle(
            moon.userData.orbitInitialPhase + omega * simulationJD
          );
        } else {
          // Fallback for moons that don't have a parseable period.
          moon.userData.orbitAngle = wrapAngle(
            moon.userData.orbitAngle +
              boundedAngularStep(moon.userData.orbitalSpeed, scaledDelta, MAX_MOON_ORBIT_STEP)
          );
        }
        // -sin on z: prograde moons revolve counterclockwise from +Y,
        // the same sense as their parent's spin and orbital revolution.
        if (!lessonHoldsMoons && !((realisticScaleMode || eclipseModeActive() || lessonActive()) && isEarthsMoon(moon))
          && !(realisticScaleMode && galileanIndex(moon) >= 0)) {
          moon.position.x = Math.cos(moon.userData.orbitAngle) * moon.userData.distance;
          moon.position.z = -Math.sin(moon.userData.orbitAngle) * moon.userData.distance;
          if (moon.userData.inclination) {
            moon.position.y = Math.sin(moon.userData.orbitAngle) * moon.userData.distance *
              Math.sin(THREE.MathUtils.degToRad(moon.userData.inclination)) * 0.3;
          } else {
            moon.position.y = 0;
          }
        }

        // Umbra test: project the moon's offset onto the sun→planet
        // axis and measure perpendicular distance. Inside the planet's
        // shadow cylinder AND behind it (t > 0) means the moon is
        // eclipsed. Smoothstep across the radius gives a soft edge so
        // the darkening glides in rather than snapping. Moons live in
        // a tilted moonFrame group now, so moon.position is NOT a
        // world-space offset — read the world position instead.
        moon.getWorldPosition(_moonUmbraWorld);
        const ox = _moonUmbraWorld.x - planetWorldPos.x;
        const oy = _moonUmbraWorld.y - planetWorldPos.y;
        const oz = _moonUmbraWorld.z - planetWorldPos.z;
        const t = ox * axisNx + oy * axisNy + oz * axisNz;
        let shadowFactor = 1.0;
        // The Earth's Moon is shaded per fragment from the real geometry
        // (attachParentPlanetShadow, earthEclipseUniforms).
        if (t > 0 && !(isEarthsMoon(moon) && earthEclipseUniforms.uEclipseOn.value > 0.5)) {
          const perpX = ox - axisNx * t;
          const perpY = oy - axisNy * t;
          const perpZ = oz - axisNz * t;
          const perpDist = Math.sqrt(perpX * perpX + perpY * perpY + perpZ * perpZ);
          const lit = THREE.MathUtils.smoothstep(
            perpDist,
            planetRadius * 0.95,
            planetRadius * 1.05
          );
          // 0.18 floor keeps the moon visible as a dim silhouette
          // rather than pitch black — earthshine / back-scatter
          // analogue so the viewer can still see where the moon is.
          shadowFactor = 0.18 + lit * 0.82;
        }

        const moonMesh = moon.children[0];
        // A moon that takes its parent's shadow per fragment
        // (attachParentPlanetShadow) is not darkened as a whole as well.
        if (moonMesh && moonMesh.material && moonMesh.material.userData && moonMesh.material.userData.parentShadowShader) {
          shadowFactor = 1.0;
        }
        if (moonMesh && moonMesh.userData.baseColor) {
          moonMesh.material.color
            .copy(moonMesh.userData.baseColor)
            .multiplyScalar(shadowFactor);
        }

        if (moon.userData._moonAtmosphere === undefined) {
          moon.userData._moonAtmosphere = moon.getObjectByName('moonAtmosphere') || null;
        }
        const moonAtmosphere = moon.userData._moonAtmosphere;
        if (moonAtmosphere && moonAtmosphere.material.uniforms) {
          moonAtmosphere.material.uniforms.sunPosition.value.set(0, 0, 0);
        }

        // Tidal locking + libration. Real Moon completes one rotation
        // per orbit (synchronous rotation) so the near side always
        // faces Earth — but the orbit is elliptical and inclined, so
        // we actually see ±7.6° in longitude (Moon spins uniformly
        // while its orbital angular velocity varies) and ±6.7° in
        // latitude (orbital plane is tilted relative to the equator).
        // Net effect: ~59% of the Moon's surface is visible from
        // Earth over a full lunar cycle, even though it's "locked".
        //
        // Three.js SphereGeometry's default UVs put u=0.5 at the
        // local +X face. The Moon map (NASA's CGI Moon Kit) has the
        // near side ("Man in the Moon" hemisphere) centered at
        // u=0.5, so the moon's local +X is the near-side direction.
        // For the near side to point at Earth (local origin in the
        // moonGroup's parent frame) when the moon sits at angle θ on
        // its orbit — position (cosθ, ·, −sinθ) — the moon must
        // rotate by π + θ around Y so that local +X aligns with the
        // inward-pointing radial direction.
        // Libration is added on top: a sinusoidal nod phase-offset
        // between the two axes so the wobble reads as a slow head
        // shake rather than wobble around a single axis.
        if (planetName === 'earth' && moon.userData.name === 'Moon') {
          const ang = moon.userData.orbitAngle;
          const libLon = Math.sin(ang) * 0.1326;            // ±7.6°
          const libLat = Math.sin(ang + 1.2) * 0.1169;       // ±6.7°, phase-offset
          moon.rotation.y = Math.PI + ang - libLon;
          moon.rotation.x = -libLat;
        } else if (moon.userData.tidallyLocked) {
          // Locked with its prime meridian (u = 0.5 of its map) on the
          // planet, as the Moon is, without the libration.
          moon.rotation.y = Math.PI + moon.userData.orbitAngle;
        }
      });
      if (realisticScaleMode && planetName === 'earth') {
        const line = (moonOrbitLines.earth || []).find((l) => l.userData.moonRef && isEarthsMoon(l.userData.moonRef));
        if (line && line.visible && Math.abs(simulationJD - (line.userData.ephemerisJD ?? -1e9)) > 1) {
          setEarthMoonOrbitLine(line, true);
        }
      }
      updateMoonTransitShadows(planetName, planet);
      if (planetName === 'earth') updateEarthEclipseUniforms(planet);
    }
  });

  // Refresh parent-planet eclipse-shadow uniforms for every
  // registered moon material. Cheap (few moons total) and happens
  // once per frame after the planet loop so Jupiter's umbra tracks
  // Io's orbit with this frame's final planet positions.
  updateParentShadowUniforms();

  // Hover animation for the secret UFOs scattered over Earth.
  // Bob amplitude is per-UFO, scaled to UFO size, so the bob
  // looks like a gentle hover instead of a teleport.
  if (secretUfos.length) {
    for (let s = 0; s < secretUfos.length; s++) {
      const ufo = secretUfos[s];
      const ud = ufo.userData;
      const bob = Math.sin(elapsed * 0.6 + ud.bobPhase) * ud.bobAmplitude;
      ufo.position.copy(ud.basePosition)
        .addScaledVector(ud.surfaceUp, bob);
      ufo.rotateOnAxis(ud.surfaceUp, ud.spinRate * delta);
    }
  }

  // Drifting / tumbling scattered debris.
  if (RUNNING_ON_MOBILE) { updateMobileDebris(); updateMobileReleases(performance.now()); }
  if (debrisField) debrisField.update(scaledDelta, delta);

  beltBodyObjects.forEach((body) => {
    if (!body.userData) return;
    // On their real orbits from JPL's elements, at the true distance or the
    // artistic one the belt's points use (asteroidArtisticDistance), and at
    // their true size at true scale.
    placeBeltBody(body);
    const model = body.userData.smallBodyModel && smallBodyModel(body.userData.smallBodyModel);
    if (model) {
      turnSmallBody(body, model, scaledDelta, simStepJD);
      return;
    }
    body.rotation.x = wrapAngle(body.rotation.x + boundedAngularStep(0.004, scaledDelta, MAX_SPIN_STEP));
    body.rotation.y = wrapAngle(body.rotation.y + boundedAngularStep(0.006, scaledDelta, MAX_SPIN_STEP));
  });

  // Animate monolith orbit around Jupiter
  if (monolith && monolith.userData) {
    monolith.userData.orbitAngle = wrapAngle(
      monolith.userData.orbitAngle +
        boundedAngularStep(monolith.userData.orbitalSpeed, scaledDelta, MAX_MOON_ORBIT_STEP)
    );
    const orbitRadius = monolith.userData.orbitRadius;
    // -sin on z: prograde around Jupiter, same sense as the moons.
    monolith.position.x = Math.cos(monolith.userData.orbitAngle) * orbitRadius;
    monolith.position.z = -Math.sin(monolith.userData.orbitAngle) * orbitRadius;
    monolith.position.y = Math.sin(monolith.userData.orbitAngle * 0.5) * 2; // Slight vertical wobble
    // Slowly rotate to face the camera occasionally
    monolith.rotation.y = wrapAngle(
      monolith.rotation.y + boundedAngularStep(0.001, scaledDelta, MAX_SPIN_STEP)
    );
  }

  // The probes leaving the solar system, on their real paths.
  placeSpaceProbes();

  // Animate satellites orbiting Earth
  satelliteObjects.forEach((entry) => {
    const { mesh, data, orbitGroup, orbitLine, isProbe } = entry;
    if (isProbe) return;
    // Hubble and Webb place themselves (placeHubble, placeJwst).
    if (entry.place) { entry.place(entry); return; }
    if (issSatrec && data.name === 'ISS' && placeIssFromOrbit(mesh, orbitGroup, orbitLine)) return;
    if (mesh.userData && mesh.userData.orbitAngle !== undefined) {
      mesh.userData.orbitAngle = wrapAngle(
        mesh.userData.orbitAngle +
          boundedAngularStep(data.orbitalSpeed || 0.1, scaledDelta, MAX_SMALL_BODY_ORBIT_STEP)
      );
      // -sin on z: prograde, matching Earth's spin sense (the ISS
      // orbits eastward). Yaw mirrored to keep the nose on velocity.
      mesh.position.x = Math.cos(mesh.userData.orbitAngle) * data.orbitRadius;
      mesh.position.z = -Math.sin(mesh.userData.orbitAngle) * data.orbitRadius;
      mesh.rotation.y = mesh.userData.orbitAngle - Math.PI * 0.5;
    }
  });

  // Animate comets with elliptical orbits
  cometObjects.forEach(({ mesh, data }) => {
    if (mesh.userData) {
      mesh.userData.orbitAngle = wrapAngle(
        mesh.userData.orbitAngle +
          boundedAngularStep(data.orbitalSpeed, scaledDelta, MAX_SMALL_BODY_ORBIT_STEP)
      );
      const angle = mesh.userData.orbitAngle;
      const orbitState = computeCometOrbitPosition(data, angle);
      mesh.position.copy(orbitState.position);
      // Orientation is set inside updateCometVisuals via buildCometBasis,
      // which builds a local frame where +z is anti-sun and +x is the
      // orbital velocity direction. That means the straight local +z
      // tail axis already points anti-sunward, no extra
      // lookAt needed here.
      updateCometVisuals(mesh, data);
    }
  });

  // Animate near-Earth asteroids
  neoObjects.forEach(({ mesh, data }) => {
    if (mesh.userData) {
      mesh.userData.orbitAngle = wrapAngle(
        mesh.userData.orbitAngle +
          boundedAngularStep(data.orbitalSpeed, scaledDelta, MAX_SMALL_BODY_ORBIT_STEP)
      );
      const ecc = data.eccentricity || 0;
      const r = data.orbitRadius * (1 - ecc * Math.cos(mesh.userData.orbitAngle));
      // -sin on z: counterclockwise from +Y, same sense as the planets.
      mesh.position.x = Math.cos(mesh.userData.orbitAngle) * r * scaleMultiplier;
      mesh.position.z = -Math.sin(mesh.userData.orbitAngle) * r * scaleMultiplier;
      mesh.position.y = Math.sin(mesh.userData.orbitAngle) * r * Math.sin(THREE.MathUtils.degToRad(data.inclination || 0)) * scaleMultiplier * 0.2;
      const model = mesh.userData.smallBodyModel && smallBodyModel(mesh.userData.smallBodyModel);
      if (model) {
        turnSmallBody(mesh, model, scaledDelta, simStepJD);
      } else {
        // Tumble rotation
        mesh.rotation.x = wrapAngle(mesh.rotation.x + boundedAngularStep(0.01, scaledDelta, MAX_SPIN_STEP));
        mesh.rotation.y = wrapAngle(mesh.rotation.y + boundedAngularStep(0.015, scaledDelta, MAX_SPIN_STEP));
      }
    }
  });
  // Swap in the small bodies' shape models once they are big enough to see.
  updateSmallBodies(camera, renderer.domElement.clientHeight || window.innerHeight);

  // Dwarf planets on their orbits
  dwarfPlanets.forEach(dwarf => {
    const planet = planets[dwarf.name.toLowerCase()];
    if (planet && planet.userData && planet.userData.isDwarfPlanet) {
      placeDwarfPlanet(planet);
      // Rotation
      if (planet.children[0]) {
        planet.children[0].rotation.y = wrapAngle(
          planet.children[0].rotation.y +
            bodySpinStep(dwarf.rotationSpeed || 0.005, scaledDelta, simStepJD, dwarf.rotationHours)
        );
      }
    }
  });
  // ʻOumuamua and 3I/ATLAS on their real paths.
  placeInterstellarObjects(scaledDelta, simStepJD);

  // Spacetime mode (src/spacetime/): every body down onto the sheet of
  // gravity wells, before anything follows or draws it.
  if (spacetimeMode) spacetimeMode.apply(delta);
  // Follow after all body/orbit motion has been applied. This keeps a
  // satellite inspection camera in the same local frame as the ISS for
  // the exact frame being rendered, instead of trailing by one orbit
  // step at sub-millimetre scene distances.
  applyScaleAnchor();
  // In XR the head rides this camera, so the follow runs as in 2D; holding
  // the solar system leaves it where it was.
  if (!xrHoldsCamera()) {
    updateFocusedObjectFollow(delta);
    updateSunCloseCamera();
    // A body focused while its well is still sinking lands centred.
    if (spacetimeMode) spacetimeMode.settle();
  }
  // Rocketry mode drives both cameras and the Earth/Moon spin from here,
  // after the bodies have moved and before the frame renders.
  if (rocketryActive && rocketry) rocketry.update();
  // Same post-motion fix for the autopilot orbit camera (fly mode): the
  // autopilot ran before the bodies moved this frame, so re-place it
  // against the body's now-current position to kill the moon jitter.
  reframeAutopilotOrbit();
  // The same for the screensaver and wallpaper tour: it placed the camera
  // from where the bodies were last frame, so a held view trailed its body
  // by one frame of orbit, and as frame times varied (the wallpaper's frame
  // cap, the Speed menu) the body shook on screen. tick() is a function of
  // the clock alone, so placing it again only moves the camera onto them.
  if (screensaverTourTicked) screensaverTour.tick(elapsed);
  // Cinematic director owns the camera here too (after all body motion,
  // so every framing uses current positions — no jitter). Refresh the
  // near plane afterwards for close fly-bys.
  if (updateCinematicDirector(delta)) {
    updateFlyCameraNear();
  }
  // A lesson's held camera frame and its labels, once the camera is where
  // this frame draws it (createLessonViewer).
  if (lessonFrameHook) lessonFrameHook(delta);
  // A famous photo's held camera (src/photos/mode.js).
  if (famousPhotoMode || famousPhotoPoints) updateFamousPhotoFrame(delta);
  // A shot's held camera (src/xr/shots.js).
  if (shotFlight) updateShotFlight();
  else if (shotHold) updateShotFrame();
  // The Cupola places the camera inside the station (src/ui/cupola.js).
  if (cupolaView) cupolaView.update(delta);
  // A planet print (scripts/prints/render-planets.mjs) holds the bodies and
  // the camera here, once every body has moved and before the frame is drawn.
  // Nothing sets it otherwise.
  if (typeof window.__printPose === 'function') {
    window.__printPose({ camera, planets, sun, resolve: resolveScreensaverBody, radiusOf: getBodyBaseRadius, jd: simulationJD });
  }

  // Update distance indicator (distance from camera to sun in AU): at true
  // scale AU_TO_SCENE units an AU, in artistic scale read off the planets'
  // drawn orbits as the Sun's glare reads it (artisticSunAu). In another
  // star system 150 units an AU at the spread.
  if (distanceValue && !flyMode) {
    const distanceFromSun = camera.position.length();
    const distanceAU = activeSystemId && activeSystemId !== 'sol'
      ? distanceFromSun / (150 * scaleMultiplier)
      : realisticScaleMode ? distanceFromSun / AU_TO_SCENE : artisticSunAu(distanceFromSun);
    // Written only when the text changes: the same string every frame still
    // replaced the text node and dirtied the layout.
    let distanceText;
    if (distanceAU < 0.01) {
      distanceText = (distanceAU * 1000).toFixed(1) + ' mAU';
    } else if (distanceAU < 10) {
      distanceText = distanceAU.toFixed(2);
    } else if (distanceAU < 100) {
      distanceText = distanceAU.toFixed(1);
    } else {
      distanceText = Math.round(distanceAU).toLocaleString();
    }
    if (distanceText !== lastDistanceText) {
      distanceValue.textContent = distanceText;
      lastDistanceText = distanceText;
    }
  }

  // Science panel readout at 4 Hz — same accumulator pattern as the
  // sim-date display. The mode toggle still calls it directly for an
  // immediate refresh.
  if (elapsed - lastSciencePanelUpdate > 0.25) {
    lastSciencePanelUpdate = elapsed;
    updateSciencePanel();
  }
  updateLocalOrbitGuideVisibility();
  updateWormholeVisuals();
  // Hover raycast: at most once per frame regardless of mousemove
  // event rate (see onMouseMove).
  if (hoverCheckPending) {
    hoverCheckPending = false;
    if (!flyMode && !isAnimating) {
      checkHover();
      updateHoverDwell();
    }
  }

  // The cinematic pass's upkeep; XR draws without the composer.
  if (!renderer.xr.isPresenting) {
    updateFutureSun(performance.now());
    updateAdaptiveExposure(delta);
    updateLensFlare(delta);
    updateSunDiscBloom(sunDiscBloomPass, { camera, sun, enabled: !scaleModeActive && !rocketryActive });
  }
  // Zodiacal light only exists in the Sol system's dust cloud; remote
  // systems and far-out overview shots hide it.
  if (zodiacalLightMesh) {
    if (activeSystemId === 'sol') {
      // Washes out with the stars inside a daylit atmosphere.
      updateZodiacalLight(zodiacalLightMesh, camera, 0.16 * (1 - atmoSkyOcclusionLive));
    } else {
      zodiacalLightMesh.visible = false;
    }
  }
  updateAlienScanHud(delta);
  if (navigation) navigation.tick();
  if (flyMode) {
    updateFlyTargetHud();
    const nowMs = performance.now();
    if (nowMs - _navPlateLast > 150) {
      _navPlateLast = nowMs;
      updateFlyNavPlate();
    }
  }
  // Saucer-specific dynamic reflections — only the brushed-steel
  // saucer uses the cube camera + planet-reflection overlay material.
  // Skip both pipelines when the user has picked a different craft
  // (sphere, Enterprise) since their materials don't sample envMap.
  if (activeCraftKey === 'saucer') {
    updateUfoPlanetReflection(delta);

    // Update UFO dynamic reflections. cubeCamera.update renders the WHOLE
    // scene 6× (all 200k+ star points included), so it's the heaviest
    // thing in fly mode. The ship is sub-pixel in normal flight, where
    // that reflection is invisible — so only refresh it when the craft is
    // actually big enough on screen to show one (e.g. the scaled-up
    // cinematic ship or a tight close-up), and cap it to every 8th frame
    // even then. This was running every 2nd frame unconditionally.
    if (flyMode && playerShip && shipCubeCamera) {
      const shipR = 0.5 * playerShip.scale.x;
      const camDist = camera.position.distanceTo(playerShip.position);
      const apparent = camDist > 1e-6 ? shipR / camDist : 1; // ~angular size
      // One face a frame, so the cube refreshes every six frames without a
      // six-scene burst. That holds on entering fly mode and after a jump
      // too: redrawing a stale cube whole drew the sky every way at once and
      // uploaded every map in it in one frame, the freeze on clicking Fly.
      if (apparent > 0.0025) {
        const wasVisible = playerShip.visible;
        playerShip.visible = false; // Hide UFO while rendering its reflection
        shipCubeCamera.position.copy(playerShip.position);
        hideSubTexelBodiesFromShipCube();
        renderShipCubeFace(frameCount % 6);
        restoreShipCubeBodies();
        playerShip.visible = wasVisible;
      }
    }
  }

  // Tick the active craft so non-saucer ships can animate (sphere
  // shader time, nacelle / bussard / impulse glow tied to speed, etc.).
  // We forward a motion descriptor so ships can scale their emissive
  // intensities with how hard the user is flying — the warp coils on
  // the Enterprise should bloom when accelerating to warp, impulse
  // engines glow harder under thrust, etc.
  if (activeCraftKey !== 'saucer' && loadedCraft[activeCraftKey] && loadedCraft[activeCraftKey].tick) {
    const _craftMotionSpeed = flyVelocity ? flyVelocity.length() : 0;
    const _craftMotionMax = (propulsionTypes[currentPropulsion] && propulsionTypes[currentPropulsion].maxSpeed) || 1;
    const _craftMotionRatio = THREE.MathUtils.clamp(_craftMotionSpeed / Math.max(_craftMotionMax, 0.0001), 0, 1);
    const _craftMotionThrust = !!(flyKeys && (flyKeys.forward || flyKeys.backward));
    loadedCraft[activeCraftKey].tick(delta, elapsed, {
      speed: _craftMotionSpeed,
      maxSpeed: _craftMotionMax,
      speedRatio: _craftMotionRatio,
      isThrusting: _craftMotionThrust,
      propulsion: currentPropulsion
    });
  }

  updateJumpDeparture(delta);
  updateHyperspace(delta, elapsed);
  if (scaleModeActive) hideSceneForScale();
  const restoreHyperspaceCamera = hyperspace ? hyperspace.applyCameraFx(camera, elapsed) : null;
  try {
    renderFrame();
  } finally {
    if (restoreHyperspaceCamera) restoreHyperspaceCamera();
  }
}

// Start the application
init().catch(console.error);

// Localhost-only debug handle for the dev tuners and headless test
// probes. Never present in production.
if (isLocalEnvironment()) {
  // The small bodies' model swaps: states() lists them, load(name) fetches one now.
  window.__smallBodies = { states: smallBodyStates, load: loadSmallBodyNow, get: smallBodyRecord };
  window.__debug = {
    get bootPreviews() { return { previewed: bootPreviewNames, swapped: bootPreviewLog }; },
    get bandUploads() { return bandUploadLog; },
    imageFile: (image) => imageFileUrl(image),
    get camera() { return camera; },
    get planetPoints() { return planetPoints; },
    get focusRoute() { return _lastFocusRoute; },
    get planets() { return planets; },
    get atmoEntry() { return atmoEntry; },
    get scaleMultiplier() { return scaleMultiplier; },
    get renderer() { return renderer; },
    get cinematicPass() { return cinematicPass; },
    get sunGlare() { return SUN_GLARE; },
    get sunLight() { return sunLight; },
    get composer() { return composer; },
    get atmoLive() { return { craftScale: atmoCraftScaleLive, skyOcclusion: atmoSkyOcclusionLive }; },
    get playerShip() { return playerShip; },
    get nearestBody() { return findNearestBodySurface(); },
    get flyVelocity() { return flyVelocity; },
    get flyMode() { return flyMode; },
    get jumpInFlight() { return starMapJumpInFlight || wormholeTunnelInFlight; },
    get hyperspaceProgress() { return hyperspace ? hyperspace.progress() : 0; },
    get hyperspace() { return hyperspace; },
    get flySpeed() { return flySpeed; },
    get flyTarget() { return flyTarget; },
    get flightAssist() { return flightAssist; },
    get flyAssistState() {
      const n = findNearestBodySurface();
      return {
        sKeyMode: _sKeyMode, brakeLatch: _brakeLatch, faArmed: _faArmed, faEase: _faEaseT,
        smoothedThrust: FLY_SMOOTHED_THRUST.length(), keys: { ...flyKeys },
        orbitStabilized, autopilot: autopilot.active, parked: !!flyParkedBody, orbitParked,
        nearest: n ? { body: n.body && n.body.userData && n.body.userData.name, dist: n.dist, depth: n.atmosphereDepth, density: computeAtmosphericDensity(n) } : null
      };
    },
    setFlightAssist,
    engageBrakeLatch,
    // Test hooks for the fly handling harness (scripts/test-fly-handling.mjs).
    findFlyBody: (name) => {
      const key = String(name || '').toLowerCase();
      let found = null;
      forEachFlyTargetCandidate((b) => {
        if (!found && String((b.userData && b.userData.name) || '').toLowerCase() === key) found = b;
      });
      return found;
    },
    setFlyTargetByName: (name) => {
      const body = window.__debug.findFlyBody(name);
      setFlyTarget(body);
      return !!body;
    },
    get navigation() { return navigation; },
    get activeSystemId() { return activeSystemId; },
    get remoteSystems() { return remoteSystems; },
    ensureNavigation,
    toggleGalaxyMap,
    engageNextJump,
    plotRouteToStar: async (name) => {
      const nav = await ensureNavigation();
      const star = nav && nav.findStar(name);
      if (!star) return null;
      return nav.plotRouteTo(star);
    },
    flyLookAtBody: (name) => {
      const body = window.__debug.findFlyBody(name);
      if (!body || !camera) return false;
      const target = body.getWorldPosition(new THREE.Vector3());
      const m = new THREE.Matrix4().lookAt(camera.position, target, new THREE.Vector3(0, 1, 0));
      const q = new THREE.Quaternion().setFromRotationMatrix(m);
      flyEuler.setFromQuaternion(q);
      flyEuler.z = 0;
      flyTargetEuler.copy(flyEuler);
      flyAngularVelocity.set(0, 0);
      camera.quaternion.setFromEuler(flyEuler);
      return true;
    },
    get atmoLookPending() { return atmoLookPending; },
    get flyMouseLookActive() { return flyMouseLookActive; },
    get scene() { return scene; },
    get controls() { return controls; },
    get activeSystemId() { return activeSystemId; },
    get selectedObject() { return selectedObject; },
    get rocketry() { return rocketry; },
    get earthCloseView() { return earthCloseView; },
    setSimulationJD: (jd) => { simulationJD = jd; },
    get simulationJD() { return simulationJD; },
    setTimeSpeed: (speed) => { timeSpeed = speed; },
    // For test scripts that frame a longitude against the Sun: sets the
    // Earth mesh's rotation.y to `angle` now and keeps that offset from the
    // clock angle until called with null. Set the date first, then pose.
    poseEarthSpin: (angle) => {
      earthSpinLag = 0;
      earthSpinPose = angle == null ? 0 : wrapAngle(angle - earthRotationAngle(simulationJD));
      const mesh = getEarthMeshForTerrain();
      if (!mesh) return null;
      mesh.rotation.y = earthSpinAngle(0, 0);
      mesh.updateMatrixWorld(true);
      return mesh.rotation.y;
    },
    focusOnObject,
    returnToOverview,
    enterRocketryMode,
    exitRocketryMode,
    getBodyVisualSurfaceRadius: (name) => {
      const body = planets[name];
      return body ? getBodyVisualSurfaceRadius(body, null) : null;
    }
  };
}

// Export for testing
export { scene, camera, planets, sun, focusOnObject, returnToOverview };
